import { useState, useEffect } from "react";

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;900&family=Barlow:wght@400;500;600&family=Bebas+Neue&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --navy:#0d1b2a;--navy-mid:#152335;--navy-light:#1e3250;--navy-border:#2a4060;
  --white:#f4f7fa;--off-white:#dce6f0;--gold:#c9a84c;--gold-light:#e8c87a;
  --red:#d94f3d;--green:#2ecc71;--muted:#6a8aaa;--card:#152335;
}
:root[data-theme="light"]{
  --navy:#f0f4f8;--navy-mid:#e2e8f0;--navy-light:#cbd5e1;--navy-border:#b0bec5;
  --white:#1a2332;--off-white:#2d3748;--gold:#b7860d;--gold-light:#9a6e00;
  --red:#c0392b;--green:#16a34a;--muted:#4a5568;--card:#e2e8f0;
}
:root[data-theme="light"] body{background:#f0f4f8;color:#1a2332;}
:root{
  --safe-bottom: env(safe-area-inset-bottom, 0px);
  --safe-top: env(safe-area-inset-top, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
  --safe-right: env(safe-area-inset-right, 0px);
}
html{-webkit-text-size-adjust:100%;text-size-adjust:100%;}
body{background:var(--navy);color:var(--white);font-family:'Barlow',sans-serif;min-height:100vh;
  -webkit-tap-highlight-color:transparent;overscroll-behavior:none;}
::-webkit-scrollbar{width:5px;height:5px;}
::-webkit-scrollbar-track{background:var(--navy);}
::-webkit-scrollbar-thumb{background:var(--navy-border);border-radius:3px;}

/* ── Base elements ── */
button{cursor:pointer;font-family:'Barlow',sans-serif;touch-action:manipulation;
  -webkit-tap-highlight-color:transparent;min-height:36px;user-select:none;-webkit-user-select:none;}
button:active{transform:scale(0.97);transition:transform 0.08s;}
input,select,textarea{font-family:'Barlow',sans-serif;}
a{-webkit-tap-highlight-color:transparent;}

/* ── Animations ── */
@keyframes fadeIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeInUp{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);}}
@keyframes slideUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
@keyframes slideInLeft{from{transform:translateX(-100%);}to{transform:translateX(0);}}
@keyframes slideInRight{from{transform:translateX(100%);}to{transform:translateX(0);}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
.fade-in{animation:fadeIn 0.3s ease forwards;}
.slide-up{animation:slideUp 0.28s cubic-bezier(0.32,0.72,0,1) forwards;}

/* ── Layout skeleton ── */
.desktop-sidebar{display:flex;flex-direction:column;}
.mobile-nav{display:none;}
.resp-grid-2,.resp-grid-3,.resp-grid-4{display:grid;}

/* ── Scrollable containers ── */
.resp-table{overflow-x:auto;-webkit-overflow-scrolling:touch;border-radius:4px;}
.resp-table table{min-width:480px;}
.scroll-x{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
.scroll-x::-webkit-scrollbar{display:none;}
.snap-x{scroll-snap-type:x mandatory;}
.snap-item{scroll-snap-align:start;}

/* ── Tab strip (horizontal scroll, no wrap) ── */
.tab-strip{display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;
  scrollbar-width:none;gap:0;border-bottom:1px solid var(--navy-border);
  flex-shrink:0;scroll-snap-type:x proximity;}
.tab-strip::-webkit-scrollbar{display:none;}
.tab-btn{flex-shrink:0;padding:9px 14px;border:none;border-bottom:2px solid transparent;
  background:none;color:var(--muted);font-family:'Barlow Condensed',sans-serif;
  font-weight:700;font-size:13px;letter-spacing:1px;text-transform:uppercase;
  cursor:pointer;white-space:nowrap;transition:color 0.15s,border-color 0.15s;
  touch-action:manipulation;-webkit-tap-highlight-color:transparent;}
.tab-btn.tab-active{color:var(--gold);border-bottom-color:var(--gold);}
.tab-btn:active{background:rgba(255,255,255,0.04);}

/* ── Mobile card pattern (replaces tables on small screens) ── */
.mob-card{background:var(--navy-mid);border:1px solid var(--navy-border);border-radius:8px;
  padding:12px 14px;transition:border-color 0.15s;}
.mob-card:active{border-color:var(--navy-light);}
.mob-card-list{display:flex;flex-direction:column;gap:8px;}

/* ── Drawer/sheet ── */
.drawer-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:490;
  backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);}
.bottom-sheet{position:fixed;bottom:0;left:0;right:0;z-index:500;
  background:var(--navy-mid);border-top:1px solid var(--navy-border);
  border-radius:16px 16px 0 0;
  padding-bottom:max(16px, var(--safe-bottom));
  animation:slideUp 0.28s cubic-bezier(0.32,0.72,0,1) forwards;}
.sheet-handle{width:36px;height:4px;background:var(--navy-border);
  border-radius:2px;margin:10px auto 4px;flex-shrink:0;}
.side-drawer{position:fixed;top:0;left:0;bottom:0;width:min(280px,85vw);z-index:500;
  background:var(--navy-mid);border-right:1px solid var(--navy-border);
  overflow-y:auto;-webkit-overflow-scrolling:touch;
  padding-top:max(16px, var(--safe-top));
  padding-bottom:max(16px, var(--safe-bottom));
  animation:slideInLeft 0.24s cubic-bezier(0.32,0.72,0,1) forwards;}

/* ── Swipe hint dots ── */
.swipe-dots{display:flex;justify-content:center;gap:5px;padding:8px 0 0;}
.swipe-dot{width:6px;height:6px;border-radius:50%;background:var(--navy-border);transition:background 0.2s,transform 0.2s;}
.swipe-dot.active{background:var(--gold);transform:scale(1.3);}

/* ── Pull indicator ── */
.pull-hint{display:flex;align-items:center;justify-content:center;gap:6px;
  font-size:11px;color:var(--muted);font-family:'Barlow Condensed',sans-serif;
  font-weight:700;letter-spacing:1.5px;text-transform:uppercase;padding:8px 0;}

/* ── Tablet: ≤899px ── */
@media(max-width:899px){
  .desktop-sidebar{display:none!important;}
  .mobile-nav{
    display:flex!important;position:fixed;bottom:0;left:0;right:0;
    height:calc(56px + var(--safe-bottom));
    padding-bottom:var(--safe-bottom);
    background:var(--navy-mid);border-top:1px solid var(--navy-border);
    z-index:200;align-items:stretch;}
  .mobile-nav-item{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    cursor:pointer;gap:3px;padding:6px 2px 2px;border-top:2px solid transparent;
    font-size:9px;font-family:'Barlow Condensed',sans-serif;font-weight:700;
    letter-spacing:1px;text-transform:uppercase;color:var(--muted);
    transition:border-color 0.15s,color 0.15s;touch-action:manipulation;
    -webkit-tap-highlight-color:transparent;user-select:none;-webkit-user-select:none;}
  .mobile-nav-item:active{background:rgba(255,255,255,0.04);}
  .mobile-nav-item.mna{border-top-color:var(--gold);color:var(--gold);}
  .mobile-nav-item .mni{font-size:20px;line-height:1;}
  .main-area{padding-bottom:calc(64px + var(--safe-bottom))!important;}
  .resp-grid-4{grid-template-columns:repeat(2,1fr)!important;}
  .resp-grid-3{grid-template-columns:repeat(2,1fr)!important;}
  .resp-grid-2{grid-template-columns:1fr!important;}
  .resp-hide{display:none!important;}
  .resp-table{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
  .resp-stack{flex-direction:column!important;}
  button,select,[role="button"]{min-height:44px;}
  input,select,textarea{font-size:16px!important;}
}

/* ── Mobile: ≤639px ── */
@media(max-width:639px){
  .main-area{padding:10px!important;padding-bottom:calc(68px + var(--safe-bottom))!important;}
  .resp-grid-4{grid-template-columns:1fr 1fr!important;}
  .resp-grid-3{grid-template-columns:1fr!important;}
  .resp-grid-2{grid-template-columns:1fr!important;}
  .resp-hide-sm{display:none!important;}
  .resp-show-sm{display:block!important;}
  .topbar-role{display:none!important;}
  .resp-full-sm{width:100%!important;max-width:100%!important;}
  .resp-stack-sm{flex-direction:column!important;align-items:stretch!important;}
  .tab-btn{padding:8px 12px;font-size:12px;}
  [data-modal]{max-width:calc(100vw - 20px)!important;margin:10px!important;}
  .mob-show{display:block!important;}
  .mob-hide{display:none!important;}
}

/* ── Reduced motion ── */
@media(prefers-reduced-motion:reduce){
  *{animation-duration:0.01ms!important;transition-duration:0.01ms!important;}
}
`;

const LOGO_URI = "data:image/png;base64,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";

// ── RESPONSIVE HOOK ──────────────────────────────────────────────────────────
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return { isMobile: w < 640, isTablet: w < 900, isDesktop: w >= 900, w };
}

// Touch swipe detection hook
function useSwipe({ onLeft, onRight, onUp, threshold=50 } = {}) {
  const start = React.useRef(null);
  const onTouchStart = React.useCallback(e => {
    start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const onTouchEnd = React.useCallback(e => {
    if (!start.current) return;
    const dx = e.changedTouches[0].clientX - start.current.x;
    const dy = e.changedTouches[0].clientY - start.current.y;
    const absDx = Math.abs(dx), absDy = Math.abs(dy);
    if (Math.max(absDx, absDy) < threshold) return;
    if (absDx > absDy) {
      if (dx < 0 && onLeft)  onLeft();
      if (dx > 0 && onRight) onRight();
    } else {
      if (dy < 0 && onUp) onUp();
    }
    start.current = null;
  }, [onLeft, onRight, onUp, threshold]);
  return { onTouchStart, onTouchEnd };
}


let _uid = 100;
const uid = () => `id_${_uid++}`;

// ── ELO & POWER RATING ──────────────────────────────────────────────────────
function calcEloDelta(rA, rB, won) {
  const ea = 1 / (1 + Math.pow(10, (rB - rA) / 400));
  return Math.round(32 * ((won ? 1 : 0) - ea));
}
function calcPowerRating(p) {
  const placePts = (p.placements || []).reduce((s, pl) =>
    s + (pl === 1 ? 100 : pl === 2 ? 70 : pl === 3 ? 50 : pl <= 6 ? 30 : 10), 0);
  return Math.round((p.elo || 1200) * 0.6 + placePts * 0.3 + Math.max(0, (p.cupDiff || 0)) * 0.1);
}

// ── BRACKET GENERATORS ──────────────────────────────────────────────────────
function genSingleElim(teams) {
  const size = Math.pow(2, Math.ceil(Math.log2(Math.max(teams.length, 2))));
  const seeded = [...teams];
  while (seeded.length < size) seeded.push(null);
  const rounds = [];
  let cur = [];
  for (let i = 0; i < seeded.length; i += 2) {
    const isBye = !seeded[i + 1];
    cur.push({ id: uid(), t1: seeded[i], t2: seeded[i+1], s1: isBye ? 1 : null, s2: isBye ? 0 : null, winner: isBye ? seeded[i] : null, status: isBye ? "bye" : "pending", ot: false });
  }
  rounds.push({ name: getRName(cur.length), matches: cur });
  let prev = cur;
  while (prev.length > 1) {
    const next = [];
    for (let i = 0; i < prev.length; i += 2) {
      next.push({ id: uid(), t1: null, t2: null, s1: null, s2: null, winner: null, status: "waiting", feedFrom: [prev[i]?.id, prev[i+1]?.id], ot: false });
    }
    rounds.push({ name: getRName(next.length), matches: next });
    prev = next;
  }
  // Auto-advance byes
  return autoAdvance(rounds);
}

function autoAdvance(rounds) {
  let r = rounds.map(rnd => ({ ...rnd, matches: rnd.matches.map(m => ({ ...m })) }));
  for (let ri = 0; ri < r.length - 1; ri++) {
    r[ri].matches.forEach((m, mi) => {
      if (m.winner) {
        const nextRi = ri + 1, nextMi = Math.floor(mi / 2), slot = mi % 2 === 0 ? "t1" : "t2";
        if (r[nextRi]?.matches[nextMi]) {
          r[nextRi].matches[nextMi][slot] = m.winner;
          const nm = r[nextRi].matches[nextMi];
          if (nm.t1 && nm.t2) nm.status = "pending";
        }
      }
    });
  }
  return r;
}

function genRoundRobin(teams) {
  // Berger round-robin schedule (or circle method)
  const n = teams.length;
  const rounds = [];
  const t = n % 2 === 0 ? [...teams] : [...teams, null]; // null = bye
  const N = t.length;
  const numRounds = N - 1;
  for (let r = 0; r < numRounds; r++) {
    const matches = [];
    for (let i = 0; i < N / 2; i++) {
      const a = t[i], b = t[N - 1 - i];
      if (a && b) {
        matches.push({ id: uid(), t1: a, t2: b, s1: null, s2: null, winner: null, status: "pending", ot: false });
      } else if (a || b) {
        const real = a || b;
        matches.push({ id: uid(), t1: real, t2: null, s1: 1, s2: 0, winner: real, status: "bye", ot: false });
      }
    }
    rounds.push({ name: `Round ${r + 1}`, matches });
    // Rotate: fix t[0], rotate rest
    const fixed = t[0];
    const rest = t.slice(1);
    rest.unshift(rest.pop());
    t.splice(0, N, fixed, ...rest);
  }
  return rounds;
}

// Compute Round Robin standings from rounds
function calcRRStandings(rounds) {
  const stats = {};
  const allMatches = rounds.flatMap(r => r.matches || []);

  // Build base stats
  allMatches.forEach(m => {
    if (m.status === "bye") return;
    [m.t1, m.t2].forEach(t => {
      if (!t) return;
      const key = t.name || t.id;
      if (!stats[key]) stats[key] = { team: t, w: 0, l: 0, cf: 0, cm: 0, ca: 0, played: 0, tiebreaker: null };
    });
    if (m.status !== "complete") return;
    const k1 = m.t1?.name || m.t1?.id, k2 = m.t2?.name || m.t2?.id;
    if (!k1 || !k2) return;
    stats[k1].played++; stats[k2].played++;
    stats[k1].cm += m.s1; stats[k1].ca += m.s2;
    stats[k2].cm += m.s2; stats[k2].ca += m.s1;
    const diff = m.ot ? (m.s1 > m.s2 ? 1 : -1) : (m.s1 - m.s2);
    stats[k1].cf += diff; stats[k2].cf -= diff;
    if (m.s1 > m.s2) { stats[k1].w++; stats[k2].l++; }
    else             { stats[k2].w++; stats[k1].l++; }
  });

  const rows = Object.values(stats);

  // ── Tiebreaker helpers ──────────────────────────────────────────────────
  function h2hMatches(a, b) {
    return allMatches.filter(m =>
      m.status === "complete" &&
      ((m.t1?.name === a.team.name && m.t2?.name === b.team.name) ||
       (m.t1?.name === b.team.name && m.t2?.name === a.team.name))
    );
  }
  // TB2: head-to-head W delta for a vs b (positive = a leads)
  function h2hRecord(a, b) {
    let delta = 0;
    h2hMatches(a, b).forEach(m => {
      const aIsT1 = m.t1?.name === a.team.name;
      (aIsT1 ? m.s1 > m.s2 : m.s2 > m.s1) ? delta++ : delta--;
    });
    return delta;
  }
  // TB3: head-to-head cup diff for a vs b (positive = a leads)
  function h2hCupDiff(a, b) {
    let diff = 0;
    h2hMatches(a, b).forEach(m => {
      const aIsT1 = m.t1?.name === a.team.name;
      diff += aIsT1 ? (m.s1 - m.s2) : (m.s2 - m.s1);
    });
    return diff;
  }
  // TB4: strength of schedule — avg wins of all opponents faced
  function sos(row) {
    let total = 0, count = 0;
    allMatches.forEach(m => {
      if (m.status !== "complete") return;
      const aIsT1 = m.t1?.name === row.team.name;
      const aIsT2 = m.t2?.name === row.team.name;
      if (!aIsT1 && !aIsT2) return;
      const oppKey = aIsT1 ? (m.t2?.name || m.t2?.id) : (m.t1?.name || m.t1?.id);
      if (stats[oppKey]) { total += stats[oppKey].w; count++; }
    });
    return count > 0 ? total / count : 0;
  }
  // TB5: record vs common opponents (positive = a leads)
  function commonOppScore(a, b) {
    // Find opponents a has faced
    const aFaced = new Set();
    allMatches.forEach(m => {
      if (m.status !== "complete") return;
      if (m.t1?.name === a.team.name) aFaced.add(m.t2?.name);
      if (m.t2?.name === a.team.name) aFaced.add(m.t1?.name);
    });
    let score = 0;
    allMatches.forEach(m => {
      if (m.status !== "complete") return;
      const bIsT1 = m.t1?.name === b.team.name;
      const bIsT2 = m.t2?.name === b.team.name;
      if (!bIsT1 && !bIsT2) return;
      const opp = bIsT1 ? m.t2?.name : m.t1?.name;
      if (!aFaced.has(opp) || opp === a.team.name || opp === b.team.name) return;
      // Find a's result vs this common opponent
      const aMatch = allMatches.find(am =>
        am.status === "complete" &&
        ((am.t1?.name === a.team.name && am.t2?.name === opp) ||
         (am.t2?.name === a.team.name && am.t1?.name === opp))
      );
      if (!aMatch) return;
      const aWon = aMatch.t1?.name === a.team.name ? aMatch.s1 > aMatch.s2 : aMatch.s2 > aMatch.s1;
      const bWon = bIsT1 ? m.s1 > m.s2 : m.s2 > m.s1;
      score += (aWon ? 1 : 0) - (bWon ? 1 : 0);
    });
    return score;
  }

  // Stable random per-pair coin flip (seeded by team names so consistent across re-renders)
  function coinFlipWinner(a, b) {
    const seed = (a.team.name + "|" + b.team.name).split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return seed % 2 === 0 ? -1 : 1; // negative = a wins flip
  }

  rows.sort((a, b) => {
    // 1. W-L record
    if (b.w !== a.w) return b.w - a.w;
    if (a.l !== b.l) return a.l - b.l;
    // 2. Overall cup differential
    if (b.cf !== a.cf) return b.cf - a.cf;
    // 3. Head-to-head W-L
    const h2hR = h2hRecord(a, b);
    if (h2hR !== 0) return -h2hR;
    // 4. Head-to-head cup differential
    const h2hCD = h2hCupDiff(a, b);
    if (h2hCD !== 0) return -h2hCD;
    // 5. Strength of schedule
    const sosA = sos(a), sosB = sos(b);
    if (Math.abs(sosA - sosB) > 0.001) return sosB - sosA;
    // 6. Record vs common opponents
    const co = commonOppScore(a, b);
    if (co !== 0) return -co;
    // 7. Coin flip (stable per-pair)
    a.tiebreaker = "coin"; b.tiebreaker = "coin";
    return coinFlipWinner(a, b);
  });

  return rows;
}

// ── DOUBLE ELIMINATION ───────────────────────────────────────────────────────
// Structure returned: { wb: WB rounds[], lb: LB rounds[], gf: grand-final match }
//
// WB round names:   WR1, WR2, … Winners Finals
// LB round names:   LR1, LR2, … Losers Finals
// Grand Final:      single match; if LB winner wins, a "reset" match fires
//
// Feed rules (8-team canonical):
//   WR1 losers  → LR1 (4 matches)
//   LR1 winners → LR2 (paired with WR2 losers, 4 matches total)
//   LR2 winners → LR3 (2 matches)   [pure LB round]
//   LR3 winners → LR4 (paired with WR SF losers)
//   LR4 winners → LB Finals
//   LB Finals winner → Grand Final vs WB champion
//
// The generator works for any power-of-2 team count.
function genDoubleElim(teams) {
  const size = Math.pow(2, Math.ceil(Math.log2(Math.max(teams.length, 2))));
  const seeded = [...teams];
  while (seeded.length < size) seeded.push(null);

  // ── Winners Bracket ──────────────────────────────────────────────────────
  const wbRounds = [];
  let wbPrev = [];
  for (let i = 0; i < size; i += 2) {
    const isBye = !seeded[i + 1];
    wbPrev.push({
      id: uid(), bracket:"W",
      t1: seeded[i], t2: seeded[i+1],
      s1: isBye?1:null, s2: isBye?0:null,
      winner: isBye?seeded[i]:null, loser: null,
      status: isBye?"bye":"pending", ot:false,
    });
  }
  wbRounds.push({ name:"Winners R1", side:"W", matches: wbPrev });

  while (wbPrev.length > 1) {
    const next = [];
    for (let i = 0; i < wbPrev.length; i += 2) {
      next.push({ id:uid(), bracket:"W", t1:null, t2:null, s1:null, s2:null, winner:null, loser:null, status:"waiting", ot:false, feedW:[wbPrev[i]?.id, wbPrev[i+1]?.id] });
    }
    const rIdx = wbRounds.length;
    const name = next.length===1 ? "Winners Finals" : next.length===2 ? "Winners Semis" : `Winners R${rIdx+1}`;
    wbRounds.push({ name, side:"W", matches: next });
    wbPrev = next;
  }

  // ── Losers Bracket ───────────────────────────────────────────────────────
  // LB has 2*(wbRounds.length-1) rounds.
  // Odd LB rounds: drop-in from WB losers + previous LB winners
  // Even LB rounds: pure LB (previous LB winners only)
  const numWbRounds = wbRounds.length; // e.g. 4 for 16 teams, 3 for 8 teams
  const lbRounds = [];
  let lbSize = size / 2; // starts with all WR1 losers

  // LB R1: purely WR1 losers vs each other
  const lbR1 = [];
  for (let i = 0; i < lbSize; i += 2) {
    lbR1.push({ id:uid(), bracket:"L", t1:null, t2:null, s1:null, s2:null, winner:null, loser:null, status:"waiting", ot:false,
      dropFrom: { wbRound:0, matchIdxA: i, matchIdxB: i+1 } // WR1 losers
    });
  }
  lbRounds.push({ name:"Losers R1", side:"L", matches: lbR1 });

  let lbPrev = lbR1;
  let wbDropRound = 1; // next WB round whose losers drop into LB

  while (lbPrev.length > 1 || wbDropRound < numWbRounds - 1) {
    const lbRoundIdx = lbRounds.length;

    if (lbRoundIdx % 2 === 1) {
      // Odd LB round: LB survivors meet WB drop-ins
      // LB survivors: lbPrev.length matches → lbPrev.length winners
      // WB drop-ins from wbRounds[wbDropRound] losers
      const dropMatches = wbRounds[wbDropRound]?.matches || [];
      const count = Math.max(lbPrev.length, Math.ceil(dropMatches.length / 1));
      const round = [];
      for (let i = 0; i < lbPrev.length; i++) {
        round.push({ id:uid(), bracket:"L", t1:null, t2:null, s1:null, s2:null, winner:null, loser:null, status:"waiting", ot:false,
          feedLB: lbPrev[i]?.id,
          dropFrom: { wbRound: wbDropRound, matchIdx: i }
        });
      }
      const rNum = lbRounds.length + 1;
      lbRounds.push({ name: lbPrev.length===1 ? "Losers Finals" : `Losers R${rNum}`, side:"L", matches: round });
      wbDropRound++;
      lbPrev = round;
    } else {
      // Even LB round: pure LB, winners from previous round play each other
      const round = [];
      for (let i = 0; i < lbPrev.length; i += 2) {
        round.push({ id:uid(), bracket:"L", t1:null, t2:null, s1:null, s2:null, winner:null, loser:null, status:"waiting", ot:false,
          feedLB: [lbPrev[i]?.id, lbPrev[i+1]?.id]
        });
      }
      if (round.length === 0) break;
      const rNum = lbRounds.length + 1;
      const name = round.length===1 ? "Losers Finals" : `Losers R${rNum}`;
      lbRounds.push({ name, side:"L", matches: round });
      lbPrev = round;
    }

    if (lbPrev.length === 1 && wbDropRound >= numWbRounds - 1) break;
  }

  // ── Grand Final ──────────────────────────────────────────────────────────
  const grandFinal = {
    id: uid(), bracket:"GF",
    t1: null, t2: null, // t1=WB champ, t2=LB champ
    s1: null, s2: null,
    winner: null, loser: null, status:"waiting", ot:false,
    reset: null, // populated if LB winner wins GF1
    feedW: wbRounds[wbRounds.length-1]?.matches[0]?.id,
    feedL: lbRounds[lbRounds.length-1]?.matches[0]?.id,
  };

  return { wb: wbRounds, lb: lbRounds, gf: grandFinal };
}

// Apply a completed WB match: advance winner in WB, drop loser into LB
function applyWBResult(state, matchId, s1, s2, ot) {
  let { wb, lb, gf } = JSON.parse(JSON.stringify(state));

  // Find match
  let foundRi=-1, foundMi=-1, match=null;
  for (let ri=0;ri<wb.length;ri++) for (let mi=0;mi<wb[ri].matches.length;mi++) {
    if (wb[ri].matches[mi].id===matchId) { foundRi=ri; foundMi=mi; match=wb[ri].matches[mi]; }
  }
  if (!match) return state;

  const winner = s1>s2 ? match.t1 : match.t2;
  const loser  = s1>s2 ? match.t2 : match.t1;
  const diff   = ot ? (s1>s2?1:-1) : s1-s2;
  wb[foundRi].matches[foundMi] = { ...match, s1, s2, winner, loser, status:"complete", ot, cupDiff:diff };

  // Advance winner in WB
  if (foundRi < wb.length-1) {
    const nextMi = Math.floor(foundMi/2), slot = foundMi%2===0?"t1":"t2";
    wb[foundRi+1].matches[nextMi][slot] = winner;
    const nm = wb[foundRi+1].matches[nextMi];
    if (nm.t1 && nm.t2) nm.status = "pending";
  } else {
    // WB champ feeds Grand Final t1
    gf.t1 = winner;
    if (gf.t2) gf.status = "pending";
  }

  // Drop loser into LB
  // LB R1 is special: pairs of WR1 losers face each other
  // LB drop-in for WR1: fill LBR1 slots
  // LB drop-in for WR2+: fill next odd LB round
  if (loser) {
    if (foundRi === 0) {
      // WR1 losers → LBR1: pair matchIdx i with i+1
      const lbR1 = lb[0];
      // Two WR1 losers from adjacent matches (0&1, 2&3, …) feed one LBR1 match
      const lbMatchIdx = Math.floor(foundMi/2);
      if (lbR1?.matches[lbMatchIdx]) {
        const slot = foundMi%2===0 ? "t1" : "t2";
        lbR1.matches[lbMatchIdx][slot] = loser;
        const lm = lbR1.matches[lbMatchIdx];
        if (lm.t1 && lm.t2) lm.status = "pending";
      }
    } else {
      // WRn (n>=2) losers drop into odd LB round corresponding to that WB round
      // Odd LB round index = 2*foundRi - 1
      const targetLBRound = 2 * foundRi - 1;
      if (lb[targetLBRound]?.matches[foundMi]) {
        // t2 slot is the drop-in (t1 is LB survivor)
        lb[targetLBRound].matches[foundMi].t2 = loser;
        const lm = lb[targetLBRound].matches[foundMi];
        if (lm.t1 && lm.t2) lm.status = "pending";
      }
    }
  }

  return { wb, lb, gf };
}

// Apply a completed LB match: advance winner further in LB
function applyLBResult(state, matchId, s1, s2, ot) {
  let { wb, lb, gf } = JSON.parse(JSON.stringify(state));

  let foundRi=-1, foundMi=-1, match=null;
  for (let ri=0;ri<lb.length;ri++) for (let mi=0;mi<lb[ri].matches.length;mi++) {
    if (lb[ri].matches[mi].id===matchId) { foundRi=ri; foundMi=mi; match=lb[ri].matches[mi]; }
  }
  if (!match) return state;

  const winner = s1>s2 ? match.t1 : match.t2;
  const diff   = ot ? (s1>s2?1:-1) : s1-s2;
  lb[foundRi].matches[foundMi] = { ...match, s1, s2, winner, loser: s1>s2?match.t2:match.t1, status:"complete", ot, cupDiff:diff };

  if (foundRi < lb.length-1) {
    // Even LB round: winners pair up (pure LB)
    // Odd LB round: winner goes to next even round as t1 (awaits WB drop-in as t2)
    const nextRi = foundRi + 1;
    if (lb[nextRi]) {
      const nextMi = Math.floor(foundMi/2);
      const slot = foundMi%2===0?"t1":"t2";
      if (lb[nextRi].matches[nextMi]) {
        lb[nextRi].matches[nextMi][slot] = winner;
        const nm = lb[nextRi].matches[nextMi];
        if (nm.t1 && nm.t2) nm.status = "pending";
      }
    }
  } else {
    // LB Finals winner → Grand Final t2
    gf.t2 = winner;
    if (gf.t1) gf.status = "pending";
  }

  return { wb, lb, gf };
}

// Apply Grand Final result (handles bracket-reset if LB wins)
function applyGFResult(state, s1, s2, ot, isReset) {
  let { wb, lb, gf } = JSON.parse(JSON.stringify(state));
  const winner = s1>s2 ? gf.t1 : gf.t2;
  const diff   = ot ? (s1>s2?1:-1) : s1-s2;

  if (!isReset && s2>s1) {
    // LB winner won GF1 → bracket reset (true final)
    gf = { ...gf, s1, s2, winner:null, status:"reset", ot,
      reset: { id:uid(), bracket:"GF", t1:gf.t1, t2:gf.t2, s1:null, s2:null, winner:null, status:"pending", ot:false, isReset:true }
    };
  } else {
    gf = { ...gf, s1, s2, winner, status:"complete", ot, cupDiff:diff };
  }
  return { wb, lb, gf };
}

function shuffleArr(a) { const b=[...a]; for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];} return b; }

function genRandomPartner(players) {
  const sh = shuffleArr(players);
  const teams = [];
  for (let i = 0; i + 1 < sh.length; i += 2)
    teams.push({ id: uid(), name: `${sh[i].name} / ${sh[i+1].name}`, playerIds: [sh[i].id, sh[i+1].id] });
  return genSingleElim(teams);
}

// ── RANDOM PARTNER GENERATORS ────────────────────────────────────────────────

// Draw one round of random partners for a player list.
// Minimizes repeat partnerships using a greedy pair-then-opponent approach.
// Returns { matches, partnerCounts } for this round.
function drawOneRPERound(players, partnerCounts, opponentHistory, roundName) {
  const n = players.length;
  const hasOdd = n % 2 !== 0;

  // ── Step 1: pair partners (minimize repeats) ──────────────────────────────
  // Build partner cost matrix, then do a greedy minimum-cost perfect matching
  // by repeatedly picking the cheapest unpaired player.
  let pool = shuffleArr([...players]);
  const paired = new Set();
  const teams = [];

  // If odd number of players, randomly sit one out (bye player rotates)
  let byePlayer = null;
  if (hasOdd) {
    // Prefer the player with the fewest byes so far
    pool.sort((a, b) => (a._byes||0) - (b._byes||0));
    byePlayer = pool[0];
    pool = pool.slice(1);
    byePlayer = { ...byePlayer, _byes: (byePlayer._byes||0) + 1 };
  }

  for (let i = 0; i < pool.length; i++) {
    if (paired.has(pool[i].id)) continue;
    // Find partner with fewest previous pairings with pool[i]
    let bestJ = -1, bestScore = Infinity;
    for (let j = i + 1; j < pool.length; j++) {
      if (paired.has(pool[j].id)) continue;
      const key = [pool[i].id, pool[j].id].sort().join("|");
      const score = partnerCounts[key] || 0;
      if (score < bestScore) { bestScore = score; bestJ = j; }
    }
    if (bestJ === -1) continue;
    const p1 = pool[i], p2 = pool[bestJ];
    const key = [p1.id, p2.id].sort().join("|");
    partnerCounts[key] = (partnerCounts[key] || 0) + 1;
    paired.add(p1.id); paired.add(p2.id);
    teams.push({ id: uid(), name: `${p1.name} / ${p2.name}`, playerIds: [p1.id, p2.id], roundPartners: true });
  }

  // ── Step 2: pair teams as opponents (no shared players, minimize rematches) ─
  const usedTeams = new Set();
  const matches = [];

  for (let ti = 0; ti < teams.length; ti++) {
    if (usedTeams.has(ti)) continue;
    let bestTj = -1, bestScore = Infinity;
    for (let tj = ti + 1; tj < teams.length; tj++) {
      if (usedTeams.has(tj)) continue;
      const t1 = teams[ti], t2 = teams[tj];
      // Skip teams sharing a player
      if (t1.playerIds.some(id => t2.playerIds.includes(id))) continue;
      // Count how many times these exact player-sets have faced each other
      const matchKey = [...t1.playerIds, ...t2.playerIds].sort().join("|");
      const score = opponentHistory[matchKey] || 0;
      if (score < bestScore) { bestScore = score; bestTj = tj; }
    }
    if (bestTj === -1) {
      // Fallback: pair with any team that doesn't share a player
      for (let tj = ti + 1; tj < teams.length; tj++) {
        if (usedTeams.has(tj)) continue;
        if (!teams[ti].playerIds.some(id => teams[tj].playerIds.includes(id))) {
          bestTj = tj; break;
        }
      }
    }
    if (bestTj === -1) {
      // True odd-team-out — give a bye
      matches.push({ id:uid(), t1:teams[ti], t2:null, s1:null, s2:null, status:"bye", winner:teams[ti], ot:false });
      usedTeams.add(ti);
      continue;
    }
    const t1 = teams[ti], t2 = teams[bestTj];
    const matchKey = [...t1.playerIds, ...t2.playerIds].sort().join("|");
    opponentHistory[matchKey] = (opponentHistory[matchKey] || 0) + 1;
    matches.push({ id:uid(), t1, t2, s1:null, s2:null, status:"pending", winner:null, ot:false });
    usedTeams.add(ti); usedTeams.add(bestTj);
  }

  // Bye for the sitting-out player
  if (byePlayer) {
    const byeTeam = { id: uid(), name: byePlayer.name + " (bye)", playerIds: [byePlayer.id], roundPartners: true };
    matches.push({ id:uid(), t1:byeTeam, t2:null, s1:null, s2:null, status:"bye", winner:byeTeam, ot:false });
  }

  return { matches, roundName };
}

// Pure random-partner-each-round (prelims only, standings winner)
function genRandomPartnerEachRound(players, numRounds) {
  numRounds = numRounds || Math.max(3, Math.ceil(players.length / 2));
  const partnerCounts = {};
  const opponentHistory = {};
  const rounds = [];
  for (let ri = 0; ri < numRounds; ri++) {
    const { matches } = drawOneRPERound(players, partnerCounts, opponentHistory, `Round ${ri+1}`);
    rounds.push({ name: `Round ${ri+1}`, matches, roundPartners: true, phase: "prelim" });
  }
  return rounds;
}

// Prelims → Elimination bracket (shared generator)
// Returns bracket object with { type, players, prelims, cutSize, elimType, bracket:null }
// bracket gets populated after all prelims are done
function genRPEWithElim(players, numPrelimRounds, cutSize, elimType) {
  numPrelimRounds = numPrelimRounds || Math.max(3, Math.ceil(players.length / 2));
  cutSize = cutSize || Math.min(8, Math.pow(2, Math.ceil(Math.log2(Math.max(players.length / 2, 2)))));
  const partnerCounts = {};
  const opponentHistory = {};
  const prelims = [];
  for (let ri = 0; ri < numPrelimRounds; ri++) {
    const { matches } = drawOneRPERound(players, partnerCounts, opponentHistory, `Prelim ${ri+1}`);
    prelims.push({ name: `Prelim ${ri+1}`, matches, roundPartners: true, phase: "prelim" });
  }
  return {
    type: elimType === "de" ? "rpe_de" : "rpe_se",
    players,
    prelims,
    cutSize,
    elimType,
    elimBracket: null, // generated after prelims complete
    partnerCounts,
    opponentHistory,
  };
}

// Set Schedule — admin-defined fixed schedule, like a sports league
// Each round has pre-set matchups, no elimination, runs to completion
// Winner determined by final standings (W/L/cup diff)
function genSetSchedule(teams, numRounds) {
  numRounds = numRounds || Math.ceil(teams.length * (teams.length - 1) / 2 / Math.floor(teams.length / 2));
  const rounds = [];
  // Round-robin style schedule to fill numRounds
  const rrRounds = genRoundRobin(teams);
  const taken = rrRounds.slice(0, numRounds);
  taken.forEach((r, i) => {
    rounds.push({ ...r, name: `Week ${i+1}`, scheduleRound: true, matches: r.matches.map(m => ({ ...m, status: m.t1 && m.t2 ? "pending" : "bye" })) });
  });
  return rounds;
}

function getRName(mc) {
  if (mc === 1) return "Championship";
  if (mc === 2) return "Semifinals";
  if (mc === 4) return "Quarterfinals";
  return `Round of ${mc * 2}`;
}

// ── MOCK DATA ────────────────────────────────────────────────────────────────
const INIT_PLAYERS = [
  { id:"p1", name:"Marcus Webb", nick:"Laser", elo:1387, wins:42, losses:11, cupsMade:891, cupsAllowed:534, cupDiff:357, placements:[1,1,2,3], tournaments:8, role:"super_admin",
    matchHistory:[
      {result:"W",opponent:"Trey Jackson",score:"10-6",tournament:"MLCP Spring Classic 2025",eloAfter:1387,eloChange:+12},
      {result:"W",opponent:"Devon Morris",score:"10-3",tournament:"MLCP Spring Classic 2025",eloAfter:1375,eloChange:+8},
      {result:"W",opponent:"Jordan Lee",score:"10-2",tournament:"MLCP Spring Classic 2025",eloAfter:1367,eloChange:+7},
      {result:"L",opponent:"Simone Park",score:"7-10",tournament:"Party Pong Open Vol. 3",eloAfter:1360,eloChange:-7},
      {result:"W",opponent:"Casey Brooks",score:"10-4",tournament:"Party Pong Open Vol. 3",eloAfter:1367,eloChange:+7},
      {result:"W",opponent:"Aaliyah Rose",score:"10-8",tournament:"MLCP Spring Classic 2025",eloAfter:1360,eloChange:+6},
      {result:"W",opponent:"Devon Morris",score:"10-5",tournament:"Party Pong Open Vol. 3",eloAfter:1354,eloChange:+9},
      {result:"W",opponent:"Jordan Lee",score:"9-10",ot:true,tournament:"Party Pong Open Vol. 3",eloAfter:1345,eloChange:+6},
    ]},
  { id:"p2", name:"Destiny Cole", nick:"D-Splash", elo:1354, wins:38, losses:14, cupsMade:812, cupsAllowed:561, cupDiff:251, placements:[1,2,2,4], tournaments:8, role:"tournament_director",
    matchHistory:[
      {result:"W",opponent:"Simone Park",score:"10-7",tournament:"MLCP Spring Classic 2025",eloAfter:1354,eloChange:+10},
      {result:"W",opponent:"Casey Brooks",score:"10-2",tournament:"MLCP Spring Classic 2025",eloAfter:1344,eloChange:+8},
      {result:"L",opponent:"Marcus Webb",score:"6-10",tournament:"MLCP Spring Classic 2025",eloAfter:1336,eloChange:-8},
      {result:"W",opponent:"Aaliyah Rose",score:"10-8",tournament:"Party Pong Open Vol. 3",eloAfter:1344,eloChange:+7},
      {result:"W",opponent:"Jordan Lee",score:"10-5",tournament:"Party Pong Open Vol. 3",eloAfter:1337,eloChange:+7},
      {result:"L",opponent:"Trey Jackson",score:"8-10",tournament:"Party Pong Open Vol. 3",eloAfter:1330,eloChange:-7},
      {result:"W",opponent:"Devon Morris",score:"10-9",ot:true,tournament:"Party Pong Open Vol. 3",eloAfter:1337,eloChange:+5},
    ]},
  { id:"p3", name:"Trey Jackson", nick:"Iceberg", elo:1331, wins:35, losses:16, cupsMade:774, cupsAllowed:588, cupDiff:186, placements:[1,2,3], tournaments:7, role:"player",
    matchHistory:[
      {result:"L",opponent:"Marcus Webb",score:"6-10",tournament:"MLCP Spring Classic 2025",eloAfter:1331,eloChange:-10},
      {result:"W",opponent:"Simone Park",score:"10-8",tournament:"MLCP Spring Classic 2025",eloAfter:1341,eloChange:+9},
      {result:"W",opponent:"Casey Brooks",score:"10-3",tournament:"Party Pong Open Vol. 3",eloAfter:1332,eloChange:+7},
      {result:"W",opponent:"Devon Morris",score:"10-6",tournament:"Party Pong Open Vol. 3",eloAfter:1325,eloChange:+8},
      {result:"W",opponent:"Destiny Cole",score:"10-8",tournament:"Party Pong Open Vol. 3",eloAfter:1317,eloChange:+9},
      {result:"L",opponent:"Jordan Lee",score:"9-10",ot:true,tournament:"MLCP Spring Classic 2025",eloAfter:1308,eloChange:-6},
    ]},
  { id:"p4", name:"Simone Park", nick:"Bullseye", elo:1298, wins:31, losses:18, cupsMade:703, cupsAllowed:601, cupDiff:102, placements:[2,3,4], tournaments:6, role:"player",
    matchHistory:[
      {result:"W",opponent:"Marcus Webb",score:"10-7",tournament:"Party Pong Open Vol. 3",eloAfter:1298,eloChange:+9},
      {result:"L",opponent:"Trey Jackson",score:"8-10",tournament:"MLCP Spring Classic 2025",eloAfter:1289,eloChange:-9},
      {result:"L",opponent:"Destiny Cole",score:"7-10",tournament:"MLCP Spring Classic 2025",eloAfter:1298,eloChange:-10},
      {result:"W",opponent:"Aaliyah Rose",score:"10-6",tournament:"Party Pong Open Vol. 3",eloAfter:1308,eloChange:+7},
      {result:"W",opponent:"Casey Brooks",score:"10-5",tournament:"Party Pong Open Vol. 3",eloAfter:1301,eloChange:+7},
      {result:"L",opponent:"Devon Morris",score:"6-10",tournament:"Party Pong Open Vol. 3",eloAfter:1294,eloChange:-8},
    ]},
  { id:"p5", name:"Devon Morris", nick:"Heat", elo:1277, wins:28, losses:20, cupsMade:651, cupsAllowed:589, cupDiff:62, placements:[3,4], tournaments:5, role:"scorekeeper",
    matchHistory:[
      {result:"L",opponent:"Marcus Webb",score:"3-10",tournament:"MLCP Spring Classic 2025",eloAfter:1277,eloChange:-8},
      {result:"W",opponent:"Simone Park",score:"10-6",tournament:"Party Pong Open Vol. 3",eloAfter:1285,eloChange:+9},
      {result:"W",opponent:"Jordan Lee",score:"10-7",tournament:"MLCP Spring Classic 2025",eloAfter:1276,eloChange:+8},
      {result:"L",opponent:"Trey Jackson",score:"6-10",tournament:"Party Pong Open Vol. 3",eloAfter:1268,eloChange:-8},
      {result:"L",opponent:"Destiny Cole",score:"9-10",ot:true,tournament:"Party Pong Open Vol. 3",eloAfter:1276,eloChange:-5},
    ]},
  { id:"p6", name:"Aaliyah Rose", nick:"Phantom", elo:1244, wins:25, losses:22, cupsMade:589, cupsAllowed:541, cupDiff:48, placements:[4,5], tournaments:5, role:"player",
    matchHistory:[
      {result:"L",opponent:"Destiny Cole",score:"8-10",tournament:"MLCP Spring Classic 2025",eloAfter:1244,eloChange:-7},
      {result:"L",opponent:"Simone Park",score:"6-10",tournament:"Party Pong Open Vol. 3",eloAfter:1251,eloChange:-7},
      {result:"W",opponent:"Jordan Lee",score:"10-9",ot:true,tournament:"Party Pong Open Vol. 3",eloAfter:1258,eloChange:+5},
      {result:"W",opponent:"Casey Brooks",score:"10-4",tournament:"MLCP Spring Classic 2025",eloAfter:1253,eloChange:+6},
      {result:"L",opponent:"Marcus Webb",score:"8-10",tournament:"Party Pong Open Vol. 3",eloAfter:1247,eloChange:-6},
      {result:"W",opponent:"Casey Brooks",score:"10-6",tournament:"Party Pong Open Vol. 3",eloAfter:1253,eloChange:+7},
    ]},
  { id:"p7", name:"Jordan Lee", nick:"Sniper", elo:1198, wins:18, losses:19, cupsMade:421, cupsAllowed:399, cupDiff:22, placements:[5,6], tournaments:4, role:"player",
    matchHistory:[
      {result:"L",opponent:"Marcus Webb",score:"2-10",tournament:"MLCP Spring Classic 2025",eloAfter:1198,eloChange:-7},
      {result:"L",opponent:"Devon Morris",score:"7-10",tournament:"MLCP Spring Classic 2025",eloAfter:1205,eloChange:-8},
      {result:"W",opponent:"Trey Jackson",score:"10-9",ot:true,tournament:"MLCP Spring Classic 2025",eloAfter:1213,eloChange:+8},
      {result:"L",opponent:"Aaliyah Rose",score:"9-10",ot:true,tournament:"Party Pong Open Vol. 3",eloAfter:1205,eloChange:-5},
      {result:"L",opponent:"Destiny Cole",score:"5-10",tournament:"Party Pong Open Vol. 3",eloAfter:1210,eloChange:-7},
      {result:"W",opponent:"Casey Brooks",score:"10-7",tournament:"Party Pong Open Vol. 3",eloAfter:1217,eloChange:+8},
    ]},
  { id:"p8", name:"Casey Brooks", nick:"Splash", elo:1155, wins:12, losses:24, cupsMade:312, cupsAllowed:388, cupDiff:-76, placements:[7,8], tournaments:4, role:"player",
    matchHistory:[
      {result:"L",opponent:"Destiny Cole",score:"2-10",tournament:"MLCP Spring Classic 2025",eloAfter:1155,eloChange:-8},
      {result:"L",opponent:"Trey Jackson",score:"3-10",tournament:"Party Pong Open Vol. 3",eloAfter:1163,eloChange:-7},
      {result:"L",opponent:"Simone Park",score:"5-10",tournament:"Party Pong Open Vol. 3",eloAfter:1170,eloChange:-7},
      {result:"L",opponent:"Aaliyah Rose",score:"4-10",tournament:"MLCP Spring Classic 2025",eloAfter:1177,eloChange:-6},
      {result:"W",opponent:"Devon Morris",score:"10-8",tournament:"MLCP Spring Classic 2025",eloAfter:1183,eloChange:+8},
      {result:"L",opponent:"Jordan Lee",score:"7-10",tournament:"Party Pong Open Vol. 3",eloAfter:1175,eloChange:-8},
      {result:"W",opponent:"Aaliyah Rose",score:"10-8",tournament:"Party Pong Open Vol. 3",eloAfter:1183,eloChange:+8},
    ]},
];

const INIT_TOURNAMENTS = [
  { id:"t1", name:"MLCP Spring Classic 2025", format:"single_elim", teamSize:"2v2", cupCount:10, type:"pro", ranked:true, status:"completed", date:"2025-02-15", venue:"Champions Hall, NYC", teamCount:8, winner:"Webb & Cole", bracket:null },
  { id:"t2", name:"Party Pong Open Vol. 3", format:"double_elim", teamSize:"2v2", cupCount:10, type:"amateur", ranked:false, status:"completed", date:"2025-01-20", venue:"The Pour House, Chicago", teamCount:16, winner:"Jackson & Morris", bracket:null },
  { id:"t3", name:"MLCP Winter Invitational", format:"round_robin", teamSize:"1v1", cupCount:6, type:"pro", ranked:true, status:"upcoming", date:"2025-03-20", venue:"Grand Arena, Miami", teamCount:null, winner:null, bracket:null },
  { id:"t4", name:"Backyard Bash Spring", format:"random_partner", teamSize:"2v2", cupCount:10, type:"amateur", ranked:false, status:"upcoming", date:"2025-04-05", venue:"Riverside Park, Austin", teamCount:null, winner:null, bracket:null },
];

const INIT_VENUES = [
  { id:"v1", name:"Champions Hall", city:"New York, NY", address:"123 Arena Blvd, New York, NY 10001", capacity:200, tables:8, contact:"info@championshall.com", phone:"(212) 555-0101", notes:"Preferred venue for pro events. Air conditioned, full bar.", status:"active" },
  { id:"v2", name:"The Pour House", city:"Chicago, IL", address:"456 Tap St, Chicago, IL 60601", capacity:120, tables:6, contact:"bookings@pourhouse.com", phone:"(312) 555-0202", notes:"Great atmosphere, popular for amateur events.", status:"active" },
  { id:"v3", name:"Grand Arena", city:"Miami, FL", address:"789 Championship Way, Miami, FL 33101", capacity:300, tables:12, contact:"events@grandarena.com", phone:"(305) 555-0303", notes:"Largest venue. Best for invitational and broadcast events.", status:"active" },
  { id:"v4", name:"Riverside Park Pavilion", city:"Austin, TX", address:"321 River Rd, Austin, TX 78701", capacity:80, tables:4, contact:"parks@austin.gov", phone:"(512) 555-0404", notes:"Outdoor venue, weather dependent.", status:"active" },
];

const INIT_SEASONS = [
  {
    id: "s1",
    name: "MLCP 2025 Season",
    year: 2025,
    status: "active", // active | completed | upcoming
    tournamentIds: ["t1", "t3"],
    pointsConfig: {
      win: 3,
      loss: 1,
      cupDiffBonus: 0.1,
      placements: { 1:100, 2:70, 3:50, 4:30, 5:15, 6:10 },
    },
    createdAt: "2025-01-01",
  },
];

const FORMATS = [
  {v:"single_elim",l:"Single Elimination"},{v:"double_elim",l:"Double Elimination"},
  {v:"round_robin",l:"Round Robin"},{v:"set_schedule",l:"Set Schedule"},
  {v:"group_stage",l:"Group Stage → Bracket"},
  {v:"random_partner",l:"Random Partner (Draw Once)"},
  {v:"random_partner_each",l:"Random Partner Each Round"},
  {v:"rpe_to_se",l:"Random Partner Prelims → Single Elim"},
  {v:"rpe_to_de",l:"Random Partner Prelims → Double Elim"},
];
const TEAM_SIZES = ["1v1","2v2","3v3","4v4","6v6","10v10"];
const ROLES = ["super_admin","tournament_director","venue_host","scorekeeper","player"];
const ROLE_COLOR = { super_admin:"gold", tournament_director:"gold", venue_host:"blue", scorekeeper:"", player:"" };

// ── MINI UI KIT ──────────────────────────────────────────────────────────────
const css = {
  card: { background:"var(--card)", border:"1px solid var(--navy-border)", borderRadius:6 },
  input: { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid var(--navy-border)", borderRadius:4, color:"var(--white)", fontSize:14, padding:"8px 12px", outline:"none" },
  select: { width:"100%", background:"var(--navy-light)", border:"1px solid var(--navy-border)", borderRadius:4, color:"var(--white)", fontSize:14, padding:"8px 12px", outline:"none", cursor:"pointer" },
  label: { display:"block", fontSize:10, letterSpacing:2.5, textTransform:"uppercase", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--muted)", marginBottom:5 },
  th: { fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", padding:"8px 12px", textAlign:"left", borderBottom:"1px solid var(--navy-border)", whiteSpace:"nowrap" },
  td: { padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.04)", fontSize:14, verticalAlign:"middle" },
};

function Badge({ color, children }) {
  const bg = color==="gold"?"rgba(201,168,76,0.15)":color==="green"?"rgba(46,204,113,0.15)":color==="red"?"rgba(217,79,61,0.15)":color==="blue"?"rgba(91,141,238,0.15)":"rgba(255,255,255,0.07)";
  const fg = color==="gold"?"var(--gold-light)":color==="green"?"var(--green)":color==="red"?"var(--red)":color==="blue"?"#5b8dee":"var(--muted)";
  return <span style={{ display:"inline-flex",alignItems:"center",padding:"2px 8px",borderRadius:3,fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",background:bg,color:fg }}>{children}</span>;
}

function Btn({ v="primary", sz="md", onClick, children, disabled, full, style={} }) {
  const sizes = { sm:{fontSize:11,padding:"5px 10px"}, md:{fontSize:13,padding:"8px 16px"}, lg:{fontSize:15,padding:"11px 22px"} };
  const variants = { primary:{background:"var(--gold)",color:"var(--navy)"}, danger:{background:"var(--red)",color:"var(--white)"}, ghost:{background:"rgba(255,255,255,0.06)",color:"var(--white)",border:"1px solid var(--navy-border)"}, success:{background:"var(--green)",color:"var(--navy)"}, navy:{background:"var(--navy-light)",color:"var(--white)",border:"1px solid var(--navy-border)"} };
  return <button disabled={disabled} onClick={onClick} style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",border:"none",cursor:"pointer",transition:"all 0.15s",display:"inline-flex",alignItems:"center",gap:6,borderRadius:4,opacity:disabled?0.45:1,width:full?"100%":undefined,...sizes[sz],...variants[v],...style }}>{children}</button>;
}

function Modal({ open, onClose, title, children, width=480 }) {
  const { isMobile } = useBreakpoint();
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.72)",zIndex:1000,display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",padding:isMobile?0:20,backdropFilter:isMobile?"none":"none" }}>
      <div onClick={e=>e.stopPropagation()} className={isMobile?"slide-up":""} style={{ background:"var(--navy-mid)",border:`1px solid var(--navy-border)`,borderRadius:isMobile?"16px 16px 0 0":"8px",width:"100%",maxWidth:isMobile?"100%":width,maxHeight:isMobile?"92vh":"90vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",paddingBottom:isMobile?"max(8px,env(safe-area-inset-bottom))":0 }}>
        {isMobile && <div className="sheet-handle" />}
        <div style={{ padding:isMobile?"10px 20px 12px":"14px 20px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
          <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:17,letterSpacing:1,textTransform:"uppercase" }}>{title}</span>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--muted)",fontSize:20,cursor:"pointer",lineHeight:1,padding:"4px 4px",minHeight:36 }}>✕</button>
        </div>
        <div style={{ padding:20,overflowY:"auto",WebkitOverflowScrolling:"touch",flex:1 }}>{children}</div>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div style={{ marginBottom:14 }}>{label&&<label style={css.label}>{label}</label>}{children}</div>;
}

function StatCard({ num, label, color="var(--gold)", sub }) {
  return (
    <div style={{ ...css.card, padding:16, position:"relative", overflow:"hidden" }}>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:42,letterSpacing:1,lineHeight:1,color:"var(--white)" }}>{num}</div>
      <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginTop:3 }}>{label}</div>
      {sub&&<div style={{ fontSize:11,color:"var(--muted)",marginTop:3 }}>{sub}</div>}
      <div style={{ position:"absolute",bottom:0,left:0,right:0,height:3,background:color }} />
    </div>
  );
}

// ── MOBILE MENU DRAWER ──────────────────────────────────────────────────────
function MobileMenuDrawer({ page, setPage, myRole, theme, setTheme }) {
  const [open, setOpen] = useState(false);
  const swipe = useSwipe({ onLeft: () => setOpen(false) });
  return (
    <>
      <button onClick={()=>setOpen(true)} style={{ background:"none",border:"none",color:"var(--white)",fontSize:22,padding:"4px 10px",cursor:"pointer",flexShrink:0,lineHeight:1,minHeight:44,display:"flex",alignItems:"center" }}>☰</button>
      {open && (
        <div style={{ position:"fixed",inset:0,zIndex:500 }}>
          {/* Backdrop */}
          <div onClick={()=>setOpen(false)} className="drawer-backdrop" />
          {/* Drawer */}
          <div className="side-drawer" {...swipe}>
            <div style={{ padding:"12px 16px 12px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
              <img src={LOGO_URI} alt="MLCP" style={{ height:28,width:"auto",filter:"invert(1) sepia(1) saturate(2) hue-rotate(10deg) brightness(1.1)" }} />
              <button onClick={()=>setOpen(false)} style={{ background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",minHeight:44,minWidth:44,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
            </div>
            <div style={{ overflowY:"auto",WebkitOverflowScrolling:"touch",flex:1 }}>
              {NAV_ITEMS.map(sec => (
                <div key={sec.section} style={{ padding:"8px 0" }}>
                  <div style={{ padding:"3px 16px 6px",fontSize:9,letterSpacing:3,textTransform:"uppercase",color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{sec.section}</div>
                  {sec.items.map(item => {
                    if ((item.id==="admin"||item.id==="users") && myRole==="player") return null;
                    const active = page===item.id;
                    return (
                      <div key={item.id} onClick={()=>{setPage(item.id);setOpen(false);}} style={{ display:"flex",alignItems:"center",gap:12,padding:"11px 16px",cursor:"pointer",background:active?"rgba(201,168,76,0.12)":"transparent",borderLeft:active?"3px solid var(--gold)":"3px solid transparent",color:active?"var(--white)":"var(--muted)",fontSize:15,fontWeight:500,minHeight:48,touchAction:"manipulation" }}>
                        <span style={{ fontSize:16,width:20,textAlign:"center" }}>{item.icon}</span>{item.label}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {/* Theme toggle */}
            {setTheme && (
              <div style={{padding:"12px 16px",borderTop:"1px solid var(--navy-border)"}}>
                <button onClick={()=>setTheme(t=>t==="dark"?"light":"dark")}
                  style={{width:"100%",padding:"10px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid var(--navy-border)",borderRadius:6,cursor:"pointer",fontSize:13,color:"var(--muted)",display:"flex",alignItems:"center",gap:10}}>
                  <span>{theme==="dark"?"☀":"🌙"}</span>
                  <span>{theme==="dark"?"Switch to Light Mode":"Switch to Dark Mode"}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── SIDEBAR ──────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { section:"Main", items:[{id:"dashboard",icon:"⚡",label:"Dashboard"},{id:"tournaments",icon:"🏆",label:"Tournaments"},{id:"bracket",icon:"🎯",label:"Active Bracket"},{id:"spectator",icon:"👁",label:"Spectator View"}] },
  { section:"Events", items:[{id:"registration",icon:"📋",label:"Registration"},{id:"checkin",icon:"✅",label:"Check-In"},{id:"seeding",icon:"🎲",label:"Seeding Tool"},{id:"tables",icon:"🏓",label:"Table Manager"}] },
  { section:"League", items:[{id:"seasons",icon:"🗓",label:"Seasons"},{id:"players",icon:"👤",label:"Players"},{id:"rankings",icon:"📊",label:"Rankings"},{id:"history",icon:"📋",label:"History"},{id:"analytics",icon:"📈",label:"Analytics"}] },
  { section:"Tools", items:[{id:"schedule",icon:"🗓",label:"Schedule Builder"},{id:"seeding",icon:"🔢",label:"Seeding Tool"},{id:"selfreport",icon:"📱",label:"Self-Report Kiosk"},{id:"scoreboard",icon:"📺",label:"Live Scoreboard"},{id:"matchroom",icon:"🎯",label:"Match Room"},{id:"h2h",icon:"⚔️",label:"H2H Lookup"},{id:"predictor",icon:"🎲",label:"Score Predictor"},{id:"tiebreak",icon:"⚖️",label:"Tiebreaker Sim"},{id:"recap",icon:"✨",label:"Event Recap"},{id:"export",icon:"📄",label:"Export / Print"}] },
  { section:"Admin", items:[{id:"venues",icon:"📍",label:"Venues"},{id:"admin",icon:"🔧",label:"Admin Panel"},{id:"users",icon:"👥",label:"User Mgmt"},{id:"notifications",icon:"📣",label:"Notifications"}] },
];

function Sidebar({ page, setPage, myRole, theme, setTheme }) {
  return (
    <div style={{ width:210,background:"var(--navy-mid)",borderRight:"1px solid var(--navy-border)",display:"flex",flexDirection:"column",flexShrink:0,overflow:"auto" }}>
      <div style={{ padding:"14px 16px 12px",borderBottom:"1px solid var(--navy-border)" }}>
        <img src={LOGO_URI} alt="MLCP" style={{ height:30,width:"auto",filter:"invert(1) sepia(1) saturate(2) hue-rotate(10deg) brightness(1.1)",mixBlendMode:"screen",display:"block" }} />
        <div style={{ fontSize:9,letterSpacing:3,color:"var(--gold)",textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginTop:5 }}>Tournament HQ</div>
        <button onClick={()=>setTheme(t=>t==="dark"?"light":"dark")}
          style={{ marginTop:8, background:"none", border:"1px solid var(--navy-border)", borderRadius:12, padding:"3px 12px", cursor:"pointer", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, display:"flex", alignItems:"center", gap:5 }}>
          {theme==="dark"?"☀ Light":"🌙 Dark"}
        </button>
      </div>
      {NAV_ITEMS.map(sec => (
        <div key={sec.section} style={{ padding:"10px 0" }}>
          <div style={{ padding:"3px 16px 7px",fontSize:9,letterSpacing:3,textTransform:"uppercase",color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{sec.section}</div>
          {sec.items.map(item => {
            if ((item.id==="admin"||item.id==="users") && myRole==="player") return null;
            const active = page===item.id;
            return (
              <div key={item.id} onClick={()=>setPage(item.id)} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 16px",cursor:"pointer",background:active?"rgba(201,168,76,0.12)":"transparent",borderLeft:active?"3px solid var(--gold)":"3px solid transparent",color:active?"var(--white)":"var(--muted)",fontSize:14,fontWeight:500,transition:"all 0.15s",userSelect:"none" }}>
                <span style={{ fontSize:15 }}>{item.icon}</span>{item.label}
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ marginTop:"auto",padding:14,borderTop:"1px solid var(--navy-border)" }}>
        <div style={{ fontSize:10,color:"var(--muted)",marginBottom:3,letterSpacing:1 }}>SIGNED IN AS</div>
        <div style={{ fontWeight:600,fontSize:13,marginBottom:5 }}>Commissioner</div>
        <Badge color="gold">{myRole.replace(/_/g," ")}</Badge>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────

function Dashboard({ tournaments, players, seasons, activeTournament, setPage, auditLog, setActiveTournament, setTournaments }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [tick,        setTick]        = useState(0);
  const [feedView,    setFeedView]    = useState("activity"); // activity | matches | app
  const [announcements, setAnnouncements] = useState([
    { id:"ann1", text:"🏆 Winter Invitational registrations now open — limited spots!", type:"event", ts: Date.now()-3600000 },
    { id:"ann2", text:"📋 New rule: disputed scores must be flagged within 5 minutes of match end.", type:"rule", ts: Date.now()-86400000 },
  ]);
  const [dismissedAnn, setDismissedAnn] = useState(new Set());
  const [newAnn, setNewAnn] = useState("");
  const [annType, setAnnType] = useState("info");
  const [showAnnCompose, setShowAnnCompose] = useState(false);
  const [matchExpand, setMatchExpand] = useState(null);

  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 1000); return () => clearInterval(t); }, []);

  // ── Derived data ──────────────────────────────────────────────────────────
  const ranked          = [...players].sort((a,b) => calcPowerRating(b) - calcPowerRating(a));
  const activeSeason    = (seasons||[]).find(s => s.status === "active");
  const seasonStandings = activeSeason ? calcSeasonStandings(activeSeason, tournaments, players) : [];

  const liveEvents      = tournaments.filter(t => t.status === "active");
  const upcomingEvents  = tournaments.filter(t => t.status === "upcoming").sort((a,b) => new Date(a.date||"9999") - new Date(b.date||"9999"));
  const completedEvents = tournaments.filter(t => t.status === "completed");

  const allMatchData       = tournaments.flatMap(t => getAllMatchesFromTournament(t));
  const totalMatchesPlayed = allMatchData.filter(m => m.status === "complete").length;
  const avgElo = players.length ? Math.round(players.reduce((s,p) => s + (p.elo||1200), 0) / players.length) : 1200;

  const activeBktMatches = activeTournament ? getAllMatchesFromTournament(activeTournament) : [];
  const activeTotal    = activeBktMatches.filter(m => m.status !== "bye" && m.status !== "waiting" && m.t1 && m.t2).length;
  const activeDone     = activeBktMatches.filter(m => m.status === "complete").length;
  const activeProgress = activeTotal > 0 ? Math.round(activeDone / activeTotal * 100) : 0;
  const activePending  = activeBktMatches.filter(m => m.status === "pending" && m.t1 && m.t2);
  const activeDisputed = activeBktMatches.filter(m => m.status === "disputed");
  const activeApproval = activeBktMatches.filter(m => m.status === "pending_approval");
  const activeCompleted = activeBktMatches.filter(m => m.status === "complete" && m.t1 && m.t2).slice().reverse();

  const checkInsToday  = (activeTournament?.registrations||[]).filter(r => r.status === "checked_in").length;
  const cupsTodayTotal = activeBktMatches.filter(m => m.status === "complete").reduce((s,m) => s+(m.s1||0)+(m.s2||0), 0);
  const unpaidActive   = (activeTournament?.registrations||[]).filter(r => r.status==="checked_in"&&r.paymentStatus!=="paid"&&r.paymentStatus!=="waived").length;

  // Live winners this event
  const eventPerformers = (() => {
    if (!activeTournament) return [];
    const wins = {};
    activeBktMatches.filter(m => m.status === "complete" && m.winner).forEach(m => {
      const n = m.winner.name; wins[n] = (wins[n]||0) + 1;
    });
    return Object.entries(wins).sort((a,b) => b[1]-a[1]).slice(0,3).map(([name,w]) => ({ name, w }));
  })();

  function daysUntil(dateStr) {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    if (isNaN(diff)) return null;
    return Math.ceil(diff / 86400000);
  }

  function countdown(dateStr) {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    if (diff <= 0 || diff > 86400000) return null;
    const h = Math.floor(diff/3600000), m = Math.floor((diff%3600000)/60000), s = Math.floor((diff%60000)/1000);
    return `${h}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  }

  function actMeta(action) {
    const map = { "check_in":["#5b8dee","🎟"],"score_entered":["var(--green)","⚽"],"score_approved":["var(--gold)","✅"],"dispute":["var(--red)","⚠️"],"tournament_created":["var(--gold)","🏆"],"player_added":["var(--muted)","👤"],"payment":["var(--green)","💵"] };
    return map[action] || ["var(--muted)","·"];
  }

  function timeAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - new Date(ts);
    if (diff < 60000) return "just now";
    if (diff < 3600000) return Math.floor(diff/60000)+"m ago";
    if (diff < 86400000) return Math.floor(diff/3600000)+"h ago";
    return Math.floor(diff/86400000)+"d ago";
  }

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const dateStr = now.toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" });

  // ── QUICK ACTIONS ──────────────────────────────────────────────────────────
  const quickActions = [
    { label:"New Tournament", icon:"🏆", color:"var(--gold)", action:()=>setPage("tournaments") },
    { label:"Check-In",       icon:"🎟", color:"var(--green)", action:()=>setPage("checkin") },
    { label:"Bracket",        icon:"📊", color:"#5b8dee", action:()=>setPage("bracket") },
    { label:"Seeding",        icon:"🎲", color:"var(--muted)", action:()=>setPage("seeding") },
    { label:"Players",        icon:"👥", color:"var(--muted)", action:()=>setPage("players") },
    { label:"Spectator",      icon:"📺", color:"var(--muted)", action:()=>setPage("spectator") },
  ];

  // ── LIVE MATCH ROWS ───────────────────────────────────────────────────────
  function MatchFeedRow({ m, i }) {
    const isLive  = m.status === "pending";
    const isDone  = m.status === "complete";
    const isDisp  = m.status === "disputed";
    const isAppr  = m.status === "pending_approval";
    const dotColor = isDone?"var(--green)":isDisp?"var(--red)":isAppr?"#5b8dee":"rgba(255,255,255,0.2)";
    const dot = isLive ? null : isDone ? "✓" : isDisp ? "⚠" : isAppr ? "●" : null;
    const winnerIs1 = m.winner?.id === m.t1?.id;
    const expanded = matchExpand === (m.id||i);

    return (
      <div
        onClick={() => setMatchExpand(expanded ? null : (m.id||i))}
        style={{ padding:"9px 14px", borderBottom:i>0?"1px solid rgba(255,255,255,0.04)":"none", cursor:"pointer", background:expanded?"rgba(255,255,255,0.02)":"transparent", transition:"background 0.1s" }}
      >
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Status dot */}
          <div style={{ width:8, height:8, borderRadius:"50%", background:dotColor, flexShrink:0, ...(isLive?{ animation:"pulse 1.5s infinite" }:{}) }} />
          {/* Bracket label */}
          <span style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", minWidth:16 }}>
            {m._bracket||""}
          </span>
          {/* Teams + score */}
          <div style={{ flex:1, display:"flex", alignItems:"center", gap:6, minWidth:0 }}>
            <span style={{ fontSize:12, fontWeight:isDone&&winnerIs1?700:400, color:isDone&&winnerIs1?"var(--gold)":"var(--white)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{m.t1?.name||"TBD"}</span>
            {isDone ? (
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:"var(--white)", flexShrink:0 }}>{m.s1}–{m.s2}</span>
            ) : (
              <span style={{ fontSize:10, color:"var(--muted)", flexShrink:0 }}>vs</span>
            )}
            <span style={{ fontSize:12, fontWeight:isDone&&!winnerIs1?700:400, color:isDone&&!winnerIs1?"var(--gold)":"var(--white)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, textAlign:"right" }}>{m.t2?.name||"TBD"}</span>
          </div>
          {/* Status badge */}
          {dot && <span style={{ fontSize:10, color:dotColor, flexShrink:0 }}>{dot}</span>}
          {m.ot && <span style={{ fontSize:9, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, flexShrink:0 }}>OT</span>}
        </div>
        {/* Expanded detail */}
        {expanded && (
          <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid rgba(255,255,255,0.05)", display:"flex", gap:12, justifyContent:"flex-end" }}>
            {isDone && <Btn sz="sm" v="ghost" onClick={e=>{ e.stopPropagation(); setActiveTournament(activeTournament); setPage("bracket"); }}>View Bracket</Btn>}
            {isAppr && <Btn sz="sm" v="success" onClick={e=>{ e.stopPropagation(); setPage("bracket"); }}>Review →</Btn>}
            {isDisp && <Btn sz="sm" v="danger"  onClick={e=>{ e.stopPropagation(); setPage("bracket"); }}>Resolve Dispute →</Btn>}
          </div>
        )}
      </div>
    );
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="fade-in" style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Greeting row ──────────────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:isMobile?24:30, letterSpacing:3, lineHeight:1 }}>{greeting} 👋</div>
          <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>{dateStr} · Major League Cup Pong</div>
        </div>
        {liveEvents.length > 0 ? (
          <div onClick={()=>{ setActiveTournament(liveEvents[0]); setPage("bracket"); }} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 16px", background:"rgba(46,204,113,0.08)", border:"1px solid rgba(46,204,113,0.35)", borderRadius:8, cursor:"pointer" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"var(--green)", animation:"pulse 1.5s infinite", flexShrink:0 }} />
            <div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:10, letterSpacing:2, textTransform:"uppercase", color:"var(--green)" }}>Live Now</div>
              <div style={{ fontSize:13, fontWeight:600 }}>{liveEvents[0].name}</div>
            </div>
          </div>
        ) : upcomingEvents[0] ? (
          <div style={{ textAlign:"right" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:10, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:2 }}>Next Event</div>
            <div style={{ fontSize:13, fontWeight:600 }}>{upcomingEvents[0].name}</div>
            {countdown(upcomingEvents[0].date) ? (
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold)", letterSpacing:2 }}>{countdown(upcomingEvents[0].date)}</div>
            ) : (
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"#5b8dee" }}>
                {daysUntil(upcomingEvents[0].date) !== null ? daysUntil(upcomingEvents[0].date)+" days away" : upcomingEvents[0].date||"Date TBD"}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* ── Quick Actions ──────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${isMobile?3:6},1fr)`, gap:8 }}>
        {quickActions.map(qa => (
          <button key={qa.label} onClick={qa.action}
            style={{ padding:"12px 6px", background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:8, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:5, transition:"all 0.15s" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor=qa.color; e.currentTarget.style.background="rgba(255,255,255,0.04)"; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor="var(--navy-border)"; e.currentTarget.style.background="var(--navy-mid)"; }}>
            <span style={{ fontSize:20 }}>{qa.icon}</span>
            <span style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, color:"var(--muted)", textTransform:"uppercase", textAlign:"center" }}>{qa.label}</span>
          </button>
        ))}
      </div>

      {/* ── Stat cards ────────────────────────────────────────────────── */}
      <div className="resp-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
        <StatCard num={liveEvents.length}    label="Live Now"   color="var(--green)" />
        <StatCard num={players.length}       label="Players"    color="#5b8dee" />
        <StatCard num={totalMatchesPlayed}   label="Matches"    color="var(--gold)" />
        <StatCard num={completedEvents.length} label="Events"   color="var(--muted)" />
      </div>

      {/* ── Announcements banner ────────────────────────────────────────── */}
      {announcements.filter(a => !dismissedAnn.has(a.id)).length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {announcements.filter(a => !dismissedAnn.has(a.id)).map(ann => {
            const colors = { event:"rgba(201,168,76,0.12)", rule:"rgba(91,141,238,0.1)", info:"rgba(46,204,113,0.08)", warn:"rgba(217,79,61,0.1)" };
            const borders = { event:"rgba(201,168,76,0.35)", rule:"rgba(91,141,238,0.35)", info:"rgba(46,204,113,0.3)", warn:"rgba(217,79,61,0.35)" };
            return (
              <div key={ann.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:colors[ann.type]||colors.info, border:`1px solid ${borders[ann.type]||borders.info}`, borderRadius:6 }}>
                <span style={{ flex:1, fontSize:13 }}>{ann.text}</span>
                <span style={{ fontSize:10, color:"var(--muted)", whiteSpace:"nowrap" }}>{timeAgo(ann.ts)}</span>
                <button onClick={() => setDismissedAnn(s => new Set([...s, ann.id]))}
                  style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:14, padding:"0 4px", lineHeight:1 }}>✕</button>
              </div>
            );
          })}
        </div>
      )}
      {/* Compose announcement (admin only) */}
      {(players.find(p=>p.role==="super_admin"||p.role==="tournament_director")) && (
        <div>
          {!showAnnCompose ? (
            <button onClick={()=>setShowAnnCompose(true)} style={{ fontSize:11, color:"var(--muted)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
              + Post Announcement
            </button>
          ) : (
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <select value={annType} onChange={e=>setAnnType(e.target.value)} style={{ ...css.select, width:90, fontSize:11 }}>
                {["event","rule","info","warn"].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              <input value={newAnn} onChange={e=>setNewAnn(e.target.value)} placeholder="Announcement text…"
                style={{ ...css.input, flex:1, fontSize:12 }}
                onKeyDown={e=>{ if(e.key==="Enter"&&newAnn.trim()){ setAnnouncements(a=>[{id:"ann"+Date.now(),text:newAnn.trim(),type:annType,ts:Date.now()},...a]); setNewAnn(""); setShowAnnCompose(false); }}} />
              <Btn sz="sm" onClick={()=>{ if(newAnn.trim()){ setAnnouncements(a=>[{id:"ann"+Date.now(),text:newAnn.trim(),type:annType,ts:Date.now()},...a]); setNewAnn(""); setShowAnnCompose(false); }}}>Post</Btn>
              <Btn sz="sm" v="ghost" onClick={()=>{setShowAnnCompose(false);setNewAnn("");}}>Cancel</Btn>
            </div>
          )}
        </div>
      )}

      {/* ── Hot Streaks leaderboard strip ─────────────────────────────────── */}
      {(() => {
        const streakPlayers = [...players]
          .map(p => {
            const hist = p.matchHistory || [];
            let streak = 0, streakType = null;
            for (let i = hist.length - 1; i >= 0; i--) {
              const w = hist[i].result === "W";
              if (streakType === null) { streakType = w ? "W" : "L"; streak = 1; }
              else if ((w && streakType === "W") || (!w && streakType === "L")) streak++;
              else break;
            }
            return { ...p, streak, streakType };
          })
          .filter(p => p.streak >= 3)
          .sort((a, b) => b.streak - a.streak)
          .slice(0, 6);

        if (streakPlayers.length === 0) return null;
        return (
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase" }}>🔥 Hot Streaks</span>
              <span style={{ fontSize:10, color:"var(--muted)" }}>3+ in a row</span>
            </div>
            <div style={{ display:"flex", gap:0, overflowX:"auto" }}>
              {streakPlayers.map((p, i) => (
                <div key={p.id} style={{ flex:"0 0 auto", padding:"10px 16px", display:"flex", flexDirection:"column", alignItems:"center", gap:4, borderRight:i<streakPlayers.length-1?"1px solid var(--navy-border)":"none", minWidth:80 }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, lineHeight:1, color:p.streakType==="W"?"var(--green)":"var(--red)" }}>{p.streak}</div>
                  <div style={{ fontSize:9, color:p.streakType==="W"?"var(--green)":"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{p.streakType==="W"?"WIN":"LOSS"} STREAK</div>
                  <div style={{ fontSize:11, fontWeight:600, textAlign:"center", maxWidth:70, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.nick||p.name}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Active tournament panel (if any) ──────────────────────────── */}
      {activeTournament && (
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          {/* Header */}
          <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, background:"rgba(255,255,255,0.01)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:activeTournament.status==="active"?"var(--green)":"var(--muted)", animation:activeTournament.status==="active"?"pulse 1.5s infinite":"none" }} />
              <div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, letterSpacing:1 }}>{activeTournament.name}</div>
                <div style={{ fontSize:11, color:"var(--muted)" }}>
                  {activeDone}/{activeTotal} matches · {checkInsToday} checked in{unpaidActive>0?` · ⚠ ${unpaidActive} unpaid`:""}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {activeDisputed.length>0 && <span style={{ fontSize:10, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, background:"rgba(217,79,61,0.1)", border:"1px solid rgba(217,79,61,0.3)", padding:"2px 8px", borderRadius:3 }}>⚠ {activeDisputed.length} DISPUTE{activeDisputed.length>1?"S":""}</span>}
              {activeApproval.length>0 && <span style={{ fontSize:10, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, background:"rgba(91,141,238,0.1)", border:"1px solid rgba(91,141,238,0.3)", padding:"2px 8px", borderRadius:3 }}>● {activeApproval.length} PENDING</span>}
              <Btn sz="sm" onClick={()=>setPage("bracket")}>Open Bracket →</Btn>
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ height:4, background:"rgba(255,255,255,0.05)" }}>
            <div style={{ height:"100%", width:`${activeProgress}%`, background:activeProgress===100?"var(--gold)":"var(--green)", transition:"width 0.4s" }} />
          </div>
          {/* Stats row */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)" }}>
            {[
              [activePending.length,   "Ready to Play", "#5b8dee"],
              [activeDone,             "Completed",     "var(--green)"],
              [activeDisputed.length,  "Disputed",      activeDisputed.length>0?"var(--red)":"var(--muted)"],
              [activeProgress+"%",     "Progress",      activeProgress===100?"var(--gold)":"var(--white)"],
            ].map(([v,l,c]) => (
              <div key={l} style={{ padding:"10px 8px", textAlign:"center", borderRight:"1px solid var(--navy-border)" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
          {/* Event leaders strip */}
          {eventPerformers.length > 0 && (
            <div style={{ padding:"8px 16px", borderTop:"1px solid var(--navy-border)", display:"flex", alignItems:"center", gap:16, background:"rgba(201,168,76,0.03)", flexWrap:"wrap" }}>
              <span style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", flexShrink:0 }}>Hot Right Now</span>
              {eventPerformers.map((p,i) => (
                <div key={p.name} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:14 }}>{["🥇","🥈","🥉"][i]}</span>
                  <span style={{ fontSize:13, fontWeight:600 }}>{p.name}</span>
                  <span style={{ fontSize:11, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{p.w}W</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Two-column: live feed + season leaders ─────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16, alignItems:"start" }}>

        {/* Live Match Feed */}
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          {/* Tab bar */}
          <div style={{ borderBottom:"1px solid var(--navy-border)", display:"flex", gap:0 }}>
            {[
              ["activity",  "Activity",   auditLog?.length||0],
              ["matches",   "Matches",    activeCompleted.length],
              ["approvals", "Approvals",  activeApproval.length + activeDisputed.length],
            ].map(([v,l,badge]) => (
              <button key={v} onClick={()=>setFeedView(v)}
                style={{ flex:1, padding:"9px 8px", background:feedView===v?"rgba(255,255,255,0.04)":"transparent", borderBottom:`2px solid ${feedView===v?"var(--gold)":"transparent"}`, color:feedView===v?"var(--white)":"var(--muted)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", border:"none", borderBottom:`2px solid ${feedView===v?"var(--gold)":"transparent"}`, transition:"all 0.15s" }}>
                {l}{badge>0?` (${badge})`:""}
              </button>
            ))}
          </div>

          {/* Activity feed */}
          {feedView === "activity" && (
            <div style={{ maxHeight:360, overflowY:"auto" }}>
              {(auditLog||[]).length === 0 ? (
                <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--muted)", fontSize:13 }}>No activity yet</div>
              ) : [...(auditLog||[])].reverse().slice(0,20).map((entry, i) => {
                const [color, icon] = actMeta(entry.action);
                return (
                  <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 14px", borderBottom:i<19?"1px solid rgba(255,255,255,0.03)":"none" }}>
                    <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{icon}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{entry.description||entry.action}</div>
                      {entry.detail && <div style={{ fontSize:10, color:"var(--muted)", marginTop:1 }}>{entry.detail}</div>}
                    </div>
                    <div style={{ fontSize:10, color:"var(--muted)", flexShrink:0, whiteSpace:"nowrap" }}>{timeAgo(entry.timestamp)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent matches feed */}
          {feedView === "matches" && (
            <div style={{ maxHeight:360, overflowY:"auto" }}>
              {activeCompleted.length === 0 ? (
                <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--muted)", fontSize:13 }}>No completed matches yet</div>
              ) : activeCompleted.slice(0,15).map((m, i) => (
                <MatchFeedRow key={m.id||i} m={m} i={i} />
              ))}
            </div>
          )}

          {/* Approvals / disputes feed */}
          {feedView === "approvals" && (
            <div style={{ maxHeight:360, overflowY:"auto" }}>
              {activeDisputed.length === 0 && activeApproval.length === 0 ? (
                <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--muted)", fontSize:13 }}>✓ All clear — no pending items</div>
              ) : (
                <>
                  {activeDisputed.map((m,i) => (
                    <div key={m.id||"d"+i} style={{ padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.04)", background:"rgba(217,79,61,0.04)" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                        <div>
                          <div style={{ fontSize:10, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:3 }}>⚠ Disputed</div>
                          <div style={{ fontSize:12 }}>{m.t1?.name} vs {m.t2?.name}</div>
                        </div>
                        <Btn sz="sm" v="danger" onClick={()=>setPage("bracket")}>Resolve →</Btn>
                      </div>
                    </div>
                  ))}
                  {activeApproval.map((m,i) => (
                    <div key={m.id||"a"+i} style={{ padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.04)", background:"rgba(91,141,238,0.04)" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                        <div>
                          <div style={{ fontSize:10, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:3 }}>● Awaiting Approval</div>
                          <div style={{ fontSize:12 }}>{m.t1?.name} <span style={{ color:"var(--muted)" }}>vs</span> {m.t2?.name}</div>
                          {m.pendingScore && <div style={{ fontSize:11, color:"var(--muted)" }}>{m.pendingScore.s1}–{m.pendingScore.s2}</div>}
                        </div>
                        <Btn sz="sm" onClick={()=>setPage("bracket")}>Review →</Btn>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Season standings / Power Rankings */}
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase" }}>
              {activeSeason ? `Season Leaders` : "Power Rankings"}
            </div>
            {activeSeason && <span style={{ fontSize:11, color:"var(--gold)" }}>{activeSeason.name}</span>}
          </div>
          {(seasonStandings.length > 0 ? seasonStandings : ranked.slice(0,8).map((p,i) => ({ player:p, pts:calcPowerRating(p), wins:p.wins||0, losses:p.losses||0, rank:i+1 }))).slice(0,8).map((row, i) => {
            const medals = ["var(--gold)","#C0C0C0","#CD7F32"];
            const bar = Math.max(...(seasonStandings.length>0?seasonStandings:ranked.slice(0,8).map((p,i)=>({pts:calcPowerRating(p)}))).map(r=>r.pts||0));
            const barPct = bar > 0 ? Math.round((row.pts/bar)*100) : 0;
            return (
              <div key={row.player.id||i} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:i<7?"1px solid rgba(255,255,255,0.04)":"none" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:medals[i]||"var(--muted)", width:22, flexShrink:0, textAlign:"center" }}>{row.rank}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.player.name}</div>
                  <div style={{ marginTop:3, height:3, background:"rgba(255,255,255,0.07)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${barPct}%`, background:medals[i]||"rgba(255,255,255,0.2)", borderRadius:2, transition:"width 0.4s" }} />
                  </div>
                </div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:medals[i]||"var(--muted)", flexShrink:0, minWidth:40, textAlign:"right" }}>{row.pts}</div>
              </div>
            );
          })}
          <div style={{ padding:"8px 14px", borderTop:"1px solid var(--navy-border)" }}>
            <button onClick={()=>setPage(activeSeason?"seasons":"rankings")} style={{ fontSize:11, color:"var(--gold)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
              {activeSeason?"Full Season Standings →":"Full Rankings →"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Upcoming events grid ───────────────────────────────────────── */}
      {upcomingEvents.length > 0 && (
        <div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>Upcoming Events</div>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":`repeat(${Math.min(upcomingEvents.length,4)},1fr)`, gap:10 }}>
            {upcomingEvents.slice(0,4).map(t => {
              const days = daysUntil(t.date);
              const ct = countdown(t.date);
              const regs = t.registrations||[];
              const checkedIn = regs.filter(r=>r.status==="checked_in").length;
              return (
                <div key={t.id} onClick={()=>{ setActiveTournament(t); setPage("bracket"); }} style={{ ...css.card, padding:"12px 14px", cursor:"pointer", transition:"all 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="var(--gold)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--navy-border)"}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.name}</div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginBottom:8 }}>{t.date||"TBD"} · {regs.length} teams</div>
                  {ct ? (
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:"var(--gold)", letterSpacing:2, lineHeight:1 }}>{ct}</div>
                  ) : days !== null ? (
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:days<=7?"var(--gold)":"#5b8dee", letterSpacing:2, lineHeight:1 }}>
                      {days===0?"TODAY":days+"d"}
                    </div>
                  ) : null}
                  {checkedIn > 0 && <div style={{ fontSize:10, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, marginTop:4 }}>✓ {checkedIn} checked in</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── TOURNAMENT CREATION WIZARD ───────────────────────────────────────────────
const WIZARD_STEPS = [
  { id:"basics",   label:"Basics",       icon:"📋" },
  { id:"format",   label:"Format",       icon:"🎯" },
  { id:"rules",    label:"Rules",        icon:"⚖️"  },
  { id:"reg",      label:"Registration", icon:"📝" },
  { id:"prizes",   label:"Prizes",       icon:"🏆" },
  { id:"review",   label:"Review",       icon:"✅" },
];

const FORMAT_CARDS = [
  { v:"single_elim",       l:"Single Elimination", icon:"🏆", desc:"Classic bracket — lose once and you're out. Fast and decisive.", best:"Small fields, quick events" },
  { v:"double_elim",       l:"Double Elimination", icon:"🔻", desc:"Two losses to be eliminated. Losers bracket gives a second chance.", best:"Competitive leagues, 8–32 teams" },
  { v:"round_robin",       l:"Round Robin",        icon:"🔄", desc:"Everyone plays everyone. Final standings by wins and cup diff.", best:"Small groups, balanced play" },
  { v:"rpe_to_se",         l:"RPE → Single Elim",  icon:"🎲", desc:"Random partner prelims, then top players seed into SE bracket.", best:"Social events, 16–32 players" },
  { v:"rpe_to_de",         l:"RPE → Double Elim",  icon:"🎲", desc:"Random partner prelims, then top players seed into DE bracket.", best:"Larger pro events, 24–64 players" },
  { v:"random_partner_each",l:"RPE Only",           icon:"🤝", desc:"New random partner every round. Individual standings, no bracket.", best:"Casual socials, 10–30 players" },
  { v:"set_schedule",      l:"Set Schedule",        icon:"📅", desc:"Pre-defined weekly matchups — like a league schedule.", best:"Season leagues, ongoing play" },
];

function TournamentWizard({ onClose, onCreate, venues, players }) {
  const { isMobile } = useBreakpoint();
  const [step, setStep] = useState(0);
  const [dir, setDir]   = useState(1); // 1=forward -1=back
  const [f, setF] = useState({
    // Basics
    name:"", date:"", venue:"", venueCustom:"", description:"",
    // Format
    format:"double_elim", numPrelimRounds:"4", cutSize:"8", numRounds:"4",
    // Rules
    teamSize:"2v2", cupCount:"10", type:"pro", ranked:true,
    entryFee:"", maxOverrides:"3", otEnabled:true, otCups:"2",
    // Registration
    capacity:"", waitlistThreshold:"", regOpen:"", regClose:"",
    requireEmail:true, allowGuests:false, notes:"",
    // Prize & streaming
    prizePool:"", prize1:"", prize2:"", prize3:"",
    streamingEnabled:false, streamUrl:"", twitchChannel:"", youtubeId:"",
    tiebreaker:"h2h",  // h2h | cups | coin
  });
  const upd = (k,v) => setF(x=>({...x,[k]:v}));

  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [created, setCreated] = useState(false);

  async function suggestNames() {
    setLoadingNames(true);
    setNameSuggestions([]);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:300,
          messages:[{ role:"user", content:`Generate 5 creative, exciting tournament names for a beer pong / cup pong event. Format: ${f.format||"competitive"} style. Make them punchy, fun, and memorable. Respond ONLY with a JSON array of 5 strings. No markdown.` }],
        }),
      });
      const data = await res.json();
      const text = data.content?.map(b=>b.text||"").join("")||"";
      const clean = text.replace(/```json|```/g,"").trim();
      setNameSuggestions(JSON.parse(clean));
    } catch {
      setNameSuggestions(["MLCP Spring Classic","The Cup Showdown","Pong Invitational","Bracket Bash","The Eliminator Series"]);
    }
    setLoadingNames(false);
  }

  // Per-step validation
  function canAdvance() {
    if (step === 0) return f.name.trim().length >= 2;
    if (step === 1) return !!f.format;
    return true;
  }

  function goNext() {
    if (!canAdvance()) return;
    setDir(1);
    setStep(s => Math.min(s+1, totalSteps-1));
  }
  function goBack() {
    setDir(-1);
    setStep(s => Math.max(s-1, 0));
  }
  function goTo(i) {
    if (i < step || (i === step+1 && canAdvance())) { setDir(i>step?1:-1); setStep(i); }
  }

  function handleCreate() {
    const venueName = f.venue === "__custom__" ? (f.venueCustom||"") : f.venue;
    onCreate({
      ...f,
      venue: venueName,
      cupCount: parseInt(f.cupCount)||10,
      ranked: f.type==="pro" ? f.ranked : false,
      capacity: f.capacity ? parseInt(f.capacity) : null,
      waitlistThreshold: f.waitlistThreshold ? parseInt(f.waitlistThreshold) : null,
      entryFee: f.entryFee ? parseFloat(f.entryFee) : null,
      maxOverrides: parseInt(f.maxOverrides)||3,
      status: "upcoming", teamCount:null, winner:null, bracket:null,
    });
  }

  // Estimated duration
  const teamCount = f.capacity ? parseInt(f.capacity) : null;
  const fmtCard = FORMAT_CARDS.find(c=>c.v===f.format);

  // ── Step content ────────────────────────────────────────────────────────
  function renderStep() {
    switch (step) {
      // ── STEP 0: BASICS ─────────────────────────────────────────────────
      case 0: return (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <Fld label="Tournament Name *">
            <div style={{ position:"relative" }}>
              <input style={{ ...css.input, paddingRight:100 }} value={f.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. MLCP Spring Classic 2025" autoFocus />
              <button onClick={suggestNames} disabled={loadingNames} style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", background:"rgba(201,168,76,0.15)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:3, color:"var(--gold)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", padding:"3px 8px", whiteSpace:"nowrap" }}>
                {loadingNames?"…":"✨ Suggest"}
              </button>
            </div>
            {f.name.length>0 && f.name.length<2 && <div style={{ fontSize:11, color:"var(--red)", marginTop:3 }}>Name must be at least 2 characters</div>}
          </Fld>
          {/* AI name suggestion chips */}
          {nameSuggestions.length > 0 && (
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {nameSuggestions.map((n,i)=>(
                <div key={i} onClick={()=>upd("name",n)} style={{ padding:"4px 12px", background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:20, fontSize:12, color:"var(--gold-light)", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, transition:"all 0.1s" }}>{n}</div>
              ))}
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Fld label="Event Date">
              <input style={css.input} type="date" value={f.date} onChange={e=>upd("date",e.target.value)} />
            </Fld>
            <Fld label="Venue">
              {(venues||[]).filter(v=>v.status==="active").length > 0 ? (
                <>
                  <select style={css.select} value={f.venue} onChange={e=>upd("venue",e.target.value)}>
                    <option value="">— Select a venue —</option>
                    {(venues||[]).filter(v=>v.status==="active").map(v=>(
                      <option key={v.id} value={v.name}>{v.name} · {v.city} ({v.tables||"?"} tables)</option>
                    ))}
                    <option value="__custom__">+ Enter custom venue…</option>
                  </select>
                  {f.venue==="__custom__" && <input style={{ ...css.input, marginTop:6 }} value={f.venueCustom||""} onChange={e=>upd("venueCustom",e.target.value)} placeholder="Venue name, city" />}
                </>
              ) : (
                <input style={css.input} value={f.venue} onChange={e=>upd("venue",e.target.value)} placeholder="e.g. The Pong Palace, Chicago" />
              )}
            </Fld>
          </div>
          {/* Event type visual pills */}
          <Fld label="Event Type">
            <div style={{ display:"flex", gap:8 }}>
              {[["pro","🏅 Professional","Ranked, Elo tracking, leaderboards"],["amateur","🎉 Amateur","Casual / party pong, no rankings"]].map(([v,l,desc])=>(
                <div key={v} onClick={()=>{ upd("type",v); if(v!=="pro") upd("ranked",false); }} style={{ flex:1, padding:"10px 12px", border:`2px solid ${f.type===v?"var(--gold)":"var(--navy-border)"}`, background:f.type===v?"rgba(201,168,76,0.08)":"rgba(255,255,255,0.02)", borderRadius:6, cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:f.type===v?"var(--gold)":"var(--white)" }}>{l}</div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginTop:3, lineHeight:1.4 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Fld>
          <Fld label="Description / Notes (optional)">
            <textarea style={{ ...css.input, minHeight:60, resize:"vertical" }} value={f.description} onChange={e=>upd("description",e.target.value)} placeholder="Any details players should know…" />
          </Fld>
        </div>
      );

      // ── STEP 1: FORMAT ──────────────────────────────────────────────────
      case 1: return (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${isMobile?1:2},1fr)`, gap:10 }}>
            {FORMAT_CARDS.map(fc=>{
              const sel = f.format===fc.v;
              return (
                <div key={fc.v} onClick={()=>upd("format",fc.v)} style={{ padding:"12px 14px", border:`2px solid ${sel?"var(--gold)":"var(--navy-border)"}`, background:sel?"rgba(201,168,76,0.07)":"var(--navy-mid)", borderRadius:8, cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                    <span style={{ fontSize:18 }}>{fc.icon}</span>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:sel?"var(--gold)":"var(--white)" }}>{fc.l}</span>
                    {sel && <span style={{ marginLeft:"auto", fontSize:12, color:"var(--gold)" }}>✓</span>}
                  </div>
                  <div style={{ fontSize:12, color:"var(--muted)", lineHeight:1.5, marginBottom:5 }}>{fc.desc}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>Best for: {fc.best}</div>
                </div>
              );
            })}
          </div>

          {/* Format-specific config */}
          {(f.format==="rpe_to_se"||f.format==="rpe_to_de"||f.format==="random_partner_each") && (
            <div style={{ padding:"14px 16px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.2)", borderRadius:6 }}>
              <div style={{ fontSize:12, color:"var(--muted)", marginBottom:12, lineHeight:1.6 }}>
                🎲 Players get a new random partner every prelim round.
                {(f.format==="rpe_to_se"||f.format==="rpe_to_de") && <> After prelims, the top <strong style={{color:"var(--white)"}}>{f.cutSize||8}</strong> advance to a <strong style={{color:"var(--white)"}}>{f.format==="rpe_to_de"?"Double":"Single"} Elim</strong> bracket.</>}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <Fld label="Prelim Rounds">
                  <input style={css.input} type="number" min="1" max="20" value={f.numPrelimRounds} onChange={e=>upd("numPrelimRounds",e.target.value)} />
                </Fld>
                {(f.format==="rpe_to_se"||f.format==="rpe_to_de") && (
                  <Fld label="Cut to Top…">
                    <select style={css.select} value={f.cutSize} onChange={e=>upd("cutSize",e.target.value)}>
                      {[2,4,8,16].map(n=><option key={n} value={n}>Top {n}</option>)}
                    </select>
                  </Fld>
                )}
              </div>
            </div>
          )}
          {f.format==="set_schedule" && (
            <Fld label="Number of Weeks / Rounds">
              <input style={css.input} type="number" min="1" max="30" value={f.numRounds} onChange={e=>upd("numRounds",e.target.value)} />
            </Fld>
          )}
        </div>
      );

      // ── STEP 2: RULES ───────────────────────────────────────────────────
      case 2: return (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <Fld label="Team Size">
            <div style={{ display:"flex", gap:6 }}>
              {TEAM_SIZES.map(sz=>(
                <div key={sz} onClick={()=>upd("teamSize",sz)} style={{ flex:1, padding:"9px 4px", textAlign:"center", borderRadius:4, border:`1px solid ${f.teamSize===sz?"var(--gold)":"var(--navy-border)"}`, background:f.teamSize===sz?"rgba(201,168,76,0.1)":"transparent", cursor:"pointer", fontSize:13, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:f.teamSize===sz?"var(--gold-light)":"var(--muted)", transition:"all 0.15s" }}>{sz}</div>
              ))}
            </div>
          </Fld>
          <Fld label="Cup Count">
            <div style={{ display:"flex", gap:6 }}>
              {[["6","6 Cups","Short"],["10","10 Cups","Standard"],["15","15 Cups","Long"],["21","21 Cups","Pro"]].map(([v,l,sub])=>(
                <div key={v} onClick={()=>upd("cupCount",v)} style={{ flex:1, padding:"8px 4px", textAlign:"center", borderRadius:4, border:`1px solid ${f.cupCount===v?"var(--gold)":"var(--navy-border)"}`, background:f.cupCount===v?"rgba(201,168,76,0.1)":"transparent", cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:f.cupCount===v?"var(--gold)":"var(--white)", lineHeight:1 }}>{v}</div>
                  <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, marginTop:2 }}>{sub}</div>
                </div>
              ))}
            </div>
          </Fld>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Fld label="Entry Fee ($)">
              <input style={css.input} type="number" min="0" step="0.01" value={f.entryFee} onChange={e=>upd("entryFee",e.target.value)} placeholder="0 = free" />
            </Fld>
            <Fld label="Overtime Cups (0=disabled)">
              <input style={css.input} type="number" min="0" max="10" value={f.otEnabled?f.otCups:"0"} onChange={e=>{ const v=e.target.value; upd("otEnabled",parseInt(v)>0); upd("otCups",v); }} />
            </Fld>
          </div>

          {/* Rule toggles */}
          {[
            ["requireEmail","📧 Require email on registration","Players must provide an email to register"],
            ["allowGuests","🚪 Allow walk-up / guest registration","Players without accounts can register at the door"],
          ].map(([key,label,desc])=>(
            <div key={key} onClick={()=>upd(key,!f[key])} style={{ padding:"10px 14px", background:"rgba(255,255,255,0.02)", border:`1px solid ${f[key]?"rgba(46,204,113,0.3)":"var(--navy-border)"}`, borderRadius:5, display:"flex", alignItems:"center", gap:12, cursor:"pointer", transition:"all 0.15s" }}>
              <div style={{ width:36, height:20, borderRadius:10, background:f[key]?"var(--green)":"var(--navy-border)", flexShrink:0, position:"relative", transition:"background 0.2s" }}>
                <div style={{ position:"absolute", top:2, left:f[key]?18:2, width:16, height:16, borderRadius:"50%", background:"var(--white)", transition:"left 0.2s" }} />
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{label}</div>
                <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>{desc}</div>
              </div>
              <div style={{ marginLeft:"auto", fontSize:11, color:f[key]?"var(--green)":"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{f[key]?"ON":"OFF"}</div>
            </div>
          ))}

          {/* Ranked toggle */}
          <div style={{ padding:"12px 16px", background:"rgba(255,255,255,0.03)", border:`1px solid ${f.ranked&&f.type==="pro"?"rgba(201,168,76,0.3)":"var(--navy-border)"}`, borderRadius:6, display:"flex", alignItems:"flex-start", gap:12, opacity:f.type!=="pro"?0.5:1 }}>
            <input type="checkbox" id="ranked-wizard" checked={!!f.ranked} disabled={f.type!=="pro"} onChange={e=>upd("ranked",e.target.checked)} style={{ width:16, height:16, marginTop:2, cursor:f.type==="pro"?"pointer":"not-allowed", accentColor:"var(--gold)", flexShrink:0 }} />
            <label htmlFor="ranked-wizard" style={{ cursor:f.type==="pro"?"pointer":"not-allowed" }}>
              <div style={{ fontWeight:700, fontSize:13 }}>Count toward player rankings</div>
              <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>Elo ratings and standings will update after this event. Pro events only.</div>
            </label>
          </div>
        </div>
      );

      // ── STEP 3: REGISTRATION ────────────────────────────────────────────
      case 3: return (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Fld label="Capacity (0 = unlimited)">
              <input style={css.input} type="number" min="0" value={f.capacity} onChange={e=>upd("capacity",e.target.value)} placeholder="e.g. 32" />
            </Fld>
            <Fld label="Auto-Waitlist After (0=off)">
              <input style={css.input} type="number" min="0" value={f.waitlistThreshold} onChange={e=>upd("waitlistThreshold",e.target.value)} placeholder="e.g. 28" />
            </Fld>
            <Fld label="Registration Opens">
              <input style={css.input} type="datetime-local" value={f.regOpen} onChange={e=>upd("regOpen",e.target.value)} />
            </Fld>
            <Fld label="Registration Closes">
              <input style={css.input} type="datetime-local" value={f.regClose} onChange={e=>upd("regClose",e.target.value)} />
            </Fld>
          </div>

          {/* Toggle options */}
          {[
            ["requireEmail","Require email address on registration","Players must provide an email to register"],
            ["allowGuests","Allow walk-up / guest registration","Players without accounts can register at the door"],
          ].map(([key, label, desc])=>(
            <div key={key} onClick={()=>upd(key,!f[key])} style={{ padding:"12px 16px", background:"rgba(255,255,255,0.03)", border:`1px solid ${f[key]?"rgba(201,168,76,0.3)":"var(--navy-border)"}`, borderRadius:6, display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer", transition:"border-color 0.15s" }}>
              <div style={{ width:44, height:24, borderRadius:12, background:f[key]?"var(--green)":"var(--navy-border)", transition:"background 0.2s", position:"relative", flexShrink:0, marginTop:1 }}>
                <div style={{ position:"absolute", top:2, left:f[key]?20:2, width:20, height:20, borderRadius:"50%", background:"var(--white)", transition:"left 0.2s" }} />
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:13 }}>{label}</div>
                <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{desc}</div>
              </div>
            </div>
          ))}

          <Fld label="Internal Notes (staff only)">
            <textarea style={{ ...css.input, minHeight:56, resize:"vertical" }} value={f.notes} onChange={e=>upd("notes",e.target.value)} placeholder="e.g. Setup crew arrives at 5pm, concessions by table 4…" />
          </Fld>
        </div>
      );

      // ── STEP 4: REVIEW ──────────────────────────────────────────────────
      case 4: return (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{...css.card,padding:"16px 18px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:12}}>🏆 Prize Pool (optional)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,display:"block",marginBottom:5}}>TOTAL PRIZE POOL</label>
                  <input style={{...css.input,width:"100%"}} value={f.prizePool} onChange={e=>upd("prizePool",e.target.value)} placeholder="e.g. $1,000" />
                </div>
                {[["1st 🥇","prize1","var(--gold)"],["2nd 🥈","prize2","#c0c0c0"],["3rd 🥉","prize3","#cd7f32"]].map(([label,field,color])=>(
                  <div key={field}>
                    <label style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,display:"block",marginBottom:5}}>{label}</label>
                    <input style={{...css.input,width:"100%",borderColor:f[field]?color+"66":"var(--navy-border)"}} value={f[field]} onChange={e=>upd(field,e.target.value)} placeholder="e.g. $500" />
                  </div>
                ))}
              </div>
            </div>
            <div style={{...css.card,padding:"16px 18px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:f.streamingEnabled?12:0}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)"}}>📺 Streaming Mode</div>
                <div onClick={()=>upd("streamingEnabled",!f.streamingEnabled)} style={{width:36,height:20,borderRadius:10,background:f.streamingEnabled?"var(--green)":"rgba(255,255,255,0.1)",position:"relative",cursor:"pointer",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:f.streamingEnabled?17:3,width:14,height:14,borderRadius:"50%",background:"var(--white)",transition:"left 0.2s"}} />
                </div>
              </div>
              {f.streamingEnabled && (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
                  <input style={{...css.input,width:"100%"}} value={f.streamUrl} onChange={e=>upd("streamUrl",e.target.value)} placeholder="Stream URL (https://twitch.tv/...)" />
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <input style={css.input} value={f.twitchChannel} onChange={e=>upd("twitchChannel",e.target.value)} placeholder="Twitch channel" />
                    <input style={css.input} value={f.youtubeId} onChange={e=>upd("youtubeId",e.target.value)} placeholder="YouTube live ID" />
                  </div>
                </div>
              )}
            </div>
            <div style={{...css.card,padding:"16px 18px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:10}}>⚖️ Tiebreaker Rule</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[["h2h","Head-to-Head","⚔️"],["cups","Cup Diff","🎯"],["coin","Coin Flip","🪙"]].map(([v,l,icon])=>(
                  <div key={v} onClick={()=>upd("tiebreaker",v)} style={{flex:1,padding:"10px 14px",borderRadius:6,background:f.tiebreaker===v?"rgba(201,168,76,0.12)":"var(--navy-mid)",border:`1px solid ${f.tiebreaker===v?"var(--gold)":"var(--navy-border)"}`,cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}>
                    <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
                    <div style={{fontSize:12,fontWeight:700,color:f.tiebreaker===v?"var(--gold)":"var(--white)"}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          <div style={{...css.card,padding:"16px 18px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:10}}>📝 Custom Rules (optional)</div>
              <textarea style={{...css.input,width:"100%",minHeight:72,resize:"vertical",fontFamily:"inherit",fontSize:13,lineHeight:1.5}}
                value={f.customRules||""}
                onChange={e=>upd("customRules",e.target.value)}
                placeholder="e.g. No island rule · Redemption allowed · Re-rack at 4-cup only · Side bounce illegal…" />
              <div style={{fontSize:10,color:"var(--muted)",marginTop:5}}>Displayed to all players and referees during the event</div>
            </div>
          </div>
        );
        case 5: {
        const venueName = f.venue==="__custom__"?(f.venueCustom||"—"):f.venue||"—";
        const fmtLabel = FORMAT_CARDS.find(c=>c.v===f.format)?.l||f.format;
        const durationMap = { single_elim:"1.5–2 hrs", double_elim:"2.5–4 hrs", round_robin:"2–3 hrs", rpe_to_se:"3–5 hrs", rpe_to_de:"4–6 hrs", random_partner_each:"2–3 hrs", set_schedule:"Ongoing" };
        const estDuration = durationMap[f.format]||"2–3 hrs";
        const sections = [
          { title:"Event Basics", stepIdx:0, items:[
            ["Name", f.name],
            ["Date", f.date||"Not set"],
            ["Venue", venueName],
            ["Type", f.type==="pro"?"Professional":"Amateur"],
            f.description && ["Notes", f.description],
          ].filter(Boolean)},
          { title:"Format", stepIdx:1, items:[
            ["Format", fmtLabel],
            (f.format==="rpe_to_se"||f.format==="rpe_to_de"||f.format==="random_partner_each") && ["Prelim Rounds", f.numPrelimRounds],
            (f.format==="rpe_to_se"||f.format==="rpe_to_de") && ["Cut to", `Top ${f.cutSize}`],
            f.format==="set_schedule" && ["Weeks", f.numRounds],
            ["Est. Duration", estDuration],
          ].filter(Boolean)},
          { title:"Rules", stepIdx:2, items:[
            ["Team Size", f.teamSize],
            ["Cups", f.cupCount],
            ["Ranked", f.ranked&&f.type==="pro"?"Yes":"No"],
            ["Entry Fee", f.entryFee?`$${parseFloat(f.entryFee).toFixed(2)}`:"Free"],
            ["Overtime", f.otEnabled?`${f.otCups} cups`:"Disabled"],
          ]},
          { title:"Registration", stepIdx:3, items:[
            ["Capacity", f.capacity?f.capacity:"Unlimited"],
            ["Waitlist at", f.waitlistThreshold?f.waitlistThreshold:"Off"],
            ["Reg Opens", f.regOpen?new Date(f.regOpen).toLocaleString():"Not set"],
            ["Reg Closes", f.regClose?new Date(f.regClose).toLocaleString():"Not set"],
            ["Allow Guests", f.allowGuests?"Yes":"No"],
          ]},
        ];
        return (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ padding:"18px 20px", background:"linear-gradient(135deg,rgba(201,168,76,0.1),rgba(91,141,238,0.05))", border:"1px solid rgba(201,168,76,0.25)", borderRadius:8, textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:isMobile?24:32, letterSpacing:3, lineHeight:1, color:"var(--gold)" }}>{f.name}</div>
              {f.date && <div style={{ fontSize:13, color:"var(--muted)", marginTop:5, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5 }}>{f.date}{venueName!=="—"?` · ${venueName}`:""}</div>}
              <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:10, flexWrap:"wrap" }}>
                <Badge color={f.type==="pro"?"gold":""}>{f.type}</Badge>
                {f.ranked&&f.type==="pro" && <Badge color="blue">Ranked</Badge>}
                <Badge color="">{fmtLabel}</Badge>
                <Badge color="">{f.teamSize}</Badge>
                {f.entryFee && <Badge color="gold">${parseFloat(f.entryFee).toFixed(2)} entry</Badge>}
                <Badge color="">⏱ {estDuration}</Badge>
              </div>
            </div>

            {sections.map(sec=>(
              <div key={sec.title} style={{ ...css.card, padding:0, overflow:"hidden" }}>
                <div style={{ padding:"9px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:1.5, textTransform:"uppercase", color:"var(--muted)" }}>{sec.title}</span>
                  <button onClick={()=>{ setDir(-1); setStep(sec.stepIdx); }} style={{ background:"none", border:"none", color:"var(--gold)", fontSize:11, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>Edit ✏️</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:`repeat(${isMobile?2:3},1fr)`, gap:0 }}>
                  {sec.items.map(([k,v])=>(
                    <div key={k} style={{ padding:"9px 14px", borderBottom:"1px solid rgba(255,255,255,0.04)", borderRight:"1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:2 }}>{k}</div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ padding:"12px 16px", background:"rgba(46,204,113,0.05)", border:"1px solid rgba(46,204,113,0.2)", borderRadius:6 }}>
              <div style={{ fontSize:11, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>What Happens Next</div>
              {[["1","Go to Registration to add teams and open sign-ups"],["2","Use Check-In to mark arrivals on event day"],["3","Click ▶ Start on Tournaments to generate the bracket"]].map(([n,t])=>(
                <div key={n} style={{ display:"flex", gap:8, alignItems:"flex-start", marginBottom:5 }}>
                  <div style={{ width:18, height:18, borderRadius:"50%", background:"rgba(46,204,113,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontFamily:"'Bebas Neue',sans-serif", color:"var(--green)", flexShrink:0, marginTop:1 }}>{n}</div>
                  <div style={{ fontSize:12, color:"var(--muted)", lineHeight:1.5 }}>{t}</div>
                </div>
              ))}
            </div>
            {/* ── Bracket Structure Preview ───────────────────────── */}
            {(() => {
              const cap = parseInt(f.capacity) || 16;
              const n = Math.pow(2, Math.ceil(Math.log2(Math.max(cap, 2))));
              const isDE = f.format === "double_elim" || f.format === "rpe_to_de";
              const isSE = f.format === "single_elim" || f.format === "rpe_to_se";
              const isRR = f.format === "round_robin";
              if (!isSE && !isDE && !isRR) return null;
              const wRounds = Math.log2(n);
              const lRounds = isDE ? (wRounds - 1) * 2 : 0;
              const totalM  = isDE ? n * 2 - 1 : isSE ? n - 1 : (cap * (cap - 1)) / 2;
              const minsPerMatch = 12;
              const tables = parseInt(f.capacity) > 0 ? Math.max(2, Math.floor(parseInt(f.capacity)/4)) : 4;
              const estMins = Math.ceil(totalM / tables) * minsPerMatch;
              const hh = Math.floor(estMins/60), mm = estMins%60;
              return (
                <div style={{ ...css.card, padding:"14px 16px", marginTop:4 }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)", marginBottom:10 }}>
                    Bracket Structure Preview
                  </div>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:12 }}>
                    {[
                      ["Format", f.format==="double_elim"?"Double Elim":f.format==="single_elim"?"Single Elim":"Round Robin"],
                      ["Bracket Size", `${n} teams`],
                      ["Total Matches", totalM],
                      [isDE?"W + L Rounds":"Rounds", isDE?`${wRounds}W + ${lRounds}L`:`${wRounds}`],
                      ["Est. Time", `${hh}h ${mm}m`],
                      ["Tables Assumed", tables],
                    ].map(([l,v])=>(
                      <div key={l}>
                        <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--white)", lineHeight:1 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {/* Round flow diagram */}
                  <div style={{ display:"flex", gap:4, alignItems:"center", flexWrap:"wrap" }}>
                    {Array.from({length:wRounds},(_,i)=>(
                      <React.Fragment key={i}>
                        <div style={{ padding:"3px 8px", borderRadius:3, background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.25)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--gold)", whiteSpace:"nowrap" }}>
                          {i===wRounds-1?"Finals":i===wRounds-2?"Semi":i===wRounds-3?"Quarters":`WB R${i+1}`}
                        </div>
                        {i<wRounds-1&&<span style={{color:"var(--muted)",fontSize:10}}>→</span>}
                      </React.Fragment>
                    ))}
                    {isDE && <span style={{ color:"var(--muted)", fontSize:10, margin:"0 4px" }}>|</span>}
                    {isDE && Array.from({length:lRounds},(_,i)=>(
                      <React.Fragment key={i}>
                        <div style={{ padding:"3px 8px", borderRadius:3, background:"rgba(217,79,61,0.08)", border:"1px solid rgba(217,79,61,0.2)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--red)", whiteSpace:"nowrap" }}>
                          LB R{i+1}
                        </div>
                        {i<lRounds-1&&<span style={{color:"var(--muted)",fontSize:10}}>→</span>}
                      </React.Fragment>
                    ))}
                    {isDE && <><span style={{color:"var(--muted)",fontSize:10}}>→</span><div style={{ padding:"3px 8px", borderRadius:3, background:"rgba(201,168,76,0.2)", border:"1px solid rgba(201,168,76,0.5)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--gold)" }}>GF</div></>}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      }

      default: return null;
    }
  }

  return (
    <Modal open={true} onClose={onClose} title="" width={620}>
      {/* Step progress bar */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          {WIZARD_STEPS.map((s, i) => {
            const done    = i < step;
            const current = i === step;
            const locked  = i > step;
            const color   = done?"var(--green)":current?"var(--gold)":"var(--muted)";
            return (
              <React.Fragment key={s.id}>
                <div onClick={()=>goTo(i)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, cursor:locked?"default":"pointer", flex:1 }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:done?"var(--green)":current?"var(--gold)":"var(--navy-border)", border:`2px solid ${color}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:done?12:11, color:done||current?"var(--navy)":"var(--muted)", transition:"all 0.2s" }}>
                    {done ? "✓" : s.icon}
                  </div>
                  {!isMobile && <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color, textAlign:"center", whiteSpace:"nowrap" }}>{s.label}</div>}
                </div>
                {i < WIZARD_STEPS.length-1 && (
                  <div style={{ flex:2, height:2, background:i<step?"var(--green)":"var(--navy-border)", transition:"background 0.3s", marginBottom:isMobile?0:18 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step header */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:2, color:"var(--gold)", lineHeight:1 }}>
          {WIZARD_STEPS[step].icon} {WIZARD_STEPS[step].label}
        </div>
        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
          {["Event name, date and location","Choose your bracket format","Set the rules and scoring","Registration settings","Review everything before creating"][step]}
        </div>
      </div>

      {/* Step content */}
      <div style={{ minHeight:240 }}>
        {renderStep()}
      </div>

      {/* Navigation */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:24, paddingTop:16, borderTop:"1px solid var(--navy-border)" }}>
        <div>
          {step > 0 && <Btn v="ghost" onClick={goBack}>← Back</Btn>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>Step {step+1} of {totalSteps}</span>
          {isLast ? (
            <Btn v="success" onClick={handleCreate} disabled={!f.name}>🏆 Create Tournament</Btn>
          ) : (
            <Btn onClick={goNext} disabled={!canAdvance()}>Next →</Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── TOURNAMENTS ──────────────────────────────────────────────────────────────
function TournamentsPage({ tournaments, setTournaments, players, setActiveTournament, setPage, seasons, venues }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [showCreate,       setShowCreate]       = useState(false);
  const [detailTournament, setDetailTournament] = useState(null);
  const [tournSearch,      setTournSearch]      = useState("");
  const [statusFilter,     setStatusFilter]     = useState("all");
  const [selectedTIds,     setSelectedTIds]     = useState(new Set());
  const [bulkMode,         setBulkMode]         = useState(false);

  function toggleSelectT(id) { setSelectedTIds(prev=>{const s=new Set(prev);s.has(id)?s.delete(id):s.add(id);return s;}); }
  function archiveTournaments(ids) {
    setTournaments(prev=>prev.map(t=>ids.has(t.id)?{...t,archived:true,status:"archived"}:t));
    setSelectedTIds(new Set());
    setBulkMode(false);
  }
  function deleteTournaments(ids) {
    if(!window.confirm("Delete "+ids.size+" tournament(s)? This cannot be undone.")) return;
    setTournaments(prev=>prev.filter(t=>!ids.has(t.id)));
    setSelectedTIds(new Set());
    setBulkMode(false);
  }

  function handleWizardCreate(data) {
    const t = { id:uid(), ...data, status:"upcoming", teamCount:null, winner:null, bracket:null };
    setTournaments(p=>[t,...p]);
    setShowCreate(false);
  }

  function cloneTournament(t) {
    const cloned = {
      ...t,
      id: uid(),
      name: t.name + " (Copy)",
      status: "upcoming",
      date: "",
      bracket: null,
      winner: null,
      teamCount: null,
      finalized: false,
      registrations: [],
      createdAt: new Date().toISOString(),
    };
    setTournaments(p=>[cloned,...p]);
  }

  function startTournament(t) {
    const regs = (t.registrations||[]).filter(r=>r.status==="checked_in"||r.status==="registered");
    const players8 = regs.length >= 2
      ? regs.map(r=>({ id:r.id, name:r.teamName, playerId:r.players?.[0]?.name }))
      : players.slice(0,8);
    const soloPl = players.slice(0,8);
    const teams = players8.map(p=>({ id:uid(), name:p.name, playerId:p.id }));
    const numPrelims = parseInt(t.numPrelimRounds)||4;
    const cutSize = parseInt(t.cutSize)||8;
    let bracket;
    if (t.format==="round_robin")           bracket = { type:"rr",  rounds: genRoundRobin(teams) };
    else if (t.format==="double_elim")      bracket = { type:"de",  ...genDoubleElim(teams) };
    else if (t.format==="random_partner")   bracket = { type:"rp",  rounds: genRandomPartner(soloPl), partnerHistory:[] };
    else if (t.format==="random_partner_each") bracket = { type:"rpe", rounds: genRandomPartnerEachRound(soloPl, numPrelims), players: soloPl };
    else if (t.format==="rpe_to_se")        bracket = genRPEWithElim(soloPl, numPrelims, cutSize, "se");
    else if (t.format==="rpe_to_de")        bracket = genRPEWithElim(soloPl, numPrelims, cutSize, "de");
    else if (t.format==="set_schedule")     bracket = { type:"ss",  rounds: genSetSchedule(teams, t.numRounds||4) };
    else                                    bracket = { type:"se",  rounds: genSingleElim(teams) };
    const updated = { ...t, status:"active", teamCount:teams.length, bracket, registeredTeams:teams };
    setTournaments(p=>p.map(x=>x.id===t.id?updated:x));
    setActiveTournament(updated);
    setPage("bracket");
  }

  return (
    <div className="fade-in">
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>Tournaments</div>
        <Btn onClick={()=>setShowCreate(true)}>{isMobile?"+ New":"+ Create Tournament"}</Btn>
      </div>

      {/* KPI row */}
      {(() => {
        const kpis = [
          [tournaments.length, "Total", "var(--muted)"],
          [tournaments.filter(t=>t.status==="active").length, "Active", "var(--green)"],
          [tournaments.filter(t=>t.status==="upcoming").length, "Upcoming", "#5b8dee"],
          [tournaments.filter(t=>t.status==="completed").length, "Completed", "var(--gold)"],
          [tournaments.filter(t=>t.finalized).length, "Finalized", "var(--gold)"],
        ];
        return (
          <div style={{ display:"flex",gap:8,marginBottom:14,flexWrap:"wrap" }}>
            {kpis.map(([v,l,c])=>(
              <div key={l} style={{ padding:"6px 14px",background:"var(--card)",border:"1px solid var(--navy-border)",borderRadius:5,display:"flex",gap:8,alignItems:"center" }}>
                <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:c,lineHeight:1 }}>{v}</span>
                <span style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase" }}>{l}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Search + status filter */}
      <div style={{ display:"flex",gap:8,marginBottom:16,flexWrap:"wrap" }}>
        <input style={{ ...css.input,flex:1,minWidth:160,maxWidth:340 }}
          placeholder="🔍 Search tournaments…"
          value={tournSearch} onChange={e=>setTournSearch(e.target.value)} />
        <div style={{ display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden" }}>
          {[["all","All"],["upcoming","Upcoming"],["active","Active"],["completed","Done"],["archived","Archived"]].map(([v,l])=>(
            <button key={v} onClick={()=>setStatusFilter(v)}
              style={{ padding:"6px 12px",background:statusFilter===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:statusFilter===v?"var(--gold)":"var(--muted)",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,cursor:"pointer",borderRight:v!=="archived"?"1px solid var(--navy-border)":"none" }}>{l}</button>
          ))}
        <Btn sz="sm" v={bulkMode?"primary":"ghost"} onClick={()=>{setBulkMode(b=>!b);setSelectedTIds(new Set());}} style={{marginLeft:"auto",fontSize:10}}>
          {bulkMode?"✕ Cancel":"☑ Bulk"}
        </Btn>
        {bulkMode && selectedTIds.size>0 && (
          <>
            <Btn sz="sm" v="ghost" onClick={()=>archiveTournaments(selectedTIds)} style={{color:"var(--muted)",fontSize:10}}>📦 Archive {selectedTIds.size}</Btn>
            <Btn sz="sm" v="danger" onClick={()=>deleteTournaments(selectedTIds)} style={{fontSize:10}}>✕ Delete {selectedTIds.size}</Btn>
          </>
        )}
        </div>
      </div>

      {/* ── Mobile card list ── */}
      {isMobile ? (
        <div className="mob-card-list">
          {tournaments.length === 0 ? (
            <div style={{ textAlign:"center",padding:"48px 20px",color:"var(--muted)" }}>
              No tournaments yet — tap <strong>+ New</strong> to get started
            </div>
          ) : (() => {
            const filtered = tournaments.filter(t => {
              const q = tournSearch.toLowerCase();
              const matchSearch = !q || t.name?.toLowerCase().includes(q) || t.venue?.toLowerCase().includes(q) || t.format?.toLowerCase().includes(q);
              const matchStatus = statusFilter === "all" ? (t.status !== "archived") : t.status === statusFilter;
              return matchSearch && matchStatus;
            });
            return filtered.length === 0 ? (
              <div style={{ textAlign:"center",padding:"32px 16px",color:"var(--muted)",fontSize:13 }}>No tournaments match your search</div>
            ) : filtered.map(t => {
            const fmtLabel = FORMATS.find(ff=>ff.v===t.format)?.l||t.format;
            const season = (seasons||[]).find(s=>(s.tournamentIds||[]).includes(t.id));
            return (
              <div key={t.id} className="mob-card" style={{ borderLeft:`3px solid ${t.status==="active"?"var(--green)":t.status==="completed"?"var(--gold)":"var(--navy-border)"}` }}>
                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:6 }}>
                  <div>
                    <div style={{ fontWeight:700,fontSize:15,lineHeight:1.2 }}>{t.name}</div>
                    {t.venue && <div style={{ fontSize:12,color:"var(--muted)",marginTop:2 }}>📍 {t.venue}</div>}
                    {t.date && <div style={{ fontSize:12,color:"var(--muted)",marginTop:1 }}>📅 {t.date}</div>}
                  </div>
                  <Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge>
                </div>
                <div style={{ display:"flex",gap:5,flexWrap:"wrap",marginBottom:10 }}>
                  <Badge color={t.type==="pro"?"gold":""}>{t.type}</Badge>
                  {t.ranked&&<Badge color="blue">Ranked</Badge>}
                  <Badge color="">{fmtLabel}</Badge>
                  <Badge color="">{t.teamSize}</Badge>
                  {season&&<Badge color="blue">{season.name}</Badge>}
                </div>
                <div style={{ display:"flex",gap:8 }}>
                  {t.status==="upcoming" && <Btn sz="sm" v="success" full onClick={()=>startTournament(t)}>▶ Start</Btn>}
                  {t.status==="active" && <Btn sz="sm" v="primary" full onClick={()=>{ setActiveTournament(t); setPage("bracket"); }}>View Bracket</Btn>}
                  {t.status==="completed" && <Btn sz="sm" v="ghost" full>View Results</Btn>}
                  <Btn sz="sm" v="ghost" onClick={()=>cloneTournament(t)} title="Clone as template">⧉ Clone</Btn>
                </div>
              </div>
            );
          });
          })()}
        </div>
      ) : (
        /* ── Desktop table ── */
        <div style={{ ...css.card, overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse",minWidth:600 }}>
            <thead><tr>{["Event","Format","Type","Size","Cups","Date","Status","Actions"].map(h=><th key={h} style={css.th}>{h}</th>)}</tr></thead>
            <tbody>
              {tournaments.length === 0 ? (
                <tr><td colSpan={8} style={{ ...css.td, textAlign:"center", padding:"40px 20px", color:"var(--muted)" }}>
                  No tournaments yet — click <strong>+ Create Tournament</strong> to get started
                </td></tr>
              ) : tournaments.filter(t=>{
                const q=tournSearch.toLowerCase();
                const matchSearch=!q||t.name?.toLowerCase().includes(q)||t.venue?.toLowerCase().includes(q);
                const matchStatus=statusFilter==="all"?(t.status!=="archived"):t.status===statusFilter;
                return matchSearch&&matchStatus;
              }).map(t=>{
              const isSel=selectedTIds.has(t.id);
              return(<tr key={t.id} onClick={()=>{if(bulkMode){toggleSelectT(t.id);}else setDetailTournament(t);}}
                style={{background:isSel?"rgba(201,168,76,0.07)":"transparent"}} style={{ cursor:"pointer" }}>
                <td style={css.td}>
                  <div style={{ fontWeight:600 }}>{t.name}</div>
                  <div style={{ fontSize:11,color:"var(--muted)",marginTop:1 }}>{t.venue}</div>
                  {(()=>{ const s=(seasons||[]).find(s=>(s.tournamentIds||[]).includes(t.id)); return s?<div style={{marginTop:3}}><Badge color="blue">{s.name}</Badge></div>:null; })()}
                </td>
                <td style={css.td}><span style={{ fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,letterSpacing:1 }}>{FORMATS.find(ff=>ff.v===t.format)?.l||t.format}</span></td>
                <td style={css.td}><div style={{display:"flex",gap:4,flexWrap:"wrap"}}><Badge color={t.type==="pro"?"gold":""}>{t.type}</Badge>{t.ranked&&<Badge color="blue">Ranked</Badge>}</div></td>
                <td style={css.td}>{t.teamSize}</td>
                <td style={css.td}>{t.cupCount}</td>
                <td style={{ ...css.td,color:"var(--muted)",fontSize:13 }}>{t.date||"—"}</td>
                <td style={css.td}><Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge></td>
                <td style={css.td}>
                  <div style={{ display:"flex",gap:6 }}>
                    {t.status==="upcoming" && <Btn sz="sm" v="success" onClick={()=>startTournament(t)}>▶ Start</Btn>}
                    {t.status==="active" && <Btn sz="sm" v="ghost" onClick={()=>{ setActiveTournament(t); setPage("bracket"); }}>View</Btn>}
                    {t.status==="completed" && <Btn sz="sm" v="ghost">Results</Btn>}
                    <Btn sz="sm" v="ghost" onClick={()=>cloneTournament(t)} title="Clone as template">⧉</Btn>
                    {t.status!=="archived"
                      ? <Btn sz="sm" v="ghost" onClick={e=>{e.stopPropagation();setTournaments(p=>p.map(x=>x.id===t.id?{...x,status:"archived",archived:true}:x));}} title="Archive" style={{color:"var(--muted)",fontSize:10}}>📦</Btn>
                      : <Btn sz="sm" v="ghost" onClick={e=>{e.stopPropagation();setTournaments(p=>p.map(x=>x.id===t.id?{...x,status:"completed",archived:false}:x));}} title="Unarchive" style={{color:"var(--muted)",fontSize:10}}>↩</Btn>
                    }
                  </div>
                </td>
              </tr>
              );})}
          </tbody>
        </table>
      </div>
      )}
      {showCreate && <TournamentWizard onClose={()=>setShowCreate(false)} onCreate={handleWizardCreate} venues={venues} players={players} />}

      {/* ── Tournament Detail Modal ───────────────────────────────────────── */}
      <Modal open={!!detailTournament} onClose={()=>setDetailTournament(null)} title="" width={680}>
        {detailTournament && (() => {
          const t = detailTournament;
          const allM = getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2);
          const regs = t.registrations||[];
          const fmtLabel = FORMATS.find(f=>f.v===t.format)?.l||t.format||"?";
          const fee = parseFloat(t.entryFee)||0;
          const revenue = fee*regs.filter(r=>r.paymentStatus==="paid").length;

          // Winner / placements from registrations
          const placements = regs
            .filter(r=>r.placement)
            .sort((a,b)=>a.placement-b.placement)
            .slice(0,4);

          // Team stats from matches
          const teamStats = {};
          allM.forEach(m=>{
            [["t1","s1","s2"],["t2","s2","s1"]].forEach(([side,myS,oppS])=>{
              const name = m[side]?.name;
              if(!name) return;
              if(!teamStats[name]) teamStats[name]={w:0,l:0,cups:0,allowed:0};
              const won = m.winner?.name===name;
              teamStats[name].w += won?1:0;
              teamStats[name].l += won?0:1;
              teamStats[name].cups   += m[myS]||0;
              teamStats[name].allowed+= m[oppS]||0;
            });
          });
          const ranked = Object.entries(teamStats).sort((a,b)=>b[1].w-a[1].w||b[1].cups-b[1].allowed-(a[1].cups-a[1].allowed));

          // OT + blowout counts
          const otCount = allM.filter(m=>m.ot).length;
          const blowouts = allM.filter(m=>Math.abs((m.s1||0)-(m.s2||0))>=7).length;
          const avgCups = allM.length?(allM.reduce((s,m)=>(m.s1||0)+(m.s2||0)+s,0)/allM.length).toFixed(1):0;

          return (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {/* Header banner */}
              <div style={{ padding:"16px 20px", background:"linear-gradient(135deg,rgba(201,168,76,0.08),rgba(91,141,238,0.04))", border:"1px solid rgba(201,168,76,0.2)", borderRadius:7, textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:3, color:"var(--gold)", lineHeight:1, marginBottom:6 }}>{t.name}</div>
                <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
                  <Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge>
                  {t.type==="pro"&&<Badge color="gold">PRO</Badge>}
                  {t.ranked&&<Badge color="blue">RANKED</Badge>}
                  <Badge>{fmtLabel}</Badge>
                  {t.date&&<span style={{fontSize:11,color:"var(--muted)"}}>{t.date}</span>}
                  {t.venue&&<span style={{fontSize:11,color:"var(--muted)"}}>{t.venue}</span>}
                </div>
              </div>

              {/* KPI row */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:8 }}>
                {[
                  [regs.length,"Teams","var(--white)"],
                  [allM.length,"Matches","var(--white)"],
                  [otCount,"OT Games","var(--gold)"],
                  [blowouts,"Blowouts","var(--red)"],
                  [avgCups,"Avg Cups/M","var(--green)"],
                  revenue>0?[`$${revenue}`,"Revenue","var(--green)"]:[regs.filter(r=>r.status==="checked_in").length,"Checked In","var(--green)"],
                ].map(([v,l,c])=>(
                  <div key={l} style={{...css.card,padding:"10px 12px",textAlign:"center"}}>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:24,color:c,lineHeight:1}}>{v}</div>
                    <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Placements / podium */}
              {placements.length>0&&(
                <div style={{...css.card,padding:"12px 16px"}}>
                  <div style={{fontSize:10,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>🏆 Placements</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {placements.map((r,i)=>{
                      const medal=["🥇","🥈","🥉","4️⃣"][i]||`${r.placement}.`;
                      return(
                        <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 14px",background:i===0?"rgba(201,168,76,0.1)":"rgba(255,255,255,0.03)",border:`1px solid ${i===0?"rgba(201,168,76,0.3)":"var(--navy-border)"}`,borderRadius:5}}>
                          <span style={{fontSize:16}}>{medal}</span>
                          <span style={{fontWeight:700,fontSize:13}}>{r.teamName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Team standings table */}
              {ranked.length>0&&(
                <div style={{...css.card,padding:0,overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>
                    Final Standings ({ranked.length} teams)
                  </div>
                  <div className="resp-table">
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:380}}>
                      <thead><tr>{["#","Team","W","L","Cups","Allowed","Diff"].map(h=><th key={h} style={css.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {ranked.map(([name,s],i)=>(
                          <tr key={name} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                            <td style={{...css.td,color:"var(--muted)",fontSize:11}}>{i+1}</td>
                            <td style={{...css.td,fontWeight:i<3?700:400}}>{name}</td>
                            <td style={{...css.td,color:"var(--green)"}}>{s.w}</td>
                            <td style={{...css.td,color:"var(--red)"}}>{s.l}</td>
                            <td style={css.td}>{s.cups}</td>
                            <td style={css.td}>{s.allowed}</td>
                            <td style={{...css.td,color:s.cups-s.allowed>0?"var(--green)":s.cups-s.allowed<0?"var(--red)":"var(--muted)",fontWeight:700}}>{s.cups-s.allowed>0?"+":""}{s.cups-s.allowed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent match log (last 8) */}
              {allM.length>0&&(
                <details>
                  <summary style={{cursor:"pointer",fontSize:12,color:"var(--muted)",padding:"6px 0"}}>Match Log ({allM.length} matches)</summary>
                  <div style={{...css.card,padding:0,overflow:"hidden",marginTop:8}}>
                    {allM.slice(-8).reverse().map((m,i)=>(
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 14px",borderBottom:i<Math.min(allM.length,8)-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                        <span style={{flex:1,fontSize:12,fontWeight:600}}>{m.t1?.name}</span>
                        <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--gold)",minWidth:60,textAlign:"center"}}>{m.s1}–{m.s2}{m.ot?" OT":""}</span>
                        <span style={{flex:1,fontSize:12,textAlign:"right"}}>{m.t2?.name}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                {t.status==="active"&&<Btn onClick={()=>{setActiveTournament(t);setPage("bracket");setDetailTournament(null);}}>▶ Open Bracket</Btn>}
                <Btn v="ghost" onClick={()=>setDetailTournament(null)}>Close</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

// ── MATCH CARD ───────────────────────────────────────────────────────────────
function MatchCard({ match, onScore, onSelfReport, accentColor="#c9a84c", showSeed=false, onInlineScore=null, inlineScoreId=null, ils1="", ils2="", ilsOt=false, setIls1=null, setIls2=null, setIlsOt=null, onInlineSubmit=null, onTableAssign=null, onMatchNote=null, onRefAssign=null }) {
  const isPending  = match.status === "pending";
  const isComplete = match.status === "complete" || match.status === "bye";
  const isApproval = match.status === "pending_approval";
  const isDisputed = match.status === "disputed";
  const isBye      = match.status === "bye";
  const isWaiting  = match.status === "waiting" || (!match.t1 && !match.t2 && !isPending);
  const isReset    = match.isReset;
  const ps         = match.pendingScore;
  const ps2        = match.pendingScore2; // second reporter's score (disputed)

  const borderColor = isDisputed  ? "rgba(217,79,61,0.6)"
    : isApproval                  ? "rgba(91,141,238,0.5)"
    : isComplete && !isBye        ? "rgba(46,204,113,0.25)"
    : isPending && match.t1 && match.t2 ? accentColor + "bb"
    : "rgba(255,255,255,0.06)";

  const cardBg = isDisputed   ? "rgba(217,79,61,0.04)"
    : isApproval              ? "rgba(91,141,238,0.04)"
    : isComplete && !isBye    ? "rgba(46,204,113,0.03)"
    : "var(--navy-mid)";

  if (!match) return null;

  return (
    <div style={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 6, overflow: "hidden", opacity: isWaiting ? 0.35 : 1, width: "100%", transition: "border-color 0.2s, box-shadow 0.2s", boxShadow: isPending && match.t1 && match.t2 ? `0 0 8px ${accentColor}22` : "none" }}>

      {/* Top banners */}
      {isReset && (
        <div style={{ padding: "2px 10px", background: "rgba(217,79,61,0.15)", fontSize: 9, color: "var(--red)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 2, textAlign: "center", textTransform: "uppercase" }}>🔁 Bracket Reset</div>
      )}
      {/* Table assignment badge + match notes indicator */}
      {(match.tableAssignment || match.note || match.referee) && (
        <div style={{ display:"flex", gap:6, padding:"2px 8px", background:"rgba(255,255,255,0.02)", borderBottom:"1px solid rgba(255,255,255,0.04)", flexWrap:"wrap" }}>
          {match.tableAssignment && (
            <span style={{ fontSize:9, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>🪑 Table {match.tableAssignment}</span>
          )}
          {match.note && (
            <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }} title={match.note}>📝 Note</span>
          )}
          {match.referee && (
            <span style={{ fontSize:9, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>👤 {match.referee}</span>
          )}
        </div>
      )}
      {isApproval && (
        <div style={{ padding: "3px 10px", background: "rgba(91,141,238,0.12)", borderBottom: "1px solid rgba(91,141,238,0.3)", fontSize: 9, color: "#5b8dee", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textAlign: "center", textTransform: "uppercase" }}>⏳ AWAITING APPROVAL</div>
      )}
      {isDisputed && (
        <div style={{ padding: "3px 10px", background: "rgba(217,79,61,0.12)", borderBottom: "1px solid rgba(217,79,61,0.3)", fontSize: 9, color: "var(--red)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textAlign: "center", textTransform: "uppercase" }}>⚠ SCORE DISPUTED — {ps&&ps2?`${ps.s1}–${ps.s2} vs ${ps2.s1}–${ps2.s2}`:"scores conflict"}</div>
      )}

      {/* Team rows */}
      {[{ team: match.t1, score: match.s1, pendScore: ps?.s1, pendScore2: ps2?.s1 }, { team: match.t2, score: match.s2, pendScore: ps?.s2, pendScore2: ps2?.s2 }].map((side, i) => {
        const isW = side.team && match.winner && (side.team?.name === match.winner?.name || side.team?.id === match.winner?.id);
        const showPend = (isApproval || isDisputed) && ps && side.pendScore !== undefined;
        const showConflict = isDisputed && ps2 && side.pendScore2 !== undefined && side.pendScore !== side.pendScore2;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 10px", borderBottom: i === 0 ? "1px solid rgba(255,255,255,0.07)" : "none", background: isW ? `${accentColor}14` : "transparent", minHeight: 38 }}>
            {showSeed && side.team?.seed && (
              <div style={{ width: 18, height: 18, borderRadius: 3, background: "rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontFamily: "'Bebas Neue',sans-serif", color: "var(--muted)", marginRight: 6, flexShrink: 0 }}>{side.team.seed}</div>
            )}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: isW ? 700 : 500, fontSize: 13, color: side.team ? (isW ? "var(--gold-light)" : "var(--white)") : "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {side.team?.name || (isBye && i === 1 ? "— BYE —" : isWaiting ? "TBD" : "TBD")}
              </div>
            </div>
            {isW && isComplete && <span style={{ fontSize: 11, marginRight: 4, flexShrink: 0 }}>✓</span>}
            {side.team?.guest && <span style={{ flexShrink: 0, background: "rgba(255,180,0,0.15)", color: "#ffb400", border: "1px solid rgba(255,180,0,0.25)", borderRadius: 2, padding: "0 4px", fontSize: 9, fontWeight: 700, letterSpacing: 1, marginRight: 4 }}>G</span>}
            {/* Disputed: show both scores with conflict highlight */}
            {showPend && showConflict ? (
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 6 }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "#5b8dee" }}>{side.pendScore}</div>
                <div style={{ fontSize: 9, color: "var(--red)" }}>≠</div>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "var(--red)", opacity: 0.7 }}>{side.pendScore2}</div>
              </div>
            ) : showPend ? (
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, color: "#5b8dee", marginLeft: 6, minWidth: 22, textAlign: "right", flexShrink: 0 }}>{side.pendScore}</div>
            ) : (side.score !== null && side.score !== undefined) ? (
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, color: isW ? accentColor : isComplete ? "var(--muted)" : "rgba(255,255,255,0.3)", marginLeft: 6, minWidth: 22, textAlign: "right", flexShrink: 0 }}>{side.score}</div>
            ) : null}
          </div>
        );
      })}

      {match.ot && isComplete && (
        <div style={{ padding: "2px 10px", background: "rgba(201,168,76,0.07)", fontSize: 9, color: "var(--gold)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textAlign: "center", borderTop: "1px solid rgba(201,168,76,0.12)", textTransform: "uppercase" }}>⭐ Overtime · ±1 Cup Diff</div>
      )}
      {match.correction && (
        <div style={{ padding: "2px 10px", background: "rgba(201,168,76,0.06)", fontSize: 9, color: "var(--muted)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.04)", textTransform: "uppercase" }}>✏ Score corrected</div>
      )}

      {(isPending || isApproval || isDisputed) && (onScore || onSelfReport) && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex" }}>
          {/* Inline score entry */}
          {isPending && match.t1 && match.t2 && inlineScoreId === match.id && setIls1 && (
            <div style={{ padding:"6px 8px", background:"rgba(201,168,76,0.06)", borderTop:"1px solid rgba(201,168,76,0.2)", display:"flex", gap:5, alignItems:"center", width:"100%" }}>
              <input autoFocus type="number" min="0" max="99" value={ils1} onChange={e=>setIls1(e.target.value)}
                style={{ width:36, textAlign:"center", background:"rgba(255,255,255,0.07)", border:"1px solid var(--navy-border)", borderRadius:3, color:"var(--white)", padding:"4px 0", fontSize:13, fontWeight:700 }} />
              <span style={{ color:"var(--muted)", fontSize:11 }}>–</span>
              <input type="number" min="0" max="99" value={ils2} onChange={e=>setIls2(e.target.value)}
                style={{ width:36, textAlign:"center", background:"rgba(255,255,255,0.07)", border:"1px solid var(--navy-border)", borderRadius:3, color:"var(--white)", padding:"4px 0", fontSize:13, fontWeight:700 }} />
              <label style={{ display:"flex", alignItems:"center", gap:3, fontSize:10, color:"var(--muted)", cursor:"pointer" }}>
                <input type="checkbox" checked={ilsOt} onChange={e=>setIlsOt(e.target.checked)} style={{accentColor:"var(--gold)"}} />OT
              </label>
              <button onClick={onInlineSubmit} disabled={!ils1||!ils2}
                style={{ marginLeft:"auto", padding:"4px 8px", background:"var(--gold)", border:"none", borderRadius:3, color:"var(--navy)", fontSize:10, fontWeight:900, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1 }}>✓</button>
              <button onClick={()=>onInlineScore(null)} style={{ padding:"4px 6px", background:"none", border:"none", color:"var(--muted)", fontSize:12, cursor:"pointer" }}>✕</button>
            </div>
          )}
          {isPending && onScore && (
            <button onClick={onScore} style={{ flex: 1, padding: "6px 6px", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", color: accentColor, border: "none", borderRight: onSelfReport ? "1px solid rgba(255,255,255,0.06)" : "none", background: `${accentColor}0e`, outline: "none", transition: "background 0.15s" }}>📋 Enter Score</button>
          )}
          {isPending && onSelfReport && (
            <button onClick={onSelfReport} style={{ flex: 1, padding: "6px 6px", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", color: "var(--muted)", border: "none", background: "transparent", outline: "none", transition: "background 0.15s" }}>📱 Self-Report</button>
          )}
          {(isApproval || isDisputed) && onScore && (
            <button onClick={onScore} style={{ flex: 1, padding: "6px", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", color: isDisputed ? "var(--red)" : "#5b8dee", border: "none", background: isDisputed ? "rgba(217,79,61,0.08)" : "rgba(91,141,238,0.08)", outline: "none" }}>
              {isDisputed ? "⚠ Resolve Dispute" : "✓ Review & Approve"}
            </button>
          )}
        </div>
      )}
      {/* Table assign + note buttons */}
      {(onTableAssign || onMatchNote) && (
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.04)", display:"flex" }}>
          {onTableAssign && (
            <button onClick={onTableAssign}
              style={{ flex:1, padding:"5px 4px", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", color:match.tableAssignment?"var(--gold)":"var(--muted)", border:"none", background:"transparent", outline:"none" }}>
              🪑 {match.tableAssignment?"T-"+match.tableAssignment:"Assign Table"}
            </button>
          )}
          {onRefAssign && (
            <button onClick={onRefAssign}
              style={{ flex:1, padding:"5px 4px", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", color:match.referee?"#5b8dee":"var(--muted)", border:"none", background:"transparent", outline:"none" }}>
              👤 {match.referee||"Ref"}
            </button>
          )}
          {onMatchNote && (
            <button onClick={onMatchNote}
              style={{ flex:1, padding:"5px 4px", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", color:match.note?"#5b8dee":"var(--muted)", border:"none", background:"transparent", outline:"none" }}>
              📝 {match.note?"Note ✓":"Add Note"}
            </button>
          )}
        </div>
      )}
      {/* Correct completed score */}
      {isComplete && !isBye && onScore && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
          <button onClick={onScore} style={{ width: "100%", padding: "4px", fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", color: "var(--muted)", border: "none", background: "transparent", outline: "none" }}>✏ Correct Score</button>
        </div>
      )}
    </div>
  );
}

// ── MATCH TICKER + RECENT SCORES ─────────────────────────────────────────────

// Scrolling live ticker strip for top of BracketPage
function MatchTicker({ matches, onClickMatch }) {
  const liveMatches = matches.filter(m =>
    m.status === "pending" && m.t1 && m.t2
  );
  const approvalMatches = matches.filter(m =>
    m.status === "pending_approval" || m.status === "disputed"
  );
  const recentDone = matches.filter(m => m.status === "complete")
    .slice(-6).reverse();

  if (liveMatches.length === 0 && approvalMatches.length === 0 && recentDone.length === 0) return null;

  const allItems = [
    ...approvalMatches.map(m => ({ ...m, _tickerType: "approval" })),
    ...liveMatches.map(m => ({ ...m, _tickerType: "live" })),
    ...recentDone.map(m => ({ ...m, _tickerType: "done" })),
  ];

  return (
    <div style={{ marginBottom: 14, background: "var(--navy-mid)", border: "1px solid var(--navy-border)", borderRadius: 6, overflow: "hidden" }}>
      {/* Header strip */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", borderBottom: "1px solid var(--navy-border)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: liveMatches.length > 0 ? "var(--green)" : "var(--muted)", animation: liveMatches.length > 0 ? "pulse 1.5s infinite" : "none", flexShrink: 0 }} />
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--muted)" }}>
          Live Matches
        </span>
        <span style={{ fontSize: 10, color: "var(--green)", fontWeight: 700 }}>{liveMatches.length} in play</span>
        {approvalMatches.filter(m=>m.status==="disputed").length > 0 && (
          <span style={{ fontSize: 10, color: "var(--red)", fontWeight: 700, marginLeft: 4 }}>· {approvalMatches.filter(m=>m.status==="disputed").length} disputed</span>
        )}
        {approvalMatches.filter(m=>m.status==="pending_approval").length > 0 && (
          <span style={{ fontSize: 10, color: "#5b8dee", fontWeight: 700, marginLeft: 4 }}>· {approvalMatches.filter(m=>m.status==="pending_approval").length} awaiting approval</span>
        )}
      </div>
      {/* Scrolling items */}
      <div className="scroll-x" style={{ display: "flex", gap: 8, padding: "8px 12px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        {allItems.map((m, i) => {
          const isDone     = m._tickerType === "done";
          const isApproval = m._tickerType === "approval";
          const isDisp     = m.status === "disputed";
          const accentClr  = isDisp ? "var(--red)" : isApproval ? "#5b8dee" : isDone ? "var(--muted)" : "var(--green)";
          const winScore   = isDone ? (m.s1 > m.s2 ? `${m.s1}–${m.s2}` : `${m.s2}–${m.s1}`) : null;
          const winner     = isDone ? (m.s1 > m.s2 ? m.t1?.name : m.t2?.name) : null;
          return (
            <div
              key={m.id}
              onClick={() => onClickMatch && onClickMatch(m)}
              style={{
                flexShrink: 0, cursor: "pointer",
                background: isDisp ? "rgba(217,79,61,0.07)" : isApproval ? "rgba(91,141,238,0.07)" : isDone ? "rgba(255,255,255,0.02)" : "rgba(46,204,113,0.06)",
                border: `1px solid ${isDisp ? "rgba(217,79,61,0.3)" : isApproval ? "rgba(91,141,238,0.3)" : isDone ? "var(--navy-border)" : "rgba(46,204,113,0.2)"}`,
                borderRadius: 6, padding: "6px 10px", minWidth: 160, maxWidth: 200,
                transition: "opacity 0.15s",
              }}
            >
              {/* Status dot + label */}
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: accentClr, animation: !isDone && !isApproval ? "pulse 1.5s infinite" : "none", flexShrink: 0 }} />
                <span style={{ fontSize: 8, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: accentClr }}>
                  {isDisp ? "Disputed" : isApproval ? "Needs Approval" : isDone ? "Final" : "In Play"}
                </span>
                {m._bracket && <span style={{ fontSize: 8, color: "var(--muted)", marginLeft: "auto" }}>{m._bracket === "GF" ? "🏆" : m._bracket === "L" ? "LB" : "WB"}</span>}
              </div>
              {/* Teams + scores */}
              {[{ team: m.t1, score: m.s1 }, { team: m.t2, score: m.s2 }].map((side, si) => {
                const isW = isDone && m.winner && (m.winner.name === side.team?.name || m.winner.id === side.team?.id);
                return (
                  <div key={si} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, marginBottom: si === 0 ? 2 : 0 }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: isW ? 700 : 500, fontSize: 12, color: isW ? "var(--gold-light)" : "var(--white)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {side.team?.name || "TBD"}
                    </span>
                    {isDone && side.score !== null && side.score !== undefined && (
                      <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, color: isW ? "var(--gold)" : "var(--muted)", flexShrink: 0 }}>{side.score}</span>
                    )}
                    {isW && <span style={{ fontSize: 9, flexShrink: 0 }}>✓</span>}
                  </div>
                );
              })}
              {m.ot && isDone && <div style={{ fontSize: 8, color: "var(--gold)", marginTop: 3, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1 }}>⭐ OT</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Collapsible recent scores feed
function RecentScoresFeed({ matches }) {
  const [open, setOpen] = React.useState(false);
  const done = matches.filter(m => m.status === "complete" && !m.status === "bye")
    .slice(-10).reverse();
  if (done.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <button onClick={() => setOpen(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid var(--navy-border)", borderRadius: 5, color: "var(--muted)", cursor: "pointer", fontSize: 11, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", width: "100%" }}>
        <span>{open ? "▲" : "▼"}</span>
        <span>Recent Results</span>
        <span style={{ marginLeft: 4, color: "var(--green)" }}>{done.length} completed</span>
      </button>
      {open && (
        <div style={{ border: "1px solid var(--navy-border)", borderTop: "none", borderRadius: "0 0 5px 5px", overflow: "hidden" }}>
          {done.slice(0, 8).map((m, i) => {
            const t1Won = m.s1 > m.s2;
            return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px", borderBottom: i < Math.min(done.length, 8) - 1 ? "1px solid rgba(255,255,255,0.04)" : "none", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                {/* Bracket label */}
                {m._bracket && <span style={{ fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, color: m._bracket === "GF" ? "var(--gold)" : m._bracket === "L" ? "var(--red)" : "var(--muted)", width: 24, flexShrink: 0, letterSpacing: 1 }}>{m._bracket === "GF" ? "🏆" : m._bracket}</span>}
                {/* Team 1 */}
                <span style={{ flex: 1, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: t1Won ? 700 : 500, fontSize: 13, color: t1Won ? "var(--gold-light)" : "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right" }}>{m.t1?.name || "—"}</span>
                {/* Score */}
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: t1Won ? "var(--gold)" : "var(--muted)" }}>{m.s1}</span>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>–</span>
                  <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: !t1Won ? "var(--gold)" : "var(--muted)" }}>{m.s2}</span>
                </div>
                {/* Team 2 */}
                <span style={{ flex: 1, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: !t1Won ? 700 : 500, fontSize: 13, color: !t1Won ? "var(--gold-light)" : "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.t2?.name || "—"}</span>
                {m.ot && <span style={{ fontSize: 9, color: "var(--gold)", flexShrink: 0, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>OT</span>}
                {m.correction && <span title="Score was corrected" style={{ fontSize: 9, color: "var(--muted)", flexShrink: 0 }}>✏</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── BRACKET CONNECTORS ──────────────────────────────────────────────────────
const CARD_W  = 224;
const CARD_H  = 90;
const CARD_GAP = 8;
const COL_GAP  = 32;

function BracketStrip({ rounds, accentColor, onScore, onSelfReport, showConnectors = true, onTableAssign=null, onMatchNote=null }) {
  const colW = CARD_W + COL_GAP;

  function connectorPaths(ri) {
    const cur  = rounds[ri];
    const next = rounds[ri + 1];
    if (!cur || !next) return [];

    const paths = [];
    for (let i = 0; i < cur.matches.length; i += 2) {
      const dst = Math.floor(i / 2);
      if (dst >= next.matches.length) continue;

      const r0Count = rounds[0].matches.length;
      const slotH   = (r0Count * (CARD_H + CARD_GAP)) / Math.pow(2, ri);
      const baseY   = (i / 2) * slotH * 2;

      const yA   = baseY + slotH / 2 - CARD_H / 2;
      const yB   = baseY + slotH + slotH / 2 - CARD_H / 2;
      const yDst = ((dst) * slotH * 2) + slotH - CARD_H / 2;

      const cA = yA   + CARD_H / 2;
      const cB = yB   + CARD_H / 2;
      const cD = yDst + CARD_H / 2;

      const x1 = CARD_W, x2 = CARD_W + COL_GAP;
      const mA = cur.matches[i], mB = cur.matches[i + 1];

      const colA = mA?.status === "complete" ? "rgba(46,204,113,0.7)" : mA?.status === "pending" ? accentColor + "99" : "rgba(255,255,255,0.08)";
      const colB = mB?.status === "complete" ? "rgba(46,204,113,0.7)" : mB?.status === "pending" ? accentColor + "99" : "rgba(255,255,255,0.08)";

      paths.push(
        `<path d="M${x1},${cA} C${x1+14},${cA} ${x2-14},${cD} ${x2},${cD}" fill="none" stroke="${colA}" stroke-width="1.5" />`,
        `<path d="M${x1},${cB} C${x1+14},${cB} ${x2-14},${cD} ${x2},${cD}" fill="none" stroke="${colB}" stroke-width="1.5" />`
      );
    }
    return paths;
  }

  return (
    <div>
      <div style={{ overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", scrollSnapType:"x proximity" }}>
        <div style={{ display: "flex", gap: 0, alignItems: "flex-start", position: "relative" }}>
        {rounds.map((round, ri) => {
          const r0Count  = rounds[0].matches.length;
          const slotH    = (r0Count * (CARD_H + CARD_GAP)) / Math.pow(2, ri);
          const totalH   = r0Count * (CARD_H + CARD_GAP);
          const paths    = showConnectors ? connectorPaths(ri) : [];
          const done     = round.matches.filter(m => m.status === "complete" || m.status === "bye").length;
          const active   = round.matches.filter(m => m.status !== "bye" && m.status !== "waiting").length;
          const roundDone= done >= active && active > 0;

          return (
            <div key={ri} style={{ display: "flex", flexShrink: 0, position: "relative" }}>
              <div style={{ width: CARD_W, flexShrink: 0 }}>
                {/* Round label with completion badge */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 4px 8px", borderBottom: `1px solid ${round.isDropIn ? "rgba(217,79,61,0.25)" : "var(--navy-border)"}`, marginBottom: 8 }}>
                  {round.isDropIn && (
                    <span style={{ fontSize: 8, background: "rgba(217,79,61,0.2)", color: "rgba(217,79,61,0.9)", padding: "1px 5px", borderRadius: 2, letterSpacing: 1, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, textTransform: "uppercase" }}>DROP-IN</span>
                  )}
                  <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 11, letterSpacing: 3, color: round.isDropIn ? "rgba(217,79,61,0.9)" : accentColor, textTransform: "uppercase" }}>{round.name}</span>
                  {active > 0 && (
                    <span style={{ fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, color: roundDone ? "var(--green)" : "var(--muted)", background: roundDone ? "rgba(46,204,113,0.12)" : "rgba(255,255,255,0.04)", padding: "1px 5px", borderRadius: 3 }}>
                      {roundDone ? "✓" : `${done}/${active}`}
                    </span>
                  )}
                </div>

                {/* Match cards */}
                <div style={{ position: "relative", height: ri === 0 ? "auto" : totalH }}>
                  {round.matches.map((m, mi) => {
                    const topOffset = ri === 0 ? 0 : mi * slotH * 2 + slotH / 2 - CARD_H / 2;
                    return (
                      <div key={m.id} style={{ position: ri === 0 ? "relative" : "absolute", top: ri === 0 ? undefined : topOffset, left: 0, right: 0, marginBottom: ri === 0 ? CARD_GAP : 0 }}>
                        <MatchCard
                          match={m}
                          accentColor={accentColor}
                          showSeed={!!m.t1?.seed}
                          onScore={(m.status === "pending" || m.status === "pending_approval" || m.status === "disputed") && onScore ? () => onScore(m.id) : null}
                          onSelfReport={m.status === "pending" && onSelfReport ? () => onSelfReport(m.id) : null}
                          onTableAssign={onTableAssign ? ()=>onTableAssign(m.id, m.tableAssignment||"") : null}
                          onMatchNote={onMatchNote ? ()=>onMatchNote(m.id, m.note||"") : null}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SVG connectors */}
              {paths.length > 0 && ri < rounds.length - 1 && (
                <svg width={COL_GAP} height={totalH + 40} style={{ flexShrink: 0, overflow: "visible", marginTop: 28 }}
                  dangerouslySetInnerHTML={{ __html: paths.join("") }}
                />
              )}
              {paths.length === 0 && ri < rounds.length - 1 && (
                <div style={{ width: COL_GAP, flexShrink: 0 }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
    <div className="pull-hint">← swipe to scroll →</div>
    </div>
  );
}


// ── BRACKET PAGE ─────────────────────────────────────────────────────────────
function BracketPage({ activeTournament, setActiveTournament, setTournaments, players, setPlayers }) {
  const { isMobile } = useBreakpoint();
  const [scoreModal, setScoreModal] = useState(null); // { matchId, side, mode } mode: "enter"|"approve"|"self_report"
  const [s1, setS1] = useState(""); const [s2, setS2] = useState(""); const [ot, setOt] = useState(false);
  const [gfReset, setGfReset] = useState(false);
  const [selfReportModal, setSelfReportModal] = useState(null); // { matchId, side, teamSlot: "t1"|"t2" }
  const [sr1, setSr1] = useState(""); const [sr2, setSr2] = useState(""); const [srOt, setSrOt] = useState(false);
  const [approvalPanel, setApprovalPanel] = useState(false);
  const [approvalToast, setApprovalToast]     = useState(null);
  const [disputeLog,    setDisputeLog]         = useState([]);   // [{matchId, t1, t2, ps, ps2, resolvedAt, resolvedBy, note}]
  const [correctionModal, setCorrectionModal]  = useState(null); // {matchId, side} — re-open score entry for completed match
  const [correctionNote,  setCorrectionNote]   = useState("");
  const [tickerOpen,      setTickerOpen]        = useState(true);
  const [recentFeedOpen,  setRecentFeedOpen]    = useState(false);
  const [quickDisputeId,  setQuickDisputeId]    = useState(null); // match being quick-resolved

  // ── New state: seed reorder, reset, inline scoring ─────────────────────
  const [seedReorderOpen, setSeedReorderOpen]   = useState(false);
  const [seedDraftOrder,  setSeedDraftOrder]     = useState([]);  // [{id,name,seed}]
  const [seedDragIdx,     setSeedDragIdx]         = useState(null);
  const [seedDragOver,    setSeedDragOver]         = useState(null);
  const [resetModal,      setResetModal]           = useState(false);
  const [resetKeepRegs,   setResetKeepRegs]        = useState(true);
  const [inlineScoreId,   setInlineScoreId]        = useState(null); // matchId with inline score inputs visible
  const [ils1,            setIls1]                 = useState("");
  const [ils2,            setIls2]                 = useState("");
  const [ilsOt,           setIlsOt]               = useState(false);
  const [finalizeModal,   setFinalizeModal]        = useState(false);
  const [disputeNoteId,   setDisputeNoteId]        = useState(null);
  const [disputeNoteText, setDisputeNoteText]      = useState("");
  const [bracketScale,    setBracketScale]    = useState(1.0);
  const [tableAssignId,  setTableAssignId]         = useState(null);  // matchId being assigned
  const [tableAssignVal, setTableAssignVal]         = useState("");
  const [refAssignId,    setRefAssignId]            = useState(null);
  const [refAssignVal,   setRefAssignVal]           = useState("");
  const [matchNoteId,    setMatchNoteId]            = useState(null);  // matchId for notes
  const [matchNoteText,  setMatchNoteText]          = useState("");
  const [bracketZoom,    setBracketZoom]            = useState(100);   // %
  const [bracketPanX,    setBracketPanX]            = useState(0);
  const [bracketPanY,    setBracketPanY]            = useState(0);
  const [isPanning,      setIsPanning]              = useState(false);
  const [panStart,       setPanStart]               = useState({x:0,y:0});

  if (!activeTournament) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", gap:12, color:"var(--muted)" }}>
      <div style={{ fontSize:52 }}>🎯</div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3, color:"var(--white)" }}>No Active Bracket</div>
      <div style={{ fontSize:13 }}>Start a tournament to manage its bracket here</div>
    </div>
  );

  const bkt = activeTournament.bracket || {};
  const isDE = bkt.type === "de";
  const maxCups = activeTournament.cupCount || 10;

  // Collect all matches flat for modal lookup
  // ── Seed reorder helpers ─────────────────────────────────────────────────
  function openSeedReorder() {
    const regs = activeTournament.registrations || [];
    const draft = [...regs]
      .filter(r => r.status === "checked_in" || r.status === "registered")
      .sort((a,b) => (a.seed||999)-(b.seed||999))
      .map((r,i) => ({ id:r.id, name:r.teamName, seed:r.seed||i+1 }));
    setSeedDraftOrder(draft);
    setSeedReorderOpen(true);
  }

  function seedDragStart(i) { setSeedDragIdx(i); }
  function seedDragOverIdx(i) { setSeedDragOver(i); }
  function seedDrop() {
    if (seedDragIdx === null || seedDragOver === null || seedDragIdx === seedDragOver) {
      setSeedDragIdx(null); setSeedDragOver(null); return;
    }
    const next = [...seedDraftOrder];
    const [moved] = next.splice(seedDragIdx, 1);
    next.splice(seedDragOver, 0, moved);
    setSeedDraftOrder(next.map((s,i) => ({...s, seed:i+1})));
    setSeedDragIdx(null); setSeedDragOver(null);
  }

  function applySnakeSeeds() {
    const n = seedDraftOrder.length;
    if (n < 2) return;
    const snaked = [...seedDraftOrder];
    // Snake: 1 gets 1, 2 gets n, 3 gets 2, 4 gets n-1...
    const order = [];
    let lo = 1, hi = n;
    let side = 0;
    while (lo <= hi) {
      if (side % 2 === 0) order.push(lo++);
      else order.push(hi--);
      side++;
    }
    const reordered = order.map((seedPos,i) => ({...snaked[seedPos-1], seed:i+1}));
    setSeedDraftOrder(reordered.sort((a,b)=>a.seed-b.seed));
  }

  function commitSeedReorder() {
    const regs = activeTournament.registrations || [];
    const updated = regs.map(r => {
      const entry = seedDraftOrder.find(s => s.id === r.id);
      return entry ? {...r, seed:entry.seed} : r;
    });
    updateAndSave(t => ({...t, registrations: updated}));
    setSeedReorderOpen(false);
  }

  // ── Bracket reset ─────────────────────────────────────────────────────────
  function doResetBracket() {
    updateAndSave(t => ({
      ...t,
      bracket: null,
      status: resetKeepRegs ? t.status : "upcoming",
      registrations: resetKeepRegs
        ? t.registrations
        : (t.registrations||[]).map(r => ({...r, status:"registered", checkedInAt:null})),
    }));
    setResetModal(false);
  }

  // ── Inline score helpers ──────────────────────────────────────────────────
  function openInlineScore(matchId) {
    const m = allMatches().find(x => x.id === matchId);
    if (!m || m.status === "complete") return;
    setInlineScoreId(matchId);
    setIls1(""); setIls2(""); setIlsOt(false);
  }

  function submitInlineScore() {
    if (!inlineScoreId) return;
    const n1 = parseInt(ils1), n2 = parseInt(ils2);
    if (isNaN(n1) || isNaN(n2)) return;
    _doSubmitScore(inlineScoreId, n1, n2, ilsOt);
    setInlineScoreId(null);
    setIls1(""); setIls2(""); setIlsOt(false);
  }

  // ── Finalize tournament ───────────────────────────────────────────────────
  function patchMatchById(matchId, patch) {
    const bkt = activeTournament.bracket;
    if (!bkt) return;
    function patchRounds(rounds) {
      return (rounds||[]).map(r => ({...r, matches:(r.matches||[]).map(m => m?.id===matchId ? {...m,...patch} : m)}));
    }
    const updated = {
      ...activeTournament,
      bracket: {
        ...bkt,
        rounds:  patchRounds(bkt.rounds),
        winners: patchRounds(bkt.winners),
        losers:  patchRounds(bkt.losers),
        wb: patchRounds(bkt.wb),
        lb: patchRounds(bkt.lb),
        gf: bkt.gf?.id===matchId ? {...bkt.gf,...patch} : bkt.gf,
      }
    };
    setTournaments(prev => prev.map(t => t.id===activeTournament.id ? updated : t));
    setActiveTournament(updated);
  }

  function saveTableAssign() {
    if (!tableAssignId) return;
    patchMatchById(tableAssignId, { tableAssignment: tableAssignVal.trim()||undefined });
    setTableAssignId(null); setTableAssignVal("");
  }

  function saveRefAssign() {
    if (!refAssignId) return;
    patchMatchById(refAssignId, { referee: refAssignVal.trim()||undefined });
    setRefAssignId(null); setRefAssignVal("");
  }

  function saveMatchNote() {
    if (!matchNoteId) return;
    patchMatchById(matchNoteId, { note: matchNoteText.trim()||undefined });
    setMatchNoteId(null); setMatchNoteText("");
  }

  function doFinalize() {
    const allM = allMatches();
    const pending = allM.filter(m => m.status !== "complete" && m.status !== "bye" && m.status !== "waiting" && m.t1 && m.t2);
    if (pending.length > 0) { setFinalizeModal(true); return; }
    updateAndSave(t => ({...t, status:"completed", finalized:true, finalizedAt:new Date().toISOString()}));
    setFinalizeModal(false);
  }

  // ── Dispute note helper ───────────────────────────────────────────────────
  function saveDisputeNote(matchId) {
    if (!disputeNoteText.trim()) return;
    setDisputeLog(prev => [...prev, {
      id: uid(), type:"note", matchId,
      note: disputeNoteText, ts: new Date().toISOString(),
      user: "Admin",
    }]);
    setDisputeNoteId(null);
    setDisputeNoteText("");
  }


  function allMatches() {
    if (isDE) {
      const wm = (bkt.wb||[]).flatMap(r=>r.matches);
      const lm = (bkt.lb||[]).flatMap(r=>r.matches);
      const gm = bkt.gf ? [bkt.gf, ...(bkt.gf.reset?[bkt.gf.reset]:[])] : [];
      return [...wm, ...lm, ...gm];
    }
    if (bkt.type==="rpe_se" || bkt.type==="rpe_de") {
      const pm = (bkt.prelims||[]).flatMap(r=>r.matches||[]);
      const eb = bkt.elimBracket;
      if (!eb) return pm;
      if (eb.type==="de_phase") {
        const wm=(eb.wb||[]).flatMap(r=>r.matches);
        const lm=(eb.lb||[]).flatMap(r=>r.matches);
        const gm=eb.gf?[eb.gf,...(eb.gf.reset?[eb.gf.reset]:[])]:[];
        return [...pm,...wm,...lm,...gm];
      }
      return [...pm,...(eb.rounds||[]).flatMap(r=>r.matches||[])];
    }
    const rounds = bkt.rounds || bkt || [];
    return (Array.isArray(rounds)?rounds:[]).flatMap(r=>r.matches||[]);
  }

  const [undoStack, setUndoStack] = useState([]);
  const [undoToast, setUndoToast] = useState(false);

  function updateAndSave(newBkt) {
    // Push current bracket to undo stack (keep last 10)
    setUndoStack(prev=>[...(prev.length>=10?prev.slice(1):prev), activeTournament.bracket]);
    const updated = { ...activeTournament, bracket: newBkt };
    setActiveTournament(updated);
    setTournaments(prev=>prev.map(t=>t.id===activeTournament.id?updated:t));
  }

  function updatePlayerElo(match, n1, n2) {
    const isRanked = activeTournament?.ranked && activeTournament?.type === "pro";
    const tName = activeTournament?.name || "Unknown Event";
    const tId   = activeTournament?.id   || "";
    const tDate = activeTournament?.date  || new Date().toISOString().slice(0,10);
    setPlayers(prev=>prev.map(p=>{
      const isT1 = match.t1?.playerId===p.id || match.t1?.name===p.name;
      const isT2 = match.t2?.playerId===p.id || match.t2?.name===p.name;
      if (!isT1&&!isT2) return p;
      const pScore=isT1?n1:n2, oScore=isT1?n2:n1, won=pScore>oScore;
      const diff = ot?(won?1:-1):pScore-oScore;
      const delta = isRanked ? calcEloDelta(p.elo, 1200, won) : 0;
      const opp = isT1 ? match.t2 : match.t1;
      const histEntry = {
        id: uid(),
        tournamentId: tId,
        tournament: tName,
        date: tDate,
        opponent: opp?.name || "Unknown",
        myScore: pScore,
        oppScore: oScore,
        won,
        ot,
        cupDiff: diff,
        eloDelta: delta,
        eloAfter: p.elo + delta,
        ranked: isRanked,
      };
      return {
        ...p,
        elo: p.elo + delta,
        wins: won ? p.wins+1 : p.wins,
        losses: won ? p.losses : p.losses+1,
        cupsMade: p.cupsMade + pScore,
        cupsAllowed: p.cupsAllowed + oScore,
        cupDiff: p.cupDiff + diff,
        matchHistory: [...(p.matchHistory||[]), histEntry],
      };
    }));
  }

  // ── Approval helpers ────────────────────────────────────────────────────────
  function showApprovalToast(msg, color="var(--green)") {
    setApprovalToast({ msg, color });
    setTimeout(()=>setApprovalToast(null), 3000);
  }

  function pendingApprovals() {
    return allMatches().filter(m=>m.status==="pending_approval"||m.status==="disputed");
  }

  // Patch a single match in the bracket regardless of WB/LB/GF/SE/RR
  function patchMatch(matchId, patch) {
    const bktCopy = JSON.parse(JSON.stringify(bkt));
    function patchIn(matches) { return matches.map(m=>m.id===matchId?{...m,...patch}:m); }
    if (isDE) {
      bktCopy.wb = (bktCopy.wb||[]).map(r=>({...r,matches:patchIn(r.matches)}));
      bktCopy.lb = (bktCopy.lb||[]).map(r=>({...r,matches:patchIn(r.matches)}));
      if (bktCopy.gf?.id===matchId) Object.assign(bktCopy.gf, patch);
      if (bktCopy.gf?.reset?.id===matchId) Object.assign(bktCopy.gf.reset, patch);
    } else if (bktCopy.type==="rpe_se" || bktCopy.type==="rpe_de") {
      // Check prelims first
      bktCopy.prelims = (bktCopy.prelims||[]).map(r=>({...r,matches:patchIn(r.matches)}));
      // Then check the elim bracket if it exists
      const eb = bktCopy.elimBracket;
      if (eb) {
        if (eb.type==="de_phase") {
          eb.wb = (eb.wb||[]).map(r=>({...r,matches:patchIn(r.matches)}));
          eb.lb = (eb.lb||[]).map(r=>({...r,matches:patchIn(r.matches)}));
          if (eb.gf?.id===matchId) Object.assign(eb.gf, patch);
          if (eb.gf?.reset?.id===matchId) Object.assign(eb.gf.reset, patch);
        } else {
          eb.rounds = (eb.rounds||[]).map(r=>({...r,matches:patchIn(r.matches)}));
        }
      }
    } else {
      bktCopy.rounds = (bktCopy.rounds||[]).map(r=>({...r,matches:patchIn(r.matches)}));
    }
    updateAndSave(bktCopy);
  }

  // Player self-reports a score — stores it as pendingScore, flips to pending_approval
  // If BOTH teams have reported and scores MATCH → auto-approve if tournament.autoApprove
  // If scores CONFLICT → disputed
  function submitSelfReport(matchId, side, reporterSlot) {
    const sr1v = parseInt(sr1), sr2v = parseInt(sr2);
    if (isNaN(sr1v)||isNaN(sr2v)||sr1v===sr2v) return;
    const match = allMatches().find(m=>m.id===matchId);
    if (!match) return;

    const existing = match.pendingScore || {};
    const newPending = { ...existing, s1:sr1v, s2:sr2v, ot:srOt, reportedBy:reporterSlot, reportedAt:new Date().toISOString() };

    // Check if opposing team already submitted a conflicting report
    const otherReport = existing.otherReport;
    let newStatus = "pending_approval";
    if (otherReport) {
      const match_ = sr1v===otherReport.s1 && sr2v===otherReport.s2;
      newStatus = match_ ? (activeTournament?.autoApprove ? "auto_confirming" : "pending_approval") : "disputed";
      newPending.otherReport = otherReport;
    } else {
      newPending.otherReport = null;
    }

    patchMatch(matchId, { status: newStatus==="auto_confirming" ? "pending_approval" : newStatus, pendingScore: newPending });

    // Auto-approve if both match and setting on
    if (newStatus==="auto_confirming" && activeTournament?.autoApprove) {
      setTimeout(()=>approveScore(matchId, side, sr1v, sr2v, srOt), 100);
      showApprovalToast("✓ Scores matched — auto-approved!", "var(--green)");
    } else if (newStatus==="disputed") {
      showApprovalToast("⚠ Score conflict — scorekeeper review needed", "var(--red)");
    } else {
      showApprovalToast("📱 Score reported — awaiting scorekeeper confirmation");
    }
    setSelfReportModal(null); setSr1(""); setSr2(""); setSrOt(false);
  }

  // Scorekeeper approves the pending score
  function approveScore(matchId, side, n1o, n2o, oto) {
    const match = allMatches().find(m=>m.id===matchId);
    if (!match) return;
    const ps = match.pendingScore;
    const n1 = n1o!==undefined ? n1o : (ps?.s1??0);
    const n2 = n2o!==undefined ? n2o : (ps?.s2??0);
    const otv= oto!==undefined ? oto  : (ps?.ot??false);
    if (match.status === "disputed") {
      setDisputeLog(prev => [...prev, {
        id: uid(), type:"dispute_approved", matchId,
        t1: match.t1?.name, t2: match.t2?.name,
        accepted: `${n1}–${n2}`, note: "Admin approved",
        at: new Date().toLocaleString(),
      }]);
    }
    _doSubmitScore(matchId, side, n1, n2, otv);
    showApprovalToast("✓ Score approved and bracket updated", "var(--green)");
    setScoreModal(null);
  }

  // Reject / clear a pending score — returns match to pending
  function rejectScore(matchId) {
    const match = allMatches().find(m=>m.id===matchId);
    if (match?.status === "disputed") {
      setDisputeLog(prev => [...prev, {
        id: uid(), type:"dispute_rejected", matchId,
        t1: match?.t1?.name, t2: match?.t2?.name,
        note: "Both reports rejected — match reset",
        at: new Date().toLocaleString(),
      }]);
    }
    patchMatch(matchId, { status:"pending", pendingScore:null, pendingScore2:null });
    showApprovalToast("✕ Score report rejected — match reset to pending", "var(--gold)");
    setScoreModal(null);
  }

  // Core score submission (extracted so approveScore can call it)
  function _doSubmitScore(matchId, side, n1, n2, otv, isCorrection=false) {
    const match = allMatches().find(m=>m.id===matchId);
    if (!match) return;
    if (side && String(side).startsWith("GS_")) {
      const groupId = String(side).slice(3);
      const newGroups = (bkt.groups||[]).map(g => {
        if (g.id !== groupId) return g;
        return { ...g, matches: (g.matches||[]).map(m => {
          if (m.id !== matchId) return m;
          const winner = n1>n2 ? m.t1 : m.t2;
          return { ...m, s1:n1, s2:n2, ot:otv, winner, status:"complete", completedAt:new Date().toISOString() };
        })};
      });
      updateAndSave({ ...bkt, groups:newGroups });
      return;
    }
    if (side === "3P" && bkt.thirdPlace?.id === matchId) {
      const winner = n1>n2 ? bkt.thirdPlace.t1 : bkt.thirdPlace.t2;
      const newBkt = { ...bkt, thirdPlace: { ...bkt.thirdPlace, status:"complete", s1:n1, s2:n2, ot:otv, winner, completedAt:new Date().toISOString() } };
      updateAndSave(newBkt);
      return;
    }
    if (isDE) {
      let newBkt;
      if (side==="W") newBkt = applyWBResult(bkt, matchId, n1, n2, otv);
      else if (side==="L") newBkt = applyLBResult(bkt, matchId, n1, n2, otv);
      else if (side==="GF") newBkt = applyGFResult(bkt, n1, n2, otv, gfReset);
      if (newBkt) updateAndSave(newBkt);
    } else {
      const rounds = bkt.rounds || bkt;
      const newRounds = rounds.map(r=>({ ...r, matches: r.matches.map(m=>{
        if (m.id!==matchId) return m;
        const winner=n1>n2?m.t1:m.t2, diff=otv?(n1>n2?1:-1):n1-n2;
        return { ...m, s1:n1, s2:n2, winner, status:"complete", ot:otv, cupDiff:diff, pendingScore:null, pendingScore2:null, ...(isCorrection?{correction:true}:{}) };
      })}));
      let advanced = newRounds;
      const roundIdx = newRounds.findIndex(r=>r.matches.some(m=>m.id===matchId));
      const mi = newRounds[roundIdx]?.matches.findIndex(m=>m.id===matchId);
      const completedMatch = newRounds[roundIdx]?.matches[mi];
      if (completedMatch?.winner && roundIdx < newRounds.length-1) {
        const nextMi=Math.floor(mi/2), slot=mi%2===0?"t1":"t2";
        advanced = newRounds.map((r,ri)=>{
          if(ri!==roundIdx+1) return r;
          return { ...r, matches: r.matches.map((m,mj)=>{
            if(mj!==nextMi) return m;
            const upd={ ...m, [slot]: completedMatch.winner };
            if(upd.t1&&upd.t2) upd.status="pending";
            return upd;
          })};
        });
      }
      updateAndSave({ ...bkt, rounds: advanced });
    }
    updatePlayerElo(match, n1, n2);
  }

  function openSelfReport(matchId, side) {
    setSelfReportModal({ matchId, side });
    setSr1(""); setSr2(""); setSrOt(false);
  }

  function submitScore() {
    const n1=parseInt(s1), n2=parseInt(s2);
    if (isNaN(n1)||isNaN(n2)||n1===n2) return;
    const { matchId, side } = scoreModal;
    // If correcting a completed score, flag it
    const prevMatch = allMatches().find(m=>m.id===matchId);
    const isCorrection = prevMatch?.status === "complete";
    if (isCorrection) {
      setDisputeLog(prev => [...prev, {
        id: uid(), type:"correction", matchId,
        t1: prevMatch.t1?.name, t2: prevMatch.t2?.name,
        from: `${prevMatch.s1}–${prevMatch.s2}`, to: `${n1}–${n2}`,
        note: correctionNote || "Score corrected",
        at: new Date().toLocaleString(),
      }]);
      setCorrectionNote("");
    }
    _doSubmitScore(matchId, side, n1, n2, ot, isCorrection);
    setScoreModal(null); setS1(""); setS2(""); setOt(false); setGfReset(false);
  }

  // Quick-resolve dispute: accept one team's submitted score
  function quickResolveDispute(matchId, side, acceptFirst) {
    const match = allMatches().find(m=>m.id===matchId);
    if (!match) return;
    const ps  = match.pendingScore;
    const ps2 = match.pendingScore2;
    const accepted = acceptFirst ? ps : ps2;
    if (!accepted) return;
    setDisputeLog(prev => [...prev, {
      id: uid(), type:"dispute_resolved", matchId,
      t1: match.t1?.name, t2: match.t2?.name,
      ps1Str: ps ? `${ps.s1}–${ps.s2}` : "—",
      ps2Str: ps2 ? `${ps2.s1}–${ps2.s2}` : "—",
      accepted: `${accepted.s1}–${accepted.s2}`,
      note: `Accepted ${acceptFirst?"team 1":"team 2"} report`,
      at: new Date().toLocaleString(),
    }]);
    _doSubmitScore(matchId, side, accepted.s1, accepted.s2, accepted.ot || false);
    showApprovalToast("✓ Dispute resolved — score applied", "var(--green)");
    setQuickDisputeId(null);
  }

  const modalMatch = scoreModal ? allMatches().find(m=>m.id===scoreModal.matchId) : null;
  const isGF = scoreModal?.side==="GF";

  // Determine if GF has been played once (might need reset)
  const gfPlayed = bkt.gf?.status==="reset";

  function openScore(matchId, side) {
    const m = allMatches().find(x=>x.id===matchId);
    setScoreModal({matchId,side});
    // Pre-fill if correcting a completed score
    if (m?.status === "complete") {
      setS1(String(m.s1 ?? ""));
      setS2(String(m.s2 ?? ""));
      setOt(m.ot || false);
    } else {
      setS1(""); setS2(""); setOt(false);
    }
    setGfReset(side==="GF" && gfPlayed);
  }

  // SE / RR rendering
  function renderSERounds() {
    const rounds = Array.isArray(bkt) ? bkt : (bkt.rounds || []);
    const isRR  = activeTournament.format === "round_robin";
    const isRPE    = bkt.type === "rpe";
    const isRPE_SE = bkt.type === "rpe_se";
    const isRPE_DE = bkt.type === "rpe_de";
    const isRP     = bkt.type === "rp";
    const isSS     = bkt.type === "ss";
    const isGS     = bkt.type === "group_stage" || activeTournament.format === "group_stage";

    // ── GROUP STAGE → BRACKET ───────────────────────────────────────────────
    if (isGS) {
      const groups   = bkt.groups   || [];
      const elimBkt  = bkt.elimBracket;
      const advCount = bkt.advanceCount || 2; // teams that advance per group

      // Compute group standings
      function getGroupStandings(group) {
        const stats = {};
        (group.teams||[]).forEach(t => {
          stats[t.id||t.name] = { team:t, wins:0, losses:0, cupDiff:0, ptsFor:0, played:0 };
        });
        (group.matches||[]).filter(m=>m.status==="complete"&&m.t1&&m.t2).forEach(m => {
          const iA = m.t1.id||m.t1.name, iB = m.t2.id||m.t2.name;
          if (stats[iA]) { stats[iA].played++; stats[iA].ptsFor+=m.s1||0; stats[iA].cupDiff+=(m.s1||0)-(m.s2||0); if((m.s1||0)>(m.s2||0))stats[iA].wins++; else stats[iA].losses++; }
          if (stats[iB]) { stats[iB].played++; stats[iB].ptsFor+=m.s2||0; stats[iB].cupDiff+=(m.s2||0)-(m.s1||0); if((m.s2||0)>(m.s1||0))stats[iB].wins++; else stats[iB].losses++; }
        });
        return Object.values(stats).sort((a,b)=>b.wins-a.wins||b.cupDiff-a.cupDiff||b.ptsFor-a.ptsFor);
      }

      const allGroupsComplete = groups.length>0 && groups.every(g=>{
        const m=(g.matches||[]).filter(m=>m.t1&&m.t2&&m.status!=="bye");
        return m.length>0 && m.every(m=>m.status==="complete");
      });

      // Build group stage bracket from scratch
      function generateGroupStage() {
        const regs = (activeTournament.registrations||[]).filter(r=>r.status!=="cancelled"&&r.status!=="no_show");
        const groupCount = bkt.groupCount || Math.max(2, Math.ceil(regs.length/4));
        const teams = regs.map(r=>({ id:r.id, name:r.teamName||(r.players?.[0]?.name)||"Team" }));
        // Distribute teams into groups evenly
        const newGroups = Array.from({length:groupCount},(_,i)=>({
          id:`g${i}`, name:`Group ${String.fromCharCode(65+i)}`,
          teams:[], matches:[]
        }));
        teams.forEach((t,i)=>newGroups[i%groupCount].teams.push(t));
        // Generate round-robin matches per group
        newGroups.forEach(g=>{
          const ts=g.teams; const m=[];
          for(let i=0;i<ts.length;i++) for(let j=i+1;j<ts.length;j++) {
            m.push({id:uid(),t1:ts[i],t2:ts[j],status:"pending",_bracket:"GS",_round:1,_groupId:g.id});
          }
          g.matches=m;
        });
        const newBkt = { type:"group_stage", groups:newGroups, advanceCount:advCount, elimBracket:null };
        updateAndSave({ ...activeTournament.bracket, ...newBkt });
      }

      // Advance top N from each group into a bracket
      function generateElimFromGroups() {
        const advancers = [];
        groups.forEach(g=>{
          const standings=getGroupStandings(g);
          standings.slice(0,advCount).forEach((row,rank)=>advancers.push({...row.team,_groupRank:rank+1,_group:g.name}));
        });
        if (advancers.length<2) return;
        // Build SE bracket from advancers
        const n = Math.pow(2,Math.ceil(Math.log2(advancers.length)));
        const seeded = [...advancers];
        while(seeded.length<n) seeded.push(null); // byes
        const rounds=[];
        let current=seeded;
        let rn=1;
        while(current.length>=2) {
          const matches=[];
          for(let i=0;i<current.length;i+=2) {
            const t1=current[i],t2=current[i+1];
            if(!t2) matches.push({id:uid(),t1:t1||null,t2:null,status:"bye",_bracket:"W",_round:rn});
            else matches.push({id:uid(),t1:t1||null,t2:t2||null,status:t1&&t2?"pending":"bye",_bracket:"W",_round:rn});
          }
          rounds.push({round:rn,matches});
          current=matches.map(()=>null);
          rn++;
        }
        const gf={id:uid(),t1:null,t2:null,status:"waiting",_bracket:"GF",_round:rn};
        const newBkt={...bkt,elimBracket:{type:"se",rounds,gf},elimTeams:advancers};
        updateAndSave(newBkt);
      }

      if (groups.length===0) {
        // Setup screen
        return (
          <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:520,margin:"0 auto"}}>
            <div style={{...css.card,padding:"24px 20px",textAlign:"center"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:26,letterSpacing:3,marginBottom:8}}>🏟 Group Stage</div>
              <div style={{fontSize:13,color:"var(--muted)",marginBottom:20}}>
                Teams are divided into groups and play round-robin. Top teams from each group advance to a bracket.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10,textAlign:"left",marginBottom:20}}>
                <Fld label="Number of Groups">
                  <input type="number" min="2" max="8" defaultValue={bkt.groupCount||4} style={{...css.input,width:"100%"}}
                    onChange={e=>updateAndSave({...bkt,groupCount:parseInt(e.target.value)||4})} />
                </Fld>
                <Fld label="Teams That Advance Per Group">
                  <select style={css.select} value={advCount} onChange={e=>updateAndSave({...bkt,advanceCount:parseInt(e.target.value)||2})}>
                    {[1,2,3,4].map(n=><option key={n} value={n}>{n} team{n!==1?"s":""}</option>)}
                  </select>
                </Fld>
              </div>
              <Btn onClick={generateGroupStage} style={{width:"100%",fontSize:14}}>🎯 Generate Groups</Btn>
              <div style={{marginTop:10,fontSize:11,color:"var(--muted)"}}>
                {(activeTournament.registrations||[]).filter(r=>r.status!=="cancelled").length} teams registered
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Progress bar */}
          {(() => {
            const allM=groups.flatMap(g=>(g.matches||[]).filter(m=>m.t1&&m.t2&&m.status!=="bye"));
            const done=allM.filter(m=>m.status==="complete").length;
            const pct=allM.length?Math.round(done/allM.length*100):0;
            return (
              <div style={{...css.card,padding:"10px 16px",display:"flex",gap:12,alignItems:"center"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Group Stage Progress</span>
                    <span style={{fontSize:11,color:pct===100?"var(--green)":"var(--gold)",fontFamily:"'Bebas Neue',sans-serif"}}>{done}/{allM.length}</span>
                  </div>
                  <div style={{height:6,borderRadius:3,background:"rgba(255,255,255,0.06)",overflow:"hidden"}}>
                    <div style={{height:"100%",background:pct===100?"var(--green)":"var(--gold)",width:`${pct}%`,transition:"width 0.4s",borderRadius:3}}/>
                  </div>
                </div>
                {allGroupsComplete && !elimBkt && (
                  <Btn sz="sm" onClick={generateElimFromGroups} style={{whiteSpace:"nowrap"}}>🏆 Generate Bracket</Btn>
                )}
                {elimBkt && <Badge color="green">Bracket Ready</Badge>}
              </div>
            );
          })()}

          {/* Group cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
            {groups.map(group=>{
              const standings=getGroupStandings(group);
              const pendingM=(group.matches||[]).filter(m=>m.status==="pending"&&m.t1&&m.t2);
              const doneM=(group.matches||[]).filter(m=>m.status==="complete");
              return (
                <div key={group.id} style={{...css.card,padding:0,overflow:"hidden"}}>
                  {/* Group header */}
                  <div style={{padding:"10px 14px",borderBottom:"1px solid var(--navy-border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1}}>{group.name}</div>
                    <div style={{fontSize:10,color:"var(--muted)"}}>{doneM.length}/{(group.matches||[]).filter(m=>m.t1&&m.t2).length} played</div>
                  </div>
                  {/* Standings */}
                  {standings.map((row,ri)=>{
                    const willAdvance=ri<advCount&&allGroupsComplete;
                    const advances=ri<advCount;
                    return (
                      <div key={row.team.id||row.team.name} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 14px",borderBottom:"1px solid rgba(255,255,255,0.03)",background:advances?"rgba(46,204,113,0.02)":"transparent"}}>
                        <div style={{width:18,fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:advances?"var(--green)":"var(--muted)",textAlign:"center"}}>{ri+1}</div>
                        <div style={{flex:1,fontSize:12,fontWeight:600}}>{row.team.name}</div>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color:"var(--white)"}}>{row.wins}–{row.losses}</div>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:13,color:row.cupDiff>=0?"var(--green)":"var(--red)",minWidth:30,textAlign:"right"}}>{row.cupDiff>=0?"+":""}{row.cupDiff}</div>
                        {advances&&<span style={{fontSize:8,color:"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>ADV</span>}
                      </div>
                    );
                  })}
                  {/* Pending matches */}
                  {pendingM.length>0&&(
                    <div style={{borderTop:"1px solid var(--navy-border)"}}>
                      {pendingM.map((m,mi)=>(
                        <div key={m.id} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderBottom:mi<pendingM.length-1?"1px solid rgba(255,255,255,0.03)":"none",cursor:"pointer"}}
                          onClick={()=>openScore(m.id,"GS_"+group.id)}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(201,168,76,0.06)"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{flex:1,fontSize:11}}>{m.t1.name}</span>
                          <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:10,color:"var(--muted)",fontWeight:700}}>vs</span>
                          <span style={{flex:1,textAlign:"right",fontSize:11}}>{m.t2.name}</span>
                          <span style={{fontSize:9,color:"var(--gold)",marginLeft:4}}>📝</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Elimination bracket if generated */}
          {elimBkt&&(
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>🏆 Elimination Bracket</div>
              <BracketStrip rounds={elimBkt.rounds||[]} accentColor="var(--gold)"
                onScore={(id)=>openScore(id,"W")} onSelfReport={(id)=>openSelfReport(id,"W")}
                onTableAssign={(id,cur)=>{setTableAssignId(id);setTableAssignVal(cur||"");}}
                onMatchNote={(id,cur)=>{setMatchNoteId(id);setMatchNoteText(cur||"");}} />
              {elimBkt.gf&&(
                <>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,letterSpacing:2,color:"var(--gold)",textTransform:"uppercase",margin:"16px 0 8px"}}>Grand Final</div>
                  <MatchCard match={elimBkt.gf} accentColor="var(--gold)" onScore={()=>openScore(elimBkt.gf.id,"GF")} onSelfReport={()=>openSelfReport(elimBkt.gf.id,"GF")} />
                </>
              )}
            </div>
          )}
        </div>
      );
    }

    // ── RANDOM PARTNER PRELIMS → ELIM BRACKET ────────────────────────────────
    if (isRPE_SE || isRPE_DE) {
      const prelims = bkt.prelims || [];
      const allPrelimM = prelims.flatMap(r => r.matches || []);
      const prelimDone = allPrelimM.filter(m => m.status === "complete").length;
      const prelimTotal = allPrelimM.filter(m => m.status !== "bye").length;
      const allPrelimsComplete = prelimTotal > 0 && prelimDone === prelimTotal;
      const hasElim = !!bkt.elimBracket;
      const cutSize = bkt.cutSize || 8;

      // Compute individual player standings from prelim results
      const playerStats = {};
      (bkt.players || []).forEach(p => {
        playerStats[p.id] = { player: p, w: 0, l: 0, cd: 0, made: 0, allowed: 0, pts: 0 };
      });
      allPrelimM.filter(m => m.status === "complete").forEach(m => {
        [m.t1, m.t2].forEach((team, ti) => {
          if (!team?.playerIds) return;
          const s = ti === 0 ? m.s1 : m.s2, os = ti === 0 ? m.s2 : m.s1;
          const won = ti === 0 ? (m.s1 > m.s2) : (m.s2 > m.s1);
          team.playerIds.forEach(pid => {
            if (!playerStats[pid]) return;
            if (won) { playerStats[pid].w++; playerStats[pid].pts += 3; } else playerStats[pid].l++;
            playerStats[pid].cd += (s - os);
            playerStats[pid].made += s || 0;
            playerStats[pid].allowed += os || 0;
          });
        });
      });
      const standings = Object.values(playerStats).sort((a, b) => b.pts - a.pts || b.cd - a.cd || b.w - a.w);

      // Partner pair counts for history matrix
      const pairCount = {};
      prelims.forEach(r => r.matches.forEach(m => {
        [m.t1, m.t2].forEach(t => {
          if (!t?.playerIds || t.playerIds.length < 2) return;
          const key = [...t.playerIds].sort().join("|");
          pairCount[key] = (pairCount[key] || 0) + 1;
        });
      }));

      function generateElimBracket() {
        const topPlayers = standings.slice(0, cutSize).map(s => s.player);
        const usedIds = new Set();
        const bracketTeams = [];
        for (let i = 0; i < topPlayers.length; i++) {
          if (usedIds.has(topPlayers[i].id)) continue;
          let bestJ = -1, bestScore = Infinity;
          for (let j = i + 1; j < topPlayers.length; j++) {
            if (usedIds.has(topPlayers[j].id)) continue;
            const key = [topPlayers[i].id, topPlayers[j].id].sort().join("|");
            const score = pairCount[key] || 0;
            if (score < bestScore) { bestScore = score; bestJ = j; }
          }
          if (bestJ === -1) continue;
          const p1 = topPlayers[i], p2 = topPlayers[bestJ];
          bracketTeams.push({ id: uid(), name: `${p1.name} / ${p2.name}`, playerIds: [p1.id, p2.id], seed: bracketTeams.length + 1, prelimRank: [i + 1, bestJ + 1] });
          usedIds.add(p1.id); usedIds.add(p2.id);
        }
        const elimBkt = isRPE_DE
          ? { type: "de_phase", ...genDoubleElim(bracketTeams) }
          : { type: "se_phase", rounds: genSingleElim(bracketTeams) };
        updateAndSave({ ...bkt, elimBracket: elimBkt, bracketTeams });
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Stat strip */}
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {[
              [prelims.length, "Prelim Rounds", "var(--gold)"],
              [allPrelimsComplete ? "Done ✓" : `${prelimDone}/${prelimTotal}`, "Matches", allPrelimsComplete ? "var(--green)" : "var(--white)"],
              [`Top ${cutSize}`, "Cut to " + (isRPE_DE ? "DE" : "SE"), "#5b8dee"],
              [(bkt.players || []).length, "Players", "var(--muted)"],
            ].map(([v, l, c]) => (
              <div key={l} style={{ ...css.card, padding:"10px 16px", textAlign:"center", flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Standings with cut line */}
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)" }}>🎲 PLAYER STANDINGS</span>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>New partner every round · Top {cutSize} advance</span>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:460 }}>
                <thead><tr>{["#","Player","Pts","W","L","Cup Diff"].map(h=><th key={h} style={{ ...css.th, textAlign:h==="Player"?"left":"center" }}>{h}</th>)}</tr></thead>
                <tbody>{standings.map((row, i) => {
                  const advances = i < cutSize;
                  const isCutLine = i === cutSize;
                  const medal = i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
                  return (
                    <tr key={row.player.id} style={{ borderBottom:`1px solid ${isCutLine?"rgba(91,141,238,0.4)":"rgba(255,255,255,0.03)"}`, background:advances?"rgba(46,204,113,0.03)":"transparent" }}>
                      <td style={{ ...css.td, width:36, textAlign:"center" }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:medal||"var(--muted)" }}>{i+1}</div>
                        {advances && !hasElim && <div style={{ fontSize:8, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>ADV</div>}
                      </td>
                      <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14 }}>
                        {row.player.name}
                        {isCutLine && <div style={{ fontSize:9, color:"#5b8dee", letterSpacing:1 }}>── CUT LINE ──</div>}
                      </td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold-light)" }}>{row.pts}</td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--green)" }}>{row.w}</td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--red)" }}>{row.l}</td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:row.cd>=0?"var(--green)":"var(--red)" }}>{row.cd>=0?"+":""}{row.cd}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>

          {/* Generate bracket CTA */}
          {allPrelimsComplete && !hasElim && (
            <div style={{ ...css.card, padding:"18px 20px", borderColor:"rgba(91,141,238,0.4)", background:"rgba(91,141,238,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
              <div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:3, color:"#5b8dee" }}>ALL PRELIMS COMPLETE</div>
                <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
                  Top {cutSize} players paired as new partners (minimizing repeats) and seeded into {isRPE_DE?"Double":"Single"} Elimination.
                </div>
              </div>
              <Btn onClick={generateElimBracket} style={{ flexShrink:0, background:"#5b8dee", border:"1px solid #5b8dee" }}>
                🏆 Generate {isRPE_DE?"DE":"SE"} Bracket →
              </Btn>
            </div>
          )}

          {/* Prelim rounds (hidden when bracket generated) */}
          {!hasElim && (
            <div style={{ ...css.card, padding:16 }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)", marginBottom:14 }}>
                PRELIM ROUNDS <span style={{ fontSize:11, color:"var(--muted)", letterSpacing:1, fontFamily:"'Barlow Condensed',sans-serif" }}>— new random partners each round</span>
              </div>
              {prelims.map((r, ri) => {
                const rDone = r.matches.filter(m=>m.status==="complete").length;
                const rTotal = r.matches.filter(m=>m.status!=="bye").length;
                const rAllDone = rDone===rTotal && rTotal>0;
                return (
                  <div key={ri} style={{ marginBottom:20 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase", marginBottom:8, display:"flex", alignItems:"center", gap:8, paddingBottom:6, borderBottom:"1px solid var(--navy-border)" }}>
                      <span style={{ color:rAllDone?"var(--green)":"var(--gold)" }}>{r.name}</span>
                      {rAllDone && <Badge color="green">✓ Done</Badge>}
                      <span style={{ fontSize:10, color:"var(--muted)", fontWeight:400 }}>{rDone}/{rTotal}</span>
                      <Badge color="blue">🎲 Random Partners</Badge>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                      {r.matches.filter(m=>m.status!=="bye").map(m=>(
                        <div key={m.id} style={{ minWidth:200, flex:"0 0 auto" }}>
                          <MatchCard match={m} accentColor="var(--gold)"
                            onInlineScore={setInlineScoreId}
                            inlineScoreId={inlineScoreId}
                            ils1={ils1} ils2={ils2} ilsOt={ilsOt}
                            setIls1={setIls1} setIls2={setIls2} setIlsOt={setIlsOt}
                            onInlineSubmit={submitInlineScore}
                            onScore={(m.status==="pending"||m.status==="pending_approval"||m.status==="disputed")?()=>openScore(m.id,"W"):null}
                            onSelfReport={m.status==="pending"?()=>openSelfReport(m.id,"W"):null}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div style={{ display:"flex", gap:10, alignItems:"center", paddingTop:8, borderTop:"1px solid var(--navy-border)" }}>
                <Btn v="ghost" sz="sm" onClick={()=>{
                  const pc = {...(bkt.partnerCounts||{})};
                  const oh = {...(bkt.opponentHistory||{})};
                  const newR = drawOneRPERound(bkt.players||[], pc, oh, `Prelim ${prelims.length+1}`);
                  updateAndSave({ ...bkt, prelims:[...prelims, { ...newR, phase:"prelim", roundPartners:true }], partnerCounts:pc, opponentHistory:oh });
                }}>🎲 Add Prelim Round</Btn>
                <span style={{ fontSize:11, color:"var(--muted)" }}>Fresh partners, avoids recent pairings</span>
              </div>
            </div>
          )}

          {/* Elimination bracket phase */}
          {hasElim && (() => {
            const eb = bkt.elimBracket;
            if (!eb) return null;
            const isDEphase = eb.type === "de_phase";
            return (
              <div id="bracket-printable" style={{ transformOrigin:"top left", transform:`scale(${bracketScale})`, transition:"transform 0.2s", width:`${Math.round(100/bracketScale)}%` }}>
                <div style={{ ...css.card, padding:"12px 16px", marginBottom:14, borderColor:"rgba(201,168,76,0.3)", background:"rgba(201,168,76,0.04)", display:"flex", alignItems:"center", gap:12 }}>
                  <span style={{ fontSize:22 }}>🏆</span>
                  <div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)" }}>{isDEphase?"DOUBLE":"SINGLE"} ELIMINATION BRACKET</div>
                    <div style={{ fontSize:11, color:"var(--muted)" }}>Fixed partners seeded from prelim standings · {bkt.bracketTeams?.length||0} teams</div>
                  </div>
                </div>
                {isDEphase ? (
                  <div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:8 }}>Winners Bracket</div>
                    <BracketStrip rounds={eb.wb||[]} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} onSelfReport={(id)=>openSelfReport(id,"W")} />
                    {(eb.lb||[]).length>0 && <>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", margin:"16px 0 8px" }}>Losers Bracket</div>
                      <BracketStrip rounds={eb.lb||[]} accentColor="var(--red)" onScore={(id)=>openScore(id,"L")} onSelfReport={(id)=>openSelfReport(id,"L")} />
                    </>}
                    {eb.gf && <>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, color:"var(--gold)", textTransform:"uppercase", margin:"16px 0 8px" }}>Grand Final</div>
                      <MatchCard match={eb.gf} accentColor="var(--gold)" onScore={()=>openScore(eb.gf.id,"GF")} onSelfReport={()=>openSelfReport(eb.gf.id,"GF")} onRefAssign={()=>{setRefAssignId(eb.gf.id);setRefAssignVal(eb.gf.referee||"");}} />
                      {/* ── Third Place Match ── */}
                      {eb.thirdPlace && (
                        <>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, color:"#cd7f32", textTransform:"uppercase", margin:"16px 0 8px" }}>🥉 Third Place</div>
                          <MatchCard match={eb.thirdPlace} accentColor="#cd7f32" onScore={()=>openScore(eb.thirdPlace.id,"3P")} onSelfReport={()=>openSelfReport(eb.thirdPlace.id,"3P")} />
                        </>
                      )}
                      {!eb.thirdPlace && eb.gf?.status==="complete" && (
                        <Btn v="ghost" sz="sm" style={{ marginTop:10, fontSize:11, color:"#cd7f32", borderColor:"rgba(205,127,50,0.4)" }}
                          onClick={()=>{
                            // Find the two semi-final losers
                            const wRounds = eb.wb||eb.winners||[];
                            const lastWR = wRounds[wRounds.length-2]?.matches||[];
                            const semis = lastWR.filter(m=>m&&m.t1&&m.t2);
                            if (semis.length < 2) return;
                            const loser1 = semis[0].winner?.id===semis[0].t1?.id ? semis[0].t2 : semis[0].t1;
                            const loser2 = semis[1].winner?.id===semis[1].t1?.id ? semis[1].t2 : semis[1].t1;
                            if (!loser1||!loser2) return;
                            const thirdMatch = { id:uid(), t1:loser1, t2:loser2, status:"pending", _bracket:"3P", _round:0 };
                            const newBkt = { ...activeTournament.bracket, thirdPlace: thirdMatch };
                            updateAndSave(newBkt);
                          }}>
                          + Generate 3rd Place Match
                        </Btn>
                      )}
                    </>}
                  </div>
                ) : (
                  <BracketStrip rounds={eb.rounds||[]} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} onSelfReport={(id)=>openSelfReport(id,"W")} onTableAssign={(id,cur)=>{setTableAssignId(id);setTableAssignVal(cur||"");}} onMatchNote={(id,cur)=>{setMatchNoteId(id);setMatchNoteText(cur||"");}} onRefAssign={(id,cur)=>{setRefAssignId(id);setRefAssignVal(cur||"");}} />
                )}
                <details style={{ marginTop:16 }}>
                  <summary style={{ cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", padding:"8px 0", userSelect:"none" }}>
                    ▸ Prelim Results ({prelims.length} rounds)
                  </summary>
                  <div style={{ marginTop:10 }}>
                    {prelims.map((r, ri) => (
                      <div key={ri} style={{ marginBottom:12 }}>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, color:"var(--gold)", marginBottom:6 }}>{r.name}</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                          {r.matches.filter(m=>m.status!=="bye").map(m=>(
                            <div key={m.id} style={{ background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:4, padding:"7px 12px", minWidth:190 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid rgba(255,255,255,0.05)", paddingBottom:3, marginBottom:3 }}>
                                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, color:m.winner?.id===m.t1?.id?"var(--gold-light)":"var(--white)" }}>{m.t1?.name||"TBD"}</span>
                                {m.status==="complete"&&<span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--gold)" }}>{m.s1}</span>}
                              </div>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, color:m.winner?.id===m.t2?.id?"var(--gold-light)":"var(--white)" }}>{m.t2?.name||"TBD"}</span>
                                {m.status==="complete"&&<span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--gold)" }}>{m.s2}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            );
          })()}

          {/* Partner history matrix */}
          {(bkt.players||[]).length >= 2 && (bkt.players||[]).length <= 12 && (() => {
            const plist = bkt.players||[];
            return (
              <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, letterSpacing:3, color:"#5b8dee" }}>🎲 PARTNER HISTORY</span>
                  <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>Times paired as partners during prelims</span>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ borderCollapse:"collapse", fontSize:11 }}>
                    <thead><tr>
                      <th style={{ ...css.th, minWidth:90, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1 }}>Player</th>
                      {plist.map(p=><th key={p.id} style={{ ...css.th, textAlign:"center", minWidth:50, padding:"6px 8px" }}>{p.name.split(" ")[0]}</th>)}
                    </tr></thead>
                    <tbody>{plist.map(pa=>(
                      <tr key={pa.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1 }}>{pa.name}</td>
                        {plist.map(pb=>{
                          if(pa.id===pb.id) return <td key={pb.id} style={{ ...css.td, textAlign:"center", background:"rgba(255,255,255,0.03)", color:"var(--muted)", padding:"6px 8px" }}>—</td>;
                          const key=[pa.id,pb.id].sort().join("|");
                          const cnt=pairCount[key]||0;
                          const bg=cnt===0?"transparent":cnt===1?"rgba(91,141,238,0.12)":cnt===2?"rgba(91,141,238,0.25)":"rgba(91,141,238,0.4)";
                          return <td key={pb.id} style={{ ...css.td, textAlign:"center", background:bg, padding:"6px 8px" }}>
                            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:cnt===0?"var(--muted)":"#5b8dee" }}>{cnt||"·"}</span>
                          </td>;
                        })}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      );
    }

    // ── RANDOM PARTNER EACH ROUND ─────────────────────────────────────────
    if (isRPE) {
      const allM = rounds.flatMap(r=>r.matches||[]);
      const done = allM.filter(m=>m.status==="complete").length;
      const total = allM.filter(m=>m.status!=="bye").length;
      const playerStats = {};
      (bkt.players||[]).forEach(p => { playerStats[p.id] = { player:p, w:0, l:0, cd:0, made:0, allowed:0 }; });
      allM.filter(m=>m.status==="complete").forEach(m => {
        [m.t1, m.t2].forEach((team, ti) => {
          if (!team?.playerIds) return;
          const s = ti===0?m.s1:m.s2, os = ti===0?m.s2:m.s1;
          const won = ti===0?(m.s1>m.s2):(m.s2>m.s1);
          team.playerIds.forEach(pid => {
            if (!playerStats[pid]) return;
            if (won) playerStats[pid].w++; else playerStats[pid].l++;
            playerStats[pid].cd += (s-os); playerStats[pid].made += s||0; playerStats[pid].allowed += os||0;
          });
        });
      });
      const standings = Object.values(playerStats).sort((a,b)=>b.w-a.w||(b.cd-a.cd));
      return (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {[["Rounds",rounds.length,"var(--gold)"],["Done",`${done}/${total}`,"var(--white)"],["Players",(bkt.players||[]).length,"var(--muted)"]].map(([l,v,c])=>(
              <div key={l} style={{ ...css.card, padding:"10px 16px", textAlign:"center", flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)" }}>🎲 PLAYER STANDINGS</span>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>Partners change every round</span>
            </div>
            <div className="resp-table"><table style={{ width:"100%", borderCollapse:"collapse",minWidth:480 }}>
              <thead><tr>{["#","Player","W","L","Cup Diff","Made","Allowed"].map(h=><th key={h} style={{ ...css.th, textAlign:h==="Player"?"left":"center" }}>{h}</th>)}</tr></thead>
              <tbody>{standings.map((row,i)=>{
                const medal=i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
                return <tr key={row.player.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                  <td style={{ ...css.td, width:36, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:medal||"var(--muted)" }}>{i+1}</td>
                  <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:medal||"var(--white)" }}>{row.player.name}</td>
                  <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--green)" }}>{row.w}</td>
                  <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--red)" }}>{row.l}</td>
                  <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:row.cd>=0?"var(--green)":"var(--red)" }}>{row.cd>=0?"+":""}{row.cd}</td>
                  <td style={{ ...css.td, textAlign:"center", fontSize:13, color:"var(--muted)" }}>{row.made}</td>
                  <td style={{ ...css.td, textAlign:"center", fontSize:13, color:"var(--muted)" }}>{row.allowed}</td>
                </tr>;
              })}</tbody>
            </table></div>
          </div>
          {/* Partner history matrix */}
          {(bkt.players||[]).length >= 2 && (bkt.players||[]).length <= 12 && (()=>{
            const plist = bkt.players||[];
            // Count how many times each pair has been partners
            const pairCount = {};
            rounds.forEach(r=>{
              r.matches.forEach(m=>{
                [m.t1,m.t2].forEach(t=>{
                  if(!t?.playerIds||t.playerIds.length<2) return;
                  const [a,b]=[t.playerIds[0],t.playerIds[1]];
                  const key=a<b?`${a}|${b}`:`${b}|${a}`;
                  pairCount[key]=(pairCount[key]||0)+1;
                });
              });
            });
            return (
              <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, letterSpacing:3, color:"#5b8dee" }}>🎲 PARTNER HISTORY MATRIX</span>
                  <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>Times paired together · darker = more repeats</span>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ borderCollapse:"collapse", fontSize:11 }}>
                    <thead><tr>
                      <th style={{ ...css.th, minWidth:90, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1 }}>Player</th>
                      {plist.map(p=><th key={p.id} style={{ ...css.th, textAlign:"center", minWidth:50, padding:"6px 8px" }}>{p.name.split(" ")[0]}</th>)}
                    </tr></thead>
                    <tbody>
                      {plist.map(pa=>(
                        <tr key={pa.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                          <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1 }}>{pa.name}</td>
                          {plist.map(pb=>{
                            if(pa.id===pb.id) return <td key={pb.id} style={{ ...css.td, textAlign:"center", background:"rgba(255,255,255,0.03)", color:"var(--muted)", padding:"6px 8px" }}>—</td>;
                            const key=pa.id<pb.id?`${pa.id}|${pb.id}`:`${pb.id}|${pa.id}`;
                            const cnt=pairCount[key]||0;
                            const bg=cnt===0?"transparent":cnt===1?"rgba(91,141,238,0.12)":cnt===2?"rgba(91,141,238,0.25)":"rgba(91,141,238,0.4)";
                            return <td key={pb.id} style={{ ...css.td, textAlign:"center", background:bg, padding:"6px 8px" }}>
                              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:cnt===0?"var(--muted)":"#5b8dee" }}>{cnt||"·"}</span>
                            </td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          <div style={{ ...css.card, padding:16 }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)", marginBottom:14 }}>ROUNDS</div>
            {rounds.map((r,ri)=>{
              const rDone=r.matches.filter(m=>m.status==="complete").length, rTotal=r.matches.filter(m=>m.status!=="bye").length;
              const rAllDone = rDone===rTotal && rTotal>0;
              return <div key={ri} style={{ marginBottom:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase", marginBottom:8, display:"flex", alignItems:"center", gap:8, paddingBottom:6, borderBottom:"1px solid var(--navy-border)" }}>
                  <span style={{ color:rAllDone?"var(--green)":"var(--gold)" }}>{r.name}</span>
                  {rAllDone && <Badge color="green">✓ Done</Badge>}
                  <span style={{ fontSize:10, color:"var(--muted)", fontWeight:400 }}>{rDone}/{rTotal}</span>
                  <Badge color="blue">🎲 New Partners</Badge>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                  {r.matches.filter(m=>m.status!=="bye").map(m=>(
                    <div key={m.id} style={{ minWidth:200, flex:"0 0 auto" }}>
                      <MatchCard match={m} accentColor="var(--gold)"
                        onScore={(m.status==="pending"||m.status==="pending_approval"||m.status==="disputed")?()=>openScore(m.id,"W"):null}
                        onSelfReport={m.status==="pending"?()=>openSelfReport(m.id,"W"):null}
                      />
                    </div>
                  ))}
                </div>
              </div>;
            })}
            <div style={{ marginTop:8, display:"flex", gap:10, alignItems:"center", paddingTop:8, borderTop:"1px solid var(--navy-border)" }}>
              <Btn v="ghost" sz="sm" onClick={()=>{
                const newR = genRandomPartnerEachRound(bkt.players||[], 1);
                updateAndSave({ ...bkt, rounds:[...rounds, { ...newR[0], name:`Round ${rounds.length+1}` }] });
              }}>🎲 Add Another Round</Btn>
              <span style={{ fontSize:11, color:"var(--muted)" }}>Greedy shuffle — minimizes partner repeats</span>
            </div>
          </div>
        </div>
      );
    }

    // ── SET SCHEDULE (League format) ─────────────────────────────────────
    if (isSS) {
      const allM = rounds.flatMap(r=>r.matches||[]);
      const done = allM.filter(m=>m.status==="complete").length;
      const total = allM.filter(m=>m.status!=="bye").length;

      // Build team map from all matches
      const teamMap = new Map();
      allM.forEach(m=>{ if(m.t1?.id) teamMap.set(m.t1.id,m.t1); if(m.t2?.id) teamMap.set(m.t2.id,m.t2); });
      const teams2 = [...teamMap.values()];

      // Compute standings
      const tStats = {};
      teams2.forEach(t=>{ tStats[t.id]={team:t,w:0,l:0,cd:0,made:0,allowed:0,gp:0,pts:0,streak:[]}; });
      allM.filter(m=>m.status==="complete").forEach(m=>{
        const w1=m.s1>m.s2;
        if(m.t1?.id&&tStats[m.t1.id]){
          tStats[m.t1.id].gp++;
          if(w1){tStats[m.t1.id].w++;tStats[m.t1.id].pts+=3;tStats[m.t1.id].streak.push("W");}
          else{tStats[m.t1.id].l++;tStats[m.t1.id].pts+=0;tStats[m.t1.id].streak.push("L");}
          tStats[m.t1.id].cd+=(m.s1-m.s2); tStats[m.t1.id].made+=m.s1||0; tStats[m.t1.id].allowed+=m.s2||0;
        }
        if(m.t2?.id&&tStats[m.t2.id]){
          tStats[m.t2.id].gp++;
          if(!w1){tStats[m.t2.id].w++;tStats[m.t2.id].pts+=3;tStats[m.t2.id].streak.push("W");}
          else{tStats[m.t2.id].l++;tStats[m.t2.id].pts+=0;tStats[m.t2.id].streak.push("L");}
          tStats[m.t2.id].cd+=(m.s2-m.s1); tStats[m.t2.id].made+=m.s2||0; tStats[m.t2.id].allowed+=m.s1||0;
        }
      });
      const ssStandings = Object.values(tStats).sort((a,b)=>b.pts-a.pts||(b.cd-a.cd)||(b.w-a.w));
      const allDone = total>0 && done===total;
      const playoffCutoff = Math.min(4, Math.floor(teams2.length/2));

      return (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Champion banner */}
          {allDone && ssStandings[0] && (
            <div style={{ ...css.card, padding:"16px 20px", borderColor:"rgba(201,168,76,0.4)", background:"rgba(201,168,76,0.07)", display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ fontSize:36 }}>🏆</div>
              <div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, letterSpacing:4, color:"var(--gold)" }}>LEAGUE CHAMPION</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, color:"var(--gold-light)", lineHeight:1 }}>{ssStandings[0].team.name}</div>
                <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>{ssStandings[0].w}W – {ssStandings[0].l}L · {ssStandings[0].pts} pts · {ssStandings[0].cd>=0?"+":""}{ssStandings[0].cd} cup diff</div>
              </div>
            </div>
          )}

          {/* Stat strip */}
          <div className="resp-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            {[
              ["Weeks",rounds.length,"var(--gold)"],
              [`${done}/${total}`,"Matches","var(--white)"],
              [teams2.length,"Teams","var(--muted)"],
              [allDone?"Final":"Active",allDone?"Season":"Status",allDone?"var(--gold)":"var(--green)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ ...css.card, padding:"10px 14px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Standings */}
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6 }}>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)" }}>📅 LEAGUE STANDINGS</span>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>3 pts/win · sorted by points then cup diff</span>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:520 }}>
                <thead>
                  <tr>{["#","Team","Pts","W","L","GP","Cup Diff","Made","Allowed","Last 5"].map(h=>(
                    <th key={h} style={{ ...css.th, textAlign:h==="Team"?"left":"center" }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {ssStandings.map((row,i)=>{
                    const medal=i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
                    const isPlayoff = !allDone && i < playoffCutoff;
                    const isBubble = !allDone && i === playoffCutoff;
                    const last5 = row.streak.slice(-5);
                    return (
                      <tr key={row.team.id} style={{ borderBottom:`1px solid ${isBubble?"rgba(201,168,76,0.2)":"rgba(255,255,255,0.03)"}`, background:i===0&&allDone?"rgba(201,168,76,0.05)":isPlayoff?"rgba(46,204,113,0.03)":"transparent" }}>
                        <td style={{ ...css.td, width:32, textAlign:"center" }}>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:medal||"var(--muted)" }}>{i+1}</div>
                          {isPlayoff && <div style={{ fontSize:8, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>PO</div>}
                        </td>
                        <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:medal||"var(--white)" }}>
                          {row.team.name}
                          {isBubble && <div style={{ fontSize:9, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1 }}>── PLAYOFF LINE ──</div>}
                        </td>
                        <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:medal||"var(--gold-light)" }}>{row.pts}</td>
                        <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--green)" }}>{row.w}</td>
                        <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--red)" }}>{row.l}</td>
                        <td style={{ ...css.td, textAlign:"center", fontSize:12, color:"var(--muted)" }}>{row.gp}</td>
                        <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:row.cd>=0?"var(--green)":"var(--red)" }}>{row.cd>=0?"+":""}{row.cd}</td>
                        <td style={{ ...css.td, textAlign:"center", fontSize:12, color:"var(--muted)" }}>{row.made}</td>
                        <td style={{ ...css.td, textAlign:"center", fontSize:12, color:"var(--muted)" }}>{row.allowed}</td>
                        <td style={{ ...css.td, textAlign:"center" }}>
                          <div style={{ display:"flex", gap:2, justifyContent:"center" }}>
                            {last5.map((r,ri)=>(
                              <div key={ri} style={{ width:14, height:14, borderRadius:2, background:r==="W"?"var(--green)":"var(--red)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"var(--navy)", fontFamily:"'Barlow Condensed',sans-serif" }}>{r}</div>
                            ))}
                            {last5.length===0 && <span style={{ fontSize:10, color:"var(--muted)" }}>—</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!allDone && teams2.length >= 4 && (
              <div style={{ padding:"8px 16px", borderTop:"1px solid var(--navy-border)", fontSize:11, color:"var(--muted)", display:"flex", gap:16 }}>
                <span><span style={{ display:"inline-block", width:8, height:8, borderRadius:2, background:"var(--green)", marginRight:4 }}/>Top {playoffCutoff} → Playoffs</span>
                <span><span style={{ display:"inline-block", width:8, height:8, borderRadius:2, background:"rgba(201,168,76,0.4)", marginRight:4 }}/>Bubble</span>
              </div>
            )}
          </div>

          {/* Head-to-head matrix */}
          {teams2.length >= 2 && teams2.length <= 10 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)" }}>
                <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, letterSpacing:3, color:"var(--gold)" }}>HEAD-TO-HEAD MATRIX</span>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ borderCollapse:"collapse", fontSize:11 }}>
                  <thead>
                    <tr>
                      <th style={{ ...css.th, minWidth:100, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1 }}>Team</th>
                      {ssStandings.map(row=>(
                        <th key={row.team.id} style={{ ...css.th, textAlign:"center", minWidth:60, padding:"6px 8px" }}>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:10, letterSpacing:1 }}>
                            {row.team.name.split(" ")[0]}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ssStandings.map(rowTeam=>(
                      <tr key={rowTeam.team.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ ...css.td, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, position:"sticky", left:0, background:"var(--navy-mid)", zIndex:1, whiteSpace:"nowrap" }}>{rowTeam.team.name}</td>
                        {ssStandings.map(colTeam=>{
                          if(rowTeam.team.id===colTeam.team.id) return <td key={colTeam.team.id} style={{ ...css.td, textAlign:"center", background:"rgba(255,255,255,0.03)", color:"var(--muted)" }}>—</td>;
                          const h2h = allM.filter(m=>m.status==="complete"&&((m.t1?.id===rowTeam.team.id&&m.t2?.id===colTeam.team.id)||(m.t2?.id===rowTeam.team.id&&m.t1?.id===colTeam.team.id)));
                          const wins = h2h.filter(m=>m.winner?.id===rowTeam.team.id).length;
                          const losses = h2h.length-wins;
                          return (
                            <td key={colTeam.team.id} style={{ ...css.td, textAlign:"center", padding:"6px 8px" }}>
                              {h2h.length===0 ? <span style={{ color:"var(--muted)" }}>—</span> :
                                <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:wins>losses?"var(--green)":wins<losses?"var(--red)":"var(--muted)" }}>{wins}–{losses}</span>
                              }
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Weekly schedule */}
          <div style={{ ...css.card, padding:16 }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)", marginBottom:14 }}>MATCH SCHEDULE</div>
            {rounds.map((r,ri)=>{
              const rDone=r.matches.filter(m=>m.status==="complete").length;
              const rTotal=r.matches.filter(m=>m.status!=="bye").length;
              const rAllDone = rDone===rTotal && rTotal>0;
              return (
                <div key={ri} style={{ marginBottom:20 }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", gap:8, paddingBottom:6, borderBottom:"1px solid var(--navy-border)" }}>
                    <span style={{ color:rAllDone?"var(--green)":"var(--gold)" }}>{r.name}</span>
                    {rAllDone && <Badge color="green">✓ Complete</Badge>}
                    <span style={{ fontSize:10, color:"var(--muted)", fontWeight:400 }}>{rDone}/{rTotal} matches</span>
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                    {r.matches.filter(m=>m.status!=="bye").map(m=>{
                      const isDone = m.status==="complete";
                      const isAprv = m.status==="pending_approval";
                      const w1 = isDone && m.winner?.id===m.t1?.id;
                      const w2 = isDone && m.winner?.id===m.t2?.id;
                      return (
                        <div key={m.id} style={{ background:"var(--navy-mid)", border:`1px solid ${isDone?"var(--navy-border)":isAprv?"rgba(91,141,238,0.4)":"rgba(201,168,76,0.4)"}`, borderRadius:6, padding:"10px 14px", minWidth:220, flex:"0 0 auto" }}>
                          {/* Team 1 */}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:w1?"var(--gold-light)":"var(--white)" }}>{m.t1?.name||"TBD"}</span>
                            {isDone && <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:w1?"var(--gold)":"var(--muted)" }}>{m.s1}</span>}
                          </div>
                          {/* Team 2 */}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0" }}>
                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:w2?"var(--gold-light)":"var(--white)" }}>{m.t2?.name||"TBD"}</span>
                            {isDone && <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:w2?"var(--gold)":"var(--muted)" }}>{m.s2}</span>}
                          </div>
                          {isDone && m.ot && <div style={{ fontSize:9, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, marginTop:3, letterSpacing:1 }}>⭐ OT</div>}
                          {!isDone && (
                            <><div style={{ display:"flex", gap:0, marginTop:6, borderTop:"1px solid rgba(255,255,255,0.04)", paddingTop:6 }}>
                              <button onClick={()=>openScore(m.id,"W")} style={{ flex:1, padding:"4px", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", color:"var(--gold)", border:"1px solid rgba(201,168,76,0.35)", background:"rgba(201,168,76,0.08)", borderRadius:"3px 0 0 3px", outline:"none" }}>📋 Score</button>
                              <button onClick={()=>openSelfReport(m.id,"W")} style={{ flex:1, padding:"4px", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", color:"var(--muted)", border:"1px solid rgba(255,255,255,0.1)", borderLeft:"none", background:"transparent", borderRadius:"0 3px 3px 0", outline:"none" }}>📱 Self</button>
                            </div>
                            {/* Table assign + note buttons */}
                            <div style={{ display:"flex", gap:4, marginTop:4 }}>
                              <button onClick={()=>{setTableAssignId(m.id);setTableAssignVal(m.tableAssignment||"");}}
                                title={m.tableAssignment?"Table "+m.tableAssignment:"Assign table"}
                                style={{ flex:1, padding:"3px 4px", fontSize:8, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:0.5, cursor:"pointer", color:m.tableAssignment?"var(--gold)":"var(--muted)", border:`1px solid ${m.tableAssignment?"rgba(201,168,76,0.4)":"rgba(255,255,255,0.08)"}`, background:m.tableAssignment?"rgba(201,168,76,0.08)":"transparent", borderRadius:3, outline:"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                📌 {m.tableAssignment?`T${m.tableAssignment}`:"Table"}
                              </button>
                              <button onClick={()=>{setMatchNoteId(m.id);setMatchNoteText(m.note||"");}}
                                title={m.note||"Add note"}
                                style={{ flex:1, padding:"3px 4px", fontSize:8, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:0.5, cursor:"pointer", color:m.note?"#5b8dee":"var(--muted)", border:`1px solid ${m.note?"rgba(91,141,238,0.4)":"rgba(255,255,255,0.08)"}`, background:m.note?"rgba(91,141,238,0.07)":"transparent", borderRadius:3, outline:"none" }}>
                                📝 {m.note?"Note ✓":"Note"}
                              </button>
                            </div></>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div style={{ display:"flex", gap:10, alignItems:"center", paddingTop:8, borderTop:"1px solid var(--navy-border)" }}>
              <Btn v="ghost" sz="sm" onClick={()=>{
                const newR = genSetSchedule(teams2, 1)[0];
                updateAndSave({ ...bkt, rounds:[...rounds, { ...newR, name:`Week ${rounds.length+1}` }] });
              }}>+ Add Week</Btn>
              <span style={{ fontSize:11, color:"var(--muted)" }}>Generates a new round of matchups avoiding recent rematches</span>
            </div>
          </div>
        </div>
      );
    }

    // ── RANDOM PARTNER (single draw → SE bracket + partner cards) ───────────
    if (isRP) {
      const allTeams = rounds.flatMap(r=>r.matches||[]).flatMap(m=>[m.t1,m.t2]).filter(Boolean);
      const uniqueTeams = [...new Map(allTeams.map(t=>[t.id,t])).values()];
      const allM = rounds.flatMap(r=>r.matches||[]);
      const done = allM.filter(m=>m.status==="complete").length;
      const total = allM.filter(m=>m.status!=="bye").length;
      return (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {[["Teams",uniqueTeams.length,"var(--gold)"],["Matches",total,"var(--white)"],["Done",done,"var(--green)"]].map(([l,v,c])=>(
              <div key={l} style={{ ...css.card, padding:"10px 16px", textAlign:"center", flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ ...css.card, padding:16, background:"rgba(91,141,238,0.04)", borderColor:"rgba(91,141,238,0.25)" }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, letterSpacing:3, color:"#5b8dee", marginBottom:12 }}>🎲 PARTNER DRAW</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))", gap:8 }}>
              {uniqueTeams.map((t,i)=>(
                <div key={t.id} style={{ background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:4, padding:"10px 14px" }}>
                  <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:3 }}>Team {i+1}</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:"var(--gold-light)" }}>{t.name}</div>
                </div>
              ))}
            </div>
          </div>
          <BracketStrip rounds={rounds} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} onSelfReport={(id)=>openSelfReport(id,"W")} />
        </div>
      );
    }

    if (!isRR) return <BracketStrip rounds={rounds} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} onSelfReport={(id)=>openSelfReport(id,"W")} />;

    // Round Robin view: standings + round-by-round
    const allM = rounds.flatMap(r => r.matches || []);
    const total = allM.filter(m => m.status !== "bye").length;
    const done  = allM.filter(m => m.status === "complete").length;
    const standings = calcRRStandings(rounds);
    const allDone = total > 0 && done === total;
    const champion = allDone ? standings[0]?.team : null;

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

        {/* Champion banner */}
        {champion && (
          <div style={{ ...css.card, padding:"16px 20px", borderColor:"rgba(201,168,76,0.4)", background:"rgba(201,168,76,0.06)", display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ fontSize:32 }}>🏆</div>
            <div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, letterSpacing:3, color:"var(--gold)" }}>TOURNAMENT CHAMPION</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--gold-light)" }}>{champion.name}</div>
            </div>
          </div>
        )}

        {/* Standings table */}
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6 }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)" }}>📊 STANDINGS</div>
            <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600, letterSpacing:1 }}>
              {done}/{total} complete · Tiebreakers: Cup Diff → H2H → H2H Cup Diff → SOS → Common Opp → 🪙 Coin Flip
            </div>
          </div>
          <div className="resp-table"><table style={{ width:"100%", borderCollapse:"collapse",minWidth:480 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--navy-border)" }}>
                {["#","Team","W","L","Cup Diff","Made","Allowed","GP"].map(h => (
                  <th key={h} style={{ padding:"7px 12px", textAlign: h==="Team"?"left":"center", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {standings.map((row, i) => {
                const medal = i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
                const isChamp = allDone && i === 0;
                const isCoinFlip = row.tiebreaker === "coin";
                return (
                  <tr key={row.team?.name||i} style={{ borderBottom:"1px solid rgba(255,255,255,0.03)", background: isChamp ? "rgba(201,168,76,0.06)" : i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color: medal||"var(--muted)", width:36 }}>{i+1}</td>
                    <td style={{ padding:"9px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color: medal||"var(--white)" }}>{row.team?.name}</div>
                        {isCoinFlip && <span title="Position determined by coin flip" style={{ fontSize:11, cursor:"default" }}>🪙</span>}
                      </div>
                    </td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--green)" }}>{row.w}</td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--red)" }}>{row.l}</td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color: row.cf>0?"var(--green)":row.cf<0?"var(--red)":"var(--muted)" }}>{row.cf>0?"+":""}{row.cf}</td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontSize:13, color:"var(--muted)" }}>{row.cm}</td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontSize:13, color:"var(--muted)" }}>{row.ca}</td>
                    <td style={{ padding:"9px 12px", textAlign:"center", fontSize:13, color:"var(--muted)" }}>{row.played}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>

        {/* Round-by-round match cards */}
        <div style={{ ...css.card, padding:16 }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color:"var(--gold)", marginBottom:14 }}>MATCH SCHEDULE</div>
          <BracketStrip rounds={rounds} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} />
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:3, lineHeight:1 }}>{activeTournament.name}</div>
            <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap", alignItems:"center" }}>
              <Badge color="green">ACTIVE</Badge>
              <Badge color={activeTournament.type==="pro"?"gold":""}>{activeTournament.type}</Badge>
              {activeTournament.ranked && <Badge color="blue">Ranked</Badge>}
              {isDE && <Badge color="blue">Double Elim</Badge>}
              <span style={{ fontSize:12, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600, letterSpacing:1 }}>
                {FORMATS.find(f=>f.v===activeTournament.format)?.l} · {activeTournament.teamSize} · {maxCups} cups
              </span>
            </div>
          </div>
          {/* Quick stats */}
          {(() => {
            const allM = allMatches();
            const total = allM.filter(m=>m.status!=="bye"&&m.status!=="waiting").length;
            const done  = allM.filter(m=>m.status==="complete").length;
            const live  = allM.filter(m=>m.status==="pending"&&m.t1&&m.t2).length;
            const pct   = total>0?Math.round(done/total*100):0;
            return (
              <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                {[
                  [done+"/"+total,"Matches",done===total&&total>0?"var(--green)":"var(--gold)"],
                  [live,"In Play","var(--green)"],
                  [pct+"%","Complete","var(--muted)"],
                ].map(([v,l,c])=>(
                  <div key={l} style={{ textAlign:"center", minWidth:48 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                    <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Undo button */}
        {undoStack.length > 0 && (
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:4 }}>
            <Btn sz="sm" v="ghost" style={{ fontSize:11, color:"var(--muted)" }}
              onClick={()=>{
                const prev = undoStack[undoStack.length-1];
                setUndoStack(s=>s.slice(0,-1));
                const updated = { ...activeTournament, bracket:prev };
                setActiveTournament(updated);
                setTournaments(p=>p.map(t=>t.id===activeTournament.id?updated:t));
                setUndoToast(true); setTimeout(()=>setUndoToast(false),2000);
              }}>↩ Undo ({undoStack.length})</Btn>
          </div>
        )}
        {undoToast && (
          <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:6, padding:"8px 18px", fontSize:12, color:"var(--muted)", zIndex:9999, pointerEvents:"none" }}>
            ↩ Bracket change undone
          </div>
        )}

        {/* Progress bar */}
        {(() => {
          const allM = allMatches();
          const total = allM.filter(m=>m.status!=="bye"&&m.status!=="waiting").length;
          const done  = allM.filter(m=>m.status==="complete").length;
          const pct   = total>0?Math.round(done/total*100):0;
          return total > 0 ? (
            <div style={{ marginTop:12 }}>
              <div style={{ height:4, background:"rgba(255,255,255,0.07)", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:pct===100?"var(--gold)":"var(--green)", borderRadius:2, transition:"width 0.4s" }} />
              </div>
            </div>
          ) : null;
        })()}

        {/* ── Bracket Action Bar ─────────────────────────────────────────────── */}
        <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap", alignItems:"center" }}>
          {/* Zoom controls */}
          <div style={{ display:"flex", alignItems:"center", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
            {[0.7,1.0,1.25,1.5].map(s=>(
              <button key={s} onClick={()=>setBracketScale(s)}
                style={{ padding:"4px 9px", background:bracketScale===s?"rgba(201,168,76,0.15)":"transparent", border:"none", borderRight:"1px solid var(--navy-border)", color:bracketScale===s?"var(--gold)":"var(--muted)", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:0.5 }}>
                {s===1.0?"100%":s===0.7?"70%":s===1.25?"125%":"150%"}
              </button>
            ))}
          </div>
          <Btn sz="sm" v="ghost" onClick={()=>{
            const el = document.getElementById('bracket-printable');
            if(!el) return;
            const win = window.open('','_blank');
            win.document.write(`<!DOCTYPE html><html><head><title>${activeTournament.name} — Bracket</title><style>*{margin:0;padding:0;box-sizing:border-box;font-family:sans-serif;}body{background:#fff;color:#000;padding:20px;}.bracket-print-area{transform-origin:top left;}</style></head><body><h2 style="margin-bottom:12px;font-size:18px;">${activeTournament.name} — Bracket</h2>${el.innerHTML}</body></html>`);
            win.document.close();
            win.focus();
            setTimeout(()=>{ win.print(); win.close(); }, 400);
          }} style={{ fontSize:11 }}>🖨 Print</Btn>
          {!activeTournament.finalized && (
            <Btn sz="sm" v="ghost" onClick={openSeedReorder} style={{ fontSize:11 }}>🔢 Reorder Seeds</Btn>
          )}
          {!activeTournament.finalized && (
            <Btn sz="sm" v="ghost" onClick={()=>setResetModal(true)} style={{ fontSize:11, color:"var(--red)", borderColor:"rgba(217,79,61,0.3)" }}>↺ Reset Bracket</Btn>
          )}
          {!activeTournament.finalized && (() => {
            const allM = allMatches();
            const total = allM.filter(m=>m.status!=="bye"&&m.status!=="waiting"&&m.t1&&m.t2).length;
            const done  = allM.filter(m=>m.status==="complete").length;
            const pct   = total > 0 ? Math.round(done/total*100) : 0;
            return (
              <Btn sz="sm" onClick={doFinalize}
                style={{ fontSize:11, background:"rgba(201,168,76,0.12)", borderColor:"rgba(201,168,76,0.4)", color:"var(--gold)", marginLeft:"auto" }}>
                {pct===100 ? "🏆 Finalize Tournament" : `🏁 Finalize (${done}/${total} done)`}
              </Btn>
            );
          })()}
          {activeTournament.finalized && (
            <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
              <Badge color="green">✓ FINALIZED</Badge>
              <span style={{ fontSize:11, color:"var(--muted)" }}>{activeTournament.finalizedAt ? new Date(activeTournament.finalizedAt).toLocaleString() : ""}</span>
            </div>
          )}

          {/* Zoom controls */}
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4 }}>
            <button onClick={()=>{setBracketZoom(z=>{const nz=Math.max(50,z-10);setBracketScale(nz/100);return nz;});}}
              style={{ width:24,height:24,borderRadius:4,background:"var(--navy-mid)",border:"1px solid var(--navy-border)",color:"var(--white)",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>−</button>
            <span style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,minWidth:36,textAlign:"center" }}>{bracketZoom}%</span>
            <button onClick={()=>{setBracketZoom(z=>{const nz=Math.min(200,z+10);setBracketScale(nz/100);return nz;});}}
              style={{ width:24,height:24,borderRadius:4,background:"var(--navy-mid)",border:"1px solid var(--navy-border)",color:"var(--white)",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>+</button>
            <button onClick={()=>{setBracketZoom(100);setBracketScale(1.0);setBracketPanX(0);setBracketPanY(0);}}
              style={{ width:24,height:24,borderRadius:4,background:"var(--navy-mid)",border:"1px solid var(--navy-border)",color:"var(--muted)",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center" }} title="Reset view">⌂</button>
            <button onClick={()=>window.print()}
              style={{ height:24,padding:"0 8px",borderRadius:4,background:"var(--navy-mid)",border:"1px solid var(--navy-border)",color:"var(--muted)",cursor:"pointer",fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>🖨 PRINT</button>
          </div>
        </div>
      </div>

      {/* ── Live Match Ticker ─────────────────────────────────────────────── */}
      {tickerOpen && (
        <MatchTicker
          matches={allMatches()}
          onClickMatch={(m) => {
            const side = isDE
              ? ((bkt.wb||[]).some(r=>r.matches.some(x=>x.id===m.id)) ? "W"
                :(bkt.lb||[]).some(r=>r.matches.some(x=>x.id===m.id)) ? "L" : "GF")
              : "W";
            if (m.status==="pending" && m.t1 && m.t2) openScore(m.id, side);
            else if (m.status==="disputed" || m.status==="pending_approval") {
              setQuickDisputeId(m.id);
            }
          }}
        />
      )}

      {/* ── Recent Scores Feed ────────────────────────────────────────────── */}
      <RecentScoresFeed matches={allMatches()} />

      {/* Approval Toast */}
      {approvalToast && (
        <div style={{ position:"fixed",top:20,right:20,zIndex:2000,background:"var(--navy-mid)",border:`1px solid ${approvalToast.color}`,borderRadius:6,padding:"12px 20px",fontSize:13,fontWeight:600,color:approvalToast.color,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"fadeIn 0.2s ease",maxWidth:360 }}>
          {approvalToast.msg}
        </div>
      )}

      {/* Approval queue banner */}
      {pendingApprovals().length > 0 && (() => {
        const disputedCount   = pendingApprovals().filter(m=>m.status==="disputed").length;
        const approvalCount   = pendingApprovals().filter(m=>m.status==="pending_approval").length;
        return (
          <div onClick={()=>setApprovalPanel(v=>!v)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", marginBottom:14, background: disputedCount>0?"rgba(217,79,61,0.08)":"rgba(91,141,238,0.1)", border:`1px solid ${disputedCount>0?"rgba(217,79,61,0.4)":"rgba(91,141,238,0.35)"}`, borderRadius:6, cursor:"pointer" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background: disputedCount>0?"var(--red)":"#5b8dee", animation:"pulse 1.5s infinite" }} />
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1, color: disputedCount>0?"var(--red)":"#5b8dee" }}>
                {disputedCount > 0 && `⚠ ${disputedCount} DISPUTED`}
                {disputedCount > 0 && approvalCount > 0 && " · "}
                {approvalCount > 0 && `${approvalCount} awaiting approval`}
              </span>
            </div>
            <span style={{ fontSize:11, color: disputedCount>0?"var(--red)":"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{approvalPanel?"▲ Hide":"▼ Review"}</span>
          </div>
        );
      })()}

      {/* Approval + Dispute queue panel */}
      {approvalPanel && pendingApprovals().length > 0 && (() => {
        const disputedList  = pendingApprovals().filter(m=>m.status==="disputed");
        const approvalList  = pendingApprovals().filter(m=>m.status==="pending_approval");

        function matchSide(m) {
          return isDE
            ? ((bkt.wb||[]).some(r=>r.matches.some(x=>x.id===m.id)) ? "W"
              :(bkt.lb||[]).some(r=>r.matches.some(x=>x.id===m.id)) ? "L" : "GF")
            : "W";
        }

        return (
          <div style={{ ...css.card, marginBottom:20, padding:0, overflow:"hidden", borderColor: disputedList.length>0?"rgba(217,79,61,0.35)":"rgba(91,141,238,0.3)" }}>
            {/* Header */}
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", background: disputedList.length>0?"rgba(217,79,61,0.06)":"rgba(91,141,238,0.07)", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:3, color: disputedList.length>0?"var(--red)":"#5b8dee" }}>
                  {disputedList.length>0 ? "⚠ SCORE DISPUTES" : "📋 SCORE APPROVAL QUEUE"}
                </span>
                <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>
                  {disputedList.length>0 ? `${disputedList.length} dispute${disputedList.length!==1?"s":""} · ${approvalList.length} pending` : "Review and approve or reject self-reported scores"}
                </span>
              </div>
              {disputeLog.length > 0 && (
                <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                  {disputeLog.length} event{disputeLog.length!==1?"s":""} logged
                </span>
              )}
            </div>

            {/* Disputed matches first — with side-by-side conflict UI */}
            {disputedList.map(m => {
              const ps  = m.pendingScore;
              const ps2 = m.pendingScore2;
              const side = matchSide(m);
              const isExpanded = quickDisputeId === m.id;
              return (
                <div key={m.id} style={{ padding:"14px 16px", borderBottom:"1px solid var(--navy-border)", background:"rgba(217,79,61,0.04)" }}>
                  {/* Match title */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:10, background:"rgba(217,79,61,0.15)", color:"var(--red)", padding:"2px 8px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5 }}>⚠ DISPUTED</span>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14 }}>{m.t1?.name} vs {m.t2?.name}</span>
                    <span style={{ fontSize:11, color:"var(--muted)" }}>— two conflicting score reports</span>
                  </div>

                  {/* Side-by-side score comparison */}
                  {ps && ps2 && (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 40px 1fr", gap:8, alignItems:"center", marginBottom:12 }}>
                      {/* Report 1 */}
                      <div style={{ background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.2)", borderRadius:6, padding:"10px 12px", textAlign:"center" }}>
                        <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"#5b8dee", textTransform:"uppercase", marginBottom:6 }}>Report 1</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--white)", lineHeight:1 }}>{ps.s1} – {ps.s2}</div>
                        <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>
                          {ps.s1>ps.s2 ? m.t1?.name : m.t2?.name} wins
                        </div>
                        {ps.ot && <div style={{ fontSize:9, color:"var(--gold)", marginTop:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>⭐ OT</div>}
                        <Btn sz="sm" full style={{ marginTop:8, justifyContent:"center", background:"rgba(91,141,238,0.12)", borderColor:"rgba(91,141,238,0.3)", color:"#5b8dee" }}
                          onClick={()=>quickResolveDispute(m.id, side, true)}>
                          ✓ Accept This
                        </Btn>
                      </div>
                      {/* VS */}
                      <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:"var(--red)", lineHeight:1 }}>≠</div>
                      {/* Report 2 */}
                      <div style={{ background:"rgba(217,79,61,0.06)", border:"1px solid rgba(217,79,61,0.2)", borderRadius:6, padding:"10px 12px", textAlign:"center" }}>
                        <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--red)", textTransform:"uppercase", marginBottom:6 }}>Report 2</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--white)", lineHeight:1 }}>{ps2.s1} – {ps2.s2}</div>
                        <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>
                          {ps2.s1>ps2.s2 ? m.t1?.name : m.t2?.name} wins
                        </div>
                        {ps2.ot && <div style={{ fontSize:9, color:"var(--gold)", marginTop:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>⭐ OT</div>}
                        <Btn sz="sm" full style={{ marginTop:8, justifyContent:"center", background:"rgba(217,79,61,0.08)", borderColor:"rgba(217,79,61,0.25)", color:"var(--red)" }}
                          onClick={()=>quickResolveDispute(m.id, side, false)}>
                          ✓ Accept This
                        </Btn>
                      </div>
                    </div>
                  )}

                  {/* Admin override actions */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <Btn v="ghost" sz="sm" onClick={()=>openScore(m.id, side)}>✏ Override Score</Btn>
                    <Btn v="danger" sz="sm" onClick={()=>rejectScore(m.id)}>✕ Reject Both — Reset</Btn>
                  </div>
                </div>
              );
            })}

            {/* Pending approval matches */}
            {approvalList.map(m => {
              const ps   = m.pendingScore;
              const side = matchSide(m);
              return (
                <div key={m.id} style={{ padding:"14px 16px", borderBottom:"1px solid var(--navy-border)" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                    <div style={{ flex:1, minWidth:160 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                        <span style={{ fontSize:10, background:"rgba(91,141,238,0.15)", color:"#5b8dee", padding:"2px 7px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5 }}>AWAITING APPROVAL</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14 }}>{m.t1?.name}</span>
                        {ps && <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"#5b8dee" }}>{ps.s1} – {ps.s2}</span>}
                        <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14 }}>{m.t2?.name}</span>
                        {ps?.ot && <span style={{ fontSize:10, color:"var(--gold)", background:"rgba(201,168,76,0.15)", padding:"1px 6px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>OT</span>}
                      </div>
                      {ps?.reportedAt && <div style={{ fontSize:11, color:"var(--muted)", marginTop:3 }}>Reported · {new Date(ps.reportedAt).toLocaleTimeString()}</div>}
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0, flexWrap:"wrap" }}>
                      {ps && <Btn v="success" sz="sm" onClick={()=>approveScore(m.id, side, ps.s1, ps.s2, ps.ot||false)}>✓ Approve</Btn>}
                      <Btn v="ghost" sz="sm" onClick={()=>openScore(m.id, side)}>✏ Override</Btn>
                      <Btn v="danger" sz="sm" onClick={()=>rejectScore(m.id)}>✕ Reject</Btn>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Dispute log footer */}
            {disputeLog.length > 0 && (
              <details>
                <summary style={{ cursor:"pointer", padding:"8px 16px", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, color:"var(--muted)", textTransform:"uppercase", background:"rgba(255,255,255,0.01)", borderTop:"1px solid var(--navy-border)" }}>
                  ▸ Dispute / Correction Log ({disputeLog.length})
                </summary>
                <div style={{ padding:"8px 16px 12px" }}>
                  {[...disputeLog].reverse().map(entry => (
                    <div key={entry.id} style={{ display:"flex", gap:10, padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.04)", fontSize:12 }}>
                      <span style={{ color: entry.type==="correction"?"var(--gold)":entry.type==="dispute_rejected"?"var(--red)":"var(--green)", flexShrink:0, width:16, textAlign:"center" }}>
                        {entry.type==="correction"?"✏":entry.type==="dispute_rejected"?"✕":"✓"}
                      </span>
                      <div style={{ flex:1 }}>
                        <span style={{ fontWeight:600 }}>{entry.t1} vs {entry.t2}</span>
                        <span style={{ color:"var(--muted)", marginLeft:8 }}>{entry.note}</span>
                        {entry.from && <span style={{ color:"var(--muted)", marginLeft:6, fontSize:11 }}>{entry.from} → {entry.to}</span>}
                        {entry.accepted && <span style={{ color:"var(--green)", marginLeft:6, fontSize:11 }}>{entry.accepted} accepted</span>}
                      </div>
                      <span style={{ color:"var(--muted)", fontSize:10, flexShrink:0 }}>{entry.at}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })()}

      {/* Legend */}
      <div style={{ display:"flex", gap:14, marginBottom:14, flexWrap:"wrap" }}>
        {[["🟡","Pending"],["🟢","Complete"],["⏳","Awaiting Approval"],["⚠","Disputed"],["⭐","OT (±1 cup diff)"],["🔁","Bracket Reset"],["✏","Corrected"]].map(([ic,lb])=>(
          <div key={lb} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}><span>{ic}</span>{lb}</div>
        ))}
      </div>

      {isDE ? (() => {
        // Tag LB drop-in rounds (odd rounds where WB losers enter)
        const lb = (bkt.lb||[]).map((r,i) => ({ ...r, isDropIn: i%2===0 }));

        // Progress counters
        const wbMatches = (bkt.wb||[]).flatMap(r=>r.matches).filter(m=>m.status!=="bye"&&m.status!=="waiting");
        const lbMatches = lb.flatMap(r=>r.matches).filter(m=>m.status!=="bye"&&m.status!=="waiting");
        const wbDone = wbMatches.filter(m=>m.status==="complete").length;
        const lbDone = lbMatches.filter(m=>m.status==="complete").length;

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

            {/* Champion banner */}
            {bkt.gf?.winner && (
              <div style={{ ...css.card, padding:"16px 20px", borderColor:"rgba(201,168,76,0.5)", background:"rgba(201,168,76,0.07)", display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ fontSize:36 }}>🏆</div>
                <div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, letterSpacing:3, color:"var(--gold)" }}>TOURNAMENT CHAMPION</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:"var(--gold-light)" }}>{bkt.gf.winner.name}</div>
                </div>
              </div>
            )}

            {/* Flow diagram key */}
            <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"center", padding:"8px 12px", background:"rgba(255,255,255,0.02)", borderRadius:4, border:"1px solid var(--navy-border)" }}>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>Flow</span>
              {[
                ["var(--gold)","WB win → advance"],
                ["var(--red)","WB loss → LB"],
                ["var(--green)","Complete"],
                ["rgba(255,255,255,0.15)","Waiting"],
              ].map(([c,l])=>(
                <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}>
                  <svg width="24" height="6"><path d="M0,3 L20,3" stroke={c} strokeWidth="2"/><polygon points="18,0 24,3 18,6" fill={c}/></svg>
                  {l}
                </div>
              ))}
              <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"rgba(217,79,61,0.8)" }}>
                <span style={{ fontSize:9, background:"rgba(217,79,61,0.2)", padding:"1px 5px", borderRadius:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>DROP-IN</span>
                WB losers enter LB here
              </div>
            </div>

            {/* Winners Bracket */}
            <div style={{ ...css.card, padding:16, borderColor:"rgba(201,168,76,0.2)" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ background:"var(--gold)", color:"var(--navy)", padding:"2px 10px", borderRadius:3, fontSize:11, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:2 }}>W</span>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:3, color:"var(--gold)" }}>WINNERS BRACKET</span>
                </div>
                <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>
                  {wbDone}/{wbMatches.length} complete · Losers drop to LB
                </div>
              </div>
              <BracketStrip rounds={bkt.wb||[]} accentColor="var(--gold)" onScore={(id)=>openScore(id,"W")} />
            </div>

            {/* WB→LB drop arrow banner */}
            {lb.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"6px 0" }}>
                <div style={{ flex:1, height:1, background:"linear-gradient(to right, transparent, rgba(217,79,61,0.4))" }} />
                <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 14px", border:"1px solid rgba(217,79,61,0.35)", borderRadius:20, background:"rgba(217,79,61,0.07)" }}>
                  <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8,2 L8,11 M4,8 L8,13 L12,8" stroke="rgba(217,79,61,0.9)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span style={{ fontSize:10, color:"rgba(217,79,61,0.9)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>WB Losers Drop Into Losers Bracket</span>
                </div>
                <div style={{ flex:1, height:1, background:"linear-gradient(to left, transparent, rgba(217,79,61,0.4))" }} />
              </div>
            )}

            {/* Losers Bracket */}
            <div style={{ ...css.card, padding:16, borderColor:"rgba(217,79,61,0.25)" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ background:"var(--red)", color:"var(--white)", padding:"2px 10px", borderRadius:3, fontSize:11, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:2 }}>L</span>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:3, color:"var(--red)" }}>LOSERS BRACKET</span>
                  <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>— Two losses = eliminated</span>
                </div>
                {lbMatches.length > 0 && (
                  <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>
                    {lbDone}/{lbMatches.length} complete
                  </div>
                )}
              </div>
              {lb.length > 0
                ? <BracketStrip rounds={lb} accentColor="var(--red)" onScore={(id)=>openScore(id,"L")} />
                : <div style={{ color:"var(--muted)", fontSize:13, padding:"12px 0", fontStyle:"italic" }}>Waiting for first WB results…</div>
              }
            </div>

            {/* LB→GF arrow */}
            {bkt.gf && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"6px 0" }}>
                <div style={{ flex:1, height:1, background:"linear-gradient(to right, transparent, rgba(201,168,76,0.4))" }} />
                <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 14px", border:"1px solid rgba(201,168,76,0.35)", borderRadius:20, background:"rgba(201,168,76,0.07)" }}>
                  <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8,2 L8,11 M4,8 L8,13 L12,8" stroke="rgba(201,168,76,0.9)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span style={{ fontSize:10, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>LB Champion Advances to Grand Final</span>
                </div>
                <div style={{ flex:1, height:1, background:"linear-gradient(to left, transparent, rgba(201,168,76,0.4))" }} />
              </div>
            )}

            {/* Grand Final */}
            {bkt.gf && (
              <div style={{ ...css.card, padding:16, borderColor:"rgba(201,168,76,0.4)", background:"rgba(201,168,76,0.03)" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:3, color:"var(--gold-light)", marginBottom:14, display:"flex", alignItems:"center", gap:10 }}>
                  🏆 GRAND FINAL
                  <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600, letterSpacing:1 }}>— WB Champion vs LB Champion · LB win triggers bracket reset</span>
                </div>
                <div style={{ display:"flex", gap:20, flexWrap:"wrap", alignItems:"flex-start" }}>
                  <div>
                    <div style={{ fontSize:10, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, marginBottom:6, textTransform:"uppercase" }}>Game 1</div>
                    <MatchCard match={bkt.gf} accentColor="var(--gold-light)" onScore={bkt.gf.status==="pending"?()=>openScore(bkt.gf.id,"GF"):null} />
                  </div>
                  {bkt.gf.reset && (
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        <svg width="14" height="14" viewBox="0 0 14 14"><path d="M7,1 L7,10 M3,7 L7,12 L11,7" stroke="var(--red)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        <div style={{ fontSize:10, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>🔁 Bracket Reset — Game 2</div>
                      </div>
                      <MatchCard match={bkt.gf.reset} accentColor="var(--red)" onScore={bkt.gf.reset.status==="pending"?()=>openScore(bkt.gf.reset.id,"GF"):null} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })() : renderSERounds()}

      {/* Score Modal */}
      {/* Self-Report Modal */}
      <Modal open={!!selfReportModal} onClose={()=>setSelfReportModal(null)} title="Self-Report Score" width={420}>
        {selfReportModal && (() => {
          const m = allMatches().find(x=>x.id===selfReportModal.matchId);
          if (!m) return null;
          return (
            <>
              <div style={{ background:"rgba(91,141,238,0.08)", border:"1px solid rgba(91,141,238,0.25)", borderRadius:4, padding:"8px 12px", marginBottom:14, fontSize:12, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                📱 PLAYER SELF-REPORT — Score will be reviewed by a scorekeeper before being finalized
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 40px 1fr", gap:10, alignItems:"center", marginBottom:16 }}>
                {[{team:m.t1,val:sr1,set:setSr1},{team:m.t2,val:sr2,set:setSr2}].map((side,i)=>(
                  <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{side.team?.name||"TBD"}</div>
                    <input style={{ ...css.input, textAlign:"center", fontSize:36, fontFamily:"'Bebas Neue',sans-serif", padding:8 }} type="number" min="0" max={maxCups} value={side.val} onChange={e=>side.set(e.target.value)} placeholder="0" />
                  </div>
                ))}
                <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--muted)" }}>VS</div>
              </div>
              <div style={{ background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.2)", borderRadius:4, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:10 }}>
                <input id="sr-ot" type="checkbox" checked={srOt} onChange={e=>setSrOt(e.target.checked)} style={{ width:16, height:16, cursor:"pointer", accentColor:"var(--gold)" }} />
                <label htmlFor="sr-ot" style={{ fontSize:13, cursor:"pointer" }}><strong>Overtime</strong> — cup differential = ±1 cup</label>
              </div>
              <div style={{ fontSize:12, color:"var(--muted)", marginBottom:14, lineHeight:1.6 }}>
                A scorekeeper will confirm this report before results are made official.
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <Btn v="ghost" onClick={()=>setSelfReportModal(null)}>Cancel</Btn>
                <Btn onClick={()=>submitSelfReport(selfReportModal.matchId, selfReportModal.side, "team")} disabled={!sr1||!sr2||sr1===sr2}>📱 Submit Report</Btn>
              </div>
            </>
          );
        })()}
      </Modal>

      <Modal open={!!scoreModal} onClose={()=>setScoreModal(null)} title={(() => {
        const m = modalMatch;
        if (!m) return "Enter Score";
        if (m.status==="complete") return "Correct Score";
        if (isGF && gfReset) return "Bracket Reset Score";
        return "Enter Match Score";
      })()} width={480}>
        {modalMatch && (() => {
          const n1 = parseInt(s1), n2 = parseInt(s2);
          const valid = !isNaN(n1) && !isNaN(n2) && n1 !== n2 && n1 >= 0 && n2 >= 0;
          const winnerName = valid ? (n1 > n2 ? modalMatch.t1?.name : modalMatch.t2?.name) : null;
          const cupDiffVal = valid ? (ot ? 1 : Math.abs(n1 - n2)) : null;
          const isCorrection = modalMatch.status === "complete";

          function stepScore(setter, val, delta) {
            const cur = parseInt(val) || 0;
            const next = Math.max(0, Math.min(maxCups, cur + delta));
            setter(String(next));
          }

          const PRESETS = maxCups >= 10
            ? [[10,6],[10,7],[10,8],[10,9]]
            : [[maxCups, Math.floor(maxCups*0.6)],[maxCups, Math.floor(maxCups*0.7)],[maxCups, Math.floor(maxCups*0.8)]];

          return (
            <>
              {isCorrection && (
                <div style={{ background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:4, padding:"8px 12px", marginBottom:12, fontSize:12, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                  ✏ CORRECTING SCORE — was {modalMatch.s1}–{modalMatch.s2} ({modalMatch.s1>modalMatch.s2?modalMatch.t1?.name:modalMatch.t2?.name} won)
                </div>
              )}
              {!isCorrection && scoreModal?.side==="L" && (
                <div style={{ background:"rgba(217,79,61,0.08)", border:"1px solid rgba(217,79,61,0.2)", borderRadius:4, padding:"8px 12px", marginBottom:14, fontSize:12, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                  ⚠️ LOSERS BRACKET — Loser of this match is eliminated
                </div>
              )}
              {!isCorrection && isGF && (
                <div style={{ background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:4, padding:"8px 12px", marginBottom:14, fontSize:12, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                  🏆 GRAND FINAL — {gfReset?"Bracket Reset (Game 2)":"Game 1 · LB win triggers bracket reset"}
                </div>
              )}

              {/* Quick presets */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Quick Presets</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {PRESETS.map(([w,l]) => (
                    <React.Fragment key={`${w}-${l}`}>
                      <button onClick={()=>{setS1(String(w));setS2(String(l));}} style={{ padding:"4px 12px", background:s1===String(w)&&s2===String(l)?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.04)", border:`1px solid ${s1===String(w)&&s2===String(l)?"rgba(201,168,76,0.4)":"var(--navy-border)"}`, borderRadius:4, color:s1===String(w)&&s2===String(l)?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Bebas Neue',sans-serif", cursor:"pointer", letterSpacing:1 }}>
                        {w}–{l}
                      </button>
                      <button onClick={()=>{setS1(String(l));setS2(String(w));}} style={{ padding:"4px 12px", background:s1===String(l)&&s2===String(w)?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.04)", border:`1px solid ${s1===String(l)&&s2===String(w)?"rgba(201,168,76,0.4)":"var(--navy-border)"}`, borderRadius:4, color:s1===String(l)&&s2===String(w)?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Bebas Neue',sans-serif", cursor:"pointer", letterSpacing:1 }}>
                        {l}–{w}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Score steppers */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 48px 1fr", gap:12, alignItems:"center", marginBottom:16 }}>
                {[{team:modalMatch.t1,val:s1,set:setS1},{team:modalMatch.t2,val:s2,set:setS2}].map((side,i)=>(
                  <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, marginBottom:10, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color: winnerName===side.team?.name?"var(--gold-light)":"var(--white)" }}>
                      {side.team?.name||"TBD"}
                      {winnerName===side.team?.name && <span style={{ marginLeft:6, fontSize:12 }}>🏆</span>}
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
                      <button onClick={()=>stepScore(side.set,side.val,-1)} style={{ width:36, height:36, borderRadius:6, border:"1px solid var(--navy-border)", background:"var(--navy)", color:"var(--white)", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontFamily:"'Bebas Neue',sans-serif" }}>−</button>
                      <input
                        style={{ ...css.input, textAlign:"center", fontSize:40, fontFamily:"'Bebas Neue',sans-serif", padding:"8px 4px", width:72, height:60, flexShrink:0 }}
                        type="number" min="0" max={maxCups}
                        value={side.val}
                        onChange={e=>side.set(e.target.value)}
                        onKeyDown={e=>{ if(e.key==="Enter"&&valid) submitScore(); }}
                        placeholder="0"
                      />
                      <button onClick={()=>stepScore(side.set,side.val,+1)} style={{ width:36, height:36, borderRadius:6, border:"1px solid var(--navy-border)", background:"var(--navy)", color:"var(--white)", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontFamily:"'Bebas Neue',sans-serif" }}>+</button>
                    </div>
                    <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", marginTop:6 }}>max {maxCups} cups</div>
                  </div>
                ))}
                <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--muted)", lineHeight:1 }}>VS</div>
              </div>

              {/* Live result preview */}
              <div style={{ background: valid?"rgba(46,204,113,0.06)":"rgba(255,255,255,0.02)", border:`1px solid ${valid?"rgba(46,204,113,0.25)":"var(--navy-border)"}`, borderRadius:6, padding:"10px 14px", marginBottom:14, transition:"all 0.2s", minHeight:44, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                <div style={{ fontSize:12, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                  {valid ? <span style={{ color:"var(--green)" }}>✓ Winner: <strong style={{ color:"var(--white)" }}>{winnerName}</strong></span> : <span>Enter scores above</span>}
                </div>
                {valid && (
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold)", lineHeight:1 }}>{ot ? "±1" : cupDiffVal}</div>
                    <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>Cup Diff</div>
                  </div>
                )}
                {!valid && s1 && s2 && s1===s2 && (
                  <div style={{ fontSize:11, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>Scores can't be equal</div>
                )}
              </div>

              {/* Overtime toggle */}
              <div onClick={()=>setOt(v=>!v)} style={{ background:ot?"rgba(201,168,76,0.1)":"rgba(255,255,255,0.02)", border:`1px solid ${ot?"rgba(201,168,76,0.4)":"var(--navy-border)"}`, borderRadius:6, padding:"10px 14px", marginBottom:isCorrection?12:16, display:"flex", alignItems:"center", gap:10, cursor:"pointer", userSelect:"none", transition:"all 0.15s" }}>
                <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${ot?"var(--gold)":"var(--muted)"}`, background:ot?"var(--gold)":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
                  {ot && <span style={{ fontSize:12, color:"var(--navy)", fontWeight:700 }}>✓</span>}
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700 }}>Overtime</div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>Cup differential = ±1 cup regardless of score</div>
                </div>
                {ot && <span style={{ marginLeft:"auto", fontSize:10, background:"rgba(201,168,76,0.15)", color:"var(--gold)", padding:"2px 8px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>OT ON</span>}
              </div>

              {isCorrection && (
                <div style={{ marginBottom:16 }}>
                  <input style={{ ...css.input, fontSize:12 }} placeholder="Reason for correction (optional)…" value={correctionNote} onChange={e=>setCorrectionNote(e.target.value)} />
                </div>
              )}

              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <Btn v="ghost" onClick={()=>setScoreModal(null)}>Cancel</Btn>
                <Btn onClick={submitScore} disabled={!valid}>{isCorrection?"✏ Apply Correction":"Submit Score"}</Btn>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* ── SEED REORDER MODAL ─────────────────────────────────────────────── */}
      <Modal open={seedReorderOpen} onClose={()=>setSeedReorderOpen(false)} title="🔢 Reorder Seeds" width={500}>
        <div style={{ marginBottom:10, display:"flex", gap:8, justifyContent:"flex-end" }}>
          <Btn sz="sm" v="ghost" onClick={applySnakeSeeds} style={{ fontSize:11 }}>🐍 Snake Seed</Btn>
          <span style={{ fontSize:11, color:"var(--muted)", alignSelf:"center" }}>Drag to reorder · saves on Apply</span>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:360, overflowY:"auto" }}>
          {seedDraftOrder.map((s,i) => {
            const isDragging = seedDragIdx === i;
            const isOver     = seedDragOver === i;
            return (
              <div key={s.id}
                draggable
                onDragStart={()=>seedDragStart(i)}
                onDragOver={e=>{e.preventDefault();seedDragOverIdx(i);}}
                onDrop={seedDrop}
                onDragEnd={()=>{setSeedDragIdx(null);setSeedDragOver(null);}}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                  background:isDragging?"rgba(201,168,76,0.07)":isOver?"rgba(91,141,238,0.1)":"rgba(255,255,255,0.02)",
                  border:`1px solid ${isOver?"#5b8dee":isDragging?"var(--gold)":"var(--navy-border)"}`,
                  borderRadius:5, cursor:"grab", transition:"all 0.12s",
                  opacity:isDragging?0.5:1 }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(201,168,76,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:15, color:"var(--gold)", flexShrink:0 }}>
                  {i+1}
                </div>
                <span style={{ flex:1, fontSize:13, fontWeight:i<4?700:400 }}>{s.name}</span>
                <span style={{ fontSize:10, color:"var(--muted)" }}>⠿</span>
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:14 }}>
          <Btn v="ghost" onClick={()=>setSeedReorderOpen(false)}>Cancel</Btn>
          <Btn onClick={commitSeedReorder}>Apply Seeds</Btn>
        </div>
      </Modal>

      {/* ── RESET BRACKET MODAL ───────────────────────────────────────────── */}
      <Modal open={resetModal} onClose={()=>setResetModal(false)} title="↺ Reset Bracket" width={420}>
        <div style={{ padding:"12px 0", fontSize:13, color:"var(--muted)", lineHeight:1.7 }}>
          This will <strong style={{color:"var(--red)"}}>delete all match results</strong> and remove the current bracket. Registrations and check-ins can optionally be preserved.
        </div>
        <div style={{ padding:"10px 14px", background:"rgba(217,79,61,0.07)", border:"1px solid rgba(217,79,61,0.25)", borderRadius:5, marginBottom:14, display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
          onClick={()=>setResetKeepRegs(k=>!k)}>
          <input type="checkbox" checked={resetKeepRegs} readOnly style={{cursor:"pointer",accentColor:"var(--gold)"}} />
          <div>
            <div style={{ fontWeight:700, fontSize:13 }}>Keep registrations & check-ins</div>
            <div style={{ fontSize:11, color:"var(--muted)" }}>Teams stay registered; only bracket data is cleared</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <Btn v="ghost" onClick={()=>setResetModal(false)}>Cancel</Btn>
          <Btn v="danger" onClick={doResetBracket}>↺ Confirm Reset</Btn>
        </div>
      </Modal>

      {/* ── FINALIZE CONFIRM MODAL ────────────────────────────────────────── */}
      <Modal open={finalizeModal} onClose={()=>setFinalizeModal(false)} title="🏆 Finalize Tournament" width={400}>
        {(() => {
          const allM = allMatches();
          const pending = allM.filter(m=>m.status!=="complete"&&m.status!=="bye"&&m.status!=="waiting"&&m.t1&&m.t2);
          return (
            <>
              {pending.length > 0 && (
                <div style={{ padding:"10px 14px", background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:5, marginBottom:14, fontSize:12, color:"var(--gold)" }}>
                  ⚠ {pending.length} match{pending.length!==1?"es":""} still pending. Finalizing will mark the tournament complete anyway.
                </div>
              )}
              <div style={{ fontSize:13, color:"var(--muted)", lineHeight:1.7, marginBottom:14 }}>
                Finalizing locks the bracket, awards placements, and updates all player rankings and Elo scores.
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <Btn v="ghost" onClick={()=>setFinalizeModal(false)}>Cancel</Btn>
                <Btn onClick={()=>{updateAndSave(t=>({...t,status:"completed",finalized:true,finalizedAt:new Date().toISOString()}));setFinalizeModal(false);}}>
                  🏆 Finalize
                </Btn>
              </div>
            </>
          );
        })()}
      {/* ── Table Assignment Modal ───────────────────────────────────────── */}
      {tableAssignId && (
        <Modal open={true} onClose={()=>{setTableAssignId(null);setTableAssignVal("");}} title="Assign Table" width={340}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:13,color:"var(--muted)"}}>Enter table number or label for this match</div>
            <input autoFocus style={{...css.input,textAlign:"center",fontSize:24,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:4}} value={tableAssignVal} onChange={e=>setTableAssignVal(e.target.value)} placeholder="Table #" onKeyDown={e=>e.key==="Enter"&&saveTableAssign()} />
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {["1","2","3","4","5","6","7","8"].map(n=>(
                <button key={n} onClick={()=>setTableAssignVal(n)} style={{flex:1,padding:"8px 0",borderRadius:4,background:tableAssignVal===n?"rgba(201,168,76,0.2)":"var(--navy-mid)",border:`1px solid ${tableAssignVal===n?"var(--gold)":"var(--navy-border)"}`,color:tableAssignVal===n?"var(--gold)":"var(--muted)",cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,minWidth:32}}>{n}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn v="ghost" onClick={()=>{setTableAssignId(null);setTableAssignVal("");}}>Cancel</Btn>
              <Btn style={{flex:1}} onClick={saveTableAssign}>✓ Assign</Btn>
              {tableAssignVal && <Btn v="danger" onClick={()=>{setTableAssignVal("");setTimeout(saveTableAssign,0);}}>✕ Clear</Btn>}
            </div>
          </div>
        </Modal>
      )}

      {/* ── Match Notes Modal ─────────────────────────────────────────────── */}
      {/* ── Referee Assignment Modal ─────────────────────────────────────── */}
      {refAssignId && (
        <Modal open={true} onClose={()=>{setRefAssignId(null);setRefAssignVal("");}} title="Assign Referee" width={360}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:13,color:"var(--muted)"}}>Assign a referee or scorekeeper to this match</div>
            <input autoFocus style={{...css.input,fontSize:14}} value={refAssignVal}
              onChange={e=>setRefAssignVal(e.target.value)}
              placeholder="Referee name or ID"
              onKeyDown={e=>e.key==="Enter"&&saveRefAssign()} />
            {/* Quick pick from scorekeepers */}
            {(players||[]).filter(p=>p.role==="scorekeeper"||p.role==="tournament_director").length>0&&(
              <div>
                <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,marginBottom:6}}>QUICK PICK</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {(players||[]).filter(p=>p.role==="scorekeeper"||p.role==="tournament_director").map(p=>(
                    <button key={p.id} onClick={()=>setRefAssignVal(p.name)}
                      style={{padding:"4px 10px",background:refAssignVal===p.name?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.04)",border:"1px solid "+(refAssignVal===p.name?"rgba(201,168,76,0.5)":"var(--navy-border)"),borderRadius:4,color:refAssignVal===p.name?"var(--gold)":"var(--muted)",fontSize:11,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <Btn v="ghost" onClick={()=>{setRefAssignId(null);setRefAssignVal("");}}>Cancel</Btn>
              <Btn style={{flex:1}} onClick={saveRefAssign}>✓ Assign</Btn>
              {refAssignVal&&<Btn v="danger" onClick={()=>{setRefAssignVal("");setTimeout(saveRefAssign,0);}}>✕ Clear</Btn>}
            </div>
          </div>
        </Modal>
      )}

      {matchNoteId && (
        <Modal open={true} onClose={()=>{setMatchNoteId(null);setMatchNoteText("");}} title="Match Notes" width={380}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:13,color:"var(--muted)"}}>Staff-only note for this match</div>
            <textarea autoFocus style={{...css.input,width:"100%",minHeight:80,resize:"vertical",fontFamily:"inherit",fontSize:13}} value={matchNoteText} onChange={e=>setMatchNoteText(e.target.value)} placeholder="e.g. Rematch from earlier, known rivalry, apply rule 4.2…" />
            <div style={{display:"flex",gap:8}}>
              <Btn v="ghost" onClick={()=>{setMatchNoteId(null);setMatchNoteText("");}}>Cancel</Btn>
              <Btn style={{flex:1}} onClick={saveMatchNote}>✓ Save Note</Btn>
              {matchNoteText && <Btn v="danger" onClick={()=>{setMatchNoteText("");setTimeout(saveMatchNote,0);}}>✕ Clear</Btn>}
            </div>
          </div>
        </Modal>
      )}

            </Modal>
    </div>
  );
}

// ── PLAYER PROFILE MODAL ─────────────────────────────────────────────────────
function PlayerProfile({ player: initPlayer, allPlayers, onClose, onUpdate }) {
  const { isMobile } = useBreakpoint();
  const [tab, setTab] = useState("overview");
  const [player, setPlayer] = useState(initPlayer);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: initPlayer.name, nick: initPlayer.nick || "", email: initPlayer.email || "", phone: initPlayer.phone || "" });
  const [histFilter, setHistFilter] = useState("all"); // all | wins | losses | ranked
  const [compareId, setCompareId] = useState("");
  const [editExtra, setEditExtra] = useState({ hometown: initPlayer.hometown||"", notes: initPlayer.notes||"", jersey: initPlayer.jersey||"" });

  if (!player) return null;

  const hist = player.matchHistory || [];
  const pr   = calcPowerRating(player);
  const winRate = (player.wins + player.losses) > 0 ? Math.round(player.wins / (player.wins + player.losses) * 100) : 0;
  const best = player.placements?.length ? Math.min(...player.placements) : null;

  // ── Streak & form ────────────────────────────────────────────────────────
  const recentResults = [...hist].reverse().slice(0, 10).map(m => m.won);
  const form5 = recentResults.slice(0, 5);
  let streak = 0, streakType = null;
  for (const r of recentResults) {
    if (streakType === null) { streakType = r ? "W" : "L"; streak = 1; }
    else if ((r && streakType === "W") || (!r && streakType === "L")) streak++;
    else break;
  }

  // ── Elo chart (last 30 matches) ─────────────────────────────────────────
  const eloPoints = hist.slice(-30).map(h => h.eloAfter || player.elo);
  const sparkMin = Math.min(...eloPoints, player.elo) - 30;
  const sparkMax = Math.max(...eloPoints, player.elo) + 30;
  const SPARK_W = 400, SPARK_H = 72;
  function toY(v) { return SPARK_H - ((v - sparkMin) / (sparkMax - sparkMin || 1)) * SPARK_H; }
  const sparkPath = eloPoints.length > 1
    ? eloPoints.map((v,i) => `${i===0?"M":"L"}${Math.round(i*(SPARK_W/(eloPoints.length-1)))},${Math.round(toY(v))}`).join(" ")
    : null;
  const eloTrend = eloPoints.length >= 2 ? eloPoints[eloPoints.length-1] - eloPoints[0] : 0;

  // ── Rolling win rate (last 10 in batches of 5) ───────────────────────────
  const rollingWR = [];
  for (let i = 5; i <= hist.length; i += 5) {
    const slice = hist.slice(i-5, i);
    rollingWR.push(Math.round(slice.filter(m=>m.won).length / slice.length * 100));
  }

  // ── H2H ─────────────────────────────────────────────────────────────────
  const h2hMap = {};
  hist.forEach(m => {
    const opp = m.opponent;
    if (!opp) return;
    if (!h2hMap[opp]) h2hMap[opp] = { w:0, l:0, cd:0, matches:0, lastDate:"" };
    h2hMap[opp].matches++;
    h2hMap[opp].cd += m.cupDiff || 0;
    if (m.won) h2hMap[opp].w++; else h2hMap[opp].l++;
    if (!h2hMap[opp].lastDate || m.date > h2hMap[opp].lastDate) h2hMap[opp].lastDate = m.date;
  });
  const h2hList = Object.entries(h2hMap).map(([opp,d]) => ({ opp, ...d })).sort((a,b) => (b.w+b.l)-(a.w+a.l));
  const nemesis   = h2hList.find(r => r.l > r.w);
  const punchBag  = h2hList.find(r => r.w > r.l && r.matches >= 2);
  const [h2hSearch,  setH2hSearch]  = useState("");
  const [scoutNote,  setScoutNote]  = useState(initPlayer?.scoutNote || "");
  const [scoutSaved, setScoutSaved] = useState(false);

  // ── Filtered history ─────────────────────────────────────────────────────
  const filteredHist = [...hist].reverse().filter(m => {
    if (histFilter === "wins")   return m.won;
    if (histFilter === "losses") return !m.won;
    if (histFilter === "ranked") return m.ranked;
    return true;
  });

  // ── Compare player ───────────────────────────────────────────────────────
  const comparePlayer = allPlayers.find(p => p.id === compareId);
  const comparePR     = comparePlayer ? calcPowerRating(comparePlayer) : 0;
  const compareWR     = comparePlayer && (comparePlayer.wins+comparePlayer.losses) > 0
    ? Math.round(comparePlayer.wins/(comparePlayer.wins+comparePlayer.losses)*100) : 0;

  function saveEdit() {
    const updated = { ...player, ...editForm, ...editExtra };
    setPlayer(updated);
    onUpdate?.(updated);
    setEditing(false);
  }

  // ── Career timeline (group history by tournament) ───────────────────────
  const careerEvents = (() => {
    const map = {};
    hist.forEach(m => {
      const key = m.tournament || "Unknown Event";
      if (!map[key]) map[key] = { name:key, date:m.date||"", matches:[], w:0, l:0, cd:0, eloStart:null, eloEnd:null };
      map[key].matches.push(m);
      if (m.won) map[key].w++; else map[key].l++;
      map[key].cd += m.cupDiff||0;
      if (m.eloAfter) map[key].eloEnd = m.eloAfter;
      if (!map[key].eloStart && m.eloBefore) map[key].eloStart = m.eloBefore;
    });
    return Object.values(map).sort((a,b) => (b.date||"").localeCompare(a.date||""));
  })();

  // Per-format stats from history
  const formatStats = (() => {
    const map = {};
    hist.forEach(m => {
      const fmt = m.format || "unknown";
      if (!map[fmt]) map[fmt] = { fmt, w:0, l:0, cd:0 };
      if (m.won) map[fmt].w++; else map[fmt].l++;
      map[fmt].cd += m.cupDiff||0;
    });
    return Object.values(map).sort((a,b) => (b.w+b.l)-(a.w+a.l));
  })();

  // ── Achievements ─────────────────────────────────────────────────────────
  const ACHIEVEMENTS = [
    { id:"first_win",   icon:"🥊", label:"First Win",         desc:"Win your first match",                  earned: player.wins >= 1 },
    { id:"ten_wins",    icon:"🔟", label:"Ten Wins",          desc:"Win 10 matches",                        earned: player.wins >= 10 },
    { id:"fifty_wins",  icon:"💯", label:"Fifty Wins",        desc:"Win 50 matches",                        earned: player.wins >= 50 },
    { id:"champion",    icon:"🏆", label:"Champion",          desc:"Win a tournament",                      earned: (player.placements||[]).includes(1) },
    { id:"podium",      icon:"🥉", label:"Podium",            desc:"Finish top 3",                          earned: (player.placements||[]).some(p=>p<=3) },
    { id:"hot_streak",  icon:"🔥", label:"Hot Streak",        desc:"Win 3 matches in a row",                earned: streak >= 3 && streakType === "W" },
    { id:"streak5",     icon:"🌋", label:"On Fire",           desc:"Win 5 matches in a row",                earned: streak >= 5 && streakType === "W" },
    { id:"elo1400",     icon:"⚡", label:"Elo Spike",         desc:"Reach 1400 Elo",                        earned: (player.elo||1200) >= 1400 },
    { id:"elo1600",     icon:"💫", label:"Elite",             desc:"Reach 1600 Elo",                        earned: (player.elo||1200) >= 1600 },
    { id:"cup_diff100", icon:"🪣", label:"Cup Monster",       desc:"+100 cup differential",                 earned: (player.cupDiff||0) >= 100 },
    { id:"veteran",     icon:"🎖", label:"Veteran",           desc:"Play 5+ tournaments",                   earned: (player.tournaments||0) >= 5 },
    { id:"perfect_game",icon:"✨", label:"Perfect Game",      desc:"Win a match with 0 cups allowed",       earned: hist.some(m=>m.won&&m.oppScore===0) },
    { id:"comeback",    icon:"⚔️", label:"Comeback",          desc:"Win a match after trailing",            earned: hist.some(m=>m.won&&m.trailed) },
    { id:"ot_win",      icon:"⭐", label:"Overtime Hero",     desc:"Win an overtime match",                 earned: hist.some(m=>m.won&&m.ot) },
    { id:"undefeated",  icon:"🛡", label:"Undefeated",        desc:"Have 0 losses",                         earned: player.wins > 0 && player.losses === 0 },
    { id:"century",     icon:"💥", label:"Century Club",      desc:"Score 100+ total cups",                 earned: (player.cupsMade||0) >= 100 },
  ];
  const earned = ACHIEVEMENTS.filter(a=>a.earned);
  const locked = ACHIEVEMENTS.filter(a=>!a.earned);
  const maxWins    = Math.max(...allPlayers.map(p=>p.wins), 1);
  const maxCupDiff = Math.max(...allPlayers.map(p=>Math.abs(p.cupDiff)), 1);
  const maxPR      = Math.max(...allPlayers.map(p=>calcPowerRating(p)), 1);
  const perf = [
    { label:"Win Rate",   val:winRate,                   max:100,      color:"var(--green)",  fmt:v=>`${v}%` },
    { label:"Power Rating",val:pr,                       max:maxPR,    color:"var(--gold)",   fmt:v=>`${v}` },
    { label:"Elo",         val:player.elo||1200,          max:2000,     color:"#5b8dee",       fmt:v=>`${v}` },
    { label:"Cup Diff",    val:Math.max(0,player.cupDiff),max:maxCupDiff,color:"var(--green)", fmt:v=>`+${v}` },
    { label:"Wins",        val:player.wins,               max:maxWins,  color:"var(--gold)",   fmt:v=>`${v}W` },
  ];

  const avgEloDelta = hist.filter(m=>m.ranked&&m.eloDelta!=null).reduce((s,m)=>s+(m.eloDelta||0),0) / Math.max(1,hist.filter(m=>m.ranked&&m.eloDelta!=null).length);
  const cupsPerGame = hist.length ? (hist.reduce((s,m)=>s+(m.myScore||0),0)/hist.length).toFixed(1) : null;

  const TABS = [["overview","📊 Overview"],["history","📋 History"],["career","🗓 Career"],["h2h","⚔️ H2H"],["trends","📈 Trends"],["compare","⚖️ Compare"],["scout","📋 Staff Notes"]];

  return (
    <Modal open={true} onClose={onClose} title="" width={680}>
      {/* ── Profile header ─────────────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"flex-start", gap:16, marginBottom:16, paddingBottom:16, borderBottom:"1px solid var(--navy-border)" }}>
        {/* Avatar */}
        <div style={{ position:"relative", flexShrink:0 }}>
          <label title="Click to upload photo" style={{ cursor:"pointer", display:"block" }}>
            <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{
              const file=e.target.files[0]; if(!file) return;
              const reader=new FileReader();
              reader.onload=ev=>{ onUpdate({...player, photoUrl:ev.target.result}); };
              reader.readAsDataURL(file);
              e.target.value="";
            }} />
            <div style={{ width:60, height:60, borderRadius:"50%", background:player.photoUrl?"transparent":`linear-gradient(135deg,${best===1?"#c9a84c,#fff3b0":best&&best<=3?"#cd7f32,#f4c87a":"#1e3250,#5b8dee"})`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:best===1?"var(--navy)":"var(--white)", border:`2px solid ${best===1?"var(--gold)":best&&best<=3?"#cd7f32":"var(--navy-border)"}`, boxShadow:best===1?"0 0 16px rgba(201,168,76,0.5)":"none", overflow:"hidden", position:"relative" }}>
              {player.photoUrl
                ? <img src={player.photoUrl} alt={player.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : player.name.charAt(0)
              }
              <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.35)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", opacity:0, transition:"opacity 0.15s" }}
                onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                <span style={{ fontSize:16 }}>📷</span>
              </div>
            </div>
          </label>
          {streak >= 3 && (
            <div style={{ position:"absolute", bottom:-4, right:-4, background:streakType==="W"?"var(--green)":"var(--red)", borderRadius:10, padding:"1px 5px", fontSize:9, fontFamily:"'Bebas Neue',sans-serif", color:"var(--white)", fontWeight:700, border:"2px solid var(--navy-mid)" }}>{streak}{streakType}</div>
          )}
        </div>

        {/* Name / badges */}
        <div style={{ flex:1, minWidth:0 }}>
          {editing ? (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <input style={{ ...css.input, fontSize:14, fontWeight:700 }} value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} placeholder="Full name" />
              <input style={{ ...css.input, fontSize:12 }} value={editForm.nick} onChange={e=>setEditForm(f=>({...f,nick:e.target.value}))} placeholder='Nickname e.g. "Laser"' />
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                <input style={{ ...css.input, fontSize:12 }} value={editForm.email} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} placeholder="Email" />
                <input style={{ ...css.input, fontSize:12 }} value={editForm.phone} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} placeholder="Phone" />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 60px", gap:6 }}>
                <input style={{ ...css.input, fontSize:12 }} value={editExtra.hometown} onChange={e=>setEditExtra(f=>({...f,hometown:e.target.value}))} placeholder="Hometown" />
                <input style={{ ...css.input, fontSize:12, textAlign:"center" }} value={editExtra.jersey} onChange={e=>setEditExtra(f=>({...f,jersey:e.target.value}))} placeholder="#" />
              </div>
              <textarea style={{ ...css.input, fontSize:11, minHeight:48, resize:"vertical" }} value={editExtra.notes} onChange={e=>setEditExtra(f=>({...f,notes:e.target.value}))} placeholder="Notes (optional)" />
              <div style={{ display:"flex", gap:6 }}>
                <Btn sz="sm" onClick={saveEdit} disabled={!editForm.name}>Save</Btn>
                <Btn sz="sm" v="ghost" onClick={()=>setEditing(false)}>Cancel</Btn>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, letterSpacing:2, lineHeight:1 }}>{player.name}</div>
                <button onClick={()=>setEditing(true)} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:12, padding:0 }}>✏️</button>
              </div>
              {player.nick && <div style={{ fontSize:12, color:"var(--gold)", fontStyle:"italic", marginTop:1 }}>"{player.nick}"</div>}
              <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap", alignItems:"center" }}>
                <Badge color={ROLE_COLOR[player.role]||""}>{player.role.replace(/_/g," ")}</Badge>
                {best && <Badge color="gold">{best===1?"🥇":best===2?"🥈":"🥉"} Best #{best}</Badge>}
                {streak >= 3 && <Badge color={streakType==="W"?"green":"red"}>{streakType==="W"?"🔥":"❄️"} {streak}-{streakType} Streak</Badge>}
              </div>
            </>
          )}
        </div>

        {/* Elo + PR + Form */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8, flexShrink:0 }}>
          <div style={{ display:"flex", gap:12 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:"var(--gold-light)", lineHeight:1 }}>{player.elo}</div>
              <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:"uppercase" }}>Elo</div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:"var(--white)", lineHeight:1 }}>{pr}</div>
              <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:"uppercase" }}>PR</div>
            </div>
            {cupsPerGame && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:"var(--gold)", lineHeight:1 }}>{cupsPerGame}</div>
                <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:"uppercase" }}>Cups/G</div>
              </div>
            )}
          </div>
          {hist.filter(m=>m.ranked&&m.eloDelta!=null).length>0 && (
            <div style={{ fontSize:10, color:avgEloDelta>=0?"var(--green)":"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
              {avgEloDelta>=0?"+":""}{avgEloDelta.toFixed(1)} avg Elo/match
            </div>
          )}
          {/* Form guide: last 5 */}
          {form5.length > 0 && (
            <div style={{ display:"flex", gap:3 }}>
              {form5.map((w,i) => (
                <div key={i} style={{ width:16, height:16, borderRadius:3, background:w?"var(--green)":"var(--red)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"var(--white)", fontFamily:"'Bebas Neue',sans-serif" }}>{w?"W":"L"}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", gap:0, marginBottom:16, borderBottom:"1px solid var(--navy-border)", overflowX:"auto" }}>
        {TABS.map(([v,l]) => (
          <div key={v} onClick={()=>setTab(v)} style={{ padding:"7px 14px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:1.5, textTransform:"uppercase", borderBottom:tab===v?"2px solid var(--gold)":"2px solid transparent", color:tab===v?"var(--white)":"var(--muted)", marginBottom:-1, whiteSpace:"nowrap" }}>{l}</div>
        ))}
      </div>

      {/* ── OVERVIEW ────────────────────────────────────────────────────── */}
      {tab==="overview" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Core stat grid */}
          <div className="resp-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            {[
              ["W–L",        `${player.wins}–${player.losses}`, player.wins>=player.losses?"var(--green)":"var(--red)"],
              ["Win Rate",   `${winRate}%`,                     winRate>=50?"var(--green)":"var(--muted)"],
              ["Cup Diff",   `${player.cupDiff>=0?"+":""}${player.cupDiff}`, player.cupDiff>=0?"var(--green)":"var(--red)"],
              ["Events",     player.tournaments||0,             "var(--gold-light)"],
              ["Cups Made",  player.cupsMade||0,                "var(--gold-light)"],
              ["Cups Allow", player.cupsAllowed||0,             "var(--muted)"],
              ["Avg Made",   hist.length ? (hist.reduce((s,m)=>s+m.myScore,0)/hist.length).toFixed(1) : "—", "var(--white)"],
              ["Avg Allow",  hist.length ? (hist.reduce((s,m)=>s+m.oppScore,0)/hist.length).toFixed(1) : "—", "var(--muted)"],
            ].map(([label,val,color]) => (
              <div key={label} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:6, padding:"10px 12px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color, lineHeight:1 }}>{val}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Performance bars */}
          <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
            <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Performance vs League</div>
            {perf.map(({ label, val, max, color, fmt }) => (
              <div key={label} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{label}</span>
                  <span style={{ fontSize:12, fontFamily:"'Bebas Neue',sans-serif", color }}>{fmt(val)}</span>
                </div>
                <div style={{ height:6, background:"var(--navy-border)", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${Math.min(100,Math.round(val/max*100))}%`, background:color, borderRadius:3, transition:"width 0.4s" }} />
                </div>
              </div>
            ))}
          </div>

          {/* H2H callouts */}
          {(nemesis || punchBag) && (
            <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {nemesis && (
                <div style={{ background:"rgba(217,79,61,0.07)", border:"1px solid rgba(217,79,61,0.25)", borderRadius:6, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>😤 Nemesis</div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{nemesis.opp}</div>
                  <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{nemesis.w}W – {nemesis.l}L against them</div>
                </div>
              )}
              {punchBag && (
                <div style={{ background:"rgba(46,204,113,0.07)", border:"1px solid rgba(46,204,113,0.2)", borderRadius:6, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>💪 Favourite Opponent</div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{punchBag.opp}</div>
                  <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{punchBag.w}W – {punchBag.l}L against them</div>
                </div>
              )}
            </div>
          )}

          {/* Recent Form */}
          {hist.length > 0 && (
            <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:"12px 14px" }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Recent Form</div>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {hist.slice(-10).reverse().map((m, i) => {
                  const won = m.result === "W";
                  const score = m.score || "";
                  return (
                    <div key={i} title={`vs ${m.opponent||"?"} ${score}${m.ot?" OT":""} — ${m.tournament||""}`}
                      style={{ width:30, height:30, borderRadius:4, background:won?"rgba(46,204,113,0.15)":"rgba(217,79,61,0.12)", border:`1px solid ${won?"rgba(46,204,113,0.4)":"rgba(217,79,61,0.35)"}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"default" }}>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:won?"var(--green)":"var(--red)" }}>{won?"W":"L"}</span>
                    </div>
                  );
                })}
              </div>
              {hist.length > 0 && (() => {
                const last5 = hist.slice(-5);
                const w5 = last5.filter(m=>m.result==="W").length;
                return (
                  <div style={{ marginTop:8, fontSize:11, color:"var(--muted)" }}>
                    Last {last5.length}: <span style={{ color:w5>=4?"var(--green)":w5>=3?"var(--gold)":"var(--muted)", fontWeight:700 }}>{w5}W – {last5.length-w5}L</span>
                    {streak >= 3 && <span style={{ marginLeft:10, color:streakType==="W"?"var(--green)":"var(--red)", fontWeight:700 }}>🔥 {streak}-game {streakType==="W"?"win":"loss"} streak</span>}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Achievements */}
          <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>Achievements</div>
              <div style={{ fontSize:11, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{earned.length}/{ACHIEVEMENTS.length}</div>
            </div>
            {earned.length === 0 ? (
              <div style={{ fontSize:12, color:"var(--muted)", fontStyle:"italic" }}>No achievements yet — keep playing!</div>
            ) : (
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {earned.map(a => (
                  <div key={a.id} title={a.desc} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:20, cursor:"default" }}>
                    <span style={{ fontSize:14 }}>{a.icon}</span>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:"var(--gold)", letterSpacing:0.5 }}>{a.label}</span>
                  </div>
                ))}
                {locked.slice(0,4).map(a => (
                  <div key={a.id} title={a.desc+" (locked)"} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:20, cursor:"default", opacity:0.45 }}>
                    <span style={{ fontSize:14, filter:"grayscale(1)" }}>{a.icon}</span>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:"var(--muted)", letterSpacing:0.5 }}>{a.label}</span>
                  </div>
                ))}
                {locked.length > 4 && <div style={{ padding:"5px 10px", fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, opacity:0.4 }}>+{locked.length-4} locked</div>}
              </div>
            )}
          </div>

          {/* Per-format stats */}
          {formatStats.length > 1 && (
            <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
              <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Performance by Format</div>
              {formatStats.map(f => {
                const total = f.w+f.l;
                const wr2 = total>0?Math.round(f.w/total*100):0;
                return (
                  <div key={f.fmt} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <div style={{ width:90, fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, flexShrink:0, textTransform:"capitalize" }}>{f.fmt.replace(/_/g," ")}</div>
                    <div style={{ flex:1, height:6, background:"var(--navy-border)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:wr2+"%", background:wr2>=50?"var(--green)":"var(--red)", borderRadius:3, transition:"width 0.3s" }} />
                    </div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:wr2>=50?"var(--green)":"var(--muted)", width:36, textAlign:"right" }}>{wr2}%</div>
                    <div style={{ fontSize:11, color:"var(--muted)", width:50, textAlign:"right" }}>{f.w}W–{f.l}L</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Hometown + notes if set */}
          {(player.hometown||player.notes||player.jersey) && (
            <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:"10px 16px" }}>
              {player.jersey && <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:player.hometown||player.notes?6:0 }}><span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, width:80 }}>JERSEY</span><span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold)" }}>#{player.jersey}</span></div>}
              {player.hometown && <div style={{ display:"flex", gap:8, marginBottom:player.notes?6:0 }}><span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, width:80, flexShrink:0, paddingTop:2 }}>FROM</span><span style={{ fontSize:13 }}>{player.hometown}</span></div>}
              {player.notes && <div style={{ display:"flex", gap:8 }}><span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, width:80, flexShrink:0, paddingTop:2 }}>NOTES</span><span style={{ fontSize:12, color:"var(--muted)", lineHeight:1.5 }}>{player.notes}</span></div>}
            </div>
          )}

          {/* Placements */}
          {(player.placements||[]).length > 0 && (
            <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:"12px 16px" }}>
              <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Tournament Placements</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {player.placements.map((pl,i) => {
                  const c = pl===1?"var(--gold)":pl===2?"#c0c0c0":pl===3?"#cd7f32":"var(--muted)";
                  const emoji = pl===1?"🥇":pl===2?"🥈":pl===3?"🥉":"";
                  return <div key={i} style={{ background:`${c}22`, border:`1px solid ${c}55`, borderRadius:4, padding:"4px 12px", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:c }}>{emoji}#{pl}</div>;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MATCH HISTORY ───────────────────────────────────────────────── */}
      {tab==="history" && (
        <div>
          <div style={{ display:"flex", gap:8, marginBottom:12, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
              {[["all","All"],["wins","Wins"],["losses","Losses"],["ranked","Ranked"]].map(([v,l]) => (
                <div key={v} onClick={()=>setHistFilter(v)} style={{ padding:"5px 10px", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", background:histFilter===v?"var(--gold)":"transparent", color:histFilter===v?"var(--navy)":"var(--muted)", transition:"all 0.15s" }}>{l}</div>
              ))}
            </div>
            <span style={{ fontSize:11, color:"var(--muted)" }}>{filteredHist.length} matches</span>
          </div>

          {filteredHist.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>📋</div>
              <div>No matches match this filter</div>
            </div>
          ) : (
            <div style={{ maxHeight:380, overflowY:"auto" }}>
              <div className="resp-table">
                <table style={{ width:"100%", borderCollapse:"collapse", minWidth:500 }}>
                  <thead>
                    <tr>{["Date","Event","Opponent","Score","Cup Δ","Elo Δ",""].map(h => (
                      <th key={h} style={{ ...css.th, position:"sticky", top:0, background:"var(--navy-mid)", zIndex:1 }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {filteredHist.map(m => (
                      <tr key={m.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:m.won?"rgba(46,204,113,0.02)":"transparent" }}>
                        <td style={{ ...css.td, fontSize:11, color:"var(--muted)", whiteSpace:"nowrap" }}>{m.date}</td>
                        <td style={{ ...css.td, fontSize:11, maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={m.tournament}>{m.tournament}</td>
                        <td style={{ ...css.td, fontWeight:600, fontSize:13 }}>{m.opponent}</td>
                        <td style={{ ...css.td, fontFamily:"'Bebas Neue',sans-serif", fontSize:18, textAlign:"center", whiteSpace:"nowrap" }}>
                          <span style={{ color:m.won?"var(--gold-light)":"var(--muted)" }}>{m.myScore}</span>
                          <span style={{ color:"var(--muted)", margin:"0 3px", fontSize:12 }}>–</span>
                          <span style={{ color:!m.won?"var(--gold-light)":"var(--muted)" }}>{m.oppScore}</span>
                          {m.ot && <span style={{ fontSize:9, color:"var(--gold)", marginLeft:4 }}>OT</span>}
                        </td>
                        <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:m.cupDiff>=0?"var(--green)":"var(--red)" }}>{m.cupDiff>=0?"+":""}{m.cupDiff}</td>
                        <td style={{ ...css.td, textAlign:"center", fontSize:12, color:m.eloDelta>0?"var(--green)":m.eloDelta<0?"var(--red)":"var(--muted)" }}>
                          {m.ranked ? `${m.eloDelta>0?"+":""}${m.eloDelta}` : <span style={{ color:"var(--muted)", fontSize:10 }}>—</span>}
                        </td>
                        <td style={{ ...css.td, textAlign:"center" }}>
                          <Badge color={m.won?"green":"red"}>{m.won?"W":"L"}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CAREER TIMELINE ─────────────────────────────────────────────── */}
      {tab==="career" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {careerEvents.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🗓</div>
              <div>No tournament history yet</div>
            </div>
          ) : (
            <>
              {/* Career summary strip */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                {[
                  [careerEvents.length, "Events", "var(--gold)"],
                  [careerEvents.reduce((s,e)=>s+e.w,0), "Total Wins", "var(--green)"],
                  [careerEvents.filter(e=>e.w>e.l).length, "Events Won", "var(--gold)"],
                  [careerEvents.reduce((s,e)=>s+e.cd,0), "Cup Diff", "var(--green)"],
                ].map(([v,l,c]) => (
                  <div key={l} style={{ ...css.card, padding:"10px 8px", textAlign:"center" }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v>0?"+":""}{v}</div>
                    <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Vertical timeline */}
              <div style={{ position:"relative", paddingLeft:32 }}>
                {/* Vertical line */}
                <div style={{ position:"absolute", left:10, top:8, bottom:8, width:2, background:"rgba(255,255,255,0.07)", borderRadius:1 }} />

                {careerEvents.map((ev, i) => {
                  const isWin   = ev.w > ev.l;
                  const isTie   = ev.w === ev.l;
                  const dotColor = isWin ? "var(--gold)" : isTie ? "var(--muted)" : "var(--red)";
                  const eloChange = ev.eloEnd && ev.eloStart ? ev.eloEnd - ev.eloStart : null;
                  const placement = player.placements?.find
                    ? null // placements are numbers, not objects
                    : null;

                  return (
                    <div key={ev.name+i} style={{ position:"relative", marginBottom:i<careerEvents.length-1?16:0 }}>
                      {/* Timeline dot */}
                      <div style={{ position:"absolute", left:-26, top:8, width:14, height:14, borderRadius:"50%", background:dotColor, border:`2px solid var(--navy-mid)`, zIndex:1, boxShadow:`0 0 8px ${dotColor}55` }} />

                      {/* Event card */}
                      <div style={{ ...css.card, padding:"12px 14px", marginLeft:4 }}>
                        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, flexWrap:"wrap", marginBottom:6 }}>
                          <div>
                            <div style={{ fontWeight:700, fontSize:13 }}>{ev.name}</div>
                            {ev.date && <div style={{ fontSize:10, color:"var(--muted)", marginTop:1 }}>{ev.date}</div>}
                          </div>
                          <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                            {/* Result badge */}
                            <span style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, color:dotColor, background:`${dotColor}18`, border:`1px solid ${dotColor}44`, padding:"2px 8px", borderRadius:3 }}>
                              {isWin?"WIN":isTie?"EVEN":"LOSS"}
                            </span>
                            {/* Elo change */}
                            {eloChange !== null && (
                              <span style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:eloChange>=0?"var(--green)":"var(--red)" }}>
                                {eloChange>=0?"+":""}{eloChange} Elo
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Match results row */}
                        <div style={{ display:"flex", gap:16, alignItems:"center" }}>
                          {/* W/L record */}
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--green)", lineHeight:1 }}>{ev.w}</span>
                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, color:"var(--muted)" }}>–</span>
                            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--muted)", lineHeight:1 }}>{ev.l}</span>
                          </div>
                          {/* Cup diff */}
                          <span style={{ fontSize:12, color:ev.cd>0?"var(--green)":ev.cd<0?"var(--red)":"var(--muted)", fontWeight:600 }}>
                            {ev.cd>0?"+":""}{ev.cd} cups
                          </span>
                          {/* Match dots */}
                          <div style={{ display:"flex", gap:3, flex:1, justifyContent:"flex-end" }}>
                            {ev.matches.slice(0,8).map((m,j) => (
                              <div key={j} title={m.opponent||""} style={{ width:12, height:12, borderRadius:2, background:m.won?"var(--green)":"var(--red)", opacity:0.85 }} />
                            ))}
                            {ev.matches.length > 8 && <span style={{ fontSize:9, color:"var(--muted)" }}>+{ev.matches.length-8}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      {/* ── HEAD-TO-HEAD ────────────────────────────────────────────────── */}
      {tab==="h2h" && (
        <div>
          {h2hList.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <input style={{ ...css.input, width:"100%" }} placeholder="Search opponent…" value={h2hSearch} onChange={e=>setH2hSearch(e.target.value)} />
            </div>
          )}
          {h2hList.filter(r=>!h2hSearch||r.opp.toLowerCase().includes(h2hSearch.toLowerCase())).length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>⚔️</div>
              <div>{h2hSearch ? "No opponent found" : "No head-to-head data yet"}</div>
            </div>
          ) : (
            <>
              {/* ── Rival spotlight cards ── */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {/* Nemesis */}
                <div style={{ ...css.card, padding:"12px 14px", borderColor:nemesis?"rgba(217,79,61,0.35)":"var(--navy-border)", background:nemesis?"rgba(217,79,61,0.04)":"rgba(255,255,255,0.01)" }}>
                  <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--red)", marginBottom:6 }}>😈 Nemesis</div>
                  {nemesis ? (
                    <>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{nemesis.opp}</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--red)", lineHeight:1 }}>{nemesis.w}–{nemesis.l}</div>
                      <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{nemesis.matches} meetings · {Math.round(nemesis.w/nemesis.matches*100)}% win rate</div>
                    </>
                  ) : (
                    <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>No losing record yet</div>
                  )}
                </div>
                {/* Punchbag */}
                <div style={{ ...css.card, padding:"12px 14px", borderColor:punchBag?"rgba(46,204,113,0.35)":"var(--navy-border)", background:punchBag?"rgba(46,204,113,0.04)":"rgba(255,255,255,0.01)" }}>
                  <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--green)", marginBottom:6 }}>💪 Favored Matchup</div>
                  {punchBag ? (
                    <>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{punchBag.opp}</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--green)", lineHeight:1 }}>{punchBag.w}–{punchBag.l}</div>
                      <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{punchBag.matches} meetings · {Math.round(punchBag.w/punchBag.matches*100)}% win rate</div>
                    </>
                  ) : (
                    <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>Need 2+ meetings first</div>
                  )}
                </div>
              </div>

              {/* ── Rivalry stats ── */}
              <div style={{ display:"flex", gap:12, marginBottom:12, fontSize:12, color:"var(--muted)", flexWrap:"wrap" }}>
                <span>{h2hList.length} opponents</span>
                <span style={{ color:"var(--green)" }}>↑ {h2hList.filter(r=>r.w>r.l).length} winning records</span>
                <span style={{ color:"var(--red)" }}>↓ {h2hList.filter(r=>r.l>r.w).length} losing records</span>
                <span>{h2hList.filter(r=>r.w===r.l).length} even</span>
              </div>

              {/* ── H2H bar chart — top opponents ── */}
              {(h2hSearch ? h2hList.filter(r=>r.opp.toLowerCase().includes(h2hSearch.toLowerCase())) : h2hList).slice(0,8).map((r,i) => {
                const total = r.w + r.l;
                const wPct  = total > 0 ? (r.w / total * 100) : 50;
                const resultColor = r.w > r.l ? "var(--green)" : r.l > r.w ? "var(--red)" : "var(--muted)";
                return (
                  <div key={r.opp} style={{ marginBottom:8 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:3 }}>
                      <span style={{ fontSize:12, fontWeight:600 }}>{r.opp}</span>
                      <span style={{ fontSize:11, fontFamily:"'Bebas Neue',sans-serif", color:resultColor }}>{r.w}–{r.l}</span>
                    </div>
                    <div style={{ height:8, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden", display:"flex" }}>
                      <div style={{ width:`${wPct}%`, background:"var(--green)", borderRadius:"4px 0 0 4px", transition:"width 0.3s" }} />
                      <div style={{ flex:1, background:"var(--red)", borderRadius:"0 4px 4px 0" }} />
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
                      <span style={{ fontSize:9, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>WIN {Math.round(wPct)}%</span>
                      <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{r.matches} meetings</span>
                    </div>
                  </div>
                );
              })}

              {/* Full table for remaining */}
              {h2hList.length > 6 && (
                <details style={{ marginTop:8 }}>
                  <summary style={{ cursor:"pointer", fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, padding:"6px 0", userSelect:"none" }}>
                    ▸ Show all {h2hList.length} opponents
                  </summary>
                  <div style={{ maxHeight:280, overflowY:"auto", marginTop:8 }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", minWidth:360 }}>
                      <thead>
                        <tr>
                          {["Opponent","W","L","Matches","Cup Diff"].map(h=>(
                            <th key={h} style={{ ...css.th, textAlign:h==="Opponent"?"left":"center" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {h2hList.slice(6).map((r,i)=>(
                          <tr key={r.opp} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ ...css.td, fontWeight:600, fontSize:12 }}>{r.opp}</td>
                            <td style={{ ...css.td, textAlign:"center", color:"var(--green)", fontWeight:700 }}>{r.w}</td>
                            <td style={{ ...css.td, textAlign:"center", color:"var(--muted)" }}>{r.l}</td>
                            <td style={{ ...css.td, textAlign:"center", color:"var(--muted)", fontSize:11 }}>{r.matches}</td>
                            <td style={{ ...css.td, textAlign:"center", color:r.cd>0?"var(--green)":r.cd<0?"var(--red)":"var(--muted)", fontWeight:600 }}>{r.cd>0?"+":""}{r.cd}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
      {/* ── TRENDS ──────────────────────────────────────────────────────── */}
      {tab==="trends" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {hist.length < 3 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>📈</div>
              <div>Play at least 3 matches to see trends</div>
            </div>
          ) : (
            <>
              {/* Elo trajectory */}
              {sparkPath && (
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                    <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>Elo Trajectory (last {eloPoints.length} matches)</div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:eloTrend>=0?"var(--green)":"var(--red)" }}>{eloTrend>=0?"+":""}{eloTrend}</div>
                  </div>
                  <svg width="100%" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" style={{ height:72, display:"block" }}>
                    {/* Grid lines */}
                    {[0.25,0.5,0.75].map(f => (
                      <line key={f} x1={0} y1={SPARK_H*f} x2={SPARK_W} y2={SPARK_H*f} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
                    ))}
                    {/* Gradient fill */}
                    <defs>
                      <linearGradient id="eloGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d={`${sparkPath} L${SPARK_W},${SPARK_H} L0,${SPARK_H} Z`} fill="url(#eloGrad)" />
                    <path d={sparkPath} fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinejoin="round" />
                    {eloPoints.map((v,i) => i===0||i===eloPoints.length-1 ? (
                      <circle key={i} cx={Math.round(i*(SPARK_W/(eloPoints.length-1)))} cy={Math.round(toY(v))} r={4} fill="var(--gold)" />
                    ) : null)}
                  </svg>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:6, fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>
                    <span>{eloPoints[0]} start</span>
                    <span style={{ color:"var(--gold-light)", fontWeight:700 }}>{eloPoints[eloPoints.length-1]} current</span>
                  </div>
                </div>
              )}

              {/* Rolling win rate */}
              {rollingWR.length >= 2 && (
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
                  <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Rolling Win Rate (per 5 matches)</div>
                  <div style={{ display:"flex", gap:4, alignItems:"flex-end", height:60 }}>
                    {rollingWR.map((wr3,i) => (
                      <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                        <div style={{ width:"100%", height:`${Math.max(4,wr3/100*52)}px`, background:wr3>=50?"var(--green)":"var(--red)", borderRadius:"3px 3px 0 0", opacity:0.7+i*0.05, transition:"height 0.3s" }} />
                        <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{wr3}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cups per match trend */}
              {hist.length >= 5 && (
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:16 }}>
                  <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Cups Per Game (last 10 matches)</div>
                  <div style={{ display:"flex", gap:10 }}>
                    {[["Made","var(--gold)",hist.slice(-10).map(m=>m.myScore)],["Allowed","var(--red)",hist.slice(-10).map(m=>m.oppScore)]].map(([lbl,color,vals]) => {
                      const avg = vals.reduce((s,v)=>s+v,0)/vals.length;
                      return (
                        <div key={lbl} style={{ flex:1, background:"rgba(255,255,255,0.02)", borderRadius:4, padding:"10px 12px" }}>
                          <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, marginBottom:4 }}>{lbl}</div>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color, lineHeight:1 }}>{avg.toFixed(1)}</div>
                          <div style={{ fontSize:10, color:"var(--muted)" }}>avg per game</div>
                        </div>
                      );
                    })}
                    <div style={{ flex:1, background:"rgba(255,255,255,0.02)", borderRadius:4, padding:"10px 12px" }}>
                      <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, marginBottom:4 }}>Efficiency</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--green)", lineHeight:1 }}>
                        {(hist.slice(-10).reduce((s,m)=>s+m.myScore,0) / Math.max(1,hist.slice(-10).reduce((s,m)=>s+m.oppScore,0)) * 100).toFixed(0)}%
                      </div>
                      <div style={{ fontSize:10, color:"var(--muted)" }}>made/allowed ratio</div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Form heatmap — last 40 matches ── */}
              {hist.length >= 5 && (() => {
                const recent40 = [...hist].reverse().slice(0, 40);
                const COLS = 8;
                const rows = [];
                for (let i = 0; i < recent40.length; i += COLS) rows.push(recent40.slice(i, i+COLS));
                return (
                  <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>Form Heatmap</div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:10, color:"var(--muted)" }}>
                        <span style={{ display:"inline-block", width:10, height:10, background:"var(--green)", borderRadius:2 }} /> W
                        <span style={{ display:"inline-block", width:10, height:10, background:"var(--red)", borderRadius:2 }} /> L
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                      {rows.map((row, ri) => (
                        <div key={ri} style={{ display:"flex", gap:3 }}>
                          {row.map((m, ci) => {
                            const cupDiff = m.cupDiff || 0;
                            const intensity = Math.min(1, Math.abs(cupDiff) / 8);
                            const baseColor = m.won ? "46,204,113" : "217,79,61";
                            return (
                              <div key={ci}
                                title={`vs ${m.opponent||"?"} — ${m.won?"W":"L"} ${m.myScore||0}–${m.oppScore||0}${m.ot?" OT":""}`}
                                style={{ width:24, height:24, borderRadius:3, background:`rgba(${baseColor},${0.25+intensity*0.65})`, border:`1px solid rgba(${baseColor},0.3)`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"default" }}>
                                {m.ot && <span style={{ fontSize:7, color:"var(--white)", fontFamily:"'Bebas Neue',sans-serif" }}>OT</span>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop:6, fontSize:10, color:"var(--muted)" }}>Most recent top-left · darker = larger cup margin</div>
                  </div>
                );
              })()}

              {/* ── Per-format breakdown ── */}
              {formatStats.length > 1 && (
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:6, padding:14 }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>Win Rate by Format</div>
                  {formatStats.map(fs => {
                    const total = fs.w + fs.l;
                    const wr = total > 0 ? Math.round(fs.w / total * 100) : 0;
                    return (
                      <div key={fs.fmt} style={{ marginBottom:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                          <span style={{ fontSize:12, textTransform:"capitalize" }}>{fs.fmt.replace(/_/g," ")}</span>
                          <span style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:wr>50?"var(--green)":"var(--muted)" }}>{wr}% · {fs.w}W–{fs.l}L</span>
                        </div>
                        <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${wr}%`, background:wr>50?"var(--green)":"rgba(255,255,255,0.2)", borderRadius:3, transition:"width 0.3s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── COMPARE ─────────────────────────────────────────────────────── */}
      {tab==="compare" && (
        <div>
          <div style={{ marginBottom:16 }}>
            <select style={{ ...css.select }} value={compareId} onChange={e=>setCompareId(e.target.value)}>
              <option value="">— Select a player to compare —</option>
              {allPlayers.filter(p=>p.id!==player.id).map(p=>(
                <option key={p.id} value={p.id}>{p.name}{p.nick?` "${p.nick}"`:""}</option>
              ))}
            </select>
          </div>

          {!comparePlayer ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>⚖️</div>
              <div>Select a player above to compare</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {/* Header row */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 1fr", gap:8, alignItems:"center" }}>
                <div style={{ background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:6, padding:"10px 14px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:2 }}>{player.name}</div>
                  {player.nick && <div style={{ fontSize:11, color:"var(--gold)", fontStyle:"italic" }}>"{player.nick}"</div>}
                </div>
                <div style={{ textAlign:"center", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, color:"var(--muted)", letterSpacing:2 }}>VS</div>
                <div style={{ background:"rgba(91,141,238,0.08)", border:"1px solid rgba(91,141,238,0.3)", borderRadius:6, padding:"10px 14px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:2 }}>{comparePlayer.name}</div>
                  {comparePlayer.nick && <div style={{ fontSize:11, color:"#5b8dee", fontStyle:"italic" }}>"{comparePlayer.nick}"</div>}
                </div>
              </div>

              {/* Stat comparisons */}
              {[
                ["Power Rating", pr, comparePR, false],
                ["Elo",          player.elo||1200, comparePlayer.elo||1200, false],
                ["Win Rate",     winRate, compareWR, true],
                ["Wins",         player.wins, comparePlayer.wins, false],
                ["Cup Diff",     player.cupDiff, comparePlayer.cupDiff, false],
                ["Events",       player.tournaments||0, comparePlayer.tournaments||0, false],
              ].map(([label, a, b, isPct]) => {
                const aWins = a > b, tie = a === b;
                const maxVal = Math.max(Math.abs(a), Math.abs(b), 1);
                const aW = Math.min(100, Math.round(Math.abs(a)/maxVal*100));
                const bW = Math.min(100, Math.round(Math.abs(b)/maxVal*100));
                return (
                  <div key={label}>
                    <div style={{ textAlign:"center", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:5 }}>{label}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 1fr", gap:8, alignItems:"center" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ flex:1, height:8, background:"var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${aW}%`, background:aWins?"var(--gold)":tie?"var(--muted)":"var(--navy-border)", borderRadius:4, marginLeft:"auto", transition:"width 0.3s" }} />
                        </div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:aWins?"var(--gold)":tie?"var(--muted)":"var(--white)", minWidth:40, textAlign:"right" }}>{a}{isPct?"%":""}</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        {aWins ? <span style={{ color:"var(--gold)", fontSize:14 }}>◀</span> : !tie ? <span style={{ color:"#5b8dee", fontSize:14 }}>▶</span> : <span style={{ color:"var(--muted)", fontSize:10 }}>TIE</span>}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:!aWins&&!tie?"#5b8dee":tie?"var(--muted)":"var(--white)", minWidth:40 }}>{b}{isPct?"%":""}</div>
                        <div style={{ flex:1, height:8, background:"var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${bW}%`, background:!aWins&&!tie?"#5b8dee":tie?"var(--muted)":"var(--navy-border)", borderRadius:4, transition:"width 0.3s" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* H2H between these two */}
              {h2hMap[comparePlayer.name] && (
                <div style={{ marginTop:4, padding:"12px 16px", background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:6, textAlign:"center" }}>
                  <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Head-to-Head Record</div>
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:16 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"var(--gold)", lineHeight:1 }}>{h2hMap[comparePlayer.name].w}</div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--muted)", fontSize:14 }}>–</div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"#5b8dee", lineHeight:1 }}>{h2hMap[comparePlayer.name].l}</div>
                  </div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>{h2hMap[comparePlayer.name].matches} matches played</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── STAFF NOTES TAB ───────────────────────────────────────────────── */}
      {tab==="scout" && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{padding:"8px 12px",background:"rgba(217,79,61,0.07)",border:"1px solid rgba(217,79,61,0.25)",borderRadius:5,fontSize:11,color:"var(--red)"}}>
            🔒 Staff-only — visible to admins and referees only. Not shown to players.
          </div>

          {/* Quick tags */}
          <div>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Quick Tags</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {["⚡ Fast Player","🎯 Accurate Shooter","🧊 Clutch","😤 Tilts Under Pressure","🤝 Good Sport","⚠ Watch for Disputes","🏆 Tournament Favorite","💤 Slow Pace","🔥 Hot Streak","🆕 New to Scene"].map(tag=>(
                <div key={tag} onClick={()=>setScoutNote(n=>n ? n+"\n"+tag : tag)}
                  style={{padding:"4px 10px",background:"rgba(255,255,255,0.04)",border:"1px solid var(--navy-border)",borderRadius:12,fontSize:11,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,transition:"all 0.1s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="var(--gold)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--navy-border)"}>{tag}</div>
              ))}
            </div>
          </div>

          {/* Note field */}
          <Fld label="Scouting Notes">
            <textarea
              value={scoutNote}
              onChange={e=>setScoutNote(e.target.value)}
              placeholder={"Observations, tendencies, notes for referees…\n\nExamples:\n• Dominant in 3v3 but struggles in 1v1\n• Verify scores carefully — has disputed 2 calls\n• Check-in usually late, confirm entry fee"}
              style={{...css.input,width:"100%",minHeight:160,resize:"vertical",lineHeight:1.7,fontFamily:"inherit"}}
            />
          </Fld>

          {/* Timestamps / history */}
          {player?.scoutNote && player.scoutNote !== scoutNote && (
            <div style={{fontSize:11,color:"var(--muted)",fontStyle:"italic"}}>
              ⚠ Unsaved changes — saved version: "{player.scoutNote.slice(0,60)}{player.scoutNote.length>60?"…":""}"
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <Btn onClick={()=>{
              if (onUpdate) onUpdate({...player, scoutNote});
              setScoutSaved(true);
              setTimeout(()=>setScoutSaved(false),2000);
            }} style={{color:scoutSaved?"var(--green)":undefined,borderColor:scoutSaved?"rgba(46,204,113,0.4)":undefined}}>
              {scoutSaved?"✓ Saved!":"💾 Save Notes"}
            </Btn>
            {scoutNote && <Btn v="ghost" onClick={()=>setScoutNote("")} style={{color:"var(--muted)"}}>Clear</Btn>}
          </div>

          {/* Last match notes */}
          {(player.matchHistory||[]).filter(m=>m.note).length > 0 && (
            <div style={{...css.card,padding:"12px 16px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Match Notes</div>
              {(player.matchHistory||[]).filter(m=>m.note).slice(-5).reverse().map((m,i)=>(
                <div key={i} style={{padding:"6px 0",borderBottom:i<4?"1px solid rgba(255,255,255,0.04)":"none",fontSize:12}}>
                  <span style={{color:"var(--muted)",fontSize:10}}>{m.tournament||"Event"} · </span>
                  <span style={{fontStyle:"italic",color:"var(--white)"}}>{m.note}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── SPECTATOR MODE ─────────────────────────────────────────────────────────────
function SpectatorMatchCard({ match, accentColor = "#c9a84c", large = false }) {
  if (!match) return null;
  const isComplete = match.status === "complete" || match.status === "bye";
  const isPending  = match.status === "pending";
  const isApproval = match.status === "pending_approval";
  const isDisputed = match.status === "disputed";
  const isBye      = match.status === "bye";

  const borderColor = isComplete ? "rgba(46,204,113,0.3)"
    : isDisputed   ? "rgba(217,79,61,0.5)"
    : isApproval   ? "rgba(91,141,238,0.4)"
    : isPending    ? accentColor + "99"
    : "rgba(255,255,255,0.06)";

  const bgColor = isComplete ? "rgba(46,204,113,0.04)"
    : isDisputed ? "rgba(217,79,61,0.04)"
    : isApproval ? "rgba(91,141,238,0.04)"
    : "var(--navy-mid)";

  const fSize = large ? 16 : 13;
  const scoreSize = large ? 28 : 20;
  const pad = large ? "10px 14px" : "8px 10px";

  return (
    <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 6, overflow: "hidden", minWidth: large ? 240 : 190 }}>
      {match.isReset && (
        <div style={{ padding: "2px 8px", background: "rgba(217,79,61,0.15)", fontSize: 9, color: "var(--red)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 2, textAlign: "center", textTransform: "uppercase" }}>🔁 Bracket Reset</div>
      )}
      {[{ team: match.t1, score: match.s1 }, { team: match.t2, score: match.s2 }].map((side, i) => {
        const isW = side.team && match.winner && (side.team?.name === match.winner?.name || side.team?.id === match.winner?.id);
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: pad, borderBottom: i === 0 ? "1px solid rgba(255,255,255,0.06)" : "none", background: isW ? "rgba(201,168,76,0.08)" : "transparent" }}>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: isW ? 700 : 400, fontSize: fSize, color: side.team ? (isW ? "var(--gold-light)" : "var(--white)") : "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                {side.team?.name || (isBye && i === 1 ? "— BYE —" : "TBD")}
              </span>
            </div>
            {isW && <span style={{ fontSize: 10, marginRight: 6, flexShrink: 0 }}>🏆</span>}
            {side.score !== null && side.score !== undefined && (
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: scoreSize, color: isW ? "var(--gold)" : isComplete ? "var(--muted)" : "var(--white)", marginLeft: 8, minWidth: 24, textAlign: "right", flexShrink: 0 }}>{side.score}</div>
            )}
          </div>
        );
      })}
      {match.ot && isComplete && (
        <div style={{ padding: "2px 8px", background: "rgba(201,168,76,0.07)", fontSize: 9, color: "var(--gold)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textAlign: "center", textTransform: "uppercase" }}>⭐ Overtime</div>
      )}
      {(isApproval || isDisputed) && (
        <div style={{ padding: "3px 8px", background: isDisputed ? "rgba(217,79,61,0.1)" : "rgba(91,141,238,0.1)", fontSize: 9, color: isDisputed ? "var(--red)" : "#5b8dee", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, textAlign: "center", textTransform: "uppercase" }}>
          {isDisputed ? "⚠ SCORE DISPUTED" : "⏳ AWAITING APPROVAL"}
        </div>
      )}
    </div>
  );
}

function SpectatorPage({ tournaments, players, seasons }) {
  const { isMobile } = useBreakpoint();

  const [selectedTournId, setSelectedTournId] = useState(tournaments[0]?.id || "");
  const [view,            setView]            = useState("live");    // live | bracket | standings | info
  const [tick,            setTick]            = useState(0);
  const [embedMode,       setEmbedMode]       = useState(false);
  const [showShareSheet,  setShowShareSheet]  = useState(false);
  const [chatMessages,   setChatMessages]   = useState([
    { id:"c1", user:"JordanL",    text:"Let's go! This bracket is 🔥",            ts: Date.now()-240000 },
    { id:"c2", user:"SimonePark", text:"Webb & Cole looking unstoppable",          ts: Date.now()-120000 },
    { id:"c3", user:"DevonM",     text:"Table 3 match going to overtime!",         ts: Date.now()-30000 },
  ]);
  const [chatInput,       setChatInput]      = useState("");
  const [chatUser,        setChatUser]       = useState("Spectator");
  const [followedPlayer,  setFollowedPlayer] = useState(null);
  const [specView,        setSpecView]       = useState("live"); // live | bracket | standings | stats | chat
  const [shareToast,      setShareToast]      = useState(false);
  const [embedToast,      setEmbedToast]      = useState(false);
  const [showQR,          setShowQR]          = useState(false);
  const [autoRefresh,     setAutoRefresh]     = useState(true);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const tournament = tournaments.find(t => t.id === selectedTournId);

  function allMatches() {
    if (!tournament) return [];
    return getAllMatchesFromTournament(tournament);
  }

  const totalMatches = allMatches().filter(m => m.t1 && m.t2 && m.status !== "bye");
  const doneMatches  = totalMatches.filter(m => m.status === "complete");
  const liveMatches  = totalMatches.filter(m => m.status === "pending" && m.t1 && m.t2);
  const awaitingApproval = totalMatches.filter(m => m.status === "pending_approval");
  const disputed    = totalMatches.filter(m => m.status === "disputed");
  const progress    = totalMatches.length > 0 ? Math.round(doneMatches.length / totalMatches.length * 100) : 0;
  const recentCompleted = [...doneMatches].reverse().slice(0, 8);

  const activeSeason = (seasons||[]).find(s => s.status === "active");
  const seasonStandings = activeSeason ? calcSeasonStandings(activeSeason, tournaments, players) : [];

  // Simple QR-code SVG (deterministic pixel grid from tournament id)
  function QRCodeSVG({ value = "mlcp.app", size = 120 }) {
    const seed = value.split("").reduce((s,c) => s + c.charCodeAt(0), 0);
    const grid = 10;
    const cells = [];
    for (let r = 0; r < grid; r++) for (let c = 0; c < grid; c++) {
      const dark = ((seed * (r + 1) * (c + 1) * 7919) % 13) < 6;
      cells.push({ r, c, dark });
    }
    const cell = size / grid;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display:"block" }}>
        <rect width={size} height={size} fill="white" rx="4" />
        {/* Corner squares */}
        {[[0,0],[0,grid-3],[grid-3,0]].map(([r,c],i)=>(
          <g key={i}>
            <rect x={c*cell} y={r*cell} width={3*cell} height={3*cell} fill="#1a2d4a" rx={2} />
            <rect x={c*cell+cell*0.5} y={r*cell+cell*0.5} width={2*cell} height={2*cell} fill="white" rx={1} />
            <rect x={c*cell+cell} y={r*cell+cell} width={cell} height={cell} fill="#1a2d4a" rx={1} />
          </g>
        ))}
        {cells.map(({r,c,dark}) => {
          // Skip corner areas
          if ((r<3&&c<3)||(r<3&&c>=grid-3)||(r>=grid-3&&c<3)) return null;
          return dark ? <rect key={`${r}-${c}`} x={c*cell+1} y={r*cell+1} width={cell-2} height={cell-2} fill="#1a2d4a" rx={1} /> : null;
        })}
      </svg>
    );
  }

  // Standings for current tournament
  function getTournamentStandings() {
    if (!tournament) return [];
    const wins = {};
    allMatches().filter(m => m.status === "complete" && m.winner).forEach(m => {
      const n = m.winner.name; wins[n] = (wins[n]||0) + 1;
    });
    const allTeams = new Set([...allMatches().map(m=>m.t1?.name), ...allMatches().map(m=>m.t2?.name)].filter(Boolean));
    return [...allTeams].map(name => ({
      name,
      wins: wins[name]||0,
      losses: allMatches().filter(m=>m.status==="complete"&&(m.t1?.name===name||m.t2?.name===name)&&m.winner?.name!==name).length,
    })).sort((a,b) => b.wins - a.wins || a.losses - b.losses);
  }

  const tournStandings = getTournamentStandings();

  function copyShareLink() {
    navigator.clipboard?.writeText(`https://mlcp.app/watch/${tournament?.id||"live"}`).catch(()=>{});
    setShareToast(true); setTimeout(()=>setShareToast(false), 2500);
  }

  function copyEmbed() {
    const code = `<iframe src="https://mlcp.app/embed/${tournament?.id||"live"}" width="800" height="600" frameborder="0"></iframe>`;
    navigator.clipboard?.writeText(code).catch(()=>{});
    setEmbedToast(true); setTimeout(()=>setEmbedToast(false), 2500);
  }

  // ── EMBED MODE (fullscreen minimal) ──────────────────────────────────────
  if (embedMode) {
    return (
      <div style={{ position:"fixed", inset:0, background:"var(--navy)", display:"flex", flexDirection:"column", zIndex:600, overflow:"hidden" }}>
        {/* Embed header strip */}
        <div style={{ background:"var(--navy-mid)", borderBottom:"2px solid var(--gold)", padding:"8px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:4, color:"var(--gold)" }}>MLCP</div>
            {tournament && (
              <>
                <div style={{ width:1, height:16, background:"var(--navy-border)" }} />
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  {tournament.status==="active" && <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--green)", animation:"pulse 1.5s infinite" }} />}
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1 }}>{tournament.name}</span>
                </div>
              </>
            )}
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>
              {progress}% · {doneMatches.length}/{totalMatches.length} matches
            </span>
            <Btn sz="sm" v="ghost" onClick={()=>setEmbedMode(false)}>⛶ Exit Embed</Btn>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height:3, background:"rgba(255,255,255,0.06)", flexShrink:0 }}>
          <div style={{ height:"100%", width:`${progress}%`, background:"var(--gold)", transition:"width 0.4s" }} />
        </div>
        {/* Embed body — live view */}
        <div style={{ flex:1, overflow:"auto", padding:16 }}>
          {renderLive(true)}
        </div>
        {/* Footer */}
        <div style={{ borderTop:"1px solid var(--navy-border)", padding:"6px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, background:"rgba(0,0,0,0.2)" }}>
          <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>mlcp.app · Live Scores</span>
          <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>Auto-refresh {autoRefresh?"ON":"OFF"}</span>
        </div>
      </div>
    );
  }

  // ── LIVE VIEW ──────────────────────────────────────────────────────────────
  function renderLive(compact = false) {
    if (!tournament) return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>📺</div>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:16 }}>Select an Event</div>
        <select style={{ ...css.select, maxWidth:300, fontSize:14 }} value={selectedTournId} onChange={e=>setSelectedTournId(e.target.value)}>
          <option value="">— choose event —</option>
          {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
    );

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {/* Alert strips */}
        {disputed.length > 0 && (
          <div style={{ padding:"8px 14px", background:"rgba(217,79,61,0.08)", border:"1px solid rgba(217,79,61,0.35)", borderRadius:6, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:16 }}>⚠️</span>
            <span style={{ fontSize:13, color:"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{disputed.length} DISPUTED SCORE{disputed.length>1?"S":""} — STAFF RESOLVING</span>
          </div>
        )}
        {awaitingApproval.length > 0 && (
          <div style={{ padding:"8px 14px", background:"rgba(91,141,238,0.06)", border:"1px solid rgba(91,141,238,0.3)", borderRadius:6, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:16 }}>⏳</span>
            <span style={{ fontSize:13, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{awaitingApproval.length} SCORE{awaitingApproval.length>1?"S":""} AWAITING APPROVAL</span>
          </div>
        )}

        {/* Live matches */}
        {liveMatches.length > 0 && (
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--green)", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:"var(--green)", animation:"pulse 1.5s infinite" }} />
              In Play ({liveMatches.length})
            </div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(auto-fill,minmax(${compact?200:240}px,1fr))`, gap:10 }}>
              {liveMatches.map((m,i) => <SpectatorMatchCard key={m.id||i} match={m} large={!compact} />)}
            </div>
          </div>
        )}

        {/* Awaiting approval */}
        {awaitingApproval.length > 0 && (
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"#5b8dee", marginBottom:10 }}>Awaiting Approval ({awaitingApproval.length})</div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(auto-fill,minmax(${compact?200:240}px,1fr))`, gap:10 }}>
              {awaitingApproval.map((m,i) => <SpectatorMatchCard key={m.id||i} match={m} accentColor="#5b8dee" />)}
            </div>
          </div>
        )}

        {/* Recent completed */}
        {recentCompleted.length > 0 && (
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>Recent Results</div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(auto-fill,minmax(${compact?180:220}px,1fr))`, gap:8 }}>
              {recentCompleted.map((m,i) => <SpectatorMatchCard key={m.id||i} match={m} />)}
            </div>
          </div>
        )}

        {/* Empty state */}
        {liveMatches.length===0 && recentCompleted.length===0 && (
          <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
            <div>No matches in progress yet. Check back soon.</div>
          </div>
        )}
      </div>
    );
  }

  // ── BRACKET VIEW ────────────────────────────────────────────────────────────
  function renderBracket() {
    if (!tournament) return null;
    const bkt = tournament.bracket;
    if (!bkt) return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:36, marginBottom:8 }}>📊</div>
        <div>Bracket not yet generated for this tournament.</div>
      </div>
    );

    function renderRounds(rounds, label) {
      if (!rounds || rounds.length === 0) return null;
      return (
        <div>
          {label && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>{label}</div>}
          <div style={{ display:"flex", gap:16, overflowX:"auto", paddingBottom:8, WebkitOverflowScrolling:"touch" }}>
            {rounds.map((round, ri) => (
              <div key={ri} style={{ flexShrink:0 }}>
                <div style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:6, textAlign:"center" }}>
                  {round.name || `Round ${ri+1}`}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {(round.matches||[]).map((m,mi) => <SpectatorMatchCard key={mi} match={m} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
        {bkt.gf && (
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)", marginBottom:10 }}>🏆 Grand Final</div>
            <SpectatorMatchCard match={bkt.gf} large />
          </div>
        )}
        {renderRounds(bkt.wb, "Winners Bracket")}
        {renderRounds(bkt.lb, "Losers Bracket")}
        {renderRounds(bkt.rounds, null)}
      </div>
    );
  }

  // ── STANDINGS VIEW ──────────────────────────────────────────────────────────
  function renderStandings() {
    const hasSeasonData = seasonStandings.length > 0;
    const displayRows = hasSeasonData ? seasonStandings.slice(0,12) : tournStandings.slice(0,12);
    if (displayRows.length === 0) return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:36, marginBottom:8 }}>🏆</div>
        <div>No standings data available yet.</div>
      </div>
    );
    const medals = ["var(--gold)","#C0C0C0","#CD7F32"];
    return (
      <div>
        <div style={{ marginBottom:12, fontSize:12, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>
          {hasSeasonData ? `Season: ${activeSeason?.name}` : `Event: ${tournament?.name}`}
        </div>
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          {displayRows.map((row, i) => {
            const name  = row.player?.name || row.name;
            const pts   = hasSeasonData ? row.pts : `${row.wins}W–${row.losses}L`;
            const color = medals[i] || "var(--muted)";
            const barMax = hasSeasonData ? (displayRows[0]?.pts||1) : (displayRows[0]?.wins||1);
            const barVal = hasSeasonData ? row.pts : row.wins;
            const barPct = barMax > 0 ? Math.round(barVal/barMax*100) : 0;
            return (
              <div key={name+i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderBottom:i<displayRows.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color, width:28, flexShrink:0, textAlign:"center" }}>{i+1}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{name}</div>
                  <div style={{ marginTop:3, height:3, background:"rgba(255,255,255,0.07)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${barPct}%`, background:color, borderRadius:2 }} />
                  </div>
                </div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color, flexShrink:0 }}>{pts}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── INFO VIEW ────────────────────────────────────────────────────────────────
  function renderInfo() {
    if (!tournament) return null;
    const regs = tournament.registrations || [];
    const checkedIn = regs.filter(r=>r.status==="checked_in").length;
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ ...css.card, padding:"16px 20px" }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:3, marginBottom:4 }}>{tournament.name}</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
            <Badge color={tournament.status==="active"?"green":tournament.status==="completed"?"gold":""}>{tournament.status}</Badge>
            {tournament.date && <span style={{ fontSize:12, color:"var(--muted)" }}>📅 {tournament.date}</span>}
            {tournament.venue && <span style={{ fontSize:12, color:"var(--muted)" }}>📍 {tournament.venue}</span>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {[
              [regs.length, "Teams", "var(--white)"],
              [checkedIn, "Checked In", "var(--green)"],
              [progress+"%", "Complete", progress===100?"var(--gold)":"var(--white)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ textAlign:"center", padding:"10px 8px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:4 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Teams list */}
        {regs.length > 0 && (
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>Registered Teams</span>
            </div>
            {regs.slice(0,20).map((r,i) => (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<regs.length-1&&i<19?"1px solid rgba(255,255,255,0.04)":"none" }}>
                {r.seed && <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:"var(--gold)", width:22, flexShrink:0 }}>#{r.seed}</span>}
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{r.teamName}</div>
                  {r.players?.length>0 && <div style={{ fontSize:11, color:"var(--muted)" }}>{r.players.map(p=>p.name).join(" · ")}</div>}
                </div>
                <Badge color={r.status==="checked_in"?"green":r.status==="no_show"?"":""}>
                  {r.status==="checked_in"?"✓ IN":r.status==="no_show"?"NO-SHOW":"PENDING"}
                </Badge>
              </div>
            ))}
            {regs.length > 20 && <div style={{ padding:"8px 14px", fontSize:11, color:"var(--muted)" }}>+{regs.length-20} more teams…</div>}
          </div>
        )}
      </div>
    );
  }

  // ── Share sheet modal ────────────────────────────────────────────────────────
  function renderShareSheet() {
    return (
      <Modal open={showShareSheet} onClose={()=>setShowShareSheet(false)} title="Share & Embed" width={440}>
        {/* QR code */}
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ display:"inline-block", padding:12, background:"white", borderRadius:8, marginBottom:10 }}>
            <QRCodeSVG value={`mlcp.app/watch/${tournament?.id||"live"}`} size={120} />
          </div>
          <div style={{ fontSize:12, color:"var(--muted)" }}>Scan to open live view on any device</div>
        </div>

        {/* Share link */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Public Link</div>
          <div style={{ display:"flex", gap:8 }}>
            <div style={{ flex:1, padding:"8px 12px", background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:4, fontSize:12, color:"var(--muted)", fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              https://mlcp.app/watch/{tournament?.id||"live"}
            </div>
            <Btn onClick={copyShareLink}>{shareToast?"✓ Copied!":"📋 Copy"}</Btn>
          </div>
        </div>

        {/* Embed code */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Embed Code</div>
          <div style={{ padding:"10px 12px", background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:4, fontSize:11, color:"var(--muted)", fontFamily:"monospace", wordBreak:"break-all", marginBottom:8 }}>
            {`<iframe src="https://mlcp.app/embed/${tournament?.id||"live"}" width="800" height="600" frameborder="0"></iframe>`}
          </div>
          <Btn v="ghost" onClick={copyEmbed} style={{ width:"100%", justifyContent:"center" }}>{embedToast?"✓ Copied!":"📋 Copy Embed Code"}</Btn>
        </div>

        {/* Options */}
        <div style={{ display:"flex", gap:12, paddingTop:12, borderTop:"1px solid var(--navy-border)" }}>
          <Btn onClick={()=>{ setEmbedMode(true); setShowShareSheet(false); }} style={{ flex:1, justifyContent:"center" }}>⛶ Preview Embed</Btn>
          <Btn v="ghost" onClick={()=>setShowShareSheet(false)} style={{ flex:1, justifyContent:"center" }}>Close</Btn>
        </div>
      </Modal>
    );
  }

  // ── MAIN RENDER ─────────────────────────────────────────────────────────────
  const VIEWS = [
    { id:"live",      label:"Live",      icon:"🔴" },
    { id:"bracket",   label:"Bracket",   icon:"📊" },
    { id:"standings", label:"Standings", icon:"🏆" },
    { id:"stats",     label:"Stats",     icon:"📈" },
    { id:"chat",        label:"Chat",        icon:"💬" },
    { id:"commentary",  label:"Commentary",  icon:"📢" },
    { id:"info",        label:"Info",        icon:"ℹ" },
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Spectator</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:3, display:"flex", alignItems:"center", gap:8 }}>
            Public live view · read-only · shareable
            {t && (
              <span style={{ display:"flex", alignItems:"center", gap:4, padding:"2px 8px", background:"rgba(46,204,113,0.1)", border:"1px solid rgba(46,204,113,0.25)", borderRadius:10 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"var(--green)", animation:"pulse 1.5s infinite", display:"inline-block" }}/>
                <span style={{ fontSize:10, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                  {Math.max(1, Math.floor(Math.sin(tick*0.7+1)*8)+12)} watching
                </span>
              </span>
            )}
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <select style={{ ...css.select, fontSize:12 }} value={selectedTournId} onChange={e=>setSelectedTournId(e.target.value)}>
            <option value="">— Select event —</option>
            {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {/* Auto-refresh toggle */}
          <label style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer", fontSize:11, color:"var(--muted)" }}>
            <div onClick={()=>setAutoRefresh(p=>!p)} style={{ width:28, height:16, borderRadius:8, background:autoRefresh?"var(--green)":"rgba(255,255,255,0.1)", position:"relative", cursor:"pointer", transition:"background 0.2s" }}>
              <div style={{ position:"absolute", top:2, left:autoRefresh?12:2, width:12, height:12, borderRadius:"50%", background:"var(--white)", transition:"left 0.2s" }} />
            </div>
            {autoRefresh?"Auto":"Paused"}
          </label>
          <Btn v="ghost" sz="sm" onClick={()=>setShowShareSheet(true)}>🔗 Share</Btn>
          <Btn sz="sm" onClick={()=>setEmbedMode(true)}>⛶ Embed</Btn>
        </div>
      </div>

      {/* Progress bar */}
      {tournament && totalMatches.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
            <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{tournament.name}</span>
            <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
              {doneMatches.length}/{totalMatches.length} · {progress}%
            </span>
          </div>
          <div style={{ height:6, background:"var(--navy-border)", borderRadius:3, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${progress}%`, background:progress===100?"var(--gold)":"var(--green)", borderRadius:3, transition:"width 0.4s" }} />
          </div>
        </div>
      )}

      {/* Live status strip */}
      {tournament && (
        <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
          {[
            [liveMatches.length, "In Play", "var(--green)", true],
            [awaitingApproval.length, "Approval", "#5b8dee", false],
            [disputed.length, "Disputed", "var(--red)", false],
            [doneMatches.length, "Done", "var(--muted)", false],
          ].map(([v,l,c,pulse]) => v > 0 || l === "Done" ? (
            <div key={l} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 12px", background:`${c}18`, border:`1px solid ${c}44`, borderRadius:20 }}>
              {pulse && v>0 && <div style={{ width:6, height:6, borderRadius:"50%", background:c, animation:"pulse 1.5s infinite" }} />}
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:c, lineHeight:1 }}>{v}</span>
              <span style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, color:c, textTransform:"uppercase" }}>{l}</span>
            </div>
          ) : null)}
        </div>
      )}

      {/* Tab strip */}
      <div className="tab-strip" style={{ marginBottom:18 }}>
        {VIEWS.map(v => (
          <button key={v.id} className={`tab-btn ${view===v.id?"tab-active":""}`} onClick={()=>setView(v.id)}>
            {v.icon} {v.label}
          </button>
        ))}
      </div>

      {view==="live"      && renderLive()}
      {view==="bracket"   && renderBracket()}
      {view==="standings" && renderStandings()}
      {view==="info"      && renderInfo()}

      {/* ── Stats View ─────────────────────────────────────────────────── */}
      {view==="stats" && tournament && (() => {
        const allM = allMatches.filter(m=>m.status==="complete"&&m.t1&&m.t2);
        const teams = Object.values(allM.reduce((acc,m)=>{
          [m.t1,m.t2].forEach((t,i)=>{
            if(!t) return;
            if(!acc[t.name]) acc[t.name]={name:t.name,w:0,l:0,cups:0,allowed:0,ot:0};
            const won = m.winner?.name===t.name;
            acc[t.name].w += won?1:0;
            acc[t.name].l += won?0:1;
            acc[t.name].cups += i===0?(m.s1||0):(m.s2||0);
            acc[t.name].allowed += i===0?(m.s2||0):(m.s1||0);
            acc[t.name].ot += m.ot?1:0;
          });
          return acc;
        },{})).sort((a,b)=>b.w-a.w||(b.cups-b.allowed)-(a.cups-a.allowed));

        const topScorer = teams.reduce((best,t)=>t.cups>best.cups?t:best, {cups:0,name:"—"});
        const otKing    = teams.reduce((best,t)=>t.ot>best.ot?t:best, {ot:0,name:"—"});
        return (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                ["🎯 Total Matches",   allM.length,          "var(--gold)"],
                ["⚡ Overtime Games",  allM.filter(m=>m.ot).length, "#5b8dee"],
                ["🏆 Top Scorer",      topScorer.name,       "var(--green)"],
                ["😅 OT King",         otKing.name,          "var(--red)"],
              ].map(([label,val,color])=>(
                <div key={label} style={{...css.card,padding:"14px 16px"}}>
                  <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>{label}</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color,lineHeight:1}}>{val||"—"}</div>
                </div>
              ))}
            </div>
            <div style={{...css.card,padding:0,overflow:"hidden"}}>
              <div style={{padding:"8px 14px",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Team Leaderboard</div>
              {teams.slice(0,10).map((t,i)=>(
                <div key={t.name} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderBottom:i<teams.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:i<3?"var(--gold)":"var(--muted)",minWidth:20,textAlign:"center"}}>{i+1}</div>
                  <div style={{flex:1,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                  <span style={{fontSize:11,color:"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>{t.w}W</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>–</span>
                  <span style={{fontSize:11,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>{t.l}L</span>
                  <span style={{fontSize:11,color:t.cups-t.allowed>=0?"var(--green)":"var(--red)",minWidth:36,textAlign:"right",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>{t.cups-t.allowed>=0?"+":""}{t.cups-t.allowed}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Chat View ──────────────────────────────────────────────────── */}
      {view==="chat" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {/* Follow a player */}
          <div style={{...css.card,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase"}}>👀 Following:</span>
            {followedPlayer ? (
              <>
                <span style={{fontSize:13,fontWeight:700,color:"var(--gold)"}}>{followedPlayer}</span>
                <button onClick={()=>setFollowedPlayer(null)} style={{fontSize:11,color:"var(--muted)",background:"none",border:"none",cursor:"pointer"}}>✕ Unfollow</button>
              </>
            ) : (
              <span style={{fontSize:12,color:"var(--muted)"}}>Nobody yet — tap a player name to follow</span>
            )}
          </div>

          {/* Chat messages */}
          <div style={{...css.card,padding:0,overflow:"hidden",minHeight:300}}>
            <div style={{padding:"8px 14px",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>
              💬 Live Chat · {chatMessages.length} messages
            </div>
            <div style={{maxHeight:340,overflowY:"auto",padding:"8px 0"}}>
              {chatMessages.map((msg,i)=>{
                // Check if player name mentioned
                const mentionsFollowed = followedPlayer && msg.text.toLowerCase().includes(followedPlayer.toLowerCase());
                return (
                  <div key={msg.id||i} style={{padding:"7px 14px",background:mentionsFollowed?"rgba(201,168,76,0.06)":"transparent",borderLeft:mentionsFollowed?"3px solid var(--gold)":"3px solid transparent"}}>
                    <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                      <span style={{fontSize:11,fontWeight:700,color:"#5b8dee",cursor:"pointer"}} onClick={()=>setFollowedPlayer(msg.user)}>{msg.user}</span>
                      <span style={{fontSize:9,color:"rgba(255,255,255,0.25)"}}>{new Date(msg.ts).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</span>
                    </div>
                    <div style={{fontSize:13,marginTop:2}}>{msg.text}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chat input */}
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <input value={chatUser} onChange={e=>setChatUser(e.target.value)} placeholder="Your name"
              style={{...css.input,width:120,fontSize:12}} />
            <input value={chatInput} onChange={e=>setChatInput(e.target.value)} placeholder="Say something…"
              style={{...css.input,flex:1,fontSize:13}}
              onKeyDown={e=>{
                if(e.key==="Enter"&&chatInput.trim()){
                  setChatMessages(prev=>[...prev,{id:"c"+Date.now(),user:chatUser||"Spectator",text:chatInput.trim(),ts:Date.now()}]);
                  setChatInput("");
                }
              }} />
            <Btn onClick={()=>{
              if(!chatInput.trim()) return;
              setChatMessages(prev=>[...prev,{id:"c"+Date.now(),user:chatUser||"Spectator",text:chatInput.trim(),ts:Date.now()}]);
              setChatInput("");
            }}>Send</Btn>
          </div>
        </div>
      )}

      {/* ── Commentary View ─────────────────────────────────────────── */}
      {view==="commentary" && (() => {
        const commentLines = [];
        if (tournament) {
          const allM = allMatches.filter(m=>m.status==="complete"&&m.t1&&m.t2);
          allM.forEach((m) => {
            const diff = Math.abs((m.s1||0)-(m.s2||0));
            const winner = m.winner?.name || (m.s1>m.s2?m.t1?.name:m.t2?.name);
            const loser  = m.winner?.name===m.t1?.name?m.t2?.name:m.t1?.name;
            const bracket = m._bracket==="GF"?"in the Grand Final":m._bracket==="L"?`in Losers R${m._round}`:`in Winners R${m._round}`;
            const emoji = diff===0?"🤝":diff>=8?"💥":m.ot?"⚡":"🏓";
            const line = m.ot
              ? `${emoji} ${winner} edges out ${loser} in OVERTIME ${bracket} — ${m.s1}–${m.s2}`
              : `${emoji} ${winner} defeats ${loser} ${bracket}${diff>=8?" in dominant fashion":diff<=2?" in a close one":""} (${m.s1}–${m.s2})`;
            commentLines.push({ line, ts:m.completedAt||null, id:m.id||Math.random() });
          });
        }
        const lines = commentLines.length > 0 ? commentLines : [
          { line:"📢 Commentary will appear here as matches are completed", ts:null, id:"placeholder" }
        ];
        return (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:2}}>
              AUTO-COMMENTARY · {commentLines.length} PLAYS RECORDED
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {lines.slice().reverse().map((item,i)=>(
                <div key={item.id} style={{...css.card,padding:"12px 16px",borderLeft:`3px solid ${i===0?"var(--gold)":"rgba(255,255,255,0.08)"}`,background:i===0?"rgba(201,168,76,0.04)":"rgba(255,255,255,0.01)"}}>
                  <div style={{fontSize:13,lineHeight:1.5,fontWeight:i===0?600:400}}>{item.line}</div>
                  {item.ts&&<div style={{fontSize:10,color:"var(--muted)",marginTop:4}}>{new Date(item.ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {renderShareSheet()}
    </div>
  );
}


// ── PLAYERS ──────────────────────────────────────────────────────────────────
function PlayersPage({ players, setPlayers }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [showAdd, setShowAdd] = useState(false);
  const [profilePlayer, setProfilePlayer] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("pr");
  const [f, setF] = useState({ name:"",nick:"",role:"player",email:"",phone:"" });
  const upd = (k,v) => setF(x=>({...x,[k]:v}));

  // ── New: bulk import, merge, compare ─────────────────────────────────────
  const [importModal,  setImportModal]  = useState(false);
  const [importRaw,    setImportRaw]    = useState("");
  const [importParsed, setImportParsed] = useState([]);
  const [mergeModal,   setMergeModal]   = useState(false);
  const [mergeIds,     setMergeIds]     = useState(new Set());
  const [mergeName,    setMergeName]    = useState("");
  const [compareIds,   setCompareIds]   = useState(new Set());
  const [compareModal, setCompareModal] = useState(false);
  const [selectMode,   setSelectMode]   = useState(false);
  const [bulkEloIds,   setBulkEloIds]   = useState(new Set());
  const [bulkEloModal, setBulkEloModal] = useState(false);
  const [bulkEloDelta, setBulkEloDelta] = useState("0");
  const [bulkEloMode,  setBulkEloMode]  = useState("adjust"); // adjust | set // "merge" | "compare" | false

  function parsePlayerCSV(raw) {
    const lines = raw.trim().split("\n").filter(Boolean);
    const parsed = [];
    lines.forEach((line,i) => {
      const cols = line.split(",").map(s=>s.trim().replace(/^"|"$/g,""));
      if (i===0 && cols[0].toLowerCase()==="name") return; // skip header
      const [name,nick,email,phone,role] = cols;
      if (!name) return;
      parsed.push({ name, nick:nick||"", email:email||"", phone:phone||"", role:role||"player" });
    });
    return parsed;
  }

  function commitPlayerImport() {
    const newPlayers = importParsed.filter(row => !players.some(p=>p.name.toLowerCase()===row.name.toLowerCase()))
      .map(row=>({ id:uid(), name:row.name, nick:row.nick, email:row.email, phone:row.phone,
        role:row.role||"player", elo:1200, wins:0, losses:0, cupsMade:0, cupsAllowed:0, cupDiff:0,
        placements:[], tournaments:0, matchHistory:[] }));
    setPlayers(p=>[...p,...newPlayers]);
    setImportModal(false); setImportRaw(""); setImportParsed([]);
  }

  function doMerge() {
    if (mergeIds.size < 2 || !mergeName) return;
    const ids = [...mergeIds];
    const base = players.find(p=>p.id===ids[0]);
    const others = players.filter(p=>ids.includes(p.id));
    const merged = { ...base, id:uid(), name:mergeName,
      wins: others.reduce((s,p)=>s+p.wins,0),
      losses: others.reduce((s,p)=>s+p.losses,0),
      cupsMade: others.reduce((s,p)=>s+p.cupsMade,0),
      cupsAllowed: others.reduce((s,p)=>s+p.cupsAllowed,0),
      matchHistory: others.flatMap(p=>p.matchHistory||[]),
      elo: Math.round(others.reduce((s,p)=>s+(p.elo||1200),0)/others.length),
    };
    merged.cupDiff = merged.cupsMade - merged.cupsAllowed;
    setPlayers(prev=>[...prev.filter(p=>!ids.includes(p.id)), merged]);
    setMergeModal(false); setMergeIds(new Set()); setMergeName("");
    setSelectMode(false);
  }

  function addPlayer() {
    setPlayers(p=>[...p,{ id:uid(),name:f.name,nick:f.nick,email:f.email,phone:f.phone,role:f.role,elo:1200,wins:0,losses:0,cupsMade:0,cupsAllowed:0,cupDiff:0,placements:[],tournaments:0,matchHistory:[] }]);
    setShowAdd(false); setF({ name:"",nick:"",role:"player",email:"",phone:"" });
  }

  function handleUpdate(updated) {
    setPlayers(prev => prev.map(p => p.id === updated.id ? updated : p));
    setProfilePlayer(updated);
  }

  const SORTS = [["pr","PR"],["elo","Elo"],["wins","Wins"],["cupDiff","Cup Diff"]];
  const sorted = [...players]
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.nick||"").toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      if (sortBy==="pr")      return calcPowerRating(b)-calcPowerRating(a);
      if (sortBy==="elo")     return b.elo-a.elo;
      if (sortBy==="wins")    return b.wins-a.wins;
      if (sortBy==="cupDiff") return b.cupDiff-a.cupDiff;
      return 0;
    });

  return (
    <div className="fade-in">
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>Players</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{players.length} registered · click any row for full profile</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <input style={{ ...css.input, width:isMobile?140:200 }} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} />
          <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
            {SORTS.map(([v,l])=>(
              <div key={v} onClick={()=>setSortBy(v)} style={{ padding:"6px 10px", cursor:"pointer", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", background:sortBy===v?"var(--gold)":"transparent", color:sortBy===v?"var(--navy)":"var(--muted)", transition:"all 0.15s" }}>{l}</div>
            ))}
          </div>
          <Btn onClick={()=>setShowAdd(true)}>+ Add Player</Btn>
          <Btn v="ghost" onClick={()=>setImportModal(true)} style={{fontSize:11}}>⬆ Import CSV</Btn>
          <Btn v="ghost" onClick={()=>{setSelectMode("merge");setMergeIds(new Set());}} style={{fontSize:11,color:selectMode==="merge"?"var(--gold)":undefined}}>⊕ Merge</Btn>
          <Btn v="ghost" onClick={()=>{setSelectMode("compare");setCompareIds(new Set());}} style={{fontSize:11,color:selectMode==="compare"?"#5b8dee":undefined}}>⊞ Compare</Btn>
          <Btn v="ghost" onClick={()=>{setSelectMode("bulkElo");setBulkEloIds(new Set());}} style={{fontSize:11,color:selectMode==="bulkElo"?"var(--gold)":undefined}}>⚡ Bulk Elo</Btn>
          <Btn v="ghost" onClick={()=>{
            const rows=[["Name","Nickname","Elo","Wins","Losses","Win%"].join(",")];
            (players||[]).forEach(p=>{
              const wr=p.wins+p.losses>0?Math.round(p.wins/(p.wins+p.losses)*100):0;
              rows.push([p.name,p.nick||"",p.elo||1200,p.wins,p.losses,wr+"%"].map(v=>`"${String(v).replace(/"/g,'""')}"`).join(","));
            });
            const blob=new Blob([rows.join("\n")],{type:"text/csv"});
            const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download="players.csv";a.click();
          }} style={{fontSize:11}}>📥 Export CSV</Btn>
          {(selectMode==="merge"||selectMode==="compare"||selectMode==="bulkElo")&&<Btn v="ghost" onClick={()=>setSelectMode(false)} style={{fontSize:11,color:"var(--muted)"}}>✕ Cancel</Btn>}
          {selectMode==="merge"&&mergeIds.size>=2&&<Btn sz="sm" onClick={()=>setMergeModal(true)} style={{fontSize:11}}>Merge {mergeIds.size}</Btn>}
          {selectMode==="bulkElo"&&bulkEloIds.size>=1&&<Btn sz="sm" onClick={()=>setBulkEloModal(true)} style={{fontSize:11,background:"rgba(201,168,76,0.12)",borderColor:"rgba(201,168,76,0.4)",color:"var(--gold)"}}>Adjust Elo ({bulkEloIds.size})</Btn>}
          {selectMode==="compare"&&compareIds.size>=2&&<Btn sz="sm" onClick={()=>setCompareModal(true)} style={{fontSize:11,background:"rgba(91,141,238,0.12)",borderColor:"rgba(91,141,238,0.4)",color:"#5b8dee"}}>Compare {compareIds.size}</Btn>}
        </div>
      </div>

      <div style={css.card}>
        {isMobile ? (
          /* ── Mobile card list ── */
          <div className="mob-card-list" style={{ padding:8 }}>
            {sorted.length === 0 ? (
              <div style={{ textAlign:"center",padding:"30px 0",color:"var(--muted)",fontSize:13 }}>No players match your search</div>
            ) : sorted.map((p,i) => {
              const pr = calcPowerRating(p);
              const wr = (p.wins+p.losses)>0 ? Math.round(p.wins/(p.wins+p.losses)*100) : 0;
              const best = p.placements?.length ? Math.min(...p.placements) : null;
              const medal = i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
              return (
                <div key={p.id} className="mob-card" onClick={()=>setProfilePlayer(p)} style={{ borderLeft:`3px solid ${medal||"var(--navy-border)"}` }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:medal||"var(--muted)",width:24,textAlign:"center",flexShrink:0 }}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":i+1}</div>
                    <div style={{ width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${medal||"#1e3250"},${medal?"var(--navy-mid)":"#5b8dee"})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:medal?"var(--navy)":"var(--white)",flexShrink:0 }}>{p.name.charAt(0)}</div>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontWeight:700,fontSize:14 }}>{p.name}{p.nick&&<span style={{ fontSize:11,color:"var(--gold)",fontStyle:"italic",marginLeft:5 }}>"{p.nick}"</span>}</div>
                      <div style={{ display:"flex",gap:8,marginTop:2,flexWrap:"wrap" }}>
                        <span style={{ fontSize:12 }}><span style={{ color:"var(--green)" }}>{p.wins}W</span><span style={{ color:"var(--muted)",margin:"0 2px" }}>–</span><span style={{ color:"var(--red)" }}>{p.losses}L</span></span>
                        <span style={{ fontSize:12,color:wr>=50?"var(--green)":"var(--muted)" }}>{wr}%</span>
                        <span style={{ fontSize:12,color:"var(--muted)" }}>Elo {p.elo}</span>
                      </div>
                    </div>
                    <div style={{ textAlign:"right",flexShrink:0 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:medal||"var(--gold-light)",lineHeight:1 }}>{pr}</div>
                      <div style={{ fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1 }}>PR</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Desktop table ── */
          <>
            <div className="resp-table"><table style={{ width:"100%",borderCollapse:"collapse",minWidth:540 }}>
              <thead>
                <tr>{["#","Player","W–L","Win %","Cup ±","Elo","PR","Events","Best","Role",""].map(h=><th key={h} style={css.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {sorted.map((p,i) => {
                  const pr = calcPowerRating(p);
                  const wr = (p.wins+p.losses)>0 ? Math.round(p.wins/(p.wins+p.losses)*100) : 0;
                  const best = p.placements?.length ? Math.min(...p.placements) : null;
                  const medal = i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;
                  // Form last 5
                  const form = (p.matchHistory||[]).slice(-5).map(m=>m.won);
                  return (
                    <tr key={p.id}
                      onClick={()=>{ if(selectMode==="merge"){ setMergeIds(prev=>{const s=new Set(prev);s.has(p.id)?s.delete(p.id):s.add(p.id);return s;}); return; } if(selectMode==="compare"){ setCompareIds(prev=>{const s=new Set(prev);s.has(p.id)?s.delete(p.id):s.add(p.id);return s;}); return; } if(selectMode==="bulkElo"){ setBulkEloIds(prev=>{const s=new Set(prev);s.has(p.id)?s.delete(p.id):s.add(p.id);return s;}); return; } setProfilePlayer(p); }}
                      style={{ cursor:"pointer", borderBottom:"1px solid rgba(255,255,255,0.04)", transition:"background 0.1s", background:mergeIds.has(p.id)?"rgba(201,168,76,0.07)":compareIds.has(p.id)?"rgba(91,141,238,0.07)":bulkEloIds.has(p.id)?"rgba(201,168,76,0.12)":"" }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ ...css.td, width:36, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:medal||"var(--muted)" }}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":i+1}</td>
                      <td style={css.td}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:30, height:30, borderRadius:"50%", background:`linear-gradient(135deg,${medal||"#1e3250"},${medal?"var(--navy-mid)":"#5b8dee"})`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:medal?"var(--navy)":"var(--white)", flexShrink:0, overflow:"hidden" }}>{p.photoUrl?<img src={p.photoUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:p.name.charAt(0)}</div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:13 }}>{p.name}</div>
                            {p.nick&&<div style={{ fontSize:11,color:"var(--gold)",fontStyle:"italic" }}>"{p.nick}"</div>}
                          </div>
                        </div>
                      </td>
                      <td style={css.td}>
                        <span style={{ color:"var(--green)" }}>{p.wins}W</span>
                        <span style={{ color:"var(--muted)",margin:"0 3px" }}>–</span>
                        <span style={{ color:"var(--red)" }}>{p.losses}L</span>
                      </td>
                      <td style={{ ...css.td }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                          <div style={{ width:32, height:5, background:"var(--navy-border)", borderRadius:2, overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${wr}%`, background:wr>=50?"var(--green)":"var(--red)", borderRadius:2 }} />
                          </div>
                          <span style={{ fontSize:11, color:wr>=50?"var(--green)":"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{wr}%</span>
                        </div>
                      </td>
                      <td style={{ ...css.td, color:p.cupDiff>=0?"var(--green)":"var(--red)", fontWeight:700 }}>{p.cupDiff>=0?"+":""}{p.cupDiff}</td>
                      <td style={{ ...css.td,fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:"var(--muted)" }}>{p.elo}</td>
                      <td style={{ ...css.td,fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:medal||"var(--gold-light)" }}>{pr}</td>
                      <td style={{ ...css.td, textAlign:"center", color:"var(--muted)" }}>{p.tournaments||0}</td>
                      <td style={{ ...css.td, textAlign:"center" }}>{best?<span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--gold)" }}>#{best}</span>:"—"}</td>
                      <td style={css.td}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <Badge color={ROLE_COLOR[p.role]||""}>{p.role.replace(/_/g," ")}</Badge>
                          {form.length > 0 && (
                            <div style={{ display:"flex", gap:2 }}>
                              {form.map((w,fi) => <div key={fi} style={{ width:8, height:8, borderRadius:2, background:w?"var(--green)":"var(--red)", opacity:0.7 }} />)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ ...css.td, textAlign:"center" }} onClick={e=>e.stopPropagation()}>
                        <button
                          onClick={e=>{ e.stopPropagation(); if(window.confirm(`Remove ${p.name} from the roster? This cannot be undone.`)) { setPlayers(prev=>prev.filter(pp=>pp.id!==p.id)); if(profilePlayer?.id===p.id) setProfilePlayer(null); }}}
                          style={{ background:"rgba(217,79,61,0.1)", border:"1px solid rgba(217,79,61,0.3)", borderRadius:4, color:"var(--red)", cursor:"pointer", fontSize:11, padding:"3px 8px", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}
                          title="Remove player">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
            {sorted.length === 0 && (
              <div style={{ textAlign:"center", padding:"30px 0", color:"var(--muted)", fontSize:13 }}>No players match your search</div>
            )}
          </>
        )}
      </div>

      {/* Profile modal */}
      {profilePlayer && (
        <PlayerProfile
          player={profilePlayer}
          allPlayers={players}
          onClose={()=>setProfilePlayer(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Add player modal */}
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Add Player">
        <Fld label="Full Name"><input style={css.input} value={f.name} onChange={e=>upd("name",e.target.value)} placeholder="First Last" /></Fld>
        <Fld label='Nickname (optional)'><input style={css.input} value={f.nick} onChange={e=>upd("nick",e.target.value)} placeholder='e.g. "Laser"' /></Fld>
        <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <Fld label="Email"><input style={css.input} type="email" value={f.email} onChange={e=>upd("email",e.target.value)} placeholder="player@email.com" /></Fld>
          <Fld label="Phone"><input style={css.input} type="tel" value={f.phone} onChange={e=>upd("phone",e.target.value)} placeholder="(555) 000-0000" /></Fld>
        </div>
        <Fld label="Role"><select style={css.select} value={f.role} onChange={e=>upd("role",e.target.value)}>{ROLES.map(r=><option key={r} value={r}>{r.replace(/_/g," ")}</option>)}</select></Fld>
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:6 }}>
          <Btn v="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
          <Btn onClick={addPlayer} disabled={!f.name}>Add Player</Btn>
        </div>
      </Modal>

      {/* ── Bulk Import Modal ─────────────────────────────────────────────── */}
      <Modal open={importModal} onClose={()=>{setImportModal(false);setImportRaw("");setImportParsed([]);}} title="⬆ Import Players via CSV" width={540}>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:10,lineHeight:1.7}}>
          Paste CSV rows: <code style={{background:"rgba(255,255,255,0.06)",padding:"1px 5px",borderRadius:3,fontSize:11}}>Name, Nickname, Email, Phone, Role</code><br/>
          First row may be a header. Duplicate names are skipped.
        </div>
        <textarea style={{...css.input,width:"100%",minHeight:140,fontFamily:"monospace",fontSize:12,resize:"vertical"}}
          placeholder={"Alex Smith, Ace, alex@mlcp.gg, 555-0100, player\nJordan Lee, JL, , ,staff"}
          value={importRaw} onChange={e=>{setImportRaw(e.target.value);setImportParsed(parsePlayerCSV(e.target.value));}} />
        {importParsed.length>0&&(
          <div style={{marginTop:10}}>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:6}}>{importParsed.length} rows parsed · {importParsed.filter(r=>players.some(p=>p.name.toLowerCase()===r.name.toLowerCase())).length} duplicates (will be skipped)</div>
            <div style={{maxHeight:160,overflowY:"auto",display:"flex",flexDirection:"column",gap:3}}>
              {importParsed.map((r,i)=>{
                const dup = players.some(p=>p.name.toLowerCase()===r.name.toLowerCase());
                return(
                  <div key={i} style={{display:"flex",gap:8,padding:"5px 10px",background:dup?"rgba(217,79,61,0.07)":"rgba(46,204,113,0.04)",border:`1px solid ${dup?"rgba(217,79,61,0.2)":"rgba(46,204,113,0.15)"}`,borderRadius:4,fontSize:12}}>
                    <span style={{color:dup?"var(--red)":"var(--green)",width:12}}>{dup?"⚠":"✓"}</span>
                    <span style={{fontWeight:700}}>{r.name}</span>
                    {r.nick&&<span style={{color:"var(--muted)"}}>{r.nick}</span>}
                    {r.role&&r.role!=="player"&&<Badge>{r.role}</Badge>}
                    {dup&&<span style={{marginLeft:"auto",fontSize:10,color:"var(--red)"}}>duplicate</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
          <Btn v="ghost" onClick={()=>{setImportModal(false);setImportRaw("");setImportParsed([]);}}>Cancel</Btn>
          <Btn onClick={commitPlayerImport} disabled={importParsed.filter(r=>!players.some(p=>p.name.toLowerCase()===r.name.toLowerCase())).length===0}>
            Import {importParsed.filter(r=>!players.some(p=>p.name.toLowerCase()===r.name.toLowerCase())).length} Players
          </Btn>
        </div>
      </Modal>

      {/* ── Merge Players Modal ───────────────────────────────────────────── */}
      <Modal open={mergeModal} onClose={()=>{setMergeModal(false);}} title="⊕ Merge Players" width={440}>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:10,lineHeight:1.7}}>
          Stats (wins, losses, cups, match history) will be combined. A new merged player is created and the originals are removed.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
          {[...mergeIds].map(id=>{
            const p = players.find(x=>x.id===id);
            if(!p) return null;
            return(
              <div key={id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:5}}>
                <span style={{fontWeight:700,fontSize:13,flex:1}}>{p.name}</span>
                <span style={{fontSize:11,color:"var(--muted)"}}>{p.wins}W {p.losses}L · Elo {p.elo||1200}</span>
                <button onClick={()=>setMergeIds(prev=>{const s=new Set(prev);s.delete(id);return s;})} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14}}>✕</button>
              </div>
            );
          })}
        </div>
        <Fld label="Merged Player Name *">
          <input style={css.input} value={mergeName} onChange={e=>setMergeName(e.target.value)} placeholder="e.g. Alex Smith (merged)" autoFocus />
        </Fld>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
          <Btn v="ghost" onClick={()=>setMergeModal(false)}>Cancel</Btn>
          <Btn onClick={doMerge} disabled={!mergeName||mergeIds.size<2}>Merge {mergeIds.size} Players</Btn>
        </div>
      </Modal>

      {/* ── Compare Modal ─────────────────────────────────────────────────── */}
      <Modal open={compareModal} onClose={()=>{setCompareModal(false);setCompareIds(new Set());setSelectMode(false);}} title="⊞ Career Comparison" width={580}>
        {compareModal&&(()=>{
          const compared = [...compareIds].map(id=>players.find(p=>p.id===id)).filter(Boolean);
          const statKeys=[
            ["Elo",p=>p.elo||1200,"var(--gold)",true],
            ["Wins",p=>p.wins,"var(--green)",true],
            ["Losses",p=>p.losses,"var(--red)",false],
            ["Win %",p=>p.wins+p.losses>0?Math.round(p.wins/(p.wins+p.losses)*100):0,"var(--green)",true],
            ["Cups Made",p=>p.cupsMade||0,"var(--green)",true],
            ["Cups Allowed",p=>p.cupsAllowed||0,"var(--red)",false],
            ["Cup Diff",p=>p.cupDiff||0,"var(--gold)",true],
            ["Tournaments",p=>p.tournaments||0,"#5b8dee",true],
          ];
          return(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {/* Player headers */}
              <div style={{display:"grid",gridTemplateColumns:`80px repeat(${compared.length},1fr)`,gap:6,alignItems:"center"}}>
                <div/>
                {compared.map(p=>(
                  <div key={p.id} style={{textAlign:"center",padding:"8px 6px",background:"rgba(255,255,255,0.03)",borderRadius:5}}>
                    <div style={{fontWeight:700,fontSize:13}}>{p.name}</div>
                    {p.nick&&<div style={{fontSize:11,color:"var(--muted)"}}>{p.nick}</div>}
                  </div>
                ))}
              </div>
              {/* Stat rows */}
              {statKeys.map(([label,fn,color,higherBetter])=>{
                const vals = compared.map(fn);
                const best = higherBetter?Math.max(...vals):Math.min(...vals);
                return(
                  <div key={label} style={{display:"grid",gridTemplateColumns:`80px repeat(${compared.length},1fr)`,gap:6,alignItems:"center"}}>
                    <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase"}}>{label}</div>
                    {vals.map((v,i)=>{
                      const isBest=v===best;
                      return(
                        <div key={i} style={{textAlign:"center",padding:"6px 4px",background:isBest?"rgba(201,168,76,0.08)":"transparent",border:isBest?"1px solid rgba(201,168,76,0.2)":"1px solid transparent",borderRadius:4}}>
                          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:isBest?color:"var(--white)"}}>{v}{label==="Win %"?"%":""}</span>
                          {isBest&&<div style={{fontSize:8,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>BEST</div>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>

      {/* ── Bulk Elo Adjust Modal ─────────────────────────────────────────── */}
      <Modal open={bulkEloModal} onClose={()=>{setBulkEloModal(false);setBulkEloIds(new Set());setSelectMode(false);}} title="⚡ Bulk Elo Adjustment" width={460}>
        {bulkEloModal&&(()=>{
          const selected = [...bulkEloIds].map(id=>(players||[]).find(p=>p.id===id)).filter(Boolean);
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{fontSize:12,color:"var(--muted)"}}>Adjusting Elo for {selected.length} player{selected.length!==1?"s":""}:</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:80,overflowY:"auto",padding:"8px 10px",background:"rgba(255,255,255,0.03)",borderRadius:4}}>
                {selected.map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:5,padding:"3px 8px",background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:12}}>
                    <span style={{fontSize:12}}>{p.name}</span>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color:"var(--gold)"}}>{p.elo||1200}</span>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden",width:"fit-content"}}>
                {[["adjust","Adjust ±"],["set","Set to"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setBulkEloMode(v)}
                    style={{padding:"6px 14px",background:bulkEloMode===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:bulkEloMode===v?"var(--gold)":"var(--muted)",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,cursor:"pointer"}}>{l}</button>
                ))}
              </div>
              <Fld label={bulkEloMode==="adjust"?"Elo Delta (e.g. +50 or -25)":"New Elo Value"}>
                <input style={{...css.input,textAlign:"center",fontSize:22,fontFamily:"'Bebas Neue',sans-serif"}}
                  type="number" value={bulkEloDelta} onChange={e=>setBulkEloDelta(e.target.value)}
                  placeholder={bulkEloMode==="adjust"?"±0":"1200"} />
              </Fld>
              {bulkEloMode==="adjust"&&bulkEloDelta&&!isNaN(parseInt(bulkEloDelta))&&(
                <div style={{padding:"8px 12px",background:"rgba(255,255,255,0.03)",borderRadius:4,fontSize:12,color:"var(--muted)"}}>
                  Preview: {selected.slice(0,3).map(p=>`${p.name}: ${p.elo||1200} → ${(p.elo||1200)+parseInt(bulkEloDelta)}`).join(" · ")}{selected.length>3?` · +${selected.length-3} more`:""}
                </div>
              )}
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn v="ghost" onClick={()=>setBulkEloModal(false)}>Cancel</Btn>
                <Btn onClick={()=>{
                  const delta=parseInt(bulkEloDelta)||0;
                  setPlayers(prev=>prev.map(p=>{
                    if(!bulkEloIds.has(p.id)) return p;
                    const newElo = bulkEloMode==="set" ? delta : (p.elo||1200)+delta;
                    return {...p,elo:Math.max(100,Math.min(3000,newElo))};
                  }));
                  setBulkEloModal(false);setBulkEloIds(new Set());setSelectMode(false);setBulkEloDelta("0");
                }} disabled={!bulkEloDelta||isNaN(parseInt(bulkEloDelta))}>Apply to {selected.length}</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}


// ── SEASONS ───────────────────────────────────────────────────────────────────

// ── Placement derivation helpers (unchanged) ──────────────────────────────────
function getTournamentPlacements(t) {
  if (!t?.bracket) return {};
  const bkt = t.bracket;
  const placements = {};

  function setPlace(teamId, place) {
    if (teamId && place) placements[teamId] = place;
  }

  if (bkt.type === "de" || bkt.type === "de_phase") {
    const wb = bkt.wb || [], lb = bkt.lb || [];
    // Champion
    if (bkt.gf?.winner?.id) setPlace(bkt.gf.winner.id, 1);
    const gfLoser = bkt.gf && (bkt.gf.winner?.id === bkt.gf.t1?.id ? bkt.gf.t2 : bkt.gf.t1);
    if (gfLoser?.id) setPlace(gfLoser.id, 2);
    // Last LB round loser = 3rd, etc.
    if (lb.length > 0) {
      const lastLB = lb[lb.length - 1];
      lastLB?.matches?.forEach(m => {
        const loser = m.winner?.id === m.t1?.id ? m.t2 : m.t1;
        if (loser?.id && !placements[loser.id]) setPlace(loser.id, 3);
      });
    }
  } else {
    const rounds = bkt.rounds || [];
    if (rounds.length > 0) {
      const finalRound = rounds[rounds.length - 1];
      finalRound?.matches?.forEach(m => {
        if (m.winner?.id) setPlace(m.winner.id, 1);
        const loser = m.winner?.id === m.t1?.id ? m.t2 : m.t1;
        if (loser?.id) setPlace(loser.id, 2);
      });
    }
  }
  return placements;
}

function getPlayerPlacementInTournament(player, tournament) {
  const placements = getTournamentPlacements(tournament);
  if (placements[player.id]) return placements[player.id];
  // Fallback: check stored placements
  const stored = (player.placements || []).find(p => p.tournamentId === tournament.id);
  return stored?.place || null;
}

// ── Head-to-head computation ──────────────────────────────────────────────────
function computeH2H(pA, pB, tournaments) {
  // Scan all match history of pA for matches against pB
  let winsA = 0, winsB = 0;
  (pA.matchHistory || []).forEach(m => {
    const opp = m.opponentId || m.opponent?.id;
    if (opp === pB.id) {
      if (m.won) winsA++; else winsB++;
    }
  });
  return { winsA, winsB, totalA: winsA + winsB };
}

// ── UPGRADED standings engine ─────────────────────────────────────────────────
function calcSeasonStandings(season, tournaments, players) {
  const pc          = season.pointsConfig || {};
  const placePts    = pc.placements || { 1:100, 2:70, 3:50, 4:30, 5:15, 6:10 };
  const winPts      = pc.win         ?? 3;
  const lossPts     = pc.loss        ?? 1;
  const cdBonus     = pc.cupDiffBonus?? 0.1;
  const bestN       = pc.bestN       || 0;   // 0 = all events count
  const tiebreakers = pc.tiebreakers || ["pts","wins","cd","h2h","alpha"];

  const relevant = tournaments.filter(t =>
    (season.tournamentIds || []).includes(t.id) && t.ranked && t.type === "pro"
  );

  const stats = {};

  players.forEach(p => {
    const hist = (p.matchHistory || []).filter(m =>
      relevant.some(t => t.id === m.tournamentId)
    );
    if (hist.length === 0) return;

    // Per-event breakdown
    const eventBreakdown = {};
    relevant.forEach(t => {
      const tHist = hist.filter(m => m.tournamentId === t.id);
      if (tHist.length === 0) return;
      const tWins   = tHist.filter(m => m.won).length;
      const tLosses = tHist.filter(m => !m.won).length;
      const tCD     = tHist.reduce((s, m) => s + (m.cupDiff || 0), 0);
      const tMatchPts = tWins * winPts + tLosses * lossPts + Math.max(0, tCD) * cdBonus;
      const placement = getPlayerPlacementInTournament(p, t);
      const maxKey    = Math.max(...Object.keys(placePts).map(Number));
      const pPts      = placement ? (placePts[placement] || placePts[maxKey] || 0) : 0;
      eventBreakdown[t.id] = {
        wins: tWins, losses: tLosses, cd: tCD,
        matchPts: Math.round(tMatchPts * 10) / 10,
        placePts: pPts,
        total: Math.round((tMatchPts + pPts) * 10) / 10,
        placement,
        tournament: t,
      };
    });

    // Best-N: if bestN>0, only count top-N event totals
    const allEventTotals = Object.values(eventBreakdown).map(e => e.total).sort((a,b) => b-a);
    const countedTotals  = bestN > 0 ? allEventTotals.slice(0, bestN) : allEventTotals;
    const dropped        = bestN > 0 ? allEventTotals.slice(bestN) : [];

    // Mark which events are "dropped"
    if (bestN > 0) {
      let droppedBudget = [...dropped];
      Object.values(eventBreakdown).forEach(e => {
        const idx = droppedBudget.indexOf(e.total);
        if (idx !== -1) { e.dropped = true; droppedBudget.splice(idx, 1); }
      });
    }

    const pts = Math.round(countedTotals.reduce((s,v) => s+v, 0) * 10) / 10;

    // Aggregate wins/losses/cd only from counted events
    const countedIds = Object.entries(eventBreakdown)
      .filter(([,e]) => !e.dropped).map(([id]) => id);
    const countedHist = hist.filter(m => countedIds.includes(m.tournamentId));
    const wins   = countedHist.filter(m => m.won).length;
    const losses = countedHist.filter(m => !m.won).length;
    const cd     = countedHist.reduce((s,m) => s+(m.cupDiff||0),0);

    stats[p.id] = {
      player: p, wins, losses, cd, pts,
      events: Object.keys(eventBreakdown).length,
      countedEvents: countedIds.length,
      droppedEvents: dropped.length,
      eventBreakdown,
      allMatchHistory: hist,
    };
  });

  const rows = Object.values(stats);

  // Multi-criteria sort with configurable tiebreakers
  rows.sort((a, b) => {
    for (const criterion of tiebreakers) {
      if (criterion === "pts")   { if (b.pts   !== a.pts)   return b.pts   - a.pts;   }
      if (criterion === "wins")  { if (b.wins  !== a.wins)  return b.wins  - a.wins;  }
      if (criterion === "cd")    { if (b.cd    !== a.cd)    return b.cd    - a.cd;    }
      if (criterion === "h2h") {
        // H2H only meaningful for direct pairwise — skip in multi-player sort
        // (handled in display layer for tied pairs)
      }
      if (criterion === "events") { if (b.events !== a.events) return b.events - a.events; }
      if (criterion === "alpha") { return a.player.name.localeCompare(b.player.name); }
    }
    return 0;
  });

  // Assign rank + tie flags
  return rows.map((row, i) => {
    const prev   = rows[i - 1];
    const next   = rows[i + 1];
    // Mark tied if pts (and wins and cd) are all equal to adjacent
    const tiedWithPrev = prev && prev.pts === row.pts && prev.wins === row.wins && prev.cd === row.cd;
    const tiedWithNext = next && next.pts === row.pts && next.wins === row.wins && next.cd === row.cd;
    return { ...row, rank: i + 1, tiedWithPrev, tiedWithNext };
  });
}

// ── Playoff seeding helpers ───────────────────────────────────────────────────
function buildPlayoffMatchups(qualifiers, format) {
  // format: "standard" (1v8 2v7...) | "top2bye" (1&2 get byes) | "sequential" (1v2 3v4...)
  const n = qualifiers.length;
  if (n < 2) return [];
  if (format === "sequential") {
    const pairs = [];
    for (let i = 0; i < n - 1; i += 2) pairs.push([i, i + 1]);
    return pairs;
  }
  if (format === "top2bye") {
    // Seeds 1 & 2 get byes, 3v6, 4v5 in first round
    const pairs = [];
    for (let i = 2; i < Math.min(n, 6); i += 2) {
      if (i + 1 < n) pairs.push([i, n - 1 - (i - 2)]);
    }
    return pairs;
  }
  // Standard: 1v(n), 2v(n-1), ...
  const pairs = [];
  for (let i = 0; i < Math.floor(n / 2); i++) pairs.push([i, n - 1 - i]);
  return pairs;
}

// ── SeasonsPage ───────────────────────────────────────────────────────────────
function SeasonsPage({ seasons, setSeasons, tournaments, setTournaments, players, setPage, setActiveTournament }) {
  const { isMobile } = useBreakpoint();

  const [selectedSeasonId, setSelectedSeasonId] = useState(seasons[0]?.id || null);
  const [tab,              setTab]               = useState("overview");
  const [showCreate,       setShowCreate]        = useState(false);
  const [expandedRow,      setExpandedRow]       = useState(null);
  const [seedOverrides,    setSeedOverrides]      = useState({});  // playerId → seed
  const [playoffN,         setPlayoffN]           = useState(8);
  const [playoffFormat,    setPlayoffFormat]      = useState("standard");
  const [tiebreakPair,     setTiebreakPair]       = useState(null); // {a,b} show H2H
  const [showH2HModal,     setShowH2HModal]       = useState(false);
  const [tiebreakerModal,  setTiebreakerModal]     = useState(null); // {pairA, pairB} standings rows
  const [tbStep,           setTbStep]              = useState(0);    // 0=h2h,1=cupdiff,2=coinflip
  const [tbResolution,     setTbResolution]        = useState(null); // {winner, method, auditNote}
  const [tbAuditLog,       setTbAuditLog]          = useState([]);   // [{pairA,pairB,winner,method,ts}]
  const [tbCoinResult,     setTbCoinResult]        = useState(null); // "heads"/"tails"
  const [tbCoinFlipping,   setTbCoinFlipping]      = useState(false);

  const [form, setForm] = useState({ name:"", description:"", year: new Date().getFullYear(), status:"upcoming", startDate:"", endDate:"" });
  const upd = (k,v) => setForm(f => ({ ...f, [k]: v }));

  const season = seasons.find(s => s.id === selectedSeasonId) || seasons[0];

  // ── Season CRUD ──────────────────────────────────────────────────────────
  function createSeason() {
    if (!form.name) return;
    const id = uid();
    const s = {
      id, name:form.name, description:form.description,
      year:parseInt(form.year)||new Date().getFullYear(),
      status:form.status, startDate:form.startDate, endDate:form.endDate,
      tournamentIds:[], pointsConfig:{
        win:3, loss:1, cupDiffBonus:0.1, bestN:0,
        placements:{ 1:100, 2:70, 3:50, 4:30, 5:15, 6:10 },
        tiebreakers:["pts","wins","cd","h2h","events","alpha"],
      },
    };
    setSeasons(prev => [...prev, s]);
    setSelectedSeasonId(id);
    setShowCreate(false);
    setForm({ name:"", description:"", year:new Date().getFullYear(), status:"upcoming", startDate:"", endDate:"" });
  }

  function toggleTournament(tid) {
    if (!season) return;
    setSeasons(prev => prev.map(s => s.id !== season.id ? s : {
      ...s, tournamentIds: (s.tournamentIds||[]).includes(tid)
        ? (s.tournamentIds||[]).filter(id => id !== tid)
        : [...(s.tournamentIds||[]), tid]
    }));
  }

  function updateField(k, v) {
    if (!season) return;
    setSeasons(prev => prev.map(s => s.id !== season.id ? s : { ...s, [k]: v }));
  }

  function updatePC(k, v) {
    if (!season) return;
    setSeasons(prev => prev.map(s => s.id !== season.id ? s : {
      ...s, pointsConfig: { ...(s.pointsConfig||{}), [k]: v }
    }));
  }

  function updatePlacementPts(place, val) {
    if (!season) return;
    const current = season.pointsConfig?.placements || {};
    setSeasons(prev => prev.map(s => s.id !== season.id ? s : {
      ...s, pointsConfig: { ...(s.pointsConfig||{}),
        placements: { ...current, [place]: parseFloat(val)||0 }
      }
    }));
  }

  function moveTiebreaker(idx, dir) {
    if (!season) return;
    const tbs = [...(season.pointsConfig?.tiebreakers || ["pts","wins","cd","h2h","events","alpha"])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= tbs.length) return;
    [tbs[idx], tbs[newIdx]] = [tbs[newIdx], tbs[idx]];
    updatePC("tiebreakers", tbs);
  }

  function deleteSeason() {
    if (!season) return;
    if (!window.confirm(`Delete season "${season.name}"?`)) return;
    setSeasons(prev => prev.filter(s => s.id !== season.id));
    setSelectedSeasonId(seasons.find(s => s.id !== season.id)?.id || null);
  }

  function daysTo(dateStr) {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    return Math.round(diff / 86400000);
  }

  // ── Computed data ────────────────────────────────────────────────────────
  const standings   = season ? calcSeasonStandings(season, tournaments, players) : [];
  const relevant    = tournaments.filter(t => (season?.tournamentIds||[]).includes(t.id));
  const remaining   = relevant.filter(t => t.status !== "completed");
  const qualifiers  = standings.slice(0, playoffN);

  // Playoff matchups
  const matchups = buildPlayoffMatchups(qualifiers, playoffFormat);

  // Seed overrides merged with standings rank
  function getEffectiveSeed(row) {
    return seedOverrides[row.player.id] ?? row.rank;
  }
  const seededQualifiers = [...qualifiers].sort((a,b) => getEffectiveSeed(a) - getEffectiveSeed(b));
  const seededMatchups   = buildPlayoffMatchups(seededQualifiers, playoffFormat);

  // ── TABS ─────────────────────────────────────────────────────────────────
  const TABS = [
    { id:"overview",  label:"Overview",        icon:"📋" },
    { id:"standings", label:"Standings",       icon:"🏆" },
    { id:"playoff",   label:"Playoff Seeding", icon:"🎯" },
    { id:"settings",  label:"Settings",        icon:"⚙" },
  ];

  // ── OVERVIEW TAB ─────────────────────────────────────────────────────────
  function renderOverview() {
    const totalEvents = relevant.length;
    const completed   = relevant.filter(t => t.status === "completed").length;
    const totalPlayers = standings.length;
    const leader       = standings[0];

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {/* Season selector pills */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {(seasons||[]).filter(s => !s.archived).map(s => (
            <div key={s.id} onClick={() => { setSelectedSeasonId(s.id); }}
              style={{ padding:"7px 16px", borderRadius:4, border:`1px solid ${selectedSeasonId===s.id?"var(--gold)":"var(--navy-border)"}`, background:selectedSeasonId===s.id?"rgba(201,168,76,0.12)":"transparent", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:selectedSeasonId===s.id?"var(--gold)":"var(--muted)", letterSpacing:1, transition:"all 0.15s" }}>
              {s.name}
              <span style={{ marginLeft:6, fontSize:10, color:"var(--muted)", fontWeight:400 }}>{s.year}</span>
            </div>
          ))}
          <Btn sz="sm" v="ghost" onClick={() => setShowCreate(true)}>+ New</Btn>
        </div>

        {!season ? (
          <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📅</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>No Seasons Yet</div>
            <Btn onClick={() => setShowCreate(true)}>+ Create First Season</Btn>
          </div>
        ) : (
          <>
            {/* Season header card */}
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                <div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, color:"var(--gold)", lineHeight:1 }}>{season.name}</div>
                  <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>{season.description||"No description"}</div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <Badge color={season.status==="active"?"green":season.status==="completed"?"gold":""}>{season.status}</Badge>
                  {season.startDate && <span style={{ fontSize:11, color:"var(--muted)" }}>{season.startDate}{season.endDate?" → "+season.endDate:""}</span>}
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)" }}>
                {[
                  [totalEvents,     "Events",        "var(--gold)"],
                  [completed+"/"+totalEvents, "Completed", "var(--green)"],
                  [totalPlayers,    "Participants",  "var(--white)"],
                  [leader?.player.name||"—", "Leader","var(--gold-light)"],
                ].map(([v,l,c])=>(
                  <div key={l} style={{ padding:"12px 10px", textAlign:"center", borderRight:"1px solid var(--navy-border)" }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:c==="var(--gold-light)"?16:26, color:c, lineHeight:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v}</div>
                    <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Events list */}
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase" }}>Events in Season</span>
                <span style={{ fontSize:11, color:"var(--muted)" }}>{totalEvents} total</span>
              </div>
              {/* Add tournament */}
              <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", background:"rgba(255,255,255,0.01)" }}>
                <select style={{ ...css.select, fontSize:12 }}
                  onChange={e => { if(e.target.value) toggleTournament(e.target.value); e.target.value=""; }}>
                  <option value="">+ Add tournament to season…</option>
                  {tournaments.filter(t => !(season.tournamentIds||[]).includes(t.id) && t.ranked && t.type==="pro").map(t=>(
                    <option key={t.id} value={t.id}>{t.name} ({t.status})</option>
                  ))}
                </select>
              </div>
              {relevant.length === 0 ? (
                <div style={{ padding:"24px 16px", textAlign:"center", color:"var(--muted)", fontSize:13 }}>No events added yet. Select ranked Pro events above.</div>
              ) : (
                relevant.map((t, i) => {
                  const d = daysTo(t.date);
                  return (
                    <div key={t.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", borderBottom:i<relevant.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:13 }}>{t.name}</div>
                        <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>{t.date||"TBD"} · {FORMATS.find(f=>f.v===t.format)?.l}</div>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                        <Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge>
                        {d !== null && d > 0 && <span style={{ fontSize:10, color:"var(--muted)" }}>in {d}d</span>}
                        <Btn sz="sm" v="ghost" onClick={()=>toggleTournament(t.id)}>✕</Btn>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Top 3 podium preview */}
            {standings.length >= 3 && (
              <div style={{ ...css.card, padding:"16px 20px" }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:14 }}>Current Leaders</div>
                <div style={{ display:"flex", gap:12, alignItems:"flex-end", justifyContent:"center" }}>
                  {[standings[1],standings[0],standings[2]].map((row,i) => {
                    if (!row) return null;
                    const heights = [80,100,68], medals = ["#C0C0C0","#C9A84C","#CD7F32"], emojis = ["🥈","🥇","🥉"];
                    const podiumRank = [1,0,2][i]; // center is rank 0
                    return (
                      <div key={row.player.id} style={{ flex:1, textAlign:"center" }}>
                        <div style={{ fontSize:13, fontWeight:600, marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.player.name}</div>
                        <div style={{ fontSize:11, color:"var(--muted)", marginBottom:6 }}>{row.pts} pts</div>
                        <div style={{ height:heights[i], background:`linear-gradient(180deg, ${medals[podiumRank]}22, ${medals[podiumRank]}08)`, border:`1px solid ${medals[podiumRank]}44`, borderRadius:"6px 6px 0 0", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ fontSize:heights[i]===100?28:22 }}>{emojis[podiumRank]}</span>
                        </div>
                        <div style={{ background:`${medals[podiumRank]}22`, padding:"3px 6px", fontSize:11, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:2, color:medals[podiumRank] }}>#{row.rank}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── STANDINGS TAB ─────────────────────────────────────────────────────────
  function renderStandings() {
    if (!season) return <div style={{ padding:40, textAlign:"center", color:"var(--muted)" }}>Select a season</div>;
    if (standings.length === 0) return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:32, marginBottom:8 }}>📊</div>
        <div>No standings yet — add ranked Pro events and ensure players have match history.</div>
      </div>
    );

    const pc     = season.pointsConfig || {};
    const bestN  = pc.bestN || 0;
    const tbs    = pc.tiebreakers || ["pts","wins","cd","h2h","events","alpha"];
    const tbLabels = { pts:"Pts", wins:"W", cd:"Cup Diff", h2h:"H2H", events:"Events", alpha:"Name" };

    return (
      <div>
        {/* Points config quick summary */}
        <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ fontSize:11, color:"var(--muted)" }}>
            <strong style={{ color:"var(--white)" }}>{pc.win??3}</strong> pts/win · <strong style={{ color:"var(--white)" }}>{pc.loss??1}</strong> pts/loss · <strong style={{ color:"var(--white)" }}>{(pc.cupDiffBonus??0.1)}×</strong> cup diff
            {bestN > 0 && <span> · <strong style={{ color:"var(--gold)" }}>Best {bestN} events count</strong></span>}
          </div>
          <button onClick={()=>setTab("settings")} style={{ fontSize:11, color:"var(--gold)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>⚙ Configure →</button>
        </div>

        {/* Tiebreaker order chips */}
        <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>Tiebreakers:</span>
          {tbs.map((tb,i) => (
            <span key={tb} style={{ fontSize:10, padding:"2px 8px", background:"rgba(255,255,255,0.05)", border:"1px solid var(--navy-border)", borderRadius:3, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
              {i+1}. {tbLabels[tb]||tb}
            </span>
          ))}
        </div>

        {/* Standings table — desktop */}
        <div className="resp-table">
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:560 }}>
            <thead>
              <tr>
                {["#","Player","Pts","W","L","Cup Diff","Events","Breakdown"].map(h=>(
                  <th key={h} style={{ ...css.th, textAlign:h==="Breakdown"||h==="Player"?"left":"center" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {standings.map((row, i) => {
                const isExpanded = expandedRow === row.player.id;
                const medals = ["var(--gold)","#C0C0C0","#CD7F32"];
                const medalColor = medals[i] || "var(--muted)";
                const isTied = row.tiedWithPrev || row.tiedWithNext;
                const prevRow = standings[i-1];

                // H2H check if tied with previous
                const showH2H = isTied && prevRow && prevRow.pts === row.pts;
                let h2hResult = null;
                if (showH2H) {
                  const { winsA, winsB } = computeH2H(prevRow.player, row.player, tournaments);
                  h2hResult = { winsA, winsB, leadName: winsA > winsB ? prevRow.player.name : winsB > winsA ? row.player.name : null };
                }

                return (
                  <React.Fragment key={row.player.id}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : row.player.id)}
                      style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", cursor:"pointer", background:isExpanded?"rgba(201,168,76,0.04)":"transparent", transition:"background 0.15s" }}
                    >
                      {/* Rank */}
                      <td style={{ ...css.td, textAlign:"center", width:40 }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:medalColor, lineHeight:1 }}>{row.rank}</div>
                        {isTied && <div style={{ fontSize:8, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>TIE</div>}
                      </td>
                      {/* Player */}
                      <td style={{ ...css.td }}>
                        <div style={{ fontWeight:600, fontSize:13 }}>{row.player.name}</div>
                        {row.player.nick && <div style={{ fontSize:10, color:"var(--gold)", fontStyle:"italic" }}>"{row.player.nick}"</div>}
                      </td>
                      {/* Pts */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:medalColor, lineHeight:1 }}>{row.pts}</div>
                        {bestN > 0 && row.droppedEvents > 0 && (
                          <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{row.droppedEvents} dropped</div>
                        )}
                      </td>
                      {/* W / L */}
                      <td style={{ ...css.td, textAlign:"center", color:"var(--green)", fontWeight:700 }}>{row.wins}</td>
                      <td style={{ ...css.td, textAlign:"center", color:"var(--muted)" }}>{row.losses}</td>
                      {/* Cup diff */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <span style={{ color:row.cd>0?"var(--green)":row.cd<0?"var(--red)":"var(--muted)", fontWeight:600, fontSize:13 }}>
                          {row.cd > 0 ? "+" : ""}{row.cd}
                        </span>
                      </td>
                      {/* Events */}
                      <td style={{ ...css.td, textAlign:"center", fontSize:12, color:"var(--muted)" }}>
                        {bestN > 0 ? `${row.countedEvents}/${row.events}` : row.events}
                      </td>
                      {/* Breakdown toggle */}
                      <td style={{ ...css.td, textAlign:"left" }}>
                        <button style={{ fontSize:10, color:"var(--muted)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, padding:"2px 6px" }}>
                          {isExpanded ? "▲ Hide" : "▼ Expand"}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded breakdown row */}
                    {isExpanded && (
                      <tr style={{ background:"rgba(255,255,255,0.02)" }}>
                        <td colSpan={8} style={{ padding:"12px 16px" }}>
                          {/* H2H alert if tied */}
                          {showH2H && h2hResult && (
                            <div style={{ marginBottom:10, padding:"8px 12px", background: h2hResult.leadName?"rgba(201,168,76,0.08)":"rgba(91,141,238,0.07)", border:`1px solid ${h2hResult.leadName?"rgba(201,168,76,0.3)":"rgba(91,141,238,0.3)"}`, borderRadius:4, fontSize:12 }}>
                              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--gold)" }}>⚔ H2H with #{prevRow.rank} {prevRow.player.name}:</span>
                              <span style={{ marginLeft:8, color:"var(--white)" }}>{prevRow.player.name} {h2hResult.winsA}–{h2hResult.winsB} {row.player.name}</span>
                              {h2hResult.leadName
                                ? <span style={{ marginLeft:8, color:"var(--green)", fontWeight:700 }}>→ {h2hResult.leadName} leads H2H</span>
                                : <span style={{ marginLeft:8, color:"var(--muted)" }}> — still tied after H2H</span>
                              }
                            </div>
                          )}

                          {/* Tiebreaker button */}
                          {isTied && prevRow && prevRow.pts === row.pts && (
                            <div style={{ marginBottom:8 }}>
                              <button onClick={()=>setTiebreakerModal({pairA:row,pairB:prevRow})}
                                style={{ padding:"4px 12px", background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:4, color:"var(--gold)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer" }}>
                                ⚖ Resolve Tiebreaker with #{prevRow.rank} {prevRow.player?.name}
                              </button>
                            </div>
                          )}

                          {/* Per-event breakdown */}
                          <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>
                            Per-Event Breakdown
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                            {Object.entries(row.eventBreakdown).map(([tid, ev]) => {
                              const t = relevant.find(x=>x.id===tid);
                              return (
                                <div key={tid} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 10px", background:ev.dropped?"rgba(255,255,255,0.01)":"rgba(255,255,255,0.03)", border:`1px solid ${ev.dropped?"rgba(255,255,255,0.04)":"var(--navy-border)"}`, borderRadius:4, opacity:ev.dropped?0.45:1 }}>
                                  {ev.dropped && <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, background:"rgba(255,255,255,0.05)", padding:"1px 5px", borderRadius:2 }}>DROPPED</span>}
                                  <span style={{ fontSize:12, fontWeight:600, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t?.name||tid}</span>
                                  {ev.placement && <Badge color={ev.placement===1?"gold":ev.placement===2?"":""}>#{ev.placement}</Badge>}
                                  <span style={{ fontSize:11, color:"var(--muted)" }}>{ev.wins}W–{ev.losses}L</span>
                                  <span style={{ fontSize:11, color:ev.cd>0?"var(--green)":"var(--muted)" }}>{ev.cd>0?"+":""}{ev.cd} cups</span>
                                  <span style={{ fontSize:10, color:"var(--muted)" }}>{ev.matchPts} match</span>
                                  {ev.placePts > 0 && <span style={{ fontSize:10, color:"var(--gold)" }}>+{ev.placePts} place</span>}
                                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:ev.dropped?"var(--muted)":"var(--gold)", minWidth:36, textAlign:"right" }}>{ev.total}</span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── PLAYOFF SEEDING TAB ───────────────────────────────────────────────────
  function renderPlayoff() {
    if (!season) return <div style={{ padding:40, textAlign:"center", color:"var(--muted)" }}>Select a season</div>;
    if (standings.length === 0) return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🎯</div>
        <div>No standings data — complete at least one ranked event first.</div>
      </div>
    );

    const effectiveQualifiers = seededQualifiers.slice(0, playoffN);
    const effectiveMatchups   = seededMatchups;

    const FORMAT_OPTIONS = [
      { v:"standard",   l:"Standard (1v8, 2v7…)",        desc:"Highest seed plays lowest" },
      { v:"top2bye",    l:"Top 2 Bye (3v6, 4v5…)",        desc:"Seeds 1 & 2 advance directly to semis" },
      { v:"sequential", l:"Sequential (1v2, 3v4…)",       desc:"Adjacent seeds meet in first round" },
    ];

    function createPlayoffTournament() {
      const t = {
        id: uid(),
        name: `${season.name} Playoffs`,
        format: "single_elim",
        type: "pro",
        ranked: true,
        status: "upcoming",
        teamSize: "2v2",
        date: "",
        venue: "",
        maxCups: 10,
        seasonId: season.id,
        playoffSeeds: effectiveQualifiers.map((row, i) => ({
          seed: getEffectiveSeed(row),
          playerId: row.player.id,
          playerName: row.player.name,
          pts: row.pts,
        })),
      };
      setTournaments(prev => [...prev, t]);
      setActiveTournament(t);
      setPage("bracket");
    }

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {/* Config bar */}
        <div style={{ ...css.card, padding:"12px 16px" }}>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr", gap:12, alignItems:"end" }}>
            <Fld label="Qualifiers">
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <input style={{ ...css.input, width:70, textAlign:"center" }} type="number" min="2" max={standings.length} value={playoffN} onChange={e=>setPlayoffN(Math.max(2,Math.min(standings.length,parseInt(e.target.value)||8)))} />
                <span style={{ fontSize:12, color:"var(--muted)" }}>of {standings.length} players</span>
              </div>
            </Fld>
            <Fld label="Bracket Format">
              <select style={css.select} value={playoffFormat} onChange={e=>setPlayoffFormat(e.target.value)}>
                {FORMAT_OPTIONS.map(f=><option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </Fld>
            <div style={{ display:"flex", gap:8, paddingBottom:14 }}>
              <Btn onClick={createPlayoffTournament} style={{ flex:1, justifyContent:"center" }}>
                🎯 Create Playoff Tournament
              </Btn>
            </div>
          </div>
          <div style={{ marginTop:8, fontSize:11, color:"var(--muted)" }}>
            {FORMAT_OPTIONS.find(f=>f.v===playoffFormat)?.desc}
          </div>
        </div>

        {/* Qualifier grid + matchup preview */}
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16, alignItems:"start" }}>
          {/* Qualifier list */}
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase" }}>Qualifiers (Top {playoffN})</span>
              <span style={{ fontSize:11, color:"var(--muted)" }}>drag seeds to override</span>
            </div>
            {effectiveQualifiers.map((row, i) => {
              const seed = getEffectiveSeed(row);
              const medals = ["#C9A84C","#C0C0C0","#CD7F32"];
              const hasSeedOverride = seedOverrides[row.player.id] !== undefined;
              return (
                <div key={row.player.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<effectiveQualifiers.length-1?"1px solid rgba(255,255,255,0.04)":"none", background:hasSeedOverride?"rgba(201,168,76,0.03)":"transparent" }}>
                  {/* Seed # */}
                  <div style={{ width:32, height:32, borderRadius:6, background:`${medals[i]||"rgba(255,255,255,0.08)"}22`, border:`1px solid ${medals[i]||"rgba(255,255,255,0.1)"}55`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:medals[i]||"var(--muted)" }}>{seed}</span>
                  </div>
                  {/* Player info */}
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{row.player.name}</div>
                    <div style={{ fontSize:10, color:"var(--muted)" }}>{row.pts} pts · {row.wins}W–{row.losses}L</div>
                  </div>
                  {/* Standings rank badge */}
                  <Badge color={row.rank<=3?"gold":""}># {row.rank}</Badge>
                  {/* Seed override input */}
                  <input
                    type="number" min="1" max={playoffN}
                    value={seedOverrides[row.player.id] ?? ""}
                    onChange={e => {
                      const v = parseInt(e.target.value);
                      if (!e.target.value) {
                        setSeedOverrides(prev => { const n={...prev}; delete n[row.player.id]; return n; });
                      } else if (v >= 1 && v <= playoffN) {
                        setSeedOverrides(prev => ({ ...prev, [row.player.id]: v }));
                      }
                    }}
                    placeholder={String(row.rank)}
                    style={{ ...css.input, width:48, textAlign:"center", fontSize:12, padding:"4px 4px" }}
                    title="Override seed"
                  />
                </div>
              );
            })}
            {Object.keys(seedOverrides).length > 0 && (
              <div style={{ padding:"8px 14px", background:"rgba(201,168,76,0.04)", borderTop:"1px solid var(--navy-border)" }}>
                <Btn sz="sm" v="ghost" onClick={()=>setSeedOverrides({})}>✕ Clear all overrides</Btn>
              </div>
            )}
          </div>

          {/* Matchup preview */}
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, textTransform:"uppercase" }}>First Round Matchups</span>
            </div>
            {effectiveMatchups.length === 0 ? (
              <div style={{ padding:"24px 16px", textAlign:"center", color:"var(--muted)", fontSize:12 }}>Not enough qualifiers for matchups</div>
            ) : (
              effectiveMatchups.map(([iA, iB], mi) => {
                const a = effectiveQualifiers[iA];
                const b = effectiveQualifiers[iB];
                if (!a || !b) return null;
                const seedA = getEffectiveSeed(a);
                const seedB = getEffectiveSeed(b);
                const { winsA, winsB } = computeH2H(a.player, b.player, tournaments);
                const h2hStr = (winsA + winsB) > 0 ? `H2H: ${a.player.name.split(" ")[0]} ${winsA}–${winsB}` : "No H2H data";
                return (
                  <div key={mi} style={{ padding:"12px 16px", borderBottom:mi<effectiveMatchups.length-1?"1px solid rgba(255,255,255,0.05)":"none" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                      <span style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase" }}>Match {mi+1}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      {/* Team A */}
                      <div style={{ flex:1, textAlign:"right" }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{a.player.name}</div>
                        <div style={{ fontSize:10, color:"var(--muted)" }}>{a.pts} pts</div>
                      </div>
                      {/* Seed badges */}
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, flexShrink:0 }}>
                        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                          <div style={{ background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:4, padding:"2px 8px", fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:"var(--gold)" }}>#{seedA}</div>
                          <span style={{ fontSize:10, color:"var(--muted)" }}>vs</span>
                          <div style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:4, padding:"2px 8px", fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:"var(--muted)" }}>#{seedB}</div>
                        </div>
                        <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{h2hStr}</div>
                      </div>
                      {/* Team B */}
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{b.player.name}</div>
                        <div style={{ fontSize:10, color:"var(--muted)" }}>{b.pts} pts</div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {/* Byes if top2bye */}
            {playoffFormat === "top2bye" && effectiveQualifiers.length >= 2 && (
              <div style={{ padding:"10px 16px", background:"rgba(201,168,76,0.04)", borderTop:"1px solid var(--navy-border)" }}>
                <div style={{ fontSize:11, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                  🎟 Byes: #{getEffectiveSeed(effectiveQualifiers[0])} {effectiveQualifiers[0]?.player.name} & #{getEffectiveSeed(effectiveQualifiers[1])} {effectiveQualifiers[1]?.player.name} advance directly
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Standings points gap chart */}
        {standings.length >= 3 && (
          <div style={{ ...css.card, padding:"14px 16px" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:12 }}>Points Gap to Leader</div>
            {standings.slice(0, Math.min(8, standings.length)).map((row, i) => {
              const leader = standings[0];
              const gap    = leader.pts - row.pts;
              const pct    = leader.pts > 0 ? (row.pts / leader.pts) * 100 : 100;
              const qualify = i < playoffN;
              return (
                <div key={row.player.id} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                  <div style={{ width:24, fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:qualify?"var(--gold)":"var(--muted)", textAlign:"right", flexShrink:0 }}>{row.rank}</div>
                  <div style={{ width:120, fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flexShrink:0, color:qualify?"var(--white)":"var(--muted)" }}>{row.player.name}</div>
                  <div style={{ flex:1, height:16, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden", position:"relative" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:qualify?"var(--gold)":"rgba(255,255,255,0.15)", borderRadius:2, transition:"width 0.3s" }} />
                    {i === playoffN - 1 && (
                      <div style={{ position:"absolute", right:0, top:-10, bottom:-10, width:2, background:"rgba(217,79,61,0.6)", pointerEvents:"none" }} title="Playoff cutoff" />
                    )}
                  </div>
                  <div style={{ width:52, textAlign:"right", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:qualify?"var(--gold)":"var(--muted)", flexShrink:0 }}>{row.pts}</div>
                  {gap > 0 && <div style={{ width:52, textAlign:"right", fontSize:11, color:"var(--red)", flexShrink:0 }}>–{gap}</div>}
                </div>
              );
            })}
            {standings.length > 8 && <div style={{ fontSize:11, color:"var(--muted)", marginTop:6 }}>+{standings.length - 8} more players…</div>}
          </div>
        )}
      </div>
    );
  }

  // ── SETTINGS TAB ──────────────────────────────────────────────────────────
  function renderSettings() {
    if (!season) return <div style={{ padding:40, textAlign:"center", color:"var(--muted)" }}>Select a season first</div>;

    const pc  = season.pointsConfig || {};
    const tbs = pc.tiebreakers || ["pts","wins","cd","h2h","events","alpha"];
    const TB_OPTIONS = [
      { v:"pts",    l:"Total Points",      desc:"Primary ranking criterion" },
      { v:"wins",   l:"Total Wins",        desc:"Cumulative wins across all counted events" },
      { v:"cd",     l:"Cup Differential",  desc:"Cumulative cups made − cups allowed" },
      { v:"h2h",    l:"Head-to-Head",      desc:"Direct wins between tied players" },
      { v:"events", l:"Events Played",     desc:"More events = ranked higher when tied" },
      { v:"alpha",  l:"Alphabetical",      desc:"Final tiebreaker — name A→Z" },
    ];

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {/* Season meta */}
        <div style={{ ...css.card, padding:"14px 16px" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:4, height:18, background:"var(--gold)", display:"inline-block", borderRadius:2 }} />Season Info
          </div>
          <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Fld label="Season Name"><input style={css.input} value={season.name} onChange={e=>updateField("name",e.target.value)} /></Fld>
            <Fld label="Year"><input style={css.input} type="number" value={season.year||""} onChange={e=>updateField("year",parseInt(e.target.value))} /></Fld>
            <Fld label="Start Date"><input style={css.input} type="date" value={season.startDate||""} onChange={e=>updateField("startDate",e.target.value)} /></Fld>
            <Fld label="End Date"><input style={css.input} type="date" value={season.endDate||""} onChange={e=>updateField("endDate",e.target.value)} /></Fld>
            <Fld label="Status">
              <select style={css.select} value={season.status||"upcoming"} onChange={e=>updateField("status",e.target.value)}>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </Fld>
          </div>
          <Fld label="Description"><textarea style={{ ...css.input, resize:"vertical", minHeight:48, fontFamily:"inherit" }} value={season.description||""} onChange={e=>updateField("description",e.target.value)} /></Fld>
        </div>

        {/* Points config */}
        <div style={{ ...css.card, padding:"14px 16px" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:4, height:18, background:"var(--green)", display:"inline-block", borderRadius:2 }} />Match Points
          </div>
          <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
            <Fld label="Win pts"><input style={css.input} type="number" step="0.5" value={pc.win??3} onChange={e=>updatePC("win",parseFloat(e.target.value)||0)} /></Fld>
            <Fld label="Loss pts"><input style={css.input} type="number" step="0.5" value={pc.loss??1} onChange={e=>updatePC("loss",parseFloat(e.target.value)||0)} /></Fld>
            <Fld label="Cup diff bonus (×)"><input style={css.input} type="number" step="0.01" value={pc.cupDiffBonus??0.1} onChange={e=>updatePC("cupDiffBonus",parseFloat(e.target.value)||0)} /></Fld>
          </div>

          {/* Best-N rule */}
          <div style={{ padding:"12px 14px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:4, marginBottom:14 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>Best-N Events Rule</div>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, color:"var(--muted)" }}>Count only best</span>
              <input style={{ ...css.input, width:60, textAlign:"center" }} type="number" min="0" max="99" value={pc.bestN||0} onChange={e=>updatePC("bestN",parseInt(e.target.value)||0)} />
              <span style={{ fontSize:12, color:"var(--muted)" }}>events per player (0 = all events count)</span>
            </div>
            {(pc.bestN||0) > 0 && (
              <div style={{ marginTop:6, fontSize:11, color:"var(--gold)" }}>
                Each player's lowest-scoring events will be dropped until only their top {pc.bestN} remain.
              </div>
            )}
          </div>

          {/* Placement points */}
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:1.5, textTransform:"uppercase", marginBottom:10, color:"var(--muted)" }}>Placement Bonus Points</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {[1,2,3,4,5,6].map(place => {
              const medals = { 1:"var(--gold)", 2:"#C0C0C0", 3:"#CD7F32", 4:"var(--muted)", 5:"var(--muted)", 6:"var(--muted)" };
              const emojis = { 1:"🥇", 2:"🥈", 3:"🥉", 4:"4th", 5:"5th", 6:"6th" };
              const val    = (pc.placements||{})[place] ?? (place===1?100:place===2?70:place===3?50:place===4?30:place===5?15:10);
              return (
                <div key={place} style={{ textAlign:"center", minWidth:64 }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:medals[place], marginBottom:5 }}>{emojis[place]}</div>
                  <input style={{ ...css.input, textAlign:"center", width:64, fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:medals[place], padding:"6px 4px" }}
                    type="number" min="0" value={val}
                    onChange={e=>updatePlacementPts(place, e.target.value)} />
                  <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", marginTop:3, letterSpacing:1 }}>PTS</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tiebreaker order */}
        <div style={{ ...css.card, padding:"14px 16px" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", marginBottom:6, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:4, height:18, background:"#5b8dee", display:"inline-block", borderRadius:2 }} />Tiebreaker Order
          </div>
          <div style={{ fontSize:11, color:"var(--muted)", marginBottom:12 }}>Use ↑↓ arrows to reorder. Applied in order when players are tied on points.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {tbs.map((tb, i) => {
              const opt = TB_OPTIONS.find(o=>o.v===tb) || { l:tb, desc:"" };
              return (
                <div key={tb} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:5 }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--muted)", width:24, flexShrink:0 }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13 }}>{opt.l}</div>
                    <div style={{ fontSize:10, color:"var(--muted)", marginTop:1 }}>{opt.desc}</div>
                  </div>
                  <div style={{ display:"flex", gap:4 }}>
                    <button onClick={()=>moveTiebreaker(i,-1)} disabled={i===0} style={{ width:26,height:26,background:"none",border:"1px solid var(--navy-border)",borderRadius:4,color:i===0?"var(--navy-border)":"var(--muted)",cursor:i===0?"default":"pointer",fontSize:12 }}>↑</button>
                    <button onClick={()=>moveTiebreaker(i,+1)} disabled={i===tbs.length-1} style={{ width:26,height:26,background:"none",border:"1px solid var(--navy-border)",borderRadius:4,color:i===tbs.length-1?"var(--navy-border)":"var(--muted)",cursor:i===tbs.length-1?"default":"pointer",fontSize:12 }}>↓</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Danger zone */}
        <div style={{ ...css.card, padding:"14px 16px", borderColor:"rgba(217,79,61,0.3)" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--red)", marginBottom:10 }}>Danger Zone</div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn v="ghost" onClick={()=>{ if(!window.confirm("Archive this season? It will be hidden but not deleted.")) return; setSeasons(prev=>prev.map(s=>s.id===season.id?{...s,status:"archived",archived:true}:s)); }} style={{ color:"var(--muted)" }}>📦 Archive Season</Btn>
            <Btn v="danger" onClick={deleteSeason}>🗑 Delete Season</Btn>
          </div>
          <div style={{ fontSize:11, color:"var(--muted)", marginTop:6 }}>Archive hides the season from active views. Delete permanently removes it.</div>
        </div>
      </div>
    );
  }

  // ── MAIN RENDER ───────────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Seasons</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
            {seasons.length} season{seasons.length!==1?"s":""} · cumulative standings, playoff seeding &amp; tiebreakers
          </div>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ New Season</Btn>
      </div>

      {/* Tab bar */}
      <div className="tab-strip" style={{ marginBottom:18 }}>
        {TABS.map(t => (
          <button key={t.id} className={`tab-btn ${tab===t.id?"tab-active":""}`} onClick={()=>setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab==="overview"  && renderOverview()}
      {tab==="standings" && renderStandings()}
      {tab==="playoff"   && renderPlayoff()}
      {tab==="settings"  && renderSettings()}

      {/* Create Season Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create New Season" width={500}>
        <Fld label="Season Name *">
          <input style={css.input} value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. MLCP 2026 Season" />
        </Fld>
        <Fld label="Description">
          <textarea style={{ ...css.input, resize:"vertical", minHeight:56, fontFamily:"inherit" }} value={form.description} onChange={e=>upd("description",e.target.value)} placeholder="Optional notes…" />
        </Fld>
        <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Fld label="Year"><input style={css.input} type="number" value={form.year} onChange={e=>upd("year",e.target.value)} /></Fld>
          <Fld label="Status">
            <select style={css.select} value={form.status} onChange={e=>upd("status",e.target.value)}>
              <option value="upcoming">Upcoming</option>
              <option value="active">Active</option>
            </select>
          </Fld>
          <Fld label="Start Date"><input style={css.input} type="date" value={form.startDate} onChange={e=>upd("startDate",e.target.value)} /></Fld>
          <Fld label="End Date"><input style={css.input} type="date" value={form.endDate} onChange={e=>upd("endDate",e.target.value)} /></Fld>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
          <Btn v="ghost" onClick={() => setShowCreate(false)}>Cancel</Btn>
          <Btn onClick={createSeason} disabled={!form.name}>Create Season</Btn>
        </div>
      </Modal>

      {/* ── TIEBREAKER WIZARD ─────────────────────────────────────────────── */}
      <Modal open={!!tiebreakerModal} onClose={()=>{setTiebreakerModal(null);setTbStep(0);setTbResolution(null);setTbCoinResult(null);}} title="⚖ Tiebreaker Resolution" width={480}>
        {tiebreakerModal&&(()=>{
          const {pairA, pairB} = tiebreakerModal;

          // H2H matches between the two players across all season tournaments
          const seasonTourneys = (season?.tournamentIds||[]).map(id=>tournaments.find(t=>t.id===id)).filter(Boolean);
          const h2hMatches = seasonTourneys.flatMap(t=>
            getAllMatchesFromTournament(t).filter(m=>
              m.status==="complete"&&m.t1&&m.t2&&
              ((m.t1.name===pairA.player?.name&&m.t2.name===pairB.player?.name)||
               (m.t2.name===pairA.player?.name&&m.t1.name===pairB.player?.name))
            )
          );
          const aWins = h2hMatches.filter(m=>m.winner?.name===pairA.player?.name).length;
          const bWins = h2hMatches.filter(m=>m.winner?.name===pairB.player?.name).length;
          const aName = pairA.player?.name||"Player A";
          const bName = pairB.player?.name||"Player B";
          const aCupDiff = pairA.cupDiff||0;
          const bCupDiff = pairB.cupDiff||0;

          const steps = [
            { id:"h2h",      label:"1. Head-to-Head",  icon:"⚔️"  },
            { id:"cupdiff",  label:"2. Cup Differential", icon:"🥤" },
            { id:"coinflip", label:"3. Coin Flip",      icon:"🪙"  },
          ];

          function resolveWith(winner, method) {
            const note = `${winner} wins tiebreaker vs ${winner===aName?bName:aName} via ${method}`;
            const entry = { pairA:aName, pairB:bName, winner, method, note, ts:new Date().toLocaleString() };
            setTbAuditLog(prev=>[...prev, entry]);
            setTbResolution({ winner, method, note });
          }

          function flipCoin() {
            setTbCoinFlipping(true);
            setTbCoinResult(null);
            setTimeout(()=>{
              const r = Math.random()<0.5?"heads":"tails";
              setTbCoinResult(r);
              setTbCoinFlipping(false);
            }, 1200+Math.random()*600);
          }

          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>

              {/* Players banner */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 32px 1fr",gap:8,alignItems:"center"}}>
                <div style={{padding:"10px 14px",background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:6,textAlign:"center"}}>
                  <div style={{fontWeight:700,fontSize:14}}>{aName}</div>
                  <div style={{fontSize:11,color:"var(--gold)"}}>{pairA.pts} pts</div>
                </div>
                <div style={{textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--muted)"}}>vs</div>
                <div style={{padding:"10px 14px",background:"rgba(91,141,238,0.08)",border:"1px solid rgba(91,141,238,0.2)",borderRadius:6,textAlign:"center"}}>
                  <div style={{fontWeight:700,fontSize:14}}>{bName}</div>
                  <div style={{fontSize:11,color:"#5b8dee"}}>{pairB.pts} pts</div>
                </div>
              </div>

              {/* Step pills */}
              <div style={{display:"flex",gap:4}}>
                {steps.map((s,i)=>(
                  <div key={s.id} onClick={()=>setTbStep(i)} style={{flex:1,padding:"6px 4px",textAlign:"center",borderRadius:4,border:`1px solid ${tbStep===i?"var(--gold)":"var(--navy-border)"}`,background:tbStep===i?"rgba(201,168,76,0.1)":"transparent",cursor:"pointer",fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>
                    {s.icon} {s.label}
                  </div>
                ))}
              </div>

              {/* Step 0: H2H */}
              {tbStep===0&&(
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {h2hMatches.length===0?(
                    <div style={{padding:"20px",textAlign:"center",color:"var(--muted)",fontSize:12}}>No head-to-head matches found in this season.</div>
                  ):(
                    <>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 32px 1fr",gap:8,alignItems:"center"}}>
                        <div style={{textAlign:"center",padding:"10px",background:"rgba(46,204,113,0.07)",borderRadius:5}}>
                          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:36,color:"var(--green)"}}>{aWins}</div>
                          <div style={{fontSize:10,color:"var(--muted)"}}>Wins</div>
                        </div>
                        <div/>
                        <div style={{textAlign:"center",padding:"10px",background:"rgba(91,141,238,0.07)",borderRadius:5}}>
                          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:36,color:"#5b8dee"}}>{bWins}</div>
                          <div style={{fontSize:10,color:"var(--muted)"}}>Wins</div>
                        </div>
                      </div>
                      {h2hMatches.slice(-4).map((m,i)=>(
                        <div key={m.id} style={{display:"flex",gap:8,padding:"6px 12px",background:"rgba(255,255,255,0.03)",borderRadius:4,fontSize:12}}>
                          <span style={{flex:1,fontWeight:m.winner?.name===aName?700:400}}>{m.t1?.name}</span>
                          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--gold)"}}>{m.s1}–{m.s2}</span>
                          <span style={{flex:1,textAlign:"right",fontWeight:m.winner?.name===bName?700:400}}>{m.t2?.name}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:4}}>
                    {aWins!==bWins?(
                      <Btn onClick={()=>resolveWith(aWins>bWins?aName:bName,"Head-to-Head")}>
                        ✓ {aWins>bWins?aName:bName} wins by H2H
                      </Btn>
                    ):(
                      <Btn v="ghost" onClick={()=>setTbStep(1)}>Tied — next: Cup Diff →</Btn>
                    )}
                  </div>
                </div>
              )}

              {/* Step 1: Cup Diff */}
              {tbStep===1&&(
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 32px 1fr",gap:8,alignItems:"center"}}>
                    <div style={{textAlign:"center",padding:"12px",background:"rgba(46,204,113,0.07)",borderRadius:5}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:40,color:aCupDiff>=bCupDiff?"var(--green)":"var(--muted)"}}>{aCupDiff>0?"+":""}{aCupDiff}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>Cup Diff</div>
                    </div>
                    <div/>
                    <div style={{textAlign:"center",padding:"12px",background:"rgba(91,141,238,0.07)",borderRadius:5}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:40,color:bCupDiff>aCupDiff?"#5b8dee":"var(--muted)"}}>{bCupDiff>0?"+":""}{bCupDiff}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>Cup Diff</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    {aCupDiff!==bCupDiff?(
                      <Btn onClick={()=>resolveWith(aCupDiff>bCupDiff?aName:bName,"Cup Differential")}>
                        ✓ {aCupDiff>bCupDiff?aName:bName} wins by Cup Diff
                      </Btn>
                    ):(
                      <Btn v="ghost" onClick={()=>setTbStep(2)}>Still tied — coin flip →</Btn>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Coin Flip */}
              {tbStep===2&&(
                <div style={{display:"flex",flexDirection:"column",gap:12,alignItems:"center",padding:"8px 0"}}>
                  <div style={{fontSize:60,filter:tbCoinFlipping?"blur(2px)":"none",transition:"filter 0.1s",animation:tbCoinFlipping?"spin 0.3s linear infinite":undefined}}>{tbCoinResult==="heads"?"🌟":tbCoinResult==="tails"?"⚡":"🪙"}</div>
                  {tbCoinResult&&(
                    <div style={{textAlign:"center"}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"var(--gold)",letterSpacing:2,textTransform:"uppercase"}}>{tbCoinResult}!</div>
                      <div style={{fontSize:13,color:"var(--muted)",marginTop:4}}>
                        {tbCoinResult==="heads"?aName:bName} wins the coin flip
                      </div>
                    </div>
                  )}
                  {!tbCoinResult&&<div style={{fontSize:13,color:"var(--muted)"}}>Last resort — flip a coin to decide the tiebreaker</div>}
                  <div style={{display:"flex",gap:8}}>
                    <Btn onClick={flipCoin} disabled={tbCoinFlipping}>{tbCoinFlipping?"Flipping…":"🪙 Flip Coin"}</Btn>
                    {tbCoinResult&&<Btn onClick={()=>resolveWith(tbCoinResult==="heads"?aName:bName,"Coin Flip")}>✓ Confirm Result</Btn>}
                  </div>
                </div>
              )}

              {/* Resolution banner */}
              {tbResolution&&(
                <div style={{padding:"12px 14px",background:"rgba(46,204,113,0.08)",border:"1px solid rgba(46,204,113,0.3)",borderRadius:6}}>
                  <div style={{fontWeight:700,color:"var(--green)",marginBottom:4}}>✓ Tiebreaker Resolved</div>
                  <div style={{fontSize:12,color:"var(--muted)"}}>{tbResolution.note}</div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Tiebreaker audit log (small floating count indicator) */}
      {tbAuditLog.length>0&&(
        <details style={{marginTop:16}}>
          <summary style={{cursor:"pointer",fontSize:12,color:"var(--muted)",padding:"4px 0"}}>⚖ Tiebreaker Log ({tbAuditLog.length})</summary>
          <div style={{...css.card,padding:0,overflow:"hidden",marginTop:6}}>
            {tbAuditLog.map((e,i)=>(
              <div key={i} style={{padding:"7px 14px",borderBottom:i<tbAuditLog.length-1?"1px solid rgba(255,255,255,0.04)":"none",fontSize:12}}>
                <span style={{fontWeight:700,color:"var(--green)"}}>{e.winner}</span>
                <span style={{color:"var(--muted)"}}> beat {e.winner===e.pairA?e.pairB:e.pairA} via {e.method}</span>
                <span style={{float:"right",fontSize:10,color:"rgba(255,255,255,0.2)"}}>{e.ts}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}



// ── VENUES ───────────────────────────────────────────────────────────────────

// Table health issue types
const HEALTH_ISSUES = [
  { id:"wobble",   label:"Wobbly Table",       icon:"⚠️",  severity:"warn"     },
  { id:"cups",     label:"Broken Cup Holders",  icon:"🥤",  severity:"warn"     },
  { id:"surface",  label:"Damaged Surface",     icon:"🔴",  severity:"critical" },
  { id:"lighting", label:"Poor Lighting",       icon:"💡",  severity:"warn"     },
  { id:"water",    label:"Water Damage",         icon:"💧",  severity:"critical" },
  { id:"cleaning", label:"Needs Cleaning",       icon:"🧹",  severity:"info"     },
  { id:"marking",  label:"Faded Markings",       icon:"📏",  severity:"info"     },
  { id:"other",    label:"Other Issue",          icon:"❓",  severity:"warn"     },
];

const TABLE_STATUS_CYCLE = ["idle","active","paused","blocked"];
const TABLE_STATUS_COLOR  = { idle:"var(--navy-border)", active:"var(--green)", paused:"var(--gold)", blocked:"var(--red)" };
const TABLE_STATUS_LABEL  = { idle:"Idle", active:"Active", paused:"Paused", blocked:"Blocked" };

function VenuesPage({ venues, setVenues, tournaments }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [tab, setTab]           = useState("overview");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showCreate, setShowCreate]       = useState(false);
  const [editId, setEditId]               = useState(null);
  const [search, setSearch]               = useState("");
  const [form, setForm] = useState({ name:"", city:"", address:"", capacity:"", tables:"", contact:"", phone:"", notes:"", status:"active" });
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  // Floor map state
  const [mapVenueId, setMapVenueId]         = useState(null);
  const [mapTables, setMapTables]           = useState({});
  const [mapEditMode, setMapEditMode]       = useState(false);
  const [selectedMapTable, setSelectedMapTable] = useState(null);
  const [dragState, setDragState]           = useState(null);  // { tableId, startX, startY, origX, origY }
  const mapRef = React.useRef(null);

  // Assignment queue state
  const [assignQueue, setAssignQueue] = useState([
    { id:"aq1", match:"Match W1", round:"Winners R1", teams:"Team Alpha vs Team Beta",    priority:1, waitMins:8  },
    { id:"aq2", match:"Match W2", round:"Winners R1", teams:"Dragon Boys vs Cup Kings",   priority:2, waitMins:5  },
    { id:"aq3", match:"Match L1", round:"Losers R1",  teams:"Pool Sharks vs Splash Zone", priority:3, waitMins:12 },
    { id:"aq4", match:"Match W3", round:"Winners R2", teams:"TBD vs TBD",                 priority:4, waitMins:0  },
  ]);
  const [tableAssignments, setTableAssignments] = useState({}); // tableId → { matchId, matchLabel, assignedAt }
  const [assignToast, setAssignToast]   = useState(null);
  const [showAssignPicker, setShowAssignPicker] = useState(null); // tableId

  // Table health state
  const [healthVenueId, setHealthVenueId] = useState(null);
  const [reportModal, setReportModal]     = useState(null);
  const [reportForm, setReportForm]       = useState({ type:"wobble", notes:"" });
  const [outOfRotation, setOutOfRotation] = useState({}); // tableId → bool
  const [showMaintenanceLog, setShowMaintenanceLog] = useState(null); // tableId
  const [venueDocMap,  setVenueDocMap]  = useState({}); // {venueId: [{id,name,type,url,notes,date}]}
  const [docForm,      setDocForm]      = useState({name:"",type:"contract",url:"",notes:"",date:""});
  const [showDocForm,  setShowDocForm]  = useState(false);
  const [docVenueId,   setDocVenueId]   = useState(null);

  const filtered = venues.filter(v =>
    !search ||
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.city.toLowerCase().includes(search.toLowerCase())
  );

  // ── Map table helpers ──────────────────────────────────────────────────────
  function getMapTables(venueId) {
    if (mapTables[venueId]) return mapTables[venueId];
    const v = venues.find(x=>x.id===venueId);
    if (!v) return [];
    const n = parseInt(v.tables)||0;
    const cols = Math.max(2, Math.ceil(Math.sqrt(n)));
    const CELL = 110, GAP = 14, PAD = 20;
    return Array.from({length:n}, (_,i)=>({
      id: `tbl_${venueId}_${i}`,
      name: `Table ${i+1}`,
      x: (i%cols)*(CELL+GAP) + PAD,
      y: Math.floor(i/cols)*(CELL+GAP) + PAD,
      status: "idle",
      healthIssues: [],
      maintenanceLog: [],
    }));
  }
  function saveMapTables(venueId, tables) {
    setMapTables(prev => ({...prev, [venueId]: tables}));
  }
  function allHealthIssues(venueId) {
    return getMapTables(venueId).flatMap(t=>(t.healthIssues||[]).map(i=>({...i, tableName:t.name, tableId:t.id})));
  }
  function openIssues(venueId) {
    return allHealthIssues(venueId).filter(i=>i.status!=="resolved");
  }
  function tableHealthScore(venueId) {
    const tbls = getMapTables(venueId);
    if (!tbls.length) return 100;
    const ok = tbls.filter(t=>(t.healthIssues||[]).filter(i=>i.status!=="resolved").length===0).length;
    return Math.round((ok/tbls.length)*100);
  }

  // ── Assignment helpers ─────────────────────────────────────────────────────
  function assignMatchToTable(tableId, queueItem) {
    setTableAssignments(prev=>({...prev,[tableId]:{ matchId:queueItem.id, matchLabel:queueItem.match, teams:queueItem.teams, assignedAt:new Date().toLocaleTimeString() }}));
    setAssignQueue(prev=>prev.filter(q=>q.id!==queueItem.id));
    // Mark table as active
    const curVenue = venues.find(v=>v.id===mapVenueId)||venues[0];
    if (curVenue) {
      const tbls = getMapTables(curVenue.id);
      saveMapTables(curVenue.id, tbls.map(t=>t.id===tableId?{...t,status:"active"}:t));
    }
    setAssignToast(`${queueItem.match} → ${getMapTables(mapVenueId||venues[0]?.id||"").find(t=>t.id===tableId)?.name||"Table"}`);
    setTimeout(()=>setAssignToast(null), 2800);
    setShowAssignPicker(null);
  }
  function freeTable(tableId) {
    setTableAssignments(prev=>{const n={...prev};delete n[tableId];return n;});
    const curVenue = venues.find(v=>v.id===mapVenueId)||venues[0];
    if (curVenue) {
      const tbls = getMapTables(curVenue.id);
      saveMapTables(curVenue.id, tbls.map(t=>t.id===tableId?{...t,status:"idle"}:t));
    }
  }
  function autoAssignAll() {
    const curVenue = venues.find(v=>v.id===mapVenueId)||venues[0];
    if (!curVenue) return;
    const tbls = getMapTables(curVenue.id);
    const freeTables = tbls.filter(t=>t.status==="idle"&&!tableAssignments[t.id]&&!outOfRotation[t.id]);
    const pending = [...assignQueue];
    const newAssign = {...tableAssignments};
    const newTbls = [...tbls];
    let assigned = 0;
    freeTables.forEach(t=>{
      if (pending.length===0) return;
      const q = pending.shift();
      newAssign[t.id] = { matchId:q.id, matchLabel:q.match, teams:q.teams, assignedAt:new Date().toLocaleTimeString() };
      const idx = newTbls.findIndex(x=>x.id===t.id);
      if (idx!==-1) newTbls[idx] = {...newTbls[idx], status:"active"};
      assigned++;
    });
    setTableAssignments(newAssign);
    setAssignQueue(pending);
    saveMapTables(curVenue.id, newTbls);
    if (assigned>0) {
      setAssignToast(`Auto-assigned ${assigned} match${assigned>1?"es":""}`);
      setTimeout(()=>setAssignToast(null), 2800);
    }
  }

  // ── Drag-and-drop (mouse + touch) ─────────────────────────────────────────
  function onTableDragStart(e, tableId) {
    if (!mapEditMode) return;
    e.preventDefault();
    const isTouch = e.type === "touchstart";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const curVenue = venues.find(v=>v.id===mapVenueId)||venues[0];
    if (!curVenue) return;
    const tbls = getMapTables(curVenue.id);
    const t = tbls.find(x=>x.id===tableId);
    if (!t) return;
    setDragState({ tableId, startX:clientX, startY:clientY, origX:t.x, origY:t.y, venueId:curVenue.id });
  }
  function onMapDragMove(e) {
    if (!dragState) return;
    e.preventDefault();
    const isTouch = e.type === "touchmove";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragState.startX;
    const dy = clientY - dragState.startY;
    const newX = Math.max(4, dragState.origX + dx);
    const newY = Math.max(4, dragState.origY + dy);
    const tbls = getMapTables(dragState.venueId);
    saveMapTables(dragState.venueId, tbls.map(t=>t.id===dragState.tableId?{...t,x:newX,y:newY}:t));
  }
  function onMapDragEnd() {
    setDragState(null);
  }

  // ── Venue CRUD ─────────────────────────────────────────────────────────────
  function openCreate() { setForm({ name:"", city:"", address:"", capacity:"", tables:"", contact:"", phone:"", notes:"", status:"active" }); setEditId(null); setShowCreate(true); }
  function openEdit(v)  { setForm({ name:v.name, city:v.city, address:v.address||"", capacity:v.capacity||"", tables:v.tables||"", contact:v.contact||"", phone:v.phone||"", notes:v.notes||"", status:v.status||"active" }); setEditId(v.id); setShowCreate(true); }
  function saveVenue() {
    if (!form.name || !form.city) return;
    if (editId) { setVenues(prev=>prev.map(v=>v.id===editId?{...v,...form,capacity:parseInt(form.capacity)||0,tables:parseInt(form.tables)||0}:v)); }
    else        { setVenues(prev=>[...prev,{ id:uid(),...form,capacity:parseInt(form.capacity)||0,tables:parseInt(form.tables)||0 }]); }
    setShowCreate(false); setEditId(null);
  }
  function deleteVenue(id) { setVenues(prev=>prev.filter(v=>v.id!==id)); if (selectedVenue?.id===id) setSelectedVenue(null); }
  function venueEvents(venueId) {
    const v = venues.find(x=>x.id===venueId);
    if (!v) return [];
    return tournaments.filter(t=>t.venue&&(t.venue===v.name||t.venue.includes(v.name)||t.venueId===venueId));
  }

  // ── Health helpers ─────────────────────────────────────────────────────────
  function reportIssue(venueId, tableId) {
    const tbls = getMapTables(venueId);
    const issType = HEALTH_ISSUES.find(h=>h.id===reportForm.type)||HEALTH_ISSUES[0];
    const issue = { id:uid(), type:reportForm.type, label:issType.label, icon:issType.icon, severity:issType.severity, notes:reportForm.notes, reportedAt:new Date().toLocaleString(), status:"open" };
    saveMapTables(venueId, tbls.map(t=>t.id===tableId?{...t,healthIssues:[...(t.healthIssues||[]),issue],maintenanceLog:[...(t.maintenanceLog||[]),{...issue,logType:"issue_reported"}]}:t));
    setReportModal(null); setReportForm({ type:"wobble", notes:"" });
  }
  function resolveIssue(venueId, tableId, issueId) {
    const tbls = getMapTables(venueId);
    const now = new Date().toLocaleString();
    saveMapTables(venueId, tbls.map(t=>t.id===tableId?{...t,healthIssues:(t.healthIssues||[]).map(i=>i.id===issueId?{...i,status:"resolved",resolvedAt:now}:i),maintenanceLog:[...(t.maintenanceLog||[]),{id:uid(),logType:"issue_resolved",label:`Resolved: ${(t.healthIssues||[]).find(i=>i.id===issueId)?.label||"Issue"}`,reportedAt:now}]}:t));
  }
  function bulkResolveAll(venueId) {
    const tbls = getMapTables(venueId);
    const now = new Date().toLocaleString();
    saveMapTables(venueId, tbls.map(t=>({...t,healthIssues:(t.healthIssues||[]).map(i=>i.status==="resolved"?i:{...i,status:"resolved",resolvedAt:now})})));
  }

  const statusBadgeColor = { active:"green", inactive:"red", pending:"" };
  const TABS = [
    { id:"overview",     label:"Overview",    icon:"📋" },
    { id:"floormap",     label:"Floor Map",   icon:"🗺️" },
    { id:"assign",       label:"Queue",       icon:"🎯" },
    { id:"calendar",     label:"Calendar",    icon:"📅" },
    { id:"docs",         label:"Documents",   icon:"📄" },
    { id:"health",       label:"Health",      icon:"🔧" },
    { id:"history",      label:"History",     icon:"📅" },
    { id:"utilization",  label:"Utilization", icon:"📊" },
  ];

  // ── OVERVIEW TAB ──────────────────────────────────────────────────────────
  function renderOverview() {
    const totalTables   = venues.reduce((s,v)=>s+(v.tables||0),0);
    const totalCapacity = venues.reduce((s,v)=>s+(v.capacity||0),0);
    const activeVenues  = venues.filter(v=>v.status==="active");
    const allOpenIssues = venues.reduce((s,v)=>s+openIssues(v.id).length,0);
    return (
      <>
        {/* Stats strip */}
        <div className="resp-grid-4" style={{ gap:12, marginBottom:18 }}>
          {[
            ["Venues", venues.length, "var(--gold)"],
            ["Active", activeVenues.length, "var(--green)"],
            ["Tables", totalTables, "var(--white)"],
            ["Capacity", totalCapacity||"—", "var(--muted)"],
          ].map(([l,val,c])=>(
            <div key={l} style={{ ...css.card, padding:"14px 16px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:c, lineHeight:1 }}>{val}</div>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:3 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Venue grid */}
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, color:"var(--white)", marginBottom:8 }}>No Venues Yet</div>
            <div style={{ fontSize:13, marginBottom:16 }}>{search ? "Try a different search" : "Add your first venue to get started"}</div>
            {!search && <Btn onClick={openCreate}>+ Add Venue</Btn>}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:14 }}>
            {filtered.map(v => {
              const evts      = venueEvents(v.id);
              const issues    = openIssues(v.id);
              const critCount = issues.filter(i=>i.severity==="critical").length;
              const warnCount = issues.filter(i=>i.severity!=="critical").length;
              const score     = tableHealthScore(v.id);
              const tbls      = getMapTables(v.id);
              const activeTbls = tbls.filter(t=>t.status==="active").length;
              return (
                <div key={v.id} style={{ ...css.card, padding:0, overflow:"hidden", cursor:"pointer", border:`1px solid ${critCount>0?"rgba(217,79,61,0.4)":"var(--navy-border)"}`, transition:"border-color 0.15s" }}
                  onClick={()=>setSelectedVenue(selectedVenue?.id===v.id?null:v)}>
                  {/* Card header */}
                  <div style={{ padding:"14px 16px 10px", borderBottom:"1px solid var(--navy-border)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:2, lineHeight:1.2 }}>{v.name}</div>
                      <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                        {critCount > 0 && <Badge color="red">🔴 {critCount}</Badge>}
                        {warnCount > 0 && <Badge color="">⚠️ {warnCount}</Badge>}
                        <Badge color={statusBadgeColor[v.status]||""}>{v.status}</Badge>
                      </div>
                    </div>
                    <div style={{ fontSize:12, color:"var(--gold-light)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>📍 {v.city}</div>
                    {v.address && <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{v.address}</div>}
                  </div>

                  {/* Stats row */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", borderBottom:"1px solid var(--navy-border)" }}>
                    {[
                      [v.tables||"—", "Tables"],
                      [v.capacity||"—", "Cap."],
                      [evts.length, "Events"],
                      [activeTbls > 0 ? `${activeTbls}/${v.tables}` : "—", "Live"],
                    ].map(([val,lbl])=>(
                      <div key={lbl} style={{ padding:"8px 10px", textAlign:"center", borderRight:"1px solid var(--navy-border)" }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold-light)", lineHeight:1 }}>{val}</div>
                        <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>{lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Capacity warning */}
                  {(() => {
                    const cap = parseInt(v.capacity) || 0;
                    if (!cap) return null;
                    const regCount = (tournaments||[])
                      .filter(t=>t.venueId===v.id&&(t.status==="upcoming"||t.status==="active"))
                      .reduce((s,t)=>s+(t.registrations||[]).filter(r=>r.status!=="no_show"&&r.status!=="cancelled").length * ((t.teamSize)||2),0);
                    const pct = Math.round(regCount/cap*100);
                    if (pct < 70) return null;
                    const overCap = regCount > cap;
                    return (
                      <div style={{ padding:"6px 12px", background:overCap?"rgba(217,79,61,0.1)":"rgba(201,168,76,0.07)", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{ fontSize:13 }}>{overCap?"🚨":"⚠️"}</span>
                        <div style={{ flex:1 }}>
                          <span style={{ fontSize:11, color:overCap?"var(--red)":"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                            {overCap ? `OVER CAPACITY: ${regCount}/${cap} registered` : `Near capacity: ${pct}% (${regCount}/${cap})`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Health bar */}
                  {v.tables > 0 && (
                    <div style={{ padding:"6px 16px 4px", borderBottom:"1px solid var(--navy-border)" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                        <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>Table Health</span>
                        <span style={{ fontSize:11, color:score>=80?"var(--green)":score>=50?"var(--gold)":"var(--red)", fontFamily:"'Bebas Neue',sans-serif" }}>{score}%</span>
                      </div>
                      <div style={{ height:3, background:"var(--navy-border)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${score}%`, background:score>=80?"var(--green)":score>=50?"var(--gold)":"var(--red)", borderRadius:2, transition:"width 0.5s" }} />
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontSize:11, color:"var(--muted)", minWidth:0 }}>
                      {v.contact && <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>✉️ {v.contact}</div>}
                      {v.phone   && <div>📞 {v.phone}</div>}
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <Btn sz="sm" v="ghost" onClick={e=>{e.stopPropagation();setMapVenueId(v.id);setTab("floormap");}}>🗺️</Btn>
                      <Btn sz="sm" v="ghost" onClick={e=>{e.stopPropagation();openEdit(v);}}>Edit</Btn>
                      <Btn sz="sm" v="danger" onClick={e=>{e.stopPropagation();deleteVenue(v.id);}}>✕</Btn>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Venue detail panel */}
        {selectedVenue && (
          <div style={{ ...css.card, marginTop:20, padding:0, overflow:"hidden", border:"1px solid rgba(201,168,76,0.25)" }}>
            <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(201,168,76,0.04)" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:3, color:"var(--gold)" }}>{selectedVenue.name}</div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn sz="sm" v="ghost" onClick={()=>{setMapVenueId(selectedVenue.id);setTab("floormap");}}>🗺️ Map</Btn>
                <Btn sz="sm" v="ghost" onClick={()=>{setHealthVenueId(selectedVenue.id);setTab("health");}}>🔧 Health</Btn>
                <button onClick={()=>setSelectedVenue(null)} style={{ background:"none", border:"none", color:"var(--muted)", fontSize:18, cursor:"pointer", padding:"0 4px", minHeight:32 }}>✕</button>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:0 }}>
              <div style={{ padding:"16px 20px", borderRight:isMobile?"none":"1px solid var(--navy-border)" }}>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Venue Info</div>
                {[["Address",selectedVenue.address||"—"],["City",selectedVenue.city],["Tables",selectedVenue.tables||"—"],["Capacity",selectedVenue.capacity||"—"],["Contact",selectedVenue.contact||"—"],["Status",selectedVenue.status]].map(([l,v])=>(
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ color:"var(--muted)", fontSize:13 }}>{l}</span>
                    <span style={{ fontWeight:600, fontSize:13 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding:"16px 20px" }}>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Tournament History — {venueEvents(selectedVenue.id).length} events</div>
                {venueEvents(selectedVenue.id).length === 0 ? (
                  <div style={{ color:"var(--muted)", fontSize:13, fontStyle:"italic" }}>No tournaments hosted at this venue</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {venueEvents(selectedVenue.id).map(t=>(
                      <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <div>
                          <div style={{ fontWeight:600, fontSize:13 }}>{t.name}</div>
                          <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>{t.date||"TBD"} · {FORMATS.find(f=>f.v===t.format)?.l||t.format}</div>
                        </div>
                        <Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── FLOOR MAP TAB ─────────────────────────────────────────────────────────
  function renderFloorMap() {
    const curVenue = venues.find(v=>v.id===mapVenueId) || venues[0];
    if (!curVenue) return <div style={{ color:"var(--muted)", padding:40, textAlign:"center" }}>No venues yet — add one in Overview</div>;
    const tbls   = getMapTables(curVenue.id);
    const issues = openIssues(curVenue.id);
    const mapW = tbls.reduce((m,t)=>Math.max(m,t.x+120),400);
    const mapH = tbls.reduce((m,t)=>Math.max(m,t.y+120),280);

    return (
      <div>
        {/* Controls */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:14, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <select style={{ ...css.select, minWidth:180 }} value={curVenue.id} onChange={e=>{setMapVenueId(e.target.value);setSelectedMapTable(null);}}>
              {venues.map(v=><option key={v.id} value={v.id}>{v.name} — {v.city}</option>)}
            </select>
            <Badge color={statusBadgeColor[curVenue.status]||""}>{curVenue.status}</Badge>
            {issues.length > 0 && <div style={{ fontSize:12, color:"var(--gold)", display:"flex", alignItems:"center", gap:5 }}>⚠️ {issues.length} open issue{issues.length!==1?"s":""}</div>}
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <Btn sz="sm" v={mapEditMode?"primary":"ghost"} onClick={()=>setMapEditMode(e=>!e)}>
              {mapEditMode ? "✓ Done" : "✏️ Edit Layout"}
            </Btn>
            <Btn sz="sm" v="ghost" onClick={()=>{setHealthVenueId(curVenue.id);setTab("health");}}>🔧 Health</Btn>
            <Btn sz="sm" v="ghost" onClick={()=>setTab("assign")}>🎯 Queue</Btn>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display:"flex", gap:14, marginBottom:12, flexWrap:"wrap" }}>
          {[["Idle","var(--navy-border)"],["Active","var(--green)"],["Paused","var(--gold)"],["Blocked","var(--red)"]].map(([l,c])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}>
              <div style={{ width:10, height:10, borderRadius:2, background:c }} />{l}
            </div>
          ))}
          <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}><span>🔴</span> Critical</div>
          <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}><span>⚠️</span> Warning</div>
          <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"var(--muted)" }}><span>🎯</span> Assigned</div>
          {mapEditMode && <div style={{ fontSize:11, color:"var(--gold)", fontStyle:"italic" }}>Drag tables to reposition · Click to change status</div>}
        </div>

        {/* Floor map canvas */}
        <div
          ref={mapRef}
          onMouseMove={dragState?onMapDragMove:undefined}
          onMouseUp={dragState?onMapDragEnd:undefined}
          onMouseLeave={dragState?onMapDragEnd:undefined}
          onTouchMove={dragState?onMapDragMove:undefined}
          onTouchEnd={dragState?onMapDragEnd:undefined}
          style={{ ...css.card, padding:0, overflowX:"auto", overflowY:"auto", maxHeight:480, position:"relative", cursor:dragState?"grabbing":"default" }}
        >
          {tbls.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--muted)" }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🏓</div>
              <div style={{ fontSize:14 }}>No tables defined</div>
              <div style={{ fontSize:12, marginTop:4 }}>Set the table count when editing the venue</div>
            </div>
          ) : (
            <div style={{ position:"relative", width:Math.max(mapW+20, 340), height:Math.max(mapH+20, 240), minWidth:"100%" }}>
              {/* Grid background */}
              <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", opacity:0.06 }}>
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--white)" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)"/>
              </svg>

              {/* Tables */}
              {tbls.map(t => {
                const iss    = (t.healthIssues||[]).filter(i=>i.status!=="resolved");
                const crit   = iss.some(i=>i.severity==="critical");
                const warn   = iss.length > 0 && !crit;
                const isOOR  = outOfRotation[t.id];
                const assign = tableAssignments[t.id];
                const bColor = isOOR ? "rgba(106,138,170,0.4)" : TABLE_STATUS_COLOR[t.status];
                const isSelected = selectedMapTable === t.id;
                return (
                  <div
                    key={t.id}
                    onMouseDown={e=>mapEditMode?onTableDragStart(e,t.id):undefined}
                    onTouchStart={e=>mapEditMode?onTableDragStart(e,t.id):undefined}
                    onClick={()=>{ if(dragState) return; if(!mapEditMode){ setSelectedMapTable(isSelected?null:t.id); } else { const s=TABLE_STATUS_CYCLE; const ni=(s.indexOf(t.status)+1)%s.length; saveMapTables(curVenue.id,tbls.map(x=>x.id===t.id?{...x,status:s[ni]}:x)); } }}
                    style={{
                      position:"absolute", left:t.x, top:t.y,
                      width:100, height:100,
                      background: isSelected ? "rgba(201,168,76,0.12)" : isOOR ? "rgba(106,138,170,0.05)" : "var(--navy-light)",
                      border:`2px solid ${isSelected?"var(--gold)":bColor}`,
                      borderRadius:10,
                      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                      cursor: mapEditMode ? "grab" : "pointer",
                      userSelect:"none", WebkitUserSelect:"none",
                      transition:"box-shadow 0.1s, border-color 0.1s",
                      boxShadow: isSelected ? "0 0 0 3px rgba(201,168,76,0.2)" : "none",
                      opacity: isOOR ? 0.5 : 1,
                      gap:2,
                    }}
                  >
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:bColor, lineHeight:1 }}>{t.name.replace("Table ","")}</div>
                    <div style={{ fontSize:8, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:bColor, lineHeight:1 }}>{isOOR?"OUT":TABLE_STATUS_LABEL[t.status]}</div>
                    {assign && <div style={{ fontSize:7, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:0.5, color:"var(--gold)", textAlign:"center", padding:"0 4px", lineHeight:1.2, marginTop:2 }}>🎯{assign.matchLabel}</div>}
                    {crit && <div style={{ position:"absolute", top:3, right:4, fontSize:11 }}>🔴</div>}
                    {warn && !crit && <div style={{ position:"absolute", top:3, right:4, fontSize:11 }}>⚠️</div>}
                    {iss.length > 0 && <div style={{ position:"absolute", top:3, left:4, fontSize:9, background:crit?"var(--red)":"rgba(201,168,76,0.3)", padding:"1px 4px", borderRadius:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{iss.length}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected table panel */}
        {selectedMapTable && (() => {
          const t = tbls.find(x=>x.id===selectedMapTable);
          if (!t) return null;
          const openIss     = (t.healthIssues||[]).filter(i=>i.status!=="resolved");
          const resolvedIss = (t.healthIssues||[]).filter(i=>i.status==="resolved");
          const isOOR       = outOfRotation[t.id];
          const assign      = tableAssignments[t.id];
          return (
            <div style={{ ...css.card, marginTop:14, padding:0, overflow:"hidden", border:"1px solid rgba(201,168,76,0.25)" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(201,168,76,0.04)" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:2, color:"var(--gold)" }}>🏓 {t.name}</div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <Badge color={t.status==="active"?"green":t.status==="blocked"?"red":t.status==="paused"?"":""}>{TABLE_STATUS_LABEL[t.status]}</Badge>
                  {isOOR && <Badge color="red">Out of Rotation</Badge>}
                  <Btn sz="sm" v="ghost" onClick={()=>setReportModal(t.id)}>+ Issue</Btn>
                  <button onClick={()=>setSelectedMapTable(null)} style={{ background:"none", border:"none", color:"var(--muted)", fontSize:18, cursor:"pointer", padding:"0 2px" }}>✕</button>
                </div>
              </div>

              <div style={{ padding:"12px 16px", display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16 }}>
                {/* Left: assignment + controls */}
                <div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Assignment</div>
                  {assign ? (
                    <div style={{ background:"rgba(46,204,113,0.08)", border:"1px solid rgba(46,204,113,0.25)", borderRadius:6, padding:"8px 12px", marginBottom:8 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:"var(--green)" }}>🎯 {assign.matchLabel}</div>
                      <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{assign.teams}</div>
                      <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>Assigned at {assign.assignedAt}</div>
                    </div>
                  ) : (
                    <div style={{ color:"var(--muted)", fontSize:12, marginBottom:8, fontStyle:"italic" }}>No match assigned</div>
                  )}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {assign ? (
                      <Btn sz="sm" v="ghost" onClick={()=>freeTable(t.id)}>✓ Free Table</Btn>
                    ) : (
                      <Btn sz="sm" v="ghost" onClick={()=>setShowAssignPicker(t.id)} disabled={isOOR}>
                        {assignQueue.length>0?"Assign Match":"No Matches Queued"}
                      </Btn>
                    )}
                    <Btn sz="sm" v={isOOR?"danger":"ghost"} onClick={()=>setOutOfRotation(prev=>({...prev,[t.id]:!prev[t.id]}))}>
                      {isOOR?"↩ In Rotation":"⛔ Out of Rotation"}
                    </Btn>
                  </div>
                  {/* Status cycling buttons */}
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Quick Status</div>
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                      {TABLE_STATUS_CYCLE.map(s=>(
                        <button key={s} onClick={()=>saveMapTables(curVenue.id,tbls.map(x=>x.id===t.id?{...x,status:s}:x))}
                          style={{ padding:"4px 10px", border:`1px solid ${t.status===s?"var(--gold)":"var(--navy-border)"}`, background:t.status===s?"rgba(201,168,76,0.15)":"transparent", borderRadius:4, color:t.status===s?"var(--gold)":"var(--muted)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer", minHeight:28 }}>
                          {TABLE_STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: health issues */}
                <div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Health Issues {openIss.length>0&&<span style={{ color:"var(--gold)" }}>({openIss.length} open)</span>}</div>
                  {openIss.length === 0 && resolvedIss.length === 0 ? (
                    <div style={{ color:"var(--muted)", fontSize:12, fontStyle:"italic" }}>✅ No issues reported</div>
                  ) : (
                    <>
                      {openIss.map(iss=>(
                        <div key={iss.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", background:"rgba(255,255,255,0.03)", borderRadius:6, marginBottom:5 }}>
                          <span style={{ fontSize:14 }}>{iss.icon}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:600, fontSize:12 }}>{iss.label}</div>
                            {iss.notes && <div style={{ fontSize:10, color:"var(--muted)", marginTop:1 }}>{iss.notes}</div>}
                          </div>
                          <Btn sz="sm" v="success" onClick={()=>resolveIssue(curVenue.id, t.id, iss.id)}>✓</Btn>
                        </div>
                      ))}
                      {resolvedIss.length > 0 && (
                        <details style={{ marginTop:6 }}>
                          <summary style={{ cursor:"pointer", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>▸ {resolvedIss.length} resolved</summary>
                          <div style={{ marginTop:6 }}>
                            {resolvedIss.map(iss=>(
                              <div key={iss.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 8px", opacity:0.6, fontSize:11 }}>
                                <span>{iss.icon}</span><span>{iss.label}</span><Badge color="green">resolved</Badge>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Report issue modal */}
        <Modal open={!!reportModal} onClose={()=>setReportModal(null)} title="Report Table Issue" width={440}>
          {reportModal && (() => {
            const t = tbls.find(x=>x.id===reportModal);
            return (
              <div>
                <div style={{ marginBottom:14, padding:"8px 12px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.15)", borderRadius:6, fontSize:13 }}>
                  Reporting issue for: <strong>{t?.name}</strong>
                </div>
                <Fld label="Issue Type">
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {HEALTH_ISSUES.map(h=>(
                      <div key={h.id} onClick={()=>setReportForm(f=>({...f, type:h.id}))}
                        style={{ padding:"8px 10px", border:`1px solid ${reportForm.type===h.id?"var(--gold)":"var(--navy-border)"}`, borderRadius:6, cursor:"pointer", display:"flex", alignItems:"center", gap:8, background:reportForm.type===h.id?"rgba(201,168,76,0.08)":"transparent", transition:"all 0.12s" }}>
                        <span>{h.icon}</span>
                        <div>
                          <div style={{ fontWeight:600, fontSize:12, lineHeight:1.2 }}>{h.label}</div>
                          <div style={{ fontSize:10, color:h.severity==="critical"?"var(--red)":"var(--muted)", marginTop:1 }}>{h.severity}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Fld>
                <Fld label="Notes (optional)">
                  <textarea style={{ ...css.input, minHeight:60, resize:"vertical" }} value={reportForm.notes} onChange={e=>setReportForm(f=>({...f,notes:e.target.value}))} placeholder="Describe the issue…" />
                </Fld>
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
                  <Btn v="ghost" onClick={()=>setReportModal(null)}>Cancel</Btn>
                  <Btn onClick={()=>reportIssue(curVenue.id, reportModal)}>Report Issue</Btn>
                </div>
              </div>
            );
          })()}
        </Modal>

        {/* Assign match picker modal */}
        <Modal open={!!showAssignPicker} onClose={()=>setShowAssignPicker(null)} title="Assign Match to Table" width={420}>
          {showAssignPicker && (() => {
            const t = tbls.find(x=>x.id===showAssignPicker);
            return (
              <div>
                <div style={{ marginBottom:12, padding:"8px 12px", background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.2)", borderRadius:6, fontSize:13 }}>
                  Assigning match to: <strong>{t?.name}</strong>
                </div>
                {assignQueue.length === 0 ? (
                  <div style={{ color:"var(--muted)", padding:"20px 0", textAlign:"center" }}>No matches in queue</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {assignQueue.map(q=>(
                      <div key={q.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background:"var(--navy-light)", borderRadius:6, border:"1px solid var(--navy-border)", cursor:"pointer" }}
                        onClick={()=>assignMatchToTable(showAssignPicker, q)}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13 }}>{q.match}</div>
                          <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>{q.round} · {q.teams}</div>
                          {q.waitMins > 0 && <div style={{ fontSize:10, color:q.waitMins>10?"var(--gold)":"var(--muted)", marginTop:1 }}>⏳ Waiting {q.waitMins}m</div>}
                        </div>
                        <Btn sz="sm" v="success">Assign</Btn>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </Modal>
      </div>
    );
  }

  // ── ASSIGNMENT QUEUE TAB ───────────────────────────────────────────────────
  function renderAssignQueue() {
    const curVenue = venues.find(v=>v.id===mapVenueId) || venues[0];
    if (!curVenue) return <div style={{ color:"var(--muted)", padding:40, textAlign:"center" }}>No venues yet</div>;
    const tbls       = getMapTables(curVenue.id);
    const freeTables = tbls.filter(t=>t.status==="idle"&&!tableAssignments[t.id]&&!outOfRotation[t.id]);
    const activeTbls = tbls.filter(t=>tableAssignments[t.id]);
    const blockedTbls= tbls.filter(t=>t.status==="blocked"||outOfRotation[t.id]);

    return (
      <div>
        {/* Venue selector */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:16, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <select style={{ ...css.select, minWidth:180 }} value={curVenue.id} onChange={e=>setMapVenueId(e.target.value)}>
              {venues.map(v=><option key={v.id} value={v.id}>{v.name} — {v.city}</option>)}
            </select>
          </div>
          <Btn v="success" onClick={autoAssignAll} disabled={freeTables.length===0||assignQueue.length===0}>
            ⚡ Auto-Assign All
          </Btn>
        </div>

        {/* Table availability summary */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
          {[
            ["Free Tables", freeTables.length, "var(--green)", "Ready to assign"],
            ["Active Tables", activeTbls.length, "var(--gold)", "Match in progress"],
            ["Blocked", blockedTbls.length, "var(--red)", "Out of play"],
          ].map(([l,v,c,sub])=>(
            <div key={l} style={{ ...css.card, padding:"12px 14px", textAlign:"center", border:`1px solid ${c}22` }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:c, lineHeight:1 }}>{v}</div>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Two-column layout: queue + active tables */}
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16 }}>

          {/* Queue column */}
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span>🎯 Match Queue</span>
              <Badge color="">{assignQueue.length} pending</Badge>
            </div>
            {assignQueue.length === 0 ? (
              <div style={{ ...css.card, textAlign:"center", padding:"32px 20px", color:"var(--muted)" }}>
                <div style={{ fontSize:28, marginBottom:8 }}>✅</div>
                <div style={{ fontSize:13 }}>All matches assigned!</div>
                <div style={{ fontSize:11, marginTop:4 }}>No matches waiting for tables</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {assignQueue.map((q,i)=>(
                  <div key={q.id} style={{ ...css.card, padding:"12px 14px", border:`1px solid ${q.waitMins>10?"rgba(201,168,76,0.35)":"var(--navy-border)"}`, borderLeft:`3px solid ${q.waitMins>10?"var(--gold)":q.waitMins>5?"rgba(201,168,76,0.5)":"var(--navy-border)"}` }}>
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:4 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14 }}>{q.match}</div>
                        <div style={{ fontSize:12, color:"var(--muted)", marginTop:1 }}>{q.round}</div>
                        <div style={{ fontSize:12, color:"var(--off-white)", marginTop:2 }}>{q.teams}</div>
                        {q.waitMins > 0 && (
                          <div style={{ fontSize:11, color:q.waitMins>10?"var(--gold)":"var(--muted)", marginTop:3, display:"flex", alignItems:"center", gap:4 }}>
                            ⏳ Waiting {q.waitMins}m{q.waitMins>10?" — needs a table!":""}
                          </div>
                        )}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:5, flexShrink:0 }}>
                        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--muted)" }}>#{i+1}</span>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                      {freeTables.slice(0,3).map(t=>(
                        <Btn key={t.id} sz="sm" v="success" onClick={()=>assignMatchToTable(t.id, q)}>
                          → {t.name}
                        </Btn>
                      ))}
                      {freeTables.length === 0 && <span style={{ fontSize:11, color:"var(--muted)", fontStyle:"italic" }}>No free tables</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active tables column */}
          <div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span>🏓 Table Status</span>
              <Badge color="green">{freeTables.length} free</Badge>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:8 }}>
              {tbls.map(t=>{
                const assign = tableAssignments[t.id];
                const isOOR  = outOfRotation[t.id];
                const iss    = (t.healthIssues||[]).filter(i=>i.status!=="resolved");
                const bColor = isOOR ? "var(--navy-border)" : TABLE_STATUS_COLOR[t.status];
                return (
                  <div key={t.id} style={{ ...css.card, padding:"10px 12px", border:`1px solid ${assign?"rgba(46,204,113,0.3)":bColor}`, opacity:isOOR?0.55:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:bColor, lineHeight:1 }}>{t.name}</div>
                      {iss.length>0 && <span style={{ fontSize:10 }}>{iss.some(i=>i.severity==="critical")?"🔴":"⚠️"}</span>}
                    </div>
                    <div style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:bColor, marginBottom:4 }}>
                      {isOOR?"OUT OF ROTATION":TABLE_STATUS_LABEL[t.status]}
                    </div>
                    {assign ? (
                      <>
                        <div style={{ fontSize:10, color:"var(--green)", fontWeight:700 }}>🎯 {assign.matchLabel}</div>
                        <div style={{ fontSize:9, color:"var(--muted)", marginTop:2 }}>since {assign.assignedAt}</div>
                        <Btn sz="sm" v="ghost" full style={{ marginTop:6 }} onClick={()=>freeTable(t.id)}>Free</Btn>
                      </>
                    ) : isOOR ? (
                      <div style={{ fontSize:10, color:"var(--muted)", fontStyle:"italic" }}>Unavailable</div>
                    ) : t.status==="blocked" ? (
                      <div style={{ fontSize:10, color:"var(--red)", fontStyle:"italic" }}>Blocked</div>
                    ) : (
                      assignQueue.length > 0 ? (
                        <Btn sz="sm" v="success" full style={{ marginTop:4 }} onClick={()=>assignMatchToTable(t.id, assignQueue[0])}>
                          Assign Next
                        </Btn>
                      ) : (
                        <div style={{ fontSize:10, color:"var(--green)", marginTop:2 }}>✓ Available</div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Assignment toast */}
        {assignToast && (
          <div style={{ position:"fixed", bottom:80, left:"50%", transform:"translateX(-50%)", background:"var(--green)", color:"var(--navy)", padding:"10px 20px", borderRadius:8, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1, zIndex:300, boxShadow:"0 4px 20px rgba(0,0,0,0.4)", whiteSpace:"nowrap" }}>
            ✓ Assigned: {assignToast}
          </div>
        )}
      </div>
    );
  }

  // ── TABLE HEALTH TAB ──────────────────────────────────────────────────────
  function renderHealth() {
    const curVenue = venues.find(v=>v.id===healthVenueId) || venues[0];
    if (!curVenue) return <div style={{ color:"var(--muted)", padding:40, textAlign:"center" }}>No venues yet</div>;
    const tbls      = getMapTables(curVenue.id);
    const allIss    = allHealthIssues(curVenue.id);
    const openIss   = allIss.filter(i=>i.status!=="resolved");
    const critIss   = openIss.filter(i=>i.severity==="critical");
    const score     = tableHealthScore(curVenue.id);

    return (
      <div>
        {/* Venue picker + summary */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <select style={{ ...css.select, minWidth:200 }} value={curVenue.id} onChange={e=>setHealthVenueId(e.target.value)}>
              {venues.map(v=>{ const cnt=openIssues(v.id).length; return <option key={v.id} value={v.id}>{v.name}{cnt>0?` (${cnt} issues)`:""}</option>; })}
            </select>
          </div>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            {[["Critical",critIss.length,"var(--red)"],["Open",openIss.length,"var(--gold)"],["Tables",tbls.length,"var(--muted)"],["Health",`${score}%`,score>=80?"var(--green)":score>=50?"var(--gold)":"var(--red)"]].map(([l,v,c])=>(
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
            {openIss.length > 0 && <Btn sz="sm" v="success" onClick={()=>bulkResolveAll(curVenue.id)}>✓ Resolve All</Btn>}
          </div>
        </div>

        {tbls.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--muted)" }}>No tables defined for this venue</div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
            {tbls.map(t => {
              const openT    = (t.healthIssues||[]).filter(i=>i.status!=="resolved");
              const resolvedT= (t.healthIssues||[]).filter(i=>i.status==="resolved");
              const crit     = openT.some(i=>i.severity==="critical");
              const isOOR    = outOfRotation[t.id];
              const assign   = tableAssignments[t.id];
              const cardBord = crit ? "rgba(217,79,61,0.4)" : openT.length > 0 ? "rgba(201,168,76,0.35)" : "var(--navy-border)";
              return (
                <div key={t.id} style={{ ...css.card, padding:0, overflow:"hidden", border:`1px solid ${cardBord}` }}>
                  <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:2 }}>🏓 {t.name}</span>
                      {crit && <Badge color="red">Critical</Badge>}
                      {!crit && openT.length > 0 && <Badge color="">⚠️ {openT.length}</Badge>}
                      {openT.length === 0 && <Badge color="green">✓ OK</Badge>}
                      {isOOR && <Badge color="red">OOR</Badge>}
                    </div>
                    <div style={{ display:"flex", gap:5 }}>
                      <Btn sz="sm" v="ghost" onClick={()=>setReportModal(t.id)}>+ Issue</Btn>
                      <Btn sz="sm" v={isOOR?"danger":"ghost"} onClick={()=>setOutOfRotation(p=>({...p,[t.id]:!p[t.id]}))}>
                        {isOOR?"↩":"⛔"}
                      </Btn>
                    </div>
                  </div>
                  <div style={{ padding:"10px 14px" }}>
                    {assign && (
                      <div style={{ fontSize:11, color:"var(--green)", marginBottom:8, fontWeight:700 }}>🎯 In use: {assign.matchLabel}</div>
                    )}
                    {openT.length === 0 ? (
                      <div style={{ fontSize:12, color:"var(--muted)", fontStyle:"italic" }}>✅ No open issues — table is healthy</div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                        {openT.map(issue=>(
                          <div key={issue.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"rgba(255,255,255,0.03)", borderRadius:5 }}>
                            <span style={{ fontSize:14, flexShrink:0 }}>{issue.icon}</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:600, fontSize:12 }}>{issue.label}</div>
                              {issue.notes && <div style={{ fontSize:10, color:"var(--muted)", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{issue.notes}</div>}
                              <div style={{ fontSize:9, color:"var(--muted)", marginTop:1 }}>{issue.reportedAt}</div>
                            </div>
                            <Btn sz="sm" v="success" onClick={()=>resolveIssue(curVenue.id, t.id, issue.id)}>✓</Btn>
                          </div>
                        ))}
                      </div>
                    )}
                    {resolvedT.length > 0 && (
                      <div style={{ fontSize:10, color:"var(--muted)", marginTop:8, fontStyle:"italic", display:"flex", justifyContent:"space-between" }}>
                        <span>+{resolvedT.length} resolved</span>
                        <span style={{ cursor:"pointer", textDecoration:"underline" }} onClick={()=>setShowMaintenanceLog(showMaintenanceLog===t.id?null:t.id)}>view log</span>
                      </div>
                    )}
                    {showMaintenanceLog === t.id && (
                      <div style={{ marginTop:8, borderTop:"1px solid var(--navy-border)", paddingTop:8 }}>
                        <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:6 }}>Maintenance Log</div>
                        {(t.maintenanceLog||[...resolvedT]).map((entry,i)=>(
                          <div key={i} style={{ display:"flex", gap:8, padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.03)", fontSize:11 }}>
                            <span style={{ color:"var(--muted)", whiteSpace:"nowrap" }}>{entry.reportedAt||entry.resolvedAt||"—"}</span>
                            <span style={{ color:entry.logType==="issue_resolved"?"var(--green)":"var(--muted)" }}>{entry.label||entry.logType}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Report modal (reuse) */}
        <Modal open={!!reportModal} onClose={()=>setReportModal(null)} title="Report Table Issue" width={440}>
          {reportModal && (() => {
            const t = tbls.find(x=>x.id===reportModal);
            return (
              <div>
                <div style={{ marginBottom:14, padding:"8px 12px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.15)", borderRadius:6, fontSize:13 }}>
                  Reporting issue for: <strong>{t?.name}</strong>
                </div>
                <Fld label="Issue Type">
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {HEALTH_ISSUES.map(h=>(
                      <div key={h.id} onClick={()=>setReportForm(f=>({...f, type:h.id}))}
                        style={{ padding:"8px 10px", border:`1px solid ${reportForm.type===h.id?"var(--gold)":"var(--navy-border)"}`, borderRadius:6, cursor:"pointer", display:"flex", alignItems:"center", gap:8, background:reportForm.type===h.id?"rgba(201,168,76,0.08)":"transparent" }}>
                        <span>{h.icon}</span>
                        <div>
                          <div style={{ fontWeight:600, fontSize:12, lineHeight:1.2 }}>{h.label}</div>
                          <div style={{ fontSize:10, color:h.severity==="critical"?"var(--red)":"var(--muted)", marginTop:1 }}>{h.severity}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Fld>
                <Fld label="Notes (optional)">
                  <textarea style={{ ...css.input, minHeight:60, resize:"vertical" }} value={reportForm.notes} onChange={e=>setReportForm(f=>({...f,notes:e.target.value}))} placeholder="Describe the issue…" />
                </Fld>
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
                  <Btn v="ghost" onClick={()=>setReportModal(null)}>Cancel</Btn>
                  <Btn onClick={()=>reportIssue(curVenue.id, reportModal)}>Report Issue</Btn>
                </div>
              </div>
            );
          })()}
        </Modal>
      </div>
    );
  }

  // ── HISTORY TAB ──────────────────────────────────────────────────────────
  function renderHistory() {
    const allEvts = venues.flatMap(v=>venueEvents(v.id).map(t=>({...t, venueName:v.name, venueCity:v.city})));
    allEvts.sort((a,b)=>(b.date||"").localeCompare(a.date||""));
    return (
      <div>
        <div style={{ fontSize:12, color:"var(--muted)", marginBottom:16 }}>{allEvts.length} events across {venues.length} venue{venues.length!==1?"s":""}</div>
        {allEvts.length === 0 ? (
          <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📅</div>
            <div>No events have been hosted at any venue yet</div>
          </div>
        ) : (
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div className="resp-table">
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:500 }}>
                <thead><tr>{["Event","Format","Date","Venue","Type","Status"].map(h=><th key={h} style={css.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {allEvts.map(t=>(
                    <tr key={t.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <td style={{ ...css.td, fontWeight:600 }}>{t.name}</td>
                      <td style={{ ...css.td, fontSize:12 }}>{FORMATS.find(f=>f.v===t.format)?.l||t.format}</td>
                      <td style={{ ...css.td, color:"var(--muted)", fontSize:12 }}>{t.date||"—"}</td>
                      <td style={css.td}><div style={{ fontSize:13 }}>{t.venueName}</div><div style={{ fontSize:10, color:"var(--muted)" }}>{t.venueCity}</div></td>
                      <td style={css.td}><Badge color={t.type==="pro"?"gold":""}>{t.type}</Badge></td>
                      <td style={css.td}><Badge color={t.status==="completed"?"gold":t.status==="active"?"green":""}>{t.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── MAIN RENDER ──────────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Venues</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>{venues.length} venue{venues.length!==1?"s":""}  ·  {venues.reduce((s,v)=>s+(v.tables||0),0)} tables</div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <input style={{ ...css.input, width:isMobile?130:200 }} placeholder="Search venues…" value={search} onChange={e=>setSearch(e.target.value)} />
          <Btn onClick={openCreate}>+ Add Venue</Btn>
        </div>
      </div>

      {/* Tab bar */}
      <div className="tab-strip" style={{ marginBottom:18 }}>
        {TABS.map(t => {
          let badge = null;
          if (t.id==="health") { const total=venues.reduce((s,v)=>s+openIssues(v.id).length,0); if(total>0) badge=total; }
          if (t.id==="assign") { if(assignQueue.length>0) badge=assignQueue.length; }
          return (
            <button key={t.id} className={`tab-btn ${tab===t.id?"tab-active":""}`} onClick={()=>setTab(t.id)}>
              {t.icon} {t.label}{badge?` (${badge})`:""}</button>
          );
        })}
      </div>

      {tab==="overview"  && renderOverview()}
      {tab==="floormap"  && renderFloorMap()}
      {tab==="assign"    && renderAssignQueue()}
      {tab==="health"    && renderHealth()}
      {tab==="history"   && renderHistory()}

      {/* ── Calendar Tab ─────────────────────────────────────────────────── */}
      {tab==="calendar" && (() => {
        const allEvents = (tournaments||[]).slice().sort((a,b)=>(a.date||"").localeCompare(b.date||""));
        const byMonth = {};
        allEvents.forEach(t => {
          const month = (t.date||"").slice(0,7)||"Unknown";
          if(!byMonth[month]) byMonth[month]=[];
          byMonth[month].push(t);
        });
        const months = Object.keys(byMonth).sort();
        if(!months.length) return (
          <div style={{textAlign:"center",padding:"48px 20px",color:"var(--muted)"}}>
            <div style={{fontSize:36,marginBottom:12}}>📅</div><div>No events scheduled yet</div>
          </div>
        );
        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {months.map(month=>(
              <div key={month}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--gold)",marginBottom:8,paddingBottom:6,borderBottom:"1px solid var(--navy-border)"}}>
                  {new Date(month+"-02").toLocaleString("default",{month:"long",year:"numeric"})}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {byMonth[month].map(t=>{
                    const regs=t.registrations||[];
                    const sc = t.status==="active"?"var(--green)":t.status==="completed"?"var(--muted)":"#5b8dee";
                    return (
                      <div key={t.id} style={{...css.card,padding:"10px 14px",borderLeft:`3px solid ${sc}`,display:"flex",alignItems:"center",gap:12}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:13}}>{t.name}</div>
                          <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>{t.date||"No date"} · {t.venue||"No venue"} · {regs.length} teams</div>
                        </div>
                        <div style={{fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:sc,padding:"2px 8px",background:sc+"15",border:`1px solid ${sc}44`,borderRadius:3}}>{t.status}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Documents Tab ────────────────────────────────────────────────── */}
      {tab==="docs" && (() => {
        const vId = docVenueId||(venues[0]?.id);
        const venue = venues.find(v=>v.id===vId);
        const docs = venueDocMap[vId]||[];
        const docTypes = ["contract","permit","layout","insurance","invoice","other"];
        const typeColors = {contract:"var(--gold)",permit:"#5b8dee",layout:"var(--green)",insurance:"var(--muted)",invoice:"var(--red)",other:"rgba(255,255,255,0.3)"};
        return (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",flexShrink:0}}>Venue:</div>
              <select style={{...css.select,flex:1,maxWidth:280}} value={vId||""} onChange={e=>setDocVenueId(e.target.value)}>
                {venues.map(v=><option key={v.id} value={v.id}>{v.name} — {v.city}</option>)}
              </select>
              <Btn sz="sm" onClick={()=>setShowDocForm(s=>!s)}>{showDocForm?"Cancel":"+ Add Document"}</Btn>
            </div>
            {showDocForm && (
              <div style={{...css.card,padding:"16px 18px",borderColor:"rgba(201,168,76,0.3)"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--gold)",marginBottom:12}}>Add Document</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,color:"var(--muted)",display:"block",marginBottom:4}}>Document Name</label>
                    <input style={{...css.input,width:"100%"}} value={docForm.name} onChange={e=>setDocForm(f=>({...f,name:e.target.value}))} placeholder="e.g. 2025 Venue Contract" /></div>
                  <div><label style={{fontSize:11,color:"var(--muted)",display:"block",marginBottom:4}}>Type</label>
                    <select style={css.select} value={docForm.type} onChange={e=>setDocForm(f=>({...f,type:e.target.value}))}>
                      {docTypes.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                    </select></div>
                  <div><label style={{fontSize:11,color:"var(--muted)",display:"block",marginBottom:4}}>Date (optional)</label>
                    <input type="date" style={css.input} value={docForm.date} onChange={e=>setDocForm(f=>({...f,date:e.target.value}))} /></div>
                  <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,color:"var(--muted)",display:"block",marginBottom:4}}>URL / Link (optional)</label>
                    <input style={{...css.input,width:"100%"}} value={docForm.url} onChange={e=>setDocForm(f=>({...f,url:e.target.value}))} placeholder="https://drive.google.com/…" /></div>
                  <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,color:"var(--muted)",display:"block",marginBottom:4}}>Notes (optional)</label>
                    <input style={{...css.input,width:"100%"}} value={docForm.notes} onChange={e=>setDocForm(f=>({...f,notes:e.target.value}))} placeholder="Brief description…" /></div>
                </div>
                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <Btn disabled={!docForm.name.trim()} onClick={()=>{
                    setVenueDocMap(m=>({...m,[vId]:[...(m[vId]||[]),{...docForm,id:"doc"+Date.now()}]}));
                    setDocForm({name:"",type:"contract",url:"",notes:"",date:""});
                    setShowDocForm(false);
                  }}>Add Document</Btn>
                </div>
              </div>
            )}
            {docs.length===0&&!showDocForm ? (
              <div style={{textAlign:"center",padding:"32px 20px",color:"var(--muted)"}}>
                <div style={{fontSize:36,marginBottom:10}}>📄</div>
                <div>No documents for {venue?.name||"this venue"} yet</div>
                <div style={{fontSize:11,marginTop:4}}>Add contracts, permits, floor layouts, and more</div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {docs.map((doc)=>(
                  <div key={doc.id} style={{...css.card,padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:typeColors[doc.type]||typeColors.other,flexShrink:0,marginTop:5}} />
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>{doc.name}</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:11,color:"var(--muted)"}}>
                        <span style={{color:typeColors[doc.type]||typeColors.other,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{doc.type}</span>
                        {doc.date&&<span>📅 {doc.date}</span>}
                        {doc.url&&<a href={doc.url} target="_blank" rel="noreferrer" style={{color:"#5b8dee",textDecoration:"none"}}>🔗 Open</a>}
                      </div>
                      {doc.notes&&<div style={{fontSize:11,color:"var(--muted)",marginTop:4,fontStyle:"italic"}}>{doc.notes}</div>}
                    </div>
                    <button onClick={()=>setVenueDocMap(m=>({...m,[vId]:(m[vId]||[]).filter(d=>d.id!==doc.id)}))}
                      style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,flexShrink:0,padding:"0 4px"}}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {tab==="utilization" && (() => {
        // Utilization: matches per venue, table usage, events frequency
        const venueStats = venues.map(v => {
          const evts = (tournaments||[]).filter(t=>t.venue===v.name||t.venue===v.id);
          const allM  = evts.flatMap(t=>getAllMatchesFromTournament(t).filter(m=>m.status==="complete"));
          const regCt = evts.reduce((s,t)=>s+(t.registrations||[]).length,0);
          return { id:v.id, name:v.name, city:v.city, tables:v.tables||0, capacity:v.capacity||0,
            events:evts.length, matches:allM.length, registrations:regCt,
            lastEvent:evts.sort((a,b)=>(b.date||"").localeCompare(a.date||""))[0]?.date||null };
        });
        const maxEvents = Math.max(...venueStats.map(v=>v.events),1);
        const maxMatches = Math.max(...venueStats.map(v=>v.matches),1);
        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
              {[
                [venues.length,"Total Venues","var(--white)"],
                [venues.filter(v=>v.status==="active").length,"Active","var(--green)"],
                [(tournaments||[]).filter(t=>venues.some(v=>v.name===t.venue)).length,"Events Hosted","var(--gold)"],
                [venues.reduce((s,v)=>s+(v.tables||0),0),"Total Tables","#5b8dee"],
              ].map(([v,l,c])=>(
                <div key={l} style={{ ...css.card, padding:"12px 14px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:c, lineHeight:1, marginBottom:4 }}>{v}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)" }}>
                Venue Utilization
              </div>
              {venueStats.length===0 ? (
                <div style={{ padding:"24px 16px", textAlign:"center", color:"var(--muted)", fontSize:12 }}>No venues yet</div>
              ) : venueStats.sort((a,b)=>b.events-a.events).map((v,i)=>(
                <div key={v.id} style={{ padding:"12px 16px", borderBottom:i<venueStats.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{v.name}</div>
                      <div style={{ fontSize:11, color:"var(--muted)" }}>{v.city} · {v.tables} tables · cap {v.capacity||"∞"}</div>
                    </div>
                    <div style={{ display:"flex", gap:12 }}>
                      {[[v.events,"events","var(--gold)"],[v.matches,"matches","var(--white)"],[v.registrations,"reg entries","var(--muted)"]].map(([val,l,c])=>(
                        <div key={l} style={{ textAlign:"right" }}>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:c, lineHeight:1 }}>{val}</div>
                          <div style={{ fontSize:9, color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>{l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <span style={{ fontSize:10, color:"var(--muted)", width:60 }}>Events</span>
                    <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${Math.round(v.events/maxEvents*100)}%`, background:"rgba(201,168,76,0.6)", borderRadius:3, transition:"width 0.4s" }} />
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, alignItems:"center", marginTop:4 }}>
                    <span style={{ fontSize:10, color:"var(--muted)", width:60 }}>Matches</span>
                    <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${Math.round(v.matches/maxMatches*100)}%`, background:"rgba(91,141,238,0.5)", borderRadius:3, transition:"width 0.4s" }} />
                    </div>
                  </div>
                  {v.lastEvent && <div style={{ fontSize:10, color:"rgba(255,255,255,0.2)", marginTop:4 }}>Last event: {v.lastEvent}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Create / Edit Modal */}}
      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title={editId?"Edit Venue":"Add New Venue"} width={560}>
        <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Fld label="Venue Name *"><input style={css.input} value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. The Pour House" /></Fld>
          <Fld label="City *"><input style={css.input} value={form.city} onChange={e=>upd("city",e.target.value)} placeholder="e.g. Austin, TX" /></Fld>
          <Fld label="Tables"><input style={css.input} type="number" min="0" value={form.tables} onChange={e=>upd("tables",e.target.value)} placeholder="0" /></Fld>
          <Fld label="Capacity"><input style={css.input} type="number" min="0" value={form.capacity} onChange={e=>upd("capacity",e.target.value)} placeholder="0" /></Fld>
          <Fld label="Contact Email"><input style={css.input} value={form.contact} onChange={e=>upd("contact",e.target.value)} /></Fld>
          <Fld label="Phone"><input style={css.input} value={form.phone} onChange={e=>upd("phone",e.target.value)} /></Fld>
          <Fld label="Status">
            <select style={css.select} value={form.status} onChange={e=>upd("status",e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </Fld>
        </div>
        <Fld label="Full Address"><input style={css.input} value={form.address} onChange={e=>upd("address",e.target.value)} /></Fld>
        <Fld label="Notes"><input style={css.input} value={form.notes} onChange={e=>upd("notes",e.target.value)} /></Fld>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
          <Btn v="ghost" onClick={()=>setShowCreate(false)}>Cancel</Btn>
          <Btn onClick={saveVenue} disabled={!form.name||!form.city}>{editId?"Save Changes":"Add Venue"}</Btn>
        </div>
      </Modal>
    </div>
  );
}


// ── CSV EXPORT HELPERS ────────────────────────────────────────────────────────
function escCSV(v) {
  const s = String(v==null?"":v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g,'""')}"`;
  return s;
}
function rowsToCSV(headers, rows) {
  return [headers.map(escCSV).join(","), ...rows.map(r=>r.map(escCSV).join(","))].join("\n");
}
function downloadCSV(filename, csv) {
  const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}
function getAllMatchesFromTournament(t) {
  if (!t?.bracket) return [];
  const bkt = t.bracket;
  const all = [];
  function collect(rounds, bracketLabel) {
    (rounds||[]).forEach(r=>(r.matches||[]).forEach(m=>{
      all.push({...m, _tournament:t, _round:r.name, _bracket:bracketLabel});
    }));
  }
  if (bkt.type==="de") {
    collect(bkt.wb||[], "W"); collect(bkt.lb||[], "L");
    if (bkt.gf) all.push({...bkt.gf, _tournament:t, _round:"Grand Final", _bracket:"GF"});
    if (bkt.gf?.reset) all.push({...bkt.gf.reset, _tournament:t, _round:"GF Reset", _bracket:"GF"});
  } else if (bkt.type==="rpe_se"||bkt.type==="rpe_de") {
    collect(bkt.prelims||[], "P");
    if (bkt.elimBracket?.type==="de_phase") {
      collect(bkt.elimBracket.wb||[], "W"); collect(bkt.elimBracket.lb||[], "L");
      if (bkt.elimBracket.gf) all.push({...bkt.elimBracket.gf, _tournament:t, _round:"Grand Final", _bracket:"GF"});
    } else { collect(bkt.elimBracket?.rounds||[], ""); }
  } else if (bkt.type==="group_stage") {
    (bkt.groups||[]).forEach(g=>collect(g.matches?.map(m=>({round:"GS",matches:g.matches}))||[], "GS"));
    if (bkt.elimBracket) {
      collect(bkt.elimBracket.rounds||[], "W");
      if (bkt.elimBracket.gf) all.push({...bkt.elimBracket.gf, _tournament:t, _round:"Grand Final", _bracket:"GF"});
    }
  } else { collect(bkt.rounds||[], ""); }
  return all;
}
function buildPlayersCSV(players, tournaments) {
  function calcPR(p) {
    const pp = (p.placements||[]).reduce((s,pl)=>s+(pl===1?100:pl===2?70:pl===3?50:pl<=6?30:10),0);
    return Math.round((p.elo||1200)*0.6+pp*0.3+Math.max(0,p.cupDiff||0)*0.1);
  }
  const ranked = [...players].sort((a,b)=>calcPR(b)-calcPR(a));
  return rowsToCSV(
    ["Rank","Name","Nickname","PR","Elo","W","L","Win%","Cup+/-","Events"],
    ranked.map((p,i)=>[
      i+1, p.name, p.nick||"",
      calcPR(p), p.elo||1200, p.wins||0, p.losses||0,
      (p.wins+p.losses)>0?Math.round(p.wins/(p.wins+p.losses)*100)+"%":"—",
      (p.cupDiff||0)>=0?"+"+p.cupDiff:p.cupDiff,
      p.tournaments||0,
    ])
  );
}
function buildMatchHistoryCSV(tournaments, filterTournamentId) {
  const allMatches = [];
  (tournaments||[]).forEach(t=>{
    if (filterTournamentId && t.id!==filterTournamentId) return;
    getAllMatchesFromTournament(t).forEach(m=>{
      if (m.status!=="complete"||!m.t1||!m.t2) return;
      allMatches.push({...m, _t:t});
    });
  });
  allMatches.sort((a,b)=>new Date(b._t.date||0)-new Date(a._t.date||0));
  return rowsToCSV(
    ["Tournament","Date","Round","Team1","Team2","Score1","Score2","Winner","OT","CupDiff"],
    allMatches.map(m=>[
      m._t.name, m._t.date||"", m._round||"",
      m.t1?.name||"", m.t2?.name||"",
      m.s1??"", m.s2??"",
      m.winner?.name||"", m.ot?"Yes":"No", m.cupDiff??""
    ])
  );
}
function buildTournamentResultsCSV(t) {
  const matches = getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2);
  return rowsToCSV(
    ["Round","Bracket","Team1","Team2","Score1","Score2","Winner","OT"],
    matches.map(m=>[m._round||"", m._bracket||"", m.t1?.name||"", m.t2?.name||"", m.s1??"", m.s2??"", m.winner?.name||"", m.ot?"Yes":"No"])
  );
}
function buildRegistrationsCSV(t) {
  return rowsToCSV(
    ["#","Team","Players","Status","Payment","Seed","Registered"],
    (t.registrations||[]).map((r,i)=>[
      i+1, r.teamName,
      (r.players||[]).map(p=>p.name).join(" / "),
      r.status||"", r.paymentStatus||"", r.seed||"", r.registeredAt||""
    ])
  );
}
function buildSeasonCSV(s) {
  return rowsToCSV(
    ["Rank","Player","Points","Events","W","L"],
    (s.standings||[]).map((p,i)=>[i+1, p.name||p.id, p.points||0, p.events||0, p.wins||0, p.losses||0])
  );
}
// Alert helpers for Notifications page
function generateAlerts(tournaments) {
  const alerts = [];
  const now = Date.now();
  const sevenDays = 7*24*60*60*1000;
  (tournaments||[]).forEach(t=>{
    const allM = getAllMatchesFromTournament(t);
    // Disputed scores
    allM.filter(m=>m.status==="disputed").forEach(m=>{
      alerts.push({ id:`dispute_${m.id}`, severity:"critical", icon:"🚨", title:`Score Dispute`, body:`${m.t1?.name||"?"} vs ${m.t2?.name||"?"} in ${t.name}`, tournamentId:t.id, action:"Resolve", notifTemplate:"dispute_resolved" });
    });
    // Pending approval
    allM.filter(m=>m.status==="pending_approval").forEach(m=>{
      alerts.push({ id:`approval_${m.id}`, severity:"high", icon:"⚡", title:`Score Awaiting Approval`, body:`${m.t1?.name||"?"} vs ${m.t2?.name||"?"} in ${t.name}`, tournamentId:t.id, action:"Review", notifTemplate:"score_approved" });
    });
    // Active with no bracket
    if (t.status==="active" && !t.bracket) {
      alerts.push({ id:`nobracket_${t.id}`, severity:"warn", icon:"⚠️", title:`No Bracket Generated`, body:`${t.name} is active but has no bracket`, tournamentId:t.id, action:"Fix" });
    }
    // Upcoming within 7 days
    if (t.status==="upcoming" && t.date) {
      const eventDate = new Date(t.date).getTime();
      if (!isNaN(eventDate) && eventDate-now > 0 && eventDate-now < sevenDays) {
        alerts.push({ id:`upcoming_${t.id}`, severity:"info", icon:"ℹ️", title:`Event in ${Math.ceil((eventDate-now)/86400000)} Days`, body:`${t.name} — ${(t.registrations||[]).length} registered`, tournamentId:t.id, action:"View" });
      }
    }
    // Completed but not finalized
    if (t.status==="completed" && !t.finalized) {
      alerts.push({ id:`finalize_${t.id}`, severity:"info", icon:"✅", title:`Ready to Finalize`, body:`${t.name} has finished — update player stats`, tournamentId:t.id, action:"Finalize" });
    }
  });
  const order = {critical:0, high:1, warn:2, info:3};
  return alerts.sort((a,b)=>(order[a.severity]||4)-(order[b.severity]||4));
}
function getAlertCount(tournaments) {
  return generateAlerts(tournaments).filter(a=>a.severity==="critical"||a.severity==="high").length;
}


// ── RANKINGS ─────────────────────────────────────────────────────────────────
function RankingsPage({ players, tournaments }) {
  const { isMobile } = useBreakpoint();
  const [tab,         setTab]         = useState("leaderboard");
  const [h2hFocus,    setH2hFocus]    = useState(null); // { a, b, matches }
  const [chartPlayers,setChartPlayers]= useState(null); // Set of player ids (null = top 5)
  const [chartWindow, setChartWindow] = useState(20);   // last N matches per player
  const [matchupA,     setMatchupA]     = useState(null);  // player id
  const [matchupB,     setMatchupB]     = useState(null);  // player id
  const [matchupSearch, setMatchupSearch] = useState({ a:"", b:"" });
  const [histTournId,  setHistTournId]  = useState(null);  // focused tournament for history tab
  const [histPlayerFilter, setHistPlayerFilter] = useState("");

  const ranked        = [...players].sort((a,b) => calcPowerRating(b) - calcPowerRating(a));
  const rankedTourneys= (tournaments||[]).filter(t => t.ranked && t.type==="pro");

  // ── Tier assignment ─────────────────────────────────────────────────────
  const maxPR = calcPowerRating(ranked[0] || {elo:1200,placements:[],cupDiff:0});
  function getTier(pr) {
    const pct = maxPR > 0 ? pr / maxPR : 0;
    if (pct >= 0.90) return { label:"S", color:"#e8c84a", bg:"rgba(232,200,74,0.12)" };
    if (pct >= 0.75) return { label:"A", color:"#5b8dee", bg:"rgba(91,141,238,0.12)" };
    if (pct >= 0.55) return { label:"B", color:"var(--green)", bg:"rgba(46,204,113,0.10)" };
    return               { label:"C", color:"var(--muted)", bg:"rgba(255,255,255,0.04)" };
  }

  // ── Streak helper ───────────────────────────────────────────────────────
  function currentStreak(p) {
    const hist = [...(p.matchHistory||[])].reverse();
    if (!hist.length) return { n:0, type:null };
    const type = hist[0].won ? "W" : "L";
    let n = 0;
    for (const m of hist) {
      if ((m.won && type==="W") || (!m.won && type==="L")) n++;
      else break;
    }
    return { n, type };
  }

  // ── H2H map across all players ──────────────────────────────────────────
  // h2hData[aId][bId] = { w, l, matches[] }
  const h2hData = {};
  players.forEach(p => {
    h2hData[p.id] = {};
    players.forEach(q => {
      if (p.id === q.id) return;
      const ms = (p.matchHistory||[]).filter(m => m.opponent === q.name);
      h2hData[p.id][q.id] = {
        w: ms.filter(m=>m.won).length,
        l: ms.filter(m=>!m.won).length,
        matches: ms,
      };
    });
  });

  // ── Elo chart data ──────────────────────────────────────────────────────
  const defaultChartIds = new Set(ranked.slice(0,5).map(p=>p.id));
  const activeChartIds  = chartPlayers || defaultChartIds;

  const CHART_COLORS = ["#e8c84a","#5b8dee","#2ecc71","#e74c3c","#9b59b6","#1abc9c","#e67e22","#ecf0f1"];
  const chartW = 560, chartH = 140;

  function buildChartSeries(p, color) {
    const hist = p.matchHistory || [];
    if (hist.length === 0) return null;
    const pts = hist.slice(-chartWindow).map((m,i) => ({ x:i, y: m.eloAfter || p.elo }));
    if (pts.length < 2) return null;
    return { player:p, color, pts };
  }

  const chartSeries = ranked
    .filter(p => activeChartIds.has(p.id))
    .map((p,i) => buildChartSeries(p, CHART_COLORS[i % CHART_COLORS.length]))
    .filter(Boolean);

  const allEloVals = chartSeries.flatMap(s => s.pts.map(pt => pt.y));
  const eloMin = allEloVals.length ? Math.min(...allEloVals) - 30 : 1100;
  const eloMax = allEloVals.length ? Math.max(...allEloVals) + 30 : 1500;
  const eloRange = eloMax - eloMin || 1;

  function toChartX(i, total) { return Math.round(i / Math.max(total-1,1) * chartW); }
  function toChartY(elo) { return Math.round(chartH - ((elo - eloMin) / eloRange) * chartH); }

  function buildSVGPath(pts) {
    if (pts.length < 2) return "";
    return pts.map((pt,i) => `${i===0?"M":"L"}${toChartX(pt.x, pts.length)},${toChartY(pt.y)}`).join(" ");
  }

  // ── Stat leaders ────────────────────────────────────────────────────────
  const qualified = players.filter(p => (p.wins + p.losses) >= 3);

  const statCategories = [
    {
      label:"👑 Win Rate", sub:"min 3 matches",
      rows: [...qualified].sort((a,b) => {
        const wr = p => p.wins/(p.wins+p.losses);
        return wr(b)-wr(a);
      }).slice(0,8).map(p => ({
        name: p.name, nick: p.nick,
        val: Math.round(p.wins/(p.wins+p.losses)*100)+"%",
        sub: `${p.wins}W – ${p.losses}L`,
        bar: p.wins/(p.wins+p.losses),
        color:"var(--green)",
      })),
    },
    {
      label:"⚡ Highest Elo", sub:"current rating",
      rows: [...players].sort((a,b)=>b.elo-a.elo).slice(0,8).map(p => ({
        name:p.name, nick:p.nick,
        val: p.elo,
        sub: `${p.wins+p.losses} matches`,
        bar: (p.elo-1000)/600,
        color:"var(--gold)",
      })),
    },
    {
      label:"🍺 Cup Differential", sub:"net cups across all matches",
      rows: [...players].sort((a,b)=>b.cupDiff-a.cupDiff).slice(0,8).map(p => ({
        name:p.name, nick:p.nick,
        val: (p.cupDiff>=0?"+":"")+p.cupDiff,
        sub: `${p.cupsMade} made / ${p.cupsAllowed} allowed`,
        bar: Math.max(0,p.cupDiff) / Math.max(1,...players.map(x=>Math.abs(x.cupDiff))),
        color: p.cupDiff>=0?"var(--green)":"var(--red)",
      })),
    },
    {
      label:"🔥 Longest Win Streak", sub:"current or historical",
      rows: players.map(p => {
        const hist = p.matchHistory||[];
        let best=0, cur=0;
        hist.forEach(m=>{ if(m.won){cur++;best=Math.max(best,cur);}else cur=0; });
        return { name:p.name, nick:p.nick, val:best+"W", sub:`${p.wins} total wins`, bar:best/Math.max(1,...players.map(q=>{ let b=0,c=0; (q.matchHistory||[]).forEach(m=>{if(m.won){c++;b=Math.max(b,c);}else c=0;}); return b; })), color:"#e67e22", _sort:best };
      }).sort((a,b)=>b._sort-a._sort).slice(0,8),
    },
    {
      label:"🏆 Most Wins", sub:"all-time",
      rows: [...players].sort((a,b)=>b.wins-a.wins).slice(0,8).map(p => ({
        name:p.name, nick:p.nick,
        val: p.wins,
        sub: `${p.losses} losses`,
        bar: p.wins/Math.max(1,...players.map(x=>x.wins)),
        color:"var(--gold)",
      })),
    },
    {
      label:"📅 Most Active", sub:"tournaments entered",
      rows: [...players].sort((a,b)=>(b.tournaments||0)-(a.tournaments||0)).slice(0,8).map(p => ({
        name:p.name, nick:p.nick,
        val: p.tournaments||0,
        sub: `${(p.matchHistory||[]).length} matches`,
        bar: (p.tournaments||0)/Math.max(1,...players.map(x=>x.tournaments||0)),
        color:"#5b8dee",
      })),
    },
  ];

  const TABS = [["leaderboard","🏆 Leaderboard"],["chart","📈 Elo Chart"],["h2h","⚔️ H2H Matrix"],["leaders","🎖 Stat Leaders"],["matchup","🔍 Matchup Explorer"],["history","📅 Tournament History"],["streaks","🔥 Win Streaks"],["formats","🎯 By Format"]];

  return (
    <div className="fade-in">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Power Rankings</div>
        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
          Formula: Elo (60%) + Placement Pts (30%) + Cup Diff (10%) · {rankedTourneys.length} ranked pro event{rankedTourneys.length!==1?"s":""} counted
        </div>
      </div>

      {/* ── Formula cards ───────────────────────────────────────────────── */}
      <div className="resp-grid-3" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
        {[
          ["Elo (60%)", "Match-by-match dynamic rating. Strong wins earn more. Base: 1200.", "var(--gold)"],
          ["Placements (30%)", "1st=100 · 2nd=70 · 3rd=50 · Top 6=30 · Entry=10 pts", "#5b8dee"],
          ["Cup Diff (10%)", "Net cups across all matches. OT = ±1 regardless of score.", "var(--green)"],
        ].map(([title,desc,color]) => (
          <div key={title} style={{ ...css.card, padding:14, borderTop:`3px solid ${color}` }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color, marginBottom:5 }}>{title}</div>
            <div style={{ fontSize:11, color:"var(--muted)", lineHeight:1.6 }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", gap:0, marginBottom:20, borderBottom:"1px solid var(--navy-border)", overflowX:"auto" }}>
        {TABS.map(([v,l]) => (
          <div key={v} onClick={()=>setTab(v)} style={{ padding:"8px 18px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:1.5, textTransform:"uppercase", borderBottom:tab===v?"2px solid var(--gold)":"2px solid transparent", color:tab===v?"var(--white)":"var(--muted)", marginBottom:-1, whiteSpace:"nowrap" }}>{l}</div>
        ))}
      </div>

      {/* ══ LEADERBOARD ════════════════════════════════════════════════ */}
      {tab === "leaderboard" && (
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          <div className="resp-table">
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:540 }}>
              <thead>
                <tr style={{ borderBottom:"1px solid var(--navy-border)" }}>
                  {["#","Tier","Player","PR","Elo","W–L","Win %","Cup ±","Form","Streak"].map(h => (
                    <th key={h} style={{ ...css.th, textAlign: h==="Player"?"left":"center" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ranked.map((p, i) => {
                  const pr     = calcPowerRating(p);
                  const tier   = getTier(pr);
                  const wr     = (p.wins+p.losses)>0 ? Math.round(p.wins/(p.wins+p.losses)*100) : 0;
                  const form5  = [...(p.matchHistory||[])].reverse().slice(0,5).map(m=>m.won);
                  const streak = currentStreak(p);
                  const medal  = i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":null;

                  // Mini Elo sparkline (last 8 matches, inline SVG)
                  const sparks = (p.matchHistory||[]).slice(-8).map(m=>m.eloAfter||p.elo);
                  const sMin = Math.min(...sparks)-10, sMax = Math.max(...sparks)+10;
                  const sH=20, sW=52;
                  const sparkPath = sparks.length>1
                    ? sparks.map((v,si)=>`${si===0?"M":"L"}${Math.round(si*(sW/(sparks.length-1)))},${Math.round(sH-((v-sMin)/(sMax-sMin||1))*sH)}`).join(" ")
                    : null;

                  return (
                    <tr key={p.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background: i===0?"rgba(201,168,76,0.04)":"transparent" }}>
                      {/* Rank */}
                      <td style={{ ...css.td, width:36, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:medal||"var(--muted)" }}>
                        {i===0?"🥇":i===1?"🥈":i===2?"🥉":i+1}
                      </td>
                      {/* Tier + progress to next */}
                      <td style={{ ...css.td, textAlign:"center", width:52 }}>
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                          <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:26, height:26, borderRadius:4, background:tier.bg, border:`1px solid ${tier.color}44`, fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:tier.color, fontWeight:700 }}>{tier.label}</div>
                          {(() => {
                            const pct = maxPR > 0 ? pr/maxPR : 0;
                            // progress within tier
                            const tierBounds = [{min:0,max:0.55},{min:0.55,max:0.75},{min:0.75,max:0.90},{min:0.90,max:1.0}];
                            const curTierBound = tierBounds.find(t=>pct<t.max&&pct>=t.min) || tierBounds[3];
                            const tierPct = curTierBound.max===curTierBound.min ? 100 :
                              Math.round(((pct-curTierBound.min)/(curTierBound.max-curTierBound.min))*100);
                            return (
                              <div title={`${tierPct}% through ${tier.label} tier`} style={{ width:26, height:3, background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${tierPct}%`, background:tier.color, borderRadius:2 }} />
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      {/* Name */}
                      <td style={css.td}>
                        <div style={{ fontWeight:700, fontSize:13, color:medal||"var(--white)" }}>{p.name}</div>
                        {p.nick && <div style={{ fontSize:11, color:"var(--gold)", fontStyle:"italic" }}>"{p.nick}"</div>}
                      </td>
                      {/* PR with mini sparkline */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:medal||"var(--gold-light)", lineHeight:1 }}>{pr}</div>
                          {sparkPath && (
                            <svg width={sW} height={sH} style={{ flexShrink:0 }}>
                              <path d={sparkPath} fill="none" stroke={medal||"var(--gold)"} strokeWidth="1.5" strokeLinejoin="round" opacity="0.7" />
                            </svg>
                          )}
                        </div>
                      </td>
                      {/* Elo */}
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--muted)" }}>{p.elo}</td>
                      {/* W-L */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <span style={{ color:"var(--green)", fontFamily:"'Bebas Neue',sans-serif", fontSize:18 }}>{p.wins}</span>
                        <span style={{ color:"var(--muted)", margin:"0 3px", fontSize:12 }}>–</span>
                        <span style={{ color:"var(--red)", fontFamily:"'Bebas Neue',sans-serif", fontSize:18 }}>{p.losses}</span>
                      </td>
                      {/* Win % */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:wr>=50?"var(--green)":"var(--muted)" }}>{wr}%</span>
                          <div style={{ width:40, height:4, background:"var(--navy-border)", borderRadius:2, overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${wr}%`, background:wr>=50?"var(--green)":"var(--red)", borderRadius:2 }} />
                          </div>
                        </div>
                      </td>
                      {/* Cup Diff */}
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:p.cupDiff>=0?"var(--green)":"var(--red)" }}>
                        {p.cupDiff>=0?"+":""}{p.cupDiff}
                      </td>
                      {/* Form dots */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        <div style={{ display:"flex", gap:3, justifyContent:"center" }}>
                          {form5.length > 0
                            ? form5.map((w,fi) => <div key={fi} style={{ width:10, height:10, borderRadius:2, background:w?"var(--green)":"var(--red)" }} />)
                            : <span style={{ color:"var(--muted)", fontSize:11 }}>—</span>
                          }
                        </div>
                      </td>
                      {/* Streak */}
                      <td style={{ ...css.td, textAlign:"center" }}>
                        {streak.n >= 2
                          ? <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:streak.type==="W"?"var(--green)":"var(--red)" }}>{streak.n}{streak.type}</span>
                          : <span style={{ color:"var(--muted)", fontSize:11 }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ ELO CHART ══════════════════════════════════════════════════ */}
      {tab === "chart" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Controls */}
          <div style={{ display:"flex", gap:12, flexWrap:"wrap", alignItems:"center" }}>
            <div style={{ fontSize:12, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>SHOW LAST</div>
            <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
              {[10,20,30,"All"].map(n => {
                const val = n==="All" ? 999 : n;
                return (
                  <div key={n} onClick={()=>setChartWindow(val)} style={{ padding:"5px 12px", cursor:"pointer", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, background:chartWindow===val?"var(--gold)":"transparent", color:chartWindow===val?"var(--navy)":"var(--muted)", transition:"all 0.15s" }}>{n}</div>
                );
              })}
            </div>
            <div style={{ fontSize:11, color:"var(--muted)" }}>matches per player</div>
          </div>

          {/* Player toggles */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {ranked.map((p, i) => {
              const color = CHART_COLORS[i % CHART_COLORS.length];
              const active = activeChartIds.has(p.id);
              return (
                <div key={p.id} onClick={() => {
                  const next = new Set(activeChartIds);
                  if (next.has(p.id)) { if (next.size > 1) next.delete(p.id); }
                  else next.add(p.id);
                  setChartPlayers(next);
                }} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:4, cursor:"pointer", border:`1px solid ${active?color+"88":"var(--navy-border)"}`, background:active?`${color}14`:"transparent", transition:"all 0.15s", userSelect:"none" }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:active?color:"var(--navy-border)" }} />
                  <span style={{ fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:active?"var(--white)":"var(--muted)" }}>{p.name}</span>
                </div>
              );
            })}
          </div>

          {/* Chart */}
          <div style={{ ...css.card, padding:20, overflow:"hidden" }}>
            {chartSeries.length === 0 ? (
              <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>No match history to chart yet</div>
            ) : (
              <>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>Elo Trajectory</div>
                <div style={{ overflowX:"auto" }}>
                  <svg width="100%" viewBox={`0 0 ${chartW} ${chartH+40}`} style={{ display:"block", minWidth:300 }}>
                    {/* Y axis labels */}
                    {[0,0.25,0.5,0.75,1].map(f => {
                      const elo = Math.round(eloMin + f * eloRange);
                      const y   = toChartY(elo);
                      return (
                        <g key={f}>
                          <line x1={0} y1={y} x2={chartW} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
                          <text x={2} y={y-3} fontSize={9} fill="rgba(255,255,255,0.3)" fontFamily="'Barlow Condensed',sans-serif">{elo}</text>
                        </g>
                      );
                    })}
                    {/* Series */}
                    {chartSeries.map(({ player:p, color, pts }) => {
                      const path = buildSVGPath(pts);
                      const last = pts[pts.length-1];
                      return (
                        <g key={p.id}>
                          <defs>
                            <linearGradient id={`grad_${p.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={color} stopOpacity="0.15" />
                              <stop offset="100%" stopColor={color} stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          {/* Area fill */}
                          <path d={`${path} L${toChartX(pts.length-1, pts.length)},${chartH} L0,${chartH} Z`} fill={`url(#grad_${p.id})`} />
                          {/* Line */}
                          <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
                          {/* First + last dot */}
                          <circle cx={toChartX(0, pts.length)} cy={toChartY(pts[0].y)} r={3} fill={color} />
                          <circle cx={toChartX(pts.length-1, pts.length)} cy={toChartY(last.y)} r={4} fill={color} />
                          {/* Label at end */}
                          <text x={toChartX(pts.length-1, pts.length)+7} y={toChartY(last.y)+4} fontSize={10} fill={color} fontFamily="'Barlow Condensed',sans-serif" fontWeight="700">{p.name.split(" ")[0]} {last.y}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                {/* Legend */}
                <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginTop:12, paddingTop:12, borderTop:"1px solid var(--navy-border)" }}>
                  {chartSeries.map(({ player:p, color, pts }) => {
                    const delta = pts[pts.length-1].y - pts[0].y;
                    return (
                      <div key={p.id} style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:14, height:3, background:color, borderRadius:2 }} />
                        <span style={{ fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--white)" }}>{p.name}</span>
                        <span style={{ fontSize:11, color:delta>=0?"var(--green)":"var(--red)" }}>{delta>=0?"+":""}{delta}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ H2H MATRIX ═════════════════════════════════════════════════ */}
      {tab === "h2h" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ fontSize:12, color:"var(--muted)" }}>
            Each cell shows <strong style={{ color:"var(--white)" }}>row player's W–L</strong> against column player. Green = winning record, red = losing, grey = even or no games. Click a cell for match details.
          </div>

          {/* Matrix */}
          <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
            <table style={{ borderCollapse:"collapse", minWidth: 80 + ranked.length*60 }}>
              <thead>
                <tr>
                  <th style={{ width:120, padding:"6px 8px", textAlign:"left", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", borderBottom:"1px solid var(--navy-border)" }}>Player ↓ vs →</th>
                  {ranked.map(p => (
                    <th key={p.id} style={{ width:60, padding:"4px 2px", textAlign:"center", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:0.5, borderBottom:"1px solid var(--navy-border)", maxWidth:60, overflow:"hidden" }}>
                      <div style={{ maxWidth:56, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", margin:"0 auto" }} title={p.name}>{p.nick||p.name.split(" ")[0]}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ranked.map(rowP => (
                  <tr key={rowP.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                    {/* Row label */}
                    <td style={{ padding:"6px 10px", borderRight:"1px solid var(--navy-border)" }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, whiteSpace:"nowrap" }}>{rowP.name}</div>
                      {rowP.nick && <div style={{ fontSize:10, color:"var(--gold)", fontStyle:"italic" }}>"{rowP.nick}"</div>}
                    </td>
                    {/* H2H cells */}
                    {ranked.map(colP => {
                      if (rowP.id === colP.id) return (
                        <td key={colP.id} style={{ width:60, height:40, textAlign:"center", background:"rgba(255,255,255,0.03)", borderRight:"1px solid rgba(255,255,255,0.03)" }}>
                          <span style={{ fontSize:16, opacity:0.3 }}>—</span>
                        </td>
                      );
                      const rec = h2hData[rowP.id]?.[colP.id] || { w:0, l:0 };
                      const total = rec.w + rec.l;
                      if (total === 0) return (
                        <td key={colP.id} style={{ width:60, height:40, textAlign:"center", borderRight:"1px solid rgba(255,255,255,0.03)" }}>
                          <span style={{ fontSize:11, color:"rgba(255,255,255,0.12)" }}>—</span>
                        </td>
                      );
                      const wr = rec.w / total;
                      const bg = wr > 0.5 ? `rgba(46,204,113,${0.08+wr*0.18})`
                        : wr < 0.5 ? `rgba(217,79,61,${0.08+(1-wr)*0.18})`
                        : "rgba(255,255,255,0.05)";
                      const textColor = wr > 0.5 ? "var(--green)" : wr < 0.5 ? "var(--red)" : "var(--muted)";
                      return (
                        <td key={colP.id} onClick={() => setH2hFocus({ a:rowP, b:colP, rec })}
                          style={{ width:60, height:40, textAlign:"center", background:bg, cursor:"pointer", borderRight:"1px solid rgba(255,255,255,0.04)", transition:"filter 0.1s" }}
                          onMouseEnter={e=>e.currentTarget.style.filter="brightness(1.4)"}
                          onMouseLeave={e=>e.currentTarget.style.filter="none"}>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:textColor, lineHeight:1 }}>{rec.w}–{rec.l}</div>
                          <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", fontFamily:"'Barlow Condensed',sans-serif" }}>{total}g</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* H2H detail panel */}
          {h2hFocus && (
            <div style={{ ...css.card, padding:0, overflow:"hidden", borderColor:"rgba(201,168,76,0.3)" }}>
              <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:15 }}>
                  {h2hFocus.a.name} <span style={{ color:"var(--muted)", margin:"0 8px" }}>vs</span> {h2hFocus.b.name}
                </div>
                <button onClick={()=>setH2hFocus(null)} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:16 }}>✕</button>
              </div>
              <div style={{ padding:"14px 16px", display:"flex", gap:20, alignItems:"center", flexWrap:"wrap" }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:42, color:"var(--green)", lineHeight:1 }}>{h2hFocus.rec.w}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>{h2hFocus.a.name.split(" ")[0]} Wins</div>
                </div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--muted)", fontSize:18 }}>–</div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:42, color:"var(--red)", lineHeight:1 }}>{h2hFocus.rec.l}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>{h2hFocus.b.name.split(" ")[0]} Wins</div>
                </div>
                <div style={{ flex:1, minWidth:120 }}>
                  <div style={{ height:8, background:"var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${Math.round(h2hFocus.rec.w/(h2hFocus.rec.w+h2hFocus.rec.l)*100)}%`, background:"var(--green)", borderRadius:4 }} />
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:4, fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                    <span>{Math.round(h2hFocus.rec.w/(h2hFocus.rec.w+h2hFocus.rec.l)*100)}%</span>
                    <span>{h2hFocus.rec.w+h2hFocus.rec.l} total matches</span>
                  </div>
                </div>
              </div>
              {/* Individual match list */}
              {h2hFocus.rec.matches?.length > 0 && (
                <div style={{ borderTop:"1px solid var(--navy-border)" }}>
                  {h2hFocus.rec.matches.slice().reverse().map((m,mi) => (
                    <div key={mi} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 16px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                      <Badge color={m.won?"green":"red"}>{m.won?"W":"L"}</Badge>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:m.won?"var(--gold)":"var(--muted)" }}>{m.myScore}</span>
                      <span style={{ color:"var(--muted)", fontSize:12 }}>–</span>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:!m.won?"var(--gold)":"var(--muted)" }}>{m.oppScore}</span>
                      {m.ot && <Badge color="gold">OT</Badge>}
                      <span style={{ fontSize:11, color:"var(--muted)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.tournament}</span>
                      <span style={{ fontSize:11, color:"var(--muted)", whiteSpace:"nowrap" }}>{m.date}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ STAT LEADERS ═══════════════════════════════════════════════ */}
      {tab === "leaders" && (
        <div>
          {qualified.length === 0 && (
            <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🎖</div>
              <div>Players need at least 3 matches to appear in stat leaders.</div>
            </div>
          )}
          <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:16 }}>
            {statCategories.map(cat => (
              <div key={cat.label} style={{ ...css.card, padding:0, overflow:"hidden" }}>
                <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"baseline", gap:8 }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1, textTransform:"uppercase" }}>{cat.label}</span>
                  <span style={{ fontSize:10, color:"var(--muted)" }}>{cat.sub}</span>
                </div>
                <div style={{ padding:"0 14px" }}>
                  {cat.rows.map((row, ri) => {
                    const medal = ri===0?"var(--gold)":ri===1?"#c0c0c0":ri===2?"#cd7f32":"var(--muted)";
                    return (
                      <div key={row.name+ri} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:medal, width:22, flexShrink:0, textAlign:"center" }}>
                          {ri===0?"🥇":ri===1?"🥈":ri===2?"🥉":ri+1}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {row.name}{row.nick ? <span style={{ fontSize:10, color:"var(--gold)", fontStyle:"italic", marginLeft:5 }}>"{row.nick}"</span> : ""}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:3 }}>
                            <div style={{ flex:1, height:4, background:"var(--navy-border)", borderRadius:2, overflow:"hidden", maxWidth:80 }}>
                              <div style={{ height:"100%", width:`${Math.min(100,Math.round((row.bar||0)*100))}%`, background:row.color, borderRadius:2 }} />
                            </div>
                            <div style={{ fontSize:10, color:"var(--muted)", whiteSpace:"nowrap" }}>{row.sub}</div>
                          </div>
                        </div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:row.color, flexShrink:0, minWidth:40, textAlign:"right" }}>{row.val}</div>
                      </div>
                    );
                  })}
                  {cat.rows.length === 0 && (
                    <div style={{ padding:"16px 0", textAlign:"center", fontSize:12, color:"var(--muted)" }}>Not enough data yet</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MATCHUP EXPLORER TAB ─────────────────────────────────────────────── */}
      {tab === "matchup" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Player pickers */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:12, alignItems:"end" }}>
            {/* Player A */}
            <div>
              <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Player / Team A</div>
              <input style={css.input} placeholder="Search name…" value={matchupSearch.a}
                onChange={e=>{ setMatchupSearch(s=>({...s,a:e.target.value})); setMatchupA(null); }} />
              {matchupSearch.a && !matchupA && (
                <div style={{ ...css.card, padding:0, overflow:"hidden", marginTop:4, border:"1px solid var(--navy-border)", maxHeight:160, overflowY:"auto" }}>
                  {ranked.filter(p=>p.name.toLowerCase().includes(matchupSearch.a.toLowerCase())).slice(0,6).map((p,i)=>(
                    <div key={p.id} onClick={()=>{ setMatchupA(p.id); setMatchupSearch(s=>({...s,a:p.name})); }}
                      style={{ padding:"8px 14px", cursor:"pointer", borderBottom:i<5?"1px solid rgba(255,255,255,0.04)":"none", fontSize:13, fontWeight:600 }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{p.name}</div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--gold)", paddingBottom:8 }}>vs</div>
            {/* Player B */}
            <div>
              <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Player / Team B</div>
              <input style={css.input} placeholder="Search name…" value={matchupSearch.b}
                onChange={e=>{ setMatchupSearch(s=>({...s,b:e.target.value})); setMatchupB(null); }} />
              {matchupSearch.b && !matchupB && (
                <div style={{ ...css.card, padding:0, overflow:"hidden", marginTop:4, border:"1px solid var(--navy-border)", maxHeight:160, overflowY:"auto" }}>
                  {ranked.filter(p=>p.name.toLowerCase().includes(matchupSearch.b.toLowerCase())).slice(0,6).map((p,i)=>(
                    <div key={p.id} onClick={()=>{ setMatchupB(p.id); setMatchupSearch(s=>({...s,b:p.name})); }}
                      style={{ padding:"8px 14px", cursor:"pointer", borderBottom:i<5?"1px solid rgba(255,255,255,0.04)":"none", fontSize:13, fontWeight:600 }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{p.name}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Matchup analysis */}
          {matchupA && matchupB && (() => {
            const pA = ranked.find(p=>p.id===matchupA);
            const pB = ranked.find(p=>p.id===matchupB);
            if (!pA || !pB) return null;

            const aVsB = (pA.matchHistory||[]).filter(m=>m.opponent===pB.name);
            const bVsA = (pB.matchHistory||[]).filter(m=>m.opponent===pA.name);
            const aWins = aVsB.filter(m=>m.won).length;
            const bWins = aVsB.filter(m=>!m.won).length;
            const total = aVsB.length;

            const prA = calcPowerRating(pA), prB = calcPowerRating(pB);
            const tierA = getTier(prA), tierB = getTier(pB);

            const aStreak = currentStreak(pA), bStreak = currentStreak(pB);

            return (
              <>
                {/* Head-to-head banner */}
                <div style={{ ...css.card, padding:"20px 24px", background:"linear-gradient(135deg,rgba(201,168,76,0.05),rgba(91,141,238,0.05))", border:"1px solid rgba(201,168,76,0.2)" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:16, alignItems:"center", textAlign:"center" }}>
                    {/* Player A */}
                    <div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:"var(--gold)", letterSpacing:2, marginBottom:4 }}>{pA.name}</div>
                      <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:8 }}>
                        <span style={{ padding:"2px 8px", borderRadius:3, background:tierA.bg, color:tierA.color, fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>Tier {tierA.label}</span>
                      </div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:56, color:"var(--gold)", lineHeight:1 }}>{aWins}</div>
                      <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2 }}>WINS</div>
                    </div>
                    {/* Record */}
                    <div>
                      <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Head-to-Head</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--muted)" }}>{total} match{total!==1?"es":""}</div>
                      {total === 0 && <div style={{ fontSize:12, color:"var(--muted)", marginTop:8 }}>Never faced</div>}
                      {total > 0 && (
                        <div style={{ marginTop:8, height:8, borderRadius:4, overflow:"hidden", display:"flex" }}>
                          <div style={{ flex:aWins, background:"var(--gold)", transition:"flex 0.3s" }} />
                          <div style={{ flex:bWins, background:"#5b8dee", transition:"flex 0.3s" }} />
                        </div>
                      )}
                    </div>
                    {/* Player B */}
                    <div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:"#5b8dee", letterSpacing:2, marginBottom:4 }}>{pB.name}</div>
                      <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:8 }}>
                        <span style={{ padding:"2px 8px", borderRadius:3, background:getTier(prB).bg, color:getTier(prB).color, fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>Tier {getTier(prB).label}</span>
                      </div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:56, color:"#5b8dee", lineHeight:1 }}>{bWins}</div>
                      <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2 }}>WINS</div>
                    </div>
                  </div>
                </div>

                {/* Stat comparison grid */}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>
                    Stat Comparison
                  </div>
                  {[
                    ["Power Rating", prA, prB, Math.max(prA,prB)*1.1, true],
                    ["Elo", pA.elo||1200, pB.elo||1200, Math.max(pA.elo||1200,pB.elo||1200)*1.1, true],
                    ["Win Rate", Math.round((pA.wins||0)/Math.max((pA.wins||0)+(pA.losses||0),1)*100), Math.round((pB.wins||0)/Math.max((pB.wins||0)+(pB.losses||0),1)*100), 100, false, "%"],
                    ["Cup Diff", Math.max(0,pA.cupDiff||0), Math.max(0,pB.cupDiff||0), Math.max(Math.abs(pA.cupDiff||0),Math.abs(pB.cupDiff||0))*1.2+1, false, ""],
                    ["Tournaments", pA.tournaments||0, pB.tournaments||0, Math.max(pA.tournaments||0,pB.tournaments||0)*1.2+1, false],
                    ["Current Streak", aStreak.n, bStreak.n, Math.max(aStreak.n,bStreak.n)+1, false, aStreak.type||"", bStreak.type||""],
                  ].map(([label, vA, vB, maxVal, higher, suffA, suffB])=>{
                    const maxV = maxVal || 1;
                    const wA = Math.max(5, Math.round(vA/maxV*100));
                    const wB = Math.max(5, Math.round(vB/maxV*100));
                    const aLeads = vA > vB, tied = vA === vB;
                    return (
                      <div key={label} style={{ padding:"10px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", display:"grid", gridTemplateColumns:"1fr 120px 1fr", gap:10, alignItems:"center" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                          <div style={{ fontSize:11, fontWeight:700 }}>{vA}{suffA||""}</div>
                          <div style={{ height:6, borderRadius:3, overflow:"hidden", background:"rgba(255,255,255,0.06)" }}>
                            <div style={{ height:"100%", width:`${wA}%`, background:aLeads?"var(--gold)":tied?"var(--muted)":"rgba(201,168,76,0.3)", borderRadius:3, transition:"width 0.3s" }} />
                          </div>
                        </div>
                        <div style={{ textAlign:"center", fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{label}</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:3, alignItems:"flex-end" }}>
                          <div style={{ fontSize:11, fontWeight:700 }}>{vB}{suffB||""}</div>
                          <div style={{ height:6, borderRadius:3, overflow:"hidden", background:"rgba(255,255,255,0.06)", width:"100%" }}>
                            <div style={{ height:"100%", width:`${wB}%`, background:!aLeads&&!tied?"#5b8dee":"rgba(91,141,238,0.3)", borderRadius:3, float:"right", transition:"width 0.3s" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Match history between them */}
                {aVsB.length > 0 && (
                  <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                    <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>
                      Match History ({aVsB.length})
                    </div>
                    {aVsB.slice(0,10).map((m,i)=>(
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", borderBottom:i<aVsB.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:m.won?"var(--gold)":"#5b8dee", flexShrink:0 }} />
                        <span style={{ flex:1, fontSize:12, fontWeight:600 }}>{m.won?pA.name:pB.name} <span style={{ color:"var(--muted)", fontWeight:400 }}>beat</span> {m.won?pB.name:pA.name}</span>
                        {m.score && <span style={{ fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{m.score}</span>}
                        {m.eloChange && <span style={{ fontSize:10, color:m.won?"var(--green)":"var(--red)" }}>{m.won?"+":""}{m.eloChange}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {!matchupA && !matchupB && (
            <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Matchup Explorer</div>
              <div style={{ fontSize:13 }}>Search two players above to see their head-to-head record and stat comparison</div>
            </div>
          )}
        </div>
      )}

      {/* ── TOURNAMENT HISTORY TAB ─────────────────────────────────────────────── */}
      {tab === "history" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Tournament list */}
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>Select Tournament</div>
            <div style={{ flex:1 }}>
              <select style={css.select} value={histTournId||""} onChange={e=>setHistTournId(e.target.value||null)}>
                <option value="">— All Tournaments —</option>
                {(tournaments||[]).filter(t=>t.bracket).map(t=>(
                  <option key={t.id} value={t.id}>{t.name} — {t.date||"No date"}</option>
                ))}
              </select>
            </div>
            <input style={{ ...css.input, maxWidth:200, fontSize:12 }} placeholder="🔍 Filter player…"
              value={histPlayerFilter} onChange={e=>setHistPlayerFilter(e.target.value)} />
          </div>

          {(() => {
            const targetTourneys = histTournId
              ? (tournaments||[]).filter(t=>t.id===histTournId)
              : (tournaments||[]).filter(t=>t.bracket);

            if (!targetTourneys.length) {
              return (
                <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
                  <div style={{ fontSize:36, marginBottom:12 }}>📅</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3 }}>No Tournament Data</div>
                  <div style={{ fontSize:13, marginTop:8 }}>Run some tournaments to see history here</div>
                </div>
              );
            }

            return targetTourneys.map(tourn => {
              const allM   = getAllMatchesFromTournament(tourn);
              const done   = allM.filter(m=>m.status==="complete"&&m.t1&&m.t2);
              const regs   = tourn.registrations || [];
              const fmt    = FORMATS.find(f=>f.v===tourn.format)?.l || tourn.format || "";

              // Build performance table
              const perfMap = {};
              done.forEach(m=>{
                const n1 = m.t1?.name, n2 = m.t2?.name;
                if (!n1||!n2) return;
                if (!perfMap[n1]) perfMap[n1]={ name:n1, w:0, l:0, cd:0, matches:[] };
                if (!perfMap[n2]) perfMap[n2]={ name:n2, w:0, l:0, cd:0, matches:[] };
                const wn = m.winner?.name;
                if (wn===n1){ perfMap[n1].w++; perfMap[n2].l++; }
                else if(wn===n2){ perfMap[n2].w++; perfMap[n1].l++; }
                const diff = (m.s1||0)-(m.s2||0);
                perfMap[n1].cd += diff; perfMap[n2].cd -= diff;
                perfMap[n1].matches.push(m); perfMap[n2].matches.push(m);
              });

              const perfRows = Object.values(perfMap)
                .filter(r=>!histPlayerFilter||r.name.toLowerCase().includes(histPlayerFilter.toLowerCase()))
                .sort((a,b)=>b.w-a.w||a.l-b.l||(b.cd-a.cd));

              const maxWins = perfRows[0]?.w || 1;

              return (
                <div key={tourn.id} style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  {/* Tournament header */}
                  <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                    <div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:2 }}>{tourn.name}</div>
                      <div style={{ fontSize:11, color:"var(--muted)", display:"flex", gap:10, marginTop:2, flexWrap:"wrap" }}>
                        {tourn.date && <span>📅 {tourn.date}</span>}
                        {fmt && <span>📋 {fmt}</span>}
                        <span>🏟 {regs.length} teams</span>
                        <span>⚽ {done.length} matches</span>
                      </div>
                    </div>
                    <div style={{ marginLeft:"auto" }}>
                      <Badge color={tourn.status==="completed"?"green":tourn.status==="active"?"gold":""}>{tourn.status?.toUpperCase()}</Badge>
                    </div>
                  </div>

                  {/* Performance bars */}
                  {perfRows.length > 0 ? (
                    <div>
                      {perfRows.slice(0,12).map((r,i)=>{
                        const medals = ["🥇","🥈","🥉"];
                        const barW = Math.round(r.w/maxWins*100);
                        return (
                          <div key={r.name} style={{ display:"grid", gridTemplateColumns:"24px 140px 1fr 50px 50px 48px", gap:8, alignItems:"center", padding:"8px 16px", borderBottom:i<perfRows.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                            <span style={{ fontSize:i<3?16:12, color:i<3?"var(--gold)":"var(--muted)" }}>{i<3?medals[i]:i+1}</span>
                            <span style={{ fontSize:12, fontWeight:i<3?700:400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}</span>
                            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                              <div style={{ flex:1, height:6, borderRadius:3, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${barW}%`, background:i===0?"var(--gold)":i===1?"rgba(201,168,76,0.7)":i===2?"rgba(205,127,50,0.8)":"rgba(91,141,238,0.5)", borderRadius:3, transition:"width 0.4s" }} />
                              </div>
                            </div>
                            <span style={{ fontSize:12, fontWeight:700, color:"var(--green)", textAlign:"right" }}>{r.w}W</span>
                            <span style={{ fontSize:12, color:"var(--muted)", textAlign:"right" }}>{r.l}L</span>
                            <span style={{ fontSize:11, color:r.cd>0?"var(--green)":r.cd<0?"var(--red)":"var(--muted)", textAlign:"right" }}>
                              {r.cd>0?"+":""}{r.cd}
                            </span>
                          </div>
                        );
                      })}
                      {perfRows.length > 12 && (
                        <div style={{ padding:"8px 16px", fontSize:11, color:"var(--muted)", textAlign:"center" }}>+{perfRows.length-12} more teams</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ padding:"24px 16px", textAlign:"center", color:"var(--muted)", fontSize:12 }}>No match data</div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── WIN STREAKS TAB ──────────────────────────────────────────────── */}
      {tab==="streaks" && (() => {
        // Compute current and best win streaks for each player
        const playerStreaks = (players||[]).map(p => {
          const hist = [...(p.matchHistory||[])].sort((a,b)=>(a.ts||0)-(b.ts||0));
          let cur=0, best=0, tmp=0;
          hist.forEach(m => {
            if (m.won) { tmp++; cur=tmp; best=Math.max(best,tmp); }
            else { tmp=0; cur=0; }
          });
          // Count current streak from end
          let curStreak=0;
          for (let i=hist.length-1;i>=0;i--) {
            if (hist[i].won) curStreak++; else break;
          }
          return { ...p, currentStreak:curStreak, bestStreak:best,
            winPct: p.wins+p.losses>0 ? Math.round(p.wins/(p.wins+p.losses)*100) : 0 };
        });
        const byCurrent = [...playerStreaks].sort((a,b)=>b.currentStreak-a.currentStreak).filter(p=>p.wins>0);
        const byBest    = [...playerStreaks].sort((a,b)=>b.bestStreak-a.bestStreak).filter(p=>p.wins>0);
        const maxStreak = Math.max(...byCurrent.map(p=>p.currentStreak), 1);

        return (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Hot right now */}
            {byCurrent.filter(p=>p.currentStreak>=2).length>0 && (
              <div style={{...css.card,padding:"12px 16px",borderColor:"rgba(217,79,61,0.4)",background:"rgba(217,79,61,0.04)"}}>
                <div style={{fontSize:10,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>🔥 Currently Hot</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {byCurrent.filter(p=>p.currentStreak>=2).slice(0,6).map(p=>(
                    <div key={p.id} style={{padding:"6px 12px",background:"rgba(217,79,61,0.1)",border:"1px solid rgba(217,79,61,0.35)",borderRadius:16,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:"var(--red)"}}>{p.currentStreak}</span>
                      <span style={{fontSize:12,fontWeight:600}}>{p.name}</span>
                      <span style={{fontSize:10,color:"var(--muted)"}}>streak</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current streaks table */}
            <div style={{...css.card,padding:0,overflow:"hidden"}}>
              <div style={{padding:"10px 16px",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Current Win Streaks</div>
              {byCurrent.slice(0,15).map((p,i)=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px",borderBottom:i<14?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--muted)",width:22,textAlign:"center"}}>{i+1}</span>
                  <span style={{flex:1,fontSize:13,fontWeight:600}}>{p.name}</span>
                  <div style={{flex:2,height:8,background:"rgba(255,255,255,0.05)",borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",background:p.currentStreak>=5?"var(--red)":p.currentStreak>=3?"var(--gold)":"var(--green)",width:`${Math.round(p.currentStreak/maxStreak*100)}%`,borderRadius:4,transition:"width 0.4s"}}/>
                  </div>
                  <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:p.currentStreak>=5?"var(--red)":p.currentStreak>=3?"var(--gold)":"var(--white)",minWidth:32,textAlign:"right"}}>{p.currentStreak}</span>
                  <span style={{fontSize:10,color:"var(--muted)",minWidth:46,textAlign:"right"}}>best: {p.bestStreak}</span>
                </div>
              ))}
            </div>

            {/* All-time best streaks */}
            <div style={{...css.card,padding:0,overflow:"hidden"}}>
              <div style={{padding:"10px 16px",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>All-Time Best Streaks</div>
              {byBest.slice(0,10).map((p,i)=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px",borderBottom:i<9?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":"var(--muted)",width:22,textAlign:"center"}}>{i+1}</span>
                  <span style={{flex:1,fontSize:13,fontWeight:600}}>{p.name}</span>
                  <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:24,color:"var(--gold)"}}>{p.bestStreak}</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>win streak</span>
                  <span style={{fontSize:11,color:"var(--green)",minWidth:42,textAlign:"right"}}>{p.winPct}% WR</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── BY FORMAT TAB ───────────────────────────────────────────────────── */}
      {tab==="formats" && (() => {
        const fmtStats = FORMATS.map(fmt => {
          const fmtTourneys = (tournaments||[]).filter(t=>t.format===fmt.v&&t.status==="completed");
          const allM = fmtTourneys.flatMap(t=>getAllMatchesFromTournament(t)).filter(m=>m.status==="complete"&&m.t1&&m.t2);
          // Best player in this format
          const playerWins = {};
          allM.forEach(m => {
            if (m.winner?.id) playerWins[m.winner.id] = (playerWins[m.winner.id]||0)+1;
          });
          const topId = Object.keys(playerWins).sort((a,b)=>playerWins[b]-playerWins[a])[0];
          const topPlayer = topId ? (players||[]).find(p=>p.id===topId) : null;
          const avgScore = allM.length ? Math.round(allM.reduce((s,m)=>s+(m.s1||0)+(m.s2||0),0)/allM.length) : 0;
          const otPct = allM.length ? Math.round(allM.filter(m=>m.ot).length/allM.length*100) : 0;
          return { fmt, events:fmtTourneys.length, matches:allM.length, avgScore, otPct, topPlayer, topWins:playerWins[topId]||0 };
        }).filter(r=>r.events>0);

        if (!fmtStats.length) return (
          <div style={{textAlign:"center",padding:"48px 20px",color:"var(--muted)"}}>
            <div style={{fontSize:36,marginBottom:8}}>🎯</div>
            <div>No completed tournaments yet</div>
          </div>
        );

        return (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
            {fmtStats.map(({fmt,events,matches,avgScore,otPct,topPlayer,topWins})=>(
              <div key={fmt.v} style={{...css.card,padding:"14px 16px"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>{fmt.l}</span>
                  <Badge>{events} event{events!==1?"s":""}</Badge>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                  {[[matches,"Matches","var(--white)"],[avgScore,"Avg Cups","var(--gold)"],[otPct+"%","OT Rate","var(--green)"],[topWins,"Top Wins","#5b8dee"]].map(([v,l,c])=>(
                    <div key={l} style={{textAlign:"center",padding:"6px",background:"rgba(255,255,255,0.03)",borderRadius:4}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:c,lineHeight:1}}>{v}</div>
                      <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{l}</div>
                    </div>
                  ))}
                </div>
                {topPlayer && (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4}}>
                    <span style={{fontSize:14}}>🏆</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--gold)"}}>{topPlayer.name}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>Format king · {topWins} match wins</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

    </div>
  );
}


// ── HISTORY ──────────────────────────────────────────────────────────────────
function HistoryPage({ tournaments, players }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [view,      setView]      = useState("events");   // events | feed | records
  const [search,    setSearch]    = useState("");
  const [filterEvt, setFilterEvt] = useState("");         // tournament id filter
  const [filterRes, setFilterRes] = useState("all");      // all | wins | losses | ot
  const [filterBkt, setFilterBkt] = useState("all");      // all | W | L | GF | P
  const [expanded,  setExpanded]  = useState({});         // { [tournId]: bool }
  const [timelinePlayer, setTimelinePlayer] = useState(null);  // player name for timeline
  const [venueBreakFilter, setVenueBreakFilter] = useState(""); // venue filter for splits
  const [formatBreakFilter, setFormatBreakFilter] = useState(""); // format for splits

  const completed = tournaments.filter(t => t.status === "completed")
    .sort((a,b) => new Date(b.date||"0") - new Date(a.date||"0"));

  // Collect ALL completed matches across ALL tournaments
  const allMatches = completed.flatMap(t =>
    getAllMatchesFromTournament(t).filter(m => m.status === "complete" && m.t1 && m.t2)
  );

  // ── Global stats ────────────────────────────────────────────────────────
  const totalCups   = allMatches.reduce((s,m) => s+(m.s1||0)+(m.s2||0), 0);
  const otMatches   = allMatches.filter(m => m.ot);
  const maxDiff     = allMatches.reduce((best,m) => {
    const d = Math.abs((m.s1||0)-(m.s2||0));
    return d > (best?.diff||0) ? { diff:d, match:m } : best;
  }, null);
  const minDiff     = allMatches.filter(m=>!m.ot).reduce((best,m) => {
    const d = Math.abs((m.s1||0)-(m.s2||0));
    return (best===null || d < best.diff) ? { diff:d, match:m } : best;
  }, null);
  const highScore   = allMatches.reduce((best,m) => {
    const top = Math.max(m.s1||0, m.s2||0);
    return top > (best?.score||0) ? { score:top, match:m } : best;
  }, null);

  // Upset tracker: higher-seeded team losing (if seeds exist), else just biggest margin wins for lower-ranked
  const upsets = allMatches.filter(m =>
    m.t1?.seed && m.t2?.seed && m.winner &&
    ((m.winner.name===m.t1.name && m.t1.seed > m.t2.seed) ||
     (m.winner.name===m.t2.name && m.t2.seed > m.t1.seed))
  ).slice(0, 5);

  // Per-player match count from history
  const playerMatchCounts = {};
  players.forEach(p => { playerMatchCounts[p.id] = (p.matchHistory||[]).length; });
  const mostActive = [...players].sort((a,b) => (playerMatchCounts[b.id]||0)-(playerMatchCounts[a.id]||0)).slice(0,3);

  // ── Filtered feed ───────────────────────────────────────────────────────
  const feedMatches = allMatches.filter(m => {
    if (filterEvt && m._tournament?.id !== filterEvt) return false;
    if (filterBkt !== "all" && m._bracket !== filterBkt) return false;
    if (filterRes === "ot" && !m.ot) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(m.t1?.name||"").toLowerCase().includes(q) &&
          !(m.t2?.name||"").toLowerCase().includes(q) &&
          !(m._tournament?.name||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  function toggleExpand(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  // ── Match replay card ───────────────────────────────────────────────────
  function MatchReplay({ m, showEvent=true }) {
    const isW1 = m.winner?.name === m.t1?.name || m.winner?.id === m.t1?.id;
    const diff = m.ot ? "±1 OT" : `+${Math.abs((m.s1||0)-(m.s2||0))}`;
    const bktColor = m._bracket==="L"?"var(--red)":m._bracket==="GF"?"var(--gold)":"var(--green)";
    const bktLabel = m._bracket==="L"?"LB":m._bracket==="GF"?"GF":m._bracket==="P"?"Prelim":"WB";
    return (
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.04)", transition:"background 0.1s" }}
        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}
        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
        {/* Bracket badge */}
        <div style={{ flexShrink:0, width:28, textAlign:"center" }}>
          <span style={{ fontSize:9, fontFamily:"'Bebas Neue',sans-serif", color:bktColor, background:`${bktColor}22`, padding:"2px 5px", borderRadius:3, letterSpacing:1 }}>{bktLabel}</span>
        </div>
        {/* Teams + scores */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:isW1?700:400, fontSize:13, color:isW1?"var(--gold-light)":"var(--muted)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {isW1 && "✓ "}{m.t1?.name}
            </span>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:isW1?"var(--gold)":"var(--muted)", flexShrink:0 }}>{m.s1}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:2 }}>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:!isW1?700:400, fontSize:13, color:!isW1?"var(--gold-light)":"var(--muted)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {!isW1 && "✓ "}{m.t2?.name}
            </span>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:!isW1?"var(--gold)":"var(--muted)", flexShrink:0 }}>{m.s2}</span>
          </div>
        </div>
        {/* Meta */}
        <div style={{ flexShrink:0, textAlign:"right" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:"var(--muted)", letterSpacing:1 }}>{diff}</div>
          {m.ot && <div style={{ fontSize:9, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>⭐ OT</div>}
          {showEvent && <div style={{ fontSize:10, color:"var(--muted)", marginTop:2, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m._tournament?.name}</div>}
          {m._round && <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:1 }}>{m._round}</div>}
        </div>
      </div>
    );
  }

  // ── EVENTS VIEW ─────────────────────────────────────────────────────────
  function renderEvents() {
    if (completed.length === 0) return (
      <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, color:"var(--white)", marginBottom:6 }}>No Completed Events Yet</div>
        <div style={{ fontSize:13 }}>Completed tournaments will appear here.</div>
      </div>
    );

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {completed.map(t => {
          const tMatches = getAllMatchesFromTournament(t).filter(m => m.status==="complete" && m.t1 && m.t2);
          const isOpen = expanded[t.id];
          const fmtLabel = FORMATS.find(f=>f.v===t.format)?.l || t.format;

          // Build podium from bracket data
          const gfMatch = tMatches.find(m => m._bracket==="GF" && !m._round?.includes("Reset"));
          const champion  = t.winner || gfMatch?.winner?.name;
          const runnerUp  = gfMatch ? (gfMatch.winner?.name===gfMatch.t1?.name ? gfMatch.t2?.name : gfMatch.t1?.name) : null;

          return (
            <div key={t.id} style={{ ...css.card, padding:0, overflow:"hidden" }}>
              {/* Tournament header row */}
              <div onClick={()=>toggleExpand(t.id)} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", cursor:"pointer", userSelect:"none" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                {/* Expand chevron */}
                <span style={{ fontSize:12, color:"var(--muted)", flexShrink:0, transition:"transform 0.2s", transform:isOpen?"rotate(90deg)":"rotate(0)" }}>▶</span>
                {/* Event info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:15 }}>{t.name}</span>
                    <Badge color={t.type==="pro"?"gold":""}>{t.type}</Badge>
                    {t.ranked && <Badge color="blue">Ranked</Badge>}
                  </div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginTop:3 }}>
                    {t.date}{t.venue ? ` · ${t.venue}` : ""} · {fmtLabel} · {t.teamSize} · {t.cupCount} cups
                  </div>
                </div>
                {/* Champion */}
                {champion && (
                  <div style={{ flexShrink:0, textAlign:"right" }}>
                    <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>Champion</div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:"var(--gold-light)" }}>🏆 {champion}</div>
                    {runnerUp && <div style={{ fontSize:11, color:"var(--muted)" }}>🥈 {runnerUp}</div>}
                  </div>
                )}
                {/* Match count */}
                <div style={{ flexShrink:0, textAlign:"center", minWidth:44 }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:"var(--gold)", lineHeight:1 }}>{tMatches.length}</div>
                  <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>matches</div>
                </div>
              </div>

              {/* Expanded match list */}
              {isOpen && (
                <div style={{ borderTop:"1px solid var(--navy-border)" }}>
                  {/* Event summary strip */}
                  <div style={{ display:"flex", gap:0, borderBottom:"1px solid var(--navy-border)", overflowX:"auto" }}>
                    {[
                      ["Teams",   t.teamCount || "—",          "var(--muted)"],
                      ["Matches", tMatches.length,              "var(--gold)"],
                      ["OT",      tMatches.filter(m=>m.ot).length, "var(--gold)"],
                      ["Cups",    tMatches.reduce((s,m)=>s+(m.s1||0)+(m.s2||0),0), "var(--green)"],
                    ].map(([l,v,c])=>(
                      <div key={l} style={{ flex:1, padding:"10px 12px", textAlign:"center", borderRight:"1px solid var(--navy-border)" }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:c, lineHeight:1 }}>{v}</div>
                        <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {/* Match list grouped by round */}
                  {(() => {
                    const byRound = {};
                    tMatches.forEach(m => {
                      const key = `${m._bracket}__${m._round}`;
                      if (!byRound[key]) byRound[key] = { label: m._round, bracket: m._bracket, matches: [] };
                      byRound[key].matches.push(m);
                    });
                    return Object.values(byRound).map(({ label, bracket, matches: ms }) => {
                      const bktColor = bracket==="L"?"var(--red)":bracket==="GF"?"var(--gold)":bracket==="P"?"#5b8dee":"rgba(46,204,113,0.8)";
                      return (
                        <div key={label+bracket}>
                          <div style={{ padding:"6px 14px", background:"rgba(255,255,255,0.02)", borderBottom:"1px solid rgba(255,255,255,0.04)", display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:9, fontFamily:"'Bebas Neue',sans-serif", color:bktColor, background:`${bktColor}22`, padding:"1px 6px", borderRadius:2, letterSpacing:1.5 }}>
                              {bracket==="L"?"LB":bracket==="GF"?"GRAND FINAL":bracket==="P"?"PRELIM":"WB"}
                            </span>
                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:"var(--muted)", letterSpacing:1.5, textTransform:"uppercase" }}>{label}</span>
                            <span style={{ fontSize:10, color:"var(--muted)" }}>{ms.length} match{ms.length!==1?"es":""}</span>
                          </div>
                          {ms.map(m => <MatchReplay key={m.id} m={m} showEvent={false} />)}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── FEED VIEW ───────────────────────────────────────────────────────────
  function renderFeed() {
    return (
      <div>
        {/* Filters */}
        <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
          <input style={{ ...css.input, width:isMobile?140:200 }} placeholder="Search team or event…" value={search} onChange={e=>setSearch(e.target.value)} />
          <select style={{ ...css.select, flex:"0 0 auto" }} value={filterEvt} onChange={e=>setFilterEvt(e.target.value)}>
            <option value="">All Events</option>
            {completed.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
            {[["all","All"],["W","WB"],["L","LB"],["GF","GF"],["P","Prelim"]].map(([v,l])=>(
              <div key={v} onClick={()=>setFilterBkt(v)} style={{ padding:"6px 10px", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", background:filterBkt===v?"var(--gold)":"transparent", color:filterBkt===v?"var(--navy)":"var(--muted)", transition:"all 0.15s" }}>{l}</div>
            ))}
          </div>
          <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
            {[["all","All"],["ot","OT Only"]].map(([v,l])=>(
              <div key={v} onClick={()=>setFilterRes(v)} style={{ padding:"6px 10px", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", background:filterRes===v?"var(--gold)":"transparent", color:filterRes===v?"var(--navy)":"var(--muted)", transition:"all 0.15s" }}>{l}</div>
            ))}
          </div>
          <span style={{ fontSize:11, color:"var(--muted)" }}>{feedMatches.length} match{feedMatches.length!==1?"es":""}</span>
        </div>

        {feedMatches.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"var(--muted)", fontSize:13 }}>No matches match your filters</div>
        ) : (
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            {feedMatches.map(m => <MatchReplay key={m.id} m={m} showEvent={true} />)}
          </div>
        )}
      </div>
    );
  }

  // ── RECORDS VIEW ────────────────────────────────────────────────────────
  function renderRecords() {
    if (allMatches.length === 0) return (
      <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>📊</div>
        <div>No match data yet</div>
      </div>
    );

    // Cup diff distribution
    const diffBuckets = { "0-2":0, "3-5":0, "6-8":0, "9+":0 };
    allMatches.filter(m=>!m.ot).forEach(m => {
      const d = Math.abs((m.s1||0)-(m.s2||0));
      if (d<=2) diffBuckets["0-2"]++;
      else if (d<=5) diffBuckets["3-5"]++;
      else if (d<=8) diffBuckets["6-8"]++;
      else diffBuckets["9+"]++;
    });
    const diffMax = Math.max(...Object.values(diffBuckets), 1);

    // Most active teams (from match data)
    const teamCounts = {};
    allMatches.forEach(m => {
      [m.t1?.name, m.t2?.name].filter(Boolean).forEach(n => {
        teamCounts[n] = (teamCounts[n]||0) + 1;
      });
    });
    const topTeams = Object.entries(teamCounts).sort((a,b)=>b[1]-a[1]).slice(0,8);

    // Win rates for teams with 3+ matches
    const teamWins = {};
    allMatches.forEach(m => {
      const w = m.winner?.name;
      if (!w) return;
      const l = w===m.t1?.name ? m.t2?.name : m.t1?.name;
      if (w) { teamWins[w] = teamWins[w]||{w:0,l:0}; teamWins[w].w++; }
      if (l) { teamWins[l] = teamWins[l]||{w:0,l:0}; teamWins[l].l++; }
    });
    const teamWRList = Object.entries(teamWins)
      .filter(([,v])=>v.w+v.l>=3)
      .map(([name,v])=>({ name, ...v, wr:Math.round(v.w/(v.w+v.l)*100) }))
      .sort((a,b)=>b.wr-a.wr).slice(0,8);

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

        {/* Record highlights */}
        <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {/* Biggest win */}
          {maxDiff?.match && (
            <div style={{ ...css.card, padding:16 }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>💥 Biggest Margin</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"var(--gold)", lineHeight:1 }}>+{maxDiff.diff}</div>
              <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>
                <span style={{ color:"var(--white)", fontWeight:700 }}>{maxDiff.match.winner?.name}</span> def. {maxDiff.match.winner?.name===maxDiff.match.t1?.name?maxDiff.match.t2?.name:maxDiff.match.t1?.name}
              </div>
              <div style={{ fontSize:11, color:"var(--gold)", marginTop:2 }}>{maxDiff.match.s1} – {maxDiff.match.s2}</div>
              <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{maxDiff.match._tournament?.name}</div>
            </div>
          )}
          {/* Closest match */}
          {minDiff?.match && (
            <div style={{ ...css.card, padding:16 }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>⚔️ Closest Match</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"#5b8dee", lineHeight:1 }}>+{minDiff.diff}</div>
              <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>
                <span style={{ color:"var(--white)", fontWeight:700 }}>{minDiff.match.winner?.name}</span> def. {minDiff.match.winner?.name===minDiff.match.t1?.name?minDiff.match.t2?.name:minDiff.match.t1?.name}
              </div>
              <div style={{ fontSize:11, color:"#5b8dee", marginTop:2 }}>{minDiff.match.s1} – {minDiff.match.s2}</div>
              <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{minDiff.match._tournament?.name}</div>
            </div>
          )}
          {/* Highest score */}
          {highScore?.match && (
            <div style={{ ...css.card, padding:16 }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>🏆 Highest Score</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"var(--green)", lineHeight:1 }}>{highScore.score}</div>
              <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>
                {highScore.match.t1?.name} <span style={{ color:"var(--gold)" }}>{highScore.match.s1} – {highScore.match.s2}</span> {highScore.match.t2?.name}
              </div>
              <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{highScore.match._tournament?.name}</div>
            </div>
          )}
          {/* OT matches */}
          <div style={{ ...css.card, padding:16 }}>
            <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>⭐ Overtime Matches</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"var(--gold)", lineHeight:1 }}>{otMatches.length}</div>
            <div style={{ fontSize:12, color:"var(--muted)", marginTop:4 }}>
              {allMatches.length > 0 && `${Math.round(otMatches.length/allMatches.length*100)}% of all matches · ±1 cup diff`}
            </div>
            {otMatches.slice(0,3).map(m=>(
              <div key={m.id} style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>
                ⭐ {m.winner?.name} over {m.winner?.name===m.t1?.name?m.t2?.name:m.t1?.name}
              </div>
            ))}
          </div>
        </div>

        {/* Cup diff distribution */}
        <div style={{ ...css.card, padding:16 }}>
          <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Cup Margin Distribution</div>
          <div style={{ display:"flex", gap:12, alignItems:"flex-end", height:80 }}>
            {Object.entries(diffBuckets).map(([label, count]) => (
              <div key={label} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{count}</div>
                <div style={{ width:"100%", height:`${Math.max(4, Math.round(count/diffMax*60))}px`, background:"var(--gold)", borderRadius:"3px 3px 0 0", opacity:0.7, transition:"height 0.3s" }} />
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Win rates + most matches */}
        <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {/* Best win rates */}
          {teamWRList.length > 0 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)" }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1.5, textTransform:"uppercase" }}>📈 Best Win Rates</span>
                <span style={{ fontSize:10, color:"var(--muted)", marginLeft:6 }}>min 3 matches</span>
              </div>
              <div style={{ padding:"0 16px" }}>
                {teamWRList.map((row,i) => (
                  <div key={row.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":"var(--muted)", width:20, flexShrink:0 }}>{i+1}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.name}</div>
                      <div style={{ fontSize:10, color:"var(--muted)" }}>{row.w}W – {row.l}L</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                      <div style={{ width:50, height:5, background:"var(--navy-border)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${row.wr}%`, background:row.wr>=50?"var(--green)":"var(--red)", borderRadius:2 }} />
                      </div>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:row.wr>=50?"var(--green)":"var(--muted)", minWidth:36, textAlign:"right" }}>{row.wr}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Most matches played */}
          {topTeams.length > 0 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)" }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1.5, textTransform:"uppercase" }}>🎯 Most Active Teams</span>
              </div>
              <div style={{ padding:"0 16px" }}>
                {topTeams.map(([name, count], i) => {
                  const maxC = topTeams[0][1];
                  return (
                    <div key={name} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--muted)", width:20, flexShrink:0 }}>{i+1}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</div>
                        <div style={{ height:4, background:"var(--navy-border)", borderRadius:2, overflow:"hidden", marginTop:4 }}>
                          <div style={{ height:"100%", width:`${Math.round(count/maxC*100)}%`, background:"var(--gold)", borderRadius:2 }} />
                        </div>
                      </div>
                      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold)", flexShrink:0 }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Upsets */}
        {upsets.length > 0 && (
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1.5, textTransform:"uppercase" }}>😱 Upsets</span>
              <span style={{ fontSize:10, color:"var(--muted)", marginLeft:6 }}>Lower seed defeating higher seed</span>
            </div>
            {upsets.map(m => <MatchReplay key={m.id} m={m} showEvent={true} />)}
          </div>
        )}

        {/* Per-event summary table */}
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--navy-border)" }}>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1.5, textTransform:"uppercase" }}>Event Summary</span>
          </div>
          <div className="resp-table">
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:460 }}>
              <thead><tr>{["Event","Date","Format","Matches","Cups","OT","Champion"].map(h=><th key={h} style={css.th}>{h}</th>)}</tr></thead>
              <tbody>
                {completed.map(t => {
                  const tM = getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2);
                  return (
                    <tr key={t.id} onClick={()=>toggleExpand(t.id)} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", cursor:"pointer" }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={css.td}><span style={{ fontWeight:600 }}>{t.name}</span></td>
                      <td style={{ ...css.td, color:"var(--muted)", fontSize:12 }}>{t.date||"—"}</td>
                      <td style={{ ...css.td, fontSize:11 }}>{FORMATS.find(f=>f.v===t.format)?.l||"—"}</td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--gold)" }}>{tM.length}</td>
                      <td style={{ ...css.td, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--green)" }}>{tM.reduce((s,m)=>s+(m.s1||0)+(m.s2||0),0)}</td>
                      <td style={{ ...css.td, textAlign:"center", color:"var(--muted)" }}>{tM.filter(m=>m.ot).length}</td>
                      <td style={css.td}>{t.winner?<span style={{ color:"var(--gold-light)", fontWeight:600 }}>🏆 {t.winner}</span>:"—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const VIEWS = [["events","📋 Events"],["feed","🎯 Match Feed"],["records","📊 Records"],["timeline","📈 Timelines"],["splits","✂ Venue & Format Splits"]];

  return (
    <div className="fade-in">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3, marginBottom:4 }}>Tournament History</div>
        <div style={{ fontSize:12, color:"var(--muted)" }}>{completed.length} events · {allMatches.length} matches · {totalCups.toLocaleString()} cups made</div>
      </div>

      {/* ── Stat strip ─────────────────────────────────────────────────── */}
      <div className="resp-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        <StatCard num={completed.length}   label="Events"        color="var(--white)" />
        <StatCard num={allMatches.length}  label="Matches"       color="var(--gold)" />
        <StatCard num={otMatches.length}   label="Overtime"      color="var(--gold)" />
        <StatCard num={totalCups}          label="Cups Scored"   color="var(--green)" />
      </div>

      {/* ── View tabs ──────────────────────────────────────────────────── */}
      <div style={{ display:"flex", gap:0, marginBottom:20, borderBottom:"1px solid var(--navy-border)" }}>
        {VIEWS.map(([v,l])=>(
          <div key={v} onClick={()=>setView(v)} style={{ padding:"8px 18px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:1.5, textTransform:"uppercase", borderBottom:view===v?"2px solid var(--gold)":"2px solid transparent", color:view===v?"var(--white)":"var(--muted)", marginBottom:-1, transition:"all 0.15s", whiteSpace:"nowrap" }}>{l}</div>
        ))}
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      {view==="events"  && renderEvents()}
      {view==="feed"    && renderFeed()}
      {view==="records" && renderRecords()}

      {/* ── TIMELINE VIEW ─────────────────────────────────────────────────────── */}
      {view==="timeline" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Player selector */}
          <div style={{ ...css.card, padding:"12px 16px" }}>
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:8 }}>Select Player / Team</div>
            <input style={css.input} placeholder="Search name…" value={timelinePlayer||""}
              onChange={e=>setTimelinePlayer(e.target.value||null)} />
          </div>

          {(() => {
            const target = timelinePlayer?.toLowerCase();
            if (!target) return (
              <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
                <div style={{ fontSize:36, marginBottom:12 }}>📈</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Match Timeline</div>
                <div style={{ fontSize:13 }}>Search a player or team above to view their full match history</div>
              </div>
            );

            const playerMatches = allMatches.filter(m =>
              m.t1?.name?.toLowerCase().includes(target) ||
              m.t2?.name?.toLowerCase().includes(target)
            ).sort((a,b) => (a.tournDate||"").localeCompare(b.tournDate||""));

            if (!playerMatches.length) return (
              <div style={{ textAlign:"center", padding:"32px 20px", color:"var(--muted)", fontSize:13 }}>No matches found for "{timelinePlayer}"</div>
            );

            const isT1 = m => m.t1?.name?.toLowerCase().includes(target);
            const wins = playerMatches.filter(m => m.winner?.name?.toLowerCase().includes(target)).length;
            const losses = playerMatches.length - wins;
            const cupDiff = playerMatches.reduce((s,m) => {
              const myScore  = isT1(m) ? (m.s1||0) : (m.s2||0);
              const oppScore = isT1(m) ? (m.s2||0) : (m.s1||0);
              return s + myScore - oppScore;
            }, 0);

            return (
              <>
                {/* Summary banner */}
                <div style={{ ...css.card, padding:"14px 16px", display:"flex", gap:20, flexWrap:"wrap" }}>
                  {[
                    [playerMatches.length, "Matches"],
                    [wins, "Wins", "var(--green)"],
                    [losses, "Losses", "var(--red)"],
                    [`${Math.round(wins/playerMatches.length*100)}%`, "Win Rate", "var(--gold)"],
                    [cupDiff>0?"+"+cupDiff:cupDiff, "Cup Diff", cupDiff>0?"var(--green)":cupDiff<0?"var(--red)":"var(--muted)"],
                  ].map(([v,l,c="var(--white)"])=>(
                    <div key={l}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
                      <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>
                    Match History — Chronological
                  </div>
                  {playerMatches.map((m,i) => {
                    const won    = m.winner?.name?.toLowerCase().includes(target);
                    const myS    = isT1(m) ? m.s1 : m.s2;
                    const oppS   = isT1(m) ? m.s2 : m.s1;
                    const opp    = isT1(m) ? m.t2?.name : m.t1?.name;
                    const tourn  = completed.find(t => getAllMatchesFromTournament(t).some(x=>x.id===m.id));
                    return (
                      <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", borderBottom:i<playerMatches.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:won?"var(--green)":"var(--red)", flexShrink:0 }} />
                        <div style={{ width:32, fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:won?"var(--green)":"var(--red)" }}>{won?"W":"L"}</div>
                        <span style={{ flex:1, fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>vs <strong>{opp||"?"}</strong></span>
                        {myS!=null && oppS!=null && <span style={{ fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, color:"var(--muted)" }}>{myS}–{oppS}{m.ot?" OT":""}</span>}
                        {m._bracket && <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{m._bracket==="GF"?"GF":m._bracket==="W"?"WB":"LB"}</span>}
                        {tourn && <span style={{ fontSize:10, color:"rgba(255,255,255,0.2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120, textAlign:"right" }}>{tourn.name}</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Win rate trend — simple bar chart */}
                <div style={{ ...css.card, padding:"14px 16px" }}>
                  <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>Win Rate Trend (rolling 5)</div>
                  <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:60 }}>
                    {playerMatches.map((_,i) => {
                      if (i < 4) return null;
                      const window5 = playerMatches.slice(i-4, i+1);
                      const wr = window5.filter(m=>m.winner?.name?.toLowerCase().includes(target)).length / 5;
                      return (
                        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                          <div style={{ width:"100%", background:`rgba(${wr>0.6?"46,204,113":wr<0.4?"217,79,61":"201,168,76"},0.7)`, borderRadius:"2px 2px 0 0", height:`${Math.round(wr*50)+4}px`, transition:"height 0.3s" }} />
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                  <div style={{ fontSize:10, color:"var(--muted)", marginTop:4 }}>Each bar = win rate of last 5 matches</div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── VENUE & FORMAT SPLITS VIEW ─────────────────────────────────────────── */}
      {view==="splits" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {allMatches.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>✂</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3 }}>No Match Data</div>
            </div>
          ) : (() => {
            // ── Venue splits ──────────────────────────────────────────────────
            const venueMap = {};
            completed.forEach(t => {
              const venue = t.venue || "Unknown Venue";
              if (!venueMap[venue]) venueMap[venue] = { matches:0, ot:0, totalCups:0, blowouts:0, events:0 };
              const vMs = getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2);
              venueMap[venue].matches += vMs.length;
              venueMap[venue].ot      += vMs.filter(m=>m.ot).length;
              venueMap[venue].totalCups += vMs.reduce((s,m)=>(m.s1||0)+(m.s2||0)+s,0);
              venueMap[venue].blowouts  += vMs.filter(m=>Math.abs((m.s1||0)-(m.s2||0))>=7).length;
              venueMap[venue].events++;
            });
            const venues_list = Object.entries(venueMap).sort((a,b)=>b[1].matches-a[1].matches);

            // ── Format splits ─────────────────────────────────────────────────
            const fmtMap = {};
            completed.forEach(t => {
              const fmt = t.format || "unknown";
              if (!fmtMap[fmt]) fmtMap[fmt] = { matches:0, ot:0, events:0, avgDiff:0, diffs:[] };
              const fMs = getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2);
              fmtMap[fmt].matches += fMs.length;
              fmtMap[fmt].ot      += fMs.filter(m=>m.ot).length;
              fmtMap[fmt].events++;
              fMs.forEach(m => fmtMap[fmt].diffs.push(Math.abs((m.s1||0)-(m.s2||0))));
            });
            const fmts_list = Object.entries(fmtMap).sort((a,b)=>b[1].matches-a[1].matches);
            const maxVenueM = venues_list[0]?.[1].matches || 1;
            const maxFmtM   = fmts_list[0]?.[1].matches || 1;

            return (
              <>
                {/* Venue splits */}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)" }}>
                    By Venue ({venues_list.length})
                  </div>
                  {venues_list.map(([venue, stats], i) => (
                    <div key={venue} style={{ padding:"10px 16px", borderBottom:i<venues_list.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{venue}</div>
                        <div style={{ display:"flex", gap:14 }}>
                          {[
                            [stats.events,"events","var(--muted)"],
                            [stats.matches,"matches","var(--white)"],
                            [stats.ot,"OT","var(--gold)"],
                            [stats.blowouts,"blowouts","var(--red)"],
                            [stats.matches?Math.round(stats.totalCups/stats.matches*10)/10:"—","avg cups","var(--green)"],
                          ].map(([v,l,c])=>(
                            <div key={l} style={{ textAlign:"right" }}>
                              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:c, lineHeight:1 }}>{v}</div>
                              <div style={{ fontSize:9, color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>{l}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${Math.round(stats.matches/maxVenueM*100)}%`, background:"rgba(201,168,76,0.6)", borderRadius:2, transition:"width 0.4s" }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Format splits */}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"#5b8dee" }}>
                    By Format ({fmts_list.length})
                  </div>
                  {fmts_list.map(([fmt, stats], i) => {
                    const fmtLabel = FORMATS?.find(f=>f.v===fmt)?.l || fmt;
                    const avgDiff  = stats.diffs.length ? (stats.diffs.reduce((s,v)=>s+v,0)/stats.diffs.length).toFixed(1) : "—";
                    return (
                      <div key={fmt} style={{ padding:"10px 16px", borderBottom:i<fmts_list.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                          <div style={{ fontWeight:700, fontSize:13 }}>{fmtLabel}</div>
                          <div style={{ display:"flex", gap:14 }}>
                            {[
                              [stats.events,"events","var(--muted)"],
                              [stats.matches,"matches","var(--white)"],
                              [`${Math.round(stats.ot/stats.matches*100)}%`,"OT rate","var(--gold)"],
                              [avgDiff,"avg diff","#5b8dee"],
                            ].map(([v,l,c])=>(
                              <div key={l} style={{ textAlign:"right" }}>
                                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:c, lineHeight:1 }}>{v}</div>
                                <div style={{ fontSize:9, color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>{l}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.round(stats.matches/maxFmtM*100)}%`, background:"rgba(91,141,238,0.6)", borderRadius:2, transition:"width 0.4s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}


// ── ADMIN PANEL ───────────────────────────────────────────────────────────────
const ROLE_PERMISSIONS = {
  super_admin:          { label:"Super Admin",         color:"gold",   icon:"👑", desc:"Full access to all features, settings, and data", perms:["Create tournaments","Edit brackets","Override scores","Manage users","Change settings","View audit log","Export data","Manage venues","Clear data"] },
  tournament_director:  { label:"Tournament Director", color:"gold",   icon:"🎯", desc:"Run events end-to-end: bracket, seeding, check-in, scores", perms:["Create tournaments","Edit brackets","Override scores","View audit log","Export data"] },
  venue_host:           { label:"Venue Host",          color:"blue",   icon:"📍", desc:"Manage venue info and check in players at the door", perms:["Check in players","View registrations","Manage venues"] },
  scorekeeper:          { label:"Scorekeeper",         color:"",       icon:"📋", desc:"Enter match scores at assigned tables during events", perms:["Enter scores","View bracket","View match queue"] },
  player:               { label:"Player",              color:"",       icon:"🎱", desc:"Compete in events; view own stats and match history", perms:["View bracket","View own stats","Self-report scores"] },
};

const ACTION_META = {
  SCORE_OVERRIDE:    { color:"var(--red)",    icon:"✏️", label:"Score Override" },
  SETTINGS_CHANGED:  { color:"var(--gold)",   icon:"⚙️", label:"Settings Changed" },
  CLEAR_DATA:        { color:"var(--red)",    icon:"⚠️", label:"Data Cleared" },
  TOURNAMENT_CREATED:{ color:"var(--green)",  icon:"🏆", label:"Tournament Created" },
  PLAYER_ADDED:      { color:"#5b8dee",       icon:"👤", label:"Player Added" },
  ROLE_CHANGED:      { color:"var(--gold)",   icon:"🔑", label:"Role Changed" },
  BRACKET_GENERATED: { color:"var(--green)",  icon:"🎯", label:"Bracket Generated" },
  DISPUTE_RESOLVED:  { color:"var(--gold)",   icon:"⚖️", label:"Dispute Resolved" },
  SCORE_APPROVED:    { color:"var(--green)",  icon:"✅", label:"Score Approved" },
};

function getMeta(action) {
  const key = Object.keys(ACTION_META).find(k => action.includes(k));
  return ACTION_META[key] || { color:"var(--muted)", icon:"📌", label: action.replace(/_/g," ") };
}

function AdminPage({ tournaments, setTournaments, players, setPlayers, activeTournament, setActiveTournament, setPage, onClearData, auditLog, addAuditEntry, adminSettings, setAdminSettings }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [tab, setTab] = useState("overview");
  const [overrideTournId, setOverrideTournId] = useState(activeTournament?.id||"");
  const [overrideMatch, setOverrideMatch] = useState(null);
  const [ovS1, setOvS1] = useState(""); const [ovS2, setOvS2] = useState(""); const [ovOt, setOvOt] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ ...DEFAULT_ADMIN_SETTINGS, ...(adminSettings||{}) });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditExpanded, setAuditExpanded] = useState(new Set());
  const [auditPage, setAuditPage] = useState(0);
  const AUDIT_PAGE_SIZE = 25;

  const TABS = [["overview","📊 Overview"],["score_override","✏️ Score Override"],["roles","🔑 Roles"],["settings","⚙️ Settings"],["audit","📋 Audit Log"],["health","🏥 Health"],["system","🛠 System"]];
  const overrideTournament = tournaments.find(t=>t.id===overrideTournId);
  const [auditDateFilter, setAuditDateFilter] = useState("all"); // all | today | week

  function getTournamentMatches(t) {
    if (!t?.bracket) return [];
    const bkt = t.bracket;
    if (bkt.type==="de") return [
      ...(bkt.wb||[]).flatMap(r=>r.matches.map(m=>({...m,_round:r.name,_side:"W"}))),
      ...(bkt.lb||[]).flatMap(r=>r.matches.map(m=>({...m,_round:r.name,_side:"L"}))),
      ...(bkt.gf?[{...bkt.gf,_round:"Grand Final",_side:"GF"}]:[]),
    ];
    return (bkt.rounds||[]).flatMap(r=>(r.matches||[]).map(m=>({...m,_round:r.name,_side:"W"})));
  }

  function doScoreOverride() {
    const n1=parseInt(ovS1), n2=parseInt(ovS2);
    if (isNaN(n1)||isNaN(n2)||n1===n2||!overrideMatch) return;
    const { match } = overrideMatch;
    const winner = n1>n2 ? match.t1 : match.t2;
    const diff = ovOt ? (n1>n2?1:-1) : n1-n2;
    function patchM(matches) {
      return matches.map(m=>m.id===match.id ? {...m,s1:n1,s2:n2,winner,status:"complete",ot:ovOt,cupDiff:diff,pendingScore:null,_overridden:true} : m);
    }
    const bkt = overrideTournament.bracket;
    let newBkt;
    if (bkt.type==="de") {
      newBkt = { ...bkt, wb:(bkt.wb||[]).map(r=>({...r,matches:patchM(r.matches)})), lb:(bkt.lb||[]).map(r=>({...r,matches:patchM(r.matches)})) };
      if (bkt.gf?.id===match.id) newBkt.gf = {...bkt.gf,s1:n1,s2:n2,winner,status:"complete",ot:ovOt,cupDiff:diff,_overridden:true};
    } else {
      newBkt = { ...bkt, rounds:(bkt.rounds||[]).map(r=>({...r,matches:patchM(r.matches)})) };
    }
    const updated = {...overrideTournament, bracket:newBkt};
    setTournaments(prev=>prev.map(t=>t.id===updated.id?updated:t));
    if (activeTournament?.id===updated.id) setActiveTournament(updated);
    addAuditEntry("SCORE_OVERRIDE", `${match.t1?.name} vs ${match.t2?.name} → ${n1}–${n2}${ovOt?" (OT)":""} in ${overrideTournament.name}`);
    setOverrideMatch(null); setOvS1(""); setOvS2(""); setOvOt(false);
  }

  function saveSettings() {
    setAdminSettings(settingsDraft);
    addAuditEntry("SETTINGS_CHANGED", "Tournament defaults, ranking weights, and system settings updated");
    setSettingsSaved(true);
    setTimeout(()=>setSettingsSaved(false), 2200);
  }

  function draftPR(p) {
    const placePts = (p.placements||[]).reduce((s,pl)=>s+(pl===1?100:pl===2?70:pl===3?50:pl<=6?30:10),0);
    return Math.round((p.elo||1200)*(settingsDraft.eloWeight/100)+placePts*(settingsDraft.placementWeight/100)+Math.max(0,(p.cupDiff||0))*(settingsDraft.cupDiffWeight/100));
  }

  function exportAuditCSV() {
    const rows = [["ID","Timestamp","Action","Details","User"].join(",")];
    (auditLog||[]).forEach(e => rows.push([e.id,new Date(e.ts).toISOString(),e.action,`"${(e.details||"").replace(/"/g,'""')}"`,e.user].join(",")));
    const blob = new Blob([rows.join("\n")], {type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`mlcp-audit-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const totalW = settingsDraft.eloWeight + settingsDraft.placementWeight + settingsDraft.cupDiffWeight;

  // Audit log filtering + pagination
  const actionTypes = [...new Set((auditLog||[]).map(e=>e.action))].sort();
  const now_ms = Date.now();
  const filteredLog = (auditLog||[]).filter(e => {
    if (auditSearch && !(e.action+e.details+(e.user||"")).toLowerCase().includes(auditSearch.toLowerCase())) return false;
    if (auditActionFilter!=="all" && e.action!==auditActionFilter) return false;
    if (auditDateFilter==="today") { const d=new Date(e.ts); const t=new Date(); if(d.toDateString()!==t.toDateString()) return false; }
    if (auditDateFilter==="week" && now_ms-e.ts > 7*24*60*60*1000) return false;
    return true;
  });
  const pagedLog = filteredLog.slice(auditPage*AUDIT_PAGE_SIZE, (auditPage+1)*AUDIT_PAGE_SIZE);
  const totalPages = Math.ceil(filteredLog.length / AUDIT_PAGE_SIZE);

  // Group paged entries by date for day headers
  const auditByDay = pagedLog.reduce((acc, e) => {
    const dayKey = new Date(e.ts).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(e);
    return acc;
  }, {});

  // Overview stats
  const completedTourneys = tournaments.filter(t=>t.status==="completed");
  const activeTourneys    = tournaments.filter(t=>t.status==="active");
  const topPlayer         = [...players].sort((a,b)=>(b.elo||1200)-(a.elo||1200))[0];
  const disputed          = tournaments.flatMap(t=>{
    const bkt = t.bracket||{};
    const all = [...(bkt.wb||[]),...(bkt.lb||[])].flatMap(r=>r.matches||[]);
    return all.filter(m=>m.status==="disputed");
  });

  return (
    <div className="fade-in">
      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3,marginBottom:4 }}>Admin Panel</div>
      <div style={{ color:"var(--muted)",fontSize:13,marginBottom:16 }}>System configuration, score overrides, audit trail, and platform settings</div>

      <div style={{ display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid var(--navy-border)",overflowX:"auto" }}>
        {TABS.map(([v,l])=>(
          <div key={v} onClick={()=>setTab(v)} style={{ padding:"8px 16px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",borderBottom:tab===v?"2px solid var(--gold)":"2px solid transparent",color:tab===v?"var(--white)":"var(--muted)",marginBottom:-1,transition:"all 0.15s",whiteSpace:"nowrap" }}>
            {v==="audit" && (auditLog||[]).length>0 ? l.replace(")",` · ${(auditLog||[]).length})`) : l}
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────────── */}
      {tab==="overview" && (
        <div>
          {/* Stat row */}
          <div className="resp-grid-4" style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20 }}>
            {[
              [activeTourneys.length,      "Active Events",   "var(--green)"],
              [tournaments.filter(t=>t.status==="upcoming").length, "Upcoming", "#5b8dee"],
              [players.length,             "Players",         "var(--white)"],
              [(auditLog||[]).length,       "Audit Entries",   "var(--muted)"],
            ].map(([n,l,c])=>(<StatCard key={l} num={n} label={l} color={c} />))}
          </div>

          {disputed.length>0 && (
            <div style={{ padding:"10px 16px",background:"rgba(217,79,61,0.09)",border:"1px solid rgba(217,79,61,0.35)",borderRadius:6,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                <div style={{ width:8,height:8,borderRadius:"50%",background:"var(--red)",animation:"pulse 1s infinite" }} />
                <span style={{ fontSize:13,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{disputed.length} disputed match{disputed.length!==1?"es":""} need resolution</span>
              </div>
              <Btn sz="sm" v="danger" onClick={()=>setTab("score_override")}>Resolve →</Btn>
            </div>
          )}

          {/* Health checks */}
          {(()=>{
            const unfinalized = tournaments.filter(t=>t.status==="completed"&&!t.finalized);
            const overrideWarn = tournaments.filter(t=>{
              const bkt = t.bracket||{};
              const all = [...(bkt.wb||[]),...(bkt.lb||[])].flatMap(r=>r.matches||[]);
              const ovCount = all.filter(m=>m._overridden).length;
              return adminSettings?.maxOverridesPerEvent>0 && ovCount >= (adminSettings.maxOverridesPerEvent-1) && ovCount>0;
            });
            const unpaidCheckedIn = tournaments.flatMap(t=>(t.registrations||[]).filter(r=>r.status==="checked_in"&&r.paymentStatus!=="paid"&&r.paymentStatus!=="waived"));
            const pendingApproval = tournaments.flatMap(t=>{
              const bkt = t.bracket||{};
              const all = [...(bkt.wb||[]),...(bkt.lb||[])].flatMap(r=>r.matches||[]);
              return all.filter(m=>m.status==="pending_approval");
            });
            const checks = [
              pendingApproval.length>0 && { color:"#5b8dee", icon:"⚡", msg:`${pendingApproval.length} score${pendingApproval.length!==1?"s":""} awaiting approval`, action:()=>setTab("score_override"), actionLabel:"Review" },
              unfinalized.length>0 && { color:"var(--gold)", icon:"📋", msg:`${unfinalized.length} completed event${unfinalized.length!==1?"s":""} not yet finalized`, action:null, actionLabel:null },
              unpaidCheckedIn.length>0 && { color:"var(--gold)", icon:"💰", msg:`${unpaidCheckedIn.length} checked-in entr${unpaidCheckedIn.length!==1?"ies":"y"} still unpaid`, action:null, actionLabel:null },
              overrideWarn.length>0 && { color:"var(--red)", icon:"⚠", msg:`${overrideWarn.map(t=>t.name).join(", ")} near override limit`, action:()=>setTab("score_override"), actionLabel:"View" },
            ].filter(Boolean);
            if (!checks.length) return null;
            return (
              <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:16 }}>
                {checks.map((c,i)=>(
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 14px",background:`rgba(255,255,255,0.02)`,border:`1px solid ${c.color}44`,borderLeft:`3px solid ${c.color}`,borderRadius:4 }}>
                    <span style={{ fontSize:14 }}>{c.icon}</span>
                    <span style={{ flex:1,fontSize:12,color:"var(--off-white)" }}>{c.msg}</span>
                    {c.action && <Btn sz="sm" v="ghost" onClick={c.action}>{c.actionLabel} →</Btn>}
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16 }}>
            {/* Quick Actions */}
            <div style={css.card}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Quick Actions</span></div>
              <div style={{ padding:16,display:"flex",flexDirection:"column",gap:8 }}>
                <Btn v="ghost" onClick={()=>setPage("tournaments")} style={{ justifyContent:"flex-start" }}>🏆 Create New Tournament</Btn>
                <Btn v="ghost" onClick={()=>setTab("score_override")} style={{ justifyContent:"flex-start" }}>✏️ Override a Score</Btn>
                <Btn v="ghost" onClick={()=>setPage("users")} style={{ justifyContent:"flex-start" }}>👥 Manage User Roles</Btn>
                <Btn v="ghost" onClick={()=>setPage("export")} style={{ justifyContent:"flex-start" }}>📄 Export / Print Reports</Btn>
                <Btn v="ghost" onClick={()=>setPage("venues")} style={{ justifyContent:"flex-start" }}>📍 Manage Venues</Btn>
                <Btn v="ghost" onClick={()=>setPage("notifications")} style={{ justifyContent:"flex-start" }}>🔔 Send Notifications</Btn>
              </div>
            </div>

            {/* System Status */}
            <div style={css.card}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>System Status</span></div>
              <div style={{ padding:"0 16px" }}>
                {[
                  ["Tournament Engine","Online","green"],
                  ["Bracket Generator","Online","green"],
                  ["Rankings Engine","Online","green"],
                  ["Score Sync","Online","green"],
                  ["Persistent Storage","Online","green"],
                  ["Score Approval",adminSettings?.requireApproval?"Active":"Disabled",adminSettings?.requireApproval?"gold":""],
                  ["Auto-Approve",adminSettings?.autoApprove?"On":"Off",adminSettings?.autoApprove?"green":""],
                ].map(([l,s,c])=>(
                  <div key={l} style={{ display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ fontSize:13 }}>{l}</span><Badge color={c}>{s}</Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Tournament Pipeline */}
            <div style={css.card}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Tournament Pipeline</span></div>
              <div style={{ padding:"0 16px" }}>
                {tournaments.length===0 ? (
                  <div style={{ padding:"20px 0",textAlign:"center",color:"var(--muted)",fontSize:13 }}>No tournaments yet</div>
                ) : tournaments.slice().sort((a,b)=>{const o={active:0,upcoming:1,completed:2};return (o[a.status]??3)-(o[b.status]??3);}).slice(0,6).map(t=>{
                  const regs = (t.registrations||[]).length;
                  const s = t.status;
                  return (
                    <div key={t.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ width:7,height:7,borderRadius:"50%",flexShrink:0,background:s==="active"?"var(--green)":s==="upcoming"?"#5b8dee":"var(--muted)",animation:s==="active"?"pulse 2s infinite":"none" }} />
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.name}</div>
                        <div style={{ fontSize:10,color:"var(--muted)" }}>{t.date||"TBD"} · {regs} registered</div>
                      </div>
                      <Badge color={s==="active"?"green":s==="completed"?"gold":""}>{s}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Activity + Data Management */}
            <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
              <div style={css.card}>
                <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Recent Activity</span>
                  {(auditLog||[]).length>0 && <span onClick={()=>setTab("audit")} style={{ fontSize:11,color:"var(--gold)",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>View all →</span>}
                </div>
                <div style={{ padding:"0 16px" }}>
                  {(auditLog||[]).length===0 && <div style={{ padding:"18px 0",textAlign:"center",color:"var(--muted)",fontSize:13 }}>No activity yet</div>}
                  {(auditLog||[]).slice(0,5).map(e=>{
                    const meta = getMeta(e.action);
                    return (
                      <div key={e.id} style={{ display:"flex",gap:10,padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",alignItems:"flex-start" }}>
                        <div style={{ width:7,height:7,borderRadius:"50%",background:meta.color,flexShrink:0,marginTop:5 }} />
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:meta.color }}>{meta.label}</div>
                          <div style={{ fontSize:11,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{e.details}</div>
                        </div>
                        <div style={{ fontSize:10,color:"var(--muted)",flexShrink:0 }}>{new Date(e.ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={css.card}>
                <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Data Management</span></div>
                <div style={{ padding:16 }}>
                  <div style={{ display:"flex",gap:8,alignItems:"center",padding:"8px 12px",background:"rgba(46,204,113,0.06)",border:"1px solid rgba(46,204,113,0.2)",borderRadius:4,marginBottom:12,fontSize:12,color:"var(--green)" }}>💾 Auto-save active — all changes persist automatically</div>
                  <div style={{ fontSize:12,color:"var(--muted)",marginBottom:12 }}>{players.length} players · {tournaments.length} tournaments · {(auditLog||[]).length} audit entries</div>
                  <Btn v="danger" onClick={onClearData}>⚠ Reset All Data</Btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCORE OVERRIDE ───────────────────────────────────────────────────── */}
      {tab==="score_override" && (
        <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
          <div style={{ background:"rgba(217,79,61,0.07)",border:"1px solid rgba(217,79,61,0.25)",borderRadius:6,padding:"10px 16px",fontSize:13,color:"var(--muted)",display:"flex",gap:10,alignItems:"flex-start" }}>
            <span style={{ fontSize:16,flexShrink:0 }}>⚠️</span>
            <div><strong style={{ color:"var(--white)" }}>All overrides are logged.</strong> They affect Elo ratings, cup differential, player match history, and bracket advancement. Use only to correct genuine errors.</div>
          </div>
          <Fld label="Select Tournament">
            <select style={css.select} value={overrideTournId} onChange={e=>setOverrideTournId(e.target.value)}>
              <option value="">— Select tournament —</option>
              {tournaments.map(t=><option key={t.id} value={t.id}>{t.name} · {t.status}</option>)}
            </select>
          </Fld>
          {overrideTournament && (()=>{
            const allMatches = getTournamentMatches(overrideTournament);
            const completed  = allMatches.filter(m=>m.status==="complete"&&m.t1&&m.t2);
            const pending    = allMatches.filter(m=>(m.status==="pending"||m.status==="pending_approval"||m.status==="disputed")&&m.t1&&m.t2);
            const overrideCount = completed.filter(m=>m._overridden).length;
            return (
              <>
                {overrideCount > 0 && (
                  <div style={{ padding:"8px 14px",background:"rgba(217,79,61,0.06)",border:"1px solid rgba(217,79,61,0.2)",borderRadius:4,fontSize:12,color:"var(--red)" }}>
                    ⚠ {overrideCount} match{overrideCount!==1?"es":""} already overridden in this tournament {adminSettings?.maxOverridesPerEvent>0?`(limit: ${adminSettings.maxOverridesPerEvent})`:""}</div>
                )}
                {pending.length > 0 && (
                  <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                    <div style={{ padding:"10px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase",color:"var(--gold)" }}>Pending / Disputed — Enter Score</span></div>
                    {pending.map(m=>(
                      <div key={m.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.03)",gap:10,flexWrap:"wrap",background:m.status==="disputed"?"rgba(217,79,61,0.04)":"transparent" }}>
                        <div>
                          <div style={{ fontSize:13,fontWeight:600 }}>{m.t1?.name} <span style={{ color:"var(--muted)",fontWeight:400 }}>vs</span> {m.t2?.name}</div>
                          <div style={{ fontSize:10,color:"var(--muted)",letterSpacing:1,marginTop:2,display:"flex",gap:6,alignItems:"center" }}>{m._round} · <Badge color={m.status==="disputed"?"red":m.status==="pending_approval"?"blue":""}>{m.status.replace(/_/g," ")}</Badge></div>
                        </div>
                        <Btn sz="sm" onClick={()=>{setOverrideMatch({match:m,side:m._side});setOvS1("");setOvS2("");setOvOt(false);}}>Enter Score</Btn>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase",color:"var(--red)" }}>Completed — Override Score</span></div>
                  {completed.length===0 && <div style={{ padding:"24px 16px",color:"var(--muted)",fontSize:13 }}>No completed matches yet.</div>}
                  {completed.map(m=>(
                    <div key={m.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.03)",gap:10,flexWrap:"wrap",background:m._overridden?"rgba(217,79,61,0.04)":"transparent" }}>
                      <div>
                        <div style={{ fontSize:13,fontWeight:600 }}>{m.t1?.name} <span style={{ color:"var(--muted)",fontWeight:400 }}>vs</span> {m.t2?.name}</div>
                        <div style={{ fontSize:10,color:"var(--muted)",display:"flex",gap:8,alignItems:"center",marginTop:2 }}>
                          <span>{m._round}</span>{m._overridden&&<span style={{ color:"var(--red)",fontWeight:700,letterSpacing:1 }}>OVERRIDDEN</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--gold)" }}>{m.s1}–{m.s2}{m.ot?" OT":""}</span>
                        <Btn sz="sm" v="danger" onClick={()=>{setOverrideMatch({match:m,side:m._side});setOvS1(String(m.s1));setOvS2(String(m.s2));setOvOt(m.ot||false);}}>Override</Btn>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          <Modal open={!!overrideMatch} onClose={()=>setOverrideMatch(null)} title="Score Override" width={400}>
            {overrideMatch && (
              <>
                <div style={{ background:"rgba(217,79,61,0.07)",border:"1px solid rgba(217,79,61,0.2)",borderRadius:4,padding:"8px 12px",marginBottom:14,fontSize:12,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>⚠ OVERRIDE — This will be logged in the audit trail</div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 40px 1fr",gap:10,alignItems:"center",marginBottom:14 }}>
                  {[{team:overrideMatch.match.t1,val:ovS1,set:setOvS1},{team:overrideMatch.match.t2,val:ovS2,set:setOvS2}].map((side,i)=>(
                    <div key={i} style={{ textAlign:"center" }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{side.team?.name}</div>
                      <input style={{ ...css.input,textAlign:"center",fontSize:32,fontFamily:"'Bebas Neue',sans-serif",padding:8 }} type="number" min="0" max={20} value={side.val} onChange={e=>side.set(e.target.value)} />
                    </div>
                  ))}
                  <div style={{ textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:"var(--muted)" }}>VS</div>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16 }}>
                  <input type="checkbox" id="ov-ot" checked={ovOt} onChange={e=>setOvOt(e.target.checked)} style={{ width:16,height:16,accentColor:"var(--gold)" }} />
                  <label htmlFor="ov-ot" style={{ fontSize:13,cursor:"pointer" }}>Overtime</label>
                </div>
                <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
                  <Btn v="ghost" onClick={()=>setOverrideMatch(null)}>Cancel</Btn>
                  <Btn v="danger" onClick={doScoreOverride} disabled={!ovS1||!ovS2||ovS1===ovS2}>⚠ Apply Override</Btn>
                </div>
              </>
            )}
          </Modal>
        </div>
      )}

      {/* ── ROLES ────────────────────────────────────────────────────────────── */}
      {tab==="roles" && (
        <div style={{ display:"flex",flexDirection:"column",gap:20 }}>
          <div style={{ fontSize:12,color:"var(--muted)",lineHeight:1.7 }}>
            Role definitions determine what each user can do in the platform. Assign roles on the <span onClick={()=>setPage("users")} style={{ color:"var(--gold)",cursor:"pointer",textDecoration:"underline" }}>User Management page →</span>
          </div>

          {/* Role cards */}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14 }}>
            {Object.entries(ROLE_PERMISSIONS).map(([roleKey, rp])=>{
              const count = players.filter(p=>p.role===roleKey).length;
              const colorMap = { gold:"var(--gold)", blue:"#5b8dee", "":"var(--muted)" };
              const color = colorMap[rp.color]||"var(--muted)";
              return (
                <div key={roleKey} style={{ ...css.card,padding:0,overflow:"hidden",borderTop:`3px solid ${color}` }}>
                  <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                    <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                      <span style={{ fontSize:18 }}>{rp.icon}</span>
                      <div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,color }}>{rp.label}</div>
                        <div style={{ fontSize:10,color:"var(--muted)",letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{roleKey.replace(/_/g," ")}</div>
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color,lineHeight:1 }}>{count}</div>
                      <div style={{ fontSize:9,color:"var(--muted)",letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>Users</div>
                    </div>
                  </div>
                  <div style={{ padding:"10px 16px" }}>
                    <div style={{ fontSize:11,color:"var(--muted)",marginBottom:10,lineHeight:1.5 }}>{rp.desc}</div>
                    <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
                      {rp.perms.map(perm=>(
                        <div key={perm} style={{ display:"flex",alignItems:"center",gap:7,fontSize:11 }}>
                          <span style={{ color:"var(--green)",flexShrink:0 }}>✓</span>
                          <span style={{ color:"var(--off-white)" }}>{perm}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding:"10px 16px",borderTop:"1px solid var(--navy-border)",display:"flex",gap:8 }}>
                    <Btn sz="sm" v="ghost" onClick={()=>setPage("users")} style={{ flex:1,justifyContent:"center" }}>
                      Manage {count} User{count!==1?"s":""}
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Permission matrix */}
          <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
            <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Permission Matrix</span>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%",borderCollapse:"collapse",minWidth:600 }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid var(--navy-border)" }}>
                    <th style={{ ...css.th,textAlign:"left",minWidth:180 }}>Permission</th>
                    {Object.entries(ROLE_PERMISSIONS).map(([k,rp])=>(
                      <th key={k} style={{ ...css.th,textAlign:"center",width:90,whiteSpace:"normal",lineHeight:1.3 }}>
                        <div style={{ fontSize:14 }}>{rp.icon}</div>
                        <div style={{ fontSize:9,letterSpacing:0.5 }}>{rp.label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    "Create tournaments","Edit brackets","Override scores","Enter scores",
                    "View bracket","Self-report scores","Check in players","View registrations",
                    "Manage users","Change settings","View audit log","Export data",
                    "Manage venues","View own stats","View match queue","Clear data",
                  ].map((perm,i)=>(
                    <tr key={perm} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                      <td style={{ ...css.td,fontSize:12,fontWeight:600 }}>{perm}</td>
                      {Object.entries(ROLE_PERMISSIONS).map(([k,rp])=>(
                        <td key={k} style={{ ...css.td,textAlign:"center" }}>
                          {rp.perms.includes(perm)
                            ? <span style={{ color:"var(--green)",fontSize:16 }}>✓</span>
                            : <span style={{ color:"rgba(255,255,255,0.1)",fontSize:14 }}>—</span>
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS ─────────────────────────────────────────────────────────── */}
      {tab==="settings" && (
        <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
          <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
            {/* Tournament Defaults */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Tournament Defaults</span></div>
              <div style={{ padding:16 }}>
                <Fld label="Default Format">
                  <select style={css.select} value={settingsDraft.defaultFormat} onChange={e=>setSettingsDraft(s=>({...s,defaultFormat:e.target.value}))}>
                    {FORMATS.map(x=><option key={x.v} value={x.v}>{x.l}</option>)}
                  </select>
                </Fld>
                <Fld label="Default Cup Count">
                  <select style={css.select} value={settingsDraft.defaultCupCount} onChange={e=>setSettingsDraft(s=>({...s,defaultCupCount:parseInt(e.target.value)}))}>
                    {[6,10,15,20].map(n=><option key={n} value={n}>{n} Cups</option>)}
                  </select>
                </Fld>
                <Fld label="Default Team Size">
                  <select style={css.select} value={settingsDraft.defaultTeamSize} onChange={e=>setSettingsDraft(s=>({...s,defaultTeamSize:e.target.value}))}>
                    {TEAM_SIZES.map(x=><option key={x} value={x}>{x}</option>)}
                  </select>
                </Fld>
              </div>
            </div>

            {/* Elo & Scoring */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Elo & Scoring</span></div>
              <div style={{ padding:16 }}>
                <Fld label="New Member Starting Elo">
                  <input style={css.input} type="number" min="800" max="2000" step="50" value={settingsDraft.startingElo||1200} onChange={e=>setSettingsDraft(s=>({...s,startingElo:parseInt(e.target.value)||1200}))} />
                </Fld>
                <Fld label="Elo K-Factor (sensitivity)">
                  <select style={css.select} value={settingsDraft.eloK||32} onChange={e=>setSettingsDraft(s=>({...s,eloK:parseInt(e.target.value)}))}>
                    {[16,24,32,48,64].map(k=><option key={k} value={k}>K={k} — {k<=16?"Conservative":k<=32?"Standard":"Aggressive"}</option>)}
                  </select>
                </Fld>
                <Fld label="Max Score Overrides Per Event">
                  <input style={css.input} type="number" min="0" max="50" value={settingsDraft.maxOverridesPerEvent||5} onChange={e=>setSettingsDraft(s=>({...s,maxOverridesPerEvent:parseInt(e.target.value)||0}))} />
                </Fld>
              </div>
            </div>

            {/* Score Approval */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Score Approval</span></div>
              <div style={{ padding:16 }}>
                {[
                  ["requireApproval","Require Scorekeeper Approval","Self-reported scores await confirmation"],
                  ["autoApprove","Auto-Approve Matching Reports","Confirm automatically when both sides agree"],
                ].map(([k,title,sub])=>(
                  <div key={k} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div>
                      <div style={{ fontSize:13,fontWeight:600 }}>{title}</div>
                      <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>{sub}</div>
                    </div>
                    <div onClick={()=>setSettingsDraft(s=>({...s,[k]:!s[k]}))} style={{ width:44,height:24,borderRadius:12,background:settingsDraft[k]?"var(--green)":"var(--navy-border)",cursor:"pointer",transition:"background 0.2s",position:"relative",flexShrink:0,marginLeft:12 }}>
                      <div style={{ position:"absolute",top:2,left:settingsDraft[k]?20:2,width:20,height:20,borderRadius:"50%",background:"var(--white)",transition:"left 0.2s" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
            {/* Ranking Weights */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>Ranking Weights</span>
                <span style={{ fontSize:11,color:totalW!==100?"var(--red)":"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>Total: {totalW}% {totalW!==100?"≠ 100%":"✓"}</span>
              </div>
              <div style={{ padding:16 }}>
                {[["eloWeight","Elo Rating","var(--gold)"],["placementWeight","Placement Points","#5b8dee"],["cupDiffWeight","Cup Differential","var(--green)"]].map(([k,l,c])=>(
                  <div key={k} style={{ marginBottom:16 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                      <span style={{ fontSize:13 }}>{l}</span>
                      <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                        <input type="number" min="0" max="100" value={settingsDraft[k]} onChange={e=>setSettingsDraft(s=>({...s,[k]:parseInt(e.target.value)||0}))} style={{ ...css.input,width:60,textAlign:"center",padding:"3px 6px",fontSize:14,fontFamily:"'Bebas Neue',sans-serif" }} />
                        <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:c }}>%</span>
                      </div>
                    </div>
                    <div style={{ height:5,background:"var(--navy-border)",borderRadius:3,overflow:"hidden" }}>
                      <div style={{ height:"100%",background:c,borderRadius:3,width:`${settingsDraft[k]}%`,transition:"width 0.3s" }} />
                    </div>
                  </div>
                ))}
                <div style={{ padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid var(--navy-border)",borderRadius:4,fontSize:12,color:"var(--muted)" }}>
                  Live PR preview (top Elo): <strong style={{ color:"var(--gold)",fontFamily:"'Bebas Neue',sans-serif",fontSize:18 }}>{players.length?draftPR([...players].sort((a,b)=>(b.elo||0)-(a.elo||0))[0]):1200}</strong>
                </div>
              </div>
            </div>

            {/* Save / Reset */}
            <div style={{ display:"flex",gap:10 }}>
              <Btn onClick={saveSettings} disabled={totalW!==100} style={{ flex:1,justifyContent:"center" }}>
                {settingsSaved?"✓ Saved!":"💾 Save Settings"}
              </Btn>
              <Btn v="ghost" onClick={()=>setSettingsDraft({...DEFAULT_ADMIN_SETTINGS,...(adminSettings||{})})}>Reset</Btn>
            </div>

            {/* Branding */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>🏷 Branding</span></div>
              <div style={{ padding:16 }}>
                <Fld label="League Name"><input style={css.input} value={settingsDraft.leagueName||""} onChange={e=>setSettingsDraft(s=>({...s,leagueName:e.target.value}))} placeholder="MLCP" /></Fld>
                <Fld label="Tagline"><input style={css.input} value={settingsDraft.leagueTagline||""} onChange={e=>setSettingsDraft(s=>({...s,leagueTagline:e.target.value}))} placeholder="Major League Cup Pong" /></Fld>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Contact Email"><input style={css.input} type="email" value={settingsDraft.contactEmail||""} onChange={e=>setSettingsDraft(s=>({...s,contactEmail:e.target.value}))} placeholder="admin@mlcp.gg" /></Fld>
                  <Fld label="Website URL"><input style={css.input} value={settingsDraft.websiteUrl||""} onChange={e=>setSettingsDraft(s=>({...s,websiteUrl:e.target.value}))} placeholder="mlcp.gg" /></Fld>
                </div>
              </div>
            </div>

            {/* Registration Defaults */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>📋 Registration Defaults</span></div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Default Entry Fee ($)"><input style={css.input} type="number" min="0" value={settingsDraft.defaultEntryFee||0} onChange={e=>setSettingsDraft(s=>({...s,defaultEntryFee:parseFloat(e.target.value)||0}))} placeholder="0" /></Fld>
                  <Fld label="Auto-Waitlist After (0=off)"><input style={css.input} type="number" min="0" value={settingsDraft.autoWaitlistThreshold||0} onChange={e=>setSettingsDraft(s=>({...s,autoWaitlistThreshold:parseInt(e.target.value)||0}))} placeholder="0" /></Fld>
                </div>
                {[
                  ["requireEmailOnReg","Require Email on Registration","Players must provide email to register"],
                  ["allowGuestRegistration","Allow Guest Registration","Walk-up players without accounts can register"],
                ].map(([k,title,sub])=>(
                  <div key={k} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div><div style={{ fontSize:13,fontWeight:600 }}>{title}</div><div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>{sub}</div></div>
                    <div onClick={()=>setSettingsDraft(s=>({...s,[k]:!s[k]}))} style={{ width:44,height:24,borderRadius:12,background:settingsDraft[k]?"var(--green)":"var(--navy-border)",cursor:"pointer",transition:"background 0.2s",position:"relative",flexShrink:0,marginLeft:12 }}>
                      <div style={{ position:"absolute",top:2,left:settingsDraft[k]?20:2,width:20,height:20,borderRadius:"50%",background:"var(--white)",transition:"left 0.2s" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Display */}
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase" }}>🖥 Display</span></div>
              <div style={{ padding:16 }}>
                {[
                  ["showEloPublicly","Show Elo Ratings Publicly","Elo visible on rankings and player profiles"],
                  ["showCupDiff","Show Cup Differential","Display net cup stats on player cards"],
                  ["showMatchHistory","Show Match History","Players can view full match history"],
                  ["compactScoreDisplay","Compact Score Display","Smaller score typography in bracket view"],
                ].map(([k,title,sub])=>(
                  <div key={k} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div><div style={{ fontSize:13,fontWeight:600 }}>{title}</div><div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>{sub}</div></div>
                    <div onClick={()=>setSettingsDraft(s=>({...s,[k]:!s[k]}))} style={{ width:44,height:24,borderRadius:12,background:settingsDraft[k]?"var(--green)":"var(--navy-border)",cursor:"pointer",transition:"background 0.2s",position:"relative",flexShrink:0,marginLeft:12 }}>
                      <div style={{ position:"absolute",top:2,left:settingsDraft[k]?20:2,width:20,height:20,borderRadius:"50%",background:"var(--white)",transition:"left 0.2s" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Danger Zone */}
            <div style={{ ...css.card,padding:0,overflow:"hidden",borderColor:"rgba(217,79,61,0.3)" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid rgba(217,79,61,0.2)",background:"rgba(217,79,61,0.05)" }}><span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase",color:"var(--red)" }}>⚠ Danger Zone</span></div>
              <div style={{ padding:16,display:"flex",flexDirection:"column",gap:10 }}>
                <div style={{ fontSize:12,color:"var(--muted)",lineHeight:1.6 }}>These actions are permanent and cannot be undone. All data cleared here is removed from persistent storage.</div>
                <Btn v="danger" onClick={onClearData} style={{ justifyContent:"flex-start" }}>⚠ Reset All Data — Players, Tournaments, Seasons</Btn>
                <Btn v="danger" onClick={()=>{
                  if(window.confirm("FACTORY RESET: This will delete ALL data including venues, seasons, settings, and audit logs. This cannot be undone. Type RESET to confirm.")) {
                    onClearData && onClearData();
                    setTournaments([]);
                    setVenues && setVenues([]);
                    setSeason && setSeason([]);
                    showToast("Factory reset complete","var(--red)");
                  }
                }} style={{ justifyContent:"flex-start" }}>🔥 Factory Reset — All Data Including Venues & Settings</Btn>
              </div>
            </div>

            {/* Broadcast Message */}
            <div style={{ ...css.card,padding:0,overflow:"hidden",borderColor:"rgba(91,141,238,0.3)" }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)" }}>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:1,textTransform:"uppercase",color:"#5b8dee" }}>📣 Broadcast Message</span>
              </div>
              <div style={{ padding:16,display:"flex",flexDirection:"column",gap:10 }}>
                <div style={{ fontSize:12,color:"var(--muted)" }}>Send a message to all active participants. Appears on Dashboard as an announcement.</div>
                {(() => {
                  const [bcMsg, setBcMsg] = React.useState("");
                  const [bcType, setBcType] = React.useState("info");
                  return (
                    <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                      <div style={{ display:"flex",gap:8 }}>
                        <select value={bcType} onChange={e=>setBcType(e.target.value)} style={{ ...css.select,width:100,fontSize:12 }}>
                          {["info","warn","event","rule"].map(t=><option key={t} value={t}>{t}</option>)}
                        </select>
                        <input value={bcMsg} onChange={e=>setBcMsg(e.target.value)} placeholder="Broadcast message text…" style={{ ...css.input,flex:1 }}
                          onKeyDown={e=>{if(e.key==="Enter"&&bcMsg.trim()){showToast("📣 Broadcast sent","var(--green)");setBcMsg("");}}} />
                      </div>
                      <Btn disabled={!bcMsg.trim()} onClick={()=>{ showToast("📣 Broadcast sent to all active users","var(--green)"); setBcMsg(""); }}>
                        📣 Send Broadcast
                      </Btn>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── AUDIT LOG ────────────────────────────────────────────────────────── */}
      {tab==="audit" && (
        <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
          {/* Filters + export */}
          <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
            <input style={{ ...css.input,maxWidth:240 }} placeholder="🔍 Search actions, details, user…" value={auditSearch} onChange={e=>{ setAuditSearch(e.target.value); setAuditPage(0); }} />
            <select style={{ ...css.select,width:180,fontSize:12 }} value={auditActionFilter} onChange={e=>{ setAuditActionFilter(e.target.value); setAuditPage(0); }}>
              <option value="all">All Action Types</option>
              {actionTypes.map(a=>{const m=getMeta(a); return <option key={a} value={a}>{m.label}</option>;})}
            </select>
            {/* Date filter */}
            <div style={{ display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden" }}>
              {[["all","All Time"],["week","This Week"],["today","Today"]].map(([v,l])=>(
                <div key={v} onClick={()=>{ setAuditDateFilter(v); setAuditPage(0); }} style={{ padding:"5px 10px",cursor:"pointer",fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",background:auditDateFilter===v?"var(--gold)":"transparent",color:auditDateFilter===v?"var(--navy)":"var(--muted)",transition:"all 0.1s",whiteSpace:"nowrap" }}>{l}</div>
              ))}
            </div>
            <span style={{ fontSize:12,color:"var(--muted)" }}>{filteredLog.length} of {(auditLog||[]).length}</span>
            <div style={{ marginLeft:"auto",display:"flex",gap:6 }}>
              {(auditLog||[]).length>0 && <Btn v="ghost" sz="sm" onClick={exportAuditCSV}>↓ CSV</Btn>}
            </div>
          </div>

          {/* Action type summary pills */}
          <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
            {Object.entries(ACTION_META).map(([key,meta])=>{
              const count = (auditLog||[]).filter(e=>e.action===key).length;
              if (!count) return null;
              return (
                <div key={key} onClick={()=>{ setAuditActionFilter(auditActionFilter===key?"all":key); setAuditPage(0); }} style={{ padding:"3px 10px",borderRadius:20,border:`1px solid ${auditActionFilter===key?meta.color:"rgba(255,255,255,0.1)"}`,background:auditActionFilter===key?`${meta.color}18`:"transparent",cursor:"pointer",fontSize:11,color:auditActionFilter===key?meta.color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,display:"flex",alignItems:"center",gap:4,transition:"all 0.15s" }}>
                  <span>{meta.icon}</span> {meta.label} <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:14 }}>{count}</span>
                </div>
              );
            })}
          </div>

          <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
            {filteredLog.length===0 && (
              <div style={{ padding:"40px 16px",textAlign:"center",color:"var(--muted)",fontSize:13 }}>
                No audit entries {auditSearch||auditActionFilter!=="all"||auditDateFilter!=="all" ? "matching your filters" : "yet"}
              </div>
            )}
            {Object.entries(auditByDay).map(([day, entries]) => (
              <div key={day}>
                {/* Day header */}
                <div style={{ padding:"6px 16px",background:"rgba(255,255,255,0.02)",borderBottom:"1px solid var(--navy-border)",fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",position:"sticky",top:0,zIndex:1 }}>
                  {day} · {entries.length} entr{entries.length===1?"y":"ies"}
                </div>
                {entries.map((e,i)=>{
                  const meta = getMeta(e.action);
                  const isExpanded = auditExpanded.has(e.id);
                  return (
                    <div key={e.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)",borderLeft:`3px solid ${meta.color}`,background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                      <div onClick={()=>setAuditExpanded(prev=>{ const s=new Set(prev); s.has(e.id)?s.delete(e.id):s.add(e.id); return s; })} style={{ display:"flex",gap:12,padding:"10px 14px",alignItems:"flex-start",cursor:"pointer" }}>
                        <span style={{ fontSize:14,flexShrink:0,marginTop:1 }}>{meta.icon}</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:meta.color,letterSpacing:0.5 }}>{meta.label}</span>
                            <span style={{ fontSize:11,color:"var(--muted)" }}>by {e.user||"system"}</span>
                          </div>
                          <div style={{ fontSize:12,color:"var(--muted)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:isExpanded?"normal":"nowrap" }}>{e.details}</div>
                        </div>
                        <div style={{ textAlign:"right",flexShrink:0 }}>
                          <div style={{ fontSize:10,color:"var(--muted)",whiteSpace:"nowrap" }}>{new Date(e.ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                          <div style={{ fontSize:9,color:"rgba(255,255,255,0.15)",marginTop:2 }}>{isExpanded?"▲":"▼"}</div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding:"0 14px 10px 42px",fontSize:11,color:"var(--muted)",lineHeight:1.6 }}>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontSize:10,color:"rgba(255,255,255,0.2)",marginBottom:4 }}>Full Details</div>
                          <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid var(--navy-border)",borderRadius:3,padding:"6px 10px",fontFamily:"monospace",fontSize:11,wordBreak:"break-all" }}>{e.details}</div>
                          <div style={{ marginTop:6,fontSize:10,color:"rgba(255,255,255,0.2)" }}>ID: {e.id} · {new Date(e.ts).toISOString()}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:"flex",justifyContent:"center",alignItems:"center",gap:10 }}>
              <button onClick={()=>setAuditPage(p=>Math.max(0,p-1))} disabled={auditPage===0} style={{ padding:"5px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid var(--navy-border)",borderRadius:4,color:"var(--muted)",cursor:auditPage===0?"not-allowed":"pointer",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>← Prev</button>
              <span style={{ fontSize:12,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>Page {auditPage+1} of {totalPages}</span>
              <button onClick={()=>setAuditPage(p=>Math.min(totalPages-1,p+1))} disabled={auditPage===totalPages-1} style={{ padding:"5px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid var(--navy-border)",borderRadius:4,color:"var(--muted)",cursor:auditPage===totalPages-1?"not-allowed":"pointer",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* ── HEALTH TAB ──────────────────────────────────────────────────── */}
      {tab==="health" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* ── Data integrity checks ── */}
          {(() => {
            const checks = [];

            // Players integrity
            const playersNoMatches = players.filter(p => p.wins===0 && p.losses===0 && (p.matchHistory||[]).length===0);
            const playersOrphaned  = players.filter(p => {
              const ids = tournaments.flatMap(t=>(t.registrations||[]).flatMap(r=>(r.players||[]).map(rp=>rp.id)));
              return !ids.includes(p.id) && p.wins===0;
            });
            checks.push({
              label:"Player Records", icon:"👥",
              ok: playersNoMatches.length === 0,
              detail: playersNoMatches.length > 0 ? `${playersNoMatches.length} players with no match history` : `All ${players.length} players have valid records`,
              severity: playersNoMatches.length > 0 ? "warn" : "ok",
            });

            // Tournament integrity
            const tournsNoRegs   = tournaments.filter(t => !t.registrations || t.registrations.length === 0);
            const tournsNoBracket = tournaments.filter(t => t.status==="active" && !t.bracket);
            checks.push({
              label:"Tournament Data", icon:"🏆",
              ok: tournsNoBracket.length === 0,
              detail: tournsNoBracket.length > 0
                ? `${tournsNoBracket.length} active tournament(s) missing bracket`
                : `All ${tournaments.length} tournaments have valid data`,
              severity: tournsNoBracket.length > 0 ? "critical" : tournsNoRegs.length > 0 ? "warn" : "ok",
            });

            // Match data
            const allMatches = tournaments.flatMap(t => getAllMatchesFromTournament(t));
            const orphanMatches = allMatches.filter(m => m.status==="complete" && (!m.t1 || !m.t2));
            const disputedOpen  = allMatches.filter(m => m.status==="disputed");
            checks.push({
              label:"Match Records", icon:"⚽",
              ok: orphanMatches.length === 0 && disputedOpen.length === 0,
              detail: disputedOpen.length > 0
                ? `${disputedOpen.length} open dispute(s) need resolution`
                : orphanMatches.length > 0
                  ? `${orphanMatches.length} incomplete match records`
                  : `${allMatches.filter(m=>m.status==="complete").length} completed matches, no issues`,
              severity: orphanMatches.length > 0 ? "critical" : disputedOpen.length > 0 ? "high" : "ok",
            });

            // Payments
            const unpaidCheckedIn = tournaments.flatMap(t=>(t.registrations||[]).filter(r=>r.status==="checked_in"&&r.paymentStatus!=="paid"&&r.paymentStatus!=="waived"));
            checks.push({
              label:"Payments", icon:"💰",
              ok: unpaidCheckedIn.length === 0,
              detail: unpaidCheckedIn.length > 0
                ? `${unpaidCheckedIn.length} checked-in team(s) with outstanding balance`
                : "All checked-in teams have settled payment",
              severity: unpaidCheckedIn.length > 0 ? "warn" : "ok",
            });

            // Seasons
            const activeSeasons = (seasons||[]).filter(s=>s.status==="active");
            checks.push({
              label:"Seasons", icon:"📅",
              ok: activeSeasons.length <= 1,
              detail: activeSeasons.length > 1
                ? `Warning: ${activeSeasons.length} seasons marked active simultaneously`
                : activeSeasons.length === 1
                  ? `Season active: ${activeSeasons[0].name}`
                  : "No active season",
              severity: activeSeasons.length > 1 ? "warn" : "ok",
            });

            // Audit log
            const recentErrors = (auditLog||[]).filter(e => e.action==="error" || e.action==="dispute").length;
            checks.push({
              label:"System Log", icon:"📋",
              ok: recentErrors === 0,
              detail: `${(auditLog||[]).length} total entries · ${recentErrors} error/dispute events`,
              severity: recentErrors > 5 ? "warn" : "ok",
            });

            const COLORS = { ok:"var(--green)", warn:"var(--gold)", high:"#f97316", critical:"var(--red)" };
            const ICONS  = { ok:"✓", warn:"⚠", high:"!", critical:"✕" };
            const passed = checks.filter(c=>c.severity==="ok").length;

            return (
              <>
                {/* Health score */}
                <div style={{ ...css.card, padding:"14px 18px", display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{ position:"relative", width:64, height:64, flexShrink:0 }}>
                    <svg viewBox="0 0 64 64" style={{ transform:"rotate(-90deg)" }}>
                      <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="8" />
                      <circle cx="32" cy="32" r="26" fill="none"
                        stroke={passed===checks.length?"var(--green)":passed>=checks.length*0.7?"var(--gold)":"var(--red)"}
                        strokeWidth="8"
                        strokeDasharray={`${Math.round(2*Math.PI*26*passed/checks.length)} ${Math.round(2*Math.PI*26)}`}
                        strokeLinecap="round" />
                    </svg>
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color: passed===checks.length?"var(--green)":"var(--gold)" }}>
                      {Math.round(passed/checks.length*100)}%
                    </div>
                  </div>
                  <div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:2, marginBottom:2 }}>SYSTEM HEALTH</div>
                    <div style={{ fontSize:12, color:"var(--muted)" }}>{passed}/{checks.length} checks passing</div>
                    <div style={{ fontSize:11, color:passed===checks.length?"var(--green)":"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, marginTop:2 }}>
                      {passed===checks.length?"All systems nominal":"Some items need attention"}
                    </div>
                  </div>
                </div>

                {/* Check list */}
                <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                  {checks.map((chk, i) => {
                    const c = COLORS[chk.severity];
                    const ic = ICONS[chk.severity];
                    return (
                      <div key={chk.label} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px", borderBottom:i<checks.length-1?"1px solid rgba(255,255,255,0.04)":"none", borderLeft:`3px solid ${c}` }}>
                        <span style={{ fontSize:20, flexShrink:0 }}>{chk.icon}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, marginBottom:1 }}>{chk.label}</div>
                          <div style={{ fontSize:11, color:"var(--muted)" }}>{chk.detail}</div>
                        </div>
                        <div style={{ width:22, height:22, borderRadius:"50%", background:`${c}18`, border:`1.5px solid ${c}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span style={{ fontSize:11, fontWeight:700, color:c }}>{ic}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Data volume */}
                <div style={{ ...css.card, padding:"14px 16px" }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:12 }}>Data Volume</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                    {[
                      ["Players",        players.length,                                     "var(--gold)"],
                      ["Tournaments",    tournaments.length,                                  "#5b8dee"],
                      ["Matches",        allMatches.length,                                   "var(--green)"],
                      ["Registrations",  tournaments.reduce((s,t)=>s+(t.registrations?.length||0),0), "var(--muted)"],
                      ["Audit Entries",  (auditLog||[]).length,                               "var(--muted)"],
                      ["Seasons",        (seasons||[]).length,                                "var(--muted)"],
                    ].map(([l,v,c])=>(
                      <div key={l} style={{ textAlign:"center", padding:"8px 6px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:4 }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                        <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Audit activity chart — daily event count for last 7 days */}
                {(auditLog||[]).length > 0 && (() => {
                  const days = Array(7).fill(0).map((_,i) => {
                    const d = new Date(); d.setDate(d.getDate()-i); d.setHours(0,0,0,0);
                    return d;
                  }).reverse();
                  const counts = days.map(d => {
                    const next = new Date(d); next.setDate(next.getDate()+1);
                    return (auditLog||[]).filter(e => {
                      const t = new Date(e.ts||e.timestamp||0);
                      return t >= d && t < next;
                    }).length;
                  });
                  const maxCount = Math.max(...counts, 1);
                  return (
                    <div style={{ ...css.card, padding:"14px 16px" }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:12 }}>Audit Activity — Last 7 Days</div>
                      <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:60 }}>
                        {counts.map((c,i) => {
                          const barH = maxCount > 0 ? Math.max(4, Math.round(c/maxCount*56)) : 4;
                          const d = days[i];
                          const label = d.toLocaleDateString("en-US",{weekday:"short"});
                          return (
                            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{c||""}</div>
                              <div style={{ width:"100%", height:barH, background:c>0?"var(--gold)":"rgba(255,255,255,0.06)", borderRadius:"2px 2px 0 0", transition:"height 0.3s" }} />
                              <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", textTransform:"uppercase" }}>{label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}

      {/* ── SYSTEM: backup / broadcast / reset ──────────────────────────────── */}
      {tab==="system" && (() => {
        const allData = { players, tournaments, venues, seasons };
        const dataSize = JSON.stringify(allData).length;
        const fmtSize = dataSize > 1024 ? (dataSize/1024).toFixed(1)+"KB" : dataSize+"B";
        return (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            {/* Backup / Restore */}
            <div style={{...css.card,padding:"16px 20px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:12}}>💾 Data Backup & Restore</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div style={{padding:"10px 14px",background:"rgba(46,204,113,0.07)",border:"1px solid rgba(46,204,113,0.2)",borderRadius:6}}>
                  <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>Players</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--green)"}}>{players.length}</div>
                </div>
                <div style={{padding:"10px 14px",background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:6}}>
                  <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>Tournaments</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--gold)"}}>{tournaments.length}</div>
                </div>
              </div>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:10}}>Total data size: <strong style={{color:"var(--white)"}}>{fmtSize}</strong> · Last action: <strong style={{color:"var(--white)"}}>This session</strong></div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Btn onClick={()=>{
                  const blob = new Blob([JSON.stringify(allData,null,2)],{type:"application/json"});
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href=url; a.download=`mlcp-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
                  URL.revokeObjectURL(url);
                }}>⬇ Download Backup</Btn>
                <label style={{cursor:"pointer"}}>
                  <input type="file" accept=".json" style={{display:"none"}} onChange={e=>{
                    const file = e.target.files?.[0]; if(!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => {
                      try {
                        const data = JSON.parse(ev.target.result);
                        if(data.players) setPlayers(data.players);
                        if(data.tournaments) setTournaments(data.tournaments);
                        if(data.venues) setVenues(data.venues);
                        if(data.seasons) setSeasons(data.seasons);
                        alert("✓ Backup restored successfully!");
                      } catch { alert("Invalid backup file"); }
                    };
                    reader.readAsText(file);
                  }} />
                  <span style={{display:"inline-flex",alignItems:"center",padding:"7px 14px",background:"var(--navy-mid)",border:"1px solid var(--navy-border)",borderRadius:5,cursor:"pointer",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,color:"var(--white)"}}>⬆ Restore from File</span>
                </label>
              </div>
            </div>

            {/* Broadcast message */}
            <div style={{...css.card,padding:"16px 20px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:12}}>📣 Admin Broadcast</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <textarea id="broadcastInput" placeholder="Type an announcement to post across all dashboards…"
                  style={{...css.input,width:"100%",minHeight:60,resize:"vertical",fontFamily:"inherit",fontSize:13}} />
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {["event","rule","info","warn"].map(t=>(
                    <button key={t} id={"btype-"+t}
                      onClick={()=>document.querySelectorAll('[id^="btype-"]').forEach(el=>{el.style.borderColor=el.id==="btype-"+t?"var(--gold)":"var(--navy-border)";el.style.color=el.id==="btype-"+t?"var(--gold)":"var(--muted)";})}
                      style={{padding:"5px 12px",borderRadius:4,background:"var(--navy-mid)",border:"1px solid var(--navy-border)",color:"var(--muted)",cursor:"pointer",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,transition:"all 0.15s"}}>{t}</button>
                  ))}
                  <Btn style={{marginLeft:"auto"}} onClick={()=>{
                    const txt = document.getElementById("broadcastInput")?.value?.trim();
                    const activeType = document.querySelector('[id^="btype-"][style*="var(--gold)"]')?.id?.replace("btype-","") || "info";
                    if(!txt) return;
                    alert(`📣 Broadcast sent:

Type: ${activeType}
Message: ${txt}

(In a live system this would push to all connected dashboards)`);
                    if(document.getElementById("broadcastInput")) document.getElementById("broadcastInput").value = "";
                  }}>📤 Broadcast</Btn>
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div style={{...css.card,padding:"16px 20px",borderColor:"rgba(217,79,61,0.3)",background:"rgba(217,79,61,0.03)"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"var(--red)",marginBottom:12}}>⚠ Danger Zone</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>Clear All Match Data</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>Removes all brackets and match results. Players and registrations stay.</div>
                  </div>
                  <Btn v="danger" sz="sm" onClick={()=>{
                    if(!window.confirm("Clear all match data? This cannot be undone.")) return;
                    setTournaments(prev=>prev.map(t=>({...t,bracket:null,status:"upcoming",finalized:false,finalizedAt:undefined})));
                  }}>Clear Brackets</Btn>
                </div>
                <div style={{borderTop:"1px solid rgba(217,79,61,0.15)",paddingTop:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>Factory Reset</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>Wipes ALL data and restores demo state. Irreversible.</div>
                  </div>
                  <Btn v="danger" sz="sm" onClick={()=>{
                    if(!window.confirm("FACTORY RESET — wipe everything and restore demo data?")) return;
                    if(!window.confirm("Are you absolutely sure? All data will be lost.")) return;
                    setPlayers([...INIT_PLAYERS]);
                    setTournaments([...INIT_TOURNAMENTS]);
                    setVenues([...INIT_VENUES]);
                    setSeasons([...INIT_SEASONS]);
                    alert("✓ Factory reset complete. Demo data restored.");
                  }}>🔥 Factory Reset</Btn>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
    );
}

// ── USER MANAGEMENT ──────────────────────────────────────────────────────────
function UsersPage({ players, setPlayers, addAuditEntry }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRole, setBulkRole] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [inviteRole, setInviteRole] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);

  const filtered = players.filter(p => {
    if (filterRole !== "all" && p.role !== filterRole) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !(p.nick||"").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  function setRole(playerId, role) {
    const p = players.find(x=>x.id===playerId);
    setPlayers(prev=>prev.map(x=>x.id===playerId?{...x,role}:x));
    if (addAuditEntry && p) addAuditEntry("ROLE_CHANGED", `${p.name} role changed to ${role}`);
    setEditingId(null);
  }

  function applyBulkRole() {
    if (!bulkRole || selectedIds.size===0) return;
    const names = players.filter(p=>selectedIds.has(p.id)).map(p=>p.name).join(", ");
    setPlayers(prev=>prev.map(p=>selectedIds.has(p.id)?{...p,role:bulkRole}:p));
    if (addAuditEntry) addAuditEntry("ROLE_CHANGED", `Bulk role change to ${bulkRole}: ${names}`);
    setSelectedIds(new Set()); setBulkRole("");
  }

  function toggleSelect(id) {
    setSelectedIds(prev=>{const s=new Set(prev); s.has(id)?s.delete(id):s.add(id); return s;});
  }

  function generateInvite(role) {
    const code = Math.random().toString(36).slice(2,10).toUpperCase();
    setInviteRole(role);
    setInviteLink(`https://mlcp.gg/join?role=${role}&code=${code}`);
  }

  const roleCounts = ROLES.reduce((acc,r)=>({...acc,[r]:players.filter(p=>p.role===r).length}),{});

  return (
    <div className="fade-in">
      <div style={{ marginBottom:20 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>User Management</div>
        <div style={{ color:"var(--muted)",fontSize:13,marginTop:3 }}>Role assignments, permissions, and access control</div>
      </div>

      {/* Role overview cards */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10,marginBottom:24 }}>
        {ROLES.map(role=>{
          const rp = ROLE_PERMISSIONS[role];
          const count = roleCounts[role]||0;
          const isExpanded = expandedRole===role;
          return (
            <div key={role} onClick={()=>setExpandedRole(isExpanded?null:role)} style={{ ...css.card,padding:14,cursor:"pointer",borderLeft:`3px solid ${isExpanded?"var(--gold)":"var(--navy-border)"}`,transition:"all 0.15s",background:isExpanded?"rgba(201,168,76,0.04)":"transparent" }}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
                <span style={{ fontSize:18 }}>{rp.icon}</span>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:0.5,flex:1 }}>{rp.label}</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--gold)" }}>{count}</div>
              </div>
              <div style={{ fontSize:11,color:"var(--muted)",lineHeight:1.5,marginBottom:isExpanded?10:0 }}>{rp.desc}</div>
              {isExpanded && (
                <div style={{ marginTop:8,borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:8 }}>
                  <div style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:5 }}>Permissions</div>
                  {rp.perms.map(p=>(
                    <div key={p} style={{ fontSize:11,color:"var(--green)",padding:"2px 0",display:"flex",alignItems:"center",gap:5 }}><span style={{ fontSize:9 }}>✓</span>{p}</div>
                  ))}
                  <div style={{ marginTop:8 }}>
                    <button onClick={e=>{e.stopPropagation();generateInvite(role);}} style={{ padding:"4px 10px",background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:4,color:"var(--gold)",fontSize:10,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,width:"100%" }}>🔗 Generate Invite</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Invite link display */}
      {inviteLink && (
        <div style={{ padding:"12px 16px",background:"rgba(46,204,113,0.08)",border:"1px solid rgba(46,204,113,0.25)",borderRadius:6,marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
          <span style={{ fontSize:13,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:"var(--green)" }}>🔗 Invite link for {ROLE_PERMISSIONS[inviteRole]?.label}:</span>
          <code style={{ fontSize:12,color:"var(--white)",background:"rgba(255,255,255,0.06)",padding:"4px 10px",borderRadius:4,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{inviteLink}</code>
          <button onClick={()=>navigator.clipboard?.writeText(inviteLink)} style={{ padding:"4px 12px",background:"rgba(46,204,113,0.15)",border:"1px solid rgba(46,204,113,0.3)",borderRadius:4,color:"var(--green)",fontSize:11,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,flexShrink:0 }}>Copy</button>
          <button onClick={()=>setInviteLink(null)} style={{ background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:16 }}>✕</button>
        </div>
      )}

      {/* Search, filter, bulk */}
      <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap" }}>
        <input style={{ ...css.input,maxWidth:240 }} placeholder="🔍 Search name or nickname…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={{ ...css.select,width:180,fontSize:12 }} value={filterRole} onChange={e=>setFilterRole(e.target.value)}>
          <option value="all">All Roles ({players.length})</option>
          {ROLES.map(r=><option key={r} value={r}>{ROLE_PERMISSIONS[r].label} ({roleCounts[r]||0})</option>)}
        </select>
        <span style={{ fontSize:12,color:"var(--muted)" }}>{filtered.length} shown</span>

        {selectedIds.size>0 && (
          <div style={{ marginLeft:"auto",display:"flex",gap:8,alignItems:"center" }}>
            <span style={{ fontSize:12,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{selectedIds.size} selected</span>
            <select style={{ ...css.select,width:"auto",fontSize:12,padding:"4px 8px" }} value={bulkRole} onChange={e=>setBulkRole(e.target.value)}>
              <option value="">Assign role…</option>
              {ROLES.map(r=><option key={r} value={r}>{ROLE_PERMISSIONS[r].label}</option>)}
            </select>
            <Btn sz="sm" disabled={!bulkRole} onClick={applyBulkRole}>Apply</Btn>
            <Btn sz="sm" v="ghost" onClick={()=>setSelectedIds(new Set())}>Clear</Btn>
          </div>
        )}
      </div>

      {/* Users table */}
      <div style={{ ...css.card,overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse",minWidth:640 }}>
            <thead>
              <tr>
                <th style={{ ...css.th,width:36 }}>
                  <input type="checkbox" checked={selectedIds.size===filtered.length&&filtered.length>0} onChange={e=>setSelectedIds(e.target.checked?new Set(filtered.map(p=>p.id)):new Set())} style={{ width:14,height:14,cursor:"pointer",accentColor:"var(--gold)" }} />
                </th>
                {["Player","Role","W–L","Elo","Cup ±","Events","Actions"].map(h=><th key={h} style={css.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p=>{
                const rp = ROLE_PERMISSIONS[p.role]||ROLE_PERMISSIONS.player;
                const wr = (p.wins+p.losses)>0?Math.round(p.wins/(p.wins+p.losses)*100):0;
                return (
                  <tr key={p.id} style={{ background:selectedIds.has(p.id)?"rgba(201,168,76,0.04)":"transparent" }}>
                    <td style={{ ...css.td,width:36 }}>
                      <input type="checkbox" checked={selectedIds.has(p.id)} onChange={()=>toggleSelect(p.id)} style={{ width:14,height:14,cursor:"pointer",accentColor:"var(--gold)" }} />
                    </td>
                    <td style={css.td}>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <div style={{ width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,rgba(201,168,76,0.3),rgba(91,141,238,0.2))`,border:"1px solid rgba(201,168,76,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,flexShrink:0 }}>{p.name[0]}</div>
                        <div>
                          <div style={{ fontWeight:600,fontSize:14 }}>{p.name}</div>
                          {p.nick&&<div style={{ fontSize:11,color:"var(--gold)",fontStyle:"italic" }}>"{p.nick}"</div>}
                          {p.email&&<div style={{ fontSize:10,color:"var(--muted)" }}>{p.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={css.td}>
                      {editingId===p.id ? (
                        <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                          <select style={{ ...css.select,width:"auto",fontSize:12,padding:"4px 8px" }} defaultValue={p.role} onChange={e=>setRole(p.id,e.target.value)}>
                            {ROLES.map(r=><option key={r} value={r}>{ROLE_PERMISSIONS[r].label}</option>)}
                          </select>
                          <Btn sz="sm" v="ghost" onClick={()=>setEditingId(null)}>✕</Btn>
                        </div>
                      ) : (
                        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                          <span style={{ fontSize:14 }}>{rp.icon}</span>
                          <Badge color={ROLE_COLOR[p.role]||""}>{rp.label}</Badge>
                        </div>
                      )}
                    </td>
                    <td style={css.td}>
                      <span style={{ color:"var(--green)" }}>{p.wins}W</span>
                      <span style={{ color:"var(--muted)",margin:"0 3px" }}>–</span>
                      <span style={{ color:"var(--red)" }}>{p.losses}L</span>
                      {(p.wins+p.losses)>0&&<div style={{ fontSize:10,color:"var(--muted)" }}>{wr}%</div>}
                    </td>
                    <td style={{ ...css.td,fontFamily:"'Bebas Neue',sans-serif",fontSize:22 }}>{p.elo||1200}</td>
                    <td style={{ ...css.td,fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:(p.cupDiff||0)>=0?"var(--green)":"var(--red)" }}>{(p.cupDiff||0)>=0?"+":""}{p.cupDiff||0}</td>
                    <td style={{ ...css.td,fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--muted)" }}>{p.tournaments||0}</td>
                    <td style={css.td}>
                      {editingId!==p.id && <Btn sz="sm" v="ghost" onClick={()=>setEditingId(p.id)}>Edit Role</Btn>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length===0&&<div style={{ padding:"32px",textAlign:"center",color:"var(--muted)",fontSize:13 }}>No players match your filters</div>}
      </div>

      {/* ── Activity Log ────────────────────────────────────────────────────── */}
      {(() => {
        const activity = (players||[])
          .flatMap(p => (p.matchHistory||[]).slice(-3).map(m => ({
            player: p.name, type:"match", ts: m.ts||"", detail: `${m.won?"W":"L"} vs ${m.opponent}${m.score?" "+m.score:""}`,
          })))
          .sort((a,b) => (b.ts||"").localeCompare(a.ts||""))
          .slice(0,20);

        const roleChanges = (players||[])
          .filter(p => p.role && p.role !== "member")
          .map(p => ({ player:p.name, type:"role", detail:`Role: ${p.role}`, ts:"" }));

        const allActivity = [...activity, ...roleChanges].slice(0,15);
        if (!allActivity.length) return null;
        return (
          <div style={{ marginTop:20 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:10 }}>Recent Activity</div>
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              {allActivity.map((a,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", borderBottom:i<allActivity.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:a.type==="match"?"var(--green)":"var(--gold)", flexShrink:0 }} />
                  <span style={{ fontSize:12, fontWeight:700, color:"var(--white)" }}>{a.player}</span>
                  <span style={{ fontSize:12, color:"var(--muted)", flex:1 }}>{a.detail}</span>
                  {a.ts && <span style={{ fontSize:10, color:"rgba(255,255,255,0.2)" }}>{a.ts}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// ── REGISTRATION PAGE ────────────────────────────────────────────────────────
function RegistrationPage({ tournaments, setTournaments, players, setPlayers, setPage }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [selectedTournament, setSelectedTournament] = useState("");
  const [tab, setTab] = useState("registrations");
  const [teamForm, setTeamForm] = useState({ name:"", p1:"", p2:"", p1email:"", p2email:"", size:"2v2", notes:"", paymentStatus:"unpaid", paymentAmount:"" });
  const [playerForm, setPlayerForm] = useState({ name:"", nick:"", email:"", phone:"", convertToMember:false, paymentStatus:"unpaid" });
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [sortBy, setSortBy] = useState("status"); // status | seed | name | time
  const [editModal, setEditModal] = useState(null); // reg object being edited
  const [editForm, setEditForm] = useState({});
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [toast, setToast] = useState(null);
  const [pairingMode, setPairingMode] = useState(false);
  const [pairA, setPairA] = useState(null);
  const [pairB, setPairB] = useState(null);
  const [pairName, setPairName] = useState("");
  const [editingReg, setEditingReg] = useState(null); // reg id being inline-edited

  // ── New state: waitlist management, team merge, bulk import ──────────────
  const [notifyLog,      setNotifyLog]      = useState([]);
  const [mergeMode,      setMergeMode]      = useState(false);
  const [mergeSelected,  setMergeSelected]  = useState([]);     // [regId, regId]
  const [mergeName,      setMergeName]      = useState("");
  const [mergeModal,     setMergeModal]     = useState(false);
  const [importModal,    setImportModal]    = useState(false);
  const [importRaw,      setImportRaw]      = useState("");      // raw CSV text
  const [importParsed,   setImportParsed]   = useState([]);      // parsed rows
  const [importErrors,   setImportErrors]   = useState([]);
  const [importConfirmed, setImportConfirmed] = useState(false);
  const [waitlistAction, setWaitlistAction] = useState(null);   // { regId, action }
  const [bulkSelected,   setBulkSelected]   = useState(new Set()); // for bulk ops
  const [bulkMode,       setBulkMode]       = useState(false);

  const upd  = (k,v) => setTeamForm(f=>({...f,[k]:v}));
  const updP = (k,v) => setPlayerForm(f=>({...f,[k]:v}));

  const upcoming = tournaments.filter(t=>t.status==="upcoming"||t.status==="active");
  const tournament = tournaments.find(t=>t.id===selectedTournament);

  function showToast(msg, color="var(--green)") {
    setToast({msg,color});
    setTimeout(()=>setToast(null), 3200);
  }

  // ── Capacity helpers ────────────────────────────────────────────────────────
  const capacity     = tournament?.capacity || 0;  // 0 = unlimited
  const allRegs      = tournament?.registrations || [];
  const activeRegs   = allRegs.filter(r=>r.status!=="waitlist"&&r.status!=="no_show");
  const waitlistRegs = allRegs.filter(r=>r.status==="waitlist");
  const isFull       = capacity>0 && activeRegs.length >= capacity;
  const capacityPct  = capacity>0 ? Math.min(100, Math.round(activeRegs.length/capacity*100)) : null;

  function saveCapacity(val) {
    const n = parseInt(val)||0;
    setTournaments(prev=>prev.map(t=>t.id===selectedTournament?{...t,capacity:n}:t));
  }

  // ── Registration actions ────────────────────────────────────────────────────
  function addTeamReg() {
    if (!selectedTournament || !teamForm.name || !teamForm.p1) return;
    const autoWaitlist = isFull;
    const reg = {
      id: uid(), type:"team",
      teamName: teamForm.name,
      players: [
        { name:teamForm.p1, email:teamForm.p1email },
        ...(teamForm.p2 ? [{ name:teamForm.p2, email:teamForm.p2email }] : []),
      ],
      size: teamForm.size,
      notes: teamForm.notes,
      registeredAt: new Date().toLocaleString(),
      status: autoWaitlist ? "waitlist" : "registered",
      paymentStatus: teamForm.paymentStatus || "unpaid",
      paymentAmount: teamForm.paymentAmount || "",
      seed: null,
    };
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      return { ...t, registrations:[...(t.registrations||[]), reg] };
    }));
    setTeamForm({ name:"", p1:"", p2:"", p1email:"", p2email:"", size:"2v2", notes:"", paymentStatus:"unpaid", paymentAmount:"" });
    showToast(autoWaitlist ? `⏳ ${reg.teamName} added to waitlist (event full)` : `✓ ${reg.teamName} registered`);
    setTab("registrations");
  }

  function addWalkUpPlayer() {
    if (!playerForm.name) return;
    const existing = players.find(p=>p.name.toLowerCase()===playerForm.name.toLowerCase());
    const isGuest = !existing && !playerForm.convertToMember;
    if (!existing && playerForm.convertToMember) {
      const newP = { id:uid(), name:playerForm.name, nick:playerForm.nick||"", elo:1200, wins:0, losses:0, cupsMade:0, cupsAllowed:0, cupDiff:0, placements:[], tournaments:0, role:"player", email:playerForm.email, phone:playerForm.phone };
      setPlayers(prev=>[...prev, newP]);
    }
    if (selectedTournament) {
      const autoWaitlist = isFull;
      const reg = {
        id: uid(),
        type: isGuest ? "guest" : "solo",
        guest: isGuest,
        teamName: playerForm.name,
        players: [{ name:playerForm.name, email:playerForm.email, nick:playerForm.nick }],
        registeredAt: new Date().toLocaleString(),
        status: autoWaitlist ? "waitlist" : "registered",
        paymentStatus: playerForm.paymentStatus || "unpaid",
        paymentAmount: playerForm.paymentAmount || "",
        seed: null,
        notes: playerForm.phone,
        linkedPlayerId: existing?.id || null,
      };
      setTournaments(prev=>prev.map(t=>{
        if(t.id!==selectedTournament) return t;
        return { ...t, registrations:[...(t.registrations||[]), reg] };
      }));
    }
    const label = isGuest ? "guest" : existing ? "existing member" : "new member";
    showToast(`✓ ${playerForm.name} added (${label})`);
    setPlayerForm({ name:"", nick:"", email:"", phone:"", convertToMember:false, paymentStatus:"unpaid" });
    setTab("registrations");
  }

  function convertGuestToMember(regId) {
    const reg = allRegs.find(r=>r.id===regId);
    if (!reg) return;
    const existing = players.find(p=>p.name.toLowerCase()===reg.teamName.toLowerCase());
    let newPlayerId;
    if (!existing) {
      const newP = { id:uid(), name:reg.teamName, nick:reg.players?.[0]?.nick||"", elo:1200, wins:0, losses:0, cupsMade:0, cupsAllowed:0, cupDiff:0, placements:[], tournaments:0, role:"player", email:reg.players?.[0]?.email||"", phone:reg.notes||"" };
      setPlayers(prev=>[...prev, newP]);
      newPlayerId = newP.id;
    } else newPlayerId = existing.id;
    patchReg(regId, { guest:false, type:"solo", linkedPlayerId:newPlayerId });
    showToast(`✓ ${reg.teamName} converted to member`);
  }

  function removeReg(regId) {
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      return { ...t, registrations:(t.registrations||[]).filter(r=>r.id!==regId) };
    }));
    setConfirmRemove(null);
    showToast("Entry removed", "var(--red)");
  }

  function patchReg(regId, patch) {
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      return { ...t, registrations:(t.registrations||[]).map(r=>r.id===regId?{...r,...patch}:r) };
    }));
  }

  function updateRegStatus(regId, status) { patchReg(regId,{status}); }
  function updateSeed(regId, seed) { const n=parseInt(seed); patchReg(regId,{seed:isNaN(n)?null:n}); }
  function markPayment(regId, paymentStatus) { patchReg(regId,{paymentStatus}); }

  // Promote next waitlist entry when a spot opens
  function promoteFromWaitlist(regId) {
    const target = regId ? waitlistRegs.find(r=>r.id===regId) : waitlistRegs[0];
    if (!target) return;
    patchReg(target.id, { status:"registered", promotedAt: new Date().toISOString() });
    showToast(`✓ ${target.teamName} promoted from waitlist`);
    // Simulate notification
    setNotifyLog(prev => [...prev, {
      name: target.teamName, type:"promote",
      msg: `Spot available! ${target.teamName} has been promoted to registered status for ${tournament?.name||"the event"}.`,
      ts: new Date().toLocaleTimeString()
    }]);
  }

  function bulkPromoteWaitlist(n) {
    const toPromote = waitlistRegs.slice(0, n);
    if (!toPromote.length) return;
    const ids = new Set(toPromote.map(r=>r.id));
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==tournament.id) return t;
      return {...t, registrations:(t.registrations||[]).map(r=>
        ids.has(r.id) ? {...r, status:"registered", promotedAt:new Date().toISOString()} : r
      )};
    }));
    showToast(`✓ ${toPromote.length} teams promoted from waitlist`);
    setNotifyLog(prev=>[...prev, ...toPromote.map(r=>({
      name:r.teamName, type:"promote",
      msg:`Spot available! ${r.teamName} promoted to registered for ${tournament?.name||"event"}.`,
      ts:new Date().toLocaleTimeString()
    }))]);
  }

  // Bulk actions
  function bulkCheckInRegistered() {
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      return { ...t, registrations:(t.registrations||[]).map(r=>r.status==="registered"?{...r,status:"checked_in",checkedInAt:new Date().toLocaleTimeString()}:r) };
    }));
    showToast("✓ All registered entries checked in");
  }

  function bulkMarkPaid(statusFilter) {
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      return { ...t, registrations:(t.registrations||[]).map(r=>(!statusFilter||r.status===statusFilter)?{...r,paymentStatus:"paid"}:r) };
    }));
    showToast("✓ Marked as paid");
  }

  // Solo pairing → create team
  function confirmPair() {
    if (!pairA || !pairB || !pairName) return;
    const regA = allRegs.find(r=>r.id===pairA);
    const regB = allRegs.find(r=>r.id===pairB);
    if (!regA||!regB) return;
    const newTeam = {
      id: uid(), type:"team",
      teamName: pairName,
      players: [...(regA.players||[{name:regA.teamName}]), ...(regB.players||[{name:regB.teamName}])],
      size: "2v2",
      registeredAt: new Date().toLocaleString(),
      status: regA.status==="registered"&&regB.status==="registered" ? "registered" : "registered",
      paymentStatus: regA.paymentStatus==="paid"&&regB.paymentStatus==="paid" ? "paid" : "unpaid",
      seed: null,
      notes: `Formed from solos: ${regA.teamName} + ${regB.teamName}`,
      formedFromSolos: true,
    };
    setTournaments(prev=>prev.map(t=>{
      if(t.id!==selectedTournament) return t;
      const filtered = (t.registrations||[]).filter(r=>r.id!==pairA&&r.id!==pairB);
      return { ...t, registrations:[...filtered, newTeam] };
    }));
    setPairingMode(false); setPairA(null); setPairB(null); setPairName("");
    showToast(`✓ Team "${pairName}" formed from ${regA.teamName} + ${regB.teamName}`);
  }

  // ── Filtered + sorted display list ─────────────────────────────────────────
  const STATUS_ORDER = { checked_in:0, registered:1, waitlist:2, no_show:3 };
  const regs = allRegs
    .filter(r => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterPayment !== "all" && (r.paymentStatus||"unpaid") !== filterPayment) return false;
      if (search) return r.teamName.toLowerCase().includes(search.toLowerCase()) || r.players?.some(p=>p.name.toLowerCase().includes(search.toLowerCase()));
      return true;
    })
    .sort((a,b) => {
      if (sortBy === "seed") return (a.seed||999)-(b.seed||999);
      if (sortBy === "name") return a.teamName.localeCompare(b.teamName);
      if (sortBy === "time") return new Date(a.registeredAt||0)-new Date(b.registeredAt||0);
      return (STATUS_ORDER[a.status]??9)-(STATUS_ORDER[b.status]??9)||(a.seed||999)-(b.seed||999);
    });

  const totalCollected = tournament?.entryFee>0
    ? allRegs.filter(r=>r.paymentStatus==="paid").length * Number(tournament.entryFee)
    : allRegs.reduce((s,r)=>s+(Number(r.paymentAmount)||0),0);

  function openEditModal(reg) {
    setEditForm({
      teamName: reg.teamName,
      p1name:   reg.players?.[0]?.name || "",
      p1email:  reg.players?.[0]?.email || "",
      p2name:   reg.players?.[1]?.name || "",
      p2email:  reg.players?.[1]?.email || "",
      seed:     reg.seed || "",
      status:   reg.status,
      paymentStatus: reg.paymentStatus || "unpaid",
      paymentAmount: reg.paymentAmount || "",
      notes:    reg.notes || "",
    });
    setEditModal(reg);
  }

  function saveEditModal() {
    if (!editModal) return;
    const players = [
      { name: editForm.p1name, email: editForm.p1email },
      ...(editForm.p2name ? [{ name: editForm.p2name, email: editForm.p2email }] : []),
    ];
    patchReg(editModal.id, {
      teamName: editForm.teamName,
      players,
      seed: editForm.seed ? parseInt(editForm.seed) : null,
      status: editForm.status,
      paymentStatus: editForm.paymentStatus,
      paymentAmount: editForm.paymentAmount,
      notes: editForm.notes,
    });
    setEditModal(null);
    showToast(`✓ ${editForm.teamName} updated`);
  }

  function moveSeed(regId, dir) {
    const sorted = [...allRegs].filter(r=>r.seed).sort((a,b)=>a.seed-b.seed);
    const reg = allRegs.find(r=>r.id===regId);
    const cur = reg?.seed;
    const swapWith = dir==="up"
      ? sorted.filter(r=>r.seed<cur).slice(-1)[0]
      : sorted.filter(r=>r.seed>cur)[0];
    if (!swapWith) return;
    patchReg(regId, { seed: swapWith.seed });
    patchReg(swapWith.id, { seed: cur });
  }

  const statusColor = { registered:"", checked_in:"green", no_show:"red", waitlist:"blue" };
  const PAY_CONFIG  = { paid:{ color:"var(--green)", label:"Paid", bg:"rgba(46,204,113,0.1)" }, unpaid:{ color:"var(--red)", label:"Unpaid", bg:"rgba(217,79,61,0.08)" }, waived:{ color:"#5b8dee", label:"Waived", bg:"rgba(91,141,238,0.08)" } };

  const checkedIn    = allRegs.filter(r=>r.status==="checked_in").length;
  const registeredCt = allRegs.filter(r=>r.status==="registered").length;
  const paidCt       = allRegs.filter(r=>r.paymentStatus==="paid").length;
  const solos        = allRegs.filter(r=>(r.type==="solo"||r.type==="guest")&&r.status!=="no_show");

  // ── Waitlist promote ─────────────────────────────────────────────────────
  function promoteFromWaitlist(regId) {
    if (!tournament) return;
    const reg = (tournament.registrations||[]).find(r=>r.id===regId);
    if (!reg) return;
    if (capacity && activeRegs.length >= capacity) {
      showToast("Capacity full — raise capacity first", "var(--red)");
      return;
    }
    updateTournament(t=>({
      ...t,
      registrations: t.registrations.map(r=>
        r.id===regId ? {...r, status:"registered", promotedAt:new Date().toLocaleString()} : r
      )
    }));
    showToast(`✓ ${reg.teamName} promoted from waitlist`);
  }

  function demoteToWaitlist(regId) {
    if (!tournament) return;
    updateTournament(t=>({
      ...t,
      registrations: t.registrations.map(r=>
        r.id===regId ? {...r, status:"waitlist"} : r
      )
    }));
    showToast("Team moved to waitlist", "var(--gold)");
  }

  function removeFromWaitlist(regId) {
    if (!tournament) return;
    updateTournament(t=>({
      ...t,
      registrations: t.registrations.filter(r=>r.id!==regId)
    }));
    showToast("Team removed from waitlist", "var(--red)");
  }

  // ── Team merge ───────────────────────────────────────────────────────────
  function doMerge() {
    if (mergeSelected.length !== 2 || !mergeName.trim()) return;
    const regs = tournament.registrations || [];
    const [a, b] = mergeSelected.map(id => regs.find(r=>r.id===id));
    if (!a || !b) return;
    const merged = {
      id: uid(), teamName: mergeName.trim(),
      players: [...(a.players||[]), ...(b.players||[])].slice(0,4),
      status: "registered", paymentStatus:"unpaid",
      registeredAt: new Date().toLocaleString(), type:"team",
      mergedFrom: [a.teamName, b.teamName],
    };
    updateTournament(t=>({
      ...t,
      registrations: [...t.registrations.filter(r=>!mergeSelected.includes(r.id)), merged]
    }));
    setMergeSelected([]); setMergeName(""); setMergeModal(false); setMergeMode(false);
    showToast(`✓ Merged into ${merged.teamName}`);
  }

  // ── Bulk CSV import ───────────────────────────────────────────────────────
  function parseImportCSV(raw) {
    const lines = raw.trim().split("\n").filter(Boolean);
    const parsed = [], errors = [];
    lines.forEach((line, i) => {
      const cols = line.split(",").map(s=>s.replace(/^"|"$/g,"").trim());
      const [teamName, p1Name, p2Name, email, payment] = cols;
      if (!teamName) { errors.push(`Row ${i+1}: missing team name`); return; }
      parsed.push({
        id: uid(), teamName, status:"registered",
        players: [p1Name&&{id:uid(),name:p1Name,email:""},p2Name&&{id:uid(),name:p2Name,email:""}].filter(Boolean),
        paymentStatus: (payment||"").toLowerCase().includes("paid")?"paid":"unpaid",
        email, registeredAt:new Date().toLocaleString(), type:"team",
      });
    });
    setImportParsed(parsed);
    setImportErrors(errors);
  }

  function confirmImport() {
    if (!tournament || !importParsed.length) return;
    updateTournament(t=>({
      ...t,
      registrations: [...(t.registrations||[]), ...importParsed]
    }));
    setImportParsed([]); setImportRaw(""); setImportConfirmed(true);
    showToast(`✓ ${importParsed.length} teams imported`);
    setTimeout(()=>{ setImportModal(false); setImportConfirmed(false); }, 2000);
  }

  // ── Bulk ops ──────────────────────────────────────────────────────────────
  function bulkMarkPaid() {
    if (!tournament || !bulkSelected.size) return;
    updateTournament(t=>({
      ...t,
      registrations: t.registrations.map(r=>bulkSelected.has(r.id)?{...r,paymentStatus:"paid"}:r)
    }));
    showToast(`✓ ${bulkSelected.size} teams marked paid`);
    setBulkSelected(new Set());
  }

  function bulkRemove() {
    if (!tournament || !bulkSelected.size) return;
    updateTournament(t=>({
      ...t,
      registrations: t.registrations.filter(r=>!bulkSelected.has(r.id))
    }));
    showToast(`Removed ${bulkSelected.size} teams`, "var(--red)");
    setBulkSelected(new Set());
  }

  function toggleBulkSelect(regId) {
    setBulkSelected(prev => {
      const next = new Set(prev);
      next.has(regId) ? next.delete(regId) : next.add(regId);
      return next;
    });
  }


  return (
    <div className="fade-in">
      <div style={{ marginBottom:20 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>Registration</div>
        <div style={{ color:"var(--muted)",fontSize:13,marginTop:3 }}>Manage team sign-ups, waitlist, payment status, and solo pairing</div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed",top:20,right:20,zIndex:2000,background:"var(--navy-mid)",border:`1px solid ${toast.color}`,borderRadius:6,padding:"12px 20px",fontSize:13,fontWeight:600,color:toast.color,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"fadeIn 0.2s ease" }}>
          {toast.msg}
        </div>
      )}

      {/* Tournament Selector */}
      <div style={{ ...css.card, padding:16, marginBottom:20 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:16, alignItems:"end", marginBottom: tournament ? 14 : 0 }}>
          <Fld label="Select Event">
            <select style={css.select} value={selectedTournament} onChange={e=>setSelectedTournament(e.target.value)}>
              <option value="">— Choose a tournament —</option>
              {upcoming.map(t=><option key={t.id} value={t.id}>{t.name} ({t.status}) · {t.date||"TBD"}</option>)}
            </select>
          </Fld>
          {tournament && <Btn sz="sm" v="ghost" onClick={()=>setPage("checkin")}>✅ Check-In Kiosk →</Btn>}
        </div>

        {tournament && (
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
            {/* Stat pills */}
            {[
              [checkedIn+registeredCt, "Active",   "var(--gold)"],
              [checkedIn,              "Checked In","var(--green)"],
              [registeredCt,           "Registered","var(--muted)"],
              [waitlistRegs.length,    "Waitlist",  "#5b8dee"],
              [paidCt,                 "Paid",      "var(--green)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:5 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}

            {/* Capacity bar */}
            <div style={{ flex:1, minWidth:180, display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, whiteSpace:"nowrap" }}>CAP:</div>
              <input
                type="number" min="0" placeholder="∞"
                value={capacity||""} onChange={e=>saveCapacity(e.target.value)}
                style={{ ...css.input, width:56, padding:"4px 8px", fontSize:12, textAlign:"center" }}
                title="Set max teams (0 = unlimited)"
              />
              {capacity>0 && (
                <div style={{ flex:1, maxWidth:120 }}>
                  <div style={{ height:5, background:"var(--navy-border)", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${capacityPct}%`, background:capacityPct>=100?"var(--red)":capacityPct>=80?"var(--gold)":"var(--green)", borderRadius:3, transition:"width 0.4s" }} />
                  </div>
                  <div style={{ fontSize:9, color:isFull?"var(--red)":"var(--muted)", marginTop:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                    {activeRegs.length}/{capacity} {isFull&&"· FULL — new entries go to waitlist"}
                  </div>
                </div>
              )}
            </div>

            {/* Waitlist promote */}
            {waitlistRegs.length>0 && !isFull && (
              <Btn sz="sm" v="ghost" onClick={promoteFromWaitlist}>⬆ Promote Waitlist ({waitlistRegs.length})</Btn>
            )}
          </div>
        )}
      </div>

      {!selectedTournament ? (
        <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
          <div style={{ fontSize:40,marginBottom:12 }}>📋</div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:3,color:"var(--white)",marginBottom:8 }}>Select a Tournament</div>
          <div style={{ fontSize:13 }}>Choose an upcoming event above to manage registrations</div>
        </div>
      ) : (
        <>
          {/* Sub Tabs */}
          <div style={{ display:"flex", gap:0, marginBottom:20, borderBottom:"1px solid var(--navy-border)" }}>
            {[
              ["registrations", `📋 Registrations (${allRegs.length})`],
              ["add_team",      "➕ Add Team"],
              ["add_player",    "👤 Walk-Up Player"],
              ["pair_solos",    `🤝 Pair Solos${solos.length>0?" ("+solos.length+")":""}`],
              ["settings",      "⚙ Event Settings"],
              ["waitlist",       `⏳ Waitlist${waitlistRegs.length>0?" ("+waitlistRegs.length+")":""}`],
              ["bulk_import",    "📥 Bulk Import"],
            ].map(([v,l])=>(
              <div key={v} onClick={()=>setTab(v)} style={{ padding:"8px 16px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",borderBottom:tab===v?"2px solid var(--gold)":"2px solid transparent",color:tab===v?"var(--white)":"var(--muted)",marginBottom:-1,transition:"all 0.15s",whiteSpace:"nowrap" }}>{l}</div>
            ))}
          </div>

          {/* ── REGISTRATIONS LIST ─────────────────────────────────────────── */}
          {tab==="registrations" && (
            <div>
              {/* Capacity warning banner */}
              {(() => {
                const t = tournament;
                if (!t?.venueId) return null;
                const venue = (venues||[]).find(v=>v.id===t.venueId);
                if (!venue?.capacity) return null;
                const cap = parseInt(venue.capacity);
                const regCount = (t.registrations||[]).filter(r=>r.status!=="cancelled").length * (t.teamSize||2);
                const pct = Math.round(regCount/cap*100);
                if (pct < 80) return null;
                const over = regCount > cap;
                return (
                  <div style={{ padding:"10px 14px", marginBottom:12, background:over?"rgba(217,79,61,0.1)":"rgba(201,168,76,0.07)", border:`1px solid ${over?"rgba(217,79,61,0.4)":"rgba(201,168,76,0.3)"}`, borderRadius:6, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:16 }}>{over?"🚨":"⚠️"}</span>
                    <div>
                      <div style={{ fontSize:12, color:over?"var(--red)":"var(--gold)", fontWeight:700 }}>
                        {over ? `Over capacity at ${venue.name}` : `Approaching capacity at ${venue.name}`}
                      </div>
                      <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>
                        {regCount} registered · {cap} venue capacity · {pct}% full
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Filters + sort + bulk actions */}
              <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center", flexWrap:"wrap" }}>
                <input style={{ ...css.input, maxWidth:220 }} placeholder="🔍 Search…" value={search} onChange={e=>setSearch(e.target.value)} />
                {/* Status filter */}
                <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                  {[["all","All"],["registered","Reg'd"],["checked_in","In"],["waitlist","Wait"],["no_show","No Show"]].map(([v,l])=>(
                    <div key={v} onClick={()=>setFilterStatus(v)} style={{ padding:"5px 9px", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", background:filterStatus===v?"var(--gold)":"transparent", color:filterStatus===v?"var(--navy)":"var(--muted)", transition:"all 0.1s", whiteSpace:"nowrap" }}>{l}</div>
                  ))}
                </div>
                {/* Bulk mode toggle */}
                <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                  <Btn sz="sm" v={bulkMode?"success":"ghost"} onClick={()=>{ setBulkMode(m=>!m); setBulkSelected(new Set()); }}>
                    {bulkMode?`✓ ${bulkSelected.size} selected`:"☰ Bulk"}
                  </Btn>
                  {bulkMode && bulkSelected.size > 0 && (
                    <>
                      <Btn sz="sm" onClick={bulkMarkPaid} style={{ background:"rgba(46,204,113,0.12)",borderColor:"rgba(46,204,113,0.35)",color:"var(--green)",fontSize:11 }}>💰 Mark Paid</Btn>
                      <Btn sz="sm" v="danger" onClick={bulkRemove} style={{ fontSize:11 }}>🗑 Remove</Btn>
                    </>
                  )}
                </div>
                {/* Payment filter */}
                <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                  {[["all","$All"],["unpaid","Unpaid"],["paid","Paid"],["waived","Waived"]].map(([v,l])=>(
                    <div key={v} onClick={()=>setFilterPayment(v)} style={{ padding:"5px 9px", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, background:filterPayment===v?(v==="unpaid"?"rgba(217,79,61,0.7)":v==="paid"?"rgba(46,204,113,0.7)":"var(--gold)"):"transparent", color:filterPayment===v?"var(--white)":"var(--muted)", transition:"all 0.1s", whiteSpace:"nowrap" }}>{l}</div>
                  ))}
                </div>
                {/* Sort */}
                <select style={{ ...css.select, fontSize:11, padding:"5px 8px", width:"auto" }} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  <option value="status">Sort: Status</option>
                  <option value="seed">Sort: Seed</option>
                  <option value="name">Sort: Name</option>
                  <option value="time">Sort: Time</option>
                </select>
                <span style={{ fontSize:11, color:"var(--muted)" }}>{regs.length} shown</span>
                {totalCollected > 0 && <span style={{ fontSize:12, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>💰 ${totalCollected} collected</span>}
                <div style={{ marginLeft:"auto", display:"flex", gap:6, flexWrap:"wrap" }}>
                  {registeredCt>0 && <Btn sz="sm" v="ghost" onClick={bulkCheckInRegistered}>✓ Check In All Registered</Btn>}
                  {allRegs.filter(r=>r.paymentStatus!=="paid").length>0 && <Btn sz="sm" v="ghost" onClick={()=>bulkMarkPaid()}>💰 Mark All Paid</Btn>}
                </div>
              </div>

              {regs.length===0 ? (
                <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--muted)" }}>
                  <div style={{ fontSize:32,marginBottom:10 }}>📭</div>
                  <div>No registrations yet. Add a team or walk-up player using the tabs above.</div>
                </div>
              ) : (
                <div style={{ ...css.card, overflow:"hidden" }}>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%",borderCollapse:"collapse",minWidth:720 }}>
                      <thead>
                        <tr>
                          {bulkMode && <th style={css.th}><input type="checkbox" onChange={e=>setBulkSelected(e.target.checked?new Set(regs.map(r=>r.id)):new Set())} checked={bulkSelected.size===regs.length&&regs.length>0} style={{cursor:"pointer",accentColor:"var(--gold)"}} /></th>}
                          {["#","Team / Entry","Players","Reg'd","Seed","Status","Payment","Actions"].map(h=>(<th key={h} style={css.th}>{h}</th>))}
                        </tr>
                      </thead>
                      <tbody>
                        {regs.map((r,i)=>{
                          const pay = PAY_CONFIG[r.paymentStatus||"unpaid"];
                          const isBulkSel = bulkSelected.has(r.id);
                          const isEditing = editingReg===r.id;
                          return (
                            <tr key={r.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:isBulkSel?"rgba(201,168,76,0.08)":r.status==="checked_in"?"rgba(46,204,113,0.025)":r.status==="waitlist"?"rgba(91,141,238,0.03)":r.status==="no_show"?"rgba(217,79,61,0.025)":"transparent", borderLeft:isBulkSel?"3px solid var(--gold)":"3px solid transparent" }}>
                              {bulkMode && <td style={css.td}><input type="checkbox" checked={isBulkSel} onChange={()=>toggleBulkSelect(r.id)} style={{cursor:"pointer",accentColor:"var(--gold)"}} /></td>}
                              <td style={{ ...css.td, width:36, fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"var(--muted)" }}>{i+1}</td>

                              {/* Team name */}
                              <td style={css.td}>
                                <div style={{ fontWeight:600, fontSize:14 }}>{r.teamName}</div>
                                {r.formedFromSolos && <div style={{ fontSize:10,color:"#5b8dee",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>🤝 PAIRED</div>}
                                {r.notes && <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>📝 {r.notes}</div>}
                              </td>

                              {/* Players */}
                              <td style={{ ...css.td, maxWidth:180 }}>
                                <div style={{ fontSize:12,color:"var(--off-white)" }}>
                                  {r.players?.map((p,pi)=>(
                                    <div key={pi} style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                      {p.name}{p.email&&<span style={{ color:"var(--muted)",fontSize:10 }}> · {p.email}</span>}
                                    </div>
                                  ))}
                                </div>
                              </td>

                              {/* Registered at */}
                              <td style={{ ...css.td, color:"var(--muted)", fontSize:11, whiteSpace:"nowrap" }}>{r.registeredAt}</td>

                              {/* Seed */}
                              <td style={css.td}>
                                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                  <input
                                    style={{ ...css.input, width:44, padding:"4px 6px", textAlign:"center", fontSize:13 }}
                                    type="number" min="1" value={r.seed||""} placeholder="—"
                                    onChange={e=>updateSeed(r.id, e.target.value)}
                                  />
                                  {r.seed && (
                                    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                                      <button onClick={()=>moveSeed(r.id,"up")} style={{ background:"none", border:"1px solid var(--navy-border)", borderRadius:2, width:16, height:14, cursor:"pointer", color:"var(--muted)", fontSize:8, padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>▲</button>
                                      <button onClick={()=>moveSeed(r.id,"down")} style={{ background:"none", border:"1px solid var(--navy-border)", borderRadius:2, width:16, height:14, cursor:"pointer", color:"var(--muted)", fontSize:8, padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>▼</button>
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Status */}
                              <td style={css.td}>
                                <select style={{ ...css.select, width:"auto", fontSize:11, padding:"3px 8px" }}
                                  value={r.status} onChange={e=>updateRegStatus(r.id, e.target.value)}>
                                  <option value="registered">Registered</option>
                                  <option value="checked_in">Checked In</option>
                                  <option value="waitlist">Waitlist</option>
                                  <option value="no_show">No Show</option>
                                </select>
                              </td>

                              {/* Payment */}
                              <td style={css.td}>
                                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                                  <select style={{ ...css.select, width:"auto", fontSize:11, padding:"3px 8px", background:pay.bg, color:pay.color, border:`1px solid ${pay.color}44` }}
                                    value={r.paymentStatus||"unpaid"} onChange={e=>markPayment(r.id, e.target.value)}>
                                    <option value="unpaid">Unpaid</option>
                                    <option value="paid">Paid</option>
                                    <option value="waived">Waived</option>
                                  </select>
                                  {r.paymentAmount && <div style={{ fontSize:10,color:"var(--gold)",fontFamily:"'Bebas Neue',sans-serif" }}>${r.paymentAmount}</div>}
                                </div>
                              </td>

                              {/* Actions */}
                              <td style={css.td}>
                                <div style={{ display:"flex", gap:5, alignItems:"center", flexWrap:"wrap" }}>
                                  <Btn sz="sm" v="ghost" onClick={()=>openEditModal(r)} title="Edit registration">✏</Btn>
                                  {r.guest && <span style={{ background:"rgba(255,180,0,0.15)",color:"#ffb400",border:"1px solid rgba(255,180,0,0.3)",borderRadius:3,padding:"1px 5px",fontSize:9,fontWeight:700,letterSpacing:1 }}>GUEST</span>}
                                  {r.guest && <Btn sz="sm" v="ghost" onClick={()=>convertGuestToMember(r.id)} title="Promote to member">👤→</Btn>}
                                  {r.status==="waitlist" && !isFull && <Btn sz="sm" v="success" onClick={()=>patchReg(r.id,{status:"registered"})}>⬆</Btn>}
                                  {r.status!=="checked_in" && r.status!=="waitlist" && <Btn sz="sm" v="success" onClick={()=>patchReg(r.id,{status:"checked_in",checkedInAt:new Date().toLocaleTimeString()})}>✓</Btn>}
                                  <Btn sz="sm" v="danger" onClick={()=>setConfirmRemove(r.id)}>✕</Btn>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Waitlist section */}
              {waitlistRegs.length>0 && filterStatus==="all" && (
                <div style={{ marginTop:16, padding:"12px 16px", background:"rgba(91,141,238,0.06)", border:"1px solid rgba(91,141,238,0.25)", borderRadius:6 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:2, color:"#5b8dee", textTransform:"uppercase" }}>⏳ Waitlist · {waitlistRegs.length} {waitlistRegs.length===1?"entry":"entries"}</div>
                    {!isFull && <Btn sz="sm" v="ghost" onClick={promoteFromWaitlist}>⬆ Promote Next</Btn>}
                    {isFull && <span style={{ fontSize:11,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>Event Full</span>}
                  </div>
                  <div style={{ fontSize:12, color:"var(--muted)" }}>
                    {waitlistRegs.map((r,i)=>(
                      <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:"#5b8dee", width:20 }}>#{i+1}</span>
                        <span style={{ flex:1, fontWeight:600, color:"var(--white)" }}>{r.teamName}</span>
                        <span style={{ fontSize:11, color:"var(--muted)" }}>{r.registeredAt}</span>
                        {!isFull && <Btn sz="sm" v="ghost" onClick={()=>patchReg(r.id,{status:"registered"})}>Register ↑</Btn>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ADD TEAM ──────────────────────────────────────────────────────── */}
          {tab==="add_team" && (
            <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:16,display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ width:4,height:20,background:"var(--gold)",display:"inline-block",borderRadius:2 }}/>
                  Team Details
                </div>
                <Fld label="Team Name *"><input style={css.input} value={teamForm.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. The Eliminators" /></Fld>
                <Fld label="Team Size">
                  <select style={css.select} value={teamForm.size} onChange={e=>upd("size",e.target.value)}>
                    {["1v1","2v2","3v3","4v4","6v6","10v10"].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </Fld>
                <div style={{ height:1,background:"var(--navy-border)",margin:"12px 0" }} />
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",color:"var(--muted)",marginBottom:8 }}>Player 1 — Captain *</div>
                <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Name"><input style={css.input} value={teamForm.p1} onChange={e=>upd("p1",e.target.value)} placeholder="Full name" /></Fld>
                  <Fld label="Email"><input style={css.input} value={teamForm.p1email} onChange={e=>upd("p1email",e.target.value)} placeholder="email@example.com" /></Fld>
                </div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",color:"var(--muted)",margin:"10px 0 8px" }}>Player 2</div>
                <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Name"><input style={css.input} value={teamForm.p2} onChange={e=>upd("p2",e.target.value)} placeholder="Full name" /></Fld>
                  <Fld label="Email"><input style={css.input} value={teamForm.p2email} onChange={e=>upd("p2email",e.target.value)} placeholder="email@example.com" /></Fld>
                </div>
                <div style={{ height:1,background:"var(--navy-border)",margin:"12px 0" }} />
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",color:"var(--muted)",marginBottom:8 }}>Payment</div>
                <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Status">
                    <select style={css.select} value={teamForm.paymentStatus} onChange={e=>upd("paymentStatus",e.target.value)}>
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="waived">Waived</option>
                    </select>
                  </Fld>
                  <Fld label="Amount ($)"><input style={css.input} value={teamForm.paymentAmount} onChange={e=>upd("paymentAmount",e.target.value)} placeholder="e.g. 20" type="number" min="0" /></Fld>
                </div>
                <Fld label="Notes (optional)"><input style={css.input} value={teamForm.notes} onChange={e=>upd("notes",e.target.value)} placeholder="e.g. seeding request, accommodations" /></Fld>
                {isFull && <div style={{ padding:"8px 12px",background:"rgba(91,141,238,0.08)",border:"1px solid rgba(91,141,238,0.25)",borderRadius:4,fontSize:12,color:"#5b8dee",marginBottom:10 }}>⏳ Event is full — this team will be added to the waitlist.</div>}
                <Btn onClick={addTeamReg} disabled={!teamForm.name||!teamForm.p1} style={{ marginTop:4, width:"100%", justifyContent:"center" }}>
                  {isFull?"Add to Waitlist":"Register Team"}
                </Btn>
              </div>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:16,display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ width:4,height:20,background:"var(--gold)",display:"inline-block",borderRadius:2 }}/>
                  Event Info
                </div>
                <div style={{ fontSize:13,color:"var(--muted)",lineHeight:1.8 }}>
                  {[["Event",tournament?.name],["Format",FORMATS.find(f=>f.v===tournament?.format)?.l],["Mode",`${tournament?.cupCount} Cups · ${tournament?.teamSize}`],["Venue",tournament?.venue||"TBD"],["Date",tournament?.date||"TBD"],["Capacity",capacity>0?`${activeRegs.length}/${capacity} spots filled`:"Unlimited"]].map(([l,v])=>(
                    <div key={l} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <span>{l}</span><strong style={{ color:"var(--white)" }}>{v}</strong>
                    </div>
                  ))}
                </div>
                {waitlistRegs.length>0 && (
                  <div style={{ marginTop:14, padding:"10px 12px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.2)", borderRadius:4, fontSize:12, color:"#5b8dee" }}>
                    ⏳ {waitlistRegs.length} {waitlistRegs.length===1?"team":"teams"} on the waitlist
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ADD WALK-UP PLAYER ────────────────────────────────────────────── */}
          {tab==="add_player" && (
            <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:4,display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ width:4,height:20,background:"var(--gold)",display:"inline-block",borderRadius:2 }}/>
                  Walk-Up Registration
                </div>
                <div style={{ fontSize:12,color:"var(--muted)",marginBottom:14 }}>Add a player on the spot — guest entries don't affect Power Rankings or Elo.</div>
                <Fld label="Full Name *"><input style={css.input} value={playerForm.name} onChange={e=>updP("name",e.target.value)} placeholder="First Last" autoComplete="off" /></Fld>
                <Fld label='Nickname'><input style={css.input} value={playerForm.nick||""} onChange={e=>updP("nick",e.target.value)} placeholder='"Laser"' /></Fld>
                <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Email"><input style={css.input} value={playerForm.email} onChange={e=>updP("email",e.target.value)} placeholder="email@example.com" /></Fld>
                  <Fld label="Phone"><input style={css.input} value={playerForm.phone} onChange={e=>updP("phone",e.target.value)} placeholder="(555) 000-0000" /></Fld>
                </div>
                <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <Fld label="Payment Status">
                    <select style={css.select} value={playerForm.paymentStatus||"unpaid"} onChange={e=>updP("paymentStatus",e.target.value)}>
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="waived">Waived</option>
                    </select>
                  </Fld>
                </div>

                {/* Existing member suggestions */}
                {playerForm.name && players.filter(p=>p.name.toLowerCase().includes(playerForm.name.toLowerCase())&&p.name.toLowerCase()!==playerForm.name.toLowerCase()).slice(0,4).length>0 && (
                  <div style={{ background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4,padding:"10px 14px",marginBottom:14 }}>
                    <div style={{ fontSize:11,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,marginBottom:6 }}>EXISTING MEMBERS — click to select</div>
                    {players.filter(p=>playerForm.name&&p.name.toLowerCase().includes(playerForm.name.toLowerCase())).slice(0,4).map(p=>(
                      <div key={p.id} onClick={()=>setPlayerForm(f=>({...f,name:p.name,nick:p.nick||"",convertToMember:false}))} style={{ padding:"6px 8px",cursor:"pointer",borderRadius:3,fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,0.03)",marginBottom:3 }}>
                        <div><span style={{ fontWeight:600 }}>{p.name}</span>{p.nick&&<span style={{ color:"var(--gold)",fontSize:11,fontStyle:"italic",marginLeft:6 }}>"{p.nick}"</span>}</div>
                        <span style={{ color:"var(--muted)",fontSize:11,fontFamily:"'Bebas Neue',sans-serif" }}>Elo {p.elo}</span>
                      </div>
                    ))}
                  </div>
                )}

                {playerForm.name && !players.find(p=>p.name.toLowerCase()===playerForm.name.toLowerCase()) && (
                  <div style={{ background:"rgba(91,141,238,0.07)",border:"1px solid rgba(91,141,238,0.2)",borderRadius:4,padding:"10px 14px",marginBottom:14 }}>
                    <label style={{ display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer" }}>
                      <input type="checkbox" checked={!!playerForm.convertToMember} onChange={e=>updP("convertToMember",e.target.checked)} style={{ marginTop:2,accentColor:"#5b8dee",width:15,height:15,flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:13,fontWeight:700,color:"#5b8dee" }}>Register as Full Member</div>
                        <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>Adds to global roster. Elo + stats tracked in ranked events.</div>
                      </div>
                    </label>
                    {!playerForm.convertToMember && <div style={{ marginTop:8,fontSize:11,color:"var(--muted)",display:"flex",alignItems:"center",gap:5 }}><span style={{ background:"rgba(255,180,0,0.15)",color:"#ffb400",border:"1px solid rgba(255,180,0,0.3)",borderRadius:3,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:1 }}>GUEST</span>Won't affect rankings or Elo.</div>}
                  </div>
                )}

                {isFull && <div style={{ padding:"8px 12px",background:"rgba(91,141,238,0.08)",border:"1px solid rgba(91,141,238,0.25)",borderRadius:4,fontSize:12,color:"#5b8dee",marginBottom:10 }}>⏳ Event is full — will be added to waitlist.</div>}
                <Btn onClick={addWalkUpPlayer} disabled={!playerForm.name} style={{ marginTop:4,width:"100%",justifyContent:"center" }}>
                  {players.find(p=>p.name.toLowerCase()===playerForm.name.toLowerCase()) ? "Add Existing Member" : playerForm.convertToMember ? "Register as Member" : "Register as Guest"}
                </Btn>
              </div>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:4,display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ width:4,height:20,background:"var(--gold)",display:"inline-block",borderRadius:2 }}/>
                  Member Roster
                </div>
                <div style={{ fontSize:12,color:"var(--muted)",marginBottom:12 }}>{players.length} members in system</div>
                <div style={{ maxHeight:320,overflow:"auto" }}>
                  {players.map(p=>(
                    <div key={p.id} onClick={()=>setPlayerForm(f=>({...f,name:p.name,nick:p.nick||"",convertToMember:false}))} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 8px",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:13,cursor:"pointer",borderRadius:3 }}>
                      <div><span style={{ fontWeight:600 }}>{p.name}</span>{p.nick&&<span style={{ color:"var(--gold)",fontSize:11,fontStyle:"italic",marginLeft:6 }}>"{p.nick}"</span>}</div>
                      <span style={{ color:"var(--muted)",fontFamily:"'Bebas Neue',sans-serif",fontSize:14 }}>Elo {p.elo}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── PAIR SOLOS ────────────────────────────────────────────────────── */}
          {tab==="pair_solos" && (
            <div>
              <div style={{ marginBottom:16, fontSize:13, color:"var(--muted)", lineHeight:1.7 }}>
                Select two solo/guest registrants to pair them into a team. Both entries will be removed and replaced with the new team registration.
              </div>
              {solos.length < 2 ? (
                <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--muted)" }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🤝</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:3, color:"var(--white)", marginBottom:8 }}>No Solo Players to Pair</div>
                  <div style={{ fontSize:13 }}>Add walk-up solo players first, then pair them here.</div>
                </div>
              ) : (
                <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
                  <div style={{ ...css.card, padding:20 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:14,color:"var(--gold)" }}>
                      Select Players to Pair
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>Solo Registrants ({solos.length})</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {solos.map(r=>{
                          const isA = pairA===r.id, isB = pairB===r.id;
                          const selected = isA||isB;
                          return (
                            <div key={r.id} onClick={()=>{
                              if (isA) { setPairA(null); return; }
                              if (isB) { setPairB(null); return; }
                              if (!pairA) setPairA(r.id);
                              else if (!pairB) setPairB(r.id);
                            }} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:5, border:`1px solid ${selected?"rgba(201,168,76,0.5)":"var(--navy-border)"}`, background:selected?"rgba(201,168,76,0.08)":"rgba(255,255,255,0.02)", cursor:"pointer", transition:"all 0.15s" }}>
                              <div style={{ width:28, height:28, borderRadius:"50%", background:isA?"rgba(201,168,76,0.2)":isB?"rgba(46,204,113,0.2)":"rgba(255,255,255,0.05)", border:`1px solid ${isA?"var(--gold)":isB?"var(--green)":"var(--navy-border)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontFamily:"'Bebas Neue',sans-serif", color:isA?"var(--gold)":isB?"var(--green)":"var(--muted)", flexShrink:0 }}>
                                {isA?"A":isB?"B":""}
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:600, fontSize:13, color:selected?"var(--white)":"var(--muted)" }}>{r.teamName}</div>
                                <div style={{ fontSize:10,color:"var(--muted)" }}>{r.players?.[0]?.email||"no email"} · {r.status}</div>
                              </div>
                              <div style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{r.guest?"GUEST":"SOLO"}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div style={{ ...css.card, padding:20 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:14,color:"var(--gold)" }}>
                      New Team
                    </div>
                    {pairA && pairB ? (
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid var(--navy-border)", marginBottom:14 }}>
                          <div style={{ flex:1, textAlign:"center" }}>
                            <div style={{ fontSize:11,color:"var(--muted)",marginBottom:4,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>PLAYER A</div>
                            <div style={{ fontWeight:700, color:"var(--gold)" }}>{allRegs.find(r=>r.id===pairA)?.teamName}</div>
                          </div>
                          <div style={{ fontSize:18, color:"var(--muted)" }}>+</div>
                          <div style={{ flex:1, textAlign:"center" }}>
                            <div style={{ fontSize:11,color:"var(--muted)",marginBottom:4,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>PLAYER B</div>
                            <div style={{ fontWeight:700, color:"var(--green)" }}>{allRegs.find(r=>r.id===pairB)?.teamName}</div>
                          </div>
                        </div>
                        <Fld label="Team Name *">
                          <input style={css.input} value={pairName} onChange={e=>setPairName(e.target.value)} placeholder="e.g. Webb & Morris" autoFocus />
                        </Fld>
                        <div style={{ fontSize:12,color:"var(--muted)",marginBottom:14,lineHeight:1.6 }}>
                          Both solo entries will be removed and replaced with this team registration. Payment status will be set to paid only if both originals are paid.
                        </div>
                        <Btn onClick={confirmPair} disabled={!pairName} style={{ width:"100%",justifyContent:"center" }}>🤝 Create Team</Btn>
                      </div>
                    ) : (
                      <div style={{ textAlign:"center", padding:"30px 10px", color:"var(--muted)" }}>
                        <div style={{ fontSize:32,marginBottom:8 }}>👥</div>
                        <div style={{ fontSize:13 }}>
                          {!pairA ? "Select Player A from the list" : "Now select Player B"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── EVENT SETTINGS ────────────────────────────────────────────────── */}
          {tab==="settings" && (
            <div className="resp-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:16,color:"var(--gold)" }}>Registration Settings</div>
                <Fld label="Max Teams / Entries (0 = unlimited)">
                  <input style={css.input} type="number" min="0" value={capacity||""} onChange={e=>saveCapacity(e.target.value)} placeholder="Unlimited" />
                </Fld>
                <Fld label="Entry Fee ($)">
                  <input style={{ ...css.input }} type="number" min="0" value={tournament?.entryFee||""} onChange={e=>setTournaments(prev=>prev.map(t=>t.id===selectedTournament?{...t,entryFee:e.target.value}:t))} placeholder="0 = free event" />
                </Fld>
                <div style={{ height:1,background:"var(--navy-border)",margin:"14px 0" }} />
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",color:"var(--muted)",marginBottom:10 }}>Bulk Actions</div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  <Btn v="ghost" onClick={bulkCheckInRegistered} style={{ justifyContent:"flex-start" }}>✓ Check In All Registered ({registeredCt})</Btn>
                  <Btn v="ghost" onClick={()=>bulkMarkPaid()} style={{ justifyContent:"flex-start" }}>💰 Mark All Entries Paid</Btn>
                  <Btn v="ghost" onClick={()=>bulkMarkPaid("checked_in")} style={{ justifyContent:"flex-start" }}>💰 Mark Checked-In Paid ({checkedIn})</Btn>
                  {waitlistRegs.length>0&&!isFull && <Btn v="ghost" onClick={promoteFromWaitlist} style={{ justifyContent:"flex-start" }}>⬆ Promote Next from Waitlist</Btn>}
                </div>
              </div>
              <div style={{ ...css.card, padding:20 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase",marginBottom:14,color:"var(--gold)" }}>Payment Summary</div>
                {[
                  ["Paid",    allRegs.filter(r=>r.paymentStatus==="paid").length,   "var(--green)"],
                  ["Unpaid",  allRegs.filter(r=>r.paymentStatus==="unpaid").length,  "var(--red)"],
                  ["Waived",  allRegs.filter(r=>r.paymentStatus==="waived").length,  "#5b8dee"],
                ].map(([l,v,c])=>(
                  <div key={l} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ fontSize:13,fontWeight:600 }}>{l}</div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:26,color:c }}>{v}</div>
                  </div>
                ))}
                {tournament?.entryFee>0 && (
                  <div style={{ marginTop:14,padding:"10px 14px",background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4,fontSize:13 }}>
                    <div style={{ color:"var(--muted)",marginBottom:4 }}>Collected (est.)</div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"var(--gold)" }}>${allRegs.filter(r=>r.paymentStatus==="paid").length * Number(tournament.entryFee)}</div>
                    <div style={{ fontSize:11,color:"var(--muted)" }}>at ${tournament.entryFee}/entry</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}


      {/* ── WAITLIST TAB ──────────────────────────────────────────────────────── */}
      {tab === "waitlist" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Header + promote-all */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
            <div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3 }}>Waitlist</div>
              <div style={{ fontSize:12, color:"var(--muted)" }}>{waitlistRegs.length} team{waitlistRegs.length!==1?"s":""} waiting · Capacity: {capacity||"∞"} · Active: {activeRegs.length}</div>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {waitlistRegs.length > 0 && capacity && activeRegs.length < capacity && (
                <Btn onClick={()=>promoteFromWaitlist(waitlistRegs[0]?.id)}>⬆ Promote Next</Btn>
              )}
              {waitlistRegs.length > 1 && capacity && (capacity - activeRegs.length) > 1 && (
                <Btn v="ghost" onClick={()=>bulkPromoteWaitlist(capacity-activeRegs.length)}>
                  ⬆ Promote All {Math.min(waitlistRegs.length, capacity-activeRegs.length)} Available
                </Btn>
              )}
            </div>
          </div>

          {/* Notify log */}
          {notifyLog.length > 0 && (
            <div style={{...css.card,padding:0,overflow:"hidden",borderColor:"rgba(91,141,238,0.3)"}}>
              <div style={{padding:"8px 14px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:10,color:"#5b8dee",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>📣 Notification Log ({notifyLog.length})</span>
                <button onClick={()=>setNotifyLog([])} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:11}}>Clear</button>
              </div>
              {notifyLog.slice().reverse().slice(0,5).map((n,i)=>(
                <div key={i} style={{padding:"8px 14px",borderBottom:i<Math.min(notifyLog.length,5)-1?"1px solid rgba(255,255,255,0.04)":"none",fontSize:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontWeight:700,color:"#5b8dee"}}>{n.name}</span>
                    <span style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>{n.ts}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--muted)"}}>{n.msg}</div>
                </div>
              ))}
            </div>
          )}

          {/* Capacity gauge */}
          {capacity > 0 && (
            <div style={{ ...css.card, padding:"12px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12 }}>
                <span style={{ color:"var(--muted)" }}>Capacity</span>
                <span style={{ fontWeight:700 }}>{activeRegs.length} / {capacity}</span>
              </div>
              <div style={{ height:8, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${Math.min(100,Math.round(activeRegs.length/capacity*100))}%`, background:activeRegs.length>=capacity?"var(--red)":activeRegs.length>=capacity*0.9?"var(--gold)":"var(--green)", borderRadius:4, transition:"width 0.3s" }} />
              </div>
              <div style={{ fontSize:11, color:"var(--muted)", marginTop:6 }}>
                {capacity > activeRegs.length
                  ? `${capacity-activeRegs.length} spot${capacity-activeRegs.length!==1?"s":""} available — ${waitlistRegs.length} waiting`
                  : "Event is at capacity"}
              </div>
            </div>
          )}

          {waitlistRegs.length === 0 ? (
            <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Waitlist Empty</div>
              <div style={{ fontSize:13 }}>No teams on the waitlist for this event</div>
            </div>
          ) : (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              {waitlistRegs.map((r,i)=>(
                <div key={r.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderBottom:i<waitlistRegs.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#1e3250,#3a5478)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, flexShrink:0 }}>
                    {i+1}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>{r.teamName}</div>
                    <div style={{ fontSize:11, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {r.players?.map(p=>p.name).join(", ")||"No players listed"}
                      {r.registeredAt && <span style={{ marginLeft:8 }}>· Joined {r.registeredAt}</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <Badge color={r.paymentStatus==="paid"?"green":""}>{r.paymentStatus==="paid"?"PAID":"UNPAID"}</Badge>
                    <Btn sz="sm" onClick={()=>promoteFromWaitlist(r.id)}
                      disabled={!!(capacity && activeRegs.length >= capacity)}
                      style={{ fontSize:10, padding:"3px 8px" }}>
                      ⬆ Promote
                    </Btn>
                    <Btn sz="sm" v="danger" onClick={()=>removeFromWaitlist(r.id)} style={{ fontSize:10, padding:"3px 8px" }}>✕</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add to waitlist shortcut */}
          <div style={{ ...css.card, padding:"12px 16px", border:"1px dashed var(--navy-border)" }}>
            <div style={{ fontSize:12, color:"var(--muted)", marginBottom:10 }}>
              💡 To add a team to the waitlist, use <strong style={{color:"var(--white)"}}>Add Team</strong> and set status to Waitlist, or register when the event is at capacity.
            </div>
            <Btn v="ghost" sz="sm" onClick={()=>setTab("add_team")}>➕ Add Team →</Btn>
          </div>
        </div>
      )}

      {/* ── BULK IMPORT TAB ────────────────────────────────────────────────────── */}
      {tab === "bulk_import" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Instructions */}
          <div style={{ padding:"12px 14px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.25)", borderRadius:5, fontSize:12, color:"var(--muted)", lineHeight:1.7 }}>
            <div style={{ fontWeight:700, color:"var(--white)", marginBottom:4 }}>📥 CSV Bulk Import</div>
            One row per team. Columns: <strong style={{color:"var(--white)"}}>Team Name, Player 1, Player 2, Email, Payment</strong>
            <br/>Example: <code style={{color:"#5b8dee",background:"rgba(91,141,238,0.1)",padding:"1px 6px",borderRadius:3}}>Thunder Cups, Alex Smith, Sam Lee, alex@mail.com, paid</code>
            <br/>Payment column: "paid" marks as paid; anything else = unpaid.
          </div>

          {/* CSV input */}
          <div>
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>
              Paste CSV Data
            </div>
            <textarea
              style={{ ...css.input, minHeight:180, resize:"vertical", fontFamily:"monospace", fontSize:12 }}
              placeholder={"Team Name,Player 1,Player 2,Email,Payment\nThunder Cups,Alex Smith,Sam Lee,alex@mail.com,paid\nFire Pong,Jordan Kim,,,unpaid"}
              value={importRaw}
              onChange={e=>{ setImportRaw(e.target.value); setImportParsed([]); setImportErrors([]); }}
            />
          </div>

          <div style={{ display:"flex", gap:8 }}>
            <Btn disabled={!importRaw.trim()||!tournament} onClick={()=>parseImportCSV(importRaw)} style={{ flex:1, justifyContent:"center" }}>
              🔍 Preview Import
            </Btn>
            {importParsed.length > 0 && (
              <Btn disabled={importConfirmed} onClick={confirmImport} style={{ flex:1, justifyContent:"center", background:"rgba(46,204,113,0.12)", borderColor:"rgba(46,204,113,0.45)", color:"var(--green)" }}>
                {importConfirmed ? "✓ Imported!" : `✓ Confirm ${importParsed.length} Teams`}
              </Btn>
            )}
          </div>

          {/* Error banner */}
          {importErrors.length > 0 && (
            <div style={{ padding:"10px 14px", background:"rgba(217,79,61,0.07)", border:"1px solid rgba(217,79,61,0.3)", borderRadius:4, fontSize:12 }}>
              <div style={{ color:"var(--red)", fontWeight:700, marginBottom:6 }}>⚠ {importErrors.length} error{importErrors.length!==1?"s":""}</div>
              {importErrors.map((e,i)=><div key={i} style={{ color:"var(--muted)", fontSize:11 }}>{e}</div>)}
            </div>
          )}

          {/* Preview table */}
          {importParsed.length > 0 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)" }}>Preview — {importParsed.length} Teams</span>
              </div>
              {importParsed.map((r,i)=>(
                <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<importParsed.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ width:20, fontSize:11, color:"var(--muted)", textAlign:"center" }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{r.teamName}</div>
                    <div style={{ fontSize:11, color:"var(--muted)" }}>{r.players?.map(p=>p.name).filter(Boolean).join(", ")||"No players"}</div>
                  </div>
                  <Badge color={r.paymentStatus==="paid"?"green":""}>{r.paymentStatus==="paid"?"PAID":"UNPAID"}</Badge>
                </div>
              ))}
            </div>
          )}

          {/* Team merge section */}
          <div style={{ borderTop:"2px solid var(--navy-border)", paddingTop:14, marginTop:4 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:2 }}>Team Merge</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Combine two solo registrations into a team</div>
              </div>
              <Btn v={mergeMode?"danger":"ghost"} sz="sm" onClick={()=>{ setMergeMode(m=>!m); setMergeSelected([]); }}>
                {mergeMode ? "Cancel" : "⊕ Merge Mode"}
              </Btn>
            </div>

            {mergeMode && (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ fontSize:12, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                  Select 2 registrations to merge ({mergeSelected.length}/2 selected)
                </div>
                <div style={{ ...css.card, padding:0, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
                  {(tournament?.registrations||[]).map((r,i)=>{
                    const sel = mergeSelected.includes(r.id);
                    return (
                      <div key={r.id} onClick={()=>{
                        if (sel) setMergeSelected(s=>s.filter(x=>x!==r.id));
                        else if (mergeSelected.length < 2) setMergeSelected(s=>[...s,r.id]);
                      }} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<(tournament.registrations.length-1)?"1px solid rgba(255,255,255,0.04)":"none", cursor:"pointer", background:sel?"rgba(201,168,76,0.1)":"transparent", borderLeft:`3px solid ${sel?"var(--gold)":"transparent"}` }}>
                        <div style={{ width:16, height:16, borderRadius:3, border:`2px solid ${sel?"var(--gold)":"var(--navy-border)"}`, background:sel?"var(--gold)":"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                          {sel && <span style={{ fontSize:10, color:"var(--navy)", fontWeight:900 }}>✓</span>}
                        </div>
                        <span style={{ flex:1, fontSize:13, fontWeight:sel?700:400 }}>{r.teamName}</span>
                        <span style={{ fontSize:11, color:"var(--muted)" }}>{r.players?.map(p=>p.name).join(", ")||"—"}</span>
                      </div>
                    );
                  })}
                </div>

                {mergeSelected.length === 2 && (
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <input style={{ ...css.input, flex:1 }} placeholder="New merged team name…"
                      value={mergeName} onChange={e=>setMergeName(e.target.value)} />
                    <Btn disabled={!mergeName.trim()} onClick={doMerge}>Merge →</Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Inline Edit Registration Modal */}
      <Modal open={!!editModal} onClose={()=>setEditModal(null)} title={`Edit: ${editModal?.teamName||""}`} width={520}>
        {editModal && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:4 }}>
              <Fld label="Team / Entry Name *">
                <input style={css.input} value={editForm.teamName} onChange={e=>setEditForm(f=>({...f,teamName:e.target.value}))} />
              </Fld>
              <Fld label="Seed #">
                <input style={css.input} type="number" min="1" value={editForm.seed} onChange={e=>setEditForm(f=>({...f,seed:e.target.value}))} placeholder="—" />
              </Fld>
            </div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:8 }}>Player 1</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
              <Fld label="Name"><input style={css.input} value={editForm.p1name} onChange={e=>setEditForm(f=>({...f,p1name:e.target.value}))} /></Fld>
              <Fld label="Email"><input style={css.input} value={editForm.p1email} onChange={e=>setEditForm(f=>({...f,p1email:e.target.value}))} /></Fld>
            </div>
            {(editModal.players?.length > 1 || editForm.p2name) && (
              <>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", marginBottom:8 }}>Player 2</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <Fld label="Name"><input style={css.input} value={editForm.p2name} onChange={e=>setEditForm(f=>({...f,p2name:e.target.value}))} /></Fld>
                  <Fld label="Email"><input style={css.input} value={editForm.p2email} onChange={e=>setEditForm(f=>({...f,p2email:e.target.value}))} /></Fld>
                </div>
              </>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <Fld label="Status">
                <select style={css.select} value={editForm.status} onChange={e=>setEditForm(f=>({...f,status:e.target.value}))}>
                  <option value="registered">Registered</option>
                  <option value="checked_in">Checked In</option>
                  <option value="waitlist">Waitlist</option>
                  <option value="no_show">No Show</option>
                </select>
              </Fld>
              <Fld label="Payment">
                <select style={css.select} value={editForm.paymentStatus} onChange={e=>setEditForm(f=>({...f,paymentStatus:e.target.value}))}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="waived">Waived</option>
                </select>
              </Fld>
              <Fld label="Amount ($)">
                <input style={css.input} type="number" min="0" value={editForm.paymentAmount} onChange={e=>setEditForm(f=>({...f,paymentAmount:e.target.value}))} placeholder="0" />
              </Fld>
            </div>
            <Fld label="Notes">
              <textarea style={{ ...css.input, resize:"vertical", minHeight:52, fontFamily:"inherit" }} value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} placeholder="Seeding requests, accommodations…" />
            </Fld>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
              <Btn v="ghost" onClick={()=>setEditModal(null)}>Cancel</Btn>
              <Btn onClick={saveEditModal} disabled={!editForm.teamName}>Save Changes</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Confirm Remove Modal */}
      <Modal open={!!confirmRemove} onClose={()=>setConfirmRemove(null)} title="Remove Registration" width={380}>
        <div style={{ fontSize:14,marginBottom:20,color:"var(--muted)",lineHeight:1.6 }}>Are you sure you want to remove this registration? This cannot be undone.</div>
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
          <Btn v="ghost" onClick={()=>setConfirmRemove(null)}>Cancel</Btn>
          <Btn v="danger" onClick={()=>removeReg(confirmRemove)}>Remove</Btn>
        </div>
      </Modal>
    </div>
  );
}


// ── CHECK-IN PAGE (KIOSK MODE) ────────────────────────────────────────────────
function CheckInPage({ tournaments, setTournaments, players, setPage }) {
  const { isMobile } = useBreakpoint();

  // ── State ────────────────────────────────────────────────────────────────
  const [selectedTournament, setSelectedTournament] = useState("");
  const [tab,                setTab]                = useState("staff");
  const [search,             setSearch]             = useState("");
  const [filterStatus,       setFilterStatus]       = useState("all");
  const [selectedIds,        setSelectedIds]        = useState([]);
  const [lastCheckedIn,      setLastCheckedIn]      = useState(null);

  // Kiosk state machine
  const [kioskStep,     setKioskStep]     = useState("idle");  // idle|scanning|found|verify|payment|success|notfound
  const [kioskFound,    setKioskFound]    = useState(null);
  const [kioskScanPct,  setKioskScanPct]  = useState(0);
  const [kioskSettings, setKioskSettings] = useState({ requirePayment:false, showSeed:true, autoClear:4 });
  const [cashDisplay,   setCashDisplay]   = useState("0");

  // Walk-up state
  const [wuStep, setWuStep] = useState(1);
  const [wuForm, setWuForm] = useState({ teamName:"", p1:"", p2:"", payStatus:"unpaid", payAmount:"", payMethod:"cash", notes:"" });
  const updWu = (k,v) => setWuForm(f=>({...f,[k]:v}));

  // Payment modal (staff + paylog)
  const [payModal,       setPayModal]       = useState(null);
  const [payModalAmount, setPayModalAmount] = useState("");
  const [paymentNote,    setPaymentNote]    = useState("");

  // ── Derived ──────────────────────────────────────────────────────────────
  const upcoming   = tournaments.filter(t => t.status === "upcoming" || t.status === "active");
  const tournament = tournaments.find(t => t.id === selectedTournament);
  const regs       = tournament?.registrations || [];
  const entryFee   = parseFloat(tournament?.entryFee || 0);

  const checkedIn  = regs.filter(r => r.status === "checked_in");
  const pending    = regs.filter(r => r.status === "registered" || r.status === "waitlist");
  const noShows    = regs.filter(r => r.status === "no_show");
  const allUnpaid  = regs.filter(r => r.paymentStatus !== "paid" && r.paymentStatus !== "waived");
  const unpaidIn   = checkedIn.filter(r => r.paymentStatus !== "paid" && r.paymentStatus !== "waived");
  const pct        = regs.length ? Math.round(checkedIn.length / regs.length * 100) : 0;
  const allIn      = regs.length > 0 && pending.length === 0;
  const payLog     = regs.filter(r => r.paymentStatus === "paid" || r.paymentStatus === "waived");

  const filteredRegs = regs.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.teamName.toLowerCase().includes(q) || r.players?.some(p=>p.name.toLowerCase().includes(q));
    if (!matchSearch) return false;
    if (filterStatus === "pending") return r.status === "registered" || r.status === "waitlist";
    if (filterStatus === "checked") return r.status === "checked_in";
    if (filterStatus === "noshow")  return r.status === "no_show";
    if (filterStatus === "unpaid")  return r.paymentStatus !== "paid" && r.paymentStatus !== "waived";
    return true;
  });

  const arrivals = [...checkedIn].sort((a,b) => {
    const ta = a.checkedInAt ? new Date("1970/1/1 "+a.checkedInAt) : 0;
    const tb = b.checkedInAt ? new Date("1970/1/1 "+b.checkedInAt) : 0;
    return ta - tb;
  });

  // ── Mutations ────────────────────────────────────────────────────────────
  function patchReg(regId, patch) {
    setTournaments(prev => prev.map(t =>
      t.id !== selectedTournament ? t : {
        ...t, registrations: t.registrations.map(r => r.id !== regId ? r : { ...r, ...patch })
      }
    ));
  }

  function checkIn(regId) {
    const reg = regs.find(r => r.id === regId);
    patchReg(regId, { status:"checked_in", checkedInAt: new Date().toLocaleTimeString() });
    setLastCheckedIn({ name:reg?.teamName, unpaid:reg?.paymentStatus!=="paid"&&reg?.paymentStatus!=="waived" });
    setSearch("");
    setTimeout(() => setLastCheckedIn(null), 4000);
  }

  function undoCheckIn(regId) { patchReg(regId, { status:"registered", checkedInAt:null }); }
  function markNoShow(regId)   { patchReg(regId, { status:"no_show" }); }

  function collectPayment(regId, amount, note) {
    patchReg(regId, { paymentStatus:"paid", payAmount:amount, payMethod:note||"cash", paidAt:new Date().toLocaleString() });
  }
  function waivePayment(regId) { patchReg(regId, { paymentStatus:"waived", paidAt:new Date().toLocaleString() }); }

  function bulkAction(action) {
    const ids = selectedIds.length > 0 ? selectedIds : filteredRegs.map(r=>r.id);
    const now = new Date().toLocaleTimeString();
    setTournaments(prev => prev.map(t =>
      t.id !== selectedTournament ? t : {
        ...t, registrations: t.registrations.map(r => {
          if (!ids.includes(r.id)) return r;
          if (action === "checkin" && r.status !== "checked_in") return { ...r, status:"checked_in", checkedInAt:now };
          if (action === "noshow" && (r.status==="registered"||r.status==="waitlist")) return { ...r, status:"no_show" };
          if (action === "late" && r.status==="registered") return { ...r, status:"checked_in", lateArrival:true, checkedInAt:now };
          return r;
        })
      }
    ));
    setSelectedIds([]);
  }

  function toggleSelect(id) { setSelectedIds(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]); }

  function completeWalkUp() {
    if (!wuForm.teamName) return;
    const newReg = {
      id: uid(), teamName: wuForm.teamName,
      players: [wuForm.p1&&{id:uid(),name:wuForm.p1}, wuForm.p2&&{id:uid(),name:wuForm.p2}].filter(Boolean),
      status:"checked_in", checkedInAt: new Date().toLocaleTimeString(),
      paymentStatus: wuForm.payStatus === "paid" ? "paid" : wuForm.payStatus === "waived" ? "waived" : "unpaid",
      payAmount: wuForm.payAmount || null, payMethod: wuForm.payMethod || null,
      paidAt: wuForm.payStatus==="paid" ? new Date().toLocaleString() : null,
      notes: wuForm.notes, walkUp:true, seed:null,
    };
    setTournaments(prev => prev.map(t =>
      t.id !== selectedTournament ? t : { ...t, registrations:[...(t.registrations||[]), newReg] }
    ));
    setLastCheckedIn({ name:wuForm.teamName, unpaid:newReg.paymentStatus==="unpaid" });
    setWuForm({ teamName:"", p1:"", p2:"", payStatus:"unpaid", payAmount:"", payMethod:"cash", notes:"" });
    setWuStep(1); setTab("staff");
    setTimeout(() => setLastCheckedIn(null), 4000);
  }

  // ── Kiosk logic ──────────────────────────────────────────────────────────
  function startKioskScan() {
    if (!tournament) return;
    setKioskStep("scanning"); setKioskScanPct(0); setKioskFound(null);
    let pct = 0;
    const interval = setInterval(() => {
      pct += Math.random()*16 + 6;
      setKioskScanPct(Math.min(Math.round(pct), 100));
      if (pct >= 100) {
        clearInterval(interval);
        const notFound = Math.random() < 0.15 || pending.length === 0;
        if (notFound) {
          setKioskStep("notfound");
          setTimeout(() => setKioskStep("idle"), 3500);
        } else {
          const found = pending[Math.floor(Math.random() * pending.length)];
          setKioskFound(found); setKioskStep("found");
          setTimeout(() => setKioskStep("verify"), 700);
        }
      }
    }, 80);
  }

  function kioskCheckIn() {
    if (!kioskFound) return;
    const needsPayment = kioskSettings.requirePayment &&
      kioskFound.paymentStatus !== "paid" && kioskFound.paymentStatus !== "waived";
    if (needsPayment) { setCashDisplay("0"); setKioskStep("payment"); }
    else doKioskSuccess(kioskFound.id);
  }

  function doKioskSuccess(regId) {
    checkIn(regId); setKioskStep("success");
    setTimeout(() => { setKioskStep("idle"); setKioskFound(null); setCashDisplay("0"); }, (kioskSettings.autoClear||4)*1000);
  }

  function kioskNumpad(val) {
    setCashDisplay(prev => {
      if (val==="C")  return "0";
      if (val==="⌫") return prev.length>1 ? prev.slice(0,-1) : "0";
      if (val==="."  && prev.includes(".")) return prev;
      if (prev==="0" && val!==".") return val;
      return prev + val;
    });
  }

  function kioskCollectCash() {
    if (!kioskFound) return;
    const amt = parseFloat(cashDisplay)||0;
    collectPayment(kioskFound.id, amt, "cash");
    doKioskSuccess(kioskFound.id);
  }

  const kioskChange = (() => {
    const paid = parseFloat(cashDisplay)||0;
    return paid>0 && entryFee>0 ? Math.max(0, paid-entryFee) : null;
  })();

  // ── Payment modal render ──────────────────────────────────────────────────
  function renderPayModal() {
    if (!payModal) return null;
    const reg    = regs.find(r=>r.id===payModal);
    const amtNum = parseFloat(payModalAmount)||0;
    const change = entryFee>0 && amtNum>entryFee ? amtNum-entryFee : null;
    return (
      <Modal open={true} onClose={()=>setPayModal(null)} title="Collect Payment" width={360}>
        <div style={{ marginBottom:12, fontSize:14, fontWeight:700 }}>{reg?.teamName}</div>
        {entryFee>0 && <div style={{ fontSize:12, color:"var(--muted)", marginBottom:14 }}>Entry fee: <strong style={{ color:"var(--gold)" }}>${entryFee}</strong></div>}
        <Fld label="Amount Received ($)">
          <input style={{ ...css.input, fontSize:28, fontFamily:"'Bebas Neue',sans-serif", textAlign:"center" }}
            type="number" min="0" step="0.01" value={payModalAmount}
            onChange={e=>setPayModalAmount(e.target.value)} autoFocus placeholder="0.00" />
        </Fld>
        {change!==null && (
          <div style={{ padding:"10px 14px", marginBottom:12, background:"rgba(46,204,113,0.08)", border:"1px solid rgba(46,204,113,0.3)", borderRadius:5, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:13, color:"var(--muted)" }}>Change due</span>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"var(--green)" }}>${change.toFixed(2)}</span>
          </div>
        )}
        <Fld label="Note (optional)">
          <input style={css.input} value={paymentNote} onChange={e=>setPaymentNote(e.target.value)} placeholder="cash, Venmo, card…" />
        </Fld>
        <div style={{ display:"flex", gap:8, marginTop:14 }}>
          <Btn v="ghost" onClick={()=>{ waivePayment(payModal); setPayModal(null); setPaymentNote(""); }} style={{ flex:1, justifyContent:"center" }}>Waive</Btn>
          <Btn v="ghost" onClick={()=>setPayModal(null)} style={{ flex:1, justifyContent:"center" }}>Cancel</Btn>
          <Btn onClick={()=>{ collectPayment(payModal, amtNum||entryFee, paymentNote||"cash"); setPayModal(null); setPaymentNote(""); }} style={{ flex:1, justifyContent:"center" }}>✓ Paid</Btn>
        </div>
      </Modal>
    );
  }

  function renderTournamentPicker() {
    return (
      <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>🎟</div>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:16 }}>Select a Tournament</div>
        <select style={{ ...css.select, maxWidth:320, fontSize:14 }} value={selectedTournament} onChange={e=>setSelectedTournament(e.target.value)}>
          <option value="">— choose event —</option>
          {upcoming.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
    );
  }

  // ── STAFF VIEW ───────────────────────────────────────────────────────────
  function renderStaff() {
    if (!tournament) return renderTournamentPicker();
    return (
      <div>
        {lastCheckedIn && (
          <div style={{ marginBottom:14, padding:"12px 16px", background:lastCheckedIn.unpaid?"rgba(201,168,76,0.1)":"rgba(46,204,113,0.1)", border:`1px solid ${lastCheckedIn.unpaid?"rgba(201,168,76,0.4)":"rgba(46,204,113,0.4)"}`, borderRadius:6, display:"flex", alignItems:"center", gap:10, animation:"fadeIn 0.2s" }}>
            <span style={{ fontSize:22 }}>{lastCheckedIn.unpaid?"⚠️":"✅"}</span>
            <div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1, color:lastCheckedIn.unpaid?"var(--gold)":"var(--green)" }}>
                {lastCheckedIn.unpaid?"CHECKED IN — PAYMENT DUE":"CHECKED IN!"}
              </div>
              <div style={{ fontSize:13, color:"var(--white)" }}>{lastCheckedIn.name}</div>
            </div>
          </div>
        )}

        {/* Stats strip */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8, marginBottom:12 }}>
          {[[checkedIn.length,"In","var(--green)"],[pending.length,"Pending","var(--gold)"],[noShows.length,"No-Show","var(--muted)"],[unpaidIn.length,"Unpaid ✓",unpaidIn.length>0?"var(--red)":"var(--muted)"],[pct+"%","Done",pct===100?"var(--gold)":"var(--white)"]].map(([v,l,c])=>(
            <div key={l} style={{ ...css.card, padding:"10px 6px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:c, lineHeight:1 }}>{v}</div>
              <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ height:4, background:"rgba(255,255,255,0.07)", borderRadius:2, overflow:"hidden", marginBottom:12 }}>
          <div style={{ height:"100%", width:`${pct}%`, background:pct===100?"var(--gold)":"var(--green)", transition:"width 0.4s", borderRadius:2 }} />
        </div>

        {/* Toolbar */}
        <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
          <input style={{ ...css.input, flex:1, minWidth:140, maxWidth:240 }} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} />
          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
            {[["all","All"],["pending","Pending"],["checked","✓ In"],["noshow","No-Show"],["unpaid","Unpaid"]].map(([v,l])=>(
              <button key={v} onClick={()=>setFilterStatus(v)} style={{ padding:"4px 9px", borderRadius:4, border:`1px solid ${filterStatus===v?"var(--gold)":"var(--navy-border)"}`, background:filterStatus===v?"rgba(201,168,76,0.12)":"transparent", color:filterStatus===v?"var(--gold)":"var(--muted)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer", letterSpacing:1 }}>{l}</button>
            ))}
          </div>
          {selectedIds.length>0 ? (
            <>
              <span style={{ fontSize:11, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{selectedIds.length} selected</span>
              <Btn sz="sm" v="success" onClick={()=>bulkAction("checkin")}>✓ Check In</Btn>
              <Btn sz="sm" v="danger"  onClick={()=>bulkAction("noshow")}>✕ No-Show</Btn>
              <Btn sz="sm" v="ghost" onClick={()=>bulkAction("late")} style={{color:"var(--gold)",borderColor:"rgba(201,168,76,0.4)"}}>⚠ Late</Btn>
              <Btn sz="sm" v="ghost"   onClick={()=>setSelectedIds([])}>Clear</Btn>
            </>
          ) : (
            <>
              <Btn sz="sm" v="ghost" onClick={()=>setSelectedIds(filteredRegs.map(r=>r.id))}>Select All</Btn>
              {pending.length>0 && <Btn sz="sm" v="ghost" onClick={()=>bulkAction("noshow")}>Mark Remaining No-Show</Btn>}
            </>
          )}
          <Btn sz="sm" onClick={()=>setTab("kiosk")} style={{ marginLeft:"auto" }}>📡 Kiosk</Btn>
          <Btn sz="sm" v="ghost" onClick={()=>{
            // Bulk check-in all currently filtered/pending registrations
            const pendingIds = filteredRegs.filter(r=>r.status!=="checked_in"&&r.status!=="no_show").map(r=>r.id);
            setSelectedIds(pendingIds);
          }} style={{fontSize:10}}>☑ Select Pending</Btn>
          <Btn sz="sm" v="ghost" onClick={()=>{
            // Print check-in list
            const win = window.open("","_blank");
            const rows = regs.map(r=>`<tr><td>${r.teamName||r.playerName||""}</td><td>${(r.players||[]).map(p=>p.name||"").join(", ")}</td><td style="text-align:center">${r.status==="checked_in"?"✓":r.status==="no_show"?"✗":"—"}</td><td>${r.paymentStatus||""}</td></tr>`).join("");
            win.document.write(`<html><head><title>Check-In List</title><style>body{font-family:sans-serif;font-size:12px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:4px 8px}th{background:#eee}@media print{button{display:none}}</style></head><body><h2>Check-In Sheet — ${activeTournament?.name||"Event"}</h2><button onclick="window.print()">Print</button><table><thead><tr><th>Team</th><th>Players</th><th>Checked In</th><th>Payment</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
            win.document.close();
          }} style={{fontSize:10}}>🖨 Print List</Btn>
        </div>

        {/* List */}
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          {filteredRegs.length===0 ? (
            <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--muted)" }}>No registrations match this filter</div>
          ) : filteredRegs.map((r,i) => {
            const isIn   = r.status==="checked_in";
            const isNS   = r.status==="no_show";
            const isLate = r.lateArrival && r.status==="checked_in";
            const unpaid = r.paymentStatus!=="paid" && r.paymentStatus!=="waived";
            const sel    = selectedIds.includes(r.id);
            return (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:i<filteredRegs.length-1?"1px solid rgba(255,255,255,0.04)":"none", background:isIn&&isLate?"rgba(201,168,76,0.04)":isIn?"rgba(46,204,113,0.02)":isNS?"rgba(217,79,61,0.02)":sel?"rgba(201,168,76,0.04)":"transparent", borderLeft:`3px solid ${sel?"rgba(201,168,76,0.5)":"transparent"}`, transition:"all 0.1s" }}>
                {/* Checkbox */}
                <div onClick={()=>toggleSelect(r.id)} style={{ width:18, height:18, borderRadius:3, border:`1.5px solid ${sel?"var(--gold)":"var(--navy-border)"}`, background:sel?"var(--gold)":"transparent", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
                  {sel && <span style={{ fontSize:11, color:"var(--navy)", fontWeight:700 }}>✓</span>}
                </div>
                {/* Status icon */}
                <div style={{ width:20, textAlign:"center", flexShrink:0 }}>
                  {isIn&&<span style={{ color:"var(--green)",fontSize:14 }}>✓</span>}
                  {isNS&&<span style={{ color:"var(--red)",  fontSize:14 }}>✕</span>}
                  {!isIn&&!isNS&&<span style={{ color:"var(--muted)",fontSize:14 }}>·</span>}
                </div>
                {/* Name + players */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {r.teamName}
                    {r.walkUp&&<span style={{ marginLeft:6, fontSize:9, color:"var(--muted)", background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:2, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>WALK-UP</span>}
                  </div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>{r.players?.map(p=>p.name).join(" · ")||"—"}</div>
                </div>
                {r.seed&&<div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:14, color:"var(--gold)", flexShrink:0 }}>#{r.seed}</div>}
                {/* Payment */}
                <div style={{ flexShrink:0 }}>
                  {r.paymentStatus==="paid" ? (
                    <span style={{ fontSize:10, color:"var(--green)", background:"rgba(46,204,113,0.1)", border:"1px solid rgba(46,204,113,0.3)", padding:"2px 7px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>✓ PAID{r.payAmount?` $${r.payAmount}`:""}</span>
                  ) : r.paymentStatus==="waived" ? (
                    <span style={{ fontSize:10, color:"var(--muted)", background:"rgba(255,255,255,0.05)", border:"1px solid var(--navy-border)", padding:"2px 7px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>WAIVED</span>
                  ) : (
                    <button onClick={()=>{ setPayModal(r.id); setPayModalAmount(""); setPaymentNote(""); }} style={{ fontSize:10, color:"var(--gold)", background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", padding:"3px 8px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>⚠ UNPAID</button>
                  )}
                </div>
                {isIn&&r.checkedInAt&&<div style={{ fontSize:11, color:"var(--muted)", flexShrink:0 }}>{r.checkedInAt}</div>}
                {/* Actions */}
                <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                  {!isIn&&!isNS&&<Btn sz="sm" v="success" onClick={()=>checkIn(r.id)}>✓ In</Btn>}
                  {isIn &&       <Btn sz="sm" v="ghost"   onClick={()=>undoCheckIn(r.id)}>↩</Btn>}
                  {!isNS&&!isIn&&<Btn sz="sm" v="danger"  onClick={()=>markNoShow(r.id)}>✕</Btn>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Arrival order */}
        {arrivals.length>0 && (
          <details style={{ marginTop:16 }}>
            <summary style={{ cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase", padding:"8px 0", userSelect:"none" }}>▸ Arrival Order ({arrivals.length})</summary>
            <div style={{ ...css.card, padding:0, overflow:"hidden", marginTop:8 }}>
              {arrivals.map((r,i)=>(
                <div key={r.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 14px", borderBottom:i<arrivals.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:i===0?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.04)", border:`1px solid ${i===0?"var(--gold)":"var(--navy-border)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:15, color:i===0?"var(--gold)":"var(--muted)", flexShrink:0 }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{r.teamName}</div>
                    <div style={{ fontSize:11, color:"var(--muted)" }}>{r.players?.map(p=>p.name).join(" · ")}</div>
                  </div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:17, color:"var(--green)" }}>{r.checkedInAt}</div>
                  {r.paymentStatus!=="paid"&&r.paymentStatus!=="waived"&&<span style={{ fontSize:9, color:"var(--gold)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>⚠ UNPAID</span>}
                </div>
              ))}
            </div>
          </details>
        )}
        {renderPayModal()}
      </div>
    );
  }

  // ── KIOSK MODE ───────────────────────────────────────────────────────────
  function renderKiosk() {
    if (!tournament) return renderTournamentPicker();
    const numpad = ["1","2","3","4","5","6","7","8","9",".","0","⌫"];

    return (
      <div style={{ position:"fixed", inset:0, background:"var(--navy)", display:"flex", flexDirection:"column", zIndex:500, overflow:"hidden" }}>
        {/* Header */}
        <div style={{ background:"var(--navy-mid)", borderBottom:"2px solid var(--gold)", padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, flexWrap:"wrap", gap:10 }}>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, letterSpacing:4 }}>CHECK-IN KIOSK</div>
            <div style={{ fontSize:12, color:"var(--gold)" }}>{tournament.name}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:20 }}>
            {[[checkedIn.length,"IN","var(--green)"],[pending.length,"PENDING","var(--gold)"],[unpaidIn.length,"UNPAID",unpaidIn.length>0?"var(--red)":"var(--muted)"]].map(([v,l,c])=>(
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:8, color:"var(--muted)", letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{l}</div>
              </div>
            ))}
            <Btn v="ghost" sz="sm" onClick={()=>setTab("staff")}>✕ Exit</Btn>
          </div>
        </div>

        {/* Progress */}
        <div style={{ height:3, background:"rgba(255,255,255,0.06)", flexShrink:0 }}>
          <div style={{ height:"100%", width:`${pct}%`, background:pct===100?"var(--gold)":"var(--green)", transition:"width 0.4s" }} />
        </div>

        {/* Body */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px" }}>

          {/* IDLE */}
          {kioskStep==="idle" && (
            <div style={{ textAlign:"center", maxWidth:420 }}>
              {allIn ? (
                <div style={{ animation:"fadeIn 0.3s" }}>
                  <div style={{ fontSize:60, marginBottom:12 }}>🚀</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:34, letterSpacing:3, color:"var(--gold)", marginBottom:8 }}>ALL TEAMS IN!</div>
                  <div style={{ fontSize:14, color:"var(--muted)" }}>Ready to start the tournament</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize:64, marginBottom:16 }}>📡</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:4, marginBottom:8 }}>SCAN TO CHECK IN</div>
                  <div style={{ fontSize:14, color:"var(--muted)", marginBottom:32 }}>Hold your QR code up to the scanner</div>
                  <Btn onClick={startKioskScan} style={{ fontSize:18, padding:"14px 48px", justifyContent:"center", letterSpacing:2 }}>📷 Simulate Scan</Btn>
                  <div style={{ marginTop:16 }}>
                    <button onClick={()=>{ setTab("walkup"); }} style={{ fontSize:12, color:"var(--muted)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>🚶 Walk-up registration →</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* SCANNING */}
          {kioskStep==="scanning" && (
            <div style={{ textAlign:"center", width:"100%", maxWidth:320 }}>
              <div style={{ position:"relative", width:220, height:220, margin:"0 auto 20px", border:"3px solid var(--gold)", borderRadius:10, overflow:"hidden", background:"rgba(201,168,76,0.03)" }}>
                {[[0,0],[0,1],[1,0],[1,1]].map(([tb,lr],i)=>(
                  <div key={i} style={{ position:"absolute", top:tb?undefined:0, bottom:tb?0:undefined, left:lr?undefined:0, right:lr?0:undefined, width:20, height:20, borderTop:tb?"none":"3px solid var(--gold)", borderBottom:tb?"3px solid var(--gold)":"none", borderLeft:lr?"none":"3px solid var(--gold)", borderRight:lr?"3px solid var(--gold)":"none" }} />
                ))}
                <div style={{ position:"absolute", left:0, right:0, height:3, background:"linear-gradient(90deg,transparent,var(--gold),transparent)", top:`${kioskScanPct}%`, transition:"top 0.08s linear", boxShadow:"0 0 10px var(--gold)" }} />
                <div style={{ position:"absolute", inset:24, display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:2, opacity:0.12 }}>
                  {Array(25).fill(0).map((_,i)=><div key={i} style={{ background:"var(--gold)", borderRadius:1, opacity:i%3===0?1:0.5 }} />)}
                </div>
              </div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:3, color:"var(--gold)", marginBottom:8 }}>SCANNING…</div>
              <div style={{ width:220, height:5, background:"rgba(255,255,255,0.08)", borderRadius:3, overflow:"hidden", margin:"0 auto" }}>
                <div style={{ height:"100%", width:`${kioskScanPct}%`, background:"var(--gold)", borderRadius:3, transition:"width 0.08s" }} />
              </div>
            </div>
          )}

          {/* FOUND flash */}
          {kioskStep==="found" && kioskFound && (
            <div style={{ textAlign:"center", animation:"fadeIn 0.2s" }}>
              <div style={{ fontSize:60, marginBottom:10 }}>✅</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:3, color:"var(--green)" }}>FOUND!</div>
              <div style={{ fontSize:18, fontWeight:600, marginTop:6 }}>{kioskFound.teamName}</div>
            </div>
          )}

          {/* VERIFY */}
          {kioskStep==="verify" && kioskFound && (() => {
            const unpaid = kioskFound.paymentStatus!=="paid"&&kioskFound.paymentStatus!=="waived";
            return (
              <div style={{ width:"100%", maxWidth:460, animation:"fadeIn 0.25s" }}>
                <div style={{ ...css.card, padding:"20px 24px", marginBottom:16, borderColor:unpaid?"rgba(201,168,76,0.5)":"rgba(46,204,113,0.4)", background:unpaid?"rgba(201,168,76,0.05)":"rgba(46,204,113,0.05)", textAlign:"center" }}>
                  {kioskSettings.showSeed&&kioskFound.seed&&<div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, color:"var(--gold)", letterSpacing:2, marginBottom:6 }}>SEED #{kioskFound.seed}</div>}
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, letterSpacing:2, marginBottom:8 }}>{kioskFound.teamName}</div>
                  <div style={{ fontSize:14, color:"var(--muted)", marginBottom:16 }}>{kioskFound.players?.map(p=>p.name).join(" · ")||"—"}</div>
                  {unpaid ? (
                    <div style={{ display:"inline-block", fontSize:13, color:"var(--gold)", background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.4)", padding:"6px 18px", borderRadius:4, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                      ⚠ PAYMENT DUE{entryFee>0?` — $${entryFee}`:""}
                    </div>
                  ) : (
                    <div style={{ display:"inline-block", fontSize:13, color:"var(--green)", background:"rgba(46,204,113,0.1)", border:"1px solid rgba(46,204,113,0.3)", padding:"6px 18px", borderRadius:4, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>✓ PAID</div>
                  )}
                </div>
                <div style={{ display:"flex", gap:12 }}>
                  <Btn v="ghost" onClick={()=>{ setKioskStep("idle"); setKioskFound(null); }} style={{ flex:1, justifyContent:"center", padding:"14px" }}>✕ Cancel</Btn>
                  <Btn onClick={kioskCheckIn} style={{ flex:2, justifyContent:"center", fontSize:18, padding:"14px", letterSpacing:2 }}>
                    {(kioskSettings.requirePayment&&unpaid)?"💵 Collect & Check In":"✓ CHECK IN"}
                  </Btn>
                </div>
              </div>
            );
          })()}

          {/* PAYMENT — cash numpad */}
          {kioskStep==="payment" && kioskFound && (
            <div style={{ width:"100%", maxWidth:340, animation:"fadeIn 0.25s" }}>
              <div style={{ textAlign:"center", marginBottom:16 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, letterSpacing:3, color:"var(--gold)" }}>COLLECT PAYMENT</div>
                <div style={{ fontSize:13, color:"var(--muted)", marginTop:2 }}>{kioskFound.teamName}</div>
                {entryFee>0&&<div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, color:"var(--white)", marginTop:6 }}>${entryFee} DUE</div>}
              </div>
              <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:8, padding:"14px 20px", marginBottom:14, textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:48, color:"var(--white)", letterSpacing:2, lineHeight:1 }}>${cashDisplay}</div>
                {kioskChange!==null&&<div style={{ marginTop:6, fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--green)" }}>CHANGE: ${kioskChange.toFixed(2)}</div>}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, marginBottom:10 }}>
                {numpad.map(k=>(
                  <button key={k} onClick={()=>kioskNumpad(k)} style={{ padding:"16px 0", fontSize:20, fontFamily:"'Bebas Neue',sans-serif", background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:5, color:"var(--white)", cursor:"pointer", letterSpacing:1 }}>{k}</button>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <button onClick={()=>kioskNumpad("C")} style={{ padding:"12px", background:"rgba(217,79,61,0.1)", border:"1px solid rgba(217,79,61,0.3)", borderRadius:5, color:"var(--red)", fontSize:13, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>CLEAR</button>
                <Btn onClick={kioskCollectCash} style={{ padding:"12px", justifyContent:"center", fontSize:15, letterSpacing:2 }}>✓ COLLECTED</Btn>
              </div>
              <div style={{ marginTop:10, textAlign:"center" }}>
                <button onClick={()=>{ waivePayment(kioskFound.id); doKioskSuccess(kioskFound.id); }} style={{ fontSize:11, color:"var(--muted)", background:"none", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>Waive payment →</button>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {kioskStep==="success" && (() => {
            const r = regs.find(x=>x.id===kioskFound?.id);
            const stillUnpaid = r&&r.paymentStatus!=="paid"&&r.paymentStatus!=="waived";
            return (
              <div style={{ textAlign:"center", animation:"fadeIn 0.2s", maxWidth:400 }}>
                <div style={{ fontSize:72, marginBottom:14 }}>{stillUnpaid?"⚠️":"🎉"}</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:38, letterSpacing:3, color:stillUnpaid?"var(--gold)":"var(--green)", marginBottom:8 }}>
                  {stillUnpaid?"CHECKED IN":"WELCOME!"}
                </div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, letterSpacing:2 }}>{kioskFound?.teamName}</div>
                {stillUnpaid&&<div style={{ fontSize:13, color:"var(--gold)", marginTop:10 }}>Payment still due — see staff</div>}
                <div style={{ marginTop:20, fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>Auto-clearing in {kioskSettings.autoClear}s…</div>
              </div>
            );
          })()}

          {/* NOT FOUND */}
          {kioskStep==="notfound" && (
            <div style={{ textAlign:"center", animation:"fadeIn 0.2s" }}>
              <div style={{ fontSize:56, marginBottom:12 }}>❓</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:3, color:"var(--red)", marginBottom:8 }}>NOT FOUND</div>
              <div style={{ fontSize:13, color:"var(--muted)", marginBottom:20 }}>QR code not recognized. Please see staff.</div>
              <Btn v="ghost" onClick={()=>{ setTab("walkup"); }} style={{ justifyContent:"center" }}>🚶 Walk-Up Registration</Btn>
            </div>
          )}
        </div>

        {/* Settings footer */}
        <div style={{ borderTop:"1px solid var(--navy-border)", padding:"8px 24px", display:"flex", gap:16, alignItems:"center", flexShrink:0, background:"rgba(0,0,0,0.2)", flexWrap:"wrap" }}>
          <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>Settings:</span>
          {[["requirePayment","Require Payment"],["showSeed","Show Seed"]].map(([k,l])=>(
            <label key={k} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color:"var(--muted)" }}>
              <div onClick={()=>setKioskSettings(p=>({...p,[k]:!p[k]}))} style={{ width:28, height:16, borderRadius:8, background:kioskSettings[k]?"var(--gold)":"rgba(255,255,255,0.1)", position:"relative", cursor:"pointer", transition:"background 0.2s" }}>
                <div style={{ position:"absolute", top:2, left:kioskSettings[k]?12:2, width:12, height:12, borderRadius:"50%", background:"var(--white)", transition:"left 0.2s" }} />
              </div>
              {l}
            </label>
          ))}
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--muted)" }}>
            Auto-clear:
            <select style={{ ...css.select, padding:"2px 6px", fontSize:11, width:60 }} value={kioskSettings.autoClear} onChange={e=>setKioskSettings(p=>({...p,autoClear:parseInt(e.target.value)}))}>
              {[2,3,4,5,8].map(n=><option key={n} value={n}>{n}s</option>)}
            </select>
          </label>
        </div>
      </div>
    );
  }

  // ── WALK-UP TAB ──────────────────────────────────────────────────────────
  function renderWalkUp() {
    if (!tournament) return renderTournamentPicker();
    const steps = [{n:1,l:"Team Info"},{n:2,l:"Payment"},{n:3,l:"Confirm"}];
    return (
      <div style={{ maxWidth:500, margin:"0 auto" }}>
        {/* Step indicator */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:24 }}>
          {steps.map((s,i)=>(
            <React.Fragment key={s.n}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:wuStep>=s.n?"var(--gold)":"rgba(255,255,255,0.06)", border:`2px solid ${wuStep>=s.n?"var(--gold)":"var(--navy-border)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:wuStep>=s.n?"var(--navy)":"var(--muted)", transition:"all 0.2s" }}>
                  {wuStep>s.n?"✓":s.n}
                </div>
                <div style={{ fontSize:10, color:wuStep===s.n?"var(--gold)":"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>{s.l}</div>
              </div>
              {i<steps.length-1&&<div style={{ flex:1, height:2, background:wuStep>s.n?"var(--gold)":"rgba(255,255,255,0.07)", margin:"0 8px 18px", transition:"background 0.2s" }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 */}
        {wuStep===1&&(
          <div style={{ ...css.card, padding:"20px 22px", animation:"fadeIn 0.2s" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:14 }}>Team Information</div>
            <Fld label="Team Name *"><input style={css.input} value={wuForm.teamName} onChange={e=>updWu("teamName",e.target.value)} placeholder="Team name" autoFocus /></Fld>
            <Fld label="Player 1"><input style={css.input} value={wuForm.p1} onChange={e=>updWu("p1",e.target.value)} placeholder="First player" /></Fld>
            <Fld label="Player 2"><input style={css.input} value={wuForm.p2} onChange={e=>updWu("p2",e.target.value)} placeholder="Second player (optional)" /></Fld>
            <Fld label="Notes"><input style={css.input} value={wuForm.notes} onChange={e=>updWu("notes",e.target.value)} placeholder="Any notes…" /></Fld>
            <div style={{ display:"flex", gap:8, marginTop:18, justifyContent:"flex-end" }}>
              <Btn v="ghost" onClick={()=>setTab("staff")}>Cancel</Btn>
              <Btn onClick={()=>setWuStep(2)} disabled={!wuForm.teamName}>Next: Payment →</Btn>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {wuStep===2&&(
          <div style={{ ...css.card, padding:"20px 22px", animation:"fadeIn 0.2s" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:14 }}>Payment</div>
            {entryFee>0&&<div style={{ padding:"8px 12px", background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:4, marginBottom:14, fontSize:13 }}>Entry fee: <strong style={{ color:"var(--gold)" }}>${entryFee}</strong></div>}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
              {[["paid","✓ Paid","var(--green)"],["unpaid","⚠ Unpaid","var(--gold)"],["waived","Waived","var(--muted)"]].map(([v,l,c])=>(
                <div key={v} onClick={()=>updWu("payStatus",v)} style={{ padding:"12px 8px", borderRadius:5, border:`2px solid ${wuForm.payStatus===v?c:"var(--navy-border)"}`, background:wuForm.payStatus===v?`${c}18`:"transparent", textAlign:"center", cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:wuForm.payStatus===v?c:"var(--muted)" }}>{l}</div>
                </div>
              ))}
            </div>
            {wuForm.payStatus==="paid"&&(
              <>
                <Fld label="Amount ($)">
                  <input style={{ ...css.input, fontSize:24, fontFamily:"'Bebas Neue',sans-serif", textAlign:"center" }} type="number" min="0" step="0.01" value={wuForm.payAmount} onChange={e=>updWu("payAmount",e.target.value)} placeholder={entryFee>0?String(entryFee):"0.00"} />
                </Fld>
                <Fld label="Method">
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {["cash","card","venmo","other"].map(m=>(
                      <button key={m} onClick={()=>updWu("payMethod",m)} style={{ padding:"4px 12px", borderRadius:4, border:`1px solid ${wuForm.payMethod===m?"var(--gold)":"var(--navy-border)"}`, background:wuForm.payMethod===m?"rgba(201,168,76,0.12)":"transparent", color:wuForm.payMethod===m?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer", letterSpacing:1, textTransform:"capitalize" }}>{m}</button>
                    ))}
                  </div>
                </Fld>
                {entryFee>0&&parseFloat(wuForm.payAmount)>entryFee&&(
                  <div style={{ padding:"8px 12px", background:"rgba(46,204,113,0.07)", border:"1px solid rgba(46,204,113,0.25)", borderRadius:4, marginTop:8, display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:12, color:"var(--muted)" }}>Change due</span>
                    <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--green)" }}>${(parseFloat(wuForm.payAmount)-entryFee).toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            <div style={{ display:"flex", gap:8, marginTop:18, justifyContent:"space-between" }}>
              <Btn v="ghost" onClick={()=>setWuStep(1)}>← Back</Btn>
              <Btn onClick={()=>setWuStep(3)}>Next: Confirm →</Btn>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {wuStep===3&&(
          <div style={{ ...css.card, padding:"20px 22px", animation:"fadeIn 0.2s" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:14 }}>Confirm Walk-Up</div>
            <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:5, padding:"12px 14px", marginBottom:14 }}>
              {[["Team",wuForm.teamName],["Players",[wuForm.p1,wuForm.p2].filter(Boolean).join(" · ")||"—"],["Payment",wuForm.payStatus==="paid"?`✓ $${wuForm.payAmount||entryFee} (${wuForm.payMethod})`:wuForm.payStatus==="waived"?"Waived":"⚠ Unpaid"]].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:11, color:"var(--muted)" }}>{k}</span>
                  <span style={{ fontSize:13, fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ padding:"9px 12px", background:"rgba(46,204,113,0.07)", border:"1px solid rgba(46,204,113,0.25)", borderRadius:4, fontSize:12, color:"var(--green)", marginBottom:18 }}>
              ✓ Will be automatically checked in
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"space-between" }}>
              <Btn v="ghost" onClick={()=>setWuStep(2)}>← Back</Btn>
              <Btn onClick={completeWalkUp} style={{ fontSize:15, padding:"10px 28px", justifyContent:"center" }}>🚶 Register &amp; Check In</Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── PAYMENT LOG TAB ──────────────────────────────────────────────────────
  function renderPayLog() {
    if (!tournament) return renderTournamentPicker();
    const totalCollected = regs.filter(r=>r.paymentStatus==="paid").reduce((s,r)=>s+(parseFloat(r.payAmount)||entryFee||0),0);
    const totalExpected  = regs.length*(entryFee||0);
    const totalWaived    = regs.filter(r=>r.paymentStatus==="waived").length;
    return (
      <div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
          {[["$"+totalCollected.toFixed(0),"Collected","var(--green)"],["$"+totalExpected.toFixed(0),"Expected","var(--gold)"],[allUnpaid.length,"Unpaid",allUnpaid.length>0?"var(--red)":"var(--muted)"],[totalWaived,"Waived","var(--muted)"]].map(([v,l,c])=>(
            <div key={l} style={{ ...css.card, padding:"10px 8px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
              <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>

        {allUnpaid.length>0&&(
          <div style={{ ...css.card, padding:0, overflow:"hidden", marginBottom:14, borderColor:"rgba(201,168,76,0.3)" }}>
            <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)", background:"rgba(201,168,76,0.05)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)" }}>⚠ Outstanding ({allUnpaid.length})</span>
              {entryFee>0&&<span style={{ fontSize:11, color:"var(--muted)" }}>Owed: ${(allUnpaid.length*entryFee).toFixed(2)}</span>}
            </div>
            {allUnpaid.map((r,i)=>(
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:i<allUnpaid.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{r.teamName}</div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>{r.players?.map(p=>p.name).join(" · ")}</div>
                </div>
                <Badge color={r.status==="checked_in"?"green":""}>{r.status==="checked_in"?"✓ IN":"pending"}</Badge>
                {entryFee>0&&<span style={{ fontSize:13, fontFamily:"'Bebas Neue',sans-serif", color:"var(--gold)" }}>${entryFee}</span>}
                <div style={{ display:"flex", gap:4 }}>
                  <Btn sz="sm" onClick={()=>{ setPayModal(r.id); setPayModalAmount(""); setPaymentNote(""); }}>Collect</Btn>
                  <Btn sz="sm" v="ghost" onClick={()=>waivePayment(r.id)}>Waive</Btn>
                </div>
              </div>
            ))}
          </div>
        )}

        {payLog.length>0&&(
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)", background:"rgba(46,204,113,0.03)" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--green)" }}>Paid / Waived ({payLog.length})</span>
            </div>
            {payLog.map((r,i)=>(
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<payLog.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{r.teamName}</div>
                  {r.paidAt&&<div style={{ fontSize:10, color:"var(--muted)" }}>{r.paidAt}{r.payMethod?` · ${r.payMethod}`:""}</div>}
                </div>
                <span style={{ fontSize:14, fontFamily:"'Bebas Neue',sans-serif", color:r.paymentStatus==="waived"?"var(--muted)":"var(--green)" }}>
                  {r.paymentStatus==="waived"?"—":"$"+(r.payAmount||entryFee||"?")}
                </span>
                <span style={{ fontSize:10, color:r.paymentStatus==="waived"?"var(--muted)":"var(--green)", background:"rgba(255,255,255,0.04)", padding:"2px 7px", borderRadius:3, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                  {r.paymentStatus==="waived"?"WAIVED":"PAID"}
                </span>
              </div>
            ))}
          </div>
        )}
        {renderPayModal()}

        {/* ── Reconciliation Tools ───────────────────────────────────────── */}
        {regs.length > 0 && (
          <div style={{ ...css.card, padding:"14px 16px", marginTop:14 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)", marginBottom:12 }}>
              ⚖ Reconciliation Tools
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
              {/* Bulk mark all unpaid as paid */}
              {allUnpaid.length > 0 && (
                <Btn sz="sm" onClick={()=>{
                  const ids = new Set(allUnpaid.map(r=>r.id));
                  updateRegs(regs.map(r=>ids.has(r.id)?{...r,paymentStatus:"paid",payAmount:entryFee||0,paidAt:new Date().toISOString()}:r));
                }}>✓ Mark All Paid ({allUnpaid.length})</Btn>
              )}
              {/* Waive all unpaid */}
              {allUnpaid.length > 0 && (
                <Btn sz="sm" v="ghost" onClick={()=>{
                  const ids = new Set(allUnpaid.map(r=>r.id));
                  updateRegs(regs.map(r=>ids.has(r.id)?{...r,paymentStatus:"waived"}:r));
                }} style={{ color:"var(--muted)" }}>Waive All Unpaid</Btn>
              )}
              {/* Export unpaid list */}
              {allUnpaid.length > 0 && (
                <Btn sz="sm" v="ghost" onClick={()=>{
                  const rows = ["Team,Players,Owed"].concat(allUnpaid.map(r=>`"${r.teamName}","${(r.players||[]).map(p=>p.name).join("/")}","$${entryFee||0}"`));
                  const blob = new Blob([rows.join("\n")],{type:"text/csv"});
                  const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
                  a.download=`unpaid-${tournament.name.replace(/\s+/g,"-")}.csv`; a.click();
                }}>📥 Export Unpaid CSV</Btn>
              )}
            </div>

            {/* Partial payment tracker */}
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"var(--muted)", marginBottom:8 }}>
              Partial Payments
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {regs.filter(r=>r.paymentStatus==="unpaid"||r.paymentStatus==="partial").map(r=>{
                const owed = entryFee||0;
                const paid = parseFloat(r.partialAmount)||0;
                const pct  = owed>0?Math.round(paid/owed*100):0;
                return (
                  <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", background:"rgba(255,255,255,0.02)", borderRadius:4 }}>
                    <span style={{ flex:1, fontSize:12, fontWeight:600 }}>{r.teamName}</span>
                    <span style={{ fontSize:11, color:"var(--muted)" }}>${paid} / ${owed}</span>
                    <div style={{ width:60, height:4, background:"rgba(255,255,255,0.07)", borderRadius:2, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:"rgba(201,168,76,0.7)", borderRadius:2 }} />
                    </div>
                    <input type="number" min="0" max={owed} placeholder="Add $"
                      style={{ ...css.input, width:72, fontSize:11, padding:"3px 8px" }}
                      onBlur={e=>{
                        const add = parseFloat(e.target.value)||0;
                        if(!add) return;
                        const newPaid = Math.min(paid+add, owed);
                        const newStatus = newPaid>=owed?"paid":"partial";
                        updateRegs(regs.map(x=>x.id===r.id?{...x,partialAmount:newPaid,paymentStatus:newStatus}:x));
                        e.target.value="";
                      }} />
                  </div>
                );
              })}
              {regs.filter(r=>r.paymentStatus==="unpaid"||r.paymentStatus==="partial").length===0&&(
                <div style={{ fontSize:12, color:"var(--muted)", padding:"8px 0" }}>All paid or waived ✓</div>
              )}
            </div>

            {/* Reconciliation summary */}
            {entryFee > 0 && (
              <div style={{ marginTop:14, padding:"10px 14px", background:"rgba(46,204,113,0.06)", border:"1px solid rgba(46,204,113,0.2)", borderRadius:5, fontSize:12 }}>
                <div style={{ fontWeight:700, color:"var(--green)", marginBottom:6 }}>💰 Reconciliation Summary</div>
                <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                  {[
                    ["Collected", "$"+(regs.filter(r=>r.paymentStatus==="paid").reduce((s,r)=>s+(parseFloat(r.payAmount)||parseFloat(r.partialAmount)||entryFee||0),0)).toFixed(2), "var(--green)"],
                    ["Partial",   "$"+(regs.filter(r=>r.paymentStatus==="partial").reduce((s,r)=>s+(parseFloat(r.partialAmount)||0),0)).toFixed(2), "var(--gold)"],
                    ["Outstanding","$"+(allUnpaid.reduce((_,__)=>entryFee,0)*allUnpaid.length).toFixed(2),"var(--red)"],
                    ["Waived",    regs.filter(r=>r.paymentStatus==="waived").length+" teams","var(--muted)"],
                  ].map(([l,v,c])=>(
                    <div key={l}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:c, lineHeight:1 }}>{v}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── MAIN RENDER ───────────────────────────────────────────────────────────
  const TABS = [
    { id:"staff",   label:"Staff View",  icon:"📋" },
    { id:"kiosk",   label:"Kiosk",       icon:"📡" },
    { id:"walkup",  label:"Walk-Up",     icon:"🚶" },
    { id:"paylog",  label:"Payment Log", icon:"💰", badge:allUnpaid.length },
  ];

  return (
    <div className="fade-in">
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Check-In</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>Kiosk mode · walk-up registration · payment collection</div>
        </div>
        <select style={css.select} value={selectedTournament} onChange={e=>setSelectedTournament(e.target.value)}>
          <option value="">— select tournament —</option>
          {upcoming.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="tab-strip" style={{ marginBottom:18 }}>
        {TABS.map(t=>(
          <button key={t.id} className={`tab-btn ${tab===t.id?"tab-active":""}`} onClick={()=>setTab(t.id)}>
            {t.icon} {t.label}{t.badge?` (${t.badge})`:""}
          </button>
        ))}
      </div>

      {tab==="staff"  && renderStaff()}
      {tab==="kiosk"  && renderKiosk()}
      {tab==="walkup" && renderWalkUp()}
      {tab==="paylog" && renderPayLog()}
    </div>
  );
}



// ── SEEDING TOOL ─────────────────────────────────────────────────────────────
// Standard bracket-seeding convention (8-team shown):
//  1 vs 8, 4 vs 5, 2 vs 7, 3 vs 6
// Generalised: seed k plays seed (n+1-k) where n = bracket size
function getStandardMatchups(seeds) {
  const n = Math.pow(2, Math.ceil(Math.log2(Math.max(seeds.length, 2))));
  const seeded = [...seeds];
  while (seeded.length < n) seeded.push(null); // byes
  const matchups = [];
  for (let i = 0; i < n / 2; i++) {
    matchups.push({ top: seeded[i], bottom: seeded[n - 1 - i] });
  }
  return matchups;
}

function SeedingPage({ tournaments, setTournaments, players, setPage }) {
  const [selectedTournament, setSelectedTournament] = useState("");
  const [seeds, setSeeds] = useState([]);          // ordered list of reg entries
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragIdx2] = useState(null);
  const [tab, setTab] = useState("seeding");        // seeding | preview
  const [saved, setSaved] = useState(false);
  const [groupCount, setGroupCount]       = useState(4);     // for group draw
  const [groupDraw,  setGroupDraw]         = useState(null);  // [{groupLabel, teams}] or null
  const [drawAnimIdx, setDrawAnimIdx]      = useState(-1);    // animation cursor
  const [btbWarnings, setBtbWarnings]      = useState([]);    // [{a,b}] same-group back-to-back pairs
  const [searchSeed,  setSearchSeed]       = useState("");
  const [lockedSeeds,  setLockedSeeds]  = useState(new Set()); // Set of reg.id

  const upcoming = tournaments.filter(t => t.status === "upcoming" || t.status === "active");
  const tournament = tournaments.find(t => t.id === selectedTournament);
  const checkedInRegs = (tournament?.registrations || []).filter(r => r.status === "checked_in");
  const allRegs = tournament?.registrations || [];

  // When tournament changes, initialise seeds from existing reg seeds or checked-in order
  useEffect(() => {
    if (!tournament) { setSeeds([]); return; }
    const regs = [...(tournament.registrations || [])];
    // Sort by existing seed if set, else push to end
    const withSeed = regs.filter(r => r.seed).sort((a, b) => a.seed - b.seed);
    const noSeed = regs.filter(r => !r.seed);
    setSeeds([...withSeed, ...noSeed]);
    setSaved(false);
  }, [selectedTournament]);

  // ── Auto-seed modes ────────────────────────────────────────────────────────
  function autoSeedByPowerRating() {
    const ranked = [...seeds].sort((a, b) => {
      const pA = players.find(p => a.players?.some(ap => ap.name === p.name));
      const pB = players.find(p => b.players?.some(bp => bp.name === p.name));
      return calcPowerRating(pB || { elo: 1200, placements: [], cupDiff: 0 }) -
             calcPowerRating(pA || { elo: 1200, placements: [], cupDiff: 0 });
    });
    setSeeds(ranked);
    setSaved(false);
  }

  function autoSeedByElo() {
    const ranked = [...seeds].sort((a, b) => {
      const pA = players.find(p => a.players?.some(ap => ap.name === p.name));
      const pB = players.find(p => b.players?.some(bp => bp.name === p.name));
      return (pB?.elo || 1200) - (pA?.elo || 1200);
    });
    setSeeds(ranked);
    setSaved(false);
  }

  function autoSeedBySeasonRecord() {
    // Seed by current season W–L record (wins desc, then win-pct desc, then Elo)
    const activeSeason = (seasons||[]).find(s=>s.status==="active");
    const ranked = [...seeds].sort((a, b) => {
      const nameA = a.players?.[0]?.name || a.teamName || "";
      const nameB = b.players?.[0]?.name || b.teamName || "";
      const pA = players.find(p => p.name===nameA);
      const pB = players.find(p => p.name===nameB);
      const wA = pA?.wins||0, lA = pA?.losses||0, wB = pB?.wins||0, lB = pB?.losses||0;
      const wpA = wA+lA>0 ? wA/(wA+lA) : 0;
      const wpB = wB+lB>0 ? wB/(wB+lB) : 0;
      if (wA !== wB) return wB - wA;
      if (Math.abs(wpA-wpB) > 0.001) return wpB - wpA;
      return (pB?.elo||1200) - (pA?.elo||1200);
    });
    setSeeds(ranked);
    setSaved(false);
  }

  function randomize() {
    setSeeds(prev => shuffleArr([...prev]));
    setSaved(false);
  }

  function resetSeeds() {
    setSeeds(prev => prev.map(r => ({ ...r, seed: null })));
    setSaved(false);
  }

  // ── Drag-and-drop handlers ─────────────────────────────────────────────────
  function onDragStart(i) { setDragIdx(i); }
  function onDragOver(e, i) { e.preventDefault(); setDragIdx2(i); }
  function onDrop(i) {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragIdx2(null); return; }
    const next = [...seeds];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setSeeds(next);
    setDragIdx(null); setDragIdx2(null);
    setSaved(false);
  }

  function moveUp(i) {
    if (i === 0) return;
    const next = [...seeds];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    setSeeds(next); setSaved(false);
  }

  function moveDown(i) {
    if (i === seeds.length - 1) return;
    const next = [...seeds];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    setSeeds(next); setSaved(false);
  }

  // ── Save seeds back to tournament ──────────────────────────────────────────
  function saveSeeds() {
    const updatedRegs = (tournament.registrations || []).map(r => {
      const idx = seeds.findIndex(s => s.id === r.id);
      return { ...r, seed: idx >= 0 ? idx + 1 : null };
    });
    setTournaments(prev => prev.map(t =>
      t.id === selectedTournament ? { ...t, registrations: updatedRegs } : t
    ));
    setSaved(true);
  }

  // ── Bracket preview matchups ───────────────────────────────────────────────
  const matchups = getStandardMatchups(seeds);

  // ── Player rating lookup ───────────────────────────────────────────────────
  function teamRating(reg) {
    const teamPlayers = (reg.players || []).map(rp =>
      players.find(p => p.name === rp.name)
    ).filter(Boolean);
    if (!teamPlayers.length) return { pr: 1200, elo: 1200 };
    const avgPR = Math.round(teamPlayers.reduce((s, p) => s + calcPowerRating(p), 0) / teamPlayers.length);
    const avgElo = Math.round(teamPlayers.reduce((s, p) => s + p.elo, 0) / teamPlayers.length);
    return { pr: avgPR, elo: avgElo };
  }

  const checkedInCount = allRegs.filter(r => r.status === "checked_in").length;

  // ── Group draw ─────────────────────────────────────────────────────────────
  function runGroupDraw() {
    const n = Math.max(2, Math.min(groupCount, seeds.length));
    // Pot system: divide seeded list into pots, draw one from each pot per group
    const potSize = Math.ceil(seeds.length / n);
    const pots = [];
    for (let i = 0; i < n; i++) {
      pots.push(seeds.slice(i * potSize, (i + 1) * potSize));
    }
    const groups = Array.from({length: n}, (_,i) => ({
      groupLabel: String.fromCharCode(65 + i), // A, B, C...
      teams: [],
    }));
    // Shuffle each pot and assign one team per group
    pots.forEach(pot => {
      const shuffled = [...pot].sort(() => Math.random() - 0.5);
      shuffled.forEach((team, gi) => {
        if (gi < groups.length) groups[gi].teams.push(team);
      });
    });

    // Check back-to-back: flag teams that were in same group last time
    const btb = [];
    groups.forEach(g => {
      g.teams.forEach((t, i) => {
        g.teams.slice(i+1).forEach(t2 => {
          const hist = tournament?.registrations?.find(r=>r.id===t.id)?.groupHistory || [];
          if (hist.includes(t2.id)) btb.push({a:t.teamName, b:t2.teamName, group:g.groupLabel});
        });
      });
    });
    setBtbWarnings(btb);
    setGroupDraw(groups);
    setDrawAnimIdx(0);
    // Animate reveal
    let i = 0;
    const reveal = setInterval(() => {
      i++;
      setDrawAnimIdx(i);
      if (i >= groups.reduce((s,g)=>s+g.teams.length,0)) clearInterval(reveal);
    }, 180);
  }

  function commitGroupDraw() {
    if (!groupDraw || !tournament) return;
    const regMap = {};
    groupDraw.forEach((g, gi) => {
      g.teams.forEach((t, ti) => {
        regMap[t.id] = { group: g.groupLabel, groupSeed: ti + 1 };
      });
    });
    const updated = (tournament.registrations || []).map(r => ({
      ...r,
      ...(regMap[r.id] || {}),
      groupHistory: [...(r.groupHistory || []), ...groupDraw.flatMap(g => g.teams.filter(t=>t.id!==r.id).map(t=>t.id))],
    }));
    setTournaments(prev => prev.map(t => t.id === tournament.id ? {...t, registrations:updated} : t));
    setGroupDraw(null); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  // ── Back-to-back prevention check ────────────────────────────────────────
  function checkBtbConflicts(currentSeeds) {
    if (!tournament) return [];
    const prev = tournament.registrations || [];
    const conflicts = [];
    // Snake matchups: seeds 1 vs n, 2 vs n-1...
    const matchups = getStandardMatchups(currentSeeds);
    matchups.forEach(({top, bottom}) => {
      if (!top || !bottom) return;
      const topReg  = prev.find(r => r.id === top.id);
      const btb = (topReg?.lastOpponents || []).includes(bottom.id);
      if (btb) conflicts.push({a: top.teamName, b: bottom.teamName});
    });
    return conflicts;
  }

  const liveConflicts = checkBtbConflicts(seeds);


  return (
    <div className="fade-in">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 3 }}>Seeding Tool</div>
        <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 3 }}>
          Drag & drop to order seeds, auto-seed by power rating, and preview first-round matchups
        </div>
      </div>

      {/* Tournament selector */}
      <div style={{ ...css.card, padding: 16, marginBottom: 20 }}>
        <div className="resp-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "end" }}>
          <Fld label="Select Event">
            <select style={css.select} value={selectedTournament} onChange={e => { setSelectedTournament(e.target.value); setSaved(false); }}>
              <option value="">— Choose a tournament —</option>
              {upcoming.map(t => <option key={t.id} value={t.id}>{t.name} · {t.date || "TBD"}</option>)}
            </select>
          </Fld>
          {tournament && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ ...css.card, padding: "8px 14px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, color: "var(--gold)" }}>{seeds.length}</div>
                <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>Entries</div>
              </div>
              <div style={{ ...css.card, padding: "8px 14px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, color: "var(--green)" }}>{checkedInCount}</div>
                <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>Checked In</div>
              </div>
              {checkedInCount < seeds.length && (
                <div style={{ background: "rgba(217,79,61,0.08)", border: "1px solid rgba(217,79,61,0.25)", borderRadius: 4, padding: "6px 10px", fontSize: 11, color: "var(--red)" }}>
                  ⚠️ {seeds.length - checkedInCount} not checked in
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!selectedTournament ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎲</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 3, color: "var(--white)", marginBottom: 8 }}>Select a Tournament</div>
          <div style={{ fontSize: 13 }}>Choose an event to configure seeding</div>
        </div>
      ) : seeds.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 3, color: "var(--white)", marginBottom: 8 }}>No Registrations</div>
          <div style={{ fontSize: 13, marginBottom: 16 }}>Add teams or players before seeding</div>
          <Btn onClick={() => setPage("registration")}>Go to Registration →</Btn>
        </div>
      ) : (
        <>
          {/* Sub-tabs */}
          <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid var(--navy-border)" }}>
            {[["seeding", "🎲 Seed Order"], ["preview", "🎯 Bracket Preview"], ["groupdraw", "🎰 Group Draw"]].map(([v, l]) => (
              <div key={v} onClick={() => setTab(v)} style={{ padding: "8px 18px", cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", borderBottom: tab === v ? "2px solid var(--gold)" : "2px solid transparent", color: tab === v ? "var(--white)" : "var(--muted)", marginBottom: -1, transition: "all 0.15s" }}>{l}</div>
            ))}
          </div>

          {/* ── SEEDING TAB ── */}
          {tab === "seeding" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20 }}>
              {/* Drag list */}
              <div>
                {/* Toolbar */}
                <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--muted)", marginRight: 4, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>Auto-Seed:</span>
                  <Btn sz="sm" v="ghost" onClick={autoSeedByPowerRating}>⚡ Power Rating</Btn>
                  <Btn sz="sm" v="ghost" onClick={autoSeedByElo}>📊 Elo Only</Btn>
                  <Btn sz="sm" v="ghost" onClick={autoSeedBySeasonRecord}>🗓 Season Record</Btn>
                  <Btn sz="sm" v="ghost" onClick={randomize}>🎲 Randomize</Btn>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <Btn sz="sm" v="ghost" onClick={resetSeeds}>↺ Reset</Btn>
                    <Btn sz="sm" onClick={saveSeeds} v={saved ? "ghost" : "primary"}>
                      {saved ? "✓ Saved" : "💾 Save Seeds"}
                    </Btn>
                  </div>
                </div>

                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span>⋮⋮</span> Drag rows to reorder · Use ↑↓ buttons · Auto-seed fills order from top
                </div>

                {/* Seed rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {seeds.map((reg, i) => {
                    const { pr, elo } = teamRating(reg);
                    const isDragging = dragIdx === i;
                    const isOver = dragOver === i;
                    const isCheckedIn = reg.status === "checked_in";
                    return (
                      <div
                        key={reg.id}
                        draggable
                        onDragStart={() => onDragStart(i)}
                        onDragOver={e => onDragOver(e, i)}
                        onDrop={() => onDrop(i)}
                        onDragEnd={() => { setDragIdx(null); setDragIdx2(null); }}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          background: isDragging ? "rgba(201,168,76,0.15)" : isOver ? "rgba(201,168,76,0.08)" : "var(--card)",
                          border: `1px solid ${isOver ? "var(--gold)" : isDragging ? "rgba(201,168,76,0.5)" : "var(--navy-border)"}`,
                          borderRadius: 4, padding: "10px 12px", cursor: "grab",
                          opacity: isDragging ? 0.5 : 1,
                          transition: "background 0.1s, border-color 0.1s",
                          transform: isOver && !isDragging ? "translateY(-2px)" : "none",
                        }}
                      >
                        {/* Drag handle */}
                        <div style={{ color: "var(--muted)", fontSize: 16, cursor: "grab", flexShrink: 0, lineHeight: 1, letterSpacing: -2 }}>⋮⋮</div>

                        {/* Seed number */}
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, width: 36, textAlign: "center", flexShrink: 0, color: i === 0 ? "var(--gold)" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "var(--muted)", lineHeight: 1 }}>
                          {i + 1}
                        </div>

                        {/* Team info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reg.teamName}</span>
                            {isCheckedIn
                              ? <span style={{ fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, color: "var(--green)", background: "rgba(46,204,113,0.12)", padding: "1px 6px", borderRadius: 3, flexShrink: 0 }}>✓ IN</span>
                              : <span style={{ fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5, color: "var(--muted)", background: "rgba(255,255,255,0.05)", padding: "1px 6px", borderRadius: 3, flexShrink: 0 }}>{reg.status.replace("_", " ")}</span>
                            }
                          </div>
                          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {reg.players?.map(p => p.name).join(" · ")}
                          </div>
                        </div>

                        {/* Ratings */}
                        <div style={{ display: "flex", gap: 14, flexShrink: 0 }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "var(--gold-light)", lineHeight: 1 }}>{pr}</div>
                            <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>PR</div>
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "var(--muted)", lineHeight: 1 }}>{elo}</div>
                            <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>Elo</div>
                          </div>
                        </div>

                        {/* Lock */}
                        <button onClick={()=>setLockedSeeds(prev=>{const s=new Set(prev); s.has(reg.id)?s.delete(reg.id):s.add(reg.id); return s;})}
                          title={lockedSeeds.has(reg.id)?"Unlock":"Lock seed position"}
                          style={{ background:"none", border:`1px solid ${lockedSeeds.has(reg.id)?"var(--gold)":"var(--navy-border)"}`, color:lockedSeeds.has(reg.id)?"var(--gold)":"var(--muted)", borderRadius:3, width:22, height:22, cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {lockedSeeds.has(reg.id)?"🔒":"🔓"}
                        </button>
                        {/* Up / Down */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                          <button onClick={() => moveUp(i)} disabled={i === 0 || lockedSeeds.has(reg.id)} style={{ background: "none", border: "1px solid var(--navy-border)", color: i === 0 ? "var(--navy-border)" : "var(--muted)", borderRadius: 3, width: 22, height: 22, cursor: i === 0 ? "default" : "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>▲</button>
                          <button onClick={() => moveDown(i)} disabled={i === seeds.length - 1 || lockedSeeds.has(reg.id)} style={{ background: "none", border: "1px solid var(--navy-border)", color: i === seeds.length - 1 ? "var(--navy-border)" : "var(--muted)", borderRadius: 3, width: 22, height: 22, cursor: i === seeds.length - 1 ? "default" : "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>▼</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Save bar */}
                {!saved && (
                  <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Btn v="ghost" onClick={() => {
                      const rows = ["Seed,Team,Players,Locked"];
                      seeds.forEach((r,i) => rows.push([i+1, r.teamName, (r.players||[]).map(p=>p.name).join("|"), lockedSeeds.has(r.id)?"Y":"N"].join(",")));
                      const blob = new Blob([rows.join("")], {type:"text/csv"});
                      const a = Object.assign(document.createElement("a"),{href:URL.createObjectURL(blob),download:"seeds.csv"});
                      a.click();
                    }}>⬇ Export CSV</Btn>
                    <Btn v="ghost" onClick={() => { setSelectedTournament(selectedTournament); }}>↺ Revert</Btn>
                    <Btn onClick={saveSeeds}>💾 Save Seeds</Btn>
                  </div>
                )}
                {saved && (
                  <div style={{ marginTop: 16, background: "rgba(46,204,113,0.08)", border: "1px solid rgba(46,204,113,0.25)", borderRadius: 4, padding: "10px 14px", fontSize: 13, color: "var(--green)", display: "flex", alignItems: "center", gap: 8 }}>
                    ✓ Seeds saved — these will be applied when the tournament is started
                  </div>
                )}
              </div>

              {/* Info panel */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ ...css.card, padding: 16 }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12, color: "var(--gold)" }}>Seeding Guide</div>
                  {[
                    ["Standard Bracket", "Best vs worst each round: #1 vs #" + seeds.length + ", #2 vs #" + (seeds.length - 1) + ", etc."],
                    ["Power Rating", "Composite score: Elo (60%) + Placements (30%) + Cup Diff (10%)"],
                    ["Manual Override", "Drag rows or use ↑↓ buttons for custom seeding"],
                    ["Byes", "If teams < next power of 2, top seeds receive automatic byes"],
                  ].map(([title, desc]) => (
                    <div key={title} style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 1, color: "var(--white)", marginBottom: 3 }}>{title}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  ))}
                </div>

                <div style={{ ...css.card, padding: 16 }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, color: "var(--gold)" }}>Bracket Size</div>
                  {(() => {
                    const n = seeds.length;
                    const bracketSize = Math.pow(2, Math.ceil(Math.log2(Math.max(n, 2))));
                    const byes = bracketSize - n;
                    return (
                      <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 2 }}>
                        <div>Teams: <strong style={{ color: "var(--white)" }}>{n}</strong></div>
                        <div>Bracket size: <strong style={{ color: "var(--gold-light)" }}>{bracketSize}-team</strong></div>
                        <div>Byes: <strong style={{ color: byes > 0 ? "var(--gold)" : "var(--green)" }}>{byes} {byes > 0 ? "(top seeds)" : "✓ None"}</strong></div>
                        <div>Rounds: <strong style={{ color: "var(--white)" }}>{Math.log2(bracketSize)}</strong></div>
                      </div>
                    );
                  })()}
                </div>

                <Btn onClick={() => setTab("preview")} v="ghost" style={{ justifyContent: "center" }}>
                  Preview Bracket Matchups →
                </Btn>
              </div>
            </div>
          )}

          {/* ── PREVIEW TAB ── */}
          {tab === "preview" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  First-round matchups based on current seed order ({seeds.length} teams → {Math.pow(2, Math.ceil(Math.log2(Math.max(seeds.length, 2))))} bracket)
                </div>
                <Btn sz="sm" v="ghost" onClick={() => setTab("seeding")}>← Edit Seeds</Btn>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, marginBottom: 20 }}>
                {matchups.map((m, i) => {
                  const topRating = m.top ? teamRating(m.top) : null;
                  const botRating = m.bottom ? teamRating(m.bottom) : null;
                  const topIdx = seeds.indexOf(m.top);
                  const botIdx = m.bottom ? seeds.indexOf(m.bottom) : -1;
                  const isBye = !m.bottom;
                  return (
                    <div key={i} style={{ ...css.card, overflow: "hidden", border: `1px solid ${isBye ? "rgba(255,255,255,0.04)" : "var(--navy-border)"}` }}>
                      <div style={{ padding: "6px 12px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid var(--navy-border)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 2, color: "var(--muted)", textTransform: "uppercase" }}>
                        Match {i + 1}
                      </div>
                      {/* Top seed */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, width: 28, textAlign: "center", color: topIdx === 0 ? "var(--gold)" : topIdx === 1 ? "#c0c0c0" : topIdx === 2 ? "#cd7f32" : "var(--muted)", flexShrink: 0 }}>{topIdx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.top?.teamName || "TBD"}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{m.top?.players?.map(p => p.name).join(" · ")}</div>
                        </div>
                        {topRating && <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "var(--gold-light)", lineHeight: 1 }}>{topRating.pr}</div>
                          <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>PR</div>
                        </div>}
                      </div>
                      {/* Bottom seed / BYE */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", opacity: isBye ? 0.45 : 1 }}>
                        {isBye ? (
                          <>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, width: 28, textAlign: "center", color: "var(--muted)", flexShrink: 0 }}>—</div>
                            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600, fontSize: 14, color: "var(--muted)", fontStyle: "italic" }}>BYE — {m.top?.teamName} advances</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, width: 28, textAlign: "center", color: "var(--muted)", flexShrink: 0 }}>{botIdx + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.bottom?.teamName}</div>
                              <div style={{ fontSize: 11, color: "var(--muted)" }}>{m.bottom?.players?.map(p => p.name).join(" · ")}</div>
                            </div>
                            {botRating && <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: "var(--gold-light)", lineHeight: 1 }}>{botRating.pr}</div>
                              <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>PR</div>
                            </div>}
                          </>
                        )}
                      </div>
                      {/* Rating gap */}
                      {!isBye && topRating && botRating && (
                        <div style={{ padding: "4px 12px", background: "rgba(255,255,255,0.02)", borderTop: "1px solid var(--navy-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>PR GAP</span>
                          <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, color: Math.abs(topRating.pr - botRating.pr) > 200 ? "var(--red)" : Math.abs(topRating.pr - botRating.pr) > 80 ? "var(--gold)" : "var(--green)" }}>
                            {Math.abs(topRating.pr - botRating.pr)}
                            <span style={{ fontSize: 10, color: "var(--muted)", marginLeft: 4 }}>{Math.abs(topRating.pr - botRating.pr) > 200 ? "Mismatch" : Math.abs(topRating.pr - botRating.pr) > 80 ? "Favored" : "Even"}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Seeding summary table */}
              <div style={{ ...css.card }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--navy-border)" }}>
                  <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: 1, textTransform: "uppercase" }}>Full Seed Order</span>
                </div>
                <div className="resp-table"><table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>{["Seed", "Team", "Players", "Power Rating", "Elo", "First Opponent", "Status"].map(h => <th key={h} style={css.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {seeds.map((reg, i) => {
                      const { pr, elo } = teamRating(reg);
                      const matchup = matchups[Math.floor(i / 2)]; // rough pairing
                      // Find actual opponent
                      const oppMatchup = matchups.find(m => m.top?.id === reg.id || m.bottom?.id === reg.id);
                      const opp = oppMatchup ? (oppMatchup.top?.id === reg.id ? oppMatchup.bottom : oppMatchup.top) : null;
                      return (
                        <tr key={reg.id}>
                          <td style={css.td}>
                            <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, color: i === 0 ? "var(--gold)" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "var(--muted)" }}>{i + 1}</span>
                          </td>
                          <td style={css.td}><div style={{ fontWeight: 600 }}>{reg.teamName}</div></td>
                          <td style={{ ...css.td, fontSize: 12, color: "var(--muted)" }}>{reg.players?.map(p => p.name).join(", ")}</td>
                          <td style={{ ...css.td, fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: "var(--gold-light)" }}>{pr}</td>
                          <td style={{ ...css.td, fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: "var(--muted)" }}>{elo}</td>
                          <td style={css.td}>
                            {opp ? (
                              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600, fontSize: 13 }}>#{seeds.indexOf(opp) + 1} {opp.teamName}</span>
                            ) : (
                              <span style={{ color: "var(--green)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 12 }}>BYE</span>
                            )}
                          </td>
                          <td style={css.td}><Badge color={reg.status === "checked_in" ? "green" : reg.status === "no_show" ? "red" : ""}>{reg.status.replace("_", " ")}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </div>
            </div>
          )}

          {/* ── Group Draw Tab ───────────────────────────────────────────────── */}
          {tab === "groupdraw" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

              {/* Controls */}
              <div style={{ ...css.card, padding:"14px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                  <Fld label="Number of Groups" style={{ flex:"0 0 auto" }}>
                    <div style={{ display:"flex", gap:4 }}>
                      {[2,3,4,6,8].map(n=>(
                        <button key={n} onClick={()=>setGroupCount(n)}
                          style={{ width:32, height:32, borderRadius:4, border:`1px solid ${groupCount===n?"var(--gold)":"var(--navy-border)"}`, background:groupCount===n?"rgba(201,168,76,0.12)":"transparent", color:groupCount===n?"var(--gold)":"var(--muted)", cursor:"pointer", fontFamily:"'Bebas Neue',sans-serif", fontSize:16 }}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </Fld>
                  <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                    <Btn onClick={runGroupDraw} disabled={seeds.length < 2}>🎰 Draw Groups</Btn>
                    {groupDraw && <Btn onClick={commitGroupDraw} style={{ background:"rgba(46,204,113,0.12)", borderColor:"rgba(46,204,113,0.35)", color:"var(--green)" }}>✓ Commit Draw</Btn>}
                    {groupDraw && <Btn v="ghost" onClick={()=>setGroupDraw(null)}>✕ Discard</Btn>}
                  </div>
                </div>
                {seeds.length < 2 && <div style={{ fontSize:12, color:"var(--red)", marginTop:8 }}>Need at least 2 seeded teams to draw groups</div>}
                <div style={{ fontSize:12, color:"var(--muted)", marginTop:8 }}>
                  Teams are distributed into groups using a pot system — top seeds spread across all groups before lower seeds are drawn.
                </div>
              </div>

              {/* Back-to-back warnings */}
              {btbWarnings.length > 0 && (
                <div style={{ padding:"10px 14px", background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:5, fontSize:12 }}>
                  <div style={{ fontWeight:700, color:"var(--gold)", marginBottom:6 }}>⚠ Back-to-Back Pairs Detected</div>
                  {btbWarnings.map((w,i)=>(
                    <div key={i} style={{ color:"var(--muted)", fontSize:11, marginBottom:3 }}>Group {w.group}: {w.a} ↔ {w.b} played together previously</div>
                  ))}
                  <div style={{ marginTop:6, fontSize:11, color:"var(--muted)" }}>Redraw to avoid repeat matchups, or commit anyway.</div>
                </div>
              )}

              {/* Group cards */}
              {groupDraw ? (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
                  {groupDraw.map((g, gi) => {
                    let cumTeams = groupDraw.slice(0,gi).reduce((s,grp)=>s+grp.teams.length,0);
                    return (
                      <div key={g.groupLabel} style={{ ...css.card, padding:0, overflow:"hidden" }}>
                        <div style={{ padding:"8px 14px", background:"rgba(201,168,76,0.08)", borderBottom:"1px solid rgba(201,168,76,0.2)", display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--gold)", letterSpacing:2 }}>Group {g.groupLabel}</div>
                          <span style={{ fontSize:11, color:"var(--muted)", marginLeft:"auto" }}>{g.teams.length} teams</span>
                        </div>
                        {g.teams.map((t, ti) => {
                          const revealed = cumTeams + ti < drawAnimIdx;
                          return (
                            <div key={t.id} style={{ padding:"8px 14px", borderBottom:"1px solid rgba(255,255,255,0.04)", display:"flex", alignItems:"center", gap:8, opacity:revealed?1:0, transform:revealed?"translateY(0)":"translateY(6px)", transition:"all 0.25s" }}>
                              <div style={{ width:20, height:20, borderRadius:"50%", background:"rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontFamily:"'Bebas Neue',sans-serif", color:"var(--muted)" }}>{t.seed||ti+1}</div>
                              <span style={{ fontSize:12, fontWeight:600 }}>{t.teamName}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>🎰</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Group Draw</div>
                  <div style={{ fontSize:13 }}>Set number of groups above and click Draw to generate balanced groups from current seeds</div>
                </div>
              )}
            </div>
          )}

          {/* ── Back-to-back conflict banner in seeding tab ─────────────────── */}
          {tab === "seeding" && liveConflicts.length > 0 && (
            <div style={{ padding:"10px 14px", background:"rgba(217,79,61,0.07)", border:"1px solid rgba(217,79,61,0.25)", borderRadius:5, fontSize:12, marginTop:4 }}>
              <div style={{ fontWeight:700, color:"var(--red)", marginBottom:5 }}>⚠ {liveConflicts.length} First-Round Back-to-Back Conflict{liveConflicts.length!==1?"s":""}</div>
              {liveConflicts.map((c,i)=>(
                <div key={i} style={{ color:"var(--muted)", fontSize:11 }}>Seed {i+1} matchup: <strong style={{color:"var(--white)"}}>{c.a}</strong> vs <strong style={{color:"var(--white)"}}>{c.b}</strong> — played previously</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── TABLE MANAGER ────────────────────────────────────────────────────────────
const DEFAULT_TABLE_COUNT = 4;

function makeTable(idx) {
  return {
    id: uid(),
    name: `Table ${idx + 1}`,
    status: "idle",        // idle | active | paused | blocked
    currentMatch: null,
    pausedAt: null,
    elapsed: 0,
    gameTimer: 0,
    timerRunning: false,
    lastTeams: [],
    history: [],
  };
}

function TableManagerPage({ tournaments, setTournaments, activeTournament, setActiveTournament, setPage }) {
  const { isMobile } = useBreakpoint();
  const [selectedTournament, setSelectedTournament] = useState(activeTournament?.id || "");
  const [tables, setTables] = useState(() => Array.from({ length: DEFAULT_TABLE_COUNT }, (_, i) => makeTable(i)));
  const [globalPaused, setGlobalPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [statusBoard, setStatusBoard] = useState(false); // full-screen status board
  const [settingsForm, setSettingsForm] = useState({ tableCount: DEFAULT_TABLE_COUNT, gameTimerMins: 0, preventBackToBack: true });
  const [tick, setTick] = useState(0);
  const [assignModal,   setAssignModal]   = useState(null);
  const [doneModal,     setDoneModal]     = useState(null);  // { tableId, team1, team2, matchId }
  const [bulkRoundModal,setBulkRoundModal]= useState(false);
  const [bulkRound,     setBulkRound]     = useState(1);
  const [assignHistory, setAssignHistory] = useState([]); // {matchId,tableId,ts,t1,t2}
  const [showAssignHistory, setShowAssignHistory] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renamVal, setRenamVal] = useState("");
  const [toast, setToast] = useState(null);
  const [queueSort, setQueueSort] = useState("priority"); // priority | round | time
  const [viewMode, setViewMode]   = useState("grid");     // grid | floormap
  const [selectedQueueMatch, setSelectedQueueMatch] = useState(null); // match to assign next
  const [dragTable,      setDragTable]      = useState(null);    // tableId being dragged to
  const [dragMatch,      setDragMatch]      = useState(null);    // matchId being dragged
  const [dropHighlight,  setDropHighlight]  = useState(null);    // tableId hovering over
  const [multiVenue,     setMultiVenue]     = useState(false);   // venue routing mode
  const [venueFilter,    setVenueFilter]    = useState("all");   // "all" | venue name
  const [tableNotes,     setTableNotes]     = useState({});      // tableId -> note string
  const [editNoteId,     setEditNoteId]     = useState(null);
  const [noteDraft,      setNoteDraft]      = useState("");
  const [timerAlert,     setTimerAlert]     = useState(null);    // tableId of expired timer
  const [historyExpand,  setHistoryExpand]  = useState(null);    // tableId for history panel

  const tournament = tournaments.find(t => t.id === selectedTournament) || activeTournament;
  const upcoming   = tournaments.filter(t => t.status === "upcoming" || t.status === "active");

  // Clock tick every second
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  // ESC closes status board
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setStatusBoard(false); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function showToast(msg, color = "var(--green)") {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  }

  // ── Timer helpers ──────────────────────────────────────────────────────────
  function getElapsed(table) {
    if (table.status !== "active") return table.elapsed;
    return table.elapsed + (Date.now() - (table.currentMatch?.startedAt || Date.now()));
  }
  function fmtTime(ms) {
    const s = Math.floor(ms / 1000), m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, "0")}`;
  }
  function fmtCountdown(table) {
    if (!table.gameTimer) return null;
    const remaining = Math.max(0, table.gameTimer * 1000 - getElapsed(table));
    return { str: fmtTime(remaining), expired: remaining === 0 };
  }

  // ── Table actions ──────────────────────────────────────────────────────────
  function pauseTable(id) {
    setTables(prev => prev.map(t => {
      if (t.id !== id) return t;
      if (t.status === "active")  return { ...t, status:"paused", elapsed:getElapsed(t), pausedAt:Date.now() };
      if (t.status === "paused")  return { ...t, status:"active", currentMatch:t.currentMatch?{...t.currentMatch,startedAt:Date.now()}:null };
      return t;
    }));
  }

  function pauseAllTables() {
    const next = !globalPaused;
    setGlobalPaused(next);
    setTables(prev => prev.map(t => {
      if (t.status === "active" && next)  return { ...t, status:"paused", elapsed:getElapsed(t), pausedAt:Date.now() };
      if (t.status === "paused" && !next) return { ...t, status:"active", currentMatch:t.currentMatch?{...t.currentMatch,startedAt:Date.now()}:null };
      return t;
    }));
    showToast(next ? "⏸ All tables paused" : "▶ All tables resumed");
  }

  function clearTable(id, skipDoneModal) {
    const table = tables.find(t => t.id === id);
    if (table?.currentMatch && !skipDoneModal) {
      setDoneModal({ tableId:id, team1:table.currentMatch.team1, team2:table.currentMatch.team2, matchId:table.currentMatch.matchId });
      return;
    }
    setTables(prev => prev.map(t => {
      if (t.id !== id) return t;
      const hist = t.currentMatch ? [...t.history, { ...t.currentMatch, endedAt:Date.now(), elapsed:getElapsed(t) }] : t.history;
      const lastTeams = t.currentMatch ? [[t.currentMatch.team1, t.currentMatch.team2], ...t.lastTeams].slice(0,2) : t.lastTeams;
      return { ...t, status:"idle", currentMatch:null, elapsed:0, pausedAt:null, lastTeams, history:hist };
    }));
    setDoneModal(null);
  }

  function blockTable(id) {
    setTables(prev => prev.map(t => {
      if (t.id !== id) return t;
      return t.status === "blocked" ? { ...t, status:"idle" } : { ...t, status:"blocked", currentMatch:null, elapsed:0 };
    }));
  }

  function assignMatch(tableId, team1, team2, matchId) {
    const table = tables.find(t => t.id === tableId);
    if (settingsForm.preventBackToBack && table) {
      const last = table.lastTeams[0];
      if (last && ((last[0]===team1&&last[1]===team2)||(last[0]===team2&&last[1]===team1))) {
        showToast(`⚠️ Back-to-back: ${team1} vs ${team2} just played here`, "var(--gold)");
        return;
      }
    }
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return { ...t, status:"active", elapsed:0, currentMatch:{ matchId, team1, team2, startedAt:Date.now() }, gameTimer:settingsForm.gameTimerMins*60 };
    }));
    setAssignModal(null);
    showToast(`▶ ${team1} vs ${team2} → ${tables.find(t=>t.id===tableId)?.name}`);
  }

  // ── AUTO-ASSIGN: fill all idle tables with next pending matches ────────────
  // ── Drag-assign helpers ──────────────────────────────────────────────────
  function handleMatchDragStart(e, match) {
    setDragMatch(match);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleTableDragOver(e, tableId) {
    const tbl = tables.find(t => t.id === tableId);
    if (!tbl || tbl.status !== "idle") return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropHighlight(tableId);
  }

  function handleTableDrop(e, tableId) {
    e.preventDefault();
    setDropHighlight(null);
    if (!dragMatch) return;
    const tbl = tables.find(t => t.id === tableId);
    if (!tbl || tbl.status !== "idle") { showToast("Table not idle", "var(--gold)"); return; }
    assignMatchToTable(tableId, dragMatch);
    setDragMatch(null);
    showToast(`✓ ${dragMatch.t1?.name} vs ${dragMatch.t2?.name} → ${tbl.name}`);
  }

  function handleTableDragLeave() { setDropHighlight(null); }

  function assignMatchToTable(tableId, match) {
    const tblName = tables.find(t=>t.id===tableId)?.name||tableId;
    setAssignHistory(h => [{
      matchId: match.id||match.matchId,
      tableId, tableName: tblName,
      t1: match.t1?.name||"?", t2: match.t2?.name||"?",
      round: match._round||match.round, bracket: match._bracket||match.bracket,
      ts: new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
    }, ...h].slice(0,50));
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const backToBack = settingsForm.preventBackToBack && t.lastTeams.some(pair =>
        pair.includes(match.t1?.name) && pair.includes(match.t2?.name)
      );
      if (backToBack) showToast(`⚠ Back-to-back same matchup at ${t.name}`, "var(--gold)");
      return { ...t, status:"active", currentMatch:{
        matchId: match.id, team1: match.t1?.name, team2: match.t2?.name,
        startedAt: Date.now(), gameTimer: settingsForm.gameTimerMins,
      }};
    }));
    setSelectedQueueMatch(null);
  }

  // ── Timer alert check ─────────────────────────────────────────────────────
  const expiredTables = tables.filter(t => {
    if (!t.gameTimer || t.status !== "active") return false;
    const cd = fmtCountdown(t);
    return cd?.expired;
  });

  // ── Table note helpers ─────────────────────────────────────────────────────
  function saveNote(tableId) {
    setTableNotes(n => ({ ...n, [tableId]: noteDraft }));
    setEditNoteId(null);
    setNoteDraft("");
  }

  // ── Venue filter logic ─────────────────────────────────────────────────────
  const venueNames = [...new Set(tables.map(t => t.venue || "Main Hall").filter(Boolean))];

  const visibleTables = venueFilter === "all"
    ? tables
    : tables.filter(t => (t.venue || "Main Hall") === venueFilter);

  // ── Session stats ──────────────────────────────────────────────────────────
  const sessionStats = {
    totalPlayed:  tables.reduce((s,t) => s + t.history.length, 0),
    avgGameMs:    (() => {
      const all = tables.flatMap(t => t.history.map(h => h.elapsed || 0)).filter(Boolean);
      return all.length ? Math.round(all.reduce((s,v)=>s+v,0)/all.length) : 0;
    })(),
    longestGame:  tables.reduce((max,t) => Math.max(max, ...t.history.map(h=>h.elapsed||0), 0), 0),
    idleTime:     tables.reduce((s,t) => s + (t.status==="idle"?1:0), 0),
  };


  function autoAssign() {
    const idleTables = tables.filter(t => t.status === "idle");
    if (idleTables.length === 0) { showToast("No idle tables available", "var(--gold)"); return; }
    if (unassigned.length === 0) { showToast("No unassigned matches pending", "var(--gold)"); return; }

    let assigned = 0;
    let updatedTables = [...tables];
    const matchQueue = [...sortedQueue.filter(m => !assignedMatchIds.includes(m.id))];

    for (const idleTable of idleTables) {
      if (matchQueue.length === 0) break;
      // Find first match that doesn't violate back-to-back for this table
      const tableState = updatedTables.find(t => t.id === idleTable.id);
      const matchIdx = matchQueue.findIndex(m => {
        if (!settingsForm.preventBackToBack || !tableState?.lastTeams[0]) return true;
        const last = tableState.lastTeams[0];
        return !((last[0]===m.t1?.name&&last[1]===m.t2?.name)||(last[0]===m.t2?.name&&last[1]===m.t1?.name));
      });
      if (matchIdx === -1) continue;
      const m = matchQueue.splice(matchIdx, 1)[0];
      updatedTables = updatedTables.map(t => {
        if (t.id !== idleTable.id) return t;
        return { ...t, status:"active", elapsed:0, currentMatch:{ matchId:m.id, team1:m.t1?.name, team2:m.t2?.name, startedAt:Date.now() }, gameTimer:settingsForm.gameTimerMins*60 };
      });
      assigned++;
    }

    setTables(updatedTables);
    if (assigned > 0) showToast(`▶ Auto-assigned ${assigned} match${assigned>1?"es":""} to tables`);
    else showToast("⚠️ All idle tables have back-to-back conflicts", "var(--gold)");
  }

  function renameTable(id) {
    if (!renamVal.trim()) return;
    setTables(prev => prev.map(t => t.id===id ? { ...t, name:renamVal.trim() } : t));
    setRenameId(null); setRenamVal("");
  }

  function applySettings() {
    const { tableCount } = settingsForm;
    setTables(prev => {
      const next = [...prev];
      while (next.length < tableCount) next.push(makeTable(next.length));
      return next.slice(0, tableCount);
    });
    setShowSettings(false);
    showToast("✓ Settings applied");
  }

  // ── Pending matches — extended to all bracket types ────────────────────────
  function getPendingMatches() {
    if (!tournament?.bracket) return [];
    const bkt = tournament.bracket;
    const all = [];
    function collectRounds(rounds, bracketLabel) {
      (rounds||[]).forEach(r => (r.matches||[]).forEach(m => {
        if (m.status === "pending" && m.t1 && m.t2)
          all.push({ ...m, _bracket: bracketLabel, _round: r.name });
      }));
    }
    if (bkt.type === "de") {
      collectRounds(bkt.wb||[], "W");
      collectRounds(bkt.lb||[], "L");
      if (bkt.gf?.status==="pending"&&bkt.gf.t1&&bkt.gf.t2) all.push({...bkt.gf, _bracket:"GF", _round:"Grand Final"});
      if (bkt.gf?.reset?.status==="pending"&&bkt.gf.reset.t1&&bkt.gf.reset.t2) all.push({...bkt.gf.reset, _bracket:"GF", _round:"Reset"});
    } else if (bkt.type==="rpe_se"||bkt.type==="rpe_de") {
      collectRounds(bkt.prelims||[], "P");
      if (bkt.elimBracket) {
        const eb = bkt.elimBracket;
        if (eb.type==="de_phase") {
          collectRounds(eb.wb||[], "W"); collectRounds(eb.lb||[], "L");
          if (eb.gf?.status==="pending"&&eb.gf.t1&&eb.gf.t2) all.push({...eb.gf,_bracket:"GF",_round:"Grand Final"});
        } else collectRounds(eb.rounds||[], "");
      }
    } else {
      collectRounds(bkt.rounds||[], "");
    }
    return all;
  }

  // Priority score: GF=0, WB=1, Prelim=2, LB=3, other=4
  function matchPriority(m) {
    if (m._bracket==="GF") return 0;
    if (m._bracket==="W")  return 1;
    if (m._bracket==="P")  return 2;
    if (m._bracket==="L")  return 3;
    return 4;
  }

  const pendingMatches    = getPendingMatches();
  const assignedMatchIds  = tables.filter(t => t.currentMatch).map(t => t.currentMatch.matchId);
  const unassigned        = pendingMatches.filter(m => !assignedMatchIds.includes(m.id));

  const sortedQueue = [...unassigned].sort((a, b) => {
    if (queueSort === "priority") return matchPriority(a) - matchPriority(b);
    if (queueSort === "round")    return (a._round||"").localeCompare(b._round||"");
    return 0;
  });

  // ── Pause bracket match ────────────────────────────────────────────────────
  function pauseBracketMatch(matchId) {
    if (!tournament?.bracket) return;
    const updateMs = ms => ms.map(m => m.id===matchId ? { ...m, paused:!m.paused } : m);
    const bkt = tournament.bracket;
    let newBkt;
    if (bkt.type==="de") {
      newBkt = { ...bkt, wb:(bkt.wb||[]).map(r=>({...r,matches:updateMs(r.matches)})), lb:(bkt.lb||[]).map(r=>({...r,matches:updateMs(r.matches)})), gf:bkt.gf?{...bkt.gf,paused:bkt.gf.id===matchId?!bkt.gf.paused:bkt.gf.paused}:bkt.gf };
    } else {
      newBkt = { ...bkt, rounds:(bkt.rounds||[]).map(r=>({...r,matches:updateMs(r.matches)})) };
    }
    const updated = { ...tournament, bracket:newBkt };
    setActiveTournament(updated);
    setTournaments(prev => prev.map(t => t.id===tournament.id ? updated : t));
  }

  // ── Per-table stats ────────────────────────────────────────────────────────
  function tableStats(table) {
    const count = table.history.length;
    if (count === 0) return { count:0, avgMs:0 };
    const avgMs = table.history.reduce((s,m) => s + (m.elapsed||0), 0) / count;
    return { count, avgMs };
  }

  const statusColor = { idle:"var(--muted)", active:"var(--green)", paused:"var(--gold)", blocked:"var(--red)" };
  const statusBg    = { idle:"rgba(255,255,255,0.03)", active:"rgba(46,204,113,0.06)", paused:"rgba(201,168,76,0.08)", blocked:"rgba(217,79,61,0.08)" };

  const idleCount    = tables.filter(t=>t.status==="idle").length;
  const activeCount  = tables.filter(t=>t.status==="active").length;
  const pausedCount  = tables.filter(t=>t.status==="paused").length;
  const blockedCount = tables.filter(t=>t.status==="blocked").length;

  // ── STATUS BOARD (full-screen overlay) ─────────────────────────────────────
  if (statusBoard) {
    return (
      <div style={{ position:"fixed",inset:0,zIndex:3000,background:"#0a0d1a",overflow:"auto",padding:20 }}>
        {/* Board header */}
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20 }}>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:40,letterSpacing:6,color:"var(--gold)" }}>LIVE TABLE STATUS</div>
            {tournament && <div style={{ fontSize:14,color:"rgba(255,255,255,0.5)",letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{tournament.name}</div>}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:16 }}>
            {/* Summary counts */}
            {[["Active",activeCount,"var(--green)"],["Paused",pausedCount,"var(--gold)"],["Idle",idleCount,"var(--muted)"],["Blocked",blockedCount,"var(--red)"]].map(([l,v,c])=>(
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:36,color:c,lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:2,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
            <button onClick={()=>setStatusBoard(false)} style={{ padding:"8px 18px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:5,color:"rgba(255,255,255,0.5)",fontSize:12,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>✕ ESC</button>
          </div>
        </div>

        {/* Table grid — larger cards */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:16 }}>
          {tables.map(table => {
            const elapsed   = getElapsed(table);
            const countdown = fmtCountdown(table);
            const expired   = countdown?.expired;
            const sc        = statusColor[table.status];

            return (
              <div key={table.id} style={{ background:expired?"rgba(217,79,61,0.12)":statusBg[table.status],border:`2px solid ${sc}55`,borderRadius:10,overflow:"hidden",minHeight:180 }}>
                {/* Card header */}
                <div style={{ padding:"12px 18px",borderBottom:`1px solid ${sc}22`,background:`${sc}18`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <div style={{ width:10,height:10,borderRadius:"50%",background:sc,animation:table.status==="active"?"pulse 2s infinite":"none",flexShrink:0 }} />
                    <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:24,letterSpacing:3 }}>{table.name}</span>
                  </div>
                  <span style={{ fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:sc,background:`${sc}22`,padding:"3px 9px",borderRadius:4 }}>{table.status}</span>
                </div>

                {/* Card body */}
                <div style={{ padding:"18px 18px 14px",textAlign:"center" }}>
                  {table.status==="blocked" ? (
                    <div style={{ color:"var(--red)",opacity:0.7 }}>
                      <div style={{ fontSize:36,marginBottom:6 }}>🔒</div>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:2,textTransform:"uppercase" }}>Out of Service</div>
                    </div>
                  ) : table.currentMatch ? (
                    <>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:4 }}>{table.currentMatch.team1}</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:13,color:"rgba(255,255,255,0.3)",letterSpacing:4,marginBottom:4 }}>VS</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:12 }}>{table.currentMatch.team2}</div>
                      {/* Big timer */}
                      {countdown ? (
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:52,lineHeight:1,color:expired?"var(--red)":table.status==="paused"?"var(--gold)":"var(--white)",animation:expired?"pulse 1s infinite":"none" }}>{countdown.str}</div>
                      ) : (
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:44,lineHeight:1,color:table.status==="paused"?"var(--gold)":"rgba(255,255,255,0.5)" }}>{fmtTime(elapsed)}</div>
                      )}
                      <div style={{ fontSize:10,color:"rgba(255,255,255,0.3)",letterSpacing:2,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase",marginTop:4 }}>
                        {expired?"⏱ TIME EXPIRED":table.status==="paused"?"PAUSED":countdown?"remaining":"elapsed"}
                      </div>
                    </>
                  ) : (
                    <div style={{ color:"rgba(255,255,255,0.25)",paddingTop:16 }}>
                      <div style={{ fontSize:40,marginBottom:8 }}>🏓</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:18,letterSpacing:3 }}>AVAILABLE</div>
                      {unassigned.length>0 && <div style={{ fontSize:12,color:"rgba(255,255,255,0.3)",marginTop:4 }}>{unassigned.length} match{unassigned.length!==1?"es":""} queued</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Queue strip at bottom */}
        {unassigned.length>0 && (
          <div style={{ marginTop:20,padding:"12px 16px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8 }}>
            <div style={{ fontSize:11,color:"rgba(255,255,255,0.4)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>⏳ Up Next ({unassigned.length})</div>
            <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
              {sortedQueue.slice(0,8).map((m,i)=>(
                <div key={m.id} style={{ padding:"6px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:5,fontSize:12 }}>
                  <span style={{ color:"rgba(255,255,255,0.4)",marginRight:6 }}>#{i+1}</span>
                  <span style={{ fontWeight:600 }}>{m.t1?.name}</span>
                  <span style={{ color:"rgba(255,255,255,0.35)",margin:"0 6px" }}>vs</span>
                  <span style={{ fontWeight:600 }}>{m.t2?.name}</span>
                  {m._bracket&&<span style={{ marginLeft:6,fontSize:10,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{m._bracket==="GF"?"GF":m._bracket==="L"?"LB":"WB"}</span>}
                </div>
              ))}
              {unassigned.length>8&&<div style={{ padding:"6px 12px",fontSize:12,color:"rgba(255,255,255,0.3)" }}>+{unassigned.length-8} more</div>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── MAIN PAGE ──────────────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed",top:20,right:20,zIndex:2000,background:"var(--navy-mid)",border:`1px solid ${toast.color}`,borderRadius:6,padding:"12px 20px",fontSize:13,fontWeight:600,color:toast.color,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"fadeIn 0.2s ease",maxWidth:360 }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>Table Manager</div>
          <div style={{ color:"var(--muted)",fontSize:13,marginTop:3 }}>Live game table assignments, timers, and auto-scheduling</div>
        </div>
        <div style={{ display:"flex",gap:8,flexWrap:"wrap",alignItems:"center" }}>
          {/* View toggle */}
          <div style={{ display:"flex",border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden" }}>
            {[["grid","⊞ Grid"],["floormap","🗺 Map"]].map(([v,l])=>(
              <button key={v} onClick={()=>setViewMode(v)} style={{ padding:"5px 12px",background:viewMode===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:viewMode===v?"var(--gold)":"var(--muted)",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,cursor:"pointer",borderRight:v==="grid"?"1px solid var(--navy-border)":"none" }}>{l}</button>
            ))}
          </div>
          {unassigned.length>0 && idleCount>0 && (
            <Btn onClick={autoAssign} style={{ background:"rgba(201,168,76,0.12)",borderColor:"rgba(201,168,76,0.45)",color:"var(--gold)" }}>
              ⚡ Auto ({Math.min(idleCount,unassigned.length)})
            </Btn>
          )}
          <Btn v="ghost" onClick={()=>setStatusBoard(true)}>📺 Board</Btn>
          <Btn v={globalPaused?"success":"ghost"} onClick={pauseAllTables}>
            {globalPaused ? "▶ Resume All" : "⏸ Pause All"}
          </Btn>
          <Btn v="ghost" onClick={()=>setShowSettings(true)}>⚙</Btn>
          {tournament && <Btn v="ghost" onClick={()=>setPage("bracket")}>Bracket →</Btn>}
        </div>
      </div>

      {/* Tournament selector + stats strip */}
      <div style={{ ...css.card,padding:14,marginBottom:16 }}>
        <div style={{ display:"grid",gridTemplateColumns:"1fr auto",gap:16,alignItems:"end" }}>
          <Fld label="Active Tournament">
            <select style={css.select} value={selectedTournament} onChange={e=>setSelectedTournament(e.target.value)}>
              <option value="">— No tournament selected —</option>
              {upcoming.map(t=><option key={t.id} value={t.id}>{t.name} · {FORMATS.find(f=>f.v===t.format)?.l}</option>)}
            </select>
          </Fld>
          <div style={{ display:"flex",gap:14,alignItems:"center",paddingBottom:14 }}>
            {[["Active",activeCount,"var(--green)"],["Paused",pausedCount,"var(--gold)"],["Idle",idleCount,"var(--muted)"],["Blocked",blockedCount,"var(--red)"]].map(([l,v,c])=>(
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:26,color:c }}>{v}</div>
                <div style={{ fontSize:9,color:"var(--muted)",letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        {tournament && unassigned.length>0 && idleCount>0 && (
          <div style={{ marginTop:8,padding:"8px 12px",background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4,fontSize:12,color:"var(--gold)",display:"flex",alignItems:"center",gap:8 }}>
            <span>⚡</span>
            <span><strong>{idleCount} idle table{idleCount!==1?"s":""}</strong> · <strong>{unassigned.length} match{unassigned.length!==1?"es":""}</strong> waiting — click Auto-Assign to fill them.</span>
          </div>
        )}
        {/* Session stats strip */}
        {sessionStats.totalPlayed > 0 && (
          <div style={{ marginTop:10,display:"flex",gap:14,flexWrap:"wrap",paddingTop:10,borderTop:"1px solid var(--navy-border)" }}>
            {[
              [sessionStats.totalPlayed, "Matches Played", "var(--green)"],
              [fmtTime(sessionStats.avgGameMs), "Avg Game", "var(--gold)"],
              [fmtTime(sessionStats.longestGame), "Longest", "var(--muted)"],
              [expiredTables.length, "Expired", expiredTables.length>0?"var(--red)":"var(--muted)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ display:"flex",alignItems:"center",gap:6 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:c,lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        )}
        {/* Venue filter — only shown if tables have venue tags */}
        {venueNames.length > 1 && (
          <div style={{ marginTop:10,display:"flex",gap:6,flexWrap:"wrap" }}>
            {["all",...venueNames].map(v=>(
              <button key={v} onClick={()=>setVenueFilter(v)}
                style={{ padding:"3px 10px",borderRadius:20,border:`1px solid ${venueFilter===v?"var(--gold)":"var(--navy-border)"}`,background:venueFilter===v?"rgba(201,168,76,0.12)":"transparent",color:venueFilter===v?"var(--gold)":"var(--muted)",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,cursor:"pointer",textTransform:"uppercase" }}>
                {v === "all" ? "All Venues" : v}
              </button>
            ))}
          </div>
        )}
        {selectedQueueMatch && (
          <div style={{ marginTop:8,padding:"8px 12px",background:"rgba(91,141,238,0.1)",border:"1px solid rgba(91,141,238,0.3)",borderRadius:4,fontSize:12,color:"#5b8dee",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8 }}>
            <span>🎯 <strong>{selectedQueueMatch.t1?.name} vs {selectedQueueMatch.t2?.name}</strong> selected — click any idle table to assign</span>
            <button onClick={()=>setSelectedQueueMatch(null)} style={{ background:"none",border:"none",color:"#5b8dee",cursor:"pointer",fontSize:14 }}>✕</button>
          </div>
        )}
      </div>

      {/* ── FLOOR MAP VIEW ──────────────────────────────────────────────────── */}
      {viewMode==="floormap" ? (
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 340px", gap:16, marginBottom:24 }}>
          {/* Map canvas */}
          <div style={{ ...css.card, padding:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:2,color:"var(--muted)",textTransform:"uppercase",marginBottom:12 }}>
              Floor Layout — {tables.length} Tables
            </div>
            {/* Legend */}
            <div style={{ display:"flex",gap:10,marginBottom:12,flexWrap:"wrap" }}>
              {[["Idle","var(--navy-border)"],["Active","var(--green)"],["Paused","var(--gold)"],["Blocked","var(--red)"]].map(([l,c])=>(
                <div key={l} style={{ display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--muted)" }}>
                  <div style={{ width:8,height:8,borderRadius:2,background:c }} />{l}
                </div>
              ))}
            </div>
            {/* Grid */}
            {(() => {
              const COLS = Math.max(2, Math.ceil(Math.sqrt(tables.length)));
              const CELL = isMobile ? 72 : 90;
              const GAP  = isMobile ? 8 : 10;
              return (
                <div style={{ display:"grid",gridTemplateColumns:`repeat(${COLS},${CELL}px)`,gap:GAP,overflowX:"auto" }}>
                  {tables.map(table => {
                    const elapsed   = getElapsed(table);
                    const countdown = fmtCountdown(table);
                    const expired   = countdown?.expired;
                    const sc = expired?"var(--red)":statusColor[table.status];
                    const isIdle = table.status==="idle";
                    const canAssign = isIdle && selectedQueueMatch;
                    return (
                      <div
                        key={table.id}
                        onClick={()=>{
                          if (canAssign) { assignMatch(table.id, selectedQueueMatch.t1?.name, selectedQueueMatch.t2?.name, selectedQueueMatch.id); setSelectedQueueMatch(null); }
                          else if (isIdle && unassigned.length>0) setAssignModal(table.id);
                          else if (table.status==="active") setDoneModal({tableId:table.id,team1:table.currentMatch?.team1,team2:table.currentMatch?.team2,matchId:table.currentMatch?.matchId});
                        }}
                        style={{
                          width:CELL, height:CELL,
                          background:canAssign?"rgba(91,141,238,0.12)":statusBg[table.status],
                          border:`2px solid ${canAssign?"#5b8dee":sc}55`,
                          borderRadius:8,
                          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                          cursor:"pointer",gap:3,position:"relative",
                          transition:"all 0.15s",
                          boxShadow:canAssign?"0 0 0 2px rgba(91,141,238,0.3)":"none",
                        }}
                      >
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?20:24,color:sc,lineHeight:1 }}>{table.name.replace("Table ","")}</div>
                        {table.currentMatch ? (
                          <>
                            <div style={{ fontSize:8,textAlign:"center",overflow:"hidden",maxWidth:CELL-10,lineHeight:1.2,color:"rgba(255,255,255,0.7)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{table.currentMatch.team1}</div>
                            <div style={{ fontSize:7,color:"var(--muted)" }}>vs</div>
                            <div style={{ fontSize:8,textAlign:"center",overflow:"hidden",maxWidth:CELL-10,lineHeight:1.2,color:"rgba(255,255,255,0.7)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>{table.currentMatch.team2}</div>
                            <div style={{ fontSize:isMobile?9:11,fontFamily:"'Bebas Neue',sans-serif",color:expired?"var(--red)":table.status==="paused"?"var(--gold)":"rgba(255,255,255,0.5)",animation:expired?"pulse 1s infinite":"none" }}>
                              {countdown?countdown.str:fmtTime(elapsed)}
                            </div>
                          </>
                        ) : table.status==="blocked" ? (
                          <div style={{ fontSize:16 }}>🔒</div>
                        ) : (
                          <div style={{ fontSize:9,color:canAssign?"#5b8dee":"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase" }}>
                            {canAssign?"← Assign":"Idle"}
                          </div>
                        )}
                        {/* Status dot */}
                        <div style={{ position:"absolute",top:5,right:5,width:6,height:6,borderRadius:"50%",background:sc,animation:table.status==="active"?"pulse 2s infinite":"none" }} />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Queue panel alongside map */}
          <div style={{ ...css.card,padding:0,overflow:"hidden",display:"flex",flexDirection:"column" }}>
            <div style={{ padding:"10px 14px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:1,textTransform:"uppercase" }}>Queue</span>
              <span style={{ fontSize:11,color:"var(--muted)" }}>{unassigned.length} pending</span>
            </div>
            <div style={{ overflowY:"auto",flex:1,padding:8 }}>
              {unassigned.length===0 ? (
                <div style={{ textAlign:"center",padding:"24px 12px",color:"var(--muted)",fontSize:12 }}>No pending matches</div>
              ) : sortedQueue.map((m,i)=>{
                const isSel = selectedQueueMatch?.id===m.id;
                const bracketLabel = m._bracket==="GF"?"🏆 GF":m._bracket==="L"?"LB":m._bracket==="W"?"WB":m._bracket==="P"?"PRE":"";
                return (
                  <div key={m.id} onClick={()=>setSelectedQueueMatch(isSel?null:m)}
                    draggable
                    onDragStart={e=>handleMatchDragStart(e,m)}
                    style={{ padding:"8px 10px",marginBottom:5,background:isSel?"rgba(91,141,238,0.12)":"rgba(255,255,255,0.03)",border:`1px solid ${isSel?"rgba(91,141,238,0.4)":"var(--navy-border)"}`,borderRadius:5,cursor:"grab",transition:"all 0.15s",opacity:dragMatch?.id===m.id?0.5:1 }}>
                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2 }}>
                      <span style={{ fontSize:9,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,color:"var(--muted)",textTransform:"uppercase" }}>#{i+1}</span>
                      {bracketLabel && <span style={{ fontSize:9,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:m._bracket==="GF"?"var(--gold)":m._bracket==="L"?"var(--red)":"var(--muted)",letterSpacing:1 }}>{bracketLabel}</span>}
                    </div>
                    <div style={{ fontSize:12,fontWeight:700,lineHeight:1.3 }}>{m.t1?.name}</div>
                    <div style={{ fontSize:10,color:"var(--muted)",lineHeight:1 }}>vs</div>
                    <div style={{ fontSize:12,fontWeight:700,lineHeight:1.3 }}>{m.t2?.name}</div>
                    {isSel && <div style={{ marginTop:4,fontSize:10,color:"#5b8dee",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700 }}>→ click a table to assign</div>}
                  </div>
                );
              })}
            </div>
            {idleCount>0&&unassigned.length>0 && (
              <div style={{ padding:10,borderTop:"1px solid var(--navy-border)",display:"flex",flexDirection:"column",gap:6 }}>
                <Btn full onClick={autoAssign}>⚡ Auto-Assign All</Btn>
                {/* Bulk assign by round */}
                {(() => {
                  const rounds = [...new Set(unassigned.map(m=>m._round).filter(Boolean))];
                  if (rounds.length < 2) return null;
                  return (
                    <div style={{ display:"flex",gap:5,flexWrap:"wrap" }}>
                      <span style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",alignSelf:"center",flexShrink:0 }}>By Round:</span>
                      {rounds.slice(0,6).map(rnd => {
                        const roundMatches = unassigned.filter(m=>m._round===rnd);
                        return (
                          <Btn key={rnd} sz="sm" v="ghost"
                            onClick={()=>{
                              let updatedTables=[...tables];
                              roundMatches.forEach(m=>{
                                const idleT = updatedTables.find(t=>t.status==="idle");
                                if(idleT){
                                  assignMatch(idleT.id, m.t1?.name, m.t2?.name, m.id);
                                  updatedTables=updatedTables.map(t=>t.id===idleT.id?{...t,status:"active"}:t);
                                }
                              });
                            }}>
                            R{rnd} ({roundMatches.length})
                          </Btn>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── TABLE GRID VIEW ──────────────────────────────────────────────── */
        <>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:14,marginBottom:24 }}>
            {tables.map(table => {
              const elapsed    = getElapsed(table);
              const countdown  = fmtCountdown(table);
              const expired    = countdown?.expired;
              const borderColor= expired ? "var(--red)" : statusColor[table.status];
              const stats      = tableStats(table);
              const isIdle     = table.status==="idle";
              const canAssign  = isIdle && selectedQueueMatch;

              return (
                <div key={table.id}
                onDragOver={e=>handleTableDragOver(e,table.id)}
                onDrop={e=>handleTableDrop(e,table.id)}
                onDragLeave={handleTableDragLeave}
                style={{ background:statusBg[table.status],border:`2px solid ${dropHighlight===table.id?"#5b8dee":canAssign?"#5b8dee":(borderColor+"44")}`,borderRadius:8,overflow:"hidden",transition:"all 0.2s",boxShadow:dropHighlight===table.id?"0 0 0 3px rgba(91,141,238,0.4)":canAssign?"0 0 0 2px rgba(91,141,238,0.2)":"none" }}>
                  {/* Table header */}
                  <div style={{ padding:"10px 14px",borderBottom:`1px solid ${borderColor}22`,display:"flex",alignItems:"center",justifyContent:"space-between",background:`${borderColor}11` }}>
                    <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                      <div style={{ width:8,height:8,borderRadius:"50%",background:statusColor[table.status],animation:table.status==="active"?"pulse 2s infinite":"none" }} />
                      {renameId===table.id ? (
                        <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                          <input autoFocus style={{ ...css.input,width:120,padding:"3px 8px",fontSize:13 }} value={renamVal} onChange={e=>setRenamVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")renameTable(table.id);if(e.key==="Escape"){setRenameId(null);setRenamVal("");}}} />
                          <button onClick={()=>renameTable(table.id)} style={{ background:"var(--gold)",border:"none",color:"var(--navy)",borderRadius:3,padding:"3px 8px",fontSize:12,fontWeight:700,cursor:"pointer" }}>✓</button>
                          <button onClick={()=>{setRenameId(null);setRenamVal("");}} style={{ background:"none",border:"1px solid var(--navy-border)",color:"var(--muted)",borderRadius:3,padding:"3px 8px",fontSize:12,cursor:"pointer" }}>✕</button>
                        </div>
                      ) : (
                        <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:18,letterSpacing:2,cursor:"pointer" }} title="Click to rename" onClick={()=>{setRenameId(table.id);setRenamVal(table.name);}}>{table.name}</span>
                      )}
                      <span style={{ fontSize:9,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:statusColor[table.status],background:`${statusColor[table.status]}22`,padding:"1px 6px",borderRadius:3 }}>{table.status}</span>
                    </div>
                    <Btn sz="sm" v="ghost" onClick={()=>blockTable(table.id)} style={{ fontSize:10,padding:"3px 8px",color:table.status==="blocked"?"var(--red)":"var(--muted)" }}>
                      {table.status==="blocked"?"🔓":"🔒"}
                    </Btn>
                  </div>

                  {/* Match body */}
                  <div style={{ padding:"14px 14px 10px" }}>
                    {table.status==="blocked" ? (
                      <div style={{ textAlign:"center",padding:"20px 0",color:"var(--red)",opacity:0.7 }}>
                        <div style={{ fontSize:28,marginBottom:4 }}>🔒</div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:2,textTransform:"uppercase" }}>Table Blocked</div>
                        <div style={{ fontSize:11,marginTop:4,color:"var(--muted)" }}>Out of service</div>
                      </div>
                    ) : table.currentMatch ? (
                      <>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:16,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{table.currentMatch.team1}</div>
                            <div style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:2,margin:"4px 0" }}>VS</div>
                            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:16,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{table.currentMatch.team2}</div>
                          </div>
                          <div style={{ textAlign:"center",minWidth:72,flexShrink:0 }}>
                            {countdown ? (
                              <>
                                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:32,lineHeight:1,color:expired?"var(--red)":table.status==="paused"?"var(--gold)":"var(--white)",animation:expired?"pulse 1s infinite":"none" }}>{countdown.str}</div>
                                <div style={{ fontSize:9,color:"var(--muted)",letterSpacing:2,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>{expired?"TIME!":"Left"}</div>
                              </>
                            ) : (
                              <>
                                <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,lineHeight:1,color:table.status==="paused"?"var(--gold)":"var(--muted)" }}>{fmtTime(elapsed)}</div>
                                <div style={{ fontSize:9,color:"var(--muted)",letterSpacing:2,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>Elapsed</div>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ display:"flex",gap:6 }}>
                          <Btn sz="sm" v={table.status==="paused"?"success":"ghost"} onClick={()=>pauseTable(table.id)} style={{ flex:1,justifyContent:"center" }}>
                            {table.status==="paused"?"▶ Resume":"⏸ Pause"}
                          </Btn>
                          <Btn sz="sm" v="danger" onClick={()=>clearTable(table.id)} style={{ flex:1,justifyContent:"center" }}>✓ Done</Btn>
                        </div>
                        {expired && <div style={{ marginTop:8,background:"rgba(217,79,61,0.12)",border:"1px solid rgba(217,79,61,0.3)",borderRadius:4,padding:"6px 10px",fontSize:11,color:"var(--red)",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,animation:"pulse 2s infinite" }}>⏱ TIME EXPIRED — Enter score</div>}
                        {table.status==="paused" && <div style={{ marginTop:8,background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4,padding:"6px 10px",fontSize:11,color:"var(--gold)",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5 }}>⏸ PAUSED — {fmtTime(elapsed)} elapsed</div>}
                      </>
                    ) : (
                      <div style={{ textAlign:"center",padding:"14px 0" }}>
                        <div style={{ fontSize:28,marginBottom:6,opacity:0.3 }}>🏓</div>
                        {canAssign ? (
                          <>
                            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:1.5,color:"#5b8dee",textTransform:"uppercase",marginBottom:8 }}>Click to Assign Here</div>
                            <Btn sz="sm" full style={{ justifyContent:"center",background:"rgba(91,141,238,0.15)",borderColor:"rgba(91,141,238,0.4)",color:"#5b8dee" }} onClick={()=>{assignMatch(table.id,selectedQueueMatch.t1?.name,selectedQueueMatch.t2?.name,selectedQueueMatch.id);setSelectedQueueMatch(null);}}>
                              ▶ {selectedQueueMatch.t1?.name} vs {selectedQueueMatch.t2?.name}
                            </Btn>
                          </>
                        ) : (
                          <>
                            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:1.5,color:"var(--muted)",textTransform:"uppercase",marginBottom:10 }}>Table Available</div>
                            {unassigned.length>0 && <Btn sz="sm" onClick={()=>setAssignModal(table.id)} style={{ width:"100%",justifyContent:"center" }}>+ Assign Match</Btn>}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div style={{ padding:"6px 14px 10px",borderTop:"1px solid rgba(255,255,255,0.04)",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                    <div>
                      {table.history.length>0 ? (
                        <>
                          <div style={{ fontSize:9,letterSpacing:2,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>Last game</div>
                          <div style={{ fontSize:11,color:"var(--muted)",marginTop:1 }}>{table.history[table.history.length-1].team1} vs {table.history[table.history.length-1].team2}</div>
                        </>
                      ) : <div style={{ fontSize:10,color:"rgba(255,255,255,0.15)" }}>No games yet</div>}
                    </div>
                    {stats.count>0 && (
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:9,letterSpacing:2,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>Avg</div>
                        <div style={{ fontSize:11,color:"var(--muted)" }}>{fmtTime(stats.avgMs)} · {stats.count} played</div>
                      </div>
                    )}
                  </div>
                  {/* ── Table note ── */}
                  {editNoteId === table.id ? (
                    <div style={{ padding:"8px 12px", borderTop:"1px solid var(--navy-border)", display:"flex", gap:6 }}>
                      <input autoFocus style={{ ...css.input, flex:1, fontSize:11, padding:"4px 8px" }}
                        value={noteDraft} onChange={e=>setNoteDraft(e.target.value)}
                        onKeyDown={e=>{ if(e.key==="Enter") saveNote(table.id); if(e.key==="Escape"){ setEditNoteId(null); setNoteDraft(""); }}}
                        placeholder="Table note (press Enter to save)…" />
                      <button onClick={()=>saveNote(table.id)} style={{ background:"none",border:"none",color:"var(--gold)",cursor:"pointer",fontSize:14 }}>✓</button>
                      <button onClick={()=>{ setEditNoteId(null); setNoteDraft(""); }} style={{ background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14 }}>✕</button>
                    </div>
                  ) : tableNotes[table.id] ? (
                    <div onClick={()=>{ setEditNoteId(table.id); setNoteDraft(tableNotes[table.id]||""); }} style={{ padding:"5px 12px", borderTop:"1px solid var(--navy-border)", fontSize:11, color:"var(--gold)", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                      <span>📝</span><span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tableNotes[table.id]}</span>
                      <span style={{ color:"var(--muted)", fontSize:10 }}>edit</span>
                    </div>
                  ) : (
                    <div onClick={()=>{ setEditNoteId(table.id); setNoteDraft(""); }} style={{ padding:"3px 12px", borderTop:"1px solid rgba(255,255,255,0.04)", fontSize:10, color:"rgba(255,255,255,0.15)", cursor:"pointer" }}>
                      + add note
                    </div>
                  )}
                  {/* ── Match history expand ── */}
                  {table.history.length > 0 && (
                    <div>
                      <div onClick={()=>setHistoryExpand(historyExpand===table.id?null:table.id)} style={{ padding:"5px 12px", borderTop:"1px solid rgba(255,255,255,0.04)", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
                        <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>History ({table.history.length})</span>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.2)" }}>{historyExpand===table.id?"▲":"▼"}</span>
                      </div>
                      {historyExpand===table.id && (
                        <div style={{ padding:"0 12px 8px", borderTop:"1px solid rgba(255,255,255,0.04)", maxHeight:140, overflowY:"auto" }}>
                          {table.history.slice().reverse().map((h,hi)=>(
                            <div key={hi} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"3px 0", borderBottom:hi<table.history.length-1?"1px solid rgba(255,255,255,0.04)":"none", fontSize:11 }}>
                              <span style={{ color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:160 }}>{h.team1} vs {h.team2}</span>
                              <span style={{ color:"var(--muted)", fontSize:10, flexShrink:0 }}>{fmtTime(h.elapsed||0)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── MATCH QUEUE ─────────────────────────────────────────────────── */}
          {tournament && (
            <div style={{ ...css.card,marginBottom:24 }}>
              <div style={{ padding:"12px 16px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8 }}>
                <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1,textTransform:"uppercase" }}>Match Queue</span>
                  <span style={{ fontSize:12,color:"var(--muted)" }}>{unassigned.length} pending · {assignedMatchIds.length} on tables</span>
                </div>
                <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                  <Btn sz="sm" v="ghost" onClick={()=>setBulkRoundModal(true)} style={{fontSize:11}}>⚡ Bulk Assign Round</Btn>
                  <Btn sz="sm" v="ghost" onClick={()=>setShowAssignHistory(h=>!h)} style={{fontSize:11}}>{showAssignHistory?"Hide":"📋"} History</Btn>
                  <span style={{ fontSize:11,color:"var(--muted)" }}>Sort:</span>
                  {[["priority","Priority"],["round","Round"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setQueueSort(v)} style={{ padding:"3px 10px",background:queueSort===v?"rgba(201,168,76,0.12)":"transparent",border:`1px solid ${queueSort===v?"rgba(201,168,76,0.4)":"var(--navy-border)"}`,borderRadius:4,color:queueSort===v?"var(--gold)":"var(--muted)",fontSize:11,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>{l}</button>
                  ))}
                  {unassigned.length>0&&idleCount>0&&<Btn sz="sm" onClick={autoAssign}>⚡ Auto</Btn>}
                </div>
              </div>

              {pendingMatches.length===0 ? (
                <div style={{ padding:"24px",textAlign:"center",color:"var(--muted)",fontSize:13 }}>No pending matches. Start a tournament with an active bracket.</div>
              ) : (
                <div className="resp-table"><table style={{ width:"100%",borderCollapse:"collapse",minWidth:480 }}>
                  <thead>
                    <tr>{["#","Bracket","Team 1","Team 2","Round","Status",""].map(h=><th key={h} style={css.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {[...pendingMatches].sort((a,b)=>matchPriority(a)-matchPriority(b)).map((m,i)=>{
                      const onTable  = tables.find(t=>t.currentMatch?.matchId===m.id);
                      const isPaused = m.paused;
                      const isNext   = !onTable && i===0;
                      const isSel    = selectedQueueMatch?.id===m.id;
                      const bracketLabel = m._bracket==="GF"?"🏆 GF":m._bracket==="L"?"LB":m._bracket==="W"?"WB":m._bracket==="P"?"PRE":"";
                      return (
                        <tr key={m.id} style={{ opacity:isPaused?0.55:1,background:isSel?"rgba(91,141,238,0.08)":isNext?"rgba(201,168,76,0.04)":"transparent",cursor:"pointer" }}
                          onClick={()=>setSelectedQueueMatch(isSel?null:m)}>
                          <td style={{ ...css.td,width:40 }}>
                            <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--muted)" }}>{i+1}</span>
                            {isNext&&!onTable&&<div style={{ fontSize:8,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,color:"var(--gold)" }}>NEXT</div>}
                          </td>
                          <td style={{ ...css.td,width:60 }}>
                            {bracketLabel&&<Badge color={m._bracket==="L"?"red":m._bracket==="GF"?"gold":""}>{bracketLabel}</Badge>}
                          </td>
                          <td style={{ ...css.td,fontWeight:600 }}>{m.t1?.name||"TBD"}</td>
                          <td style={{ ...css.td,fontWeight:600 }}>{m.t2?.name||"TBD"}</td>
                          <td style={{ ...css.td,fontSize:12,color:"var(--muted)" }}>{m._round||"—"}</td>
                          <td style={css.td}>
                            {onTable ? <Badge color="green">On {onTable.name}</Badge> : isPaused ? <Badge color="gold">Paused</Badge> : isSel?<Badge color="blue">Selected</Badge>:<Badge>Queued</Badge>}
                          </td>
                          <td style={css.td}>
                            <div style={{ display:"flex",gap:6 }}>
                              {!onTable && idleCount>0 && !isSel && (
                                <Btn sz="sm" onClick={e=>{e.stopPropagation();setAssignModal({tableId:tables.find(t=>t.status==="idle")?.id,matchId:m.id,team1:m.t1?.name,team2:m.t2?.name});}}>▶</Btn>
                              )}
                              <Btn sz="sm" v="ghost" onClick={e=>{e.stopPropagation();pauseBracketMatch(m.id);}}>{isPaused?"▶":"⏸"}</Btn>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── ASSIGN MODAL ── */}
      <Modal open={!!assignModal} onClose={()=>setAssignModal(null)} title="Assign Match to Table" width={480}>
        {assignModal && (() => {
          const tableId  = typeof assignModal==="string" ? assignModal : assignModal.tableId;
          const preMatch = typeof assignModal==="object"&&assignModal.matchId ? assignModal : null;
          const table    = tables.find(t=>t.id===tableId);
          return (
            <div>
              <div style={{ marginBottom:16,padding:"10px 14px",background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:4,fontSize:13,color:"var(--gold)" }}>
                Assigning to: <strong>{table?.name}</strong>
              </div>
              {preMatch ? (
                <div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,marginBottom:4 }}>{preMatch.team1} vs {preMatch.team2}</div>
                    {settingsForm.preventBackToBack && table?.lastTeams[0] && (table.lastTeams[0].includes(preMatch.team1)||table.lastTeams[0].includes(preMatch.team2)) && (
                      <div style={{ background:"rgba(217,79,61,0.1)",border:"1px solid rgba(217,79,61,0.3)",borderRadius:4,padding:"8px 12px",fontSize:12,color:"var(--red)" }}>⚠️ Back-to-back warning: one of these teams just played at {table.name}</div>
                    )}
                  </div>
                  <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
                    <Btn v="ghost" onClick={()=>setAssignModal(null)}>Cancel</Btn>
                    <Btn onClick={()=>assignMatch(tableId,preMatch.team1,preMatch.team2,preMatch.matchId)}>▶ Start Match</Btn>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom:14,fontSize:13,color:"var(--muted)" }}>Select a pending match:</div>
                  {unassigned.length===0 ? (
                    <div style={{ textAlign:"center",padding:24,color:"var(--muted)" }}>No unassigned pending matches</div>
                  ) : (
                    <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflow:"auto" }}>
                      {sortedQueue.map(m=>{
                        const bb = settingsForm.preventBackToBack && table?.lastTeams[0] && (table.lastTeams[0].includes(m.t1?.name)||table.lastTeams[0].includes(m.t2?.name));
                        const bracketLabel = m._bracket==="GF"?"GF":m._bracket==="L"?"LB":m._bracket==="W"?"WB":m._bracket==="P"?"Pre":"";
                        return (
                          <div key={m.id} onClick={()=>assignMatch(tableId,m.t1?.name,m.t2?.name,m.id)} style={{ padding:"10px 14px",background:"rgba(255,255,255,0.03)",border:`1px solid ${bb?"rgba(217,79,61,0.4)":"var(--navy-border)"}`,borderRadius:4,cursor:"pointer",transition:"background 0.1s" }}>
                            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                              <div>
                                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14 }}>{m.t1?.name} <span style={{ color:"var(--muted)" }}>vs</span> {m.t2?.name}</div>
                                <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>{m._round}{bb&&<span style={{ color:"var(--red)",marginLeft:8 }}>⚠️ Back-to-back</span>}</div>
                              </div>
                              {bracketLabel&&<Badge color={m._bracket==="L"?"red":m._bracket==="GF"?"gold":""}>{bracketLabel}</Badge>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* ── DONE MODAL ── */}
      <Modal open={!!doneModal} onClose={()=>{ if(doneModal)clearTable(doneModal.tableId,true); setDoneModal(null); }} title="Match Finished" width={420}>
        {doneModal && (
          <div>
            <div style={{ fontSize:14,color:"var(--muted)",lineHeight:1.7,marginBottom:20 }}>
              <strong style={{ color:"var(--white)" }}>{doneModal.team1}</strong> vs <strong style={{ color:"var(--white)" }}>{doneModal.team2}</strong> has ended.
              <br/>Would you like to enter the score in the bracket?
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
              <Btn v="ghost" onClick={()=>{ clearTable(doneModal.tableId,true); setDoneModal(null); }}>Just Clear Table</Btn>
              <Btn onClick={()=>{ clearTable(doneModal.tableId,true); setDoneModal(null); setPage("bracket"); }}>📝 Enter Score →</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Bulk Assign by Round + Assignment History ─────────────────── */}
      {tournament && unassigned.length > 0 && (
        <div style={{...css.card,padding:"12px 16px",marginBottom:12,borderColor:"rgba(201,168,76,0.25)"}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,letterSpacing:2,textTransform:"uppercase",color:"var(--muted)",marginBottom:10}}>⚡ Bulk Assign by Round</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[...new Set(unassigned.map(m=>m._bracket?`${m._bracket}R${m._round}`:"R?"))].map(round=>{
              const roundMatches = unassigned.filter(m=>(m._bracket?`${m._bracket}R${m._round}`:"R?")===round);
              const idleTables = tables.filter(t=>t.status==="idle"||!t.status||t.status==="ready");
              return (
                <button key={round} onClick={()=>{
                  let available = [...idleTables];
                  roundMatches.forEach(m=>{
                    if(available.length===0) return;
                    const tbl = available.shift();
                    setTables(prev=>prev.map(t=>t.id===tbl.id?{...t,status:"active",currentMatch:m,startedAt:Date.now()}:t));
                  });
                }} style={{padding:"6px 14px",borderRadius:4,background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.3)",color:"var(--gold)",cursor:"pointer",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>
                  {round} ({roundMatches.length} matches)
                </button>
              );
            })}
            <button onClick={()=>{
              let idleTables = tables.filter(t=>t.status==="idle"||!t.status||t.status==="ready");
              unassigned.forEach(m=>{
                if(idleTables.length===0) return;
                const tbl = idleTables.shift();
                setTables(prev=>prev.map(t=>t.id===tbl.id?{...t,status:"active",currentMatch:m,startedAt:Date.now()}:t));
              });
            }} style={{padding:"6px 14px",borderRadius:4,background:"rgba(46,204,113,0.1)",border:"1px solid rgba(46,204,113,0.3)",color:"var(--green)",cursor:"pointer",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>
              ⚡ Assign All ({Math.min(unassigned.length, tables.filter(t=>!t.currentMatch).length)} fits)
            </button>
          </div>
        </div>
      )}

      {/* ── Per-table QR Codes ────────────────────────────────────────────── */}
      {tables.length > 0 && (
        <details style={{marginBottom:12}}>
          <summary style={{cursor:"pointer",fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",padding:"8px 0"}}>
            📱 Table QR Codes ({tables.length} tables) ▸
          </summary>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",paddingTop:12}}>
            {tables.map((tbl,i)=>{
              const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`https://mlcp.app/table/${tbl.name||tbl.id}`)}`;
              return (
                <div key={tbl.id} style={{...css.card,padding:"10px 12px",textAlign:"center",minWidth:110}}>
                  <div style={{width:100,height:100,background:"white",borderRadius:4,padding:2,margin:"0 auto 8px"}}>
                    <img src={qrUrl} width="96" height="96" alt={`Table ${tbl.name}`} style={{borderRadius:2}}
                      onError={e=>{e.target.style.display="none";}} />
                  </div>
                  <div style={{fontSize:11,fontWeight:700}}>{tbl.name||`Table ${i+1}`}</div>
                  <div style={{fontSize:9,color:"var(--muted)",marginTop:2}}>{tbl.status||"idle"}</div>
                </div>
              );
            })}
          </div>
        </details>
      )}

      {/* ── ASSIGN HISTORY PANEL ── */}
      {showAssignHistory && assignHistory.length > 0 && (
        <div style={{...css.card,padding:0,overflow:"hidden",marginBottom:12}}>
          <div style={{padding:"8px 14px",borderBottom:"1px solid var(--navy-border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>📋 Assignment History ({assignHistory.length})</span>
            <button onClick={()=>setAssignHistory([])} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:11}}>Clear</button>
          </div>
          <div style={{maxHeight:240,overflowY:"auto"}}>
            {assignHistory.map((h,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderBottom:i<assignHistory.length-1?"1px solid rgba(255,255,255,0.04)":"none",fontSize:12}}>
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color:"var(--gold)",minWidth:60}}>{h.tableName}</span>
                <span style={{flex:1}}>{h.t1} <span style={{color:"var(--muted)"}}>vs</span> {h.t2}</span>
                {h.round&&<span style={{fontSize:10,color:"var(--muted)"}}>{h.bracket==="GF"?"GF":h.bracket==="L"?"L-R"+h.round:"W-R"+h.round}</span>}
                <span style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>{h.ts}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BULK ROUND ASSIGN MODAL ── */}
      <Modal open={bulkRoundModal} onClose={()=>setBulkRoundModal(false)} title="⚡ Bulk Assign by Round" width={420}>
        {(() => {
          const t = (selectedTournament && tournaments.find(x=>x.id===selectedTournament)) || activeTournament;
          if (!t?.bracket) return <div style={{color:"var(--muted)",padding:16}}>No active tournament bracket found</div>;
          const allRounds = [];
          const pushRounds = (rounds, label) => (rounds||[]).forEach((r,ri) =>
            (r.matches||[]).forEach(m => { if(m?.t1&&m?.t2&&m.status==="pending") allRounds.push({...m,_round:ri+1,_bracket:label}); })
          );
          const bkt = t.bracket;
          pushRounds(bkt.rounds,"W"); pushRounds(bkt.winners,"W"); pushRounds(bkt.losers,"L");
          const rounds = [...new Set(allRounds.map(m=>m._round))].sort((a,b)=>a-b);
          const matchesInRound = allRounds.filter(m=>m._round===bulkRound);
          const idleTables = tables.filter(t=>t.status==="idle");
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>Select Round</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {rounds.map(r=>(
                    <button key={r} onClick={()=>setBulkRound(r)}
                      style={{padding:"5px 14px",borderRadius:4,background:bulkRound===r?"rgba(201,168,76,0.15)":"transparent",border:`1px solid ${bulkRound===r?"var(--gold)":"var(--navy-border)"}`,color:bulkRound===r?"var(--gold)":"var(--muted)",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12}}>
                      R{r}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{...css.card,padding:"12px 14px",background:"rgba(255,255,255,0.02)"}}>
                <div style={{fontSize:12,color:"var(--muted)",marginBottom:6}}>{matchesInRound.length} pending matches in Round {bulkRound} · {idleTables.length} idle tables available</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {matchesInRound.slice(0,6).map((m,i)=>(
                    <div key={m.id||i} style={{fontSize:12,padding:"4px 0",borderBottom:i<Math.min(matchesInRound.length,6)-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      {m.t1?.name} vs {m.t2?.name}
                      {i < idleTables.length && <span style={{color:"var(--green)",marginLeft:8}}>→ {idleTables[i]?.name}</span>}
                    </div>
                  ))}
                  {matchesInRound.length > 6 && <div style={{fontSize:11,color:"var(--muted)"}}>+{matchesInRound.length-6} more…</div>}
                </div>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn v="ghost" onClick={()=>setBulkRoundModal(false)}>Cancel</Btn>
                <Btn disabled={!matchesInRound.length||!idleTables.length} onClick={()=>{
                  matchesInRound.slice(0,idleTables.length).forEach((m,i)=>assignMatchToTable(idleTables[i].id,m));
                  setBulkRoundModal(false);
                  showToast(`✓ Assigned ${Math.min(matchesInRound.length,idleTables.length)} matches from Round ${bulkRound}`);
                }}>Assign {Math.min(matchesInRound.length,idleTables.length)} Matches</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ── SETTINGS MODAL ── */}
      <Modal open={showSettings} onClose={()=>setShowSettings(false)} title="Table Settings" width={500}>
        <div className="resp-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16 }}>
          <Fld label="Number of Tables">
            <input style={css.input} type="number" min="1" max="99" value={settingsForm.tableCount} onChange={e=>setSettingsForm(f=>({...f,tableCount:Math.max(1,Math.min(99,parseInt(e.target.value)||1))}))} />
          </Fld>
          <Fld label="Game Timer (0 = unlimited)">
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <input style={{ ...css.input,flex:1 }} type="number" min="0" max="120" value={settingsForm.gameTimerMins} onChange={e=>setSettingsForm(f=>({...f,gameTimerMins:Math.max(0,parseInt(e.target.value)||0)}))} />
              <span style={{ fontSize:12,color:"var(--muted)",flexShrink:0 }}>min</span>
            </div>
          </Fld>
        </div>
        <div style={{ marginBottom:16,padding:"12px 14px",background:"rgba(255,255,255,0.03)",border:"1px solid var(--navy-border)",borderRadius:4,display:"flex",alignItems:"center",gap:12 }}>
          <input type="checkbox" id="btb" checked={settingsForm.preventBackToBack} onChange={e=>setSettingsForm(f=>({...f,preventBackToBack:e.target.checked}))} style={{ width:16,height:16,cursor:"pointer",accentColor:"var(--gold)" }} />
          <label htmlFor="btb" style={{ cursor:"pointer" }}>
            <div style={{ fontWeight:600,fontSize:14 }}>Prevent back-to-back same table</div>
            <div style={{ fontSize:12,color:"var(--muted)",marginTop:2 }}>Warn when same two teams are assigned to a table they just played at</div>
          </label>
        </div>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,letterSpacing:1.5,textTransform:"uppercase",color:"var(--muted)",marginBottom:10 }}>Rename Tables</div>
          <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflow:"auto" }}>
            {tables.map((t,i)=>(
              <div key={t.id} style={{ display:"flex",gap:8,alignItems:"center" }}>
                <span style={{ width:20,textAlign:"center",color:"var(--muted)",fontSize:12 }}>{i+1}</span>
                <input style={{ ...css.input,flex:1,fontSize:13,padding:"6px 10px" }} value={t.name} onChange={e=>setTables(prev=>prev.map(x=>x.id===t.id?{...x,name:e.target.value}:x))} />
                <Badge color={t.status==="active"?"green":t.status==="paused"?"gold":t.status==="blocked"?"red":""}>{t.status}</Badge>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
          <Btn v="ghost" onClick={()=>setShowSettings(false)}>Cancel</Btn>
          <Btn onClick={applySettings}>Apply Settings</Btn>
        </div>
      </Modal>
    </div>
  );
}



// ── NOTIFICATIONS PAGE ───────────────────────────────────────────────────────
const NOTIF_TEMPLATES = {
  registration_confirm: {
    label: "Registration Confirmation",
    trigger: "On registration",
    sms: "Hi {{name}}! You're registered for {{event}}. See you {{date}} at {{venue}}. – MLCP",
    email_subject: "Registration Confirmed – {{event}}",
    email_body: "Hi {{name}},\n\nYou're officially registered for {{event}}!\n\nDate: {{date}}\nVenue: {{venue}}\nFormat: {{format}}\n\nSee you on the tables.\n\nMLCP Staff",
  },
  match_called: {
    label: "Match Called to Table",
    trigger: "When match is assigned to a table",
    sms: "{{team}} – your match vs {{opponent}} is starting NOW at {{table}}. Report immediately. – MLCP",
    email_subject: "Your Match is Starting – {{event}}",
    email_body: "Hi {{name}},\n\nYour match against {{opponent}} has been called.\n\nTable: {{table}}\nEvent: {{event}}\n\nPlease report to your table immediately.\n\nMLCP Staff",
  },
  checkin_reminder: {
    label: "Check-In Reminder",
    trigger: "Sent manually or on schedule",
    sms: "Reminder: Check in for {{event}} is open NOW at {{venue}}. Don't miss your spot! – MLCP",
    email_subject: "Check-In Open – {{event}}",
    email_body: "Hi {{name}},\n\nCheck-in for {{event}} is now open.\n\nVenue: {{venue}}\nDate: {{date}}\n\nBe sure to check in before the tournament begins.\n\nMLCP Staff",
  },
  bracket_update: {
    label: "Bracket Update",
    trigger: "Sent manually after a bracket change",
    sms: "MLCP update: The bracket for {{event}} has been updated. Check your next match. – MLCP",
    email_subject: "Bracket Updated – {{event}}",
    email_body: "Hi {{name}},\n\nThe bracket for {{event}} has been updated. Please check the latest matchups.\n\nMLCP Staff",
  },
  score_disputed: {
    label: "Score Disputed",
    trigger: "When self-reports don't match",
    sms: "⚠ Dispute in {{event}}: {{team}} vs {{opponent}} — a TD will review shortly. – MLCP",
    email_subject: "Score Dispute – {{event}}",
    email_body: "Hi {{name}},\n\nA score dispute has been flagged for your match against {{opponent}} in {{event}}.\n\nA tournament director will review and resolve it shortly.\n\nMLCP Staff",
  },
  score_approved: {
    label: "Score Approved",
    trigger: "When TD approves a pending score",
    sms: "✅ Score approved: {{team}} vs {{opponent}} in {{event}}. – MLCP",
    email_subject: "Score Confirmed – {{event}}",
    email_body: "Hi {{name}},\n\nThe score for your match against {{opponent}} in {{event}} has been confirmed.\n\nMLCP Staff",
  },
  dispute_resolved: {
    label: "Dispute Resolved",
    trigger: "When TD resolves a score dispute",
    sms: "Resolved: The dispute for {{team}} vs {{opponent}} in {{event}} has been settled. – MLCP",
    email_subject: "Dispute Resolved – {{event}}",
    email_body: "Hi {{name}},\n\nThe score dispute for your match against {{opponent}} in {{event}} has been resolved by the tournament director.\n\nMLCP Staff",
  },
};

function fillTemplate(template, vars) {
  return Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{${k}}}`, v || "—"), template);
}

function NotificationsPage({ tournaments, players, setPage, setActiveTournament }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [tab, setTab] = useState("alerts");
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [templateKey, setTemplateKey] = useState("registration_confirm");
  const [channel, setChannel] = useState("sms"); // sms | email | both
  const [selectedTournament, setSelectedTournament] = useState("");
  const [recipients, setRecipients] = useState("all"); // all | checkedin | specific
  const [specificNames, setSpecificNames] = useState("");
  const [customMsg, setCustomMsg] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [vars, setVars] = useState({ name:"[Player Name]", team:"[Team Name]", opponent:"[Opponent]", table:"Table 1", event:"", date:"", venue:"", format:"" });
  const [queue, setQueue] = useState([]);           // sent log
  const [logSearch, setLogSearch] = useState("");
  const [logExpanded, setLogExpanded] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [alertTournFilter, setAlertTournFilter] = useState("all");
  const [quietHours, setQuietHours] = useState(false);
  const [customTemplates, setCustomTemplates] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [preview, setPreview] = useState(null);    // { sms, email_subject, email_body }
  const [autoTriggers, setAutoTriggers] = useState({ registration_confirm: true, match_called: true, checkin_reminder: false, bracket_update: false });

  // ── New state: Broadcast + DM ──────────────────────────────────────────
  const [broadcastMsg,      setBroadcastMsg]      = useState("");
  const [broadcastChannel,  setBroadcastChannel]  = useState("sms");
  const [broadcastAudience, setBroadcastAudience] = useState("all");    // all | active | checkedin
  const [broadcastPriority, setBroadcastPriority] = useState("normal"); // normal | urgent | info
  const [broadcastGenerating, setBroadcastGenerating] = useState(false);
  const [broadcastPreview,  setBroadcastPreview]  = useState(null);
  const [broadcastSent,     setBroadcastSent]     = useState(false);
  const [pushSimLog,        setPushSimLog]         = useState([]);      // simulated push receipts
  const [pushSimRunning,    setPushSimRunning]     = useState(false);

  const [dmSearch,          setDmSearch]           = useState("");
  const [dmRecipient,       setDmRecipient]        = useState(null);    // registration object
  const [dmMsg,             setDmMsg]              = useState("");
  const [dmChannel,         setDmChannel]          = useState("sms");
  const [dmGenerating,      setDmGenerating]       = useState(false);
  const [dmPreview,         setDmPreview]          = useState(null);
  const [dmSent,            setDmSent]             = useState(false);
  const [dmHistory,         setDmHistory]          = useState({});      // playerId → [{msg,ts,ch}]

  const tournament = tournaments.find(t => t.id === selectedTournament);

  // Sync vars when tournament changes
  useEffect(() => {
    if (tournament) {
      setVars(v => ({
        ...v,
        event: tournament.name,
        date: tournament.date || "TBD",
        venue: tournament.venue || "TBD",
        format: FORMATS.find(f => f.v === tournament.format)?.l || tournament.format,
      }));
    }
  }, [selectedTournament]);

  const allTemplates = { ...NOTIF_TEMPLATES, ...customTemplates };
  const tmpl = allTemplates[templateKey] || allTemplates["registration_confirm"];
  const alerts = generateAlerts(tournaments).filter(a => {
    if (alertTournFilter !== "all" && a.tournamentId !== alertTournFilter) return false;
    return !dismissedAlerts.has(a.id);
  });
  const urgentCount = alerts.filter(a=>a.severity==="critical"||a.severity==="high").length;

  // Build recipient list
  function getRecipientList() {
    const regs = tournament?.registrations || [];
    if (recipients === "checkedin") return regs.filter(r => r.status === "checked_in");
    if (recipients === "specific") {
      const names = specificNames.split(",").map(s => s.trim().toLowerCase());
      return regs.filter(r => names.some(n => r.teamName.toLowerCase().includes(n) || r.players?.some(p => p.name.toLowerCase().includes(n))));
    }
    return regs;
  }

  const recipientList = getRecipientList();

  // Generate AI preview using Claude
  async function generateAIPreview() {
    setGenerating(true);
    setPreview(null);
    const context = `You are composing tournament notifications for Major League Cup Pong (MLCP).

Tournament: ${vars.event}
Date: ${vars.date}
Venue: ${vars.venue}
Format: ${vars.format}
Notification type: ${tmpl.label}
Channel: ${channel}
${useCustom ? `Custom message request: "${customMsg}"` : `Base template: SMS: "${tmpl.sms}" | Email: "${tmpl.email_body}"`}

Generate a polished, professional notification. Keep SMS under 160 characters. Email should be warm but concise.

Respond ONLY with a JSON object (no markdown) with these exact keys:
{
  "sms": "...",
  "email_subject": "...",
  "email_body": "..."
}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: context }],
        }),
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setPreview(parsed);
    } catch (e) {
      setPreview({ sms: fillTemplate(tmpl.sms, vars), email_subject: fillTemplate(tmpl.email_subject, vars), email_body: fillTemplate(tmpl.email_body, vars) });
    }
    setGenerating(false);
  }

  // "Send" — adds to queue log (real send would call Twilio/SendGrid)
  function sendNotifications() {
    if (!preview && !useCustom) return;
    const msg = preview || { sms: fillTemplate(tmpl.sms, vars), email_subject: fillTemplate(tmpl.email_subject, vars), email_body: fillTemplate(tmpl.email_body, vars) };
    const entry = {
      id: uid(),
      sentAt: new Date().toLocaleString(),
      scheduledAt: scheduleEnabled && scheduledAt ? scheduledAt : null,
      type: tmpl.label,
      channel,
      recipients: recipientList.length,
      recipientNames: recipientList.slice(0,5).map(r=>r.teamName||r.players?.[0]?.name||"Team"),
      event: vars.event || "—",
      sms: msg.sms,
      email_subject: msg.email_subject,
      email_body: msg.email_body,
      status: scheduleEnabled && scheduledAt ? "scheduled" : "delivered",
    };
    setQueue(q => [entry, ...q]);
    setSendSuccess(true);
    setTimeout(() => { setSendSuccess(false); setTab("log"); }, 1400);
  }

  const smsLen = (preview?.sms || fillTemplate(tmpl.sms, vars)).length;

  // ── Broadcast push simulation ────────────────────────────────────────────
  async function runPushSim(recipients, msg, channel, priority) {
    setPushSimRunning(true);
    setPushSimLog([]);
    const results = [];
    for (let i = 0; i < recipients.length; i++) {
      await new Promise(r => setTimeout(r, 120 + Math.random()*80));
      const delivered = Math.random() > 0.04; // 96% delivery rate
      const r = recipients[i];
      results.push({
        id: r.id,
        name: r.teamName || r.players?.[0]?.name || "Team",
        status: delivered ? "delivered" : "failed",
        ts: new Date().toLocaleTimeString(),
        ch: channel,
      });
      setPushSimLog([...results]);
    }
    setPushSimRunning(false);
    return results;
  }

  async function generateBroadcastAI() {
    setBroadcastGenerating(true);
    setBroadcastPreview(null);
    const audienceDesc = broadcastAudience === "all" ? "all registered teams" : broadcastAudience === "active" ? "teams currently in active tournaments" : "checked-in teams only";
    const context = `You are composing an admin broadcast for Major League Cup Pong (MLCP).
Priority: ${broadcastPriority}
Audience: ${audienceDesc}
Channel: ${broadcastChannel}
Staff message: "${broadcastMsg}"

Generate a polished broadcast. Keep SMS under 160 chars. Return ONLY a JSON object:
{"sms":"...","email_subject":"...","email_body":"..."}`;
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:600, messages:[{role:"user",content:context}] }),
      });
      const data = await res.json();
      const text = data.content?.map(b=>b.text||"").join("")||"";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setBroadcastPreview(parsed);
    } catch(e) {
      setBroadcastPreview({ sms: broadcastMsg, email_subject: "MLCP Announcement", email_body: broadcastMsg });
    }
    setBroadcastGenerating(false);
  }

  async function sendBroadcast() {
    const allRegs = tournaments.flatMap(t => t.registrations || []);
    const audience = broadcastAudience === "checkedin" ? allRegs.filter(r=>r.status==="checked_in")
      : broadcastAudience === "active" ? tournaments.filter(t=>t.status==="active").flatMap(t=>t.registrations||[])
      : allRegs;
    const msg = broadcastPreview || { sms: broadcastMsg, email_subject:"MLCP Announcement", email_body:broadcastMsg };
    const entry = {
      id: uid(), sentAt: new Date().toLocaleString(), type:"Broadcast",
      channel: broadcastChannel, recipients: audience.length, priority: broadcastPriority,
      recipientNames: audience.slice(0,5).map(r=>r.teamName||"Team"),
      event:"All", sms: msg.sms, email_subject: msg.email_subject, email_body: msg.email_body,
      status:"delivered",
    };
    setQueue(q => [entry, ...q]);
    await runPushSim(audience.slice(0,12), msg.sms, broadcastChannel, broadcastPriority);
    setBroadcastSent(true);
    setTimeout(() => setBroadcastSent(false), 3000);
  }

  // ── DM helpers ───────────────────────────────────────────────────────────
  async function generateDMAI() {
    if (!dmRecipient || !dmMsg) return;
    setDmGenerating(true);
    setDmPreview(null);
    const context = `You are sending a direct message from MLCP admin to a specific team/player.
Recipient: ${dmRecipient.teamName || dmRecipient.players?.[0]?.name}
Channel: ${dmChannel}
Staff message: "${dmMsg}"

Generate a polished, personal direct message. Keep SMS under 160 chars. Return ONLY JSON:
{"sms":"...","email_subject":"...","email_body":"..."}`;
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:500, messages:[{role:"user",content:context}] }),
      });
      const data = await res.json();
      const text = data.content?.map(b=>b.text||"").join("")||"";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setDmPreview(parsed);
    } catch(e) {
      setDmPreview({ sms: dmMsg, email_subject:"Message from MLCP", email_body: dmMsg });
    }
    setDmGenerating(false);
  }

  function sendDM() {
    if (!dmRecipient) return;
    const msg = dmPreview || { sms: dmMsg, email_subject:"Message from MLCP", email_body: dmMsg };
    const entry = {
      id: uid(), sentAt: new Date().toLocaleString(), type:"Direct Message",
      channel: dmChannel, recipients: 1, priority:"normal",
      recipientNames: [dmRecipient.teamName||"Team"],
      event:"—", sms: msg.sms, email_subject: msg.email_subject, email_body: msg.email_body,
      status:"delivered",
    };
    setQueue(q => [entry, ...q]);
    const rid = dmRecipient.id;
    setDmHistory(h => ({ ...h, [rid]: [{ msg: msg.sms, ts: new Date().toLocaleTimeString(), ch: dmChannel }, ...(h[rid]||[])] }));
    setDmSent(true);
    setTimeout(() => { setDmSent(false); setDmMsg(""); setDmPreview(null); }, 2000);
  }


  return (
    <div className="fade-in">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 3 }}>Notifications</div>
        <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 3 }}>
          AI-composed SMS &amp; email notifications for players and teams
        </div>
      </div>

      {/* Integration notice */}
      <div style={{ background: "rgba(91,141,238,0.08)", border: "1px solid rgba(91,141,238,0.25)", borderRadius: 6, padding: "10px 16px", marginBottom: 20, fontSize: 12, color: "#5b8dee", display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
        <div>
          <strong>Integration ready.</strong> Message content is AI-composed and previewed here. Live delivery requires connecting a provider:
          SMS via <strong>Twilio</strong> · Email via <strong>SendGrid</strong> or <strong>Postmark</strong>.
          Connect in Admin → Settings when ready. Until then, all sends are <strong>logged &amp; queued</strong>.
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid var(--navy-border)", overflowX:"auto" }}>
        {[
          ["alerts", urgentCount > 0 ? `🚨 Live Alerts (${urgentCount})` : "🔔 Live Alerts"],
          ["compose", "✍️ Compose"],
          ["templates", `📝 Templates (${Object.keys(allTemplates).length})`],
          ["auto", "⚡ Auto Triggers"],
          ["log", `📬 Sent Log (${queue.length})`],
          ["broadcast", "📣 Broadcast"],
          ["dm", "💬 Direct Message"],
        ].map(([v, l]) => (
          <div key={v} onClick={() => setTab(v)} style={{ padding: "8px 16px", cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", borderBottom: tab === v ? "2px solid var(--gold)" : "2px solid transparent", color: tab === v ? "var(--white)" : "var(--muted)", marginBottom: -1, transition: "all 0.15s", whiteSpace:"nowrap" }}>{l}</div>
        ))}
      </div>

      {/* ── COMPOSE TAB ─────────────────────────────────────────────────────── */}
      {/* ── LIVE ALERTS TAB ── */}
      {tab === "alerts" && (() => {
        const sevCfg = {
          critical: { color:"var(--red)",    bg:"rgba(217,79,61,0.08)",   border:"rgba(217,79,61,0.3)",   label:"Critical",        pulse:true },
          high:     { color:"var(--gold)",   bg:"rgba(201,168,76,0.08)", border:"rgba(201,168,76,0.3)", label:"Action Required", pulse:false },
          warn:     { color:"#5b8dee",       bg:"rgba(91,141,238,0.08)", border:"rgba(91,141,238,0.3)", label:"Warning",         pulse:false },
          info:     { color:"var(--muted)",  bg:"rgba(255,255,255,0.03)", border:"rgba(255,255,255,0.1)", label:"Info",           pulse:false },
        };
        const rawAlerts = generateAlerts(tournaments).filter(a => !dismissedAlerts.has(a.id));
        const urgentTotal = rawAlerts.filter(a=>a.severity==="critical"||a.severity==="high").length;
        return (
          <div>
            {/* Tournament filter + dismiss all */}
            <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
              <select style={{ ...css.select, width:"auto", fontSize:12 }} value={alertTournFilter} onChange={e=>setAlertTournFilter(e.target.value)}>
                <option value="all">All Tournaments</option>
                {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["critical","high","warn","info"].map(sev=>{
                  const cnt = alerts.filter(a=>a.severity===sev).length;
                  if (!cnt) return null;
                  const sc = sevCfg[sev];
                  return <div key={sev} style={{ padding:"3px 10px", background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:20, fontSize:11, color:sc.color, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{sc.label}: {cnt}</div>;
                })}
              </div>
              {rawAlerts.length > 0 && (
                <button onClick={()=>setDismissedAlerts(new Set(rawAlerts.map(a=>a.id)))} style={{ marginLeft:"auto", padding:"4px 12px", background:"none", border:"1px solid var(--navy-border)", borderRadius:4, color:"var(--muted)", fontSize:11, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>Dismiss All</button>
              )}
            </div>

            {alerts.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, color:"var(--white)", marginBottom:8 }}>All Clear</div>
                <div style={{ fontSize:13 }}>{alertTournFilter!=="all"?"No alerts for this tournament.":"No active alerts. Everything looks good."}</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {alerts.map(a=>{
                  const sc = sevCfg[a.severity]||sevCfg.info;
                  return (
                    <div key={a.id} style={{ padding:"14px 16px", background:sc.bg, border:`1px solid ${sc.border}`, borderLeft:`3px solid ${sc.color}`, borderRadius:6, display:"flex", alignItems:"flex-start", gap:12 }}>
                      {sc.pulse && <div style={{ width:8, height:8, borderRadius:"50%", background:sc.color, flexShrink:0, marginTop:5, animation:"pulse 1s infinite" }} />}
                      <div style={{ fontSize:20, flexShrink:0 }}>{a.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:sc.color }}>{a.title}</span>
                          <span style={{ fontSize:10, padding:"1px 6px", background:`${sc.color}22`, borderRadius:3, color:sc.color, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>{sc.label}</span>
                          {a.tournamentId && <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{tournaments.find(t=>t.id===a.tournamentId)?.name||""}</span>}
                        </div>
                        <div style={{ fontSize:12, color:"var(--muted)", lineHeight:1.5 }}>{a.body}</div>
                      </div>
                      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                        {a.notifTemplate && (
                          <Btn sz="sm" v="ghost" onClick={()=>{ setTemplateKey(a.notifTemplate); if(a.tournamentId) setSelectedTournament(a.tournamentId); setTab("compose"); }}>📨 Notify</Btn>
                        )}
                        <Btn sz="sm" v="ghost" onClick={()=>setDismissedAlerts(prev=>new Set([...prev,a.id]))}>✕</Btn>
                      </div>
                    </div>
                  );
                })}
                {/* Bulk notify for critical+high */}
                {urgentTotal > 0 && (
                  <div style={{ padding:"12px 16px", background:"rgba(217,79,61,0.05)", border:"1px solid rgba(217,79,61,0.2)", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:13, color:"var(--muted)" }}>{urgentTotal} urgent alert{urgentTotal!==1?"s":""} require attention</span>
                    <Btn sz="sm" v="danger" onClick={()=>setTab("compose")}>📨 Compose Alert for Players</Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "compose" && (
        <div>
          {/* Success flash */}
          {sendSuccess && (
            <div style={{ padding:"12px 18px", background:"rgba(46,204,113,0.12)", border:"1px solid rgba(46,204,113,0.4)", borderRadius:6, marginBottom:16, display:"flex", alignItems:"center", gap:10, animation:"fadeIn 0.2s" }}>
              <span style={{ fontSize:18 }}>✅</span>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:"var(--green)" }}>
                {scheduleEnabled && scheduledAt ? `Scheduled for ${scheduledAt}` : `Sent to ${recipientList.length} recipient${recipientList.length!==1?"s":""}!`}
              </span>
            </div>
          )}
          <div className="resp-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Left: config */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ ...css.card, padding: 18 }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--gold)", marginBottom: 14 }}>Message Setup</div>

              <Fld label="Tournament">
                <select style={css.select} value={selectedTournament} onChange={e => setSelectedTournament(e.target.value)}>
                  <option value="">— No tournament (generic) —</option>
                  {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Fld>

              <Fld label="Notification Type">
                <select style={css.select} value={templateKey} onChange={e => { setTemplateKey(e.target.value); setPreview(null); }}>
                  {Object.entries(allTemplates).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Fld>

              <Fld label="Channel">
                <div style={{ display: "flex", gap: 8 }}>
                  {[["sms", "📱 SMS"], ["email", "📧 Email"], ["both", "📱📧 Both"]].map(([v, l]) => (
                    <div key={v} onClick={() => setChannel(v)} style={{ flex: 1, padding: "8px 6px", textAlign: "center", borderRadius: 4, border: `1px solid ${channel === v ? "var(--gold)" : "var(--navy-border)"}`, background: channel === v ? "rgba(201,168,76,0.1)" : "transparent", cursor: "pointer", fontSize: 12, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1, color: channel === v ? "var(--gold-light)" : "var(--muted)", transition: "all 0.15s" }}>{l}</div>
                  ))}
                </div>
              </Fld>

              <Fld label="Recipients">
                <select style={css.select} value={recipients} onChange={e => setRecipients(e.target.value)}>
                  <option value="all">All Registered ({tournament?.registrations?.length || 0})</option>
                  <option value="checkedin">Checked-In Only ({tournament?.registrations?.filter(r => r.status === "checked_in").length || 0})</option>
                  <option value="specific">Specific Team/Player</option>
                </select>
              </Fld>

              {recipients === "specific" && (
                <Fld label="Names (comma-separated)">
                  <input style={css.input} value={specificNames} onChange={e => setSpecificNames(e.target.value)} placeholder="e.g. Marcus Webb, The Eliminators" />
                </Fld>
              )}

              {/* Recipient preview chips */}
              {recipientList.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:10 }}>
                  {recipientList.slice(0,6).map((r,i)=>(
                    <div key={i} style={{ padding:"2px 8px", background:"rgba(255,255,255,0.05)", border:"1px solid var(--navy-border)", borderRadius:12, fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                      {r.teamName||r.players?.[0]?.name||"Team"}
                    </div>
                  ))}
                  {recipientList.length > 6 && <div style={{ padding:"2px 8px", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>+{recipientList.length-6} more</div>}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--navy-border)", borderRadius: 4, marginBottom: 10 }}>
                <input type="checkbox" id="custom-chk" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--gold)" }} />
                <label htmlFor="custom-chk" style={{ cursor: "pointer", fontSize: 13 }}>Custom message request (AI will write it)</label>
              </div>

              {useCustom && (
                <Fld label="Describe what to communicate">
                  <textarea style={{ ...css.input, height: 72, resize: "vertical" }} value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder="e.g. Tell players the bracket has been delayed 30 minutes due to table setup, apologize for the inconvenience, and say games will start at 3pm" />
                </Fld>
              )}

              {/* Schedule option */}
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:4, marginBottom:10 }}>
                <input type="checkbox" id="sched-chk" checked={scheduleEnabled} onChange={e=>setScheduleEnabled(e.target.checked)} style={{ width:16, height:16, cursor:"pointer", accentColor:"var(--gold)" }} />
                <label htmlFor="sched-chk" style={{ cursor:"pointer", fontSize:13 }}>Schedule for later</label>
              </div>
              {scheduleEnabled && (
                <Fld label="Send At">
                  <input style={css.input} type="datetime-local" value={scheduledAt} onChange={e=>setScheduledAt(e.target.value)} />
                </Fld>
              )}
              {quietHours && scheduleEnabled && scheduledAt && (()=>{
                const h = new Date(scheduledAt).getHours();
                return (h >= 23 || h < 8) ? (
                  <div style={{ padding:"6px 12px", background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:4, fontSize:11, color:"var(--gold)", marginBottom:8 }}>⚠️ Quiet Hours active — send will be held until 8:00 AM</div>
                ) : null;
              })()}

              <Btn onClick={generateAIPreview} disabled={generating} style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>
                {generating ? "⏳ Generating…" : "✨ Generate with AI"}
              </Btn>
            </div>

            {/* Template vars */}
            <div style={{ ...css.card, padding: 18 }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--muted)", marginBottom: 12 }}>Template Variables</div>
              <div className="resp-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[["name", "Player Name"], ["team", "Team Name"], ["opponent", "Opponent"], ["table", "Table"]].map(([k, l]) => (
                  <Fld key={k} label={l}>
                    <input style={{ ...css.input, fontSize: 12, padding: "6px 10px" }} value={vars[k]} onChange={e => setVars(v => ({ ...v, [k]: e.target.value }))} placeholder={`{{${k}}}`} />
                  </Fld>
                ))}
              </div>
            </div>
          </div>

          {/* Right: preview */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ ...css.card, padding: 18, flex: 1 }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--gold)", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Message Preview
                {preview && <span style={{ fontSize: 10, color: "var(--green)", fontWeight: 700, letterSpacing: 1 }}>✓ AI GENERATED</span>}
              </div>

              {generating && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)" }}>
                  <div style={{ fontSize: 28, marginBottom: 10, animation: "pulse 1s infinite" }}>✨</div>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1.5 }}>Composing with AI…</div>
                </div>
              )}

              {!generating && (channel === "sms" || channel === "both") && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 11, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--muted)" }}>📱 SMS</div>
                    <div style={{ fontSize: 11, color: smsLen > 160 ? "var(--red)" : smsLen > 130 ? "var(--gold)" : "var(--muted)" }}>{smsLen}/160</div>
                  </div>
                  {/* SMS char bar */}
                  <div style={{ height:3, background:"var(--navy-border)", borderRadius:2, overflow:"hidden", marginBottom:8 }}>
                    <div style={{ height:"100%", width:`${Math.min(100,Math.round(smsLen/160*100))}%`, background:smsLen>160?"var(--red)":smsLen>130?"var(--gold)":"var(--green)", borderRadius:2, transition:"width 0.2s" }} />
                  </div>
                  {/* Phone mockup */}
                  <div style={{ margin:"0 auto 12px", width:220, background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:16, overflow:"hidden", boxShadow:"0 4px 20px rgba(0,0,0,0.3)" }}>
                    <div style={{ padding:"8px 12px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--green)" }} />
                      <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>MLCP · SMS</div>
                    </div>
                    <div style={{ padding:"12px 14px" }}>
                      <div style={{ background:"rgba(91,141,238,0.15)", borderRadius:"12px 12px 12px 2px", padding:"8px 12px", display:"inline-block", maxWidth:"90%", fontSize:12, lineHeight:1.5, color:"var(--off-white)", wordBreak:"break-word" }}>
                        {preview?.sms || fillTemplate(tmpl.sms, vars)}
                      </div>
                    </div>
                  </div>
                  <textarea style={{ ...css.input, background:"transparent", border:"1px solid var(--navy-border)", resize:"vertical", height:56, fontSize:12, lineHeight:1.6 }}
                    value={preview?.sms || fillTemplate(tmpl.sms, vars)}
                    onChange={e => setPreview(p => ({ ...(p || {}), sms: e.target.value }))}
                  />
                  {smsLen > 160 && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>⚠️ Exceeds 160 chars — will split into 2 messages</div>}
                </div>
              )}

              {!generating && (channel === "email" || channel === "both") && (
                <div>
                  <div style={{ fontSize: 11, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>📧 Email</div>
                  <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--navy-border)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--navy-border)", fontSize: 12 }}>
                      <span style={{ color: "var(--muted)" }}>Subject: </span>
                      <input style={{ ...css.input, background: "transparent", border: "none", padding: 0, display: "inline", width: "calc(100% - 60px)", fontSize: 13, fontWeight: 600 }}
                        value={preview?.email_subject || fillTemplate(tmpl.email_subject, vars)}
                        onChange={e => setPreview(p => ({ ...(p || {}), email_subject: e.target.value }))}
                      />
                    </div>
                    <div style={{ padding: 12 }}>
                      <textarea style={{ ...css.input, background: "transparent", border: "none", padding: 0, resize: "vertical", height: 140, fontSize: 13, lineHeight: 1.7 }}
                        value={preview?.email_body || fillTemplate(tmpl.email_body, vars)}
                        onChange={e => setPreview(p => ({ ...(p || {}), email_body: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!generating && (
                <div style={{ marginTop: 16, padding: "10px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--navy-border)", borderRadius: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap:"wrap", gap:8 }}>
                  <div style={{ fontSize:13 }}>
                    <span style={{ color: "var(--muted)" }}>To: </span>
                    <strong>{recipientList.length} recipient{recipientList.length !== 1 ? "s" : ""}</strong>
                    {tournament && <span style={{ color: "var(--muted)", marginLeft: 6, fontSize: 12 }}>({tournament.name})</span>}
                    {scheduleEnabled && scheduledAt && <div style={{ fontSize:11, color:"var(--gold)", marginTop:2 }}>🕐 Scheduled: {new Date(scheduledAt).toLocaleString()}</div>}
                  </div>
                  <Btn onClick={sendNotifications} v="success" disabled={recipientList.length === 0}>
                    {scheduleEnabled && scheduledAt ? "🕐 Schedule" : `📨 Send ${channel === "both" ? "SMS + Email" : channel === "sms" ? "SMS" : "Email"}`}
                  </Btn>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      )}

      {/* ── TEMPLATES TAB ───────────────────────────────────────────────────── */}
      {tab === "templates" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:13, color:"var(--muted)" }}>Edit existing templates or create custom ones. Variables like <code style={{ background:"rgba(255,255,255,0.08)", borderRadius:3, padding:"1px 5px", fontSize:11 }}>{"{{name}}"}</code> are auto-replaced on send.</div>
            <Btn sz="sm" onClick={()=>{
              const newKey = "custom_"+uid().slice(0,6);
              setCustomTemplates(t=>({...t,[newKey]:{ label:"New Template", trigger:"Manual", sms:"Hi {{name}}! – MLCP", email_subject:"Message from MLCP", email_body:"Hi {{name}},\n\nMLCP Staff" }}));
              setEditingTemplate(newKey);
              setTemplateDraft({ label:"New Template", trigger:"Manual", sms:"Hi {{name}}! – MLCP", email_subject:"Message from MLCP", email_body:"Hi {{name}},\n\nMLCP Staff" });
            }}>+ New Template</Btn>
          </div>

          {/* Variable reference */}
          <div style={{ padding:"8px 14px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--navy-border)", borderRadius:4, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
            <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, flexShrink:0 }}>VARIABLES:</span>
            {["{{name}}","{{team}}","{{opponent}}","{{event}}","{{date}}","{{venue}}","{{table}}","{{format}}"].map(v=>(
              <span key={v} style={{ fontSize:11, background:"rgba(91,141,238,0.12)", border:"1px solid rgba(91,141,238,0.2)", borderRadius:3, padding:"1px 6px", color:"#5b8dee", fontFamily:"monospace" }}>{v}</span>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:14 }}>
            {Object.entries(allTemplates).map(([key, t]) => {
              const isCustom = key in customTemplates;
              const isEditing = editingTemplate === key;
              const draft = isEditing ? templateDraft : t;
              return (
                <div key={key} style={{ ...css.card, padding:0, overflow:"hidden", borderLeft:`3px solid ${isCustom?"var(--gold)":"rgba(255,255,255,0.1)"}` }}>
                  <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                    {isEditing ? (
                      <input style={{ ...css.input, fontSize:13, fontWeight:700, flex:1 }} value={draft.label} onChange={e=>setTemplateDraft(d=>({...d,label:e.target.value}))} />
                    ) : (
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, flex:1 }}>{t.label}</div>
                    )}
                    <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                      {isCustom && !isEditing && <Badge color="">Custom</Badge>}
                      {!isEditing && <Btn sz="sm" v="ghost" onClick={()=>{ setEditingTemplate(key); setTemplateDraft({...t}); }}>✏️</Btn>}
                      {isEditing && <>
                        <Btn sz="sm" onClick={()=>{
                          if (isCustom) setCustomTemplates(ct=>({...ct,[key]:templateDraft}));
                          setEditingTemplate(null); setTemplateDraft(null);
                        }}>Save</Btn>
                        <Btn sz="sm" v="ghost" onClick={()=>{ setEditingTemplate(null); setTemplateDraft(null); }}>Cancel</Btn>
                        {isCustom && <Btn sz="sm" v="danger" onClick={()=>{ setCustomTemplates(ct=>{ const n={...ct}; delete n[key]; return n; }); setEditingTemplate(null); }}>Delete</Btn>}
                      </>}
                    </div>
                  </div>
                  <div style={{ padding:"10px 14px" }}>
                    {isEditing ? (
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        <Fld label="Trigger"><input style={{ ...css.input, fontSize:12 }} value={draft.trigger} onChange={e=>setTemplateDraft(d=>({...d,trigger:e.target.value}))} /></Fld>
                        <Fld label="SMS Template"><textarea style={{ ...css.input, fontSize:12, resize:"vertical", height:60 }} value={draft.sms} onChange={e=>setTemplateDraft(d=>({...d,sms:e.target.value}))} /></Fld>
                        <Fld label="Email Subject"><input style={{ ...css.input, fontSize:12 }} value={draft.email_subject} onChange={e=>setTemplateDraft(d=>({...d,email_subject:e.target.value}))} /></Fld>
                        <Fld label="Email Body"><textarea style={{ ...css.input, fontSize:12, resize:"vertical", height:100 }} value={draft.email_body} onChange={e=>setTemplateDraft(d=>({...d,email_body:e.target.value}))} /></Fld>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, marginBottom:4 }}>TRIGGER: {t.trigger}</div>
                        <div style={{ fontSize:12, color:"var(--off-white)", fontStyle:"italic", lineHeight:1.5, marginBottom:8 }}>{t.sms}</div>
                        <Btn sz="sm" v="ghost" onClick={()=>{ setTemplateKey(key); setTab("compose"); }}>Use in Compose →</Btn>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── AUTO TRIGGERS TAB ───────────────────────────────────────────────── */}
      {tab === "auto" && (
        <div className="resp-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {Object.entries(NOTIF_TEMPLATES).map(([key, tmpl]) => (
            <div key={key} style={{ ...css.card, padding: 18, borderLeft: `3px solid ${autoTriggers[key] ? "var(--green)" : "var(--navy-border)"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: 1 }}>{tmpl.label}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Trigger: {tmpl.trigger}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: autoTriggers[key] ? "var(--green)" : "var(--muted)", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 1 }}>{autoTriggers[key] ? "ON" : "OFF"}</span>
                  <div onClick={() => setAutoTriggers(a => ({ ...a, [key]: !a[key] }))} style={{ width: 40, height: 22, borderRadius: 11, background: autoTriggers[key] ? "var(--green)" : "var(--navy-border)", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--white)", position: "absolute", top: 2, left: autoTriggers[key] ? 20 : 2, transition: "left 0.2s" }} />
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, color: "var(--white)", marginBottom: 3 }}>SMS preview:</div>
                <div style={{ fontStyle: "italic", lineHeight: 1.6 }}>{tmpl.sms}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn sz="sm" v="ghost" onClick={() => { setTemplateKey(key); setTab("compose"); }}>✏️ Edit Template</Btn>
              </div>
            </div>
          ))}
          <div style={{ gridColumn: "1/-1", ...css.card, padding: 16, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>How Auto Triggers Work</div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
              When enabled, these fire automatically at the relevant moment — registration save, table assignment, or check-in open.
              Requires a connected SMS/email provider. Toggle templates on/off without losing your settings.
            </div>
          </div>
          {/* Quiet Hours setting */}
          <div style={{ gridColumn:"1/-1", ...css.card, padding:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1, marginBottom:2 }}>🌙 Quiet Hours</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Hold all notifications between 11:00 PM – 8:00 AM. Sends will queue and deliver at 8 AM.</div>
              </div>
              <div onClick={()=>setQuietHours(q=>!q)} style={{ width:44, height:24, borderRadius:12, background:quietHours?"var(--green)":"var(--navy-border)", cursor:"pointer", transition:"background 0.2s", position:"relative", flexShrink:0, marginLeft:16 }}>
                <div style={{ position:"absolute", top:2, left:quietHours?20:2, width:20, height:20, borderRadius:"50%", background:"var(--white)", transition:"left 0.2s" }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "log" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {/* Stats strip */}
          {queue.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {[
                [queue.length, "Total Sent", "var(--white)"],
                [queue.filter(e=>e.channel==="sms"||e.channel==="both").length, "SMS", "var(--gold)"],
                [queue.filter(e=>e.channel==="email"||e.channel==="both").length, "Email", "#5b8dee"],
                [queue.reduce((s,e)=>s+(e.recipients||0),0), "Recipients", "var(--green)"],
              ].map(([v,l,c])=>(
                <div key={l} style={{ ...css.card, padding:"10px 8px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                  <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>
          )}
          {queue.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 3, color: "var(--white)", marginBottom: 8 }}>No Messages Sent Yet</div>
              <div style={{ fontSize: 13, marginBottom: 16 }}>Compose and send a notification to see it here</div>
              <Btn onClick={() => setTab("compose")}>Compose Message →</Btn>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {/* Search + stats */}
              <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                <input style={{ ...css.input, maxWidth:260 }} placeholder="🔍 Search by type, event, recipients…" value={logSearch} onChange={e=>setLogSearch(e.target.value)} />
                <div style={{ display:"flex", gap:10, marginLeft:"auto" }}>
                  {[["SMS",queue.filter(e=>e.channel==="sms"||e.channel==="both").length,"#5b8dee"],
                    ["Email",queue.filter(e=>e.channel==="email"||e.channel==="both").length,"var(--gold)"],
                    ["Scheduled",queue.filter(e=>e.status==="scheduled").length,"var(--green)"],
                  ].filter(([,n])=>n>0).map(([l,n,c])=>(
                    <div key={l} style={{ fontSize:11, color:c, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{l}: {n}</div>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {queue.filter(e => !logSearch || (e.type+e.event+e.channel+(e.sms||"")+(e.email_subject||"")).toLowerCase().includes(logSearch.toLowerCase())).map(entry => {
                  const isExpanded = logExpanded.has(entry.id);
                  const statusColor = entry.status==="scheduled"?"var(--gold)":entry.status==="delivered"?"var(--green)":"var(--muted)";
                  return (
                    <div key={entry.id} style={{ ...css.card, padding:0, overflow:"hidden" }}>
                      <div onClick={()=>setLogExpanded(prev=>{ const s=new Set(prev); s.has(entry.id)?s.delete(entry.id):s.add(entry.id); return s; })} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", cursor:"pointer" }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:statusColor, flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13 }}>{entry.type}</div>
                          <div style={{ fontSize:11, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{entry.sms||entry.email_subject}</div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ display:"flex", gap:6, alignItems:"center", justifyContent:"flex-end", marginBottom:3 }}>
                            <Badge color={entry.channel==="sms"?"blue":entry.channel==="email"?"":"gold"}>{entry.channel}</Badge>
                            <Badge color={entry.status==="scheduled"?"gold":"green"}>{entry.status}</Badge>
                          </div>
                          <div style={{ fontSize:10, color:"var(--muted)" }}>{entry.scheduledAt||entry.sentAt} · {entry.recipients} recipients</div>
                        </div>
                        <div style={{ fontSize:10, color:"var(--muted)", flexShrink:0 }}>{isExpanded?"▲":"▼"}</div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding:"0 16px 14px", borderTop:"1px solid var(--navy-border)" }}>
                          {entry.sms && (
                            <div style={{ marginTop:10 }}>
                              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, marginBottom:4 }}>SMS</div>
                              <div style={{ fontSize:12, background:"rgba(91,141,238,0.08)", border:"1px solid rgba(91,141,238,0.2)", borderRadius:4, padding:"8px 12px", lineHeight:1.6 }}>{entry.sms}</div>
                            </div>
                          )}
                          {entry.email_body && (
                            <div style={{ marginTop:10 }}>
                              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, marginBottom:4 }}>EMAIL: {entry.email_subject}</div>
                              <div style={{ fontSize:12, background:"rgba(255,255,255,0.03)", border:"1px solid var(--navy-border)", borderRadius:4, padding:"8px 12px", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{entry.email_body}</div>
                            </div>
                          )}
                          {entry.recipientNames?.length>0 && (
                            <div style={{ marginTop:8, display:"flex", gap:5, flexWrap:"wrap" }}>
                              {entry.recipientNames.map((n,i)=><div key={i} style={{ padding:"2px 8px", background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:12, fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{n}</div>)}
                              {entry.recipients>entry.recipientNames.length && <div style={{ fontSize:10, color:"var(--muted)", padding:"2px 4px" }}>+{entry.recipients-entry.recipientNames.length} more</div>}
                            </div>
                          )}
                          <div style={{ marginTop:10 }}>
                            <Btn sz="sm" v="ghost" onClick={()=>{ setTemplateKey(Object.keys(allTemplates).find(k=>allTemplates[k].label===entry.type)||"registration_confirm"); setTab("compose"); }}>↩ Re-Send</Btn>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BROADCAST TAB ────────────────────────────────────────────────────── */}
      {tab === "broadcast" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Priority selector */}
          <div>
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:8 }}>Priority</div>
            <div style={{ display:"flex", gap:8 }}>
              {[["normal","💬 Normal","var(--muted)"],["info","ℹ Info","#5b8dee"],["urgent","🚨 Urgent","var(--red)"]].map(([v,l,c])=>(
                <button key={v} onClick={()=>setBroadcastPriority(v)} style={{ flex:1, padding:"8px 12px", borderRadius:5, border:`1.5px solid ${broadcastPriority===v?c:"var(--navy-border)"}`, background:broadcastPriority===v?`${c}18`:"transparent", color:broadcastPriority===v?c:"var(--muted)", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", transition:"all 0.15s" }}>{l}</button>
              ))}
            </div>
          </div>

          {/* Audience + channel */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Audience</div>
              <select style={css.select} value={broadcastAudience} onChange={e=>setBroadcastAudience(e.target.value)}>
                <option value="all">All Registered Teams ({tournaments.reduce((s,t)=>s+(t.registrations?.length||0),0)})</option>
                <option value="active">Active Event Teams</option>
                <option value="checkedin">Checked-In Only</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Channel</div>
              <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
                {[["sms","SMS"],["email","Email"],["both","Both"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setBroadcastChannel(v)} style={{ flex:1, padding:"7px 0", background:broadcastChannel===v?"rgba(201,168,76,0.12)":"transparent", border:"none", borderRight:v!=="both"?"1px solid var(--navy-border)":"none", color:broadcastChannel===v?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Message */}
          <div>
            <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>Message</div>
            <textarea
              style={{ ...css.input, minHeight:80, resize:"vertical", fontSize:13 }}
              placeholder="Enter your broadcast message — AI will polish it for each channel…"
              value={broadcastMsg}
              onChange={e=>setBroadcastMsg(e.target.value)}
            />
          </div>

          {/* AI polish button */}
          <div style={{ display:"flex", gap:8 }}>
            <Btn disabled={!broadcastMsg || broadcastGenerating} onClick={generateBroadcastAI} style={{ flex:1, justifyContent:"center" }}>
              {broadcastGenerating ? "✨ Generating…" : "✨ Polish with AI"}
            </Btn>
          </div>

          {/* Preview */}
          {broadcastPreview && (
            <div style={{ ...css.card, padding:"14px 16px", border:"1px solid rgba(201,168,76,0.3)", background:"rgba(201,168,76,0.04)" }}>
              <div style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)", marginBottom:10 }}>AI Preview</div>
              {(broadcastChannel==="sms"||broadcastChannel==="both") && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, marginBottom:4 }}>SMS ({broadcastPreview.sms?.length||0} chars)</div>
                  <div style={{ padding:"8px 12px", background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:4, fontSize:12 }}>{broadcastPreview.sms}</div>
                </div>
              )}
              {(broadcastChannel==="email"||broadcastChannel==="both") && (
                <div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, marginBottom:4 }}>Email Subject</div>
                  <div style={{ padding:"6px 12px", background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:4, fontSize:12, marginBottom:6 }}>{broadcastPreview.email_subject}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, marginBottom:4 }}>Email Body</div>
                  <div style={{ padding:"8px 12px", background:"rgba(255,255,255,0.04)", border:"1px solid var(--navy-border)", borderRadius:4, fontSize:12, whiteSpace:"pre-line" }}>{broadcastPreview.email_body}</div>
                </div>
              )}
            </div>
          )}

          {/* Send + push sim */}
          {broadcastSent ? (
            <div style={{ padding:"12px 16px", background:"rgba(46,204,113,0.1)", border:"1px solid rgba(46,204,113,0.35)", borderRadius:5, textAlign:"center", color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, letterSpacing:1 }}>
              ✓ Broadcast sent!
            </div>
          ) : (
            <Btn disabled={!broadcastMsg||pushSimRunning} onClick={sendBroadcast} style={{ justifyContent:"center" }}>
              {pushSimRunning ? "📡 Sending…" : `📣 Send Broadcast`}
            </Btn>
          )}

          {/* Push sim receipt log */}
          {pushSimLog.length > 0 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"8px 14px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>📡 Delivery Receipts</span>
                <span style={{ fontSize:11, color:pushSimRunning?"var(--gold)":"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                  {pushSimRunning?"Sending…":`${pushSimLog.filter(r=>r.status==="delivered").length}/${pushSimLog.length} delivered`}
                </span>
              </div>
              <div style={{ maxHeight:200, overflowY:"auto" }}>
                {pushSimLog.map((r,i)=>(
                  <div key={r.id||i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 14px", borderBottom:i<pushSimLog.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:r.status==="delivered"?"var(--green)":"var(--red)", flexShrink:0 }} />
                    <span style={{ flex:1, fontSize:12 }}>{r.name}</span>
                    <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif" }}>{r.ch.toUpperCase()}</span>
                    <span style={{ fontSize:10, color:r.status==="delivered"?"var(--green)":"var(--red)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
                      {r.status==="delivered"?"✓ Delivered":"✕ Failed"}
                    </span>
                    <span style={{ fontSize:10, color:"var(--muted)" }}>{r.ts}</span>
                  </div>
                ))}
                {pushSimRunning && (
                  <div style={{ padding:"8px 14px", textAlign:"center" }}>
                    <div style={{ width:16, height:16, borderRadius:"50%", border:"2px solid var(--gold)", borderTopColor:"transparent", animation:"spin 0.8s linear infinite", display:"inline-block" }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DIRECT MESSAGE TAB ────────────────────────────────────────────────── */}
      {tab === "dm" && (() => {
        const allRegs = tournaments.flatMap(t => (t.registrations||[]).map(r=>({...r, _tournName:t.name})));
        const filtered = dmSearch
          ? allRegs.filter(r =>
              r.teamName?.toLowerCase().includes(dmSearch.toLowerCase()) ||
              r.players?.some(p=>p.name.toLowerCase().includes(dmSearch.toLowerCase()))
            )
          : allRegs;

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

            {/* Recipient picker */}
            <div>
              <div style={{ fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:6 }}>
                Search Recipients
              </div>
              <input
                style={{ ...css.input }}
                placeholder="🔍 Team name or player name…"
                value={dmSearch}
                onChange={e=>{ setDmSearch(e.target.value); setDmRecipient(null); setDmPreview(null); }}
              />
              {dmSearch && !dmRecipient && filtered.length > 0 && (
                <div style={{ ...css.card, padding:0, overflow:"hidden", marginTop:4, border:"1px solid var(--navy-border)", maxHeight:200, overflowY:"auto" }}>
                  {filtered.slice(0,8).map((r,i)=>(
                    <div key={r.id} onClick={()=>{ setDmRecipient(r); setDmSearch(r.teamName||""); }}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:i<Math.min(7,filtered.length-1)?"1px solid rgba(255,255,255,0.04)":"none", cursor:"pointer", background:"transparent" }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#1e3250,#5b8dee)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, flexShrink:0 }}>
                        {(r.teamName||"T").charAt(0)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{r.teamName||"Unnamed"}</div>
                        <div style={{ fontSize:11, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {r.players?.map(p=>p.name).join(", ")||"—"} · {r._tournName}
                        </div>
                      </div>
                      <Badge color={r.status==="checked_in"?"green":""}>{r.status==="checked_in"?"✓ IN":"PENDING"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected recipient card */}
            {dmRecipient && (
              <div style={{ ...css.card, padding:"12px 14px", border:"1px solid rgba(91,141,238,0.3)", background:"rgba(91,141,238,0.05)", display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:"linear-gradient(135deg,#1e3250,#5b8dee)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, flexShrink:0 }}>
                  {(dmRecipient.teamName||"T").charAt(0)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{dmRecipient.teamName}</div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>{dmRecipient.players?.map(p=>p.name).join(", ")||"—"}</div>
                  {dmHistory[dmRecipient.id]?.length > 0 && (
                    <div style={{ fontSize:10, color:"#5b8dee", marginTop:2 }}>
                      💬 {dmHistory[dmRecipient.id].length} previous message{dmHistory[dmRecipient.id].length>1?"s":""}
                    </div>
                  )}
                </div>
                <button onClick={()=>{ setDmRecipient(null); setDmSearch(""); setDmPreview(null); }} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:18 }}>✕</button>
              </div>
            )}

            {/* Message history */}
            {dmRecipient && dmHistory[dmRecipient.id]?.length > 0 && (
              <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
                <div style={{ padding:"7px 14px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>Message History</div>
                {dmHistory[dmRecipient.id].map((m,i)=>(
                  <div key={i} style={{ padding:"8px 14px", borderBottom:i<dmHistory[dmRecipient.id].length-1?"1px solid rgba(255,255,255,0.04)":"none", display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ fontSize:12, flexShrink:0 }}>{m.ch==="sms"?"📱":"✉️"}</span>
                    <div style={{ flex:1, fontSize:12 }}>{m.msg}</div>
                    <span style={{ fontSize:10, color:"var(--muted)", flexShrink:0, whiteSpace:"nowrap" }}>{m.ts}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Message composer */}
            {dmRecipient && (
              <>
                {/* Channel */}
                <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden", alignSelf:"flex-start" }}>
                  {[["sms","📱 SMS"],["email","✉️ Email"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setDmChannel(v)} style={{ padding:"6px 14px", background:dmChannel===v?"rgba(201,168,76,0.12)":"transparent", border:"none", borderRight:v==="sms"?"1px solid var(--navy-border)":"none", color:dmChannel===v?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>{l}</button>
                  ))}
                </div>

                <textarea
                  style={{ ...css.input, minHeight:72, resize:"vertical", fontSize:13 }}
                  placeholder={`Message to ${dmRecipient.teamName}…`}
                  value={dmMsg}
                  onChange={e=>{ setDmMsg(e.target.value); setDmPreview(null); }}
                />

                <div style={{ display:"flex", gap:8 }}>
                  <Btn disabled={!dmMsg||dmGenerating} v="ghost" onClick={generateDMAI} style={{ flex:1, justifyContent:"center" }}>
                    {dmGenerating?"✨ Generating…":"✨ Polish with AI"}
                  </Btn>
                  <Btn disabled={!dmMsg||dmSent} onClick={sendDM} style={{ flex:1, justifyContent:"center" }}>
                    {dmSent?"✓ Sent!":"Send →"}
                  </Btn>
                </div>

                {dmPreview && (
                  <div style={{ ...css.card, padding:"12px 14px", border:"1px solid rgba(91,141,238,0.3)", background:"rgba(91,141,238,0.04)" }}>
                    <div style={{ fontSize:10, color:"#5b8dee", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>AI Preview</div>
                    <div style={{ fontSize:12, lineHeight:1.7, whiteSpace:"pre-line" }}>
                      {dmChannel==="sms" ? dmPreview.sms : `${dmPreview.email_subject}

${dmPreview.email_body}`}
                    </div>
                  </div>
                )}
              </>
            )}

            {!dmRecipient && !dmSearch && (
              <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>💬</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Direct Message</div>
                <div style={{ fontSize:13 }}>Search for a team or player above to send a private message</div>
              </div>
            )}

          </div>
        );
      })()}

    </div>
  );
}

// ── STORAGE HELPERS ──────────────────────────────────────────────────────────
const DEFAULT_ADMIN_SETTINGS = {
  // Tournament defaults
  defaultFormat: "double_elim",
  defaultCupCount: 10,
  defaultTeamSize: "2v2",
  // Elo & scoring
  eloWeight: 60,
  placementWeight: 30,
  cupDiffWeight: 10,
  autoApprove: false,
  requireApproval: true,
  maxOverridesPerEvent: 5,
  eloK: 32,
  startingElo: 1200,
  // Branding
  leagueName: "MLCP",
  leagueTagline: "Major League Cup Pong",
  contactEmail: "",
  websiteUrl: "mlcp.gg",
  // Registration defaults
  defaultEntryFee: 0,
  autoWaitlistThreshold: 0,
  requireEmailOnReg: false,
  allowGuestRegistration: true,
  // Display
  showEloPublicly: true,
  showCupDiff: true,
  compactScoreDisplay: false,
  showMatchHistory: true,
};

const STORAGE_VERSION = 1;
const SK = { players:"mlcp:players", tournaments:"mlcp:tournaments", activeTournament:"mlcp:activeTournament", page:"mlcp:page", version:"mlcp:version", seasons:"mlcp:seasons", venues:"mlcp:venues", auditLog:"mlcp:auditLog", adminSettings:"mlcp:adminSettings" };

async function storageGet(key) {
  try { const r = await window.storage.get(key); return r ? JSON.parse(r.value) : null; }
  catch { return null; }
}
async function storageSet(key, value) {
  try { await window.storage.set(key, JSON.stringify(value)); } catch {}
}
async function storageDel(key) {
  try { await window.storage.delete(key); } catch {}
}
async function loadAllState() {
  const [ver, players, tournaments, activeTournament, page, seasons, venues] = await Promise.all([
    storageGet(SK.version), storageGet(SK.players), storageGet(SK.tournaments),
    storageGet(SK.activeTournament), storageGet(SK.page), storageGet(SK.seasons), storageGet(SK.venues),
  ]);
  return {
    isFirstLoad: !ver,
    players:           players      || INIT_PLAYERS,
    tournaments:       tournaments  || INIT_TOURNAMENTS,
    activeTournament:  activeTournament || null,
    page:              page         || "dashboard",
    seasons:           seasons      || INIT_SEASONS,
    venues:            venues       || INIT_VENUES,
  };
}
async function clearAllStorage() {
  await Promise.all(Object.values(SK).map(k => storageDel(k)));
}

// ── EXPORT & PRINT PAGE ──────────────────────────────────────────────────────
function ExportPage({ tournaments, players, seasons, activeTournament, setPage }) {
  const { isMobile } = useBreakpoint();

  const [tab,            setTab]            = useState("exports");  // exports | print | scoresheet
  const [selTournId,     setSelTournId]     = useState(activeTournament?.id || tournaments[0]?.id || "");
  const [selSeasonId,    setSelSeasonId]    = useState(seasons?.[0]?.id || "");
  const [toast,          setToast]          = useState(null);
  const [printing,       setPrinting]       = useState(false);
  const [printTarget,    setPrintTarget]    = useState(null);  // {type, data}
  const [sheetRound,     setSheetRound]     = useState(0);

  const tournament = tournaments.find(t => t.id === selTournId);
  const season     = (seasons||[]).find(s => s.id === selSeasonId);

  function flash(msg, color="var(--green)") {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  }

  // ── CSV downloads ─────────────────────────────────────────────────────────
  function dlPlayers()      { downloadCSV("mlcp_players.csv",           buildPlayersCSV(players, tournaments)); flash("Players CSV downloaded"); }
  function dlMatches()      { downloadCSV("mlcp_match_history.csv",     buildMatchHistoryCSV(tournaments, selTournId||null)); flash("Match history CSV downloaded"); }
  function dlRegistrations(){ if(!tournament) return; downloadCSV(`${tournament.name}_registrations.csv`, buildRegistrationsCSV(tournament)); flash("Registrations CSV downloaded"); }
  function dlTournResults() { if(!tournament) return; downloadCSV(`${tournament.name}_results.csv`,       buildTournamentResultsCSV(tournament)); flash("Tournament results CSV downloaded"); }
  function dlSeason()       { if(!season) return; downloadCSV(`${season.name}_standings.csv`,             buildSeasonCSV(season, tournaments, players)); flash("Season standings CSV downloaded"); }

  function dlAllData() {
    const data = { exportedAt: new Date().toISOString(), players, tournaments, seasons: seasons||[] };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href = url; a.download = "mlcp_full_export.json"; a.click();
    URL.revokeObjectURL(url);
    flash("Full data JSON downloaded");
  }

  // ── JSON Import ────────────────────────────────────────────────────────────
  function importJSON(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.players && !data.tournaments) throw new Error("Invalid format");
        if (data.players)     setPlayers(data.players);
        if (data.tournaments) setTournaments(data.tournaments);
        if (data.seasons)     setSeasons(data.seasons);
        flash("✓ Data imported successfully (" + (data.players?.length||0) + " players, " + (data.tournaments?.length||0) + " tournaments)");
      } catch(err) {
        flash("✗ Import failed: " + err.message);
      }
    };
    reader.readAsText(file);
  }

  // ── Print helpers ──────────────────────────────────────────────────────────
  function printBracket() {
    if (!tournament) return;
    setPrintTarget({ type:"bracket", tournament });
    setPrinting(true);
  }

  function printStandings() {
    if (!tournament) return;
    setPrintTarget({ type:"standings", tournament });
    setPrinting(true);
  }

  function printRegistrations() {
    if (!tournament) return;
    setPrintTarget({ type:"registrations", tournament });
    setPrinting(true);
  }

  function printScoreSheets() {
    if (!tournament) return;
    setPrintTarget({ type:"scoresheets", tournament });
    setPrinting(true);
  }

  function doActualPrint() {
    window.print();
    setTimeout(() => { setPrinting(false); setPrintTarget(null); }, 800);
  }

  // ── Export type definitions ────────────────────────────────────────────────
  const CSV_EXPORTS = [
    {
      id:"players", icon:"👥", label:"Players & Rankings",
      desc:"All players with Elo, W/L, power rating, cup diff",
      action: dlPlayers, alwaysEnabled: true,
    },
    {
      id:"matches", icon:"⚽", label:"Match History",
      desc:"Complete match log — all tournaments or filter to one",
      action: dlMatches, alwaysEnabled: true,
    },
    {
      id:"registrations", icon:"📋", label:"Registrations",
      desc:"Team list, payment status, check-in times",
      action: dlRegistrations, needsTournament: true,
    },
    {
      id:"results", icon:"🏆", label:"Tournament Results",
      desc:"Final bracket results and placements",
      action: dlTournResults, needsTournament: true,
    },
    {
      id:"season", icon:"📅", label:"Season Standings",
      desc:"Full season points table with event breakdown",
      action: dlSeason, needsSeason: true,
    },
    {
      id:"full", icon:"💾", label:"Full Data Backup",
      desc:"All platform data as JSON (reimportable)",
      action: dlAllData, alwaysEnabled: true,
    },
  ];

  const PRINT_EXPORTS = [
    {
      id:"bracket", icon:"📊", label:"Bracket Poster",
      desc:"Full printable bracket — A3/tabloid landscape",
      action: printBracket, needsTournament: true,
    },
    {
      id:"registrations", icon:"📋", label:"Registration Sheet",
      desc:"Checklist with check-in boxes and payment status",
      action: printRegistrations, needsTournament: true,
    },
    {
      id:"standings", icon:"🏆", label:"Standings Sheet",
      desc:"Current standings for posting at the venue",
      action: printStandings, needsTournament: true,
    },
    {
      id:"scoresheets", icon:"✏️", label:"Score Sheets",
      desc:"Printable per-match score recording sheets",
      action: printScoreSheets, needsTournament: true,
    },
  ];

  // ── Print preview renderer ─────────────────────────────────────────────────
  function renderPrintPreview() {
    if (!printTarget) return null;
    const t = printTarget.tournament;
    const regs = t?.registrations || [];
    const allM = getAllMatchesFromTournament(t);
    const completed = allM.filter(m => m.status === "complete" && m.t1 && m.t2);

    function PrintHeader({ title, subtitle }) {
      return (
        <div style={{ borderBottom:"3px solid #1a2d4a", paddingBottom:10, marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
            <div>
              <div style={{ fontSize:28, fontWeight:900, letterSpacing:3, textTransform:"uppercase", fontFamily:"sans-serif" }}>MLCP</div>
              <div style={{ fontSize:18, fontWeight:700, marginTop:2, color:"#1a2d4a" }}>{title}</div>
              {subtitle && <div style={{ fontSize:12, color:"#666", marginTop:2 }}>{subtitle}</div>}
            </div>
            <div style={{ textAlign:"right", fontSize:11, color:"#999" }}>
              <div>{t?.name}</div>
              <div>{t?.date||"Date TBD"}</div>
              <div>Printed {new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      );
    }

    // ── Bracket print ──
    if (printTarget.type === "bracket") {
      const bkt = t?.bracket;
      const allRounds = bkt ? [
        ...(bkt.wb||bkt.rounds||[]).map(r => ({...r, label:"Winners"})),
        ...(bkt.lb||[]).map(r => ({...r, label:"Losers"})),
        ...(bkt.gf ? [{name:"Grand Final", matches:[bkt.gf], label:"Final"}] : []),
      ] : [];

      return (
        <div style={{ fontFamily:"sans-serif", color:"#1a2d4a", background:"white", padding:"24px 28px" }}>
          <PrintHeader title="Tournament Bracket" />
          {allRounds.length === 0 ? (
            <div style={{ textAlign:"center", padding:60, color:"#999" }}>No bracket generated yet</div>
          ) : (
            <div style={{ display:"flex", gap:24, overflowX:"auto" }}>
              {allRounds.map((round, ri) => (
                <div key={ri} style={{ flexShrink:0, minWidth:160 }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"#666", marginBottom:10, textAlign:"center" }}>
                    {round.label} · {round.name}
                  </div>
                  {(round.matches||[]).map((m,mi) => {
                    const isDone = m.status==="complete";
                    const w1 = m.winner && m.t1 && (m.winner.id===m.t1.id||m.winner.name===m.t1.name);
                    return (
                      <div key={mi} style={{ marginBottom:10, border:"1.5px solid #ccc", borderRadius:4, overflow:"hidden" }}>
                        {[{team:m.t1,score:m.s1},{team:m.t2,score:m.s2}].map((side,si) => {
                          const isWin = isDone && ((si===0&&w1)||(si===1&&!w1));
                          return (
                            <div key={si} style={{ padding:"5px 8px", borderBottom:si===0?"1px solid #eee":"none", background:isWin?"#f0f8e8":"white", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <span style={{ fontSize:12, fontWeight:isWin?700:400, maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {side.team?.name||"TBD"}
                              </span>
                              {isDone ? (
                                <span style={{ fontSize:14, fontWeight:700, color:isWin?"#2a7a2a":"#666" }}>{side.score}</span>
                              ) : (
                                <span style={{ fontSize:11, color:"#ccc", border:"1px solid #eee", borderRadius:3, padding:"1px 6px", minWidth:20, textAlign:"center" }}>&nbsp;</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── Registrations print ──
    if (printTarget.type === "registrations") {
      const sorted = [...regs].sort((a,b) => (a.seed||999)-(b.seed||999) || a.teamName.localeCompare(b.teamName));
      return (
        <div style={{ fontFamily:"sans-serif", color:"#1a2d4a", background:"white", padding:"24px 28px" }}>
          <PrintHeader title="Registration Checklist" subtitle={`${regs.length} teams registered`} />
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#1a2d4a", color:"white" }}>
                {["#","Team","Players","Seed","Payment","Check-In","✓"].map(h=>(
                  <th key={h} style={{ padding:"6px 10px", textAlign:"left", fontWeight:700, letterSpacing:1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r,i) => (
                <tr key={r.id} style={{ borderBottom:"1px solid #eee", background:i%2===0?"white":"#fafafa" }}>
                  <td style={{ padding:"6px 10px", color:"#999" }}>{i+1}</td>
                  <td style={{ padding:"6px 10px", fontWeight:600 }}>{r.teamName}</td>
                  <td style={{ padding:"6px 10px", color:"#555", fontSize:11 }}>{r.players?.map(p=>p.name).join(", ")||"—"}</td>
                  <td style={{ padding:"6px 10px", textAlign:"center" }}>{r.seed||"—"}</td>
                  <td style={{ padding:"6px 10px" }}>
                    <span style={{ padding:"1px 6px", borderRadius:3, background:r.paymentStatus==="paid"?"#d4edda":r.paymentStatus==="waived"?"#e2e8f0":"#fff3cd", color:r.paymentStatus==="paid"?"#155724":r.paymentStatus==="waived"?"#555":"#856404", fontSize:11, fontWeight:600 }}>
                      {r.paymentStatus==="paid"?"PAID":r.paymentStatus==="waived"?"WAIVED":"UNPAID"}
                    </span>
                  </td>
                  <td style={{ padding:"6px 10px", fontSize:11, color:"#555" }}>{r.checkedInAt||"—"}</td>
                  <td style={{ padding:"6px 10px", textAlign:"center" }}>
                    <div style={{ width:16, height:16, border:"2px solid #ccc", borderRadius:3, display:"inline-block" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // ── Standings print ──
    if (printTarget.type === "standings") {
      const wins = {};
      completed.forEach(m => {
        const n = m.winner?.name;
        if (n) wins[n] = (wins[n]||0)+1;
      });
      const teams = [...new Set(allM.filter(m=>m.t1&&m.t2).flatMap(m=>[m.t1.name,m.t2.name]))];
      const rows  = teams.map(name => ({
        name,
        wins:   wins[name]||0,
        losses: completed.filter(m=>(m.t1?.name===name||m.t2?.name===name)&&m.winner?.name!==name).length,
      })).sort((a,b)=>b.wins-a.wins||a.losses-b.losses);

      return (
        <div style={{ fontFamily:"sans-serif", color:"#1a2d4a", background:"white", padding:"24px 28px" }}>
          <PrintHeader title="Current Standings" subtitle={`${completed.length} matches completed`} />
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"#1a2d4a", color:"white" }}>
                {["Rank","Team","W","L","Win%"].map(h=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontWeight:700, letterSpacing:1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r,i) => (
                <tr key={r.name} style={{ borderBottom:"1px solid #eee", background:i<3?"#fffbf0":i%2===0?"white":"#fafafa" }}>
                  <td style={{ padding:"8px 14px", fontWeight:700, color:i===0?"#b8860b":i===1?"#888":i===2?"#cd7f32":"#333", fontSize:18 }}>{i+1}</td>
                  <td style={{ padding:"8px 14px", fontWeight:i<3?700:400, fontSize:14 }}>{r.name}</td>
                  <td style={{ padding:"8px 14px", color:"#2a7a2a", fontWeight:700, fontSize:16 }}>{r.wins}</td>
                  <td style={{ padding:"8px 14px", color:"#666", fontSize:14 }}>{r.losses}</td>
                  <td style={{ padding:"8px 14px", color:"#333", fontWeight:600 }}>
                    {r.wins+r.losses>0?Math.round(r.wins/(r.wins+r.losses)*100)+"%" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // ── Score sheets print ──
    if (printTarget.type === "scoresheets") {
      const pending = allM.filter(m => m.status==="pending" && m.t1 && m.t2);
      const sheets  = pending.length > 0 ? pending : allM.filter(m=>m.t1&&m.t2).slice(0,8);
      const perPage = 2;
      const pages   = [];
      for (let i=0; i<sheets.length; i+=perPage) pages.push(sheets.slice(i,i+perPage));
      const displayIdx = sheetRound < pages.length ? sheetRound : 0;
      const displayPage = pages[displayIdx] || [];

      return (
        <div style={{ fontFamily:"sans-serif", color:"#1a2d4a", background:"white", padding:"24px 28px" }}>
          <PrintHeader title="Score Recording Sheets" subtitle={`${sheets.length} matches · Page ${displayIdx+1} of ${pages.length}`} />
          {displayPage.map((m, mi) => (
            <div key={mi} style={{ marginBottom:24, pageBreakInside:"avoid" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10, background:"#f0f4fa", padding:"8px 14px", borderRadius:4 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:"#666", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>{m._bracket||""} {m._round||""}</div>
                  <div style={{ fontSize:16, fontWeight:700, marginTop:2 }}>{m.t1?.name} vs {m.t2?.name}</div>
                </div>
                <div style={{ fontSize:11, color:"#666" }}>Table: ______</div>
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    <th style={{ padding:"5px 10px", background:"#1a2d4a", color:"white", textAlign:"left" }}>Team</th>
                    {Array(10).fill(0).map((_,i)=>(
                      <th key={i} style={{ padding:"5px 6px", background:"#1a2d4a", color:"white", textAlign:"center", width:32 }}>R{i+1}</th>
                    ))}
                    <th style={{ padding:"5px 10px", background:"#1a2d4a", color:"white", textAlign:"center", width:50 }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {[m.t1,m.t2].map((team,ti) => (
                    <tr key={ti} style={{ borderBottom:"1px solid #ccc" }}>
                      <td style={{ padding:"8px 10px", fontWeight:600, background:ti%2===0?"#fafafa":"white" }}>{team?.name||"TBD"}</td>
                      {Array(10).fill(0).map((_,ci)=>(
                        <td key={ci} style={{ padding:"8px 6px", textAlign:"center", border:"1px solid #eee", background:ti%2===0?"#fafafa":"white" }}>&nbsp;</td>
                      ))}
                      <td style={{ padding:"8px 10px", textAlign:"center", border:"2px solid #1a2d4a", background:"#f0f4fa", fontWeight:700 }}>&nbsp;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:24, marginTop:8, fontSize:11, color:"#666" }}>
                <span>Referee initials: ________</span>
                <span>Winner: ________</span>
                <span>Time: ________</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  }

  // ── TABS ───────────────────────────────────────────────────────────────────
  const TABS = [
    { id:"exports",    label:"CSV / JSON",    icon:"📦" },
    { id:"print",      label:"Print",         icon:"🖨" },
    { id:"scoresheet", label:"Score Sheets",  icon:"✏️" },
    { id:"share",      label:"Share",         icon:"🔗" },
    { id:"matchreport",label:"Match Reports", icon:"📋" },
  ];

  // ── MAIN RENDER ────────────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:20, right:20, zIndex:2000, background:"var(--navy-mid)", border:`1px solid ${toast.color}`, borderRadius:6, padding:"12px 20px", fontSize:13, fontWeight:600, color:toast.color, boxShadow:"0 8px 32px rgba(0,0,0,0.4)", animation:"fadeIn 0.2s ease" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Export & Print</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>Download CSVs · Print bracket posters · Score sheets</div>
        </div>
        {/* Context selectors */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <select style={css.select} value={selTournId} onChange={e=>setSelTournId(e.target.value)}>
            <option value="">— All Tournaments —</option>
            {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {(seasons||[]).length > 0 && (
            <select style={css.select} value={selSeasonId} onChange={e=>setSelSeasonId(e.target.value)}>
              <option value="">— Season —</option>
              {(seasons||[]).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Tab strip */}
      <div className="tab-strip" style={{ marginBottom:18 }}>
        {TABS.map(t=>(
          <button key={t.id} className={`tab-btn ${tab===t.id?"tab-active":""}`} onClick={()=>setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── EXPORTS TAB ── */}
      {tab==="exports" && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Stats strip */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:6 }}>
            {[
              [players.length,                                   "Players",    "var(--gold)"],
              [tournaments.length,                               "Tournaments","#5b8dee"],
              [tournaments.reduce((s,t)=>(s+(t.registrations?.length||0)),0), "Registrations","var(--green)"],
              [getAllMatchesFromTournament(tournaments[0]||{}).length > 0 ? "✓":"—", "Match Data","var(--muted)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ ...css.card, padding:"10px 8px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>

          {CSV_EXPORTS.map(exp => {
            const disabled = (exp.needsTournament && !tournament) || (exp.needsSeason && !season);
            return (
              <div key={exp.id} style={{ ...css.card, padding:"14px 16px", display:"flex", alignItems:"center", gap:14, opacity:disabled?0.45:1 }}>
                <div style={{ fontSize:28, flexShrink:0 }}>{exp.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>{exp.label}</div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>
                    {exp.desc}
                    {exp.needsTournament && !tournament && <span style={{ color:"var(--gold)", marginLeft:6 }}>— select a tournament above</span>}
                    {exp.needsSeason    && !season     && <span style={{ color:"var(--gold)", marginLeft:6 }}>— select a season above</span>}
                  </div>
                </div>
                <Btn disabled={disabled} onClick={exp.action} style={{ flexShrink:0 }}>⬇ CSV</Btn>
              </div>
            );
          })}

          {/* ── Import ── */}
          <div style={{ marginTop:8 }}>
            <div style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>📥 Import Data</div>
            <div style={{ ...css.card, padding:"14px 16px", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ fontSize:28, flexShrink:0 }}>📥</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:2 }}>Restore from Backup</div>
                <div style={{ fontSize:11, color:"var(--muted)" }}>Upload a JSON backup file to restore all players, tournaments and seasons. <strong style={{color:"var(--red)"}}>Overwrites existing data.</strong></div>
              </div>
              <label style={{ cursor:"pointer" }}>
                <input type="file" accept=".json" style={{ display:"none" }} onChange={e=>{ if(window.confirm("This will overwrite all current data. Continue?")) importJSON(e.target.files[0]); e.target.value=""; }} />
                <span style={{ padding:"6px 14px", background:"rgba(91,141,238,0.12)", border:"1px solid rgba(91,141,238,0.35)", borderRadius:4, color:"#5b8dee", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, whiteSpace:"nowrap" }}>📁 Choose File</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── PRINT TAB ── */}
      {tab==="print" && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Print guide */}
          <div style={{ padding:"10px 14px", background:"rgba(91,141,238,0.07)", border:"1px solid rgba(91,141,238,0.25)", borderRadius:5, fontSize:12, color:"var(--muted)" }}>
            💡 Use your browser's Print dialog (Ctrl/Cmd+P). For the bracket poster, set layout to <strong style={{color:"var(--white)"}}>Landscape</strong> and paper to <strong style={{color:"var(--white)"}}>A3 or Tabloid</strong>.
          </div>

          {PRINT_EXPORTS.map(exp => {
            const disabled = exp.needsTournament && !tournament;
            return (
              <div key={exp.id} style={{ ...css.card, padding:"14px 16px", display:"flex", alignItems:"center", gap:14, opacity:disabled?0.45:1 }}>
                <div style={{ fontSize:28, flexShrink:0 }}>{exp.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>{exp.label}</div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>
                    {exp.desc}
                    {disabled && <span style={{ color:"var(--gold)", marginLeft:6 }}>— select a tournament above</span>}
                  </div>
                </div>
                <Btn disabled={disabled} onClick={exp.action} style={{ flexShrink:0 }}>🖨 Preview</Btn>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SCORE SHEETS TAB ── */}
      {tab==="scoresheet" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {!tournament ? (
            <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>✏️</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:12 }}>Select a Tournament</div>
              <select style={{ ...css.select, maxWidth:280 }} value={selTournId} onChange={e=>setSelTournId(e.target.value)}>
                <option value="">— choose —</option>
                {tournaments.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          ) : (() => {
            const allM = getAllMatchesFromTournament(tournament);
            const pending = allM.filter(m=>m.status==="pending"&&m.t1&&m.t2);
            const allValid = allM.filter(m=>m.t1&&m.t2);
            const rounds = [...new Set(allValid.map(m=>m._round||"Round 1"))];

            return (
              <>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:4 }}>Filter by Round</div>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                      <button onClick={()=>setSheetRound(-1)} style={{ padding:"4px 10px", borderRadius:4, border:`1px solid ${sheetRound===-1?"var(--gold)":"var(--navy-border)"}`, background:sheetRound===-1?"rgba(201,168,76,0.12)":"transparent", color:sheetRound===-1?"var(--gold)":"var(--muted)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>All</button>
                      {rounds.map((r,ri)=>(
                        <button key={r} onClick={()=>setSheetRound(ri)} style={{ padding:"4px 10px", borderRadius:4, border:`1px solid ${sheetRound===ri?"var(--gold)":"var(--navy-border)"}`, background:sheetRound===ri?"rgba(201,168,76,0.12)":"transparent", color:sheetRound===ri?"var(--gold)":"var(--muted)", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, cursor:"pointer" }}>{r}</button>
                      ))}
                    </div>
                  </div>
                  <Btn onClick={()=>{ setPrintTarget({type:"scoresheets",tournament}); setPrinting(true); }} style={{ flexShrink:0 }}>🖨 Print Sheets</Btn>
                </div>

                {/* Preview of sheets */}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {(sheetRound===-1?allValid:allValid.filter(m=>m._round===rounds[sheetRound])).slice(0,6).map((m,mi)=>(
                    <div key={mi} style={{ ...css.card, padding:"12px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                        <span style={{ fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, color:"var(--muted)", textTransform:"uppercase" }}>{m._bracket||""} {m._round||""}</span>
                        <span style={{ flex:1, fontSize:13, fontWeight:700 }}>{m.t1?.name} vs {m.t2?.name}</span>
                        {m.status==="complete" && <span style={{ fontSize:10, color:"var(--green)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>✓ {m.s1}–{m.s2}</span>}
                      </div>
                      {/* Mini score grid preview */}
                      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                        {[m.t1,m.t2].map((team,ti)=>(
                          <div key={ti} style={{ display:"flex", gap:3, alignItems:"center" }}>
                            <span style={{ fontSize:11, fontWeight:600, minWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{team?.name||"TBD"}</span>
                            {Array(10).fill(0).map((_,ci)=>(
                              <div key={ci} style={{ width:22, height:22, border:"1px solid var(--navy-border)", borderRadius:2, background:"rgba(255,255,255,0.02)" }} />
                            ))}
                            <div style={{ width:32, height:22, border:"2px solid var(--gold)", borderRadius:2, background:"rgba(201,168,76,0.06)" }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {allValid.length === 0 && (
                    <div style={{ textAlign:"center", padding:40, color:"var(--muted)" }}>No matches found for this tournament</div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── PRINT PREVIEW MODAL ── */}
      {printing && printTarget && (
        <div style={{ position:"fixed", inset:0, zIndex:600, background:"rgba(0,0,0,0.85)", display:"flex", flexDirection:"column" }}>
          {/* Toolbar */}
          <div style={{ background:"var(--navy-mid)", borderBottom:"2px solid var(--gold)", padding:"10px 20px", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:3 }}>Print Preview</span>
            <div style={{ flex:1 }} />
            {printTarget.type==="scoresheets" && (() => {
              const allM = getAllMatchesFromTournament(printTarget.tournament);
              const pages = [];
              const valid = allM.filter(m=>m.t1&&m.t2);
              for (let i=0;i<valid.length;i+=2) pages.push(valid.slice(i,i+2));
              return pages.length > 1 ? (
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:13 }}>
                  <Btn sz="sm" v="ghost" disabled={sheetRound===0} onClick={()=>setSheetRound(p=>Math.max(0,p-1))}>← Prev</Btn>
                  <span style={{ color:"var(--muted)" }}>Page {sheetRound+1}/{pages.length}</span>
                  <Btn sz="sm" v="ghost" disabled={sheetRound>=pages.length-1} onClick={()=>setSheetRound(p=>Math.min(pages.length-1,p+1))}>Next →</Btn>
                </div>
              ) : null;
            })()}
            <Btn onClick={doActualPrint} style={{ justifyContent:"center" }}>🖨 Print Now</Btn>
            <Btn v="ghost" onClick={()=>{ setPrinting(false); setPrintTarget(null); }}>✕ Close</Btn>
          </div>
          {/* Preview area */}
          <div style={{ flex:1, overflow:"auto", padding:20 }} id="print-preview-area">
            <div style={{ background:"white", minWidth:600, borderRadius:4, boxShadow:"0 4px 32px rgba(0,0,0,0.4)", margin:"0 auto", maxWidth:960 }}>
              {renderPrintPreview()}
            </div>
          </div>
        </div>
      )}

      {/* ── MATCH REPORTS TAB ─────────────────────────────────────────────── */}
      {tab==="matchreport" && (() => {
        const allM = activeTournament ? getAllMatchesFromTournament(activeTournament).filter(m=>m.status==="complete"&&m.t1&&m.t2) : [];
        if (!activeTournament) return (
          <div style={{textAlign:"center",padding:"48px 16px",color:"var(--muted)"}}>
            <div style={{fontSize:36,marginBottom:8}}>📋</div>
            <div>No active tournament. Activate one from Tournaments to generate match reports.</div>
          </div>
        );
        if (allM.length===0) return (
          <div style={{textAlign:"center",padding:"48px 16px",color:"var(--muted)"}}>
            <div style={{fontSize:36,marginBottom:8}}>📋</div>
            <div>No completed matches yet in <strong>{activeTournament.name}</strong>.</div>
          </div>
        );

        function printMatchReport(m) {
          const winner = m.winner?.name || (m.s1>m.s2?m.t1.name:m.t2.name);
          const loser  = m.winner?.name===m.t1.name ? m.t2.name : m.t1.name;
          const dur = m.duration ? `${Math.floor(m.duration/60)}m ${m.duration%60}s` : "—";
          const html = `<!DOCTYPE html><html><head><title>Match Report</title>
<style>
  body{font-family:'Arial',sans-serif;max-width:580px;margin:40px auto;color:#111;background:#fff;}
  h1{font-size:28px;font-weight:900;letter-spacing:2px;margin:0 0 4px;text-transform:uppercase;}
  .sub{font-size:12px;color:#888;margin-bottom:24px;}
  .score-box{display:flex;align-items:center;justify-content:space-between;background:#f8f8f8;border-radius:8px;padding:20px 24px;margin-bottom:20px;}
  .team{text-align:center;}
  .team-name{font-size:18px;font-weight:700;}
  .team-score{font-size:48px;font-weight:900;line-height:1;}
  .winner .team-name{color:#1a6b35;}
  .sep{font-size:20px;color:#999;}
  table{width:100%;border-collapse:collapse;margin-bottom:16px;}
  th{text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#999;padding:4px 8px;border-bottom:1px solid #eee;}
  td{padding:7px 8px;font-size:13px;border-bottom:1px solid #f0f0f0;}
  .badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;background:#f0f0f0;}
  .ot{background:#fff3cd;color:#856404;}
  @media print{button{display:none}body{margin:20px}}
</style></head><body>
<h1>${activeTournament.name}</h1>
<div class="sub">Match Report · ${new Date().toLocaleDateString()} · ${m._bracket==="GF"?"Grand Final":m._bracket==="L"?"Losers Bracket":"Winners Bracket"} R${m._round||1}</div>
<div class="score-box">
  <div class="team ${m.winner?.id===m.t1.id?"winner":""}"><div class="team-name">${m.t1.name}</div><div class="team-score">${m.s1}</div></div>
  <div class="sep">vs</div>
  <div class="team ${m.winner?.id===m.t2.id?"winner":""}"><div class="team-name">${m.t2.name}</div><div class="team-score">${m.s2}</div></div>
</div>
<table>
  <tr><th>Field</th><th>Value</th></tr>
  <tr><td>Winner</td><td><strong>${winner}</strong></td></tr>
  <tr><td>Result</td><td>${m.s1}–${m.s2}${m.ot?" (OT)":""}</td></tr>
  <tr><td>Duration</td><td>${dur}</td></tr>
  <tr><td>Table</td><td>${m.tableAssignment||"—"}</td></tr>
  <tr><td>Format</td><td>${activeTournament.format||"—"}</td></tr>
  <tr><td>Bracket</td><td>${m._bracket==="GF"?"Grand Final":m._bracket==="L"?"Losers Bracket":"Winners Bracket"} · Round ${m._round||1}</td></tr>
  <tr><td>Completed</td><td>${m.completedAt?new Date(m.completedAt).toLocaleString():"—"}</td></tr>
  ${m.ot?'<tr><td>Overtime</td><td><span class="badge ot">Yes</span></td></tr>':""}
  ${m.refNote?`<tr><td>Referee Note</td><td><em>${m.refNote}</em></td></tr>`:""}
</table>
<button onclick="window.print()" style="padding:8px 20px;background:#111;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;font-weight:700">🖨 Print Report</button>
</body></html>`;
          const w = window.open("","_blank");
          w.document.write(html);
          w.document.close();
        }

        function printAllReports() {
          const rows = allM.map(m=>`
            <tr>
              <td>${m._bracket==="GF"?"Grand Final":m._bracket==="L"?"LB":"WB"} R${m._round||1}</td>
              <td>${m.t1.name}</td><td>${m.s1}–${m.s2}${m.ot?"*":""}</td><td>${m.t2.name}</td>
              <td><strong>${m.winner?.name||"?"}</strong></td>
              <td>${m.duration?Math.floor(m.duration/60)+"m":""}</td>
              <td>${m.tableAssignment||""}</td>
              <td style="font-size:11px;color:#888">${m.refNote||""}</td>
            </tr>`).join("");
          const html = `<!DOCTYPE html><html><head><title>${activeTournament.name} — All Matches</title>
<style>body{font-family:Arial,sans-serif;max-width:900px;margin:30px auto}h1{font-size:22px;font-weight:900;text-transform:uppercase;letter-spacing:2px}table{width:100%;border-collapse:collapse}th{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#999;padding:6px 8px;border-bottom:2px solid #eee;text-align:left}td{padding:7px 8px;font-size:12px;border-bottom:1px solid #f0f0f0}@media print{button{display:none}}</style></head>
<body><h1>${activeTournament.name}</h1><p style="color:#888;font-size:12px">${allM.length} completed matches · Generated ${new Date().toLocaleString()}</p>
<button onclick="window.print()" style="padding:6px 16px;background:#111;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-bottom:16px">🖨 Print All</button>
<table><thead><tr><th>Round</th><th>Team 1</th><th>Score</th><th>Team 2</th><th>Winner</th><th>Time</th><th>Table</th><th>Note</th></tr></thead>
<tbody>${rows}</tbody></table></body></html>`;
          const w = window.open("","_blank");
          w.document.write(html);
          w.document.close();
        }

        return (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:12,color:"var(--muted)"}}>{allM.length} completed matches in <strong style={{color:"var(--white)"}}>{activeTournament.name}</strong></div>
              <Btn sz="sm" v="ghost" onClick={printAllReports}>📋 Print All Reports</Btn>
            </div>
            {allM.map((m,i) => {
              const winner = m.winner?.id===m.t1.id ? m.t1 : m.t2;
              const loser  = m.winner?.id===m.t1.id ? m.t2 : m.t1;
              const dur = m.duration ? `${Math.floor(m.duration/60)}m ${m.duration%60}s` : null;
              return (
                <div key={m.id||i} style={{...css.card,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:140}}>
                    <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:4}}>
                      {m._bracket==="GF"?"Grand Final":m._bracket==="L"?"Losers":"Winners"} · R{m._round||1}
                      {m.tableAssignment&&<span style={{marginLeft:6,color:"var(--gold)"}}>T{m.tableAssignment}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:13,fontWeight:700,color:winner.id===m.t1.id?"var(--gold)":"var(--muted)"}}>{m.t1.name}</span>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:"var(--white)",letterSpacing:1}}>{m.s1}–{m.s2}</span>
                      <span style={{fontSize:13,fontWeight:700,color:winner.id===m.t2.id?"var(--gold)":"var(--muted)"}}>{m.t2.name}</span>
                      {m.ot&&<Badge>OT</Badge>}
                    </div>
                    {(dur||m.refNote)&&(
                      <div style={{fontSize:10,color:"var(--muted)",marginTop:4}}>
                        {dur&&<span>⏱ {dur}</span>}
                        {m.refNote&&<span style={{marginLeft:8,fontStyle:"italic"}}>"{m.refNote}"</span>}
                      </div>
                    )}
                  </div>
                  <Btn sz="sm" v="ghost" onClick={()=>printMatchReport(m)}>🖨 Report</Btn>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}


// ── ANALYTICS DASHBOARD ──────────────────────────────────────────────────────
function AnalyticsPage({ tournaments, players }) {
  const { isMobile } = useBreakpoint();
  const [tab, setTab]   = useState("overview");
  const [winnerFilter, setWinnerFilter] = useState("");

  const completed = (tournaments||[]).filter(t=>t.status==="completed");
  const allTourneys = tournaments||[];

  const getAllM = () => completed.flatMap(t =>
    getAllMatchesFromTournament(t).filter(m=>m.status==="complete"&&m.t1&&m.t2)
  );
  const allM = getAllM();

  // ── Summary KPIs ──────────────────────────────────────────────────────────
  const totalMatches = allM.length;
  const totalCups    = allM.reduce((s,m)=>(m.s1||0)+(m.s2||0)+s,0);
  const otCount      = allM.filter(m=>m.ot).length;
  const otRate       = totalMatches>0?Math.round(otCount/totalMatches*100):0;
  const avgCupsPerM  = totalMatches>0?(totalCups/totalMatches).toFixed(1):0;
  const blowouts     = allM.filter(m=>Math.abs((m.s1||0)-(m.s2||0))>=7).length;
  const totalRevenue = completed.reduce((s,t)=>{
    const fee = parseFloat(t.entryFee)||0;
    return s + fee*(t.registrations||[]).filter(r=>r.paymentStatus==="paid").length;
  },0);

  // ── Elo distribution buckets ──────────────────────────────────────────────
  const eloBuckets = { "<1100":0, "1100–1200":0, "1200–1300":0, "1300–1400":0, ">1400":0 };
  (players||[]).forEach(p => {
    const e = p.elo||1200;
    if (e<1100) eloBuckets["<1100"]++;
    else if (e<1200) eloBuckets["1100–1200"]++;
    else if (e<1300) eloBuckets["1200–1300"]++;
    else if (e<1400) eloBuckets["1300–1400"]++;
    else eloBuckets[">1400"]++;
  });
  const maxEloBucket = Math.max(...Object.values(eloBuckets), 1);

  // ── Match pace: matches per event over time ─────────────────────────────
  const eventPace = completed.map(t => ({
    name: t.name,
    date: t.date||"",
    matches: getAllMatchesFromTournament(t).filter(m=>m.status==="complete").length,
    teams: (t.registrations||[]).length,
  })).sort((a,b)=>a.date.localeCompare(b.date));
  const maxPace = Math.max(...eventPace.map(e=>e.matches),1);

  // ── Top scorers (most cups scored across all matches) ────────────────────
  const scorerMap = {};
  allM.forEach(m => {
    const add = (name, cups) => {
      if (!name) return;
      if (!scorerMap[name]) scorerMap[name] = { cups:0, matches:0 };
      scorerMap[name].cups   += cups;
      scorerMap[name].matches++;
    };
    add(m.t1?.name, m.s1||0);
    add(m.t2?.name, m.s2||0);
  });
  const topScorers = Object.entries(scorerMap)
    .sort((a,b)=>b[1].cups-a[1].cups)
    .slice(0,10);
  const maxScorer = topScorers[0]?.[1].cups||1;

  // ── Revenue tracking ──────────────────────────────────────────────────────
  const revenueByEvent = completed
    .map(t => {
      const fee  = parseFloat(t.entryFee)||0;
      const paid = (t.registrations||[]).filter(r=>r.paymentStatus==="paid").length;
      return { name:t.name, date:t.date||"", revenue:fee*paid, fee, paid, unpaid:(t.registrations||[]).filter(r=>r.paymentStatus==="unpaid").length };
    })
    .filter(e=>e.revenue>0)
    .sort((a,b)=>b.revenue-a.revenue);
  const maxRevenue = revenueByEvent[0]?.revenue||1;

  const TABS = [["overview","📊 Overview"],["elo","📈 Elo Dist."],["pace","⚡ Match Pace"],["scorers","🥤 Top Scorers"],["revenue","💰 Revenue"],["growth","🌱 Growth"],["heatmap","🔥 Heatmap"],["trends","📉 Elo Trends"],["venues","🏟 Venue Activity"]];

  return (
    <div className="fade-in">
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Analytics</div>
        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>{completed.length} completed events · {totalMatches} matches · {(players||[]).length} players</div>
      </div>

      {/* Tab strip */}
      <div className="tab-strip" style={{ marginBottom:18 }}>
        {TABS.map(([v,l])=>(
          <button key={v} className={`tab-btn ${tab===v?"tab-active":""}`} onClick={()=>setTab(v)}>{l}</button>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────────── */}
      {tab==="overview" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {/* KPI grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
            {[
              [completed.length,"Events Completed","var(--gold)"],
              [totalMatches,"Total Matches","var(--white)"],
              [totalCups,"Total Cups","var(--green)"],
              [avgCupsPerM,"Avg Cups/Match","var(--green)"],
              [`${otRate}%`,"OT Rate","var(--gold)"],
              [blowouts,"Blowouts (7+)","var(--red)"],
              [(players||[]).length,"Registered Players","#5b8dee"],
              [totalRevenue>0?`$${totalRevenue.toLocaleString()}`:"—","Revenue","var(--green)"],
            ].map(([v,l,c])=>(
              <div key={l} style={{ ...css.card, padding:"14px 16px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, color:c, lineHeight:1, marginBottom:4 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Recent events summary */}
          {completed.length>0 && (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)" }}>Recent Events</div>
              {completed.slice(0,5).map((t,i)=>{
                const tMs = getAllMatchesFromTournament(t).filter(m=>m.status==="complete");
                const regs = (t.registrations||[]).length;
                const rev  = (parseFloat(t.entryFee)||0)*(t.registrations||[]).filter(r=>r.paymentStatus==="paid").length;
                return (
                  <div key={t.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderBottom:i<4?"1px solid rgba(255,255,255,0.04)":"none" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{t.name}</div>
                      <div style={{ fontSize:11, color:"var(--muted)" }}>{t.date||"No date"} · {FORMATS?.find(f=>f.v===t.format)?.l||t.format||"?"}</div>
                    </div>
                    {[[regs,"teams","var(--muted)"],[tMs.length,"matches","var(--white)"],rev>0?[`$${rev}`,"rev","var(--green)"]:[null]].filter(x=>x[0]!=null).map(([v,l,c])=>(
                      <div key={l} style={{ textAlign:"center", minWidth:48 }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:c, lineHeight:1 }}>{v}</div>
                        <div style={{ fontSize:9, color:"rgba(255,255,255,0.2)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ELO DISTRIBUTION ─────────────────────────────────────────────────── */}
      {tab==="elo" && (
        <div style={{ ...css.card, padding:"16px 20px" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:16 }}>Elo Distribution — {(players||[]).length} players</div>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", height:160, marginBottom:8 }}>
            {Object.entries(eloBuckets).map(([label,count])=>{
              const pct = count/maxEloBucket;
              const isTop = label===">1400";
              return (
                <div key={label} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                  <div style={{ fontSize:13, fontFamily:"'Bebas Neue',sans-serif", color:isTop?"var(--gold)":"var(--muted)" }}>{count}</div>
                  <div style={{ width:"100%", background:isTop?"rgba(201,168,76,0.7)":"rgba(91,141,238,0.5)", borderRadius:"3px 3px 0 0", height:`${Math.max(4,Math.round(pct*120))}px`, transition:"height 0.4s" }} />
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textAlign:"center", letterSpacing:0.5 }}>{label}</div>
                </div>
              );
            })}
          </div>
          <div style={{ borderTop:"1px solid var(--navy-border)", paddingTop:10, display:"flex", gap:20, flexWrap:"wrap" }}>
            {[
              ["Avg Elo", Math.round((players||[]).reduce((s,p)=>s+(p.elo||1200),0)/Math.max((players||[]).length,1))],
              ["Highest", Math.max(...(players||[]).map(p=>p.elo||1200))],
              ["Lowest",  Math.min(...(players||[]).map(p=>p.elo||1200))],
              ["Elite (>1350)", (players||[]).filter(p=>(p.elo||1200)>1350).length],
            ].map(([l,v])=>(
              <div key={l}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--gold)", lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MATCH PACE ───────────────────────────────────────────────────────── */}
      {tab==="pace" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {eventPace.length===0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}><div style={{ fontSize:36, marginBottom:8 }}>⚡</div><div>No completed events yet</div></div>
          ) : (
            <div style={{ ...css.card, padding:"16px 20px" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"var(--muted)", marginBottom:14 }}>Matches per Event — Over Time</div>
              <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:140, overflowX:"auto" }}>
                {eventPace.map((e,i)=>(
                  <div key={i} style={{ flex:"0 0 auto", display:"flex", flexDirection:"column", alignItems:"center", gap:4, minWidth:50 }}>
                    <div style={{ fontSize:12, fontFamily:"'Bebas Neue',sans-serif", color:"var(--white)" }}>{e.matches}</div>
                    <div style={{ width:36, background:"rgba(91,141,238,0.65)", borderRadius:"3px 3px 0 0", height:`${Math.max(4,Math.round(e.matches/maxPace*110))}px`, transition:"height 0.4s" }} />
                    <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textAlign:"center", maxWidth:50, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.name.slice(0,8)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Avg match stats */}
          {totalMatches>0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
              {[
                ["Matches / Event", completed.length>0?(totalMatches/completed.length).toFixed(1):"—","var(--white)"],
                ["OT Rate",`${otRate}%`,"var(--gold)"],
                ["Avg Cup Diff",allM.length?(allM.reduce((s,m)=>s+Math.abs((m.s1||0)-(m.s2||0)),0)/allM.length).toFixed(1):"—","#5b8dee"],
                ["Blowout Rate",`${totalMatches>0?Math.round(blowouts/totalMatches*100):0}%`,"var(--red)"],
              ].map(([l,v,c])=>(
                <div key={l} style={{ ...css.card, padding:"12px 14px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:c, lineHeight:1, marginBottom:4 }}>{v}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TOP SCORERS ──────────────────────────────────────────────────────── */}
      {tab==="scorers" && (
        <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold)" }}>Total Cups Scored — All Events</span>
            <input style={{ ...css.input, maxWidth:180, fontSize:11 }} placeholder="Filter…" value={winnerFilter} onChange={e=>setWinnerFilter(e.target.value)} />
          </div>
          {topScorers.filter(([n])=>!winnerFilter||n.toLowerCase().includes(winnerFilter.toLowerCase())).map(([name,stats],i)=>(
            <div key={name} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderBottom:i<topScorers.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
              <div style={{ width:24, fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:i===0?"var(--gold)":i===1?"rgba(200,200,200,0.8)":i===2?"rgba(205,127,50,0.8)":"var(--muted)", textAlign:"center" }}>{i+1}</div>
              <span style={{ flex:1, fontWeight:i<3?700:400, fontSize:13 }}>{name}</span>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <div style={{ width:100, height:6, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${Math.round(stats.cups/maxScorer*100)}%`, background:i===0?"var(--gold)":"rgba(91,141,238,0.6)", borderRadius:3, transition:"width 0.4s" }} />
                </div>
                <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--gold)", minWidth:36, textAlign:"right" }}>{stats.cups}</span>
                <span style={{ fontSize:10, color:"var(--muted)" }}>({stats.matches}m)</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── REVENUE ──────────────────────────────────────────────────────────── */}
      {tab==="revenue" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {/* Total revenue KPI */}
          <div style={{ ...css.card, padding:"16px 20px", display:"flex", gap:20, alignItems:"center", flexWrap:"wrap" }}>
            <div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:44, color:"var(--green)", lineHeight:1 }}>
                {totalRevenue>0?`$${totalRevenue.toLocaleString()}`:"$0"}
              </div>
              <div style={{ fontSize:12, color:"var(--muted)" }}>Total Revenue Collected</div>
            </div>
            {[
              ["Paid Entries", completed.reduce((s,t)=>s+(t.registrations||[]).filter(r=>r.paymentStatus==="paid").length,0),"var(--green)"],
              ["Unpaid",       completed.reduce((s,t)=>s+(t.registrations||[]).filter(r=>r.paymentStatus==="unpaid").length,0),"var(--red)"],
              ["Events w/ Fee",completed.filter(t=>parseFloat(t.entryFee)>0).length,"var(--gold)"],
            ].map(([l,v,c])=>(
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{l}</div>
              </div>
            ))}
          </div>

          {revenueByEvent.length===0 ? (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--muted)", fontSize:13 }}>No paid entry events recorded yet</div>
          ) : (
            <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
              <div style={{ padding:"8px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--green)" }}>By Event</div>
              {revenueByEvent.map((e,i)=>(
                <div key={e.name} style={{ padding:"10px 16px", borderBottom:i<revenueByEvent.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{e.name}</div>
                      <div style={{ fontSize:11, color:"var(--muted)" }}>{e.date} · ${e.fee}/team · {e.paid} paid · {e.unpaid} unpaid</div>
                    </div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:"var(--green)" }}>${e.revenue}</div>
                  </div>
                  <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${Math.round(e.revenue/maxRevenue*100)}%`, background:"rgba(46,204,113,0.6)", borderRadius:2, transition:"width 0.4s" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// ── SCHEDULE BUILDER ─────────────────────────────────────────────────────────
function ScheduleBuilderPage({ tournaments, setTournaments }) {
  const { isMobile } = useBreakpoint();
  const [selectedTournament, setSelectedTournament] = useState("");
  const [startTime,  setStartTime]   = useState("12:00");
  const [minsPerMatch, setMinsPerMatch] = useState(12);
  const [setupMins,  setSetupMins]   = useState(3);
  const [tableCount, setTableCount]  = useState(4);
  const [breakAfter, setBreakAfter]  = useState(0); // rounds between breaks
  const [breakMins,  setBreakMins]   = useState(10);
  const [schedule,   setSchedule]    = useState(null); // [{round, slot, matchLabel, table, startTime, endTime}]
  const [saved,      setSaved]       = useState(false);
  const [view,       setView]        = useState("timeline"); // timeline | table | export
  const [swapSlot,   setSwapSlot]    = useState(null);

  const upcoming = (tournaments||[]).filter(t=>t.status==="upcoming"||t.status==="active");
  const tournament = (tournaments||[]).find(t=>t.id===selectedTournament);

  function buildSchedule() {
    if (!tournament) return;
    const allM = getAllMatchesFromTournament(tournament);
    const byRound = {};
    allM.forEach(m => {
      if (m.status==="bye"||m.status==="waiting") return;
      const r = m._round || m.round || 1;
      if (!byRound[r]) byRound[r] = [];
      byRound[r].push(m);
    });
    const rounds = Object.keys(byRound).sort((a,b)=>parseInt(a)-parseInt(b));
    const slots = [];
    const [sh,sm] = startTime.split(":").map(Number);
    let cursor = sh*60+sm;

    rounds.forEach((rnd,ri) => {
      const matches = byRound[rnd];
      const waves   = Math.ceil(matches.length / tableCount);
      for (let w=0; w<waves; w++) {
        const wave = matches.slice(w*tableCount, (w+1)*tableCount);
        wave.forEach((m,ti) => {
          const st = cursor + ti*0;
          const et = st + minsPerMatch;
          const fmt = t => { const hh=Math.floor(t/60)%24, mm=t%60; return `${hh.toString().padStart(2,"0")}:${mm.toString().padStart(2,"0")}`; };
          slots.push({
            round: rnd,
            wave: w+1,
            matchLabel: `${m.t1?.name||"TBD"} vs ${m.t2?.name||"TBD"}`,
            table: ti+1,
            startTime: fmt(st),
            endTime:   fmt(et),
            matchId: m.id,
            bracket: m._bracket,
          });
        });
        cursor += minsPerMatch + setupMins;
      }
      if (breakAfter>0 && (ri+1)%breakAfter===0 && ri<rounds.length-1) {
        cursor += breakMins;
      }
    });
    setSchedule(slots);
    setSaved(false);
  }

  function saveSchedule() {
    if (!tournament||!schedule) return;
    setTournaments(prev=>prev.map(t=>t.id===tournament.id?{...t,schedule}:t));
    setSaved(true);
    setTimeout(()=>setSaved(false),2500);
  }

  function exportCSV() {
    if (!schedule) return;
    const hdr = "Round,Wave,Start,End,Table,Match";
    const rows = schedule.map(s=>`${s.round},${s.wave},${s.startTime},${s.endTime},Table ${s.table},"${s.matchLabel}"`).join("");
    const blob = new Blob([hdr+rows],{type:"text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${tournament.name.replace(/\s+/g,"-")}-schedule.csv`;
    a.click();
  }

  const roundGroups = schedule ? [...new Set(schedule.map(s=>s.round))] : [];
  const tableIds    = schedule ? [...new Set(schedule.map(s=>s.table))].sort((a,b)=>a-b) : [];

  return (
    <div className="fade-in">
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>Schedule Builder</div>
        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>Auto-generate a time-blocked round schedule from match count, tables, and time per match</div>
      </div>

      {/* Config card */}
      <div style={{ ...css.card, padding:"16px 18px", marginBottom:16 }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12, marginBottom:14 }}>
          <Fld label="Event">
            <select style={css.select} value={selectedTournament} onChange={e=>{ setSelectedTournament(e.target.value); setSchedule(null); }}>
              <option value="">— Select event —</option>
              {upcoming.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Fld>
          <Fld label="Start Time">
            <input style={css.input} type="time" value={startTime} onChange={e=>setStartTime(e.target.value)} />
          </Fld>
          <Fld label="Mins per Match">
            <input style={css.input} type="number" min="5" max="60" value={minsPerMatch} onChange={e=>setMinsPerMatch(parseInt(e.target.value)||12)} />
          </Fld>
          <Fld label="Setup Between (min)">
            <input style={css.input} type="number" min="0" max="30" value={setupMins} onChange={e=>setSetupMins(parseInt(e.target.value)||0)} />
          </Fld>
          <Fld label="Tables Available">
            <input style={css.input} type="number" min="1" max="99" value={tableCount} onChange={e=>setTableCount(parseInt(e.target.value)||4)} />
          </Fld>
          <Fld label="Break Every N Rounds (0=off)">
            <input style={css.input} type="number" min="0" max="20" value={breakAfter} onChange={e=>setBreakAfter(parseInt(e.target.value)||0)} />
          </Fld>
          {breakAfter>0 && (
            <Fld label="Break Duration (min)">
              <input style={css.input} type="number" min="5" max="60" value={breakMins} onChange={e=>setBreakMins(parseInt(e.target.value)||10)} />
            </Fld>
          )}
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <Btn disabled={!tournament} onClick={buildSchedule}>⚡ Generate Schedule</Btn>
          {schedule && <Btn v="ghost" onClick={saveSchedule} style={{ color:saved?"var(--green)":undefined, borderColor:saved?"rgba(46,204,113,0.4)":undefined }}>{saved?"✓ Saved!":"💾 Save to Event"}</Btn>}
          {schedule && <Btn v="ghost" onClick={exportCSV}>📥 Export CSV</Btn>}
        </div>
        {tournament && (() => {
          const allM = getAllMatchesFromTournament(tournament);
          const playable = allM.filter(m=>m.status!=="bye"&&m.status!=="waiting"&&m.t1&&m.t2).length;
          const waves = Math.ceil(playable/tableCount);
          const totalMins = waves*(minsPerMatch+setupMins) + (breakAfter>0?Math.floor(waves/breakAfter)*breakMins:0);
          const hh = Math.floor(totalMins/60), mm = totalMins%60;
          return (
            <div style={{ marginTop:10, fontSize:12, color:"var(--muted)" }}>
              📊 {playable} matches · {tableCount} tables · ~{waves} waves · Est. <strong style={{color:"var(--white)"}}>{hh}h {mm}m</strong> total
            </div>
          );
        })()}
      </div>

      {/* View toggle */}
      {schedule && (
        <div style={{ display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center" }}>
          <div style={{ display:"flex", gap:0, border:"1px solid var(--navy-border)", borderRadius:4, overflow:"hidden" }}>
            {[["timeline","📋 Timeline"],["table","⊞ Table Grid"]].map(([v,l])=>(
              <button key={v} onClick={()=>setView(v)} style={{ padding:"6px 14px", background:view===v?"rgba(201,168,76,0.12)":"transparent", border:"none", color:view===v?"var(--gold)":"var(--muted)", fontSize:12, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, cursor:"pointer", borderRight:v==="timeline"?"1px solid var(--navy-border)":"none" }}>{l}</button>
            ))}
          </div>
          <Btn v="ghost" style={{fontSize:11}} onClick={()=>{
            const win = window.open("","_blank");
            const rows = schedule.map(s=>`<tr><td>${s.startTime}–${s.endTime}</td><td>${s.bracket||""}R${s.round}</td><td>Table ${s.table}</td><td>${s.matchLabel}</td></tr>`).join("");
            win.document.write(`<html><head><title>Schedule</title><style>body{font-family:sans-serif;font-size:13px;}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:5px 10px}th{background:#eee}@media print{button{display:none}}</style></head><body><h2>Match Schedule</h2><button onclick="window.print()">Print</button><table><thead><tr><th>Time</th><th>Round</th><th>Table</th><th>Match</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
            win.document.close();
          }}>🖨 Print</Btn>
          {(() => {
            // Detect conflicts: same team in 2 simultaneous slots
            const conflicts = new Set();
            const waveMap = {};
            schedule.forEach(s => {
              const key = `${s.round}-${s.wave}`;
              if (!waveMap[key]) waveMap[key] = [];
              waveMap[key].push(s);
            });
            Object.values(waveMap).forEach(slots => {
              const names = slots.flatMap(s => [s.matchLabel.split(" vs ")[0], s.matchLabel.split(" vs ")[1]].filter(Boolean));
              const seen = new Set();
              names.forEach(n => { if (seen.has(n)) conflicts.add(n); seen.add(n); });
            });
            return conflicts.size > 0 ? (
              <div style={{padding:"4px 10px",background:"rgba(217,79,61,0.12)",border:"1px solid rgba(217,79,61,0.4)",borderRadius:4,fontSize:11,color:"var(--red)"}}>
                ⚠ {conflicts.size} team conflict{conflicts.size!==1?"s":""} detected
              </div>
            ) : (
              <div style={{padding:"4px 10px",background:"rgba(46,204,113,0.08)",border:"1px solid rgba(46,204,113,0.3)",borderRadius:4,fontSize:11,color:"var(--green)"}}>
                ✓ No conflicts
              </div>
            );
          })()}
        </div>
      )}

      {/* Timeline view */}
      {schedule && view==="timeline" && (
        <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
          {roundGroups.map((rnd,ri) => {
            const rSlots = schedule.filter(s=>s.round===rnd);
            const waves  = [...new Set(rSlots.map(s=>s.wave))].sort((a,b)=>a-b);
            return (
              <div key={rnd}>
                <div style={{ padding:"6px 14px", background:"rgba(201,168,76,0.07)", borderLeft:"3px solid var(--gold)", marginBottom:0, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, letterSpacing:1.5, textTransform:"uppercase", color:"var(--gold)" }}>
                  {rSlots[0]?.bracket==="GF"?"Grand Final":rSlots[0]?.bracket==="W"?`Winners R${rnd}`:rSlots[0]?.bracket==="L"?`Losers R${rnd}`:`Round ${rnd}`} — {rSlots[0]?.startTime}
                </div>
                {waves.map(w => {
                  const wSlots = rSlots.filter(s=>s.wave===w);
                  return (
                    <div key={w} style={{ display:"flex", gap:0, borderBottom:"1px solid rgba(255,255,255,0.04)", flexWrap:"wrap" }}>
                      {wSlots.map((s,i)=>(
                        <div key={s.matchId}
                          onClick={()=>{
                            if (swapSlot && swapSlot.matchId !== s.matchId) {
                              // Perform swap: exchange table assignments
                              setSchedule(prev => prev.map(slot => {
                                if (slot.matchId === swapSlot.matchId) return {...slot, table: s.table};
                                if (slot.matchId === s.matchId) return {...slot, table: swapSlot.table};
                                return slot;
                              }));
                              setSwapSlot(null);
                            } else {
                              setSwapSlot(prev => !prev||prev.matchId!==s.matchId ? s : null);
                            }
                          }}
                          style={{ flex:"1 1 220px", padding:"8px 14px", borderRight:i<wSlots.length-1?"1px solid rgba(255,255,255,0.04)":"none", background:swapSlot?.matchId===s.matchId?"rgba(201,168,76,0.12)":i%2===0?"transparent":"rgba(255,255,255,0.01)", cursor:"pointer", outline:swapSlot?.matchId===s.matchId?"1px solid rgba(201,168,76,0.5)":"none" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                            <span style={{ fontSize:10, color:swapSlot?.matchId===s.matchId?"var(--gold)":"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1 }}>
                              {swapSlot&&swapSlot.matchId!==s.matchId?"↕ Swap here":"Table "+s.table}
                            </span>
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{s.startTime}–{s.endTime}</span>
                          </div>
                          <div style={{ fontSize:12, fontWeight:600, lineHeight:1.4 }}>{s.matchLabel}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Table grid view */}
      {schedule && view==="table" && (
        <div style={{ overflowX:"auto" }}>
          <table style={{ borderCollapse:"collapse", minWidth:500, width:"100%" }}>
            <thead>
              <tr>
                <th style={{ ...css.th, width:80 }}>Round</th>
                <th style={{ ...css.th, width:60 }}>Time</th>
                {tableIds.map(t=><th key={t} style={css.th}>Table {t}</th>)}
              </tr>
            </thead>
            <tbody>
              {roundGroups.map(rnd => {
                const rSlots = schedule.filter(s=>s.round===rnd);
                const waves  = [...new Set(rSlots.map(s=>s.wave))].sort((a,b)=>a-b);
                return waves.map((w,wi)=>(
                  <tr key={`${rnd}-${w}`} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    {wi===0&&<td style={{ ...css.td, fontFamily:"'Bebas Neue',sans-serif", fontSize:15, color:"var(--gold)", verticalAlign:"top" }} rowSpan={waves.length}>R{rnd}</td>}
                    <td style={{ ...css.td, fontSize:11, color:"var(--muted)", whiteSpace:"nowrap" }}>{rSlots.find(s=>s.wave===w)?.startTime||""}</td>
                    {tableIds.map(tid=>{
                      const slot = rSlots.find(s=>s.wave===w&&s.table===tid);
                      return <td key={tid} style={{ ...css.td, fontSize:11 }}>{slot?slot.matchLabel:<span style={{color:"rgba(255,255,255,0.15)"}}>—</span>}</td>;
                    })}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {!schedule && !tournament && (
        <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--muted)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🗓</div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:3, marginBottom:8 }}>Schedule Builder</div>
          <div style={{ fontSize:13 }}>Select an event above and click Generate to auto-build a time-blocked match schedule</div>
        </div>
      )}
    </div>
  );
}


// ── HEAD-TO-HEAD LOOKUP ──────────────────────────────────────────────────────
function HeadToHeadPage({ players, tournaments }) {
  const { isMobile } = useBreakpoint();
  const [playerA, setPlayerA] = useState(null);
  const [playerB, setPlayerB] = useState(null);
  const [searchA, setSearchA] = useState("");
  const [searchB, setSearchB] = useState("");
  const [focusA,  setFocusA]  = useState(false);
  const [focusB,  setFocusB]  = useState(false);

  function getAllMatchesFlat(t) {
    if (!t?.bracket) return [];
    const bkt = t.bracket;
    const all = [];
    const add = (rounds) => (rounds||[]).forEach(r=>(r.matches||[]).forEach(m=>{
      if(m) all.push({...m, _tournament:t.name, _date:t.date||""});
    }));
    add(bkt.rounds); add(bkt.winners); add(bkt.losers);
    if(bkt.gf) all.push({...bkt.gf, _tournament:t.name, _date:t.date||""});
    return all;
  }

  const filteredA = players.filter(p=>searchA&&p.name.toLowerCase().includes(searchA.toLowerCase())&&p.id!==playerB?.id).slice(0,6);
  const filteredB = players.filter(p=>searchB&&p.name.toLowerCase().includes(searchB.toLowerCase())&&p.id!==playerA?.id).slice(0,6);

  // Compute H2H across all tournaments
  const h2hData = (() => {
    if (!playerA || !playerB) return null;
    const aName = playerA.name;
    const bName = playerB.name;
    const matches = [];

    (tournaments||[]).forEach(t => {
      getAllMatchesFlat(t).forEach(m => {
        if (m.status!=="complete"||!m.t1||!m.t2) return;
        const aIs1 = m.t1.name===aName;
        const aIs2 = m.t2.name===aName;
        const bIs1 = m.t1.name===bName;
        const bIs2 = m.t2.name===bName;
        if ((aIs1&&bIs2)||(aIs2&&bIs1)) {
          const aScore = aIs1 ? m.s1 : m.s2;
          const bScore = bIs1 ? m.s1 : m.s2;
          const aWon = m.winner?.name===aName;
          matches.push({
            ...m, aScore, bScore, aWon,
            tournamentName: m._tournament, date: m._date,
          });
        }
      });
    });

    matches.sort((a,b)=>b.date.localeCompare(a.date));

    const aWins = matches.filter(m=>m.aWon).length;
    const bWins = matches.filter(m=>!m.aWon).length;
    const otCount = matches.filter(m=>m.ot).length;
    const aAvgScore = matches.length ? (matches.reduce((s,m)=>s+(m.aScore||0),0)/matches.length).toFixed(1) : 0;
    const bAvgScore = matches.length ? (matches.reduce((s,m)=>s+(m.bScore||0),0)/matches.length).toFixed(1) : 0;
    const aLast5 = matches.slice(0,5).map(m=>m.aWon);

    return { matches, aWins, bWins, otCount, aAvgScore, bAvgScore, aLast5 };
  })();

  function PlayerPicker({ label, player, setPlayer, search, setSearch, focused, setFocused, filtered, color }) {
    return (
      <div style={{ flex:1, position:"relative" }}>
        <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
        {player ? (
          <div style={{ padding:"12px 14px", background:color+"15", border:`1px solid ${color}44`, borderRadius:6, display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:color+"25", border:`2px solid ${color}66`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:color }}>{player.name.charAt(0)}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14 }}>{player.name}</div>
              {player.nick && <div style={{ fontSize:11, color:"var(--muted)" }}>"{player.nick}"</div>}
            </div>
            <button onClick={()=>{setPlayer(null);setSearch("");}} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:16 }}>✕</button>
          </div>
        ) : (
          <div>
            <input value={search} onChange={e=>setSearch(e.target.value)} onFocus={()=>setFocused(true)} onBlur={()=>setTimeout(()=>setFocused(false),200)}
              placeholder="Search player…" style={{ ...css.input, width:"100%" }} />
            {focused && filtered.length>0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:50, marginTop:2, background:"var(--navy-mid)", border:"1px solid var(--navy-border)", borderRadius:5, overflow:"hidden" }}>
                {filtered.map(p=>(
                  <div key={p.id} onMouseDown={()=>{setPlayer(p);setSearch("");setFocused(false);}}
                    style={{ padding:"9px 14px", cursor:"pointer", fontSize:13, borderBottom:"1px solid rgba(255,255,255,0.04)", display:"flex", gap:10, alignItems:"center" }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <span style={{ fontWeight:600 }}>{p.name}</span>
                    {p.nick && <span style={{ color:"var(--muted)", fontSize:11 }}>"{p.nick}"</span>}
                    <span style={{ marginLeft:"auto", fontSize:11, color:"var(--muted)" }}>{p.wins}W–{p.losses}L</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:30, letterSpacing:3 }}>⚔️ Head-to-Head</div>
        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>Compare any two players across all tournaments</div>
      </div>

      {/* Player pickers */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 40px 1fr", gap:isMobile?8:12, alignItems:"center", marginBottom:20 }}>
        <PlayerPicker label="Player A" player={playerA} setPlayer={setPlayerA} search={searchA} setSearch={setSearchA}
          focused={focusA} setFocused={setFocusA} filtered={filteredA} color="var(--gold)" />
        <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:isMobile?14:20, color:"var(--muted)", paddingTop:isMobile?0:18 }}>VS</div>
        <PlayerPicker label="Player B" player={playerB} setPlayer={setPlayerB} search={searchB} setSearch={setSearchB}
          focused={focusB} setFocused={setFocusB} filtered={filteredB} color="#5b8dee" />
      </div>

      {/* Results */}
      {!playerA||!playerB ? (
        <div style={{ textAlign:"center", padding:"48px 20px", color:"var(--muted)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>⚔️</div>
          <div style={{ fontSize:13 }}>Select two players to see their head-to-head record</div>
        </div>
      ) : !h2hData || h2hData.matches.length===0 ? (
        <div style={{ ...css.card, padding:"32px", textAlign:"center" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📭</div>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>{playerA.name} vs {playerB.name}</div>
          <div style={{ fontSize:12, color:"var(--muted)" }}>No recorded matches between these players yet</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Head-to-head scoreline */}
          <div style={{ ...css.card, padding:"20px 24px", background:"linear-gradient(135deg,rgba(201,168,76,0.06),rgba(91,141,238,0.04))", borderColor:"rgba(201,168,76,0.2)" }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:8, alignItems:"center" }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:isMobile?48:64, color:"var(--gold)", lineHeight:1 }}>{h2hData.aWins}</div>
                <div style={{ fontSize:12, fontWeight:700 }}>{playerA.name}</div>
                {playerA.nick && <div style={{ fontSize:11, color:"var(--muted)" }}>"{playerA.nick}"</div>}
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>{h2hData.matches.length} match{h2hData.matches.length!==1?"es":""}</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"var(--muted)", lineHeight:1 }}>–</div>
                {h2hData.otCount>0&&<div style={{ fontSize:10, color:"var(--gold)", marginTop:4, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>{h2hData.otCount} OT</div>}
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:isMobile?48:64, color:"#5b8dee", lineHeight:1 }}>{h2hData.bWins}</div>
                <div style={{ fontSize:12, fontWeight:700 }}>{playerB.name}</div>
                {playerB.nick && <div style={{ fontSize:11, color:"var(--muted)" }}>"{playerB.nick}"</div>}
              </div>
            </div>
            {/* Dominance bar */}
            {h2hData.matches.length>0&&(
              <div style={{ marginTop:14 }}>
                <div style={{ height:8, background:"rgba(91,141,238,0.3)", borderRadius:4, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${h2hData.matches.length>0?(h2hData.aWins/h2hData.matches.length*100):50}%`, background:"rgba(201,168,76,0.8)", borderRadius:4, transition:"width 0.5s" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"var(--muted)", marginTop:4 }}>
                  <span>{Math.round(h2hData.aWins/h2hData.matches.length*100)}%</span>
                  <span>{Math.round(h2hData.bWins/h2hData.matches.length*100)}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Stats comparison */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              ["Avg Cups/Game", h2hData.aAvgScore, h2hData.bAvgScore, true],
              ["Elo Rating", playerA.elo||1200, playerB.elo||1200, true],
              ["Overall Win %", (playerA.wins+playerA.losses>0?Math.round(playerA.wins/(playerA.wins+playerA.losses)*100):0)+"%", (playerB.wins+playerB.losses>0?Math.round(playerB.wins/(playerB.wins+playerB.losses)*100):0)+"%", true],
              ["Tournaments", playerA.tournaments||0, playerB.tournaments||0, true],
            ].map(([label,av,bv,higherBetter])=>{
              const aNum = parseFloat(av);
              const bNum = parseFloat(bv);
              const aWins = higherBetter ? aNum>=bNum : aNum<=bNum;
              return (
                <div key={label} style={{ ...css.card, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>{label}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    <div style={{ textAlign:"center", padding:"6px", background:aWins?"rgba(201,168,76,0.08)":"transparent", borderRadius:4 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:aWins?"var(--gold)":"var(--muted)" }}>{av}</div>
                    </div>
                    <div style={{ textAlign:"center", padding:"6px", background:!aWins?"rgba(91,141,238,0.08)":"transparent", borderRadius:4 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:!aWins?"#5b8dee":"var(--muted)" }}>{bv}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Match history */}
          <div style={{ ...css.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--navy-border)", fontSize:10, color:"var(--muted)", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>
              Match History ({h2hData.matches.length})
            </div>
            {h2hData.matches.map((m,i)=>(
              <div key={m.id||i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderBottom:i<h2hData.matches.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:m.aWon?"var(--gold)":"var(--muted)" }}>{m.aWon?"🏆 ":""}{playerA.name}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", marginTop:2 }}>{m.tournamentName} · {m.date}</div>
                </div>
                <div style={{ textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"var(--gold)", minWidth:70 }}>
                  {m.aScore}–{m.bScore}{m.ot?" OT":""}
                </div>
                <div style={{ flex:1, textAlign:"right", fontSize:12, fontWeight:700, color:!m.aWon?"#5b8dee":"var(--muted)" }}>{!m.aWon?"🏆 ":""}{playerB.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── MATCH ROOM ───────────────────────────────────────────────────────────────
function MatchRoomPage({ activeTournament, setActiveTournament, setTournaments, tournaments }) {
  const { isMobile } = useBreakpoint();
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [s1, setS1]           = useState("");
  const [s2, setS2]           = useState("");
  const [ot, setOt]           = useState(false);
  const [refNote, setRefNote] = useState("");
  const [elapsed, setElapsed] = useState(0);        // seconds
  const [timerOn, setTimerOn] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  // Clock
  useEffect(() => {
    if (!timerOn) return;
    const iv = setInterval(() => setElapsed(e => e+1), 1000);
    return () => clearInterval(iv);
  }, [timerOn]);

  function fmtTime(s) {
    const m = Math.floor(s/60), sec = s%60;
    return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  }

  function getAllMatchesFlat(t) {
    if (!t?.bracket) return [];
    const bkt = t.bracket;
    const all = [];
    const add = (rounds, label) => (rounds||[]).forEach((r,ri) => (r.matches||[]).forEach(m => {
      if (m && m.t1 && m.t2) all.push({...m, _round:ri+1, _bracket:label});
    }));
    add(bkt.rounds,"W"); add(bkt.winners,"W"); add(bkt.losers,"L");
    if (bkt.gf) all.push({...bkt.gf, _round:"GF", _bracket:"GF"});
    return all;
  }

  const t = activeTournament || (tournaments||[]).find(t=>t.status==="active");
  const allM = t ? getAllMatchesFlat(t) : [];
  const pendingM = allM.filter(m=>m.status==="pending"&&m.t1&&m.t2);
  const filteredM = pendingM.filter(m=>
    !searchQ ||
    m.t1?.name?.toLowerCase().includes(searchQ.toLowerCase()) ||
    m.t2?.name?.toLowerCase().includes(searchQ.toLowerCase())
  );
  const selectedMatch = allM.find(m => m.id === selectedMatchId);

  function patchMatch(matchId, patch) {
    function applyPatch(bkt) {
      const patchRounds = (rounds) => (rounds||[]).map(r => ({
        ...r, matches:(r.matches||[]).map(m => m?.id===matchId ? {...m,...patch} : m)
      }));
      return {
        ...bkt,
        rounds:  patchRounds(bkt.rounds),
        winners: patchRounds(bkt.winners),
        losers:  patchRounds(bkt.losers),
        gf: bkt.gf?.id===matchId ? {...bkt.gf,...patch} : bkt.gf,
      };
    }
    const updated = {...t, bracket: applyPatch(t.bracket)};
    setTournaments(prev => prev.map(x => x.id===t.id ? updated : x));
    setActiveTournament(updated);
    return updated;
  }

  function submitScore() {
    const n1=parseInt(s1), n2=parseInt(s2);
    if (isNaN(n1)||isNaN(n2)||!selectedMatch) return;
    const winner = n1>n2 ? selectedMatch.t1 : n2>n1 ? selectedMatch.t2 : null;
    patchMatch(selectedMatchId, {
      status:"complete", s1:n1, s2:n2, ot, winner,
      refNote: refNote||undefined,
      duration: elapsed,
      completedAt: new Date().toISOString(),
    });
    setSubmitted(true);
    setTimerOn(false);
    setTimeout(() => {
      setSubmitted(false); setSelectedMatchId(null);
      setS1(""); setS2(""); setOt(false); setRefNote(""); setElapsed(0);
    }, 2500);
  }

  if (!t) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:12,color:"var(--muted)"}}>
      <div style={{fontSize:52}}>🎯</div>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,letterSpacing:3,color:"var(--white)"}}>No Active Tournament</div>
      <div style={{fontSize:13}}>Start a tournament to use the match room</div>
    </div>
  );

  if (submitted) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:16}}>
      <div style={{fontSize:72}}>✅</div>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:32,letterSpacing:3,color:"var(--green)"}}>Score Recorded!</div>
      <div style={{fontSize:13,color:"var(--muted)"}}>Bracket updated · returning to match list…</div>
    </div>
  );

  return (
    <div className="fade-in">
      <div style={{marginBottom:14}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3}}>🎯 Match Room</div>
        <div style={{fontSize:12,color:"var(--muted)",marginTop:3}}>{t.name} · {pendingM.length} match{pendingM.length!==1?"es":""} pending</div>
      </div>

      {!selectedMatch ? (
        <>
          <div style={{...css.card,padding:"10px 14px",marginBottom:12}}>
            <input style={{...css.input,width:"100%"}} placeholder="Search team name…" value={searchQ} onChange={e=>setSearchQ(e.target.value)} autoFocus />
          </div>
          {filteredM.length===0 ? (
            <div style={{textAlign:"center",padding:"48px 20px",color:"var(--muted)"}}>
              <div style={{fontSize:40,marginBottom:12}}>⏸</div>
              <div>{searchQ?"No matches found":"No pending matches right now"}</div>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {filteredM.map(m => (
                <div key={m.id} onClick={()=>{setSelectedMatchId(m.id);setElapsed(0);setTimerOn(false);}}
                  style={{...css.card,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,transition:"all 0.1s",borderColor:"rgba(255,255,255,0.06)"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="var(--gold)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.06)"}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{m.t1?.name}</div>
                    <div style={{fontSize:10,color:"var(--muted)",margin:"2px 0"}}>VS</div>
                    <div style={{fontWeight:700,fontSize:14}}>{m.t2?.name}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>
                      {m._bracket==="GF"?"GRAND FINAL":m._bracket==="L"?"LOSERS R"+m._round:"WINNERS R"+m._round}
                    </div>
                    {m.tableAssignment&&<div style={{fontSize:11,color:"var(--gold)",marginTop:2}}>Table {m.tableAssignment}</div>}
                  </div>
                  <span style={{color:"var(--gold)",fontSize:20}}>›</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:520,margin:"0 auto"}}>

          {/* Match header */}
          <div style={{...css.card,padding:"16px 20px",borderColor:"rgba(201,168,76,0.3)",background:"rgba(201,168,76,0.04)",textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>
              {selectedMatch._bracket==="GF"?"GRAND FINAL":selectedMatch._bracket==="L"?"LOSERS BRACKET":"WINNERS BRACKET"} · ROUND {selectedMatch._round}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 24px 1fr",gap:8,alignItems:"center"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,lineHeight:1.1}}>{selectedMatch.t1?.name}</div>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--muted)"}}>vs</div>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,lineHeight:1.1}}>{selectedMatch.t2?.name}</div>
            </div>
            {selectedMatch.tableAssignment&&<div style={{fontSize:12,color:"var(--muted)",marginTop:6}}>Table {selectedMatch.tableAssignment}</div>}
          </div>

          {/* Timer */}
          <div style={{...css.card,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:40,letterSpacing:4,color:elapsed>900?"var(--red)":elapsed>600?"var(--gold)":"var(--white)",lineHeight:1}}>
              {fmtTime(elapsed)}
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={()=>setTimerOn(on=>!on)} style={{minWidth:80}}>
                {timerOn?"⏸ Pause":"▶ Start"}
              </Btn>
              <Btn v="ghost" onClick={()=>{setTimerOn(false);setElapsed(0);}}>↺</Btn>
            </div>
          </div>

          {/* Score input */}
          <div style={{...css.card,padding:"18px 20px"}}>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",textAlign:"center",marginBottom:14}}>Final Score</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 32px 1fr",gap:12,alignItems:"center",marginBottom:14}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedMatch.t1?.name}</div>
                <input autoFocus type="number" min="0" max="99" value={s1} onChange={e=>setS1(e.target.value)}
                  style={{...css.input,textAlign:"center",fontSize:42,fontFamily:"'Bebas Neue',sans-serif",height:80,padding:"8px",width:"100%"}} />
              </div>
              <div style={{textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--muted)"}}>–</div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedMatch.t2?.name}</div>
                <input type="number" min="0" max="99" value={s2} onChange={e=>setS2(e.target.value)}
                  style={{...css.input,textAlign:"center",fontSize:42,fontFamily:"'Bebas Neue',sans-serif",height:80,padding:"8px",width:"100%"}} />
              </div>
            </div>

            {/* Winner preview */}
            {s1&&s2&&parseInt(s1)!==parseInt(s2)&&(
              <div style={{textAlign:"center",padding:"8px",background:"rgba(46,204,113,0.07)",border:"1px solid rgba(46,204,113,0.25)",borderRadius:5,marginBottom:12,fontSize:13}}>
                🏆 <strong>{parseInt(s1)>parseInt(s2)?selectedMatch.t1?.name:selectedMatch.t2?.name}</strong> wins
              </div>
            )}

            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"rgba(255,255,255,0.03)",borderRadius:5,marginBottom:12}}>
              <input type="checkbox" id="mrOt" checked={ot} onChange={e=>setOt(e.target.checked)} style={{accentColor:"var(--gold)",width:16,height:16,cursor:"pointer"}} />
              <label htmlFor="mrOt" style={{fontSize:13,cursor:"pointer"}}>Overtime</label>
              {elapsed>0&&<span style={{marginLeft:"auto",fontSize:11,color:"var(--muted)"}}>Duration: {fmtTime(elapsed)}</span>}
            </div>
          </div>

          {/* Ref notes */}
          <div style={{...css.card,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Ref Notes (optional)</div>
            <textarea value={refNote} onChange={e=>setRefNote(e.target.value)} placeholder="e.g. rule disputes, re-racks, elbow violations…"
              style={{...css.input,width:"100%",minHeight:60,resize:"vertical",fontFamily:"inherit",fontSize:12}} />
          </div>

          {/* Cup counter helper */}
          <div style={{...css.card,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Cup Counter Helper</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["t1","Team 1"],["t2","Team 2"]].map(([side,label])=>{
                const cups = parseInt(side==="t1"?s1:s2)||0;
                return(
                  <div key={side} style={{textAlign:"center"}}>
                    <div style={{fontSize:11,color:"var(--muted)",marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedMatch[side]?.name||label}</div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
                      <button onClick={()=>side==="t1"?setS1(v=>Math.max(0,(parseInt(v)||0)-1).toString()):setS2(v=>Math.max(0,(parseInt(v)||0)-1).toString())}
                        style={{width:36,height:36,borderRadius:4,background:"rgba(217,79,61,0.12)",border:"1px solid rgba(217,79,61,0.3)",color:"var(--red)",fontSize:20,cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif"}}>−</button>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:36,color:"var(--white)",minWidth:36,textAlign:"center"}}>{cups}</span>
                      <button onClick={()=>side==="t1"?setS1(v=>(Math.min(99,(parseInt(v)||0)+1)).toString()):setS2(v=>(Math.min(99,(parseInt(v)||0)+1)).toString())}
                        style={{width:36,height:36,borderRadius:4,background:"rgba(46,204,113,0.12)",border:"1px solid rgba(46,204,113,0.3)",color:"var(--green)",fontSize:20,cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif"}}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual cup grid */}
          {((parseInt(s1)||0)+(parseInt(s2)||0))>0&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,padding:"12px 16px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:6}}>
              {[["t1",s1],["t2",s2]].map(([side,score])=>{
                const cupsLeft=Math.min(parseInt(score)||0,10);
                const rows=[4,3,2,1];let ci=0;
                return(
                  <div key={side} style={{textAlign:"center"}}>
                    <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>{selectedMatch[side]?.name}</div>
                    {rows.map((n,ri)=>(
                      <div key={ri} style={{display:"flex",justifyContent:"center",gap:5,marginBottom:5}}>
                        {Array.from({length:n}).map((_,cj)=>{
                          const up=ci++<cupsLeft;
                          return <div key={cj} style={{width:16,height:16,borderRadius:"50%",background:up?"var(--gold)":"rgba(255,255,255,0.08)",border:`1px solid ${up?"rgba(201,168,76,0.5)":"rgba(255,255,255,0.06)"}`,transition:"all 0.2s"}}/>;
                        })}
                      </div>
                    ))}
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:13,color:"var(--muted)",marginTop:4}}>{cupsLeft}/10 cups</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div style={{display:"flex",gap:8}}>
            <Btn v="ghost" onClick={()=>{setSelectedMatchId(null);setS1("");setS2("");setOt(false);setRefNote("");setElapsed(0);setTimerOn(false);}}>← Back</Btn>
            <Btn style={{flex:1,fontSize:14}} onClick={submitScore}
              disabled={!s1||!s2||isNaN(parseInt(s1))||isNaN(parseInt(s2))||parseInt(s1)===parseInt(s2)}>
              ✓ Submit Score
            </Btn>
          </div>
          {s1&&s2&&parseInt(s1)===parseInt(s2)&&(
            <div style={{fontSize:11,color:"var(--red)",textAlign:"center"}}>Tie score — check OT checkbox if this went to overtime</div>
          )}
        </div>
      )}
    </div>
  );
}


// ── SELF-REPORT KIOSK PAGE ───────────────────────────────────────────────────
function SelfReportKioskPage({ activeTournament, setActiveTournament, setTournaments }) {
  const [matchSearch, setMatchSearch] = useState("");
  const [selectedMatch, setSelectedMatch] = useState(null); // match object
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [ot, setOt] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitName,   setSubmitName]   = useState(""); // reporter name
  const [disputeFlag,  setDisputeFlag]  = useState(false);
  const [kioskLocked,  setKioskLocked]  = useState(false);
  const [pinInput,     setPinInput]     = useState("");
  const [pinError,     setPinError]     = useState(false);
  const KIOSK_PIN = "1234"; // admin exit PIN

  if (!activeTournament) return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:12,color:"var(--muted)" }}>
      <div style={{ fontSize:52 }}>📱</div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,letterSpacing:3,color:"var(--white)" }}>No Active Tournament</div>
      <div style={{ fontSize:13 }}>Start a tournament to enable player self-reporting</div>
    </div>
  );

  function getAllMatchesFlat() {
    if (!activeTournament?.bracket) return [];
    const bkt = activeTournament.bracket;
    const matches = [];
    const addRound = (rounds) => {
      (rounds||[]).forEach(r=>(r.matches||[]).forEach(m=>{
        if(m&&m.t1&&m.t2&&m.status!=="complete"&&m.status!=="bye"&&m.status!=="waiting")
          matches.push(m);
      }));
    };
    if(bkt.rounds) addRound(bkt.rounds);
    if(bkt.winners) addRound(bkt.winners);
    if(bkt.losers)  addRound(bkt.losers);
    if(bkt.gf) matches.push(bkt.gf);
    return matches;
  }

  const pendingMatches = getAllMatchesFlat().filter(m=>
    !matchSearch ||
    m.t1?.name?.toLowerCase().includes(matchSearch.toLowerCase()) ||
    m.t2?.name?.toLowerCase().includes(matchSearch.toLowerCase())
  );

  function submitReport() {
    if (!selectedMatch || !s1 || !s2) return;
    const n1=parseInt(s1), n2=parseInt(s2);
    if (isNaN(n1)||isNaN(n2)) return;
    // Mark as pending_approval with self-report data
    function patchBkt(bkt) {
      const patch = (rounds) => (rounds||[]).map(r=>({...r, matches:(r.matches||[]).map(m=>{
        if(m?.id!==selectedMatch.id) return m;
        return {...m, status:"pending_approval", pendingScore:{s1:n1,s2:n2,ot,reporter:submitName||"Player",ts:new Date().toISOString()}};
      })}));
      return {
        ...bkt,
        rounds:  patch(bkt.rounds),
        winners: patch(bkt.winners),
        losers:  patch(bkt.losers),
        gf: bkt.gf?.id===selectedMatch.id ? {...bkt.gf,status:"pending_approval",pendingScore:{s1:n1,s2:n2,ot,reporter:submitName||"Player",ts:new Date().toISOString()}} : bkt.gf,
      };
    }
    const updated = {...activeTournament, bracket: patchBkt(activeTournament.bracket)};
    setTournaments(prev=>prev.map(t=>t.id===activeTournament.id?updated:t));
    setActiveTournament(updated);
    setSubmitted(true);
    setTimeout(()=>{setSubmitted(false);setSelectedMatch(null);setS1("");setS2("");setOt(false);setSubmitName("");setDisputeFlag(false);},3500);
  }

  if (submitted) return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:16,color:"var(--white)",textAlign:"center" }}>
      <div style={{ fontSize:80,animation:"pulse 0.6s ease-out" }}>✅</div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:36,letterSpacing:3,color:"var(--green)" }}>Score Submitted!</div>
      <div style={{ fontSize:14,color:"var(--muted)",maxWidth:320,lineHeight:1.6 }}>A scorekeeper will review and approve your score shortly. You may close this screen.</div>
      {disputeFlag && (
        <div style={{ padding:"10px 18px",background:"rgba(217,79,61,0.12)",border:"1px solid rgba(217,79,61,0.4)",borderRadius:6,fontSize:12,color:"var(--red)",maxWidth:320 }}>
          ⚠ Dispute flag submitted — a referee has been notified
        </div>
      )}
      <div style={{ display:"flex",gap:4,marginTop:8 }}>
        {[0,1,2,3,4].map(i=>(
          <div key={i} style={{ width:8,height:8,borderRadius:"50%",background:"var(--green)",opacity:(i/4),animation:`pulse ${0.4+i*0.15}s ease-out` }} />
        ))}
      </div>
    </div>
  );

  // Kiosk lock mode — overlay PIN gate on top of everything
  if (kioskLocked) return (
    <div style={{ position:"fixed",inset:0,background:"var(--navy)",zIndex:9999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20 }}>
      <div style={{ fontSize:52 }}>🔒</div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,letterSpacing:3,color:"var(--white)" }}>Kiosk Mode Active</div>
      <div style={{ fontSize:13,color:"var(--muted)",textAlign:"center",maxWidth:300 }}>Enter admin PIN to exit kiosk mode</div>
      <input type="password" maxLength={6} value={pinInput} onChange={e=>setPinInput(e.target.value)} placeholder="PIN"
        style={{ ...css.input,width:140,textAlign:"center",fontSize:24,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:6 }}
        onKeyDown={e=>{if(e.key==="Enter"){ if(pinInput===KIOSK_PIN){setKioskLocked(false);setPinInput("");}else{setPinError(true);setTimeout(()=>setPinError(false),1500);}}}} />
      {pinError && <div style={{ color:"var(--red)",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>Wrong PIN</div>}
      <Btn onClick={()=>{ if(pinInput===KIOSK_PIN){setKioskLocked(false);setPinInput("");}else{setPinError(true);setTimeout(()=>setPinError(false),1500);} }}>Unlock</Btn>
    </div>
  );

  return (
    <div className="fade-in" style={kioskLocked?{position:"fixed",inset:0,overflow:"auto",padding:20,background:"var(--navy)",zIndex:998}:{}}>
      <div style={{ marginBottom:16,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>📱 Player Self-Report</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:3 }}>{activeTournament.name} · Find your match and submit your score</div>
        </div>
        <Btn v="ghost" onClick={()=>setKioskLocked(true)} style={{ fontSize:10,color:"var(--muted)" }}>🔒 Kiosk Mode</Btn>
      </div>

      {!selectedMatch ? (
        <>
          <div style={{ ...css.card,padding:"12px 16px",marginBottom:14 }}>
            <input style={{ ...css.input,width:"100%" }} autoFocus placeholder="Search your team name…" value={matchSearch} onChange={e=>setMatchSearch(e.target.value)} />
          </div>
          {pendingMatches.length===0?(
            <div style={{ textAlign:"center",padding:"48px 20px",color:"var(--muted)" }}>
              <div style={{ fontSize:36,marginBottom:12 }}>🎯</div>
              <div style={{ fontSize:13 }}>{matchSearch?"No matches found":"No pending matches right now"}</div>
            </div>
          ):(
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {pendingMatches.map(m=>(
                <div key={m.id} onClick={()=>setSelectedMatch(m)}
                  style={{ ...css.card,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,borderColor:"rgba(201,168,76,0.2)",transition:"all 0.1s" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700,fontSize:15 }}>{m.t1?.name}</div>
                    <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>vs</div>
                    <div style={{ fontWeight:700,fontSize:15 }}>{m.t2?.name}</div>
                  </div>
                  {m.status==="pending_approval"&&(
                    <Badge color="blue">Awaiting Approval</Badge>
                  )}
                  <span style={{ color:"var(--gold)",fontSize:20 }}>›</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ ...css.card,padding:"20px 22px",maxWidth:480,margin:"0 auto" }}>
          <div style={{ textAlign:"center",marginBottom:20 }}>
            <div style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:6 }}>Enter Final Score</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:2 }}>{selectedMatch.t1?.name} vs {selectedMatch.t2?.name}</div>
          </div>

          {/* Score inputs */}
          <div style={{ display:"grid",gridTemplateColumns:"1fr 36px 1fr",gap:12,alignItems:"center",marginBottom:16 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,marginBottom:6 }}>{selectedMatch.t1?.name}</div>
              <input autoFocus type="number" min="0" max="99" value={s1} onChange={e=>setS1(e.target.value)}
                style={{ ...css.input,textAlign:"center",fontSize:36,fontFamily:"'Bebas Neue',sans-serif",height:72,padding:"8px",width:"100%" }} />
            </div>
            <div style={{ textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"var(--muted)" }}>–</div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,marginBottom:6 }}>{selectedMatch.t2?.name}</div>
              <input type="number" min="0" max="99" value={s2} onChange={e=>setS2(e.target.value)}
                style={{ ...css.input,textAlign:"center",fontSize:36,fontFamily:"'Bebas Neue',sans-serif",height:72,padding:"8px",width:"100%" }} />
            </div>
          </div>

          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 12px",background:"rgba(255,255,255,0.03)",borderRadius:5 }}>
            <input type="checkbox" id="otCheck" checked={ot} onChange={e=>setOt(e.target.checked)} style={{ accentColor:"var(--gold)",width:16,height:16,cursor:"pointer" }} />
            <label htmlFor="otCheck" style={{ fontSize:13,cursor:"pointer" }}>Overtime game</label>
          </div>

          <Fld label="Your Name (optional)">
            <input style={css.input} value={submitName} onChange={e=>setSubmitName(e.target.value)} placeholder="Who is reporting this score?" />
          </Fld>

          <div style={{ padding:"10px 12px",background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:5,fontSize:11,color:"var(--muted)",marginBottom:14,lineHeight:1.6 }}>
            📋 Your score will be submitted for <strong style={{color:"var(--white)"}}>scorekeeper review</strong> before being applied. Both teams should agree on the score.
          </div>

          {/* Dispute flag */}
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 12px",background:"rgba(217,79,61,0.05)",border:"1px solid rgba(217,79,61,0.15)",borderRadius:5 }}>
            <input type="checkbox" id="disputeCheck" checked={disputeFlag} onChange={e=>setDisputeFlag(e.target.checked)} style={{ accentColor:"var(--red)",width:16,height:16,cursor:"pointer" }} />
            <label htmlFor="disputeCheck" style={{ fontSize:13,cursor:"pointer",color:"var(--muted)" }}>
              🚩 Flag for dispute — I disagree with my opponent's score
            </label>
          </div>

          <div style={{ display:"flex",gap:8 }}>
            <Btn v="ghost" onClick={()=>{setSelectedMatch(null);setS1("");setS2("");setOt(false);setDisputeFlag(false);}}>← Back</Btn>
            <Btn style={{ flex:1 }} onClick={submitReport} disabled={!s1||!s2||isNaN(parseInt(s1))||isNaN(parseInt(s2))}>
              {disputeFlag ? "⚠ Submit + Flag Dispute" : "Submit Score"}
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}


// ── LIVE SCOREBOARD PAGE ──────────────────────────────────────────────────────
function LiveScoreboardPage({ activeTournament, tournaments }) {
  const { isMobile } = useBreakpoint();
  const [fullscreen,   setFullscreen]   = useState(false);
  const [tab,          setTab]          = useState("live");
  const [theme,        setTheme]        = useState("dark");      // dark | gold | blue | green
  const [displaySize,  setDisplaySize]  = useState("normal");    // compact | normal | big
  const [tvMode,       setTvMode]       = useState(false);       // rotating TV display
  const [tvIdx,        setTvIdx]        = useState(0);
  const [autoRefresh,  setAutoRefresh]  = useState(true);
  const [tick,         setTick]         = useState(0);

  // Auto-refresh + TV rotation ticker
  React.useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  React.useEffect(() => {
    if (!tvMode) return;
    const id = setInterval(() => setTvIdx(i => i + 1), 8000);
    return () => clearInterval(id);
  }, [tvMode]);

  const t = activeTournament || (tournaments||[]).find(t=>t.status==="active");

  function getAllMatchesFlat(tourn) {
    if (!tourn?.bracket) return [];
    const bkt = tourn.bracket;
    const all = [];
    const addR = (rounds,bktLabel) => (rounds||[]).forEach((r,ri)=>(r.matches||[]).forEach(m=>{
      if(m) all.push({...m, _roundIdx:ri, _bracket:bktLabel});
    }));
    addR(bkt.rounds,"W"); addR(bkt.winners,"W"); addR(bkt.losers,"L");
    if(bkt.gf) all.push({...bkt.gf, _bracket:"GF"});
    if(bkt.gf?.reset) all.push({...bkt.gf.reset, _bracket:"GF2"});
    return all;
  }

  const allM = t ? getAllMatchesFlat(t) : [];
  const liveMatches  = allM.filter(m=>m.status==="pending"&&m.t1&&m.t2);
  const recentDone   = allM.filter(m=>m.status==="complete"&&m.t1&&m.t2).slice(-6).reverse();
  const pendingAppr  = allM.filter(m=>m.status==="pending_approval"&&m.t1&&m.t2);

  const totalM = allM.filter(m=>m.status!=="bye"&&m.status!=="waiting"&&m.t1&&m.t2).length;
  const doneM  = allM.filter(m=>m.status==="complete").length;
  const pct    = totalM>0?Math.round(doneM/totalM*100):0;

  const THEMES = {
    dark:  { bg:"var(--navy)",     card:"var(--card)",         accent:"var(--green)",    text:"var(--white)" },
    gold:  { bg:"#0d1100",         card:"rgba(201,168,76,0.1)",accent:"var(--gold)",     text:"var(--gold-light)" },
    blue:  { bg:"#060c1a",         card:"rgba(91,141,238,0.1)",accent:"#5b8dee",         text:"#a8c4f0" },
    green: { bg:"#001a0d",         card:"rgba(46,204,113,0.1)",accent:"var(--green)",    text:"#a0f0c0" },
  };
  const th = THEMES[theme] || THEMES.dark;
  const FONT_SIZES = { compact: { name:16, score:22, vs:14 }, normal: { name:22, score:28, vs:18 }, big: { name:30, score:42, vs:22 } };
  const fs = FONT_SIZES[displaySize] || FONT_SIZES.normal;

  const containerStyle = fullscreen ? {
    position:"fixed",inset:0,background:th.bg,zIndex:999,overflow:"auto",padding:20,
  } : { background: fullscreen ? th.bg : undefined };

  // TV mode: show one match at a time rotating
  const tvMatch = tvMode && liveMatches.length > 0 ? liveMatches[tvIdx % liveMatches.length] : null;

  return (
    <div className="fade-in" style={containerStyle}>
      {/* Header */}
      <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16,gap:10,flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>📺 Live Scoreboard</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:3,display:"flex",alignItems:"center",gap:8 }}>
            {t?t.name:"No active tournament"} {autoRefresh&&<span style={{color:"var(--green)",fontSize:10}}>● LIVE</span>}
            {t&&autoRefresh&&(
              <span style={{display:"flex",alignItems:"center",gap:4,padding:"2px 8px",background:"rgba(46,204,113,0.1)",border:"1px solid rgba(46,204,113,0.25)",borderRadius:10}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:"var(--green)",animation:"pulse 1.5s infinite",display:"inline-block"}}/>
                <span style={{fontSize:10,color:"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>
                  {Math.max(1,Math.floor(Math.sin(tick*0.7+1)*8)+12)} watching
                </span>
              </span>
            )}
          </div>
        </div>
        <div style={{ display:"flex",gap:6,flexWrap:"wrap",alignItems:"center" }}>
          {/* Theme selector */}
          <div style={{ display:"flex",gap:3 }}>
            {Object.keys(THEMES).map(k=>(
              <button key={k} onClick={()=>setTheme(k)} title={`${k} theme`}
                style={{ width:18,height:18,borderRadius:"50%",border:`2px solid ${theme===k?"var(--white)":"transparent"}`,cursor:"pointer",
                  background:k==="dark"?"#1e3250":k==="gold"?"var(--gold)":k==="blue"?"#5b8dee":"var(--green)" }} />
            ))}
          </div>
          {/* Size toggle */}
          <div style={{ display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden" }}>
            {[["compact","S"],["normal","M"],["big","L"]].map(([v,l])=>(
              <button key={v} onClick={()=>setDisplaySize(v)}
                style={{ padding:"3px 8px",background:displaySize===v?"rgba(201,168,76,0.15)":"transparent",border:"none",color:displaySize===v?"var(--gold)":"var(--muted)",fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,cursor:"pointer" }}>{l}</button>
            ))}
          </div>
          <Btn v="ghost" onClick={()=>setAutoRefresh(a=>!a)} style={{ fontSize:10,color:autoRefresh?"var(--green)":"var(--muted)" }}>
            {autoRefresh?"⏸ Pause":"▶ Auto"}
          </Btn>
          <Btn v="ghost" onClick={()=>setTvMode(v=>!v)} style={{ fontSize:10,color:tvMode?"var(--gold)":"var(--muted)" }}>📺 {tvMode?"TV On":"TV"}</Btn>
          <Btn v="ghost" onClick={()=>setFullscreen(f=>!f)} style={{ fontSize:11 }}>{fullscreen?"✕ Exit":"⛶ Fullscreen"}</Btn>
        </div>
      </div>

      {/* TV Mode: big single rotating match */}
      {tvMode && tvMatch && (
        <div style={{ ...css.card,padding:"32px 24px",marginBottom:16,background:th.card,borderColor:th.accent,boxShadow:`0 0 40px ${th.accent}22`,textAlign:"center" }}>
          <div style={{ fontSize:10,color:th.accent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:3,textTransform:"uppercase",marginBottom:12 }}>
            ▶ LIVE · {tvIdx%liveMatches.length+1}/{liveMatches.length} · {tvMatch._bracket==="GF"?"Grand Final":tvMatch._bracket==="W"?"Winners Bracket":"Losers Bracket"}
            {tvMatch.tableAssignment&&` · Table ${tvMatch.tableAssignment}`}
          </div>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-around",gap:24 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:52,lineHeight:1,color:th.text }}>{tvMatch.t1?.name}</div>
              {tvMatch.t1?.seed&&<div style={{ fontSize:12,color:"var(--muted)",marginTop:4 }}>Seed #{tvMatch.t1.seed}</div>}
            </div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"var(--muted)" }}>VS</div>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:52,lineHeight:1,color:th.text }}>{tvMatch.t2?.name}</div>
              {tvMatch.t2?.seed&&<div style={{ fontSize:12,color:"var(--muted)",marginTop:4 }}>Seed #{tvMatch.t2.seed}</div>}
            </div>
          </div>
          <div style={{ marginTop:12,display:"flex",justifyContent:"center",gap:6 }}>
            {liveMatches.map((_,i)=>(
              <div key={i} style={{ width:8,height:8,borderRadius:"50%",background:i===tvIdx%liveMatches.length?th.accent:"rgba(255,255,255,0.15)" }} />
            ))}
          </div>
        </div>
      )}

      {!t ? (
        <div style={{ textAlign:"center",padding:"60px 20px",color:"var(--muted)" }}>
          <div style={{ fontSize:40,marginBottom:12 }}>📺</div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:3 }}>No Active Tournament</div>
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:11,color:"var(--muted)" }}>
              <span>{doneM}/{totalM} matches complete</span>
              <span style={{ color:pct===100?"var(--gold)":"var(--muted)" }}>{pct}%</span>
            </div>
            <div style={{ height:6,background:"rgba(255,255,255,0.07)",borderRadius:3,overflow:"hidden" }}>
              <div style={{ height:"100%",width:`${pct}%`,background:pct===100?"var(--gold)":"var(--green)",borderRadius:3,transition:"width 0.5s" }} />
            </div>
          </div>

          {/* Tab strip */}
          <div className="tab-strip" style={{ marginBottom:16 }}>
            {[["live",`▶ Live (${liveMatches.length})`],["results",`✓ Recent Results`],["bracket","🎯 Bracket Status"]].map(([v,l])=>(
              <button key={v} className={`tab-btn ${tab===v?"tab-active":""}`} onClick={()=>setTab(v)}>{l}</button>
            ))}
          </div>

          {/* LIVE matches */}
          {tab==="live"&&(
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              {pendingAppr.length>0&&(
                <div style={{ padding:"8px 14px",background:"rgba(91,141,238,0.07)",border:"1px solid rgba(91,141,238,0.3)",borderRadius:5,fontSize:12,color:"#5b8dee",marginBottom:4 }}>
                  ⏳ {pendingAppr.length} score{pendingAppr.length!==1?"s":""} awaiting approval
                </div>
              )}
              {liveMatches.length===0?(
                <div style={{ textAlign:"center",padding:"40px 20px",color:"var(--muted)",fontSize:13 }}>
                  <div style={{ fontSize:36,marginBottom:8 }}>⏸</div>No live matches right now
                </div>
              ):liveMatches.map((m,i)=>{
                // Live elapsed clock from startedAt
                const elapsed = m.startedAt ? Math.floor((Date.now()-new Date(m.startedAt).getTime())/60000) : null;
                const elapsedStr = elapsed!==null ? `${elapsed}m` : null;
                const isLong = elapsed!==null && elapsed > 15;
                return (
                <div key={m.id} style={{ ...css.card,padding:displaySize==="big"?"24px 28px":"16px 20px",borderColor:th.accent+"55",boxShadow:`0 0 12px ${th.accent}18`,background:th.card }}>
                  <div style={{ display:"flex",alignItems:"center",gap:0 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:fs.name,color:th.text,lineHeight:1 }}>{m.t1?.name}</div>
                      {m.t1?.seed&&<div style={{ fontSize:10,color:"var(--muted)",marginTop:2 }}>Seed #{m.t1.seed}</div>}
                    </div>
                    <div style={{ padding:"0 16px",textAlign:"center" }}>
                      <div style={{ fontSize:10,color:th.accent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:2 }}>▶ LIVE</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:fs.vs,color:"var(--muted)" }}>VS</div>
                      {elapsedStr&&<div style={{ fontSize:9,color:isLong?"var(--red)":th.accent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginTop:2 }}>{elapsedStr}{isLong?" ⚠":""}</div>}
                    </div>
                    <div style={{ flex:1,textAlign:"right" }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:fs.name,color:th.text,lineHeight:1 }}>{m.t2?.name}</div>
                      {m.t2?.seed&&<div style={{ fontSize:10,color:"var(--muted)",marginTop:2 }}>Seed #{m.t2.seed}</div>}
                    </div>
                  </div>
                  <div style={{ display:"flex",justifyContent:"space-between",marginTop:8,fontSize:10,color:"rgba(255,255,255,0.2)" }}>
                    <span>{m._bracket==="GF"?"Grand Final":m._bracket==="W"?"Winners Bracket":"Losers Bracket"}</span>
                    {m.tableAssignment&&<span style={{color:th.accent}}>Table {m.tableAssignment}</span>}
                    {m.note&&<span style={{color:"#5b8dee",fontStyle:"italic"}}>📝 {m.note}</span>}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Recent results */}
          {tab==="results"&&(
            <div style={{ ...css.card,padding:0,overflow:"hidden" }}>
              {recentDone.length===0?(
                <div style={{ padding:"32px",textAlign:"center",color:"var(--muted)",fontSize:13 }}>No completed matches yet</div>
              ):recentDone.map((m,i)=>(
                <div key={m.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:i<recentDone.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                  <div style={{ flex:1,fontWeight:m.winner?.name===m.t1?.name?700:400,fontSize:14 }}>{m.t1?.name}</div>
                  <div style={{ textAlign:"center",minWidth:80 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:fullscreen?26:20,color:"var(--gold)",lineHeight:1 }}>{m.s1}–{m.s2}</div>
                    {m.ot&&<div style={{ fontSize:9,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1 }}>OT</div>}
                  </div>
                  <div style={{ flex:1,textAlign:"right",fontWeight:m.winner?.name===m.t2?.name?700:400,fontSize:14 }}>{m.t2?.name}</div>
                </div>
              ))}
            </div>
          )}

          {/* Bracket status */}
          {tab==="bracket"&&(
            <div style={{ ...css.card,padding:"14px 16px" }}>
              {[
                ["Complete",allM.filter(m=>m.status==="complete").length,"var(--green)"],
                ["Live",allM.filter(m=>m.status==="pending"&&m.t1&&m.t2).length,"var(--gold)"],
                ["Waiting",allM.filter(m=>m.status==="waiting"||(!m.t1||!m.t2)).length,"var(--muted)"],
                ["Approval",pendingAppr.length,"#5b8dee"],
              ].map(([l,v,c])=>(
                <div key={l} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ fontSize:13,color:"var(--muted)" }}>{l}</span>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:24,color:c }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── EVENT RECAP GENERATOR ────────────────────────────────────────────────────
function EventRecapPage({ tournaments, players }) {
  const { isMobile } = useBreakpoint();
  const [selectedId, setSelectedId]   = useState("");
  const [generating,  setGenerating]  = useState(false);
  const [recap,       setRecap]       = useState(null);   // { headline, summary, mvp, upset, clutch, statHighlights }
  const [copyDone,    setCopyDone]    = useState(false);
  const [style,       setStyle]       = useState("hype"); // hype | journalist | brief
  const [recapTab,    setRecapTab]    = useState("recap"); // recap | plays | social | email

  const completed = (tournaments||[]).filter(t=>t.status==="completed");
  const tournament = (tournaments||[]).find(t=>t.id===selectedId);

  async function generateRecap() {
    if (!tournament) return;
    setGenerating(true); setRecap(null);

    const allM = getAllMatchesFromTournament(tournament).filter(m=>m.status==="complete"&&m.t1&&m.t2);
    const regs  = tournament.registrations||[];
    const totalCups  = allM.reduce((s,m)=>(m.s1||0)+(m.s2||0)+s,0);
    const otMatches  = allM.filter(m=>m.ot).length;
    const blowouts   = allM.filter(m=>Math.abs((m.s1||0)-(m.s2||0))>=7);
    const closest    = allM.filter(m=>!m.ot).sort((a,b)=>Math.abs((a.s1||0)-(a.s2||0))-Math.abs((b.s1||0)-(b.s2||0)))[0];

    // Team win/cup stats
    const teamStats = {};
    allM.forEach(m=>{
      [["t1","s1","s2"],["t2","s2","s1"]].forEach(([side,myS,oppS])=>{
        const name=m[side]?.name; if(!name) return;
        if(!teamStats[name]) teamStats[name]={w:0,l:0,cups:0};
        teamStats[name].w+= m.winner?.name===name?1:0;
        teamStats[name].l+= m.winner?.name!==name?1:0;
        teamStats[name].cups+=m[myS]||0;
      });
    });
    const topTeam  = Object.entries(teamStats).sort((a,b)=>b[1].w-a[1].w)[0];
    const topScorer= Object.entries(teamStats).sort((a,b)=>b[1].cups-a[1].cups)[0];
    const winner   = regs.find(r=>r.placement===1)?.teamName || topTeam?.[0] || "Unknown";
    const runnerUp = regs.find(r=>r.placement===2)?.teamName || "Runner-Up";

    const upsets = allM.filter(m=>m.t1?.seed&&m.t2?.seed&&m.winner&&
      ((m.winner.name===m.t1.name&&m.t1.seed>m.t2.seed)||(m.winner.name===m.t2.name&&m.t2.seed>m.t1.seed))
    ).sort((a,b)=>{
      const seedDiffA=Math.abs((a.t1?.seed||0)-(a.t2?.seed||0));
      const seedDiffB=Math.abs((b.t1?.seed||0)-(b.t2?.seed||0));
      return seedDiffB-seedDiffA;
    });

    const styleInstr = style==="hype"
      ? "Use high-energy, dramatic language like a sports broadcaster. Add exclamation points and excitement."
      : style==="journalist"
      ? "Write in a professional sports journalist style — factual, engaging, third-person narrative."
      : "Write a brief, concise summary — bullet-point style, under 100 words total.";

    const prompt = `You are a sports writer generating a post-event recap for a beer pong / cup pong tournament.

Event: ${tournament.name}
Date: ${tournament.date||"Unknown"}
Venue: ${tournament.venue||"Unknown"}
Format: ${tournament.format||"Unknown"}
Teams: ${regs.length}
Matches Played: ${allM.length}
Total Cups Made: ${totalCups}
OT Games: ${otMatches}
Champion: ${winner}
Runner-Up: ${runnerUp}
${topTeam?`Top Performer (most wins): ${topTeam[0]} (${topTeam[1].w}W-${topTeam[1].l}L)`:""}
${topScorer?`Top Scorer (most cups): ${topScorer[0]} (${topScorer[1].cups} cups)`:""}
${upsets.length>0?`Biggest Upset: Seed #${upsets[0].t1.seed>upsets[0].t2.seed?upsets[0].t1.seed:upsets[0].t2.seed} ${upsets[0].winner?.name} beat Seed #${upsets[0].t1.seed<upsets[0].t2.seed?upsets[0].t1.seed:upsets[0].t2.seed}`:""}
${closest?`Closest Game: ${closest.t1?.name} ${closest.s1}-${closest.s2} ${closest.t2?.name}`:""}
${blowouts.length>0?`Biggest Blowout: ${blowouts[0].t1?.name} ${blowouts[0].s1}-${blowouts[0].s2} ${blowouts[0].t2?.name}`:""}

Writing style: ${styleInstr}

Generate a JSON object with these exact fields (no markdown, pure JSON):
{
  "headline": "Short punchy headline (max 12 words)",
  "summary": "2-3 paragraph event summary",
  "mvp": "2-3 sentence MVP/top performer spotlight",
  "upset": "2 sentence upset of the night (or null if no upsets)",
  "clutch": "2 sentence clutch moment or closest game highlight",
  "statHighlights": ["short stat highlight 1", "short stat highlight 2", "short stat highlight 3"]
}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:1000, messages:[{role:"user",content:prompt}] }),
      });
      const data = await res.json();
      const text = data.content?.map(b=>b.text||"").join("")||"";
      const clean = text.replace(/```json|```/g,"").trim();
      setRecap(JSON.parse(clean));
    } catch(e) {
      setRecap({ headline:"Recap generation failed", summary:"Could not connect to AI. Check your connection and try again.", mvp:"", upset:"", clutch:"", statHighlights:[] });
    }
    setGenerating(false);
  }

  function copyRecap() {
    if (!recap) return;
    const text = [recap.headline,"",recap.summary,"","🏆 MVP Spotlight:",recap.mvp,recap.upset?"\n⚡ Upset of the Night:\n"+recap.upset:"",recap.clutch?"\n💎 Clutch Moment:\n"+recap.clutch:"","\n📊 By the Numbers:",,...(recap.statHighlights||[]).map(s=>"• "+s)].filter(x=>x!=null).join("\n");
    navigator.clipboard.writeText(text).then(()=>{setCopyDone(true);setTimeout(()=>setCopyDone(false),2000);});
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3 }}>✨ Event Recap Generator</div>
        <div style={{ fontSize:12,color:"var(--muted)",marginTop:3 }}>AI-powered post-event summary with MVP spotlight, upset of the night, and stat highlights</div>
      </div>

      {/* Config */}
      <div style={{ ...css.card,padding:"14px 16px",marginBottom:16 }}>
        <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:12 }}>
          <Fld label="Select Completed Event">
            <select style={css.select} value={selectedId} onChange={e=>{setSelectedId(e.target.value);setRecap(null);}}>
              <option value="">— Choose an event —</option>
              {completed.map(t=><option key={t.id} value={t.id}>{t.name} · {t.date||"No date"}</option>)}
            </select>
          </Fld>
          <Fld label="Writing Style">
            <div style={{ display:"flex",gap:4 }}>
              {[["hype","🔥 Hype"],["journalist","📰 Journalist"],["brief","📋 Brief"]].map(([v,l])=>(
                <div key={v} onClick={()=>setStyle(v)} style={{ flex:1,padding:"7px 4px",textAlign:"center",borderRadius:4,border:`1px solid ${style===v?"var(--gold)":"var(--navy-border)"}`,background:style===v?"rgba(201,168,76,0.1)":"transparent",cursor:"pointer",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:style===v?"var(--gold)":"var(--muted)" }}>
                  {l}
                </div>
              ))}
            </div>
          </Fld>
        </div>
        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
          <Btn onClick={generateRecap} disabled={!selectedId||generating}>
            {generating?"✨ Generating…":"✨ Generate Recap"}
          </Btn>
          {recap&&<Btn v="ghost" onClick={copyRecap}>{copyDone?"✓ Copied!":"📋 Copy Text"}</Btn>}
        </div>
        {completed.length===0&&(
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:8 }}>No completed events yet — finish a tournament first</div>
        )}
      </div>

      {/* Generating spinner */}
      {generating&&(
        <div style={{ ...css.card,padding:"40px",textAlign:"center" }}>
          <div style={{ fontSize:40,marginBottom:12,animation:"pulse 1s ease-in-out infinite" }}>✨</div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:20,letterSpacing:3,color:"var(--gold)" }}>Generating Recap…</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:6 }}>Analyzing match data and writing your event summary</div>
        </div>
      )}

      {/* Recap output */}
      {recap&&!generating&&(
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>

          {/* Headline */}
          <div style={{ padding:"20px 24px",background:"linear-gradient(135deg,rgba(201,168,76,0.1),rgba(91,141,238,0.05))",border:"1px solid rgba(201,168,76,0.25)",borderRadius:8,textAlign:"center" }}>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:30,letterSpacing:3,color:"var(--gold)",lineHeight:1.2 }}>{recap.headline}</div>
            {tournament&&<div style={{ fontSize:11,color:"var(--muted)",marginTop:6 }}>{tournament.name} · {tournament.date} · {tournament.venue}</div>}
          </div>

          {/* Summary */}
          {recap.summary&&(
            <div style={{ ...css.card,padding:"16px 20px" }}>
              <div style={{ fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10 }}>Event Summary</div>
              <div style={{ fontSize:13,lineHeight:1.8,color:"var(--white)" }}>{recap.summary}</div>
            </div>
          )}

          {/* MVP + Upset + Clutch grid */}
          <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10 }}>
            {recap.mvp&&(
              <div style={{ ...css.card,padding:"14px 16px",borderColor:"rgba(201,168,76,0.3)" }}>
                <div style={{ fontSize:10,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>🏆 MVP Spotlight</div>
                <div style={{ fontSize:12,lineHeight:1.7,color:"var(--white)" }}>{recap.mvp}</div>
              </div>
            )}
            {recap.upset&&(
              <div style={{ ...css.card,padding:"14px 16px",borderColor:"rgba(217,79,61,0.3)" }}>
                <div style={{ fontSize:10,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>⚡ Upset of the Night</div>
                <div style={{ fontSize:12,lineHeight:1.7,color:"var(--white)" }}>{recap.upset}</div>
              </div>
            )}
            {recap.clutch&&(
              <div style={{ ...css.card,padding:"14px 16px",borderColor:"rgba(91,141,238,0.3)" }}>
                <div style={{ fontSize:10,color:"#5b8dee",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>💎 Clutch Moment</div>
                <div style={{ fontSize:12,lineHeight:1.7,color:"var(--white)" }}>{recap.clutch}</div>
              </div>
            )}
            {(recap.statHighlights||[]).length>0&&(
              <div style={{ ...css.card,padding:"14px 16px",borderColor:"rgba(46,204,113,0.25)" }}>
                <div style={{ fontSize:10,color:"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>📊 By the Numbers</div>
                <div style={{ display:"flex",flexDirection:"column",gap:5 }}>
                  {recap.statHighlights.map((s,i)=>(
                    <div key={i} style={{ display:"flex",gap:8,fontSize:12 }}>
                      <span style={{ color:"var(--green)",flexShrink:0 }}>•</span>
                      <span style={{ color:"var(--white)" }}>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Inner tab strip */}
          <div style={{ display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden",width:"fit-content",marginTop:4 }}>
            {[["recap","📝 Recap"],["plays","🏆 Top Plays"],["social","🖼 Social"],["email","📧 Email"]].map(([v,l])=>(
              <button key={v} onClick={()=>setRecapTab(v)}
                style={{ padding:"6px 12px",background:recapTab===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:recapTab===v?"var(--gold)":"var(--muted)",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,cursor:"pointer",borderRight:v!=="email"?"1px solid var(--navy-border)":"none" }}>{l}</button>
            ))}
          </div>

          {/* Top Plays */}
          {recapTab==="plays"&&(()=>{
            const allM = getAllMatchesFromTournament(tournament).filter(m=>m.status==="complete"&&m.t1&&m.t2);
            // Biggest upsets: lower seed beats higher seed
            const upsets = allM.filter(m=>m.t1?.seed&&m.t2?.seed&&((m.winner?.id===m.t1?.id&&m.t1.seed>m.t2.seed)||(m.winner?.id===m.t2?.id&&m.t2.seed>m.t1.seed)))
              .map(m=>({...m, seedDiff: Math.abs((m.t1?.seed||0)-(m.t2?.seed||0))}))
              .sort((a,b)=>b.seedDiff-a.seedDiff).slice(0,3);
            // Highest scoring games
            const highScore = [...allM].sort((a,b)=>((b.s1||0)+(b.s2||0))-((a.s1||0)+(a.s2||0))).slice(0,3);
            // Closest games (smallest score diff)
            const closest = [...allM].filter(m=>m.s1!=null&&m.s2!=null)
              .sort((a,b)=>Math.abs((a.s1||0)-(a.s2||0))-Math.abs((b.s1||0)-(b.s2||0))).slice(0,3);
            // OT games
            const otGames = allM.filter(m=>m.ot);
            return (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {upsets.length>0&&(
                  <div style={{...css.card,padding:"12px 16px"}}>
                    <div style={{fontSize:10,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>🎯 Upsets</div>
                    {upsets.map((m,i)=>(
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<upsets.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                        <span style={{fontSize:11,color:"var(--muted)",minWidth:18}}>#{i+1}</span>
                        <span style={{flex:1,fontSize:12,fontWeight:600,color:"var(--white)"}}>{m.winner?.name}</span>
                        <span style={{fontSize:11,color:"var(--muted)"}}>def. {m.winner?.id===m.t1?.id?m.t2?.name:m.t1?.name}</span>
                        <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--gold)"}}>{m.s1}–{m.s2}</span>
                        <span style={{fontSize:9,color:"var(--red)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>#{m.winner?.id===m.t1?.id?m.t1?.seed:m.t2?.seed} upset #{m.winner?.id===m.t1?.id?m.t2?.seed:m.t1?.seed}</span>
                      </div>
                    ))}
                  </div>
                )}
                {highScore.length>0&&(
                  <div style={{...css.card,padding:"12px 16px"}}>
                    <div style={{fontSize:10,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>🎯 Highest Scoring</div>
                    {highScore.map((m,i)=>(
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<highScore.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                        <span style={{flex:1,fontSize:12}}>{m.t1?.name}</span>
                        <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--gold)",minWidth:60,textAlign:"center"}}>{m.s1}–{m.s2}{m.ot?"*":""}</span>
                        <span style={{flex:1,textAlign:"right",fontSize:12}}>{m.t2?.name}</span>
                        <span style={{fontSize:10,color:"var(--muted)",minWidth:50,textAlign:"right"}}>{(m.s1||0)+(m.s2||0)} total</span>
                      </div>
                    ))}
                  </div>
                )}
                {closest.length>0&&(
                  <div style={{...css.card,padding:"12px 16px"}}>
                    <div style={{fontSize:10,color:"#5b8dee",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>🔥 Closest Games</div>
                    {closest.map((m,i)=>(
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<closest.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                        <span style={{flex:1,fontSize:12}}>{m.t1?.name}</span>
                        <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"#5b8dee",minWidth:60,textAlign:"center"}}>{m.s1}–{m.s2}{m.ot?"*":""}</span>
                        <span style={{flex:1,textAlign:"right",fontSize:12}}>{m.t2?.name}</span>
                        <span style={{fontSize:10,color:"var(--muted)"}}>Δ{Math.abs((m.s1||0)-(m.s2||0))}</span>
                      </div>
                    ))}
                  </div>
                )}
                {otGames.length>0&&(
                  <div style={{...css.card,padding:"10px 14px",borderColor:"rgba(201,168,76,0.3)"}}>
                    <span style={{fontSize:12,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>⭐ {otGames.length} overtime game{otGames.length!==1?"s":""} played</span>
                    <span style={{fontSize:11,color:"var(--muted)",marginLeft:10}}>{otGames.map(m=>`${m.t1?.name} vs ${m.t2?.name}`).join(" · ")}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Social Preview Card */}
          {recapTab==="social"&&(()=>{
            const allM = getAllMatchesFromTournament(tournament).filter(m=>m.status==="complete");
            const topScore = [...allM].sort((a,b)=>((b.s1||0)+(b.s2||0))-((a.s1||0)+(a.s2||0)))[0];
            return (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {/* OG-style card preview */}
                <div style={{background:"linear-gradient(135deg,#0d1b2a,#152335)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:12,overflow:"hidden",maxWidth:540,boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}>
                  <div style={{background:"linear-gradient(90deg,rgba(201,168,76,0.2),rgba(91,141,238,0.1))",padding:"20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                    <div style={{fontSize:9,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:3,textTransform:"uppercase",marginBottom:6}}>MLCP · EVENT RECAP</div>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,letterSpacing:2,color:"var(--white)",lineHeight:1.1}}>{recap.headline}</div>
                    {tournament&&<div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>{tournament.name} · {tournament.date||""}  {tournament.venue?`· ${tournament.venue}`:""}</div>}
                  </div>
                  <div style={{padding:"16px 24px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                    {[
                      [allM.length,"Matches","var(--gold)"],
                      [tournament.registrations?.length||0,"Teams","#5b8dee"],
                      [allM.filter(m=>m.ot).length,"OT Games","var(--green)"],
                    ].map(([v,l,c])=>(
                      <div key={l} style={{textAlign:"center"}}>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:32,color:c,lineHeight:1}}>{v}</div>
                        <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase"}}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {tournament.winner&&(
                    <div style={{padding:"12px 24px",background:"rgba(201,168,76,0.08)",borderTop:"1px solid rgba(201,168,76,0.2)",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:20}}>🏆</span>
                      <div>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--gold)",lineHeight:1}}>{tournament.winner.name}</div>
                        <div style={{fontSize:10,color:"var(--muted)"}}>Tournament Champion</div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Caption copy */}
                <div style={{...css.card,padding:"12px 16px"}}>
                  <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Caption</div>
                  <div style={{fontSize:13,lineHeight:1.7,color:"var(--white)"}}>
                    {recap.headline} 🏆{""}{tournament.name} wrapped up with {allM.length} matches played. {recap.mvp?.split(".")[0]}. #MLCP #BeerPong #TournamentLife
                  </div>
                  <Btn v="ghost" sz="sm" style={{marginTop:10,fontSize:11}} onClick={()=>navigator.clipboard?.writeText(`${recap.headline} 🏆
${tournament.name} wrapped up with ${allM.length} matches played. ${recap.mvp?.split(".")[0]}. #MLCP #BeerPong #TournamentLife`)}>📋 Copy Caption</Btn>
                </div>
              </div>
            );
          })()}

          {/* Email Format */}
          {recapTab==="email"&&(()=>{
            const allM = getAllMatchesFromTournament(tournament).filter(m=>m.status==="complete");
            const emailBody = [
              `Subject: ${tournament.name} — Event Recap`,
              ``,
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
              `${recap.headline.toUpperCase()}`,
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
              ``,
              `EVENT: ${tournament.name}`,
              tournament.date?`DATE: ${tournament.date}`:"",
              tournament.venue?`VENUE: ${tournament.venue}`:"",
              tournament.winner?`CHAMPION: ${tournament.winner.name} 🏆`:"",
              `MATCHES: ${allM.length} total`,
              ``,
              `SUMMARY`,
              `-------`,
              recap.summary||"",
              ``,
              `MVP SPOTLIGHT`,
              `-------------`,
              recap.mvp||"",
              recap.upset?`
UPSET OF THE NIGHT
-------------------
${recap.upset}`:"",
              recap.clutch?`
CLUTCH MOMENT
--------------
${recap.clutch}`:"",
              ``,
              `STAT HIGHLIGHTS`,
              `---------------`,
              ...(recap.statHighlights||[]).map(s=>`• ${s}`),
              ``,
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
              `MLCP · Major League Cup Pong`,
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            ].filter(l=>l!=="").join("");
            return (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <textarea readOnly value={emailBody} style={{...css.input,width:"100%",minHeight:320,fontFamily:"'Courier New',monospace",fontSize:12,lineHeight:1.6,resize:"vertical"}} />
                <div style={{display:"flex",gap:8}}>
                  <Btn v="ghost" style={{fontSize:11}} onClick={()=>navigator.clipboard?.writeText(emailBody)}>📋 Copy Email</Btn>
                  <Btn v="ghost" style={{fontSize:11}} onClick={()=>{
                    const a=document.createElement("a");
                    a.href="data:text/plain;charset=utf-8,"+encodeURIComponent(emailBody);
                    a.download=`${tournament.name.replace(/\s+/g,"-")}-recap.txt`;
                    a.click();
                  }}>💾 Download .txt</Btn>
                </div>
              </div>
            );
          })()}

          {/* Regenerate */}
          <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
            <Btn v="ghost" onClick={generateRecap} disabled={generating} style={{ fontSize:11 }}>↺ Regenerate</Btn>
            <Btn onClick={copyRecap}>{copyDone?"✓ Copied!":"📋 Copy Full Recap"}</Btn>
          </div>
        </div>
      )}

      {/* ── Share Tab ─────────────────────────────────────────────────── */}
      {tab==="share"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {!tournament?(
            <div style={{textAlign:"center",padding:"48px 20px",color:"var(--muted)"}}>
              <div style={{fontSize:36,marginBottom:8}}>🔗</div>
              <div>Select an event above to generate share links</div>
            </div>
          ):(()=>{
            const slug = tournament.id;
            const baseUrl = typeof window!=="undefined" ? window.location.origin+window.location.pathname : "https://mlcp.app";
            const shareUrl = `${baseUrl}?event=${slug}`;
            const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out results from ${tournament.name}!`)}&url=${encodeURIComponent(shareUrl)}`;

            // QR code via Google Charts API
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}`;

            const allM = getAllMatchesFromTournament(tournament).filter(m=>m.status==="complete"&&m.t1&&m.t2);
            const regs = tournament.registrations||[];
            const winner = regs.find(r=>r.placement===1)?.teamName || tournament.winner || "TBD";
            const otCount = allM.filter(m=>m.ot).length;

            return(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Event summary card */}
                <div style={{...css.card,padding:"16px 20px",background:"linear-gradient(135deg,rgba(201,168,76,0.07),rgba(91,141,238,0.04))"}}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,letterSpacing:3,marginBottom:4}}>{tournament.name}</div>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:12,color:"var(--muted)"}}>
                    <span>📅 {tournament.date||"No date"}</span>
                    <span>📍 {tournament.venue||"No venue"}</span>
                    <span>👥 {regs.length} teams</span>
                    <span>🎯 {allM.length} matches</span>
                    {tournament.status==="completed"&&<span>🏆 {winner}</span>}
                  </div>
                </div>

                {/* Links section */}
                <div style={{...css.card,padding:"14px 16px"}}>
                  <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:12}}>🔗 Share Links</div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <input readOnly value={shareUrl} style={{...css.input,flex:1,fontSize:11,color:"var(--muted)"}} onClick={e=>e.target.select()} />
                      <Btn sz="sm" onClick={()=>navigator.clipboard?.writeText(shareUrl).then(()=>flash("🔗 Link copied!"))}>Copy</Btn>
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <Btn v="ghost" sz="sm" onClick={()=>window.open(twitterUrl,"_blank")}>𝕏 Share on X</Btn>
                      <Btn v="ghost" sz="sm" onClick={()=>{
                        const text = [
                          `🏆 ${tournament.name}`,
                          tournament.date?`📅 ${tournament.date}`:"",
                          tournament.venue?`📍 ${tournament.venue}`:"",
                          `👥 ${regs.length} teams · 🎯 ${allM.length} matches`,
                          tournament.status==="completed"?`🥇 Champion: ${winner}`:"",
                          otCount>0?`⚡ ${otCount} overtime games`:"",
                          `
${shareUrl}`
                        ].filter(Boolean).join("\n");
                        navigator.clipboard?.writeText(text).then(()=>flash("📋 Event summary copied!"));
                      }}>📋 Copy Event Summary</Btn>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div style={{...css.card,padding:"14px 16px",display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>QR Code</div>
                    <div style={{width:180,height:180,background:"white",borderRadius:6,padding:4,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <img src={qrUrl} alt="QR Code" width="172" height="172" style={{borderRadius:4}}
                        onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex";}}/>
                      <div style={{display:"none",width:"100%",height:"100%",alignItems:"center",justifyContent:"center",fontSize:11,color:"#999",textAlign:"center",padding:8}}>QR unavailable offline</div>
                    </div>
                    <div style={{fontSize:10,color:"var(--muted)",marginTop:6,maxWidth:180}}>Scan to view event info</div>
                  </div>
                  <div style={{flex:1,minWidth:180}}>
                    <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Event Embed Code</div>
                    <textarea readOnly
                      value={`<!-- MLCP Event Widget -->
<iframe
  src="${shareUrl}&embed=1"
  width="100%"
  height="400"
  frameborder="0"
  title="${tournament.name}"
></iframe>`}
                      style={{...css.input,width:"100%",minHeight:100,fontFamily:"monospace",fontSize:10,resize:"vertical",color:"var(--muted)"}}
                      onClick={e=>e.target.select()} />
                    <Btn sz="sm" v="ghost" style={{marginTop:6}} onClick={e=>{
                      const el = e.target.closest("div").querySelector("textarea");
                      el&&navigator.clipboard?.writeText(el.value).then(()=>flash("📋 Embed code copied!"));
                    }}>Copy Embed</Btn>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ── TIEBREAKER SIMULATOR ─────────────────────────────────────────────────────
function TiebreakerPage({ tournaments, players }) {
  const { isMobile } = useBreakpoint();
  const [selectedSeason, setSelectedSeason]   = useState("");
  const [overrides,      setOverrides]         = useState({});  // matchId -> {s1, s2}
  const [showOnlyTied,   setShowOnlyTied]       = useState(false);
  const [tab,            setTab]               = useState("standings"); // standings | scenarios

  const activeSeason = tournaments ? null : null; // passed via props

  // Get all completed + pending matches from selected tournament
  const tournament = (tournaments||[]).find(t=>t.id===selectedSeason);
  const allMatches = tournament ? getAllMatchesFromTournament(tournament).filter(m=>m.t1&&m.t2&&m.status!=="bye"&&m.status!=="waiting") : [];
  const completedM = allMatches.filter(m=>m.status==="complete");
  const pendingM   = allMatches.filter(m=>m.status==="pending"||m.status==="pending_approval");

  // Build standings with optional overrides
  function buildStandings(overrideMap={}) {
    const stats = {};
    const initTeam = (t) => {
      if (!t?.id) return;
      if (!stats[t.id]) stats[t.id] = { team:t, wins:0, losses:0, cupDiff:0, matchesPlayed:0, pointsFor:0, pointsAgainst:0 };
    };
    // Apply completed matches
    completedM.forEach(m => {
      initTeam(m.t1); initTeam(m.t2);
      const ov = overrideMap[m.id];
      const s1 = ov ? parseInt(ov.s1)||m.s1||0 : m.s1||0;
      const s2 = ov ? parseInt(ov.s2)||m.s2||0 : m.s2||0;
      const w = s1>s2 ? m.t1 : m.t2;
      const l = s1>s2 ? m.t2 : m.t1;
      if (stats[m.t1.id]) { stats[m.t1.id].matchesPlayed++; stats[m.t1.id].pointsFor+=s1; stats[m.t1.id].pointsAgainst+=s2; stats[m.t1.id].cupDiff+=s1-s2; }
      if (stats[m.t2.id]) { stats[m.t2.id].matchesPlayed++; stats[m.t2.id].pointsFor+=s2; stats[m.t2.id].pointsAgainst+=s1; stats[m.t2.id].cupDiff+=s2-s1; }
      if (w?.id && stats[w.id]) stats[w.id].wins++;
      if (l?.id && stats[l.id]) stats[l.id].losses++;
    });
    // Apply pending matches with hypothetical scores
    pendingM.forEach(m => {
      const ov = overrideMap[m.id];
      if (!ov || (!ov.s1 && !ov.s2)) return;
      initTeam(m.t1); initTeam(m.t2);
      const s1=parseInt(ov.s1)||0, s2=parseInt(ov.s2)||0;
      if (s1===s2) return; // tie not allowed
      const w=s1>s2?m.t1:m.t2, l=s1>s2?m.t2:m.t1;
      if (stats[m.t1.id]) { stats[m.t1.id].matchesPlayed++; stats[m.t1.id].pointsFor+=s1; stats[m.t1.id].pointsAgainst+=s2; stats[m.t1.id].cupDiff+=s1-s2; }
      if (stats[m.t2.id]) { stats[m.t2.id].matchesPlayed++; stats[m.t2.id].pointsFor+=s2; stats[m.t2.id].pointsAgainst+=s1; stats[m.t2.id].cupDiff+=s2-s1; }
      if (w?.id && stats[w.id]) stats[w.id].wins++;
      if (l?.id && stats[l.id]) stats[l.id].losses++;
    });
    // Sort: wins desc → cup diff desc → points for desc
    return Object.values(stats).sort((a,b)=>{
      if (a.wins!==b.wins) return b.wins-a.wins;
      if (a.cupDiff!==b.cupDiff) return b.cupDiff-a.cupDiff;
      return b.pointsFor-a.pointsFor;
    });
  }

  const baseStandings  = buildStandings({});
  const hypoStandings  = buildStandings(overrides);

  // Detect rank changes
  const rankChanges = hypoStandings.map((row, newRank) => {
    const oldRank = baseStandings.findIndex(r=>r.team.id===row.team.id);
    return { ...row, newRank, oldRank, rankDelta: oldRank - newRank };
  });

  // Find ties in base standings
  const tiedGroups = [];
  let i=0;
  while (i < baseStandings.length) {
    let j=i+1;
    while (j<baseStandings.length && baseStandings[j].wins===baseStandings[i].wins && baseStandings[j].cupDiff===baseStandings[i].cupDiff) j++;
    if (j-i>1) tiedGroups.push(baseStandings.slice(i,j).map(r=>r.team.id));
    i=j;
  }

  const hasOverrides = Object.keys(overrides).some(k=>overrides[k]?.s1||overrides[k]?.s2);

  return (
    <div className="fade-in">
      <div style={{marginBottom:16}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3}}>⚖️ Tiebreaker Simulator</div>
        <div style={{fontSize:12,color:"var(--muted)",marginTop:3}}>Set hypothetical match scores to see how standings would shift — perfect for tiebreaker scenarios</div>
      </div>

      {/* Event selector */}
      <div style={{...css.card,padding:"12px 16px",marginBottom:16}}>
        <Fld label="Select Tournament / Event">
          <select style={css.select} value={selectedSeason} onChange={e=>{setSelectedSeason(e.target.value);setOverrides({});}}>
            <option value="">— Pick an event —</option>
            {(tournaments||[]).map(t=><option key={t.id} value={t.id}>{t.name} ({t.status})</option>)}
          </select>
        </Fld>
        {tournament && (
          <div style={{marginTop:8,fontSize:11,color:"var(--muted)"}}>
            {completedM.length} completed · {pendingM.length} pending · {Object.keys(overrides).filter(k=>overrides[k]?.s1||overrides[k]?.s2).length} overrides active
            {hasOverrides&&<button onClick={()=>setOverrides({})} style={{marginLeft:10,background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>✕ Clear all</button>}
          </div>
        )}
      </div>

      {!tournament ? (
        <div style={{...css.card,padding:"48px 20px",textAlign:"center",color:"var(--muted)"}}>
          <div style={{fontSize:48,marginBottom:14}}>⚖️</div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:3,color:"var(--white)",marginBottom:8}}>Select an Event</div>
          <div style={{fontSize:13}}>Choose a tournament to simulate tiebreaker scenarios</div>
        </div>
      ) : (
        <>
          <div style={{display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden",marginBottom:14,width:"fit-content"}}>
            {[["standings","📊 Standings"],["scenarios","🎯 Set Scores"]].map(([v,l])=>(
              <button key={v} onClick={()=>setTab(v)} style={{padding:"6px 14px",background:tab===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:tab===v?"var(--gold)":"var(--muted)",fontSize:12,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,cursor:"pointer",borderRight:v==="standings"?"1px solid var(--navy-border)":"none"}}>{l}</button>
            ))}
          </div>

          {/* STANDINGS VIEW */}
          {tab==="standings"&&(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {tiedGroups.length>0&&!hasOverrides&&(
                <div style={{padding:"10px 14px",background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:6,fontSize:12}}>
                  ⚠ <strong style={{color:"var(--gold)"}}>{tiedGroups.length} tiebreaker situation{tiedGroups.length!==1?"s":""}</strong> detected. Go to Set Scores to simulate outcomes.
                </div>
              )}
              {hasOverrides&&(
                <div style={{padding:"8px 14px",background:"rgba(91,141,238,0.07)",border:"1px solid rgba(91,141,238,0.3)",borderRadius:6,fontSize:11,color:"#5b8dee"}}>
                  🔮 Showing hypothetical standings with {Object.keys(overrides).filter(k=>overrides[k]?.s1||overrides[k]?.s2).length} score override{Object.keys(overrides).filter(k=>overrides[k]?.s1||overrides[k]?.s2).length!==1?"s":""}
                </div>
              )}
              <div style={{...css.card,padding:0,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"32px 1fr 80px 60px 60px 60px",gap:0,padding:"8px 16px",borderBottom:"1px solid var(--navy-border)",fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>
                  <div>#</div><div>Team</div><div>W–L</div><div>Cup±</div><div>Pts</div><div style={{textAlign:"right"}}>Δ</div>
                </div>
                {(hasOverrides?rankChanges:baseStandings.map((r,i)=>({...r,newRank:i,oldRank:i,rankDelta:0}))).map((row,i)=>{
                  const isTied = tiedGroups.some(g=>g.includes(row.team.id));
                  const moved = row.rankDelta!==0;
                  return (
                    <div key={row.team.id} style={{display:"grid",gridTemplateColumns:"32px 1fr 80px 60px 60px 60px",gap:0,padding:"10px 16px",borderBottom:i<rankChanges.length-1?"1px solid rgba(255,255,255,0.04)":"none",background:moved?(row.rankDelta>0?"rgba(46,204,113,0.04)":"rgba(217,79,61,0.04)"):isTied?"rgba(201,168,76,0.04)":"transparent",alignItems:"center"}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:i===0?"var(--gold)":i===1?"#c0c0c0":i===2?"#cd7f32":"var(--muted)"}}>{i+1}</div>
                      <div>
                        <div style={{fontSize:13,fontWeight:600}}>{row.team.name||row.team.teamName}</div>
                        {isTied&&!hasOverrides&&<div style={{fontSize:9,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>TIE</div>}
                      </div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16}}><span style={{color:"var(--green)"}}>{row.wins}</span><span style={{color:"var(--muted)"}}>–</span><span style={{color:"var(--red)"}}>{row.losses}</span></div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:row.cupDiff>=0?"var(--green)":"var(--red)"}}>{row.cupDiff>=0?"+":""}{row.cupDiff}</div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:"var(--muted)"}}>{row.pointsFor}</div>
                      <div style={{textAlign:"right",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,color:row.rankDelta>0?"var(--green)":row.rankDelta<0?"var(--red)":"var(--muted)"}}>
                        {row.rankDelta>0?`▲${row.rankDelta}`:row.rankDelta<0?`▼${Math.abs(row.rankDelta)}`:"—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SET SCORES VIEW */}
          {tab==="scenarios"&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:4}}>Enter hypothetical scores for any match to see how standings would change.</div>

              {/* Pending matches */}
              {pendingM.length>0&&(
                <div style={{...css.card,padding:0,overflow:"hidden"}}>
                  <div style={{padding:"8px 16px",borderBottom:"1px solid var(--navy-border)",fontSize:9,color:"var(--gold)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Pending Matches — set hypothetical outcomes</div>
                  {pendingM.map((m,i)=>{
                    const ov = overrides[m.id]||{s1:"",s2:""};
                    return (
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 16px",borderBottom:i<pendingM.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                        <div style={{flex:1,fontSize:12,fontWeight:600}}>{m.t1?.name}</div>
                        <input type="number" min="0" max="99" value={ov.s1} placeholder="—"
                          onChange={e=>setOverrides(prev=>({...prev,[m.id]:{...prev[m.id],s1:e.target.value}}))}
                          style={{...css.input,width:48,textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:20,padding:"4px"}} />
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color:"var(--muted)"}}>–</div>
                        <input type="number" min="0" max="99" value={ov.s2} placeholder="—"
                          onChange={e=>setOverrides(prev=>({...prev,[m.id]:{...prev[m.id],s2:e.target.value}}))}
                          style={{...css.input,width:48,textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:20,padding:"4px"}} />
                        <div style={{flex:1,textAlign:"right",fontSize:12,fontWeight:600}}>{m.t2?.name}</div>
                        {(ov.s1||ov.s2)&&<button onClick={()=>setOverrides(p=>{const n={...p};delete n[m.id];return n;})} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:11}}>✕</button>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Completed matches - allow override */}
              <details style={{marginTop:4}}>
                <summary style={{cursor:"pointer",fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,padding:"6px 0",userSelect:"none"}}>
                  ▸ Override completed match scores ({completedM.length})
                </summary>
                <div style={{...css.card,padding:0,overflow:"hidden",marginTop:8}}>
                  {completedM.map((m,i)=>{
                    const ov = overrides[m.id]||{s1:"",s2:""};
                    const hasOv = ov.s1||ov.s2;
                    return (
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 16px",borderBottom:i<completedM.length-1?"1px solid rgba(255,255,255,0.04)":"none",background:hasOv?"rgba(91,141,238,0.04)":"transparent"}}>
                        <div style={{flex:1,fontSize:11,fontWeight:600,color:"var(--white)"}}>{m.t1?.name}</div>
                        <div style={{fontSize:10,color:"var(--muted)",minWidth:36,textAlign:"center"}}>{m.s1}–{m.s2}</div>
                        <input type="number" min="0" max="99" value={ov.s1} placeholder={m.s1}
                          onChange={e=>setOverrides(prev=>({...prev,[m.id]:{...prev[m.id],s1:e.target.value}}))}
                          style={{...css.input,width:42,textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,padding:"3px",borderColor:hasOv?"rgba(91,141,238,0.4)":undefined}} />
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:12,color:"var(--muted)"}}>–</div>
                        <input type="number" min="0" max="99" value={ov.s2} placeholder={m.s2}
                          onChange={e=>setOverrides(prev=>({...prev,[m.id]:{...prev[m.id],s2:e.target.value}}))}
                          style={{...css.input,width:42,textAlign:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,padding:"3px",borderColor:hasOv?"rgba(91,141,238,0.4)":undefined}} />
                        <div style={{flex:1,textAlign:"right",fontSize:11,fontWeight:600,color:"var(--white)"}}>{m.t2?.name}</div>
                        {hasOv&&<button onClick={()=>setOverrides(p=>{const n={...p};delete n[m.id];return n;})} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:10}}>✕</button>}
                      </div>
                    );
                  })}
                </div>
              </details>

              {hasOverrides&&(
                <Btn onClick={()=>setTab("standings")} style={{marginTop:4}}>📊 View Updated Standings</Btn>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ── SCORE PREDICTOR ───────────────────────────────────────────────────────────
function ScorePredictorPage({ players, tournaments }) {
  const { isMobile } = useBreakpoint();
  const [playerA,    setPlayerA]    = useState("");
  const [playerB,    setPlayerB]    = useState("");
  const [searchA,    setSearchA]    = useState("");
  const [searchB,    setSearchB]    = useState("");
  const [focusA,     setFocusA]     = useState(false);
  const [focusB,     setFocusB]     = useState(false);
  const [format,     setFormat]     = useState("10cup");
  const [advantage,  setAdvantage]  = useState("neutral");

  const allPlayers = (players||[]).filter(p => p.wins + p.losses > 0);

  function eloWinProb(eloA, eloB) { return 1 / (1 + Math.pow(10, (eloB - eloA) / 400)); }

  function getH2H(pA, pB) {
    if (!pA || !pB) return { aWins:0, bWins:0, matches:[] };
    const allM = (tournaments||[]).flatMap(t => getAllMatchesFromTournament(t))
      .filter(m => m.status==="complete" && m.t1 && m.t2);
    const h2hM = allM.filter(m =>
      ((m.t1?.id===pA.id||m.t1?.name===pA.name) && (m.t2?.id===pB.id||m.t2?.name===pB.name)) ||
      ((m.t1?.id===pB.id||m.t1?.name===pB.name) && (m.t2?.id===pA.id||m.t2?.name===pA.name))
    );
    const aWins = h2hM.filter(m=>m.winner?.id===pA.id||m.winner?.name===pA.name).length;
    const bWins = h2hM.filter(m=>m.winner?.id===pB.id||m.winner?.name===pB.name).length;
    return { aWins, bWins, matches: h2hM.slice(-5).reverse() };
  }

  const pA = allPlayers.find(p=>p.id===playerA);
  const pB = allPlayers.find(p=>p.id===playerB);
  const eloA = pA?.elo || 1200;
  const eloB = pB?.elo || 1200;
  const h2h  = pA && pB ? getH2H(pA, pB) : { aWins:0, bWins:0, matches:[] };

  let probA = pA && pB ? eloWinProb(eloA, eloB) : 0.5;
  if (advantage === "home") probA = Math.min(0.95, probA + 0.05);
  if (advantage === "away") probA = Math.max(0.05, probA - 0.05);
  if (format === "3cup") probA += (probA > 0.5 ? 0.02 : -0.02);
  if (h2h.aWins + h2h.bWins >= 2) {
    const edge = Math.min(0.06, Math.abs(h2h.aWins - h2h.bWins) * 0.02);
    probA = h2h.aWins > h2h.bWins ? Math.min(0.95, probA + edge) : Math.max(0.05, probA - edge);
  }
  probA = Math.max(0.03, Math.min(0.97, probA));
  const probB = 1 - probA;
  const pctA = Math.round(probA * 100);
  const pctB = 100 - pctA;
  const diff = Math.abs(pctA - 50);
  const confidence = diff >= 25 ? "High Confidence" : diff >= 15 ? "Moderate Confidence" : diff >= 7 ? "Low Confidence" : "Coin Flip";
  const confColor  = diff >= 25 ? "var(--gold)" : diff >= 15 ? "var(--green)" : diff >= 7 ? "#5b8dee" : "var(--muted)";

  function recentForm(p) { return (p?.matchHistory||[]).slice(-5).reverse().map(m => m.won ? "W" : "L"); }
  const listA = searchA ? allPlayers.filter(p=>p.name.toLowerCase().includes(searchA.toLowerCase()) && p.id !== playerB).slice(0,8) : [];
  const listB = searchB ? allPlayers.filter(p=>p.name.toLowerCase().includes(searchB.toLowerCase()) && p.id !== playerA).slice(0,8) : [];

  const DropList = ({list, onPick, color}) => !list.length ? null : (
    <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:50,background:"var(--card)",border:"1px solid var(--navy-border)",borderRadius:4,maxHeight:160,overflowY:"auto",boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}}>
      {list.map(p=>(
        <div key={p.id} onMouseDown={()=>onPick(p)}
          style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.04)",display:"flex",justifyContent:"space-between"}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <span style={{fontSize:13,fontWeight:600}}>{p.name}</span>
          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color}}>{p.elo||1200}</span>
        </div>
      ))}
    </div>
  );

  const PlayerBox = ({which}) => {
    const isA = which==="A";
    const p = isA ? pA : pB;
    const elo = isA ? eloA : eloB;
    const color = isA ? "var(--gold)" : "#5b8dee";
    const search = isA ? searchA : searchB;
    const list = isA ? listA : listB;
    const focus = isA ? focusA : focusB;
    return (
      <div style={{position:"relative"}}>
        <Fld label={`Player ${which}`}>
          <input style={{...css.input,width:"100%",borderColor:p?`${color}66`:undefined}}
            value={p ? p.name : search}
            onChange={e=>{isA?(setSearchA(e.target.value),p&&setPlayerA("")):(setSearchB(e.target.value),p&&setPlayerB(""));}}
            onFocus={()=>isA?setFocusA(true):setFocusB(true)}
            onBlur={()=>setTimeout(()=>isA?setFocusA(false):setFocusB(false),150)}
            placeholder="Search player…" />
        </Fld>
        {focus && <DropList list={list} onPick={pl=>{isA?(setPlayerA(pl.id),setSearchA("")):(setPlayerB(pl.id),setSearchB(""));}} color={color} />}
        {p && (
          <div style={{marginTop:6,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color,lineHeight:1}}>{elo}</span>
            <span style={{fontSize:10,color:"var(--muted)"}}>Elo · {p.wins}W–{p.losses}L</span>
            <div style={{display:"flex",gap:2}}>
              {recentForm(p).map((r,i)=><span key={i} style={{fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:r==="W"?"var(--green)":"var(--red)"}}>{r}</span>)}
            </div>
            <button onClick={()=>isA?(setPlayerA(""),setSearchA("")):(setPlayerB(""),setSearchB(""))} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:11}}>✕</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fade-in">
      <div style={{marginBottom:16}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:30,letterSpacing:3}}>🎲 Score Predictor</div>
        <div style={{fontSize:12,color:"var(--muted)",marginTop:3}}>Elo-based win probability with H2H adjustment, recent form, and format modifiers</div>
      </div>

      {/* Player pickers */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 40px 1fr",gap:12,alignItems:"start",marginBottom:14}}>
        <PlayerBox which="A"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",paddingTop:isMobile?0:22}}>
          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:"var(--muted)"}}>VS</span>
        </div>
        <PlayerBox which="B"/>
      </div>

      {/* Modifiers */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        <div>
          <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Format</div>
          <div style={{display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden"}}>
            {[["10cup","10 Cup"],["6cup","6 Cup"],["3cup","3 Cup"]].map(([v,l])=>(
              <button key={v} onClick={()=>setFormat(v)} style={{padding:"5px 11px",background:format===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:format===v?"var(--gold)":"var(--muted)",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,cursor:"pointer",borderRight:v!=="3cup"?"1px solid var(--navy-border)":"none"}}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Home Court</div>
          <div style={{display:"flex",gap:0,border:"1px solid var(--navy-border)",borderRadius:4,overflow:"hidden"}}>
            {[["home","A"],["neutral","Neutral"],["away","B"]].map(([v,l])=>(
              <button key={v} onClick={()=>setAdvantage(v)} style={{padding:"5px 11px",background:advantage===v?"rgba(201,168,76,0.12)":"transparent",border:"none",color:advantage===v?"var(--gold)":"var(--muted)",fontSize:11,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,cursor:"pointer",borderRight:v!=="away"?"1px solid var(--navy-border)":"none"}}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {pA && pB ? (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Probability bar card */}
          <div style={{...css.card,padding:"20px 24px",borderColor:"rgba(201,168,76,0.2)",background:"rgba(201,168,76,0.03)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:14}}>
              <div>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?32:48,color:"var(--gold)",lineHeight:1}}>{pctA}%</div>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>{pA.name}</div>
              </div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:9,color:confColor,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>{confidence}</div>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color:"var(--muted)",marginTop:2}}>Elo Δ {Math.abs(eloA-eloB)}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?32:48,color:"#5b8dee",lineHeight:1}}>{pctB}%</div>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1}}>{pB.name}</div>
              </div>
            </div>
            <div style={{height:14,borderRadius:7,overflow:"hidden",display:"flex"}}>
              <div style={{width:`${pctA}%`,background:"linear-gradient(90deg,var(--gold),rgba(201,168,76,0.7))",transition:"width 0.6s cubic-bezier(0.4,0,0.2,1)"}}/>
              <div style={{flex:1,background:"linear-gradient(90deg,rgba(91,141,238,0.7),#5b8dee)",transition:"width 0.6s cubic-bezier(0.4,0,0.2,1)"}}/>
            </div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:8,lineHeight:1.6}}>
              Based on: Elo ratings{h2h.aWins+h2h.bWins>=2?` · H2H record (${h2h.aWins}–${h2h.bWins})`:""}{advantage!=="neutral"?` · ${advantage==="home"?pA.name:pB.name} +5%`:""}
            </div>
          </div>

          {/* Stats comparison */}
          <div style={{...css.card,padding:0,overflow:"hidden"}}>
            {[["Elo Rating",eloA,eloB,true,""],["Win Rate",pA.wins+pA.losses>0?Math.round(pA.wins/(pA.wins+pA.losses)*100):0,pB.wins+pB.losses>0?Math.round(pB.wins/(pB.wins+pB.losses)*100):0,true,"%"],["Wins",pA.wins,pB.wins,true,""],["Losses",pA.losses,pB.losses,false,""],["H2H Wins",h2h.aWins,h2h.bWins,true,""]].map(([label,vA,vB,hb,sfx],i)=>{
              const aL=hb?vA>vB:vA<vB, bL=hb?vB>vA:vB<vA;
              return (
                <div key={label} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:i<4?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <div style={{flex:1,textAlign:"right"}}>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:aL?"var(--gold)":"var(--white)"}}>{vA}{sfx}</span>
                    {aL&&<span style={{fontSize:9,color:"var(--gold)",marginLeft:3,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>▲</span>}
                  </div>
                  <div style={{width:110,textAlign:"center",fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",padding:"0 8px"}}>{label}</div>
                  <div style={{flex:1}}>
                    {bL&&<span style={{fontSize:9,color:"#5b8dee",marginRight:3,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>▲</span>}
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,color:bL?"#5b8dee":"var(--white)"}}>{vB}{sfx}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* H2H match history */}
          {h2h.matches.length>0&&(
            <div style={{...css.card,padding:0,overflow:"hidden"}}>
              <div style={{padding:"8px 16px",borderBottom:"1px solid var(--navy-border)",fontSize:9,color:"var(--muted)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>
                Recent H2H · {h2h.aWins}–{h2h.bWins} all time
              </div>
              {h2h.matches.map((m,i)=>{
                const aW=m.winner?.id===pA.id||m.winner?.name===pA.name;
                return(
                  <div key={m.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 16px",borderBottom:i<h2h.matches.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                    <div style={{flex:1,fontSize:12,fontWeight:aW?700:400,color:aW?"var(--gold)":"var(--muted)"}}>{aW?"🏆 ":""}{pA.name}</div>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:18,color:"var(--white)",minWidth:56,textAlign:"center"}}>{m.s1}–{m.s2}{m.ot?"*":""}</div>
                    <div style={{flex:1,textAlign:"right",fontSize:12,fontWeight:!aW?700:400,color:!aW?"#5b8dee":"var(--muted)"}}>{!aW?"🏆 ":""}{pB.name}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{fontSize:10,color:"rgba(255,255,255,0.18)",textAlign:"center",fontStyle:"italic"}}>
            Prediction is probabilistic — upsets are always possible. 🎯
          </div>
        </div>
      ) : (
        <div style={{...css.card,padding:"48px 20px",textAlign:"center",color:"var(--muted)"}}>
          <div style={{fontSize:52,marginBottom:14}}>🎲</div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:24,letterSpacing:3,color:"var(--white)",marginBottom:8}}>Select Two Players</div>
          <div style={{fontSize:13,maxWidth:320,margin:"0 auto"}}>Choose any two players to see win probability, head-to-head history, and Elo comparison</div>
          {allPlayers.length<2&&<div style={{marginTop:16,fontSize:12,color:"var(--red)"}}>⚠ Need at least 2 players with match history</div>}
        </div>
      )}
    </div>
  );
}




// ── ROOT ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { isTablet } = useBreakpoint();
  const [ready, setReady] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("mlcp_theme") || "dark"; } catch { return "dark"; }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("mlcp_theme", theme); } catch {}
  }, [theme]);
  const [page, setPageRaw] = useState("dashboard");
  const [players, setPlayersRaw] = useState(INIT_PLAYERS);
  const [tournaments, setTournamentsRaw] = useState(INIT_TOURNAMENTS);
  const [seasons, setSeasonsRaw] = useState(INIT_SEASONS);
  const [venues, setVenuesRaw] = useState(INIT_VENUES);
  const [activeTournament, setActiveTournamentRaw] = useState(null);
  const [saveIndicator, setSaveIndicator] = useState(false); // brief flash on save
  const [auditLog, setAuditLogRaw] = useState([]);
  const [adminSettings, setAdminSettingsRaw] = useState(DEFAULT_ADMIN_SETTINGS);
  const [confirmClear, setConfirmClear] = useState(false);
  const myRole = "super_admin";

  // ── Wrapped setters that also persist ────────────────────────────────────
  function flash() { setSaveIndicator(true); setTimeout(() => setSaveIndicator(false), 900); }

  function setPage(p) { setPageRaw(p); storageSet(SK.page, p); }

  function setPlayers(fn) {
    setPlayersRaw(prev => {
      const next = typeof fn === "function" ? fn(prev) : fn;
      storageSet(SK.players, next);
      flash();
      return next;
    });
  }

  function setTournaments(fn) {
    setTournamentsRaw(prev => {
      const next = typeof fn === "function" ? fn(prev) : fn;
      storageSet(SK.tournaments, next);
      // If activeTournament is in the list, sync it too
      setActiveTournamentRaw(act => {
        if (!act) return act;
        const synced = next.find(t => t.id === act.id);
        if (synced) storageSet(SK.activeTournament, synced);
        return synced || act;
      });
      flash();
      return next;
    });
  }

  function setActiveTournament(t) {
    setActiveTournamentRaw(t);
    storageSet(SK.activeTournament, t);
    flash();
  }

  // ── Boot: load from storage ───────────────────────────────────────────────
  useEffect(() => {
    loadAllState().then(state => {
      setPlayersRaw(state.players);
      setTournamentsRaw(state.tournaments);
      setActiveTournamentRaw(state.activeTournament);
      setPageRaw(state.page);
      setSeasonsRaw(state.seasons || INIT_SEASONS);
      setVenuesRaw(state.venues || INIT_VENUES);
      setAuditLogRaw(state.auditLog || []);
      setAdminSettingsRaw(state.adminSettings || DEFAULT_ADMIN_SETTINGS);
      // Stamp version on first load
      storageSet(SK.version, STORAGE_VERSION);
      setReady(true);
    });
  }, []);

  // ── Clear all data ────────────────────────────────────────────────────────
  async function handleClearData() {
    await clearAllStorage();
    setPlayersRaw(INIT_PLAYERS);
    setTournamentsRaw(INIT_TOURNAMENTS);
    setActiveTournamentRaw(null);
    setSeasonsRaw(INIT_SEASONS);
    setVenuesRaw(INIT_VENUES);
    setAuditLogRaw([]);
    setAdminSettingsRaw(DEFAULT_ADMIN_SETTINGS);
    setPageRaw("dashboard");
    await storageSet(SK.version, STORAGE_VERSION);
    setConfirmClear(false);
  }

  // ── Loading screen ────────────────────────────────────────────────────────
  if (!ready) return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",background:"var(--navy)",gap:18 }}>
        <img src={LOGO_URI} alt="MLCP" style={{ height:44,width:"auto",filter:"invert(1) sepia(1) saturate(2) hue-rotate(10deg) brightness(1.1)" }} />
        <div style={{ width:32,height:32,border:"3px solid var(--navy-border)",borderTopColor:"var(--gold)",borderRadius:"50%",animation:"spin 0.8s linear infinite" }} />
        <div style={{ fontSize:12,color:"var(--muted)",letterSpacing:2,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,textTransform:"uppercase" }}>Loading…</div>
      </div>
    </>
  );

  const TITLES = { dashboard:"Dashboard",tournaments:"Tournaments",bracket:"Live Bracket",spectator:"Spectator View",registration:"Registration",checkin:"Event Check-In",seeding:"Seeding Tool",tables:"Table Manager",notifications:"Notifications",players:"Players",rankings:"Rankings",history:"History",seasons:"Seasons",export:"Export & Print",venues:"Venues",admin:"Admin Panel",users:"User Management" };

  function setSeasons(next) {
    setSeasonsRaw(prev => {
      const v = typeof next==="function" ? next(prev) : next;
      storageSet(SK.seasons, v);
      return v;
    });
  }

  function setVenues(next) {
    setVenuesRaw(prev => {
      const v = typeof next==="function" ? next(prev) : next;
      storageSet(SK.venues, v);
      return v;
    });
  }
  function setAuditLog(next) {
    setAuditLogRaw(prev => {
      const v = typeof next==="function" ? next(prev) : next;
      storageSet(SK.auditLog, v);
      return v;
    });
  }
  function setAdminSettings(next) {
    setAdminSettingsRaw(prev => {
      const v = typeof next==="function" ? next(prev) : next;
      storageSet(SK.adminSettings, v);
      flash();
      return v;
    });
  }
  function addAuditEntry(action, details="", user="Admin") {
    const entry = { id:uid(), ts:new Date().toISOString(), action, details, user };
    setAuditLogRaw(prev => {
      const v = [entry, ...(prev||[])].slice(0,200);
      storageSet(SK.auditLog, v);
      return v;
    });
  }

  function renderPage() {
    const props = { tournaments,setTournaments,players,setPlayers,activeTournament,setActiveTournament,setPage,seasons,setSeasons,venues,setVenues,auditLog,setAuditLog,adminSettings,setAdminSettings,addAuditEntry };
    switch(page) {
      case "dashboard": return <Dashboard {...props} />;
      case "tournaments": return <TournamentsPage {...props} />;
      case "bracket": return <BracketPage {...props} />;
      case "registration": return <RegistrationPage {...props} />;
      case "checkin": return <CheckInPage {...props} />;
      case "seeding": return <SeedingPage {...props} />;
      case "tables": return <TableManagerPage {...props} />;
      case "players": return <PlayersPage {...props} />;
      case "rankings": return <RankingsPage {...props} tournaments={tournaments} />;
      case "history":   return <HistoryPage {...props} />;
      case "analytics": return <AnalyticsPage {...props} />;
      case "schedule":  return <ScheduleBuilderPage {...props} />;
      case "selfreport": return <SelfReportKioskPage {...props} />;
      case "scoreboard": return <LiveScoreboardPage {...props} />;
      case "recap":     return <EventRecapPage {...props} />;
      case "matchroom": return <MatchRoomPage {...props} />;
      case "h2h":       return <HeadToHeadPage {...props} />;
      case "predictor":  return <ScorePredictorPage {...props} />;
      case "tiebreak":   return <TiebreakerPage {...props} />;
      case "admin": return <AdminPage {...props} onClearData={()=>setConfirmClear(true)} />;
      case "users": return <UsersPage {...props} />;
      case "seasons": return <SeasonsPage {...props} />;
      case "venues": return <VenuesPage {...props} />;
      case "export": return <ExportPage {...props} />;
      case "notifications": return <NotificationsPage {...props} />;
      case "spectator": return <SpectatorPage {...props} />;
      default: return null;
    }
  }

  const MOBILE_NAV = [
    {id:"dashboard",icon:"⚡",label:"Home"},
    {id:"bracket",icon:"🎯",label:"Bracket"},
    {id:"spectator",icon:"👁",label:"Spectate"},
    {id:"tables",icon:"🏓",label:"Tables"},
    {id:"checkin",icon:"✅",label:"Check-In"},
  ];

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{ display:"flex",height:"100vh",overflow:"hidden",background:"var(--navy)" }}>
        <div className="desktop-sidebar"><Sidebar page={page} setPage={setPage} myRole={myRole} theme={theme} setTheme={setTheme} /></div>
        <div style={{ flex:1,overflow:"hidden",display:"flex",flexDirection:"column",minWidth:0 }}>
          {/* Topbar */}
          <div style={{ background:"var(--navy-mid)",borderBottom:"1px solid var(--navy-border)",padding:"0 16px",height:50,display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
            {isTablet && <MobileMenuDrawer page={page} setPage={setPage} myRole={myRole} theme={theme} setTheme={setTheme} />}
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:17,letterSpacing:1.5,textTransform:"uppercase",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{TITLES[page]}</span>
            {activeTournament && (
              <div onClick={()=>setPage("bracket")} style={{ display:"flex",alignItems:"center",gap:6,marginLeft:8,padding:"3px 9px",background:"rgba(46,204,113,0.1)",border:"1px solid rgba(46,204,113,0.3)",borderRadius:4,cursor:"pointer",flexShrink:0 }}>
                <div style={{ width:6,height:6,borderRadius:"50%",background:"var(--green)",animation:"pulse 1.5s infinite" }} />
                <span style={{ fontSize:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,color:"var(--green)",textTransform:"uppercase" }}>Live</span>
              </div>
            )}
            <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:8,flexShrink:0 }}>
              {saveIndicator && <span style={{ fontSize:10,color:"var(--green)",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase" }}>✓ Saved</span>}
              {/* Notification bell */}
              {(() => {
                const alertCnt = getAlertCount(tournaments);
                return (
                  <div onClick={()=>setPage("notifications")} style={{ position:"relative",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",borderRadius:6,background:"rgba(255,255,255,0.04)",border:"1px solid var(--navy-border)",transition:"background 0.15s" }}>
                    <span style={{ fontSize:16 }}>{alertCnt>0?"🚨":"🔔"}</span>
                    {alertCnt>0 && <div style={{ position:"absolute",top:-5,right:-5,width:18,height:18,borderRadius:"50%",background:"var(--red)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontFamily:"'Bebas Neue',sans-serif",color:"var(--white)",fontWeight:700,animation:"pulse 1.5s infinite" }}>{alertCnt}</div>}
                  </div>
                );
              })()}
              <span className="topbar-role"><Badge color="gold">{myRole.replace(/_/g," ")}</Badge></span>
            </div>
          </div>
          {/* Content */}
          <div className="main-area" style={{ flex:1,overflow:"auto",padding:24 }}>{renderPage()}</div>
        </div>
        {/* Mobile bottom nav */}
        <nav className="mobile-nav">
          {MOBILE_NAV.map(item=>(
            <div key={item.id} className={`mobile-nav-item${page===item.id?" mna":""}`} onClick={()=>setPage(item.id)}>
              <span className="mni">{item.icon}</span>{item.label}
            </div>
          ))}
        </nav>
      </div>

      {/* Confirm clear data modal */}
      <Modal open={confirmClear} onClose={()=>setConfirmClear(false)} title="Clear All Data" width={400}>
        <div style={{ fontSize:14,color:"var(--muted)",lineHeight:1.7,marginBottom:20 }}>
          This will permanently delete all players, tournaments, registrations, brackets, and scores — resetting the app to its default state.<br/><br/>
          <strong style={{ color:"var(--red)" }}>This cannot be undone.</strong>
        </div>
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
          <Btn v="ghost" onClick={()=>setConfirmClear(false)}>Cancel</Btn>
          <Btn v="danger" onClick={handleClearData}>Yes, Clear Everything</Btn>
        </div>
      </Modal>
    </>
  );
}
